import {
  api, ApiError, setApiLine, setCurrentCallSession,
  type KernelOptions, type BrowserbaseOptions, type BenchState, type BenchTask, type BenchBrowser, type BenchRow, type ChatMessage, type ChatRead, type Conversation, type Presence, type Invite, type LiveCall, type VoiceKey, type Billing, type StorageInfo, type MenuOption, type Note, type PhoneSettings, type Reaction,
  type Recording, type RouteAction, type Text, type Thread, type ThreadCall, type AiAgentCall, type AgentMessage, type SelenaSettingsBody, type SelenaSettings, type PaSettings, type NumbersBody, type User,
  type ContactSummary, type ContactDetail, type ContactNote, type ActivityItem, type SearchHit, type Snippet, type BlockedNumber,
  type Task, type PendingTakeover, type Analytics, type ContactDetails, type NotesJob, type MfaGate, type TwoFactorStatus, type SecuritySettings, type SecurityLogItem, type EmailSettings, type EmailReplyStatus, setMfaHandler, type Weekday, type Attachment, type UserPrefs, type CallSettings, type QuoImportStatus, type MonitorMode, type CallTarget, type ScheduledText,
} from "./api";
import { startCrashWatch } from "./crashwatch";
import type { InternalThread } from "./api";
import { startMicGuard, micComingBack } from "./micguard";
import { openViewer, viewerHandlesPop, setViewerBackHook } from "./viewer";
import { Phone, isEnded, otherParty, lastMediaPath, type PhoneCall, type CallQuality } from "./voice";
import { callDelayByRegion, REGION_NAMES, AUTO_REGIONS, effectiveRegion, lastSpeedRun, saveSpeedRun, pickBest, speedScore, onwardMs, retestReason, slowerThanBest, type RegionResult, type SpeedResult } from "./region";
import { MEDIA_SITE_PLACES, REGION_PLACES, FLY_PLACES, km, minRttMs, geoip, drawMap, isTelnyxMediaIp, measureDeviceDelay, type DeviceDelay, type GeoIp, type MapPin, type MapLine, type Place } from "./speed";
import { h, mount, patch, reuseFrame, setSameScreen, adoptInto, isTextField, fingersOnScreen, setKeepTyped, timeAgo, listTime, fullTime, duration, phone, toE164, splitExtension, toast, Ringer, serverTime, when, fmtDate } from "./ui";
import { icon, ticketIcon } from "./icons";
import { startRingback, stopRingback, setCallAudio, SOUND_OPTIONS, getSoundPrefs, setSoundPrefs, setSoundUser, preview, playTextSound, playMentionSound, playDtmf, playHangupTone, type SoundPrefs } from "./sounds";
import { listDevices, getDevicePrefs, setDevicePrefs, startMicLevel, micOn, micOff, micReady, micIsOn, micKeepReady, setMicKeepReady, micForCall, micOpensSilently, isIOS, onMicReadyChange, checkMicPermission, steadyAudioSession } from "./devices";
import { startAuthentication, startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";

type Route =
  | { name: "inbox" }
  | { name: "dialer" }
  | { name: "thread"; number: string }
  | { name: "chat" }
  | { name: "ai-agent" }
  | { name: "settings" }
  | { name: "team" }
  | { name: "phone-settings" }
  | { name: "contacts" }
  | { name: "contact"; number: string }
  | { name: "notes-to-profiles" }
  | { name: "activity" }
  | { name: "tasks" }
  | { name: "analytics" }
  | { name: "search"; q?: string }
  | { name: "snippets" }
  | { name: "blocked" }
  | { name: "settings-calling" }
  | { name: "settings-notifications" }
  | { name: "settings-sounds" }
  | { name: "settings-audio" }
  | { name: "call-settings" }
  | { name: "scheduled" }
  | { name: "import-quo" }
  | { name: "security" }
  | { name: "email-alerts" }
  | { name: "storage" }
  | { name: "phone-numbers" }
  | { name: "new-group" }
  | { name: "billing" }
  | { name: "speed" }
  | { name: "settings-rcs" };
const SETTINGS_ROUTES = ["phone-numbers", "speed", "settings-rcs", "billing", "storage", "security", "email-alerts", "import-quo", "settings", "team", "phone-settings", "snippets", "blocked", "settings-calling", "settings-notifications", "settings-sounds", "settings-audio", "call-settings", "scheduled", "analytics"];
type Filter = "all" | "unread" | "unresponded" | "mine" | "missed" | "voicemail";
/** Which list the middle column shows. */
type Section = "inbox" | "contacts" | "none";
type Theme = "system" | "light" | "dark";
type Reply = { id: number; name: string; body: string };

declare const __APP_VERSION__: string;
const VERSION = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";
/** "v54.10.8" with a green dot when it's the latest, or a red dot when a newer version has been deployed (click → Update). */
function versionLabel() {
  const fresh = updateState.version;
  return h("div", { class: "version" },
    `v${VERSION}`,
    fresh
      ? h("button", { class: "update-dot", title: `Version ${fresh} is ready`, "aria-label": `Update to version ${fresh}`, onclick: (e: MouseEvent) => {
          e.stopPropagation();
          void promptUpdate();
        } })
      : h("span", { class: "latest-dot", title: "You're on the latest version", "aria-label": "Latest version" }));
}
/**
 * Frosted-glass pop-up for confirmations and quick questions (replaces the browser's plain confirm/prompt).
 * Resolves true / the typed text when confirmed, false / null when canceled (Esc, Cancel or a tap outside).
 */
function glassDialog(opts: { title: string; message?: string; confirm: string; cancel?: string; danger?: boolean; input?: { placeholder?: string; type?: string; value?: string; inputmode?: string } }): Promise<string | boolean | null> {
  return new Promise((resolve) => {
    const prev = document.activeElement as HTMLElement | null;
    const input = opts.input
      ? h("input", { class: "field glass-input", type: opts.input.type ?? "text", placeholder: opts.input.placeholder ?? "", value: opts.input.value ?? "", inputmode: opts.input.inputmode, autocomplete: "off" })
      : null;
    const done = (v: string | boolean | null) => {
      overlay.classList.add("closing");
      document.removeEventListener("keydown", onKey, true);
      setTimeout(() => overlay.remove(), 140);
      prev?.focus?.();
      resolve(v);
    };
    // Confirmed with the box empty: "" (a cancel is null), so a name can be cleared on purpose.
    const ok = () => done(input ? input.value.trim() : true);
    const cancel = () => done(input ? null : false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") (e.preventDefault(), e.stopPropagation(), cancel());
      else if (e.key === "Enter" && !e.isComposing) {
        // Enter on another focused button (Cancel) presses that button, not Confirm.
        const a = document.activeElement;
        if (a instanceof HTMLButtonElement && a !== confirmBtn) return;
        e.preventDefault(), e.stopPropagation(), ok();
      }
    };
    const confirmBtn = h("button", { class: opts.danger ? "btn danger" : "btn", onclick: ok }, opts.confirm);
    const overlay = h("div", { class: "glass-overlay dialog", role: "dialog", "aria-modal": "true", "aria-label": opts.title, onclick: (e: MouseEvent) => e.target === overlay && cancel() },
      h("div", { class: "glass-card glass-dialog" },
        h("div", { class: "glass-dialog-title" }, opts.title),
        opts.message ? h("div", { class: "glass-dialog-msg" }, opts.message) : null,
        input,
        h("div", { class: "glass-dialog-actions" },
          h("button", { class: "btn quiet", onclick: cancel }, opts.cancel ?? "Cancel"),
          confirmBtn)));
    document.addEventListener("keydown", onKey, true);
    document.body.append(overlay);
    requestAnimationFrame(() => (input ?? confirmBtn).focus());
  });
}

/**
 * 4.10.20 "there's no option to edit a task?": change a task's wording and when it's due (who it's for stays on the
 * assign menu). For her own call for later, the due time is when she calls.
 */
function editTask(t: Task) {
  const prev = document.activeElement as HTMLElement | null;
  const pad = (n: number) => String(n).padStart(2, "0");
  const due = t.dueAt ? new Date(t.dueAt.includes("T") || t.dueAt.includes("Z") ? t.dueAt : t.dueAt.replace(" ", "T") + "Z") : null;
  const local = due ? `${due.getFullYear()}-${pad(due.getMonth() + 1)}-${pad(due.getDate())}T${pad(due.getHours())}:${pad(due.getMinutes())}` : "";
  const title = h("input", { class: "field glass-input", value: t.title, maxlength: 200, autocomplete: "off", "aria-label": "Task" }) as HTMLInputElement;
  const when = h("input", { class: "field glass-input", type: "datetime-local", value: local, "aria-label": "Due" }) as HTMLInputElement;
  const close = () => {
    overlay.classList.add("closing");
    document.removeEventListener("keydown", onKey, true);
    setTimeout(() => overlay.remove(), 140);
    prev?.focus?.();
  };
  const save = async () => {
    const text = title.value.trim();
    if (!text) return toast("What's the task?");
    if (t.aiCall && !when.value) return toast(`Pick when ${t.aiCall.agent} should call`);
    const dueAt = when.value ? new Date(when.value).getTime() : null;
    if (dueAt != null && !Number.isFinite(dueAt)) return toast("That date isn't valid");
    close();
    const before = { title: t.title, dueAt: t.dueAt };
    Object.assign(t, { title: text, dueAt: dueAt != null ? new Date(dueAt).toISOString() : null });
    render();
    try {
      await api.updateTask(t.id, { title: text, dueAt });
      toast("Task updated");
    } catch (e) {
      Object.assign(t, before);
      toast((e as Error).message);
    }
    if (state.route.name === "tasks") await loadTasks();
    else void refresh();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") (e.preventDefault(), e.stopPropagation(), close());
    else if (e.key === "Enter" && !e.isComposing && document.activeElement === title) (e.preventDefault(), void save());
  };
  const overlay = h("div", { class: "glass-overlay dialog", role: "dialog", "aria-modal": "true", "aria-label": "Edit task", onclick: (e: MouseEvent) => e.target === overlay && close() },
    h("div", { class: "glass-card glass-dialog task-edit" },
      h("div", { class: "glass-dialog-title" }, "Edit task"),
      title,
      h("label", { class: "task-edit-label" }, t.aiCall ? `When ${t.aiCall.agent} calls` : "Due"),
      // Her call for later needs a time (without one she'd never make it).
      h("div", { class: "task-edit-due" }, when, t.aiCall ? null : h("button", { class: "btn quiet sm", type: "button", onclick: () => (when.value = "") }, "No date")),
      h("div", { class: "glass-dialog-actions" },
        h("button", { class: "btn quiet", onclick: close }, "Cancel"),
        h("button", { class: "btn", onclick: () => void save() }, "Save"))));
  document.addEventListener("keydown", onKey, true);
  document.body.append(overlay);
  requestAnimationFrame(() => title.focus());
}

/** Your contacts (CardDAV) passwords, loaded when the Calls settings show them. */
let appPwList: { id: number; label: string; createdAt: string; lastUsedAt: string | null }[] | null = null;
let appPwLoading = false;
function appPasswordRows(): Node[] {
  if (appPwList === null) {
    if (!appPwLoading) {
      appPwLoading = true;
      void api.appPasswords().then((r) => (appPwList = r.passwords)).catch(() => (appPwList = [])).finally(() => ((appPwLoading = false), render()));
    }
    return [];
  }
  const day = (iso: string) => fmtDate(new Date(iso.includes("T") || iso.includes("Z") ? iso : iso.replace(" ", "T") + "Z"), { month: "short", day: "numeric", year: "numeric" });
  return appPwList.map((pw) => h("div", { class: "item" },
    h("div", { class: "grow" },
      h("div", {}, pw.label),
      h("div", { class: "sub" }, `Made ${day(pw.createdAt)} · ${pw.lastUsedAt ? `last used ${day(pw.lastUsedAt)}` : "never used"}`)),
    h("button", { class: "text-btn danger", onclick: async () => {
      const ok = await glassDialog({ title: "Remove this contacts password?", message: "A phone using it stops getting Joy Phone contacts until you set it up again.", confirm: "Remove", danger: true });
      if (!ok) return;
      try {
        await api.deleteAppPassword(pw.id);
        appPwList = (appPwList ?? []).filter((x) => x.id !== pw.id);
        toast("Removed");
      } catch (e) {
        toast((e as Error).message);
      }
      render();
    } }, "Remove")));
}

/**
 * 5.10.20 Edit a text that's waiting to go out (its wording and when it goes): before, it could only be sent now or
 * cancelled and written again.
 */
function editScheduled(sc: ScheduledText) {
  const prev = document.activeElement as HTMLElement | null;
  const pad = (n: number) => String(n).padStart(2, "0");
  const d = new Date(sc.sendAt);
  const body = h("textarea", { class: "field glass-input", rows: 4, maxlength: 1600, "aria-label": "Text" }) as HTMLTextAreaElement;
  body.value = sc.body;
  const when = h("input", { class: "field glass-input", type: "datetime-local", "aria-label": "Send at",
    value: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` }) as HTMLInputElement;
  const close = () => {
    overlay.classList.add("closing");
    document.removeEventListener("keydown", onKey, true);
    setTimeout(() => overlay.remove(), 140);
    prev?.focus?.();
  };
  const save = async () => {
    const text = body.value.trim();
    if (!text && !sc.media.length) return toast("Write the text first");
    const at = new Date(when.value).getTime();
    if (!when.value || !Number.isFinite(at)) return toast("Pick when it goes out");
    try {
      await api.updateScheduled(sc.id, { body: text, sendAt: at });
      close();
      toast(`Scheduled for ${fmtDate(new Date(at), { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`);
    } catch (e) {
      toast((e as Error).message); // stays open (e.g. "That time has passed"), so nothing typed is lost
      return;
    }
    if (state.route.name === "scheduled") await loadScheduled();
    else await refresh();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") (e.preventDefault(), e.stopPropagation(), close());
  };
  const overlay = h("div", { class: "glass-overlay dialog", role: "dialog", "aria-modal": "true", "aria-label": "Edit scheduled text", onclick: (e: MouseEvent) => e.target === overlay && close() },
    h("div", { class: "glass-card glass-dialog task-edit" },
      h("div", { class: "glass-dialog-title" }, "Edit scheduled text"),
      body,
      h("label", { class: "task-edit-label" }, "Sends"),
      h("div", { class: "task-edit-due" }, when),
      h("div", { class: "glass-dialog-actions" },
        h("button", { class: "btn quiet", onclick: close }, "Cancel"),
        h("button", { class: "btn", onclick: () => void save() }, "Save"))));
  document.addEventListener("keydown", onKey, true);
  document.body.append(overlay);
  requestAnimationFrame(() => body.focus());
}

/** "Which number does that device show?": the choices as buttons plus Deny. Resolves the pick, or null for Deny. */
function numberChoiceDialog(title: string, message: string, choices: string[]): Promise<string | null> {
  return new Promise((resolve) => {
    const prev = document.activeElement as HTMLElement | null;
    const done = (v: string | null) => {
      overlay.classList.add("closing");
      document.removeEventListener("keydown", onKey, true);
      setTimeout(() => overlay.remove(), 140);
      prev?.focus?.();
      resolve(v);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") (e.preventDefault(), e.stopPropagation(), done(null));
    };
    const overlay = h("div", { class: "glass-overlay dialog", role: "dialog", "aria-modal": "true", "aria-label": title, onclick: (e: MouseEvent) => e.target === overlay && done(null) },
      h("div", { class: "glass-card glass-dialog" },
        h("div", { class: "glass-dialog-title" }, title),
        h("div", { class: "glass-dialog-msg" }, message),
        h("div", { class: "approval-choices", role: "group", "aria-label": "Number shown on that device" },
          ...choices.map((n) => h("button", { class: "btn approval-choice", onclick: () => done(n) }, n))),
        h("div", { class: "glass-dialog-actions" },
          h("button", { class: "btn quiet danger-text", onclick: () => done(null) }, "Deny"))));
    document.addEventListener("keydown", onKey, true);
    document.body.append(overlay);
  });
}

// ---------------------------------------------------------------- AI agent

/** The AI agent's calls (its screen), and what's typed in its New call form (kept across re-renders). */
const aiAgent = { calls: [] as AiAgentCall[], loaded: false, form: { to: "", who: "", ask: "", details: "", book: false }, busy: false, error: "",
  tab: "chat" as "chat" | "calls" | "voice", chat: [] as AgentMessage[], chatLoaded: false, thinking: false,
  settings: null as SelenaSettingsBody | null, allVoices: false, voiceQuery: "", openGroups: new Set<string>(), rulesDraft: null as { canSay: string; cannotSay: string; about: string } | null,
  // Personal mode (Selena as your own assistant): your private settings, and edits not saved yet.
  contactHits: { term: "", list: [] as { number: string; name: string }[] },
  mode: "business" as "business" | "personal", pa: null as PaSettings | null, paDraft: null as Partial<PaSettings> | null, paSaving: false,
  /** Selena → Settings: the business side or your personal side (null: follows the line you're on). */
  side: null as null | "business" | "personal",
  /** Selena → Settings: show Telnyx's long list of extra voices too (off: just her original voices). */
  moreVoices: false };
/** Settings → Try models: the same question to several Telnyx models, side by side with how long each took. */
const tryM = { open: false, loaded: false, fetched: false, current: null as string | null, models: [] as string[], pick: new Set<string>(), thinking: false,
  question: "", running: 0, web: true, searchMs: null as number | null, results: new Map<string, { ms?: number; reply?: string; error?: string; busy?: boolean }>() };
function tryModelsGroup() {
  if (tryM.open && !tryM.loaded) {
    tryM.loaded = true;
    void api.agentModels().then((r) => {
      tryM.current = r.current; tryM.models = r.models; tryM.thinking = r.thinking; tryM.fetched = true;
      if (!tryM.pick.size) for (const m of r.models.slice(0, 4)) tryM.pick.add(m);
      if (r.current && r.models.includes(r.current)) tryM.pick.add(r.current);
      render();
    }).catch((e) => {
      // Once per opening (no retry on every screen refresh): closing and opening the panel tries again.
      tryM.fetched = true;
      toast((e as Error).message);
      render();
    });
  }
  const run = async () => {
    const q = tryM.question.trim();
    if (!q) return toast("Type a question first");
    const picked = tryM.models.filter((m) => tryM.pick.has(m));
    if (!picked.length) return toast("Pick at least one model");
    tryM.results = new Map(picked.map((m) => [m, { busy: true }]));
    tryM.searchMs = null;
    tryM.running = picked.length;
    render();
    await Promise.all(picked.map(async (m) => {
      try {
        const r = await api.agentTry(m, q, tryM.thinking, tryM.web);
        if (r.searchMs != null) tryM.searchMs = r.searchMs;
        // Its own time, without the shared web search.
        tryM.results.set(m, { ms: Math.max(0, r.ms), reply: r.reply, error: r.error });
      } catch (e) {
        tryM.results.set(m, { error: (e as Error).message });
      }
      tryM.running--;
      render();
    }));
  };
  const secs = (ms?: number) => (ms == null ? "" : `${(ms / 1000).toFixed(1)} s`);
  const done = [...tryM.results.entries()];
  const fastest = Math.min(...done.filter(([, r]) => r.ms != null && !r.error).map(([, r]) => r.ms!));
  return h("div", { class: "try-models" },
    h("button", { class: "try-head", type: "button", "aria-expanded": String(tryM.open), onclick: () => {
      tryM.open = !tryM.open;
      if (tryM.open && !tryM.models.length) (tryM.loaded = false), (tryM.fetched = false); // try the list again
      render();
    } },
      icon("sparkle", "sm"), h("span", {}, h("b", {}, "Try models"), h("small", {}, `Ask Telnyx's chat models the same thing and compare answers and speed. ${aiName()}'s model doesn't change.`)),
      icon("chevronDown", "sm")),
    tryM.open ? h("div", { class: "try-body" },
      !tryM.models.length ? h("p", { class: "muted" }, tryM.fetched ? "No chat models came back from Telnyx (is the AI provider on?)." : "Loading Telnyx's models…") : h("div", { class: "try-models-list" },
        ...tryM.models.map((m) => h("label", { class: `try-chip${tryM.pick.has(m) ? " on" : ""}` },
          h("input", { type: "checkbox", checked: tryM.pick.has(m), onchange: (e: Event) => { (e.target as HTMLInputElement).checked ? tryM.pick.add(m) : tryM.pick.delete(m); render(); } }),
          h("span", {}, m.replace(/^[^/]+\//, "")), m === tryM.current ? h("em", {}, "in use") : null))),
      h("textarea", { class: "field", rows: "3", maxlength: "2000", placeholder: "e.g. What's going on with Mrs. Semp?", value: tryM.question,
        oninput: (e: Event) => (tryM.question = (e.target as HTMLTextAreaElement).value) }),
      h("div", { class: "try-actions" },
        h("span", { class: "try-opts" },
          h("label", { class: "try-think" }, h("input", { type: "checkbox", checked: tryM.web, onchange: (e: Event) => ((tryM.web = (e.target as HTMLInputElement).checked), render()) }),
            `Search the web first (like ${aiName()})`),
          h("label", { class: "try-think" }, h("input", { type: "checkbox", checked: tryM.thinking, onchange: (e: Event) => ((tryM.thinking = (e.target as HTMLInputElement).checked), render()) }),
            "Let them think first (slower)")),
        h("button", { class: "btn sm", type: "button", disabled: tryM.running > 0, onclick: () => void run() }, tryM.running ? `Running… ${tryM.running} left` : "Run test")),
      tryM.searchMs != null ? h("p", { class: "muted try-note" }, `Web search: ${secs(tryM.searchMs)}, done once and shared by every model (not counted in their times).`) : null,
      done.length ? h("div", { class: "try-results" },
        ...done.map(([m, r]) => h("div", { class: `try-card${r.error ? " err" : ""}` },
          h("div", { class: "try-card-head" }, h("b", {}, m.replace(/^[^/]+\//, "")), m === tryM.current ? h("em", {}, "in use") : null,
            r.busy ? h("span", { class: "try-time" }, "…") : h("span", { class: `try-time${r.ms === fastest ? " best" : ""}` }, secs(r.ms))),
          r.busy ? h("div", { class: "muted" }, "Waiting for the answer…")
            : r.error ? h("div", { class: "try-err" }, r.error)
            : h("div", { class: "try-reply" }, ...selenaText(r.reply || "(empty answer)")),
          !r.busy && !r.error ? h("button", { class: "link-btn try-copy", type: "button", onclick: () => void navigator.clipboard.writeText(m).then(() => toast(`Copied ${m}: paste it as AI_AGENT_MODEL in the Fly portal`)) }, "Copy model name") : null))) : null,
      h("p", { class: "muted try-note" }, `Uses your recent chat and calls as context, like ${aiName()} does. Nothing is sent, called or booked. Each test costs a little Telnyx usage.`)) : null);
}

/** Which side of Selena's settings is showing: the one you picked, else the line you're on. */
function settingsSide(): "business" | "personal" {
  if (!isAdmin() || !state.personalLine) return "business";
  return aiAgent.side ?? (state.line === "personal" ? "personal" : "business");
}
/** One request at a time: several screens ask for it while drawing (it went out 6 times at startup, 9.10.18). */
let personalLoading: Promise<void> | null = null;
function loadPersonal(): Promise<void> {
  personalLoading ??= (async () => {
    try {
      const r = await api.agentPersonal();
      aiAgent.pa = r.settings;
      aiAgent.mode = r.settings.mode;
      if (state.route.name === "ai-agent") render();
    } catch {
      // shown with defaults
    }
  })().finally(() => (personalLoading = null));
  return personalLoading;
}
/** Business ⇄ Personal: who Selena works for in your private chat (and on the calls she makes from it). */
async function setSelenaMode(mode: "business" | "personal", quiet = false) {
  if (aiAgent.mode === mode) return;
  const before = aiAgent.mode;
  aiAgent.mode = mode;
  render();
  try {
    aiAgent.pa = (await api.saveAgentPersonal({ mode })).settings;
    if (!quiet) toast(mode === "personal" ? `Personal mode: ${aiName()} works just for you, privately` : `Business mode: ${aiName()} works for Joy Travel`);
    if (!quiet && mode === "personal" && !aiAgent.pa.name && !aiAgent.pa.number) {
      aiAgent.tab = "voice"; // first time: show where her personal details go
      requestAnimationFrame(() => document.getElementById("pa-section")?.scrollIntoView({ block: "start" }));
    }
  } catch (e) {
    aiAgent.mode = before;
    toast((e as Error).message);
  }
  render();
}
async function loadAgentVoice() {
  try {
    aiAgent.settings = await api.agentSettings();
    if (state.route.name === "ai-agent") render();
  } catch {
    // shown empty
  }
}
/** Saves one change to Selena's settings (admins); the screen updates at once and rolls back if it fails. */
/** The two names from Settings: with a woman's voice and with a man's voice. */
function agentNames(): { woman: string; man: string } {
  const st = aiAgent.settings?.settings;
  return { woman: st?.nameWoman || "Selena", man: st?.nameMan || "Steven" };
}
/** The draft of a name being typed in Settings (saved with its Save button). */
const nameDraft: { woman?: string; man?: string } = {};

async function saveSelena(change: Partial<SelenaSettings>, said?: string) {
  const body = aiAgent.settings;
  if (!body) return;
  const before = body.settings;
  body.settings = { ...before, ...change };
  render();
  try {
    aiAgent.settings = await api.saveAgentSettings(change);
    if (said) toast(said);
    if ("nameWoman" in change || "nameMan" in change || "persona" in change || "voice" in change) void refresh(); // the name shown everywhere
  } catch (e) {
    body.settings = before;
    toast((e as Error).message);
  }
  render();
}
async function loadAgentChat() {
  try {
    const r = await api.agentChat();
    const grew = r.messages.length !== aiAgent.chat.length;
    aiAgent.chat = r.messages;
    aiAgent.thinking = r.thinking;
    if (r.mode) aiAgent.mode = r.mode;
    aiAgent.chatLoaded = true;
    if (state.route.name === "ai-agent") {
      render();
      if (grew) requestAnimationFrame(() => {
        const tl = document.querySelector<HTMLElement>(".agent-chat .timeline");
        if (tl) tl.scrollTop = tl.scrollHeight;
      });
    }
  } catch {
    // shown as empty
  }
}
async function loadAiCalls() {
  try {
    aiAgent.calls = (await api.aiCalls()).calls;
    aiAgent.loaded = true;
    if (state.route.name === "ai-agent") render();
  } catch {
    // shown as empty
  }
}
const aiLive = () => state.live.filter((c) => c.ai && (c.status === "ringing" || c.status === "active"));

/** The AI agent's row under Team chat (sidebar on computers). */
/** 5.11.2 "let me text the agent when I click on his name": his chat, with the box ready to type in. */
function openAgentChat() {
  aiAgent.tab = "chat";
  go({ name: "ai-agent" });
  requestAnimationFrame(() => requestAnimationFrame(() => (document.getElementById("draft") as HTMLTextAreaElement | null)?.focus({ preventScroll: true })));
}
function aiAgentRow(variant: "rail" | "page") {
  const live = aiLive();
  const status = live.length ? `On a call${live.length > 1 ? ` (${live.length})` : ""} · ${nameFor(live[0].external_number, live[0].contact_name)}` : aiAgent.thinking ? "Thinking…" : "Calls, books, arranges";
  // One frame per row, like the teammates below: the name is a plain button inside the row (not a framed rail item),
  // so the call button keeps its place on the right.
  const row = h("button", { class: `ai-page-row rail-ai${live.length ? " busy" : ""}`, "aria-current": String(state.route.name === "ai-agent"),
    title: `Text ${aiName()}, your AI assistant: calls, books and arranges for you`, onclick: () => openAgentChat() },
    h("span", { class: "av-wrap" }, h("span", { class: "ai-av sm" }, icon("sparkle", "sm")), h("span", { class: "presence on", "aria-hidden": "true" })),
    h("span", { class: "rail-label two" }, h("span", {}, `${aiName()} – AI assistant`), h("small", { class: live.length ? "ai-busy" : "" }, status)),
    state.agentUnread && !seeingSelenaChat() ? h("span", { class: "rail-badge", "aria-label": `${state.agentUnread} new from ${aiName()}` }, state.agentUnread > 99 ? "99+" : String(state.agentUnread)) : null);
  // Call her (like a teammate): the phone icon shows on hover (always on phones).
  const callBtn = state.user?.canCall && !(voice.call && !isEnded(voice.call))
    ? h("button", { class: "mate-call", type: "button", "aria-label": `Call ${aiName()}`, title: `Call ${aiName()}`, onclick: (e: MouseEvent) => (e.stopPropagation(), void callSelena()) }, icon("phone", "sm"))
    : null;
  return h("div", { class: `rail-mate ai-mate${variant === "page" ? " on-page" : ""}${state.route.name === "ai-agent" ? " current" : ""}`, role: "listitem" }, row, callBtn);
}

async function startAiAgentCall() {
  const f = aiAgent.form;
  const to = toE164(f.to);
  if (!to) return void ((aiAgent.error = "Enter their full phone number"), render());
  if (!f.ask.trim()) return void ((aiAgent.error = "Say what the AI should find out"), render());
  aiAgent.busy = true;
  aiAgent.error = "";
  render();
  try {
    await api.aiCall(to, { hotel: f.who.trim() || undefined, request: f.ask.trim(), details: f.details.trim() || undefined, book: f.book, ext: splitExtension(f.to).ext ?? undefined });
    aiAgent.form = { to: "", who: "", ask: "", details: "", book: false };
    toast(`${aiName()} is calling`);
    void refresh();
    void loadAiCalls();
  } catch (e) {
    aiAgent.error = (e as Error).message;
  }
  aiAgent.busy = false;
  render();
}

/** You're looking at your chat with Selena right now (on screen, app in front). */
function seeingSelenaChat(): boolean {
  return state.route.name === "ai-agent" && aiAgent.tab === "chat" && document.visibilityState === "visible";
}
function markAgentRead() {
  state.agentUnread = 0;
  syncBadge();
  void api.agentRead().catch(() => {});
}

function aiAgentView() {
  const tabs = h("div", { class: "seg ai-tabs", role: "tablist" },
    ...(["chat", "calls", "voice"] as const).map((t) => h("button", { role: "tab", "aria-selected": String(aiAgent.tab === t), "aria-pressed": String(aiAgent.tab === t), onclick: () => {
      aiAgent.tab = t;
      if (t === "chat" && state.agentUnread) markAgentRead();
      void (t === "calls" ? loadAiCalls() : t === "voice" ? loadAgentVoice() : loadAgentChat());
      render();
    } }, t === "chat" ? "Chat" : t === "calls" ? "Calls" : "Settings")));
  const head = h("header", { class: "thread-head" },
    backButton({ name: "chat" }),
    h("span", { class: "ai-av lg" }, icon("sparkle")),
    // Phones: the Business / Personal switch sits right next to her name (19.10.18; it was a bar of its own).
    h("div", { class: "who" }, h("div", { class: "title" }, aiName(), selenaModeInline()),
      // 13.10.21: the status shows in full; "AI assistant · " only where there's room (phones: just the status).
      h("div", { class: "number" }, h("span", { class: "hide-narrow" }, "AI assistant · "), aiAgent.thinking ? "Thinking…" : aiLive().length ? "On a call" : "Calls, books, arranges")),
    // The call button sits in the top row next to her name (on phones the tabs get their own row underneath).
    state.user?.canCall && !(voice.call && !isEnded(voice.call))
      ? h("button", { class: "btn live sm selena-call-btn", "aria-label": `Call ${aiName()}`, title: `Call ${aiName()}`, onclick: () => void callSelena() }, icon("phone", "sm"), h("span", { class: "hide-narrow" }, "Call"))
      : null,
    h("div", { class: "head-actions" }, tabs));
  return aiAgent.tab === "chat" ? agentChatView(head) : aiAgent.tab === "voice" ? agentVoiceView(head) : aiCallsView(head);
}

/** Selena → Settings: language, voice (every Kokoro voice with a sample, plus Azure), speed and what she may do. */
/**
 * 1.10.30 "a full suite of settings for each Kernel browser": Kernel's own options, shown under Browser when Kernel
 * runs the website tasks. "Default" leaves the option out (Kernel decides). Not offered: stealth mode (it switches on
 * Kernel's CAPTCHA solver), its stealth / residential proxies and fingerprint overrides (they exist to get past
 * sites' bot checks).
 */
/**
 * 1.10.30 "did you also load Browserbase's settings?": Browserbase's own options, shown under Browser when Browserbase
 * runs the website tasks ("Default" leaves it to Browserbase). Never offered: automatic CAPTCHA solving (always off),
 * its proxies and "advanced stealth".
 */
function browserbaseOptionsRows(o: BrowserbaseOptions, admin: boolean, chip: (label: string, active: boolean, run: () => void) => HTMLElement) {
  const set = (change: Partial<BrowserbaseOptions>, said: string) => {
    const next: BrowserbaseOptions = { ...o, ...change };
    for (const key of Object.keys(next) as (keyof BrowserbaseOptions)[]) if (next[key] === undefined) delete next[key];
    void saveSelena({ browserbase: next }, `Browserbase: ${said}`);
  };
  const row = (title: string, sub: string, chips: HTMLElement[]) =>
    h("div", { class: "item" }, h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, title), h("div", { class: "sub" }, sub)), h("div", { class: "chips" }, ...chips));
  const pick = <T extends string | number | boolean>(options: [T | undefined, string][], cur: T | undefined, apply: (v: T | undefined) => Partial<BrowserbaseOptions>, what: string) =>
    options.map(([v, label]) => chip(label, cur === v, () => admin && set(apply(v), `${what} ${label.toLowerCase()}`)));
  return h("div", { class: "kernel-options" },
    h("div", { class: "group-title" }, "Browserbase browser options"),
    h("p", { class: "muted ready-note", style: "margin:2px 16px 10px;font-size:13px" }, "✓ Ready as it is: Joy Phone already uses the recommended settings (sessions up to 25 minutes, ads blocked, CAPTCHA solving off). Change something only if you need to."),
    row("Approved bot identity", o.verified
      ? "Verified mode: Browserbase's identity, recognised by its bot-protection partners, so verified sessions see far fewer checks. Needs Browserbase's Scale plan. Sites still decide whether to let it in."
      : "Off. Verified mode (Scale plan) makes Steven a browser Browserbase's bot-protection partners recognise. (The BROWSERBASE_VERIFIED secret on joy-browser turns it on too.)",
      pick<boolean>([[undefined, "Default (off)"], [true, "Verified"]], o.verified, (v) => ({ verified: v }), "identity")),
    h("p", { class: "muted", style: "margin:2px 16px 10px;font-size:13px" }, "Recommended = what Joy Phone uses when you leave it. Automatic CAPTCHA solving is always off."),
    row("Region", "Where the browser runs (Browserbase's default: US West).",
      pick<NonNullable<BrowserbaseOptions["region"]>>([[undefined, "Default"], ["us-east-1", "US East"], ["eu-central-1", "Europe"], ["ap-southeast-1", "Asia"]], o.region, (v) => ({ region: v }), "region")),
    row("Longest session", "A task's browser closes after this long at most. Website tasks stop at 20 minutes, so 25 is enough without paying for leftovers.",
      pick<number>([[undefined, "Recommended (25 min)"], [10, "10 min"], [30, "30 min"], [60, "1 hour"]], o.timeoutMinutes, (v) => ({ timeoutMinutes: v }), "longest session")),
    row("Keep alive", "The session survives a dropped connection (Hobby plan and above). Steven still closes it when the task ends.",
      pick<boolean>([[undefined, "Default"], [true, "On"]], o.keepAlive, (v) => ({ keepAlive: v }), "keep alive")),
    row("Block ads", "Faster, cleaner pages with fewer pop-ups in the way.", pick<boolean>([[undefined, "Recommended (block)"], [false, "Don't block"]], o.blockAds, (v) => ({ blockAds: v }), "ads")),
    row("Recording", "Keeps a replay of each session in your Browserbase dashboard (Browserbase's default: on).",
      pick<boolean>([[undefined, "Default (on)"], [false, "Off"]], o.recordSession, (v) => ({ recordSession: v }), "recording")),
    row("Logs", "Keeps each session's logs in your Browserbase dashboard (Browserbase's default: on).",
      pick<boolean>([[undefined, "Default (on)"], [false, "Off"]], o.logSession, (v) => ({ logSession: v }), "logs")),
    row("Operating system", "What the browser presents itself as (Verified sessions: Windows by default).",
      pick<NonNullable<BrowserbaseOptions["os"]>>([[undefined, "Default"], ["windows", "Windows"], ["mac", "Mac"], ["linux", "Linux"], ["mobile", "Phone"], ["tablet", "Tablet"]], o.os, (v) => ({ os: v }), "operating system")),
    row("Window size", "The page size sites see (phone size shows mobile pages).",
      pick<NonNullable<BrowserbaseOptions["viewport"]>>([[undefined, "Default"], ["1440x900", "1440×900"], ["1280x800", "1280×800"], ["1024x768", "1024×768"], ["390x844", "Phone"]], o.viewport, (v) => ({ viewport: v }), "window size")),
    row("Saved sign-ins", "A Browserbase context id (from your Browserbase dashboard or API), to start each task signed in. Leave empty for a fresh browser.",
      [h("input", { id: "bb-context", class: "field", type: "text", value: o.contextId ?? "", placeholder: "None", "aria-label": "Context id", disabled: !admin, autocomplete: "off", spellcheck: false,
        onchange: (e: Event) => {
          const v = (e.target as HTMLInputElement).value.trim();
          if (v && !/^[A-Za-z0-9-]{8,64}$/.test(v)) return void toast("That doesn't look like a Browserbase context id");
          set({ contextId: v || undefined }, `saved sign-ins ${v || "none"}`);
        } })]),
    o.contextId ? row("Save sign-ins back", "At the end of each task, save what changed (new sign-ins) to that context.",
      pick<boolean>([[undefined, "Default (don't)"], [true, "Save"]], o.persistContext, (v) => ({ persistContext: v }), "save to context")) : null);
}

function kernelOptionsRows(k: KernelOptions, admin: boolean, chip: (label: string, active: boolean, run: () => void) => HTMLElement) {
  const set = (change: Partial<KernelOptions>, said: string) => {
    const next: KernelOptions = { ...k, ...change };
    for (const key of Object.keys(next) as (keyof KernelOptions)[]) if (next[key] === undefined) delete next[key];
    // Memory sizes differ with and without a screen: a size that doesn't fit the new choice goes back to Kernel's default.
    if (next.memory && (next.headless ? !["1GiB", "2GiB", "4GiB"].includes(next.memory) : !["8GiB", "16GiB"].includes(next.memory))) delete next.memory;
    void saveSelena({ kernel: next }, `Kernel: ${said}`);
  };
  const row = (title: string, sub: string, chips: HTMLElement[]) =>
    h("div", { class: "item" }, h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, title), h("div", { class: "sub" }, sub)), h("div", { class: "chips" }, ...chips));
  const pick = <T extends string | number | boolean>(options: [T | undefined, string][], cur: T | undefined, apply: (v: T | undefined) => Partial<KernelOptions>, what: string) =>
    options.map(([v, label]) => chip(label, cur === v, () => admin && set(apply(v), `${what} ${label.toLowerCase()}`)));
  const memOptions: [KernelOptions["memory"], string][] = k.headless ? [[undefined, "Default (1 GiB)"], ["2GiB", "2 GiB"], ["4GiB", "4 GiB"]] : [[undefined, "Default (8 GiB)"], ["16GiB", "16 GiB"]];
  const text = (id: string, label: string, value: string | undefined, placeholder: string, apply: (v: string | undefined) => Partial<KernelOptions>, check: RegExp) =>
    h("input", { id, class: "field", type: "text", value: value ?? "", placeholder, "aria-label": label, disabled: !admin, autocomplete: "off", spellcheck: false,
      onchange: (e: Event) => {
        const v = (e.target as HTMLInputElement).value.trim();
        if (v && !check.test(v)) return void toast(`That doesn't look like a ${label.toLowerCase()}`);
        set(apply(v || undefined), `${label.toLowerCase()} ${v || "default"}`);
      } });
  return h("div", { class: "kernel-options" },
    h("div", { class: "group-title" }, "Kernel browser options"),
    h("p", { class: "muted ready-note", style: "margin:2px 16px 10px;font-size:13px" }, "✓ Ready as it is: Kernel's own settings suit website tasks (a full browser with a live view, so you can take over). Change something only if you need to."),
    // 1.10.30 "the settings that make Kernel a registered, approved bot": Web Bot Auth, the signed identity that
    // Cloudflare, Vercel and other bot-protection services verify (each site still decides whether to let it in).
    row("Approved bot identity", k.identityExtension
      ? `Every Kernel browser loads "${k.identityExtension}", which signs its requests (Web Bot Auth) so sites can verify who it is. Sites still decide whether to let it in.`
      : "Off. To have sites verify Steven as a known bot (Web Bot Auth): ask Kernel (support@kernel.sh, Start-Up or Enterprise plan) to enable its approved \"Kernel Agent\" identity, or upload a signing extension built with your own key. Then type that extension's name here.",
      [text("kernel-identity", "Extension name", k.identityExtension, "Extension name", (v) => ({ identityExtension: v }), /^[A-Za-z0-9._-]{1,255}$/)]),
    h("p", { class: "muted", style: "margin:2px 16px 10px;font-size:13px" }, "Default = Kernel decides. Some need a Kernel plan (noted)."),
    row("Screen", k.headless ? "Headless: no screen of its own, lighter and cheaper. Steven's live view still works." : "With a screen (Kernel's default).",
      pick<boolean>([[undefined, "Default"], [true, "Headless"]], k.headless, (v) => ({ headless: v }), "screen")),
    row("Closes when idle", "How long it stays open with nothing happening (Kernel's default: 1 minute). Longer costs more if a task stalls.",
      pick<number>([[undefined, "Default"], [5, "5 min"], [15, "15 min"], [60, "1 hour"]], k.idleMinutes, (v) => ({ idleMinutes: v }), "closes when idle after")),
    row("Region", "Where the browser runs. Picking one needs Kernel's Start-Up or Enterprise plan; on other plans leave it on Default (if not, Kernel runs without it and the task says so).",
      pick<NonNullable<KernelOptions["region"]>>([[undefined, "Default (US East)"], ["us-west", "US West"], ["eu-west", "Europe"], ["ap-southeast", "Asia"]], k.region, (v) => ({ region: v }), "region")),
    row("Time zone", "The browser's clock, for sites that show local times or prices (e.g. Asia/Jerusalem).",
      [text("kernel-tz", "Time zone", k.timezone, "Default", (v) => ({ timezone: v }), /^[A-Za-z]+(?:\/[A-Za-z0-9_+-]+){1,2}$/)]),
    row("Language", "The browser's language (e.g. en-US, he-IL).",
      [text("kernel-locale", "Language", k.locale, "Default", (v) => ({ locale: v }), /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/)]),
    row("Window size", "The page size sites see (phone size shows mobile pages).",
      pick<NonNullable<KernelOptions["viewport"]>>([[undefined, "Default (1920×1080)"], ["1440x900", "1440×900"], ["1280x800", "1280×800"], ["1024x768", "1024×768"], ["390x844", "Phone"]], k.viewport, (v) => ({ viewport: v }), "window size")),
    row("Memory", k.headless ? "Headless browsers: 1, 2 or 4 GiB." : "Browsers with a screen: 8 or 16 GiB.",
      pick<NonNullable<KernelOptions["memory"]>>(memOptions, k.memory, (v) => ({ memory: v }), "memory")),
    row("Graphics card", "Faster for heavy pages. Needs Kernel's Start-Up plan, a screen and US East.",
      pick<boolean>([[undefined, "Default (off)"], [true, "On"]], k.gpu, (v) => ({ gpu: v }), "graphics card")),
    row("Hide address bar", "Kernel's own live view shows the page only (no address bar or tabs).",
      pick<boolean>([[undefined, "Default (shown)"], [true, "Hide"]], k.kiosk, (v) => ({ kiosk: v }), "address bar")),
    row("Saved profile", "Load a profile you saved in Kernel (your sign-ins), by its name. Leave empty for a fresh browser each time.",
      [text("kernel-profile", "Profile name", k.profile, "None", (v) => ({ profile: v }), /^[A-Za-z0-9._-]{1,255}$/)]),
    k.profile ? row("Save sign-ins back", "At the end of each task, save what changed (new sign-ins) to that profile.",
      pick<boolean>([[undefined, "Default (don't)"], [true, "Save"]], k.saveProfile, (v) => ({ saveProfile: v }), "save to profile")) : null);
}

function agentVoiceView(head: Node) {
  const body = aiAgent.settings;
  if (!body) void loadAgentVoice();
  const admin = isAdmin();
  if (!body) return screen(head, h("div", { class: "page" }, h("div", { class: "page-inner ai-page" }, h("div", { class: "empty" }, h("strong", {}, "Loading…")))));
  const st = body.settings;
  const lang = body.languages.find((l) => l.key === st.language) ?? body.languages[0];
  // Voices made for her language first ("All voices" shows every language and dialect).
  const fits = (v: (typeof body.voices)[number]) => v.lang === st.language;
  // Telnyx has hundreds of Ultra voices: a search box, "picks for phone calls" first, and long groups folded.
  const find = aiAgent.moreVoices ? aiAgent.voiceQuery.trim().toLowerCase() : "";
  // Her original voices (the ones she started with) first; Telnyx's hundreds of others only after "More voices".
  const original = (v: (typeof body.voices)[number]) => v.family === "Azure" || v.family === "Kokoro" || !v.family;
  const extra = body.voices.filter((v) => !original(v));
  // Only voices that match who's answering: women's for Selena, men's for Steven (a man never says "Selena").
  const want = st.persona === "steven" ? "man" : "woman";
  // Most human-like first (published ratings, see humanScore on the server); robotic-sounding ones sink to the bottom.
  const human = (v: (typeof body.voices)[number]) => v.human ?? 50;
  // The short list also gets Telnyx's 4 most human-like voices for her language (Ultra / HD), so the best are never hidden.
  const best = new Set(extra.filter((v) => fits(v) && v.gender === want && human(v) >= 85).sort((a, b) => human(b) - human(a)).slice(0, 4).map((v) => v.key));
  // 12.10.30: ♥ voices first, in their own Favorites list (whatever the filters), then the rest without them.
  const favKeys = st.favoriteVoices ?? [];
  const favs = favKeys.map((k) => body.voices.find((v) => v.key === k)).filter((v): v is (typeof body.voices)[number] => !!v && (v.gender === want || v.key === st.voice))
    .filter((v) => !find || `${v.name} ${v.sub}`.toLowerCase().includes(find));
  const isFav = (k: string) => favKeys.includes(k);
  const shown = (aiAgent.moreVoices ? (aiAgent.allVoices || find ? body.voices : body.voices.filter(fits)) : body.voices.filter((v) => (fits(v) && original(v)) || best.has(v.key) || v.key === st.voice))
    .filter((v) => !isFav(v.key))
    .filter((v) => v.gender === want || v.key === st.voice)
    .filter((v) => !find || `${v.name} ${v.sub}`.toLowerCase().includes(find) || v.key === st.voice)
    .sort((a, b) => Number(b.key === st.voice) - Number(a.key === st.voice) || human(b) - human(a)); // her current voice always shows
  const groups = new Map<string, typeof shown>();
  for (const v of shown) {
    // One list per language, ranked by how human the voices sound (not split by maker), so the best are always on top.
    const label = `${body.languages.find((l) => l.key === v.lang)?.label ?? v.lang} · ${v.gender === "woman" ? `Women · call as ${agentNames().woman}` : `Men · call as ${agentNames().man}`} · most human-like first`;
    groups.set(label, [...(groups.get(label) ?? []), v]);
  }
  // The group with the most human-like voice first (each group is already most human-like first).
  // Her own language first (under "All voices"), then by how human each group's best voice sounds.
  const ordered = [...groups].sort(([, a], [, b]) => Number(fits(b[0])) - Number(fits(a[0])) || Math.max(...b.map(human)) - Math.max(...a.map(human)));
  // "May sound robotic" only where a more human-like voice is on offer for the same language and gender.
  const bestFor = new Map<string, number>();
  for (const v of body.voices) bestFor.set(`${v.lang}|${v.gender}`, Math.max(bestFor.get(`${v.lang}|${v.gender}`) ?? 0, human(v)));
  const FOLD = aiAgent.moreVoices ? 8 : 100;
  const voiceRow = (v: (typeof body.voices)[number]) => {
    const on = st.voice === v.key;
    return h("div", { class: `item agent-voice${on ? " on" : ""}` },
      h("button", { class: "agent-voice-pick", type: "button", role: "radio", "aria-checked": String(on), disabled: !admin && !on,
        title: admin ? `Use ${v.name} on ${aiName()}'s calls` : `Only admins can change ${aiName()}'s voice`,
        onclick: () => admin && !on && void saveSelena({ voice: v.key }, `${aiName()} now sounds like ${v.name}`) },
        h("span", { class: "radio-dot", "aria-hidden": "true" }),
        h("span", { class: "grow" }, h("b", {}, v.name),
          h("small", {}, human(v) >= 80 ? h("span", { class: "voice-tag human" }, "Most human-like") : human(v) < 50 && (bestFor.get(`${v.lang}|${v.gender}`) ?? 0) >= 60 ? h("span", { class: "voice-tag robotic" }, "May sound robotic") : null, v.sub))),
      playButton(`agent-voice-${v.key}`, "Play", () => (v.sample ? v.sample : api.agentVoiceSample(v.key))),
      admin ? favButton(v.name, isFav(v.key), () => void saveSelena({ favoriteVoices: isFav(v.key) ? favKeys.filter((k) => k !== v.key) : [...favKeys, v.key] },
        isFav(v.key) ? `${v.name} is no longer a favorite` : `${v.name} added to your favorites`)) : null,
      // Remove a voice you don't like: it's gone from the list for everyone (restorable at the bottom).
      admin && !on ? h("button", { class: "icon-btn sm voice-remove", type: "button", "aria-label": `Remove ${v.name}`, title: `Remove ${v.name} from the list`,
        onclick: () => void saveSelena({ hiddenVoices: [...st.hiddenVoices, v.key], favoriteVoices: favKeys.filter((k) => k !== v.key) }, `${v.name} removed`) }, icon("close", "sm"))
        : admin ? h("span", { class: "voice-remove-gap", "aria-hidden": "true" }) : null); // her current voice can't be removed
  };
  const chip = (label: string, active: boolean, run: () => void) =>
    h("button", { class: active ? "chip on" : "chip", type: "button", "aria-pressed": String(active), disabled: !admin && !active, onclick: run }, label);
  return screen(head,
    h("div", { class: "page" }, h("div", { class: "page-inner ai-page selena-settings" },
      // Business and personal never share a screen: Joy Travel's Selena, or your own personal assistant.
      isAdmin() && state.personalLine ? h("div", { class: "seg selena-side", role: "tablist", "aria-label": "Whose settings" },
        ...([["business", "Joy Travel", "For the whole team"], ["personal", personalLineName(), "Just you · private"]] as const).map(([k, label, sub]) =>
          h("button", { type: "button", role: "tab", class: settingsSide() === k ? "on" : "", "aria-selected": String(settingsSide() === k),
            onclick: () => ((aiAgent.side = k), render(), document.querySelector(".page")?.scrollTo({ top: 0 })) },
            icon(k === "personal" ? "lock" : "phone", "sm"), h("span", {}, h("b", {}, label), h("small", {}, sub))))) : null,
      settingsSide() === "personal" ? personalSection() : null,
      ...(settingsSide() === "personal" ? [] : [
      // The same order on both sides: what she can say first, then what she may do, then how she sounds.
      h("p", { class: "muted agent-voice-intro" }, admin ? "Joy Travel's assistant for the whole team: on calls, when teammates call, and in chat." : "Only admins can change these settings. You can still play the voices."),
      h("div", { class: "group-title" }, "Who answers and calls"),
      personaGroup(st.persona, (p) => admin && p !== "same" && void saveSelena({ persona: p }, p === "steven" ? `${agentNames().man} answers and calls for Joy Travel now (a man's voice)` : `${agentNames().woman} answers and calls for Joy Travel now (a woman's voice)`), !admin),
      admin ? agentNamesGroup() : null,
      h("div", { class: "group-title" }, `What ${st.persona === "steven" ? "he" : "she"} can say`),
      h("div", { class: "group selena-rules" },
        h("label", { class: "rules-label", for: "selena-about" }, h("b", {}, `What ${st.persona === "steven" ? "he" : "she"} may use about Joy Travel`), h("small", {}, "One per line. Shared only when a call needs it.")),
        h("textarea", { id: "selena-about", class: "field", rows: "4", maxlength: "3000", disabled: !admin, value: aiAgent.rulesDraft?.about ?? st.about,
          placeholder: "Office: 1428 Ocean Ave, Santa Monica, CA\nHours: Monday to Friday, 9 AM to 6 PM\nWebsite: joytravelinc.com · Email: hello@joytravelinc.com\nWe're an IATA-accredited travel agency",
          oninput: (e: Event) => ((aiAgent.rulesDraft ??= { canSay: st.canSay, cannotSay: st.cannotSay, about: st.about }).about = (e.target as HTMLTextAreaElement).value, paintRulesSave()) }),
        h("label", { class: "rules-label", for: "selena-can" }, h("b", {}, `${st.persona === "steven" ? "He" : "She"} may say or share`), h("small", {}, "One per line. For example: our office hours, that we're IATA accredited, the client's first name.")),
        h("textarea", { id: "selena-can", class: "field", rows: "4", maxlength: "3000", disabled: !admin, value: aiAgent.rulesDraft?.canSay ?? st.canSay,
          placeholder: "Our office hours are 9 AM to 6 PM, Monday to Friday\nWe're an IATA-accredited travel agency\nThe client's first name and travel dates",
          oninput: (e: Event) => ((aiAgent.rulesDraft ??= { canSay: st.canSay, cannotSay: st.cannotSay, about: st.about }).canSay = (e.target as HTMLTextAreaElement).value, paintRulesSave()) }),
        h("label", { class: "rules-label", for: "selena-cannot" }, h("b", {}, `${st.persona === "steven" ? "He" : "She"} must never say or share`), h("small", {}, "One per line. For example: client phone numbers, our commission, prices we haven't confirmed.")),
        h("textarea", { id: "selena-cannot", class: "field", rows: "4", maxlength: "3000", disabled: !admin, value: aiAgent.rulesDraft?.cannotSay ?? st.cannotSay,
          placeholder: "Client last names, phone numbers or email addresses\nOur commission or net rates\nAny price or promise a teammate hasn't confirmed",
          oninput: (e: Event) => ((aiAgent.rulesDraft ??= { canSay: st.canSay, cannotSay: st.cannotSay, about: st.about }).cannotSay = (e.target as HTMLTextAreaElement).value, paintRulesSave()) }),
        admin ? h("div", { class: "rules-foot" },
          h("small", {}, pron("Used on her calls, when teammates call her, and in chat.", st.persona === "steven")),
          h("button", { id: "selena-rules-save", class: "btn sm", type: "button", disabled: !rulesChanged(), onclick: async () => {
            const d = aiAgent.rulesDraft;
            if (!d) return;
            await saveSelena({ canSay: d.canSay, cannotSay: d.cannotSay, about: d.about }, `${st.persona === "steven" ? agentNames().man : agentNames().woman}'s details are saved`);
            aiAgent.rulesDraft = null;
            render();
          } }, "Save")) : null),
      admin && body.chatModel ? h("p", { class: "muted agent-voice-intro chat-model-line" }, "Chat answers come from ", h("code", {}, body.chatModel),
        body.smartModel ? ", checks and Claude bookings from " : null, body.smartModel ? h("code", {}, body.smartModel) : null,
        body.smartModel && !/claude/i.test(body.smartModel) ? " (Claude isn't available on this Telnyx account)" : null, ".") : null,
      admin ? tryModelsGroup() : null,
      // 13.10.20 "upgrade the calling brain": which AI model thinks on the calls.
      body.phoneModels?.length ? h("div", { class: "group-title" }, "Phone brain") : null,
      body.phoneModels?.length ? h("div", { class: "group" },
        h("div", { class: "item" },
          h("div", { class: "grow" },
            h("div", { style: "font-weight:600" }, body.phoneModels.find((m) => m.id === st.phoneModel)?.label ?? "Claude Haiku 4.5"),
            h("div", { class: "sub" }, `${body.phoneModels.find((m) => m.id === st.phoneModel)?.note ?? ""}${body.phoneModelState === "refused" ? " ⚠ Telnyx didn't accept it yet: calls use the previous model." : ""} If it ever fails on a call, Kimi K2.6 takes over by itself.`)),
          h("div", { class: "chips" }, ...body.phoneModels.map((m) => chip(m.label, m.id === st.phoneModel, () => admin && void saveSelena({ phoneModel: m.id }, `${aiName()}'s calls now use ${m.label}`)))))) : null,
      // 9.10.21 "put those in settings so I can test": which brain texts and which drives website bookings.
      h("div", { class: "group-title" }, "Chat and booking brains"),
      h("div", { class: "group" },
        h("div", { class: "item" },
          h("div", { class: "grow" },
            h("div", { style: "font-weight:600" }, "Texting with you"),
            h("div", { class: "sub" }, st.chatBrain === "deepseek"
              ? "DeepSeek only: answers in about a second. No second check."
              : "Hybrid: DeepSeek answers in about a second; Claude checks any call, booking or task before it starts (those wait 4–9 s) and looks over answers afterwards (\"Sorry, I stand corrected…\" if something was wrong).")),
          h("div", { class: "chips" },
            chip("DeepSeek", st.chatBrain === "deepseek", () => admin && void saveSelena({ chatBrain: "deepseek" }, "Texting: DeepSeek only")),
            chip("Hybrid", st.chatBrain !== "deepseek", () => admin && void saveSelena({ chatBrain: "hybrid" }, "Texting: DeepSeek with Claude checking")))),
        h("div", { class: "item" },
          h("div", { class: "grow" },
            h("div", { style: "font-weight:600" }, "Website bookings"),
            h("div", { class: "sub" }, st.webBrain === "deepseek"
              ? "DeepSeek: quicker (about 2–3 min a booking), about 10¢ each."
              : "Claude: better at reading pages and choosing (a few minutes longer, about 30–40¢ a booking). DeepSeek takes any step Claude can't.")),
          h("div", { class: "chips" },
            chip("Claude", st.webBrain !== "deepseek", () => admin && void saveSelena({ webBrain: "claude" }, "Website bookings: Claude")),
            chip("DeepSeek", st.webBrain === "deepseek", () => admin && void saveSelena({ webBrain: "deepseek" }, "Website bookings: DeepSeek"))))),
      body.hearingModels?.length ? h("div", { class: "group-title" }, "Hearing") : null,
      body.hearingModels?.length ? h("div", { class: "group" },
        h("div", { class: "item" },
          h("div", { class: "grow" },
            h("div", { style: "font-weight:600" }, body.hearingModels.find((m) => m.id === st.hearing)?.label ?? "AssemblyAI Universal-3.5 Pro"),
            h("div", { class: "sub" }, `${body.hearingModels.find((m) => m.id === st.hearing)?.note ?? ""}${body.hearingState === "flux-instead" ? " ⚠ Telnyx didn't accept it yet: calls use Deepgram Flux." : ""} Each call also tells it the names, emails and codes it's about.`)),
          h("div", { class: "chips" }, ...body.hearingModels.map((m) => chip(m.label.replace(/^AssemblyAI /, ""), m.id === st.hearing, () => admin && void saveSelena({ hearing: m.id }, `${aiName()} now hears calls with ${m.label}`)))))) : null,
      h("div", { class: "group-title" }, `What ${st.persona === "steven" ? "he" : "she"} may do on calls`),
      h("div", { class: "group" },
        switchRow("selena-book", "Book and request things", pron("Make reservations and appointments and pass on special requests when asked. Off: she only gathers information.", st.persona === "steven"), st.allowBooking, (v) => admin && void saveSelena({ allowBooking: v })),
        switchRow("selena-vm", "Leave a voicemail", pron("If nobody answers and it goes to voicemail, she waits for the beep and leaves a short message with the callback number (said twice). Off: she hangs up.", st.persona === "steven"), st.leaveVoicemail, (v) => admin && void saveSelena({ leaveVoicemail: v })),
        switchRow("selena-web", "Search the web", pron("In chat she can look things up online: a business's phone number, today's prices, hours and policies, and links to where she found them.", st.persona === "steven"), st.webBrowsing, (v) => admin && void saveSelena({ webBrowsing: v })),
        st.webBrowsing ? switchRow("selena-book-web", "Book on websites", pron("Ask her in chat to book a rental car (or similar) and she does it on the company's website in her own browser, then posts the result with a screenshot. Never with a card: if a site needs one, she stops and tells you.", st.persona === "steven") + (body.browser === "down" ? pron(" ⚠ Her browser server isn't answering right now.", st.persona === "steven") : body.browser === "missing" ? pron(" ⚠ Her browser server isn't set up yet.", st.persona === "steven") : ""), st.webBooking, (v) => admin && void saveSelena({ webBooking: v })) : null,
        // 6.10.29 "testing Browserbase and Kernel, an option to compare them": which browser runs website tasks.
        st.webBrowsing && st.webBooking ? (() => {
          const has = body.browsers ?? null;
          const choice = st.webBrowser ?? "auto";
          const opts: [NonNullable<typeof st.webBrowser>, string, boolean | null][] = [
            ["auto", "Automatic", null], ["browserbase", "Browserbase", has ? has.browserbase : null], ["kernel", "Kernel", has ? has.kernel : null],
            ["steel", "Steel", has ? has.steel : null], ["browseruse", "Browser Use", has ? !!has.browseruse : null], ["own", "Joy Phone's own", null]];
          const setUp = has ? opts.filter(([, , ok]) => ok).map(([, n]) => n) : [];
          const label = opts.find(([k]) => k === choice)?.[1] ?? "Automatic";
          // 1.11.1: Automatic picks the one that has done best (done most often, then quickest), per site when it can.
          const rank = (body.browserRank ?? []).filter((r) => r.runs > 0);
          const nm = (b: string) => opts.find(([k]) => k === b)?.[1] ?? b;
          const sub = choice === "auto"
            ? rank.length
              ? `Picks the browser that has done best: done most often, then quickest (for a site it has history on, that site's results). Right now: ${(body.browserRank ?? []).map((r) => r.runs ? `${nm(r.browser)} (${r.done} of ${r.runs} done${r.medianMs != null ? `, typically ${Math.floor(r.medianMs / 60_000)}:${String(Math.round((r.medianMs % 60_000) / 1000)).padStart(2, "0")}` : ""})` : `${nm(r.browser)} (not tried yet)`).join(", then ")}. It learns from every website task and from Compare browsers.`
              : `Picks the browser that does best (done most often, then quickest). It learns from every website task and from Compare browsers; until then: ${setUp.length ? setUp.join(", then ") + ", then Joy Phone's own" : "Joy Phone's own (no hosted browser has its key yet)"}.`
            : choice === "own" ? "Joy Phone's own browser (on Fly), as before."
            : `${label} runs every website task, and the result says which browser it used, to compare them.${choice === "steel" ? " Ready as it is: sessions up to 25 minutes (Steel's own default, 5, cut tasks off), CAPTCHA solving off." : choice === "browseruse" ? " Ready as it is: CAPTCHA solving off, no proxy, sessions up to 30 minutes, the same screen size as Joy Phone's own." : ""}${has && !has[choice] ? ` ⚠ ${label} isn't set up yet: add its key on joy-browser in the Fly dashboard (Secrets). Until then, Joy Phone's own runs them.` : ""}`;
          return h("div", { class: "item" },
            h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Browser"), h("div", { class: "sub" }, sub)),
            h("div", { class: "chips" }, ...opts.map(([k, n, ok]) => chip(ok === false ? `${n} (no key)` : n, choice === k, () => admin && void saveSelena({ webBrowser: k }, `Website tasks: ${n}`)))));
        })() : null,
        st.webBrowsing && st.webBooking && admin ? benchCard() : null,
        st.webBrowsing && st.webBooking && (st.webBrowser === "browserbase" || (st.webBrowser === "auto" && !!body.browsers?.browserbase)) ? browserbaseOptionsRows(st.browserbase ?? {}, admin, chip) : null,
        st.webBrowsing && st.webBooking && (st.webBrowser === "kernel" || (st.webBrowser === "auto" && !body.browsers?.browserbase && !!body.browsers?.kernel)) ? kernelOptionsRows(st.kernel ?? {}, admin, chip) : null,
        st.webBrowsing && st.webBooking ? switchRow("selena-identity", "Verified identity", pron("When a website task runs in her built-in browser (Joy Phone's own), it signs every request as Joy Travel's assistant (Web Bot Auth, the standard Cloudflare, Akamai and DataDome use to recognise AI agents), so sites can tell she's a known, well-behaved agent instead of an anonymous bot. Hosted browsers (Browserbase, Kernel, Steel, Browser Use) don't sign her requests. Her public key is at /.well-known/http-message-signatures-directory.", st.persona === "steven"), st.webIdentity, (v) => admin && void saveSelena({ webIdentity: v })) : null,
        st.webBrowsing && st.webBooking && admin ? identityCheckRow() : null,
        st.webBrowsing && st.webBooking ? switchRow("selena-fallback", "Blocked by a site? Phone them", pron("If a website stops her with a security check (and nobody took over her browser), she never tries to get past it: she finds the branch's number and phones to reserve with pay at the counter (in Practice mode she only asks the price).", st.persona === "steven"), st.webPhoneFallback, (v) => admin && void saveSelena({ webPhoneFallback: v })) : null,
        st.webBrowsing && st.webBooking ? switchRow("selena-handoff", "Hand me her browser when it needs a person", pron("A security check, a password or a code sent to your phone: she pauses and you get an alert. You open her live browser, do just that step, tap Done, and she carries on. Nobody in 10 minutes: she phones them instead (if that's on).", st.persona === "steven"), st.webHandoff, (v) => admin && void saveSelena({ webHandoff: v })) : null,
        st.webBrowsing && st.webBooking ? switchRow("selena-practice", "Practice mode", pron("She fills everything in and stops right before the final Reserve / Book button, so you can check her work with nothing really booked. Turn off when you trust her.", st.persona === "steven"), st.webPractice, (v) => admin && void saveSelena({ webPractice: v })) : null,
        switchRow("selena-callbacks", "Pick up callbacks", pron("When a business she called in the last 7 days calls back and nobody answers, she picks up (instead of voicemail), carries on from her call, and posts what they said.", st.persona === "steven"), st.answerCallbacks, (v) => admin && void saveSelena({ answerCallbacks: v })),
        switchRow("selena-ask", "Ask the team while they hold", pron("Put the business on hold with music and ask in chat when she needs a detail or a decision.", st.persona === "steven"), st.askTeam, (v) => admin && void saveSelena({ askTeam: v })),
        st.askTeam ? h("div", { class: "item" },
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Longest hold"), h("div", { class: "sub" }, pron("Then she carries on and says a colleague will follow up.", st.persona === "steven"))),
          h("div", { class: "chips" }, ...[2, 4, 6, 10].map((m) => chip(`${m} min`, st.holdMinutes === m, () => admin && void saveSelena({ holdMinutes: m }))))) : null),
      h("div", { class: "group-title" }, `How ${st.persona === "steven" ? "he talks" : "she talks"}`),
      h("div", { class: "group" },
        h("div", { class: "item selena-speed" },
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Talking speed"), h("div", { class: "sub" }, (st.speed < 0.98 ? "A little slower" : st.speed > 1.02 ? "A little faster" : "Normal") + (st.relaxed ? " · eased for relaxed speech" : ""))),
          h("input", { id: "selena-speed", type: "range", min: "0.85", max: "1.15", step: "0.05", value: String(st.speed), disabled: !admin, "aria-label": "Talking speed",
            onchange: (e: Event) => void saveSelena({ speed: Number((e.target as HTMLInputElement).value) }) })),
        switchRow("selena-relaxed", "Relaxed, natural speech", "Looser and less scripted: short sentences, easy fillers (\"sure, one sec\"), natural breathing pauses between thoughts, the odd \"um\", and a beat before numbers.", st.relaxed, (v) => admin && void saveSelena({ relaxed: v })),
        switchRow("selena-office", "Office background sound", pron("A soft office hum behind her voice, so it doesn't sound like an empty room.", st.persona === "steven"), st.officeSound, (v) => admin && void saveSelena({ officeSound: v })),
        // Telnyx's expressive mode works only with its Ultra voices: shown when one is chosen.
        body.voices.find((v) => v.key === st.voice)?.family === "Ultra"
          ? switchRow("selena-expressive", "Expressive voice (Ultra)", "Telnyx adds feeling from the conversation: warmth, short pauses, the odd small laugh. Try a test call: it can come out a little too cheerful for some business calls.", st.expressive, (v) => admin && void saveSelena({ expressive: v }))
          : null),
      h("div", { class: "group-title" }, `Language on ${st.persona === "steven" ? "his" : "her"} calls`),
      h("div", { class: "group selena-langs" }, h("div", { class: "chips" },
        ...body.languages.map((l) => chip(l.label, l.key === st.language, () => admin && void saveSelena({ language: l.key }, `${aiName()} will speak ${l.label} on her calls`))))),
      h("div", { class: "selena-voice-head" },
        h("div", { class: "group-title" }, `Voice${aiAgent.moreVoices && aiAgent.allVoices ? " · all languages" : ` · made for ${lang.label}`}`),
        aiAgent.moreVoices ? h("button", { class: "text-btn", type: "button", onclick: () => ((aiAgent.allVoices = !aiAgent.allVoices), render()) }, aiAgent.allVoices ? `Only ${lang.label}` : "All languages") : null),
      h("p", { class: "muted agent-voice-intro" }, `Tap Play to hear each voice, then tap a name to choose it. ${st.persona === "steven" ? `Men's voices for ${agentNames().man}.` : `Women's voices for ${agentNames().woman}.`} To switch, change "Who answers and calls" above.`),
      aiAgent.moreVoices ? h("input", { id: "voice-search", class: "field voice-search", type: "search", placeholder: "Search voices (e.g. casual, warm, Madison)", value: aiAgent.voiceQuery, autocomplete: "off",
        oninput: (e: Event) => ((aiAgent.voiceQuery = (e.target as HTMLInputElement).value), render()) }) : null,
      favs.length ? h("div", { class: "agent-voice-group voice-favorites" },
        h("div", { class: "voice-group-title" }, icon("heart", "sm"), ` Favorites (${favs.length})`),
        h("div", { class: "group" }, ...favs.map(voiceRow))) : null,
      !ordered.length && !favs.length ? h("p", { class: "muted agent-voice-intro" }, "No voices match that search.") : null,
      ...ordered.map(([label, list]) => {
        const open = aiAgent.openGroups.has(label) || !!find;
        const visible = open ? list : list.slice(0, FOLD);
        return h("div", { class: "agent-voice-group" },
          h("div", { class: "voice-group-title" }, `${label} (${list.length})`),
          h("div", { class: "group" }, ...visible.map(voiceRow),
            list.length > visible.length
              ? h("button", { class: "item voice-more", type: "button", onclick: () => (aiAgent.openGroups.add(label), render()) }, `Show ${list.length - visible.length} more`)
              : null));
      }),
      // The long list waits behind one button.
      extra.length || aiAgent.moreVoices
        ? h("button", { class: "btn quiet block voice-more-all", type: "button", onclick: () => ((aiAgent.moreVoices = !aiAgent.moreVoices), (aiAgent.voiceQuery = ""), render()) },
            aiAgent.moreVoices ? pron("Show only her original voices", st.persona === "steven") : `More voices (${extra.length})`)
        : null,
      body.hidden?.length && admin
        ? h("details", { class: "voice-hidden" },
            h("summary", {}, `Removed voices (${body.hidden.length})`),
            h("div", { class: "group" }, ...body.hidden.map((v) => h("div", { class: "item agent-voice" },
              h("span", { class: "grow" }, h("b", {}, v.name), h("small", {}, v.sub)),
              h("button", { class: "btn quiet sm", type: "button", onclick: () => void saveSelena({ hiddenVoices: st.hiddenVoices.filter((k) => k !== v.key) }, `${v.name} is back in the list`) }, "Restore")))))
        : null,
      h("p", { class: "muted agent-voice-intro" }, `${st.persona === "steven" ? agentNames().man : agentNames().woman} introduces ${st.persona === "steven" ? "himself" : "herself"} as Joy Travel's personal assistant. Whatever the rules say, card details are never given or taken, and if someone directly asks whether it's a real person, the answer is truthful.`)]))));
}
/** Who answers and calls: Selena (a woman's voice) or Steven (a man's voice); on the personal side also "same as Joy Travel". */
/** Admins: the names the AI agent goes by (on calls, in chat, everywhere in the app). */
function agentNamesGroup() {
  const n = agentNames();
  const field = (k: "woman" | "man", label: string) => h("label", { class: "agent-name-field" },
    h("span", {}, label),
    h("input", { class: "field", type: "text", maxlength: "24", autocomplete: "off", value: nameDraft[k] ?? n[k], placeholder: k === "woman" ? "Selena" : "Steven",
      oninput: (e: Event) => {
        nameDraft[k] = (e.target as HTMLInputElement).value;
        const btn = document.getElementById("agent-names-save") as HTMLButtonElement | null;
        if (btn) btn.disabled = !namesChanged();
      } }));
  const namesChanged = () => (nameDraft.woman !== undefined && nameDraft.woman.trim() !== n.woman) || (nameDraft.man !== undefined && nameDraft.man.trim() !== n.man);
  return h("div", { class: "group agent-names" },
    h("div", { class: "agent-names-row" }, field("woman", "Name with a woman's voice"), field("man", "Name with a man's voice")),
    h("div", { class: "rules-foot" },
      h("small", {}, "Said on calls (\"Hi, this is …\") and shown everywhere in the app: chat, Team chat, calls. Letters only, up to 24."),
      h("button", { id: "agent-names-save", class: "btn sm", type: "button", disabled: !namesChanged(), onclick: async () => {
        const woman = (nameDraft.woman ?? n.woman).trim(), man = (nameDraft.man ?? n.man).trim();
        if (!/^[\p{L}][\p{L}' -]{0,22}[\p{L}]?$/u.test(woman) || !/^[\p{L}][\p{L}' -]{0,22}[\p{L}]?$/u.test(man)) return toast("Names can only have letters (and spaces, ' or -)");
        delete nameDraft.woman;
        delete nameDraft.man;
        await saveSelena({ nameWoman: woman, nameMan: man }, `Saved: ${woman} with a woman's voice, ${man} with a man's voice`);
      } }, "Save names")));
}
function personaGroup(cur: "same" | "selena" | "steven", pick: (p: "same" | "selena" | "steven") => void, locked = false, same?: string) {
  const names = agentNames();
  const opt = (k: "same" | "selena" | "steven", title: string, sub: string) =>
    h("button", { type: "button", class: `persona-opt${cur === k ? " on" : ""}`, role: "radio", "aria-checked": String(cur === k), disabled: locked && cur !== k,
      onclick: () => cur !== k && pick(k) }, h("b", {}, title), h("small", {}, sub));
  return h("div", { class: "group persona-group", role: "radiogroup", "aria-label": "Who answers and calls" },
    ...(same ? [opt("same", "Same as Joy Travel", same)] : []),
    opt("selena", names.woman, `A woman's voice. Says "this is ${names.woman}".`),
    opt("steven", names.man, `A man's voice. Says "this is ${names.man}".`));
}
// ---- 13.10.30 Settings → Browser → Compare browsers ----
const BENCH_NAME: Record<BenchBrowser, string> = { own: "Joy Phone's own", browserbase: "Browserbase", kernel: "Kernel", steel: "Steel", browseruse: "Browser Use" };
// 6.11.1 Settings → Verified identity → Check my signature (the checks a site's bot protection makes, on the live address).
const idUi = { checks: null as { label: string; ok: boolean; detail: string }[] | null, busy: false };
function identityCheckRow() {
  const go = async () => {
    idUi.busy = true;
    render();
    try {
      idUi.checks = (await api.identityCheck()).checks;
    } catch (e) {
      toast((e as Error).message);
    }
    idUi.busy = false;
    render();
  };
  const c = idUi.checks;
  const allOk = !!c && c.every((x) => x.ok);
  return h("div", { class: "item id-check" },
    h("div", { class: "grow" },
      h("div", { style: "font-weight:600" }, "Check my signature"),
      h("div", { class: "sub" }, !c ? "Checks her public key and a signed request the way Cloudflare does, on the live address." : allOk ? "✓ All good: sites that check Web Bot Auth can verify her." : "Something needs fixing (see below).")),
    h("button", { class: "btn sm", type: "button", disabled: idUi.busy, onclick: () => void go() }, idUi.busy ? "Checking…" : c ? "Check again" : "Check"),
    c ? h("ul", { class: "id-checks" }, ...c.map((x) => h("li", { class: x.ok ? "ok" : "bad" }, h("span", { class: "id-mark" }, x.ok ? "✓" : "✕"), h("span", {}, h("b", {}, x.label), " ", h("span", { class: "sub" }, x.detail))))) : null);
}
const benchUi = { data: null as { state: BenchState; tasks: BenchTask[] } | null, loading: false, draft: null as string | null, timer: 0, open: null as string | null };
async function loadBench() {
  if (benchUi.loading) return;
  benchUi.loading = true;
  try {
    benchUi.data = await api.browserTest();
  } catch {
    // shown as not loaded
  }
  benchUi.loading = false;
  render();
  // While it runs: fresh results every few seconds, as long as this page is open.
  clearTimeout(benchUi.timer);
  if (benchUi.data?.state.running && state.route.name === "ai-agent") benchUi.timer = window.setTimeout(() => void loadBench(), 4000);
}
const mmss = (ms: number) => `${Math.floor(ms / 60_000)}:${String(Math.round((ms % 60_000) / 1000)).padStart(2, "0")}`;
function benchCard() {
  if (!benchUi.data && !benchUi.loading) void loadBench();
  const d = benchUi.data;
  const s = d?.state;
  const run = async () => {
    try {
      benchUi.data = await api.startBrowserTest();
      toast("Comparing browsers: the results fill in as each one finishes");
      void loadBench();
    } catch (e) {
      toast((e as Error).message);
    }
    render();
  };
  const stop = async () => {
    try {
      await api.stopBrowserTest();
      toast("Stopped");
    } catch (e) {
      toast((e as Error).message);
    }
    void loadBench();
  };
  const host = (u: string) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };
  const rows = s?.rows ?? [];
  // Per browser: how many it got, the median time of those it got, and how fast it opened the first page.
  const median = (xs: number[]) => (xs.length ? [...xs].sort((a, b) => a - b)[Math.floor((xs.length - 1) / 2)] : null);
  const stats = (s?.browsers ?? []).map((b) => {
    const mine = rows.filter((r) => r.browser === b);
    const ok = mine.filter((r) => r.result === "ok");
    const opens = mine.map((r) => r.openMs).filter((x): x is number => x != null);
    return { b, done: ok.length, tried: mine.length, stuck: mine.filter((r) => r.result === "stuck").length, med: median(ok.map((r) => r.ms)), open: opens.length ? Math.round(opens.reduce((a, x) => a + x, 0) / opens.length) : null };
  });
  const finished = s && !s.running && rows.length;
  // The winner: most tasks done, then the quickest (median).
  const ranked = [...stats].filter((x) => x.done).sort((a, b) => b.done - a.done || (a.med ?? 1e12) - (b.med ?? 1e12));
  const winner = finished ? ranked[0]?.b ?? null : null;
  // 8.11.1: what each result means, and tap one to see what happened (what it found, or where and why it stopped).
  const word = (r: BenchRow) => r.result === "ok" ? `✓ ${mmss(r.ms)}` : r.result === "stuck" ? "✕ Got stuck" : r.result === "refused" ? "✕ Refused" : r.result === "didnt_start" ? "✕ Didn't start" : r.result === "stopped" ? "Stopped" : "✕ Didn't finish";
  const cell = (r: BenchRow | undefined) => {
    if (!r) return h("span", { class: "muted" }, "…");
    const key = `${r.task}:${r.browser}`;
    return h("button", { type: "button", class: `bench-cell ${r.result === "ok" ? "bench-ok" : "bench-bad"}${benchUi.open === key ? " on" : ""}`, "aria-expanded": String(benchUi.open === key),
      onclick: () => { benchUi.open = benchUi.open === key ? null : key; render(); } }, word(r));
  };
  const openRow = benchUi.open ? rows.find((r) => `${r.task}:${r.browser}` === benchUi.open) : undefined;
  const MEANS: Record<BenchRow["result"], string> = {
    ok: "Got the answer.",
    stuck: "Got stuck: it hit the time or step limit, the site kept not responding, or it stopped answering. Counts as a failure.",
    failed: "Didn't finish: it gave up or something broke. Counts as a failure.",
    refused: "Refused: the site turned this browser away (a security check). Counts as a failure.",
    didnt_start: "Didn't start: this browser didn't open, so it was tested in Joy Phone's own instead. Counts as a failure.",
    stopped: "Stopped: you stopped the comparison.",
  };
  const draft = benchUi.draft ?? (d?.tasks ?? []).map((t) => `${t.url} | ${t.goal}`).join("\n");
  return h("div", { class: "item stack bench" },
    h("div", { class: "bench-head" },
      h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Compare browsers"),
        h("div", { class: "sub" }, `Runs the same price checks in each browser that's set up (Joy Phone's own${(s?.browsers ?? []).length > 1 ? `, ${(s?.browsers ?? []).filter((b) => b !== "own").map((b) => BENCH_NAME[b]).join(", ")}` : " and any hosted browser with its key"}), one at a time so the timing is fair, and shows which got the answer and how fast. Nothing is booked. It takes a few minutes per task and uses AI and browser minutes.`)),
      s?.running
        ? h("button", { class: "btn quiet sm", type: "button", onclick: () => void stop() }, "Stop")
        : h("button", { class: "btn sm", type: "button", disabled: !d, onclick: () => void run() }, rows.length ? "Run again" : "Run comparison")),
    s?.running && s.current ? h("div", { class: "sub bench-now" }, h("span", { class: "spinner" }), ` Task ${s.current.task + 1} of ${s.tasks.length} in ${BENCH_NAME[s.current.browser]}…`) : null,
    stats.length && rows.length ? h("div", { class: "bench-summary" }, ...stats.map((x) =>
      h("div", { class: `bench-card${winner === x.b ? " win" : ""}` },
        h("b", {}, BENCH_NAME[x.b], winner === x.b ? " 🏆" : ""),
        h("div", { class: "sub" }, `${x.done} of ${x.tried} done`),
        x.stuck ? h("div", { class: "sub bench-bad" }, `${x.stuck} stuck`) : null,
        h("div", { class: "sub" }, x.med != null ? `Median ${mmss(x.med)}` : "—"),
        h("div", { class: "sub" }, x.open != null ? `Opens ${(x.open / 1000).toFixed(1)}s` : "")))) : null,
    rows.length && s ? h("div", { class: "bench-table-wrap" }, h("table", { class: "bench-table" },
      h("thead", {}, h("tr", {}, h("th", {}, "Task"), ...s.browsers.map((b) => h("th", {}, BENCH_NAME[b])))),
      h("tbody", {}, ...s.tasks.map((t, i) => h("tr", {},
        h("td", { title: t.goal }, h("b", {}, host(t.url)), h("div", { class: "sub" }, t.goal.length > 70 ? `${t.goal.slice(0, 70)}…` : t.goal)),
        ...s.browsers.map((b) => h("td", {}, cell(rows.find((r) => r.task === i && r.browser === b))))))))) : null,
    openRow && s ? h("div", { class: "bench-detail" },
      h("div", {}, h("b", {}, `${BENCH_NAME[openRow.browser]} · ${host(s.tasks[openRow.task]?.url ?? "")}`), h("span", { class: "sub" }, ` · ${mmss(openRow.ms)} · ${openRow.steps} step${openRow.steps === 1 ? "" : "s"}`)),
      h("div", { class: `sub ${openRow.result === "ok" ? "bench-ok" : "bench-bad"}` }, MEANS[openRow.result]),
      openRow.summary ? h("div", { class: "bench-said" }, openRow.summary) : null,
      openRow.shot ? h("a", { href: openRow.shot, target: "_blank", rel: "noopener", class: "bench-shot" }, h("img", { src: openRow.shot, alt: "The last page it was on", loading: "lazy" })) : null) : null,
    rows.length ? h("div", { class: "sub" }, finished && !winner ? "No winner: no browser got an answer. " : "", "🏆 The winner is the browser that got the most answers, then the quickest. Got stuck, didn't finish, refused or didn't start all count as failures.") : null,
    finished ? h("div", { class: "sub" }, `Finished ${when(s!.finishedAt!.replace("T", " ").slice(0, 19))}${s!.by ? ` · run by ${s!.by}` : ""}. Tap a result to see what it found, or where it got stuck.`) : null,
    h("details", { class: "bench-tasks" },
      h("summary", {}, `Tasks (${d?.tasks.length ?? 0})`),
      h("div", { class: "sub" }, "One per line: website | what to find. {date} is 30 days from today, {date+3} three days after. Up to 8."),
      h("textarea", { class: "field", rows: 6, disabled: !!s?.running, value: draft, oninput: (e: Event) => (benchUi.draft = (e.target as HTMLTextAreaElement).value) }),
      h("div", { class: "row-actions" },
        h("button", { class: "btn sm", type: "button", disabled: !!s?.running, onclick: async () => {
          const tasks = (benchUi.draft ?? draft).split("\n").map((l) => l.split("|")).filter((p) => p.length >= 2).map(([u, ...g]) => ({ url: u.trim(), goal: g.join("|").trim() }));
          try {
            const r = await api.saveBrowserTestTasks({ tasks });
            if (d) d.tasks = r.tasks;
            benchUi.draft = null;
            toast(`${r.tasks.length} task${r.tasks.length === 1 ? "" : "s"} saved`);
          } catch (e) {
            toast((e as Error).message);
          }
          render();
        } }, "Save tasks"),
        h("button", { class: "btn quiet sm", type: "button", disabled: !!s?.running, onclick: async () => {
          try {
            const r = await api.saveBrowserTestTasks({ reset: true });
            if (d) d.tasks = r.tasks;
            benchUi.draft = null;
            toast("Back to the usual tasks");
          } catch (e) {
            toast((e as Error).message);
          }
          render();
        } }, "Usual tasks"))));
}
/** 12.10.30: ♥ a voice: it goes to Favorites at the top of the list (tap again to take it off). */
function favButton(name: string, on: boolean, toggle: () => void) {
  return h("button", { class: `icon-btn sm voice-fav${on ? " on" : ""}`, type: "button", "aria-pressed": String(on),
    "aria-label": on ? `Take ${name} off your favorites` : `Add ${name} to your favorites`, title: on ? "Favorite (tap to remove)" : "Add to favorites", onclick: toggle }, icon("heart", "sm"));
}
/** Personal side: your own voice (only voices that match your Selena / Steven). */
function personalVoices(pa: PaSettings) {
  const body = aiAgent.settings;
  if (!body) void loadAgentVoice();
  if (pa.persona === "same" || !body) return null;
  const gender = pa.persona === "steven" ? "man" : "woman";
  const original = (v: (typeof body.voices)[number]) => v.family === "Azure" || v.family === "Kokoro" || !v.family;
  const list = body.voices.filter((v) => v.gender === gender && original(v) && v.lang === body.settings.language);
  const all = list.length ? list : body.voices.filter((v) => v.gender === gender && original(v));
  // 12.10.30: your own ♥ voices first and your own removed voices (×) hidden, separate from Joy Travel's lists.
  const favKeys = pa.favoriteVoices ?? [], hidKeys = pa.hiddenVoices ?? [];
  const favVoices = favKeys.map((k) => body.voices.find((v) => v.key === k)).filter((v): v is (typeof body.voices)[number] => !!v && v.gender === gender);
  const shown = [...favVoices, ...all.filter((v) => !favKeys.includes(v.key) && (!hidKeys.includes(v.key) || v.key === pa.voice))];
  const removed = all.filter((v) => hidKeys.includes(v.key) && v.key !== pa.voice);
  const savePa = async (change: Partial<PaSettings>, ok: string) => {
    try {
      aiAgent.pa = (await api.saveAgentPersonal(change)).settings;
      toast(ok);
    } catch (e) {
      toast((e as Error).message);
    }
    render();
  };
  return [
    h("div", { class: "group-title" }, `${pa.persona === "steven" ? "His" : "Her"} voice on your calls`),
    h("div", { class: "group" }, ...shown.map((v) => {
      const on = pa.voice === v.key;
      const fav = favKeys.includes(v.key);
      return h("div", { class: `item agent-voice${on ? " on" : ""}` },
        h("button", { class: "agent-voice-pick", type: "button", role: "radio", "aria-checked": String(on), onclick: async () => {
          if (on) return;
          try {
            aiAgent.pa = (await api.saveAgentPersonal({ voice: v.key })).settings;
            toast(`${v.name} on your personal calls`);
          } catch (e) {
            toast((e as Error).message);
          }
          render();
        } }, h("span", { class: "radio-dot", "aria-hidden": "true" }), h("span", { class: "grow" }, h("b", {}, v.name), h("small", {}, v.sub))),
        playButton(`pa-voice-${v.key}`, "Play", () => (v.sample ? v.sample : api.agentVoiceSample(v.key))),
        favButton(v.name, fav, () => void savePa({ favoriteVoices: fav ? favKeys.filter((k) => k !== v.key) : [...favKeys, v.key] }, fav ? `${v.name} is no longer a favorite` : `${v.name} added to your favorites`)),
        !on ? h("button", { class: "icon-btn sm voice-remove", type: "button", "aria-label": `Remove ${v.name}`, title: `Remove ${v.name} from your list`,
          onclick: () => void savePa({ hiddenVoices: [...hidKeys, v.key], favoriteVoices: favKeys.filter((k) => k !== v.key) }, `${v.name} removed`) }, icon("close", "sm"))
          : h("span", { class: "voice-remove-gap", "aria-hidden": "true" })); // the voice in use can't be removed
    })),
    removed.length ? h("details", { class: "voice-hidden" },
      h("summary", {}, `Removed voices (${removed.length})`),
      h("div", { class: "group" }, ...removed.map((v) => h("div", { class: "item agent-voice" },
        h("span", { class: "grow" }, h("b", {}, v.name), h("small", {}, v.sub)),
        h("button", { class: "btn quiet sm", type: "button", onclick: () => void savePa({ hiddenVoices: hidKeys.filter((k) => k !== v.key) }, `${v.name} is back in the list`) }, "Restore"))))) : null,
  ];
}

/** Selena → Settings → Personal assistant: each teammate's own, private details for Personal mode. */
function personalSection() {
  if (!isAdmin()) return null; // Personal mode is for admins only
  if (!aiAgent.pa) void loadPersonal();
  const pa = aiAgent.pa;
  if (!pa) return h("div", { id: "pa-section", class: "group" }, h("div", { class: "item" }, "Loading…"));
  const d = (aiAgent.paDraft ??= {});
  const val = <K extends keyof PaSettings>(k: K): PaSettings[K] => (d[k] ?? pa[k]) as PaSettings[K];
  const edit = <K extends keyof PaSettings>(k: K, v: PaSettings[K]) => {
    d[k] = v;
    const b = document.getElementById("pa-save") as HTMLButtonElement | null;
    if (b) b.disabled = !paChanged();
  };
  const text = (id: string, k: "name" | "number" | "about" | "canSay" | "cannotSay" | "voicemail", label: string, hint: string, attrs: Record<string, unknown>, area = false) => [
    h("label", { class: "rules-label", for: id }, h("b", {}, label), h("small", {}, hint)),
    h(area ? "textarea" : "input", { id, class: "field", value: k === "number" && d.number === undefined && pa.number ? phone(pa.number) : String(val(k) ?? ""), autocomplete: "off", ...attrs,
      oninput: (e: Event) => edit(k, (e.target as HTMLInputElement).value) } as any),
  ];
  const first = firstName(state.user?.name ?? "");
  // He or she: whoever answers and calls for you (your own pick, or the same as Joy Travel).
  const steven = pa.persona === "steven" || (pa.persona === "same" && aiAgent.settings?.settings.persona === "steven");
  const sheHe = steven ? "he" : "she", SheHe = steven ? "He" : "She", nm = steven ? agentNames().man : agentNames().woman;
  return h("section", { id: "pa-section", class: "pa-section" },
    h("div", { class: "group-title" }, h("span", {}, `Your personal assistant on ${personalLineName()}`), h("span", { class: "pa-lock" }, icon("lock", "sm"), "Private")),
    h("p", { class: "muted agent-voice-intro" }, `Your own assistant on ${personalLineName()}: doctors, restaurants, repairs, bills. Used whenever you're on your personal line. These chats, calls, reminders and your number are only visible to you, never in the team inbox, not even to other admins.`),
    h("div", { class: "group-title" }, "Who answers and calls for you"),
    personaGroup(pa.persona, async (k) => {
      try {
        aiAgent.pa = (await api.saveAgentPersonal({ persona: k })).settings;
        toast(k === "same" ? "Same as Joy Travel on your calls" : k === "steven" ? `${agentNames().man} answers and calls for you (a man's voice)` : `${agentNames().woman} answers and calls for you (a woman's voice)`);
        void refresh();
      } catch (e) {
        toast((e as Error).message);
      }
      render();
    }, false, (() => {
      const team = aiAgent.settings?.settings;
      const v = team && aiAgent.settings!.voices.find((x) => x.key === team.voice);
      return team ? `${team.persona === "steven" ? agentNames().man : agentNames().woman}${v ? `, voice ${v.name}` : ""}` : "Joy Travel's name and voice";
    })()),
    ...(personalVoices(pa) ?? []),
    h("div", { class: "group-title" }, "Personality in your chat"),
    h("div", { class: "group" },
      h("div", { class: "item" },
        h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, `${steven ? "His" : "Her"} personality with you`),
          h("div", { class: "sub" }, pa.vibe === "cheeky" ? pron("Playful, teasing and a little naughty. Only in your Personal chat; her calls stay professional.", steven)
            : pa.vibe === "professional" ? "Crisp and efficient, no jokes." : "Warm and relaxed, with a light sense of humor.")),
        h("div", { class: "chips" }, ...(([["professional", "Professional"], ["friendly", "Friendly"], ["cheeky", "Playful 😏"]] as const).map(([k, label]) =>
          h("button", { class: pa.vibe === k ? "chip on" : "chip", type: "button", "aria-pressed": String(pa.vibe === k), onclick: async () => {
            try {
              aiAgent.pa = (await api.saveAgentPersonal({ vibe: k })).settings;
              toast(k === "cheeky" ? `${aiName()}'s feeling cheeky 😏` : k === "professional" ? `${aiName()}'s all business` : `${aiName()}'s friendly again`);
            } catch (e) {
              toast((e as Error).message);
            }
            render();
          } }, label)))))),
    h("div", { class: "group-title" }, `What ${sheHe} can say`),
    h("div", { class: "group selena-rules" },
      ...text("pa-about", "about", `What ${sheHe} may use about you`, "One per line. Only what a call needs is shared.", { rows: "4", maxlength: "3000", placeholder: "Home address: 12 Main St, Brooklyn NY\nDate of birth (for doctor's offices): March 3, 1985\nInsurance: Aetna\nI prefer mornings for appointments" }, true),
      ...text("pa-can", "canSay", `${SheHe} may say or share`, "One per line.", { rows: "2", maxlength: "3000", placeholder: "That I'm a returning patient" }, true),
      ...text("pa-cannot", "cannotSay", `${SheHe} must never say or share`, "One per line.", { rows: "2", maxlength: "3000", placeholder: "My work number or where I work\nMy date of birth unless they need it to find my file" }, true)),
    h("div", { class: "group-title" }, "About you"),
    h("div", { class: "group selena-rules" },
      ...text("pa-name", "name", "Your name on her calls", `The name ${SheHe.toLowerCase()} gives only when a business needs one (a booking, a callback): "${val("name") || first || "…"}". ${SheHe} never says "calling on behalf of". Leave empty to use your first name.`, { type: "text", maxlength: "80", placeholder: first || "Your first name" }),
      ...text("pa-number", "number", "Your personal number", "Once it's on Telnyx, calls and texts to it go to your own Personal inbox (only you see it, only you ring), and you can call and text from it there.", { type: "tel", inputmode: "tel", maxlength: "20", placeholder: "(555) 123-4567" }),
      ...text("pa-voicemail", "voicemail", "Voicemail greeting on your personal line", `Read to callers you don't answer. Empty: "Hi, you've reached ${val("name") || first || "…"}. Please leave a message after the tone."`, { rows: "2", maxlength: "500", placeholder: `Hi, you've reached ${val("name") || first || "me"}. Leave a message and I'll call you back.` }, true),
      h("label", { class: "ai-book pa-callerid" },
        h("input", { type: "checkbox", checked: !!val("callerId"), onchange: (e: Event) => {
          edit("callerId", (e.target as HTMLInputElement).checked);
          render();
        } }),
        h("span", {}, h("b", {}, `${nm} calls from my personal number`), h("small", {}, ` Turn on once the number is on Telnyx. Until then (or if Telnyx refuses it) ${sheHe} calls from the team number and won't give it out.`))),
      h("div", { class: "rules-foot" },
        h("small", {}, pron("Private to you. She never gives card, bank or social security details.", steven)),
        h("button", { id: "pa-save", class: "btn sm", type: "button", disabled: !paChanged() || aiAgent.paSaving, onclick: async () => {
          const draft = aiAgent.paDraft;
          if (!draft) return;
          aiAgent.paSaving = true;
          render();
          try {
            aiAgent.pa = (await api.saveAgentPersonal(draft)).settings;
            aiAgent.paDraft = null;
            if (draft.number && !aiAgent.pa.number) toast("That personal number doesn't look complete; saved without it");
            else toast("Your personal assistant details are saved");
          } catch (e) {
            toast((e as Error).message);
          }
          aiAgent.paSaving = false;
          render();
        } }, aiAgent.paSaving ? "Saving…" : "Save"))));
}
function paChanged(): boolean {
  const d = aiAgent.paDraft;
  const pa = aiAgent.pa;
  return !!d && !!pa && (Object.keys(d) as (keyof PaSettings)[]).some((k) => d[k] !== pa[k]);
}
/** Unsaved edits to Selena's rules (kept while you type; the Save button lights up). */
function rulesChanged(): boolean {
  const d = aiAgent.rulesDraft;
  const st = aiAgent.settings?.settings;
  return !!d && !!st && (d.canSay !== st.canSay || d.cannotSay !== st.cannotSay || d.about !== st.about);
}
function paintRulesSave() {
  const b = document.getElementById("selena-rules-save") as HTMLButtonElement | null;
  if (b) b.disabled = !rulesChanged();
}

/** Phones: the same switch, small, next to her name in the header ("Steven  Business | Personal"). */
function selenaModeInline() {
  if (!isAdmin()) return null;
  if (!aiAgent.pa) void loadPersonal();
  const byLine = !!state.personalLine;
  const personal = byLine ? state.line === "personal" : aiAgent.mode === "personal";
  const pick = (m: "business" | "personal") => ((stevenBusiness = m === "business"), byLine ? switchLine(m === "personal" ? "personal" : "team", { name: "ai-agent" }) : void setSelenaMode(m));
  return h("span", { class: `mode-inline${personal ? " personal" : ""}`, role: "radiogroup", "aria-label": `Who ${aiName()} works for` },
    ...(["business", "personal"] as const).map((m) => h("button", { type: "button", role: "radio", "aria-checked": String((m === "personal") === personal),
      onclick: (e: MouseEvent) => { e.stopPropagation(); pick(m); } }, m === "personal" ? "Personal" : "Business")));
}

/** The Business / Personal switch above your chat with Selena (computers; phones have it next to her name). */
function selenaModeBar() {
  if (!isAdmin()) return null; // Personal mode is for admins only
  if (!aiAgent.pa) void loadPersonal();
  // With a personal line, the line decides: Business = the Joy Travel line, Personal = your own line (total separation).
  const byLine = !!state.personalLine;
  const personal = byLine ? state.line === "personal" : aiAgent.mode === "personal";
  const who = aiAgent.pa?.name || firstName(state.user?.name ?? "") || "you";
  const pick = (m: "business" | "personal") => ((stevenBusiness = m === "business"), byLine ? switchLine(m === "personal" ? "personal" : "team", { name: "ai-agent" }) : void setSelenaMode(m));
  return h("div", { class: `selena-mode${personal ? " personal" : ""}` },
    h("div", { class: "seg ai-tabs selena-mode-seg", role: "radiogroup", "aria-label": `Who ${aiName()} works for` },
      ...(["business", "personal"] as const).map((m) => h("button", { type: "button", role: "radio", "aria-checked": String((m === "personal") === personal), "aria-pressed": String((m === "personal") === personal),
        onclick: () => pick(m) }, icon(m === "personal" ? "lock" : "briefcase", "sm"), m === "personal" ? "Personal" : "Business"))),
    h("small", {}, personal
      ? pron(`Just for you: she makes your personal calls (as ${who} when a name is needed)${aiAgent.pa?.callerId && aiAgent.pa.number ? ` from ${phone(aiAgent.pa.number)}` : ""}. Private, not in the inbox.`, state.agentMan)
      : "For Joy Travel: calls from the team number, results shared as usual."));
}

function agentChatView(head: Node) {
  if (!aiAgent.chatLoaded) void loadAgentChat();
  const timeline: Node[] = [];
  // Start fresh: clear the whole chat (her calls stay under Calls).
  if (aiAgent.chat.some((m) => m.role !== "call" && m.id > 0)) timeline.push(h("div", { class: "agent-clear" },
    h("button", { class: "link-btn", type: "button", onclick: async () => {
      const ok = await glassDialog({ title: `Clear your chat with ${aiName()}?`, message: pron("Every message here is deleted and she forgets them. Her calls stay under Calls. This can't be undone.", state.agentMan), confirm: "Clear chat", danger: true });
      if (!ok) return;
      try {
        await api.clearAgentChat();
        aiAgent.chat = aiAgent.chat.filter((m) => m.role === "call");
        render();
        toast("Chat cleared");
      } catch (e) {
        toast((e as Error).message);
      }
    } }, icon("trash", "sm"), "Clear chat")));
  let lastDay = "";
  for (const m of aiAgent.chat) {
    const d = serverTime(m.created_at);
    const label = d ? dayLabel(d) : "";
    if (label && label !== lastDay) {
      timeline.push(h("div", { class: "day" }, label));
      lastDay = label;
    }
    // Your phone calls with her are kept as her memory (chat and later calls know them), not shown in the chat.
    if (m.role === "call") continue;
    const mine = m.role === "me";
    const me0 = state.user?.name ?? "You";
    const reply: Reply = { id: m.id, name: mine ? me0 : aiName(), body: m.body || (m.media?.length ? "📎 Attachment" : "") };
    // Reply / Copy / Delete: right-click or press-and-hold the message, or its ⋮.
    const items = () => [
      { label: "Reply", iconName: "reply", run: () => { state.reply = reply; render(); document.getElementById("draft")?.focus(); } },
      ...(m.body ? [{ label: "Copy text", iconName: "copy", run: () => void navigator.clipboard?.writeText(m.body).then(() => toast("Copied"), () => toast("Couldn't copy")) }] : []),
      { label: "Delete", iconName: "trash", danger: true, run: () => void (async () => {
        const ok = await glassDialog({ title: mine ? "Delete your message?" : `Delete ${aiName()}'s message?`, message: `It's removed from your chat with ${aiName()}, and ${aiName()} forgets it. This can't be undone.`, confirm: "Delete", danger: true });
        if (!ok) return;
        try {
          await api.deleteAgentMessage(m.id);
          aiAgent.chat = aiAgent.chat.filter((x) => x.id !== m.id);
          render();
        } catch (e) {
          toast((e as Error).message);
        }
      })() },
    ];
    let hold: number | undefined;
    const gestures = m.id > 0 ? {
      oncontextmenu: (e: MouseEvent) => { if ((e.target as HTMLElement).closest("a")) return; e.preventDefault(); if (Date.now() - touchHoldAt < 1500) return; openPointMenu(e.clientX, e.clientY, items()); },
      onpointerdown: (e: PointerEvent) => {
        if (e.pointerType !== "touch") return;
        const x = e.clientX, y = e.clientY;
        hold = window.setTimeout(() => { touchHoldAt = Date.now(); openPointMenu(x, y, items(), undefined, true); }, 480);
      },
      onpointerup: () => clearTimeout(hold), onpointercancel: () => clearTimeout(hold), onpointermove: () => clearTimeout(hold),
    } : {};
    timeline.push(h("div", { class: mine ? "bubble-row mine chat-row" : "bubble-row chat-row agent-row", id: `a-${m.id}` },
      h("div", { class: "bubble-line" },
        mine ? null : h("span", { class: "ai-av sm agent-face" }, icon("sparkle", "sm")),
        h("div", { class: "bubble-stack" },
          h("div", { class: `${mine ? "bubble" : "bubble bot-bubble"}${m.id > 0 ? " has-dots" : ""}`, ...gestures },
            m.reply_to ? quoteBlock(m.reply_role === "agent" ? aiName() : me0, m.reply_body ?? null, `a-${m.reply_to}`) : null,
            ...(mine ? withMentions(m.body) : selenaText(m.body)), ...mediaList(m.media ?? []),
            m.call_id ? h("button", { class: "agent-open-call", type: "button", onclick: () => {
              const c = aiAgent.calls.find((x) => x.id === m.call_id);
              state.focusRef = `call:${m.call_id}`;
              if (c) openAiCall(c);
              else void api.aiCalls().then((r) => {
                const hit = r.calls.find((x) => x.id === m.call_id);
                if (hit) openAiCall(hit);
              });
            } }, icon("phone", "sm"), "Open the call") : null,
            m.id > 0 ? h("button", { class: "reply-btn more-btn", "aria-label": "Reply and more", "aria-haspopup": "menu", title: "Reply, copy", onclick: (e: MouseEvent) => {
              e.stopPropagation();
              const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
              openPointMenu(r.left, r.bottom + 4, items());
            } }, icon("more", "sm")) : null),
          h("div", { class: "under-bubble" }, h("span", { class: "bubble-time" }, clock(d)))))));
  }
  if (aiAgent.thinking) timeline.push(h("div", { class: "bubble-row chat-row agent-row" }, h("div", { class: "bubble-line" },
    h("span", { class: "ai-av sm agent-face" }, icon("sparkle", "sm")), h("div", { class: "bubble bot-bubble typing-dots", "aria-label": "Thinking" }, h("i"), h("i"), h("i")))));
  const personal = aiAgent.mode === "personal";
  if (aiAgent.chatLoaded && !aiAgent.chat.length) {
    timeline.push(h("div", { class: "agent-hello" },
      h("span", { class: "ai-av lg" }, icon("sparkle")),
      h("strong", {}, personal ? (aiAgent.pa?.vibe === "cheeky" ? `Well hello there 😏 I'm ${aiName()}, all yours.` : `Hi! I'm ${aiName()}, your personal assistant.`) : `Hi! I'm ${aiName()}, your AI assistant.`),
      h("p", {}, personal
        ? "I can phone companies and offices for you: negotiate bills down, cancel subscriptions, dispute charges, book or move appointments and follow up. Send me a bill, letter or screenshot and I'll read it. Only you see these chats and calls."
        : "I can phone hotels, restaurants and other businesses for you: get prices, book appointments and reservations, and pass on special requests. I'll post the results here. Send me a photo, PDF, Word or Excel file and I'll read it too."),
      h("div", { class: "agent-ideas" }, ...(personal ? [
        "Call my internet provider and get my bill down, I pay $89 a month",
        "Cancel my gym membership and get a cancellation number",
        "Call my dentist and move my cleaning to next week, any morning",
      ] : [
        "Call Hotel Mar Cancun and ask the king room rate for Mar 12–18",
        "Book a table for 4 at 7 PM Friday at Nobu Malibu under Joy Travel",
        "Ask the Grand Oasis to put a birthday cake in Maria Lopez's room",
      ]).map((t) => h("button", { type: "button", class: "agent-idea", onclick: () => {
        state.drafts["agent"] = t;
        render();
        document.getElementById("draft")?.focus();
      } }, t)))));
  }
  return h("div", { class: "agent-chat", style: "flex:1;display:flex;flex-direction:column;min-height:0" }, screen(
    head,
    selenaModeBar(),
    h("div", { class: "timeline", "data-scroll": "timeline" }, h("div", { class: "timeline-inner" }, ...timeline)),
    composer({
      key: "agent",
      modes: ["chat"],
      placeholder: personal ? `Ask ${aiName()} to handle something personal…` : `Ask ${aiName()} to call, book or arrange something…`,
      send: async (body, media) => {
        if (!body && !media.length) return;
        const replyTo = state.reply && state.reply.id > 0 ? state.reply : null;
        const tempId = -Date.now();
        aiAgent.chat.push({ id: tempId, role: "me", body, media, call_id: null, created_at: new Date().toISOString().replace("T", " ").replace("Z", ""),
          reply_to: replyTo?.id ?? null, reply_body: replyTo?.body ?? null, reply_role: replyTo ? (replyTo.name === aiName() ? "agent" : "me") : null });
        aiAgent.thinking = true;
        // Empty the box now: the re-render below would otherwise keep what was typed (it keeps typing in progress).
        typedValues.delete("draft");
        const box = document.getElementById("draft") as HTMLTextAreaElement | null;
        if (box) box.value = "";
        render();
        try {
          await api.agentSend(body, media, replyTo?.id ?? null);
        } catch (e) {
          // Not sent: take the message and the "thinking" dots back off (the box gets the text back to try again).
          aiAgent.chat = aiAgent.chat.filter((m) => m.id !== tempId);
          aiAgent.thinking = false;
          render();
          throw e;
        }
        void loadAgentChat();
      },
    })));
}

/** Opens one of Selena's calls in its conversation (a personal one in your Personal inbox). */
function openAiCall(c: AiAgentCall) {
  const to: Route = { name: "thread", number: c.external_number };
  if (!!c.personal !== (state.line === "personal")) switchLine(c.personal ? "personal" : "team", to);
  else go(to);
}

/** Saved contacts matching what's typed in Selena's New call box (searched on the server, newest answer wins). */
let aiContactTimer: number | undefined;
function findAiContacts(raw: string) {
  const term = raw.trim().toLowerCase();
  clearTimeout(aiContactTimer);
  if (term.length < 2 || toE164(raw)) return;
  aiContactTimer = window.setTimeout(async () => {
    try {
      const list = await api.contacts(term);
      if (aiAgent.form.to.trim().toLowerCase() !== term) return; // typed on meanwhile
      aiAgent.contactHits = { term, list: list.slice(0, 8).map((c) => ({ number: c.number, name: c.name })) };
      if (state.route.name === "ai-agent") render();
    } catch {
      // the suggestions from conversations still show
    }
  }, 180);
}

function aiCallsView(head: Node) {
  if (!aiAgent.loaded) void loadAiCalls();
  const f = aiAgent.form;
  const field = (id: string, key: "to" | "who" | "ask" | "details", attrs: Record<string, unknown>, area = false) =>
    h(area ? "textarea" : "input", { id, class: "field", value: f[key], autocomplete: "off", ...attrs,
      oninput: (e: Event) => {
        f[key] = (e.target as HTMLInputElement).value;
        if (key === "to") {
          render(); // name or digits → contact suggestions
          findAiContacts(f.to);
        }
      } } as any);
  // Picking a contact: typing part of a name, company or number finds them in all saved contacts (not only
  // conversations in the inbox), plus recent conversations with a name.
  const term = f.to.trim().toLowerCase();
  const digits = term.replace(/\D/g, "");
  const match: { number: string; name: string }[] = [];
  if (term.length >= 2 && !toE164(f.to)) {
    const seen = new Set<string>();
    const add = (number: string, name: string | null | undefined) => {
      if (!name || seen.has(number) || match.length >= 5) return;
      seen.add(number);
      match.push({ number, name });
    };
    for (const c of aiAgent.contactHits.term === term ? aiAgent.contactHits.list : []) add(c.number, c.name);
    for (const c of state.conversations) {
      const n = (c.contact_name ?? "").toLowerCase();
      if ((n && n.includes(term)) || (digits.length >= 3 && c.external_number.replace(/\D/g, "").includes(digits))) add(c.external_number, c.contact_name);
    }
  }
  const statusOf = (c: AiAgentCall) =>
    c.status === "ringing" ? "Calling…" : c.status === "active" ? "On the call" : c.status === "missed" ? "No answer" : c.status === "canceled" ? "Stopped"
    : c.summary ? "Results ready" : c.status === "completed" ? "Getting results…" : "Ended";
  const peek = (c: AiAgentCall) => c.summary ? summaryPeek(c.summary) : c.lastLine ? `${c.lastLine.role === "ai" ? "AI" : "Them"}: ${c.lastLine.text}` : c.task?.request ?? "";
  return screen(
    head,
    h("div", { class: "page" }, h("div", { class: "page-inner ai-page" },
      h("div", { class: "group ai-new" },
        h("div", { class: "ai-new-title" }, "New call"),
        field("ai-to", "to", { type: "text", inputmode: "tel", placeholder: "Number, or a contact's name" }),
        match.length ? h("div", { class: "ai-matches", role: "listbox" }, ...match.map((c) => h("button", { type: "button", class: "ai-match", role: "option",
          // pointerdown: picks it before the phone keyboard closing moves the list away from your finger.
          onpointerdown: (e: PointerEvent) => e.preventDefault(),
          onclick: () => {
            f.to = phone(c.number);
            if (!f.who) f.who = c.name;
            typedValues.delete("ai-to");
            typedValues.delete("ai-who");
            render();
          } }, avatar(c.number, c.name, "xs"), h("span", {}, c.name), h("small", {}, phone(c.number))))) : null,
        field("ai-who", "who", { type: "text", placeholder: "Who it's calling (e.g. Hotel Mar Cancun)", maxlength: "120" }),
        field("ai-ask", "ask", { rows: "4", maxlength: "3000", placeholder: "What should it find out? e.g. Nightly rate and total for a king room, Mar 12–18. Taxes included? Breakfast? Cancellation policy?" }, true),
        field("ai-details", "details", { rows: "2", maxlength: "2000", placeholder: "Details it may share (optional): 2 adults, 1 child age 6, arriving late…" }, true),
        h("label", { class: "ai-book" }, h("input", { type: "checkbox", checked: f.book, onchange: (e: Event) => (f.book = (e.target as HTMLInputElement).checked) }),
          h("span", {}, "It may book, reserve or request this (not just ask)")),
        aiAgent.error ? h("div", { class: "ai-err", role: "alert" }, aiAgent.error) : null,
        h("div", { class: "ai-new-foot" },
          h("small", {}, aiAgent.mode === "personal"
            ? `Personal mode: makes your personal calls (gives the name ${aiAgent.pa?.name || firstName(state.user?.name ?? "") || "yours"} only when one is needed), private to you. Never gives card details.`
            : "Calls from the team number as Joy Travel's personal assistant. Never gives card details."),
          h("button", { class: "btn", disabled: aiAgent.busy, onclick: () => void startAiAgentCall() }, icon("phone", "sm"), aiAgent.busy ? "Starting…" : "Start call"))),
      h("div", { class: "group-title" }, "Calls"),
      !aiAgent.loaded ? h("div", { class: "empty" }, h("strong", {}, "Loading…"))
      : !aiAgent.calls.length ? emptyState({ icon: "sparkle", tone: "teal", title: "No AI calls yet", text: "Tell it who to call and what to find out. You'll see the conversation live and the results here and in that contact's conversation." })
      : h("div", { class: "group ai-calls" }, ...aiAgent.calls.map((c) => {
          const live = c.status === "ringing" || c.status === "active";
          return h("button", { class: `item link ai-call-row${live ? " live" : ""}`, onclick: () => {
            state.focusRef = `call:${c.id}`;
            openAiCall(c);
          } },
            avatar(c.external_number, c.contact_name),
            h("div", { class: "grow" },
              h("div", { class: "ai-row-top" }, h("b", {}, c.task?.hotel || nameFor(c.external_number, c.contact_name)),
                c.personal ? h("span", { class: "ai-pill personal", title: "Personal: only you see this call" }, icon("lock", "sm"), "Personal") : null,
                h("span", { class: `ai-pill ${live ? "live" : c.summary ? "done" : ""}` }, statusOf(c)),
                h("span", { class: "ai-row-time" }, listTime(c.started_at))),
              h("div", { class: "sub ellipsis" }, peek(c)),
              c.initiated_by_name ? h("div", { class: "sub ai-row-by" }, peek(c) === c.task?.request ? `For ${firstName(c.initiated_by_name)}` : `For ${firstName(c.initiated_by_name)} · ${c.task?.request ?? ""}`) : null),
            icon("chevron", "sm"));
        })))));
}

/** An AI call that's ringing, talking, or just ended (before its recording and results are in). */
function aiCallCard(c: ThreadCall) {
  const t = c.aiTask!;
  const live = c.status === "ringing" || c.status === "active";
  const status =
    c.status === "ringing" ? "Calling…"
    : c.status === "active" && c.on_hold ? "On hold · asked the team"
    : c.status === "active" ? "On the call"
    : c.status === "missed" ? "No answer"
    : c.status === "canceled" ? "Stopped"
    : c.status === "completed" ? "Done · getting the results…"
    : "Ended";
  const lines = (c.aiLog ?? []).slice(live ? -8 : -40);
  // The same bubble as every call: Selena's sparkles, what's happening, and (while it's on) End call.
  const by = c.initiated_by_name && c.initiated_by !== state.user?.id ? `for ${firstName(c.initiated_by_name)}` : "";
  return h("article", { class: `rec-d ai-card call-bubble ai${live ? " live" : ""}`, id: `call-${c.id}`, "aria-label": `AI call, ${status}` },
    h("div", { class: "cb-head" },
      h("span", { class: "cb-icon ai", role: "img", "aria-label": `${aiName()}'s call` }, icon("aiSparkles", "md")),
      h("span", { class: "cb-text" },
        h("span", { class: "cb-title" }, c.private_to ? `${aiName()}'s call · private` : `${aiName()}'s call`, h("span", { class: "cb-time" }, clock(serverTime(c.started_at)))),
        h("span", { class: "cb-sub" }, [status, by].filter(Boolean).join(" · "))),
      live ? h("button", { class: "cb-callback ai-stop", type: "button", "aria-label": "End call", title: "End call", onclick: async () => {
        try { await api.aiCallStop(c.id); } catch (e) { toast((e as Error).message); }
        void refresh();
      } }, icon("hangup", "sm")) : null),
    h("div", { class: "ai-ask" }, h("b", {}, t.hotel ? `Asking ${t.hotel}: ` : "Asking: "), t.request),
    lines.length
      ? (() => {
          const box = h("div", { class: "ai-lines", "aria-live": live ? "polite" : "off" },
            ...lines.map((l) => h("div", { class: `ai-line ${l.role}` }, h("span", { class: "who" }, l.role === "ai" ? "AI" : "Them"), h("span", {}, l.text))));
          // Live: shown as it happens. Ended: folded (the transcript stays hidden until you open it).
          return live ? box : h("details", { class: "ts-fold" }, h("summary", {}, h("span", { class: "fold-title" }, "Transcript"), icon("chevron", "sm")), box);
        })()
      : live ? h("div", { class: "ai-wait" }, h("span", { class: "spinner" }), c.status === "ringing" ? "Ringing…" : "Listening…") : null);
}

/** A newer version deployed on the server (checked every few minutes and when the app comes back). */
const updateState = { version: null as string | null, open: false };
let rerenderAny = () => {};
/** Under "Team chat" (instead of listing everyone while the team is small). */
const CHAT_TAGLINE = "Let's get things done ✨";
/** Settings → Phone line: one sample plays at a time; tapping the playing one stops it. */
const sample = { audio: null as HTMLAudioElement | null, id: "", loading: "" };
async function checkForUpdate() {
  try {
    const r = await fetch(`/health?check=${Date.now()}`, { cache: "no-store" });
    const v = (await r.json()).version as string | undefined;
    const next = v && v !== VERSION && VERSION !== "dev" ? v : null;
    if (next !== updateState.version) {
      updateState.version = next;
      rerenderAny();
    }
    // On the sign-in screen there's no call or draft to lose: take the new version straight away.
    // (Once per version: if the reload still brings the old one, it doesn't keep reloading.)
    if (next && !state.user && !(voice.call && !isEnded(voice.call))) {
      let tried = "";
      try {
        tried = sessionStorage.getItem("joy-auto-update") ?? "";
        sessionStorage.setItem("joy-auto-update", next);
      } catch {
        tried = next; // can't remember: don't risk a reload loop
      }
      if (tried !== next) void applyUpdate();
    }
  } catch {
    // offline: try again later
  }
}
/**
 * Phones and tablets: a new version shows at the top of Alerts (and counts on the bell). Computers don't get an
 * alert; they keep the small red dot by the version number.
 */
function updateAlert(): boolean {
  return !!updateState.version && (isIOS() || /Android/.test(navigator.userAgent) || matchMedia("(hover: none) and (pointer: coarse)").matches);
}

async function promptUpdate() {
  const fresh = updateState.version;
  if (!fresh) return;
  const onCall = !!voice.call && !isEnded(voice.call);
  const go = await glassDialog({
    title: `Joy Phone ${fresh} is ready`,
    message: onCall ? `You're on ${VERSION}. Finish your call first, then update (it reloads the app).` : `You're on ${VERSION}. Updating reloads the app; it takes a second.`,
    confirm: onCall ? "OK" : "Update now",
    cancel: "Later",
  });
  if (go && !onCall) await applyUpdate();
}

async function applyUpdate() {
  if (voice.call && !isEnded(voice.call)) return toast("Finish your call first, then update");
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    await reg?.update();
  } catch {
    // the page reload below is what matters
  }
  location.reload();
}
setInterval(() => void checkForUpdate(), 5 * 60_000);

// iPhone: one audio mode from the start, so the mic turning on or off (or an update reload) makes no sound.
// Set before any sound plays (the first sound creates iOS's audio session in whatever mode is set).
steadyAudioSession();

const root = document.getElementById("app")!;
const voice = new Phone();
// 7.10.30: Hold / Transfer / Add / Pause rec. / listen-in mode act on the call on screen (sent as X-Joy-Call).
setCurrentCallSession(() => voice.call?.options.telnyxSessionId ?? null);
const ringer = new Ringer();
/** Answering this ringing call already (a second tap while it connects does nothing). */
const answeringRing = new Set<string>();

const state = {
  /** Which inbox: the team's line, or an admin's personal line (their own number; private to them). */
  line: "team" as "team" | "personal",
  /** The admin's personal line (sidebar row and count); null for everyone else. */
  personalLine: null as { number: string | null; unread: number } | null,
  /** Unread on the team line (shown while you're in your personal inbox). */
  teamUnread: 0,
  /** Selena's replies in your chat with her you haven't seen (the number on her row). */
  agentUnread: 0,
  /** The AI agent's name on the line you're on (Settings → Who answers and calls). */
  agentName: savedAgent().name,
  /** The assistant is a man (Steven, or his custom name): "he/him/his" in the app. */
  agentMan: savedAgent().man,
  /** Joy Travel's assistant and (admins) your personal one, by name: Settings shows each side's own. */
  agents: null as { team: { name: string; man: boolean }; personal?: { name: string; man: boolean } } | null,
  user: null as User | null,
  vapidKey: null as string | null,
  route: { name: "inbox" } as Route,
  conversations: [] as Conversation[],
  live: [] as LiveCall[],
  chatSummary: { unread: 0, last: null as { body: string; user_name: string; created_at: string } | null },
  thread: null as Thread | null,
  chat: [] as ChatMessage[],
  /** When each teammate last had Team chat open (for the seen checks on your messages). */
  chatReads: [] as ChatRead[],
  /** Team page (admins): each teammate's own media site, if any. */
  teamSites: null as null | "loading" | { sites: string[]; users: Record<number, string>; error?: string },
  team: [] as User[],
  /** Teammates' status (green / yellow / red / grey), from /api/presence. */
  presence: [] as Presence[],
  invites: [] as Invite[],
  filter: "all" as Filter,
  search: "",
  drafts: {} as Record<string, string>,
  composeMode: "text" as "text" | "comment" | "selena",
  reply: null as Reply | null,
  dialed: "",
  editingName: false,
  menuOpen: false,
  callMinimized: false,
  pendingAnswer: null as string | null,
  /** Teammates typing right now: where ("chat" / "t:<number>") → user id → who, what and until when. */
  typing: {} as Record<string, Record<number, { name: string; mode: "chat" | "text" | "comment"; until: number }>>,
  /** Saved contacts matching the inbox search box (includes people you've never called or texted). */
  searchContacts: null as null | { q: string; list: ContactSummary[] },
  /** Team calls whose glass window you closed with "Not now" (desktop). */
  glassDismissed: new Set<string>(),
  /** The internal comment / chat message / contact note being edited in place (and its unsaved text). */
  editingMsg: null as null | { kind: "comment" | "chat" | "note"; id: number; draft: string },
  /** 3.10.22: the internal comment whose Reply bubble is open right under it. */
  inlineReply: null as number | null,
  muted: false,
  held: false,
  showKeypad: false,
  showTransfer: false,
  transferringTo: null as string | null,
  callStartedAt: 0,
  /** Thread (or "chat") we've already scrolled to the newest item for. */
  scrolled: "",
  newInviteUrl: null as string | null,
  /** "Invite link ready" vs "Reset link for Ben ready" */
  linkLabel: "Invite link ready",
  changingPassword: false,
  /** Internal comment threads that are expanded (keyed "call:<id>" / "sms:<id>" / "top"). Collapsed by default. */
  openThreads: new Set<string>(),
  /** The call or text a new internal comment will be attached to. */
  commentOn: null as { key: string; label: string } | null,
  /** Which emoji picker is open ("compose" or "react:<kind>:<id>"). */
  picker: null as string | null,
  editingMyName: false,
  /** Team member whose admin panel is open. */
  managing: null as number | null,
  confirmRemove: null as number | null,
  phoneDraft: null as PhoneSettings | null,
  phonePreview: "",
  phoneSaving: false,
  /** Show conversations marked done instead of open ones. */
  showDone: false,
  filterMenu: false as false | "status" | "more",
  activityUnread: 0,
  contacts: [] as ContactSummary[],
  contactsLoaded: false,
  contactSearch: "",
  contact: null as ContactDetail | null,
  editingContact: false,
  activity: [] as ActivityItem[],
  activityLoaded: false,
  tasks: [] as Task[],
  tasksLoaded: false,
  tasksMine: true,
  showDoneTasks: false,
  threadTasks: [] as Task[],
  analytics: null as Analytics | null,
  billing: null as Billing | null,
  billingMonth: "this" as "this" | "last",
  billingError: null as string | null,
  analyticsDays: 7 as 7 | 30,
  searchQ: "",
  /** Telnyx refused the chosen phone voice on a recent call (from GET /settings/phone). */
  voiceProblem: null as { voice: string; reason: string; at: string } | null,
  /** Sidebar search box: what's typed, and the contacts it matches (soft search). */
  railQ: "",
  /** Sidebar soft search: the contact highlighted with ↓/↑ (-1 = none); Enter calls it. */
  railSel: -1,
  railHits: null as { q: string; list: { number: string; name: string; company?: string | null }[] } | null,
  /** Computer: the inbox's search box is open (from the icon next to "Inbox"). */
  listSearchOpen: false,
  /** Phones: the inbox search bar is open (from the search icon in the header). */
  phoneSearchOpen: false,
  searchHits: null as SearchHit[] | null,
  searching: false,
  snippets: [] as Snippet[],
  snippetsLoaded: false,
  editingSnippet: null as number | "new" | null,
  blocked: [] as BlockedNumber[],
  /** Thread a follow-up is being added to. */
  addingTask: false,
  /** The call/text a follow-up is being added to (its timeline key), or "top". */
  taskFor: null as string | null,
  /** What the task being added is about, when it isn't the line it shows under (an internal comment). */
  taskRef: null as string | null,
  /** Item to flash after opening a search result ("sms:12", "comment:5"…). */
  focusRef: null as string | null,
  noteReply: null as Reply | null,
  teamNumber: "",
  prefs: null as UserPrefs | null,
  scheduledAll: [] as ScheduledText[],
  devices: null as { mics: MediaDeviceInfo[]; speakers: MediaDeviceInfo[]; canPickSpeaker: boolean } | null,
  micLevel: -1,
  quo: null as QuoImportStatus | null,
  /** Settings → Import from Quo → "Can my number move to Joy Phone?" */
  portInput: null as string | null,
  portBusy: false,
  portResults: null as { phone_number: string; portable: boolean; not_portable_reason: string; fast_portable: boolean }[] | null,
  portError: null as string | null,
  quoNumbers: null as { id: string; number: string; name: string | null }[] | null,
  quoPick: new Set<string>(),
  quoSince: "",
  quoRecordings: true,
  quoMarkOld: true,
  quoError: null as string | null,
  quality: null as CallQuality | null,
  regionTest: null as null | "running" | RegionResult[],
  /** Admin: where Telnyx handles call audio (null = not loaded yet, "error: …" when it couldn't be read). */
  mediaSite: null as null | "loading" | { site: string | null; sites: string[] } | { error: string },
  connTest: null as null | "running" | { browserMs: number | null; serverRegion: string | null; telnyxApiMs: number | null; error: string | null },
  /** Pictures / PDFs waiting to be sent, per composer. */
  attachments: {} as Record<string, (Attachment & { preview?: string })[]>,
  uploading: 0,
  speaker: false,
  recPaused: false,
  /** In-call sheet: pick who to add (3-way) or consult (warm transfer). */
  callSheet: null as null | "add" | "transfer",
  sheetNumber: "",
  schedulePicker: false,
  lookupEnabled: null as boolean | null,
  sec: null as null | { me: TwoFactorStatus; admin: { settings: SecuritySettings; countries: Record<string, string>; log: SecurityLogItem[] } | null },
  secDraft: null as SecuritySettings | null,
  secSetup: null as null | { secret: string; otpauthUrl: string; qr: string },
  countryQ: "",
  emailCfg: null as null | { settings: EmailSettings; configured: boolean; via?: string | null; lastError: string | null; replies?: EmailReplyStatus | null },
  emailDraft: "",
  /** The iPhone contacts password just created (shown once). */
  appPw: null as string | null,
  /** Conversation being assigned from a swipe. */
  assignFor: null as string | null,
  /** Your most-used emoji (server history + this device's picks), newest pick first. */
  topEmoji: [] as string[],
  office: null as { enabled: boolean; open: boolean } | null,
  /** Admins: the server's storage is nearly full (from the inbox). */
  storageWarn: null as StorageInfo | null,
  /** Selena is waiting for me to do one step in her browser (security check, password, code). */
  pendingTakeover: null as PendingTakeover | null,
  /** 4.10.21: her browser is open on a website for you: "View browser" (watch it live). */
  liveBrowser: null as { token: string | null; where: string; by?: string | null; starting?: "queued" | "opening" } | null,
  storageFull: null as { storage: StorageInfo | null; warnPct: number } | null,
};

// ---------------------------------------------------------------- theme

function storedTheme(): Theme {
  try {
    const t = localStorage.getItem("joy-theme");
    return t === "light" || t === "dark" ? t : "system";
  } catch {
    return "system";
  }
}

function applyTheme(t: Theme) {
  const el = document.documentElement;
  if (t === "system") delete el.dataset.theme;
  else el.dataset.theme = t;
  try {
    if (t === "system") localStorage.removeItem("joy-theme");
    else localStorage.setItem("joy-theme", t);
  } catch {
    // private mode: theme still applies for this visit
  }
  // Match the phone's status bar to the app background.
  requestAnimationFrame(() => {
    const bg = getComputedStyle(document.body).backgroundColor;
    document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute("content", bg));
  });
}
applyTheme(storedTheme());

/**
 * Brightness (Settings › Your preferences): a lever from Darker to Lighter, on this device. It shifts the app's
 * background colours (page, side panel, cards, lines) in whichever theme is showing; the middle = standard colours.
 */
const SHADE_MIN = -4;
const SHADE_MAX = 4;
function storedShade(): number {
  try {
    const v = Number(localStorage.getItem("joy-shade") ?? 0);
    return Number.isFinite(v) ? Math.max(SHADE_MIN, Math.min(SHADE_MAX, v)) : 0;
  } catch {
    return 0;
  }
}
function applyShade(v: number, save = true) {
  let tag = document.getElementById("joy-shade") as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement("style");
    tag.id = "joy-shade";
    document.head.append(tag);
  }
  // Plain hex colours (not color-mix), so the phone's status bar colour can match too.
  const blend = (a: string, b: string, t: number) => "#" + [1, 3, 5].map((i) =>
    Math.round(parseInt(a.slice(i, i + 2), 16) * (1 - t) + parseInt(b.slice(i, i + 2), 16) * t).toString(16).padStart(2, "0")).join("");
  const mix = (hex: string, light: boolean) =>
    v > 0 ? blend(hex, "#ffffff", (v * (light ? 1.5 : 4)) / 100) : blend(hex, light ? "#17202f" : "#000000", (-v * (light ? 4 : 9)) / 100);
  const set = (tokens: Record<string, string>, light: boolean) =>
    Object.entries(tokens).map(([k, hex]) => `--${k}: ${mix(hex, light)};`).join(" ");
  const dark = set({ canvas: "#17202f", sidebar: "#17202f", surface: "#1f2a3e", raised: "#263145", line: "#29344a", "line-strong": "#3d4a63" }, false);
  const light = set({ canvas: "#f7f7fa", sidebar: "#fbfbfd", surface: "#ffffff", raised: "#ffffff", line: "#e7e7ef", "line-strong": "#d9d9e4" }, true);
  tag.textContent = v === 0 ? "" : `
@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) { ${dark} } }
:root[data-theme="dark"] { ${dark} }
@media (prefers-color-scheme: light) { :root:not([data-theme="dark"]) { ${light} } }
:root[data-theme="light"] { ${light} }`;
  if (save) {
    try {
      if (v === 0) localStorage.removeItem("joy-shade");
      else localStorage.setItem("joy-shade", String(v));
    } catch {
      // private mode: applies for this visit
    }
  }
  requestAnimationFrame(() => {
    const bg = getComputedStyle(document.body).backgroundColor;
    document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute("content", bg));
  });
}
applyShade(storedShade(), false);
matchMedia("(prefers-color-scheme: dark)").addEventListener?.("change", () => storedTheme() === "system" && applyTheme("system"));

// Any tap arms the ringtone (browsers block sound until the user interacts). iPhone only counts a finished tap
// (touchend / click), not a finger going down, so listen to all of them.
for (const type of ["pointerdown", "pointerup", "touchend", "click", "keydown"]) document.addEventListener(type, () => ringer.unlock(), { passive: true, capture: true });

// Phones: swipe from the left edge to go back (the back arrow in the top corner is hard to reach).
// The page follows your finger; let go past a third of the way (or flick) and it goes back.
// The move/end listeners go on the touched element itself: if a live update redraws the screen mid-swipe, that
// element leaves the page and its touch events would no longer reach a listener on the document.
(() => {
  const EDGE = 36;
  const narrow = () => matchMedia("(max-width: 767px)").matches;
  const pane = () => document.querySelector<HTMLElement>(".pane-main");
  const backBtn = () => document.querySelector<HTMLElement>('.pane-main button[aria-label="Back"]');
  const reset = (el: HTMLElement, animate: boolean) => {
    el.style.transition = animate ? "transform 180ms ease" : "";
    el.style.transform = "";
    el.style.boxShadow = "";
    if (animate) setTimeout(() => (el.style.transition = ""), 200);
  };
  document.addEventListener("touchstart", (e) => {
    if (!narrow() || e.touches.length !== 1 || document.querySelector(".glass-overlay")) return;
    const t0 = e.touches[0];
    if (t0.clientX > EDGE) return;
    const back = backBtn();
    let el = pane();
    if (!back || !el || back.offsetParent === null) return;
    const target = e.target as EventTarget;
    const sx = t0.clientX, sy = t0.clientY, st = Date.now();
    let dx = 0, decided = false, horizontal = false;
    const move = (ev: TouchEvent) => {
      const t = ev.touches[0];
      if (!t) return;
      const mx = t.clientX - sx, my = t.clientY - sy;
      if (!decided && (Math.abs(mx) > 8 || Math.abs(my) > 8)) {
        decided = true;
        horizontal = mx > 0 && Math.abs(mx) > Math.abs(my) * 1.2;
        if (!horizontal) return void done();
      }
      if (!horizontal) return;
      // Ours now: stop the browser treating it as a scroll (it would also slow the touch updates down).
      if (ev.cancelable) ev.preventDefault();
      dx = Math.max(0, mx);
      const cur = pane();
      if (cur && cur !== el) {
        reset(el!, false);
        el = cur;
      }
      el!.style.transform = `translateX(${dx}px)`;
      el!.style.boxShadow = "-8px 0 24px rgba(0,0,0,0.25)";
    };
    const end = () => {
      done();
      if (!horizontal || !el) return;
      const fast = dx > 40 && dx / Math.max(1, Date.now() - st) > 0.5;
      const cur = pane() ?? el;
      if (dx > innerWidth / 3 || fast) {
        cur.style.transition = "transform 160ms ease-out";
        cur.style.transform = `translateX(${innerWidth}px)`;
        setTimeout(() => {
          (backBtn() ?? back).click();
          reset(cur, false);
        }, 150);
      } else reset(cur, true);
    };
    const opts = { passive: false } as AddEventListenerOptions;
    const done = () => {
      target.removeEventListener("touchmove", move as EventListener, opts);
      target.removeEventListener("touchend", end, opts);
      target.removeEventListener("touchcancel", end, opts);
    };
    target.addEventListener("touchmove", move as EventListener, opts);
    target.addEventListener("touchend", end, opts);
    target.addEventListener("touchcancel", end, opts);
  }, { passive: true });
})();
// Tapping outside an open menu closes it.
document.addEventListener("click", (e) => {
  if ((state.menuOpen || state.filterMenu || updateState.open) && !(e.target as HTMLElement).closest?.(".menu-wrap")) {
    state.menuOpen = false;
    state.filterMenu = false;
    updateState.open = false;
    render();
  }
  if (state.picker && !(e.target as HTMLElement).closest?.(".emoji-wrap")) {
    state.picker = null;
    render();
  }
});
// Esc closes an open menu, emoji picker or the "Assign to" picker too (dialogs handle their own Esc first).
document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  if (!(state.menuOpen || state.filterMenu || updateState.open || state.picker || state.assignFor)) return;
  e.preventDefault();
  state.menuOpen = false;
  state.filterMenu = false;
  updateState.open = false;
  state.picker = null;
  state.assignFor = null;
  render();
});

// Tapping anywhere else tucks away an open swipe's buttons.
document.addEventListener("pointerdown", (e) => {
  if (swipeOpen && !(e.target as HTMLElement).closest?.(".swipe-row.open")) closeSwipe();
}, true);

// Desktop: start typing a phone number anywhere to dial it (Enter calls). During a call, keys play tones.
document.addEventListener("keydown", (e) => {
  if (!state.user || e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
  const target = e.target as HTMLElement | null;
  if (target && (target.closest("input, textarea, select, [contenteditable=true]") || target.isContentEditable)) return;
  if (document.querySelector(".glass-overlay")) return;
  const key = e.key;
  const call = voice.call && !isEnded(voice.call) ? voice.call : null;
  if (call && call.state === "active" && /^[0-9*#]$/.test(key)) {
    sendTone(call, key);
    return;
  }
  if (call || !state.user.canCall) return;
  if (/^[0-9+*#]$/.test(key) || (key === "(" && state.route.name === "dialer")) {
    e.preventDefault();
    state.dialed += key;
    if (state.route.name !== "dialer") go({ name: "dialer" });
    else render();
    const d = document.getElementById("dial-display") as HTMLInputElement | null;
    if (d) {
      d.value = dialFormat(state.dialed);
      d.focus();
      d.setSelectionRange(d.value.length, d.value.length);
    }
  } else if (state.route.name === "dialer" && (key === "Backspace" || key === "Enter")) {
    e.preventDefault();
    if (key === "Enter") void dial(state.dialed);
    else {
      state.dialed = state.dialed.slice(0, -1);
      render();
    }
  } else if (state.route.name === "dialer" && key === "Escape") {
    state.dialed = "";
    go({ name: "inbox" });
  }
});

/** In-call key: you hear the beep instantly (local tone) while the real tone goes down the line. */
function sendTone(c: PhoneCall, k: string) {
  playDtmf(k);
  navigator.vibrate?.(8);
  try {
    c.dtmf(k);
  } catch {
    // the call just ended
  }
}

/**
 * Keypad buttons act on finger/mouse DOWN, not on release (a click fires ~100-150 ms later, and on a phone the
 * finger has to lift first). The click that follows is ignored; keyboard Enter/Space still works through click.
 */
// (Module-level, so a screen refresh between press and release can't make a key count twice.)
let keyDownAt = 0;
function pressKey(fn: () => void) {
  return {
    onpointerdown: (e: PointerEvent) => {
      if (e.button !== 0) return;
      keyDownAt = Date.now();
      fn();
    },
    onclick: (e: MouseEvent) => {
      if (e.detail !== 0 && Date.now() - keyDownAt < 1500) return; // already handled on pointer down
      fn();
    },
  };
}

// ---------------------------------------------------------------- boot

void boot();

async function boot() {
  const invite = /^\/invite\/([A-Za-z0-9_-]+)$/.exec(location.pathname);
  navigator.serviceWorker?.register("/sw.js").catch(() => {});
  if (invite) return renderInvite(invite[1]);
  const reset = /^\/reset\/([A-Za-z0-9_-]+)$/.exec(location.pathname);
  if (reset) return renderReset(reset[1]);
  // The inbox you were in last time (your personal line, for an admin with Personal mode).
  try {
    if (localStorage.getItem("joy-line") === "personal") {
      state.line = "personal";
      setApiLine("personal");
    }
  } catch {
    // not kept
  }
  startCrashWatch();
  readUrl(location.href);
  navigator.serviceWorker?.addEventListener("message", (e) => {
    if (e.data?.type === "ring-check") {
      // The service worker asks before showing a system "Incoming call" alert: if this window is in front and
      // already ringing on screen, it skips its alert so the call shows once (20.10).
      e.ports[0]?.postMessage({ shown: appShowsRing() });
      return;
    }
    if (e.data?.type === "open") {
      // Tell the service worker we got it, so it doesn't hand the same alert over again.
      navigator.serviceWorker.controller?.postMessage({ type: "opened", id: e.data.id });
      openFromAlert(e.data.url);
    }
  });
  // iPhone/Android can hand a tapped alert to a sleeping app before it's awake to hear it; ask on wake-up.
  claimPendingOpen();
  onMicReadyChange(() => state.user && render());
  // The Mic switch stays on until you turn it off (or 3 minutes without talking): see micguard.ts.
  startMicGuard(() => !!voice.call && !isEnded(voice.call), () => {
    render();
    toast("Mic turned off after 3 minutes without talking");
  });
  void checkMicPermission();
  // Ask for the mic up front (the first tap after opening the app), not when a call starts, so calls connect
  // without a permission prompt in the way. On iPhone this also keeps the mic warm (see micOn).
  const warmMic = () => {
    // Phones: only if you turned "keep the mic ready" on (off by default); computers just check the permission.
    if (!state.user?.canCall || micReady() || !micKeepReady()) return;
    if (isIOS() && !micOpensSilently()) return; // iPhone: no prompt you didn't ask for (tap the Mic switch)
    document.removeEventListener("pointerdown", warmMic, true);
    void micOn().then(() => render(), () => {});
  };
  document.addEventListener("pointerdown", warmMic, { capture: true, passive: true });
  setMfaHandler((gate) => {
    if (state.user || document.querySelector(".login")) void renderMfa(gate);
  });
  // A sign-out that hadn't reached the server yet: finish it, and stay signed out.
  if (pendingSignOut()) {
    await null; // let the rest of this file load first (the sign-in screen uses parts defined further down)
    renderLogin();
    void endServerSession();
    return;
  }
  try {
    const me = await api.me();
    if (me.mfa) return void renderMfa(me.mfa);
    state.user = me.user;
    state.vapidKey = me.vapidPublicKey;
    state.teamNumber = me.teamNumber;
    state.activityUnread = me.activityUnread ?? 0;
    await afterSignIn();
  } catch {
    renderLogin();
  }
}

/** Asks the service worker whether an alert was tapped that this window hasn't opened yet. */
function claimPendingOpen() {
  const sw = navigator.serviceWorker;
  if (!sw) return;
  const ask = () => {
    if (document.visibilityState !== "visible") return;
    const ch = new MessageChannel();
    ch.port1.onmessage = (e) => {
      if (e.data?.url) openFromAlert(e.data.url);
    };
    const target = sw.controller;
    if (target) target.postMessage({ type: "claim-open" }, [ch.port2]);
    else void sw.ready.then((r) => r.active?.postMessage({ type: "claim-open" }, [ch.port2]));
  };
  ask();
  document.addEventListener("visibilitychange", ask);
  window.addEventListener("focus", ask);
}

// ---------------------------------------------------------------- browser history (Back / Android back gesture)

/** Screens without a parameter, as /?page=<name> (so a reload stays put). A function: boot() reads it before consts load. */
function pageRoutes() {
  return ["dialer", "chat", "ai-agent", "settings", "team", "phone-settings", "contacts", "notes-to-profiles", "activity", "tasks", "analytics", "search", "snippets", "blocked",
  "settings-calling", "settings-notifications", "settings-sounds", "settings-audio", "call-settings", "scheduled", "import-quo", "security", "email-alerts", "storage", "billing", "phone-numbers", "new-group", "speed", "settings-rcs"]; // 1.10.28: speed, RCS reopen on reload
}
function urlFor(r: Route): string {
  if (r.name === "inbox") return "/";
  if (r.name === "thread") return `/?thread=${encodeURIComponent(r.number)}`;
  if (r.name === "contact") return `/?contact=${encodeURIComponent(r.number)}`;
  if (r.name === "chat") return "/?chat=1";
  if (r.name === "search" && r.q) return `/?page=search&q=${encodeURIComponent(r.q)}`;
  return `/?page=${r.name}`;
}
const routeKey = (r: Route) => JSON.stringify(r);
/** The app's own history entries (route keys by index), mirroring the browser's; navIdx = the current one. */
const navStack: string[] = [];
let navIdx = 0;
/** popstate events caused by our own history.back() (already handled). */
let ignorePops = 0;
type NavState = { joy: Route; idx: number };

/** After sign-in: the current screen becomes a history entry (an alert/reload that opened a screen gets the inbox behind it). */
function initHistory() {
  const r = state.route;
  const s = history.state as NavState | null;
  navStack.length = 0;
  if (s?.joy && typeof s.idx === "number" && s.idx > 0) {
    // reload: keep our place in the existing entries
    navIdx = s.idx;
    navStack[navIdx] = routeKey(r);
    history.replaceState({ joy: r, idx: navIdx } satisfies NavState, "", urlFor(r));
  } else if (r.name !== "inbox") {
    history.replaceState({ joy: { name: "inbox" }, idx: 0 } satisfies NavState, "", "/");
    navStack[0] = routeKey({ name: "inbox" });
    navIdx = 1;
    navStack[1] = routeKey(r);
    history.pushState({ joy: r, idx: 1 } satisfies NavState, "", urlFor(r));
  } else {
    navIdx = 0;
    navStack[0] = routeKey(r);
    history.replaceState({ joy: r, idx: 0 } satisfies NavState, "", "/");
  }
}

/** go() to a new screen: a new history entry (or a step back when it's the screen we just came from, e.g. the ← button). */
function recordNav(route: Route) {
  const k = routeKey(route);
  if (navStack[navIdx] === k) return;
  if (navIdx > 0 && navStack[navIdx - 1] === k) {
    navIdx--;
    ignorePops++;
    history.back();
    return;
  }
  navIdx++;
  navStack.length = navIdx;
  navStack[navIdx] = k;
  try {
    history.pushState({ joy: route, idx: navIdx } satisfies NavState, "", urlFor(route));
  } catch {
    // too many entries (Safari limits pushState): just stay on this screen's URL
  }
}

setViewerBackHook(() => ignorePops++);
window.addEventListener("popstate", (e) => {
  const s = e.state as NavState | null;
  if (ignorePops > 0) {
    ignorePops--;
    if (typeof s?.idx === "number") navIdx = s.idx;
    return;
  }
  if (viewerHandlesPop()) return; // Back closed the full-screen picture
  if (!state.user) return;
  const route: Route = s?.joy ?? { name: "inbox" };
  navIdx = typeof s?.idx === "number" ? s.idx : 0;
  navStack[navIdx] = routeKey(route);
  if (!s?.joy) history.replaceState({ joy: route, idx: navIdx } satisfies NavState, "", urlFor(route));
  go(route, true);
});

/** Alerts open the app at /?answer=<call>, /?thread=<number> or /?chat=1. */
function readUrl(href: string): boolean {
  const url = new URL(href, location.origin);
  const answer = url.searchParams.get("answer");
  const thread = url.searchParams.get("thread");
  const chat = url.searchParams.get("chat");
  const contact = url.searchParams.get("contact");
  const activity = url.searchParams.get("activity");
  const tasks = url.searchParams.get("tasks");
  const call = url.searchParams.get("call");
  const dialParam = url.searchParams.get("dial");
  const settingsParam = url.searchParams.get("settings");
  const page = url.searchParams.get("page");
  // "Selena needs you" alert: open her live browser.
  const takeover = url.searchParams.get("takeover");
  if (takeover && /^[\w-]{10,64}$/.test(takeover)) {
    history.replaceState(history.state, "", "/");
    // After sign-in, once the app is up.
    const open = () => (state.user ? openTakeover(takeover) : setTimeout(open, 500));
    setTimeout(open, 0);
  }
  if (settingsParam === "storage") {
    state.route = { name: "storage" };
    history.replaceState(history.state, "", "/");
    return true;
  }
  if (dialParam) {
    state.dialed = dialParam.replace(/^tel:/i, "").replace(/[^\d+*#]/g, "");
    state.route = { name: "dialer" };
    history.replaceState(history.state, "", "/");
  }
  if (answer) state.pendingAnswer = answer;
  const intercom = url.searchParams.get("intercom");
  if (intercom) {
    void pickupIntercomSoon(intercom);
    history.replaceState(history.state, "", "/");
  }
  if (page && pageRoutes().includes(page)) state.route = (page === "search" ? { name: "search", q: url.searchParams.get("q") ?? undefined } : { name: page }) as Route;
  // Alerts for the personal line say &line=personal; anything else about a conversation is the team's.
  // A link that says which line (any alert can), a conversation/call alert (Joy Travel's unless it says personal),
  // or Team chat (always Joy Travel's): open on that line, with everything that goes with it.
  const lineParam = url.searchParams.get("line");
  const linkLine: "team" | "personal" | null = lineParam === "personal" ? "personal"
    : lineParam === "team" || thread || call || url.searchParams.get("chat") ? "team" : null;
  if (linkLine && linkLine !== state.line && (linkLine === "team" || !state.user || (isAdmin() && state.personalLine))) {
    const line = linkLine;
    {
      state.line = line;
      setApiLine(line);
      if (state.user) {
        // The app is already running: switch fully (activity, follow-ups, Selena's chat and mode, this device's alerts).
        state.activity = [];
        state.activityLoaded = false;
        state.tasksLoaded = false;
        aiAgent.chat = [];
        aiAgent.chatLoaded = false;
        aiAgent.side = null;
        lineSwitchQuietUntil = Date.now() + 4000;
        void syncPushSubscription();
        if (isAdmin() && state.personalLine) void setSelenaMode(line === "personal" ? "personal" : "business", true);
      }
      // (At startup this runs before the thread cache exists; it's empty then anyway.)
      try {
        threadCache.clear();
      } catch {
        // not created yet
      }
      state.conversations = [];
      try {
        localStorage.setItem("joy-line", line);
      } catch {
        // not kept
      }
    }
  }
  if (thread) state.route = { name: "thread", number: thread };
  if (chat) state.route = { name: "chat" };
  if (contact) state.route = { name: "contact", number: contact };
  if (activity) state.route = { name: "activity" };
  if (tasks) state.route = { name: "tasks" };
  if (call) state.route = { name: "thread", number: call };
  // Which call / text / comment the alert was about: scroll to it and highlight it.
  const ref = url.searchParams.get("ref");
  if (ref && (thread || chat || contact || call)) state.focusRef = ref;
  if (answer || thread || chat || contact || activity || tasks || call || page) history.replaceState(history.state, "", "/");
  return !!(answer || thread || chat || contact || activity || tasks || call || dialParam || page);
}

/**
 * A teammate is calling (the notification was tapped). If it isn't already ringing here, wait for the phone line
 * to connect, then ask the server to ring this app again (it was closed or asleep when the call started).
 */
async function pickupIntercomSoon(callId: string) {
  const until = Date.now() + 15_000;
  while (Date.now() < until && !(state.user && voice.ready)) await new Promise((r) => setTimeout(r, 250));
  const c = voice.call;
  if (c && !isEnded(c) && c.direction === "inbound" && c.intercomName) return; // already ringing (or answered) here
  try {
    await api.intercomPickup(callId);
  } catch (e) {
    if (e instanceof ApiError && e.status === 410) toast("That call already ended");
  }
}

/** Call Selena (the AI assistant) from the app: a voice conversation; what you ask lands in her chat. */
let agentIdCache: number | null = null;
/**
 * 7.10.30: a call is being placed (from the tap until the call exists). Tapping Call twice (or Call then Enter)
 * placed two calls, and the first was lost with its mic open: the second tap does nothing now.
 */
let placingCall = false;
async function placing(start: () => Promise<unknown>) {
  if (placingCall) return;
  placingCall = true;
  try {
    await start();
  } finally {
    placingCall = false;
  }
}
async function callSelena() {
  return placing(callSelenaNow);
}
async function callSelenaNow() {
  if (!state.user?.canCall) return toast("Your account can't make calls");
  if (voice.call && !isEnded(voice.call)) return toast("Finish your call first");
  ringer.unlock();
  if (isIOS()) await micForCall();
  try {
    // 1.10.28: an admin's calls with Steven are always on their personal line (the server sees to it), and you stay
    // on the inbox you're on: nothing switches. Teammates (no personal line) call him on the business line.
    agentIdCache ??= (await api.agentInfo()).id;
    await voice.dialTeammate(agentIdCache, aiName(), state.user.name);
    // Selena answers at once (no ringing to wait for).
    if (voice.call) outAnswered.add(voice.call.id);
    syncOutgoingRing();
    render();
  } catch (e) {
    toast(`Couldn't call ${aiName()}: ${(e as Error).message}`);
  }
}

/** A tapped alert asked to open a page (app already running): go there properly, keeping any live call. */
function openFromAlert(href: string) {
  const before = state.route;
  const keepRef = state.focusRef;
  if (!readUrl(href)) return;
  const to = state.route;
  state.route = before; // let go() see where we came from (it resets per-conversation state)
  const ref = state.focusRef ?? keepRef;
  if (state.user) go(to);
  else state.route = to;
  state.focusRef = ref;
  if (state.user) render();
}

let signedInOnce = false;
/** This teammate's own ringtone and call sounds (saved on the server, so nobody else's choice changes them). */
function loadMySounds() {
  const id = state.user?.id;
  if (id == null) return;
  setSoundUser(id, null); // this device's copy right away, so a call ringing now uses their ringtone
  const save = (p: SoundPrefs) => void api.savePrefs({ sounds: p }).catch(() => {});
  void api.prefs().then((r) => {
    if (state.user?.id !== id) return;
    setSoundUser(id, r.prefs.sounds ?? null, save);
    alertsKeptOn = !!r.alertsLocked;
    minVolume = r.minVolume ?? 0;
    // Kept on by an admin: this device's sound comes up to the floor too (1.10.28).
    if (getSoundPrefs().volume < minVolume) setSoundPrefs({ volume: minVolume });
    // …and a "None" sound chosen on this device before plays the default instead.
    if (alertsKeptOn) {
      const sp = getSoundPrefs();
      const fix: Partial<SoundPrefs> = {};
      if (sp.ringtone === "none") fix.ringtone = "sunrise";
      if (sp.text === "none") fix.text = "ding";
      if (sp.mention === "none") fix.mention = "pop";
      if (Object.keys(fix).length) setSoundPrefs(fix);
    }
  }, () => {
    if (state.user?.id === id) setSoundUser(id, getSoundPrefs(), save);
  });
}

async function afterSignIn() {
  markSignedOut(false); // signed in again on purpose
  keepMembersOnTeamLine();
  loadMySounds();
  watchCallSpeed();
  initHistory();
  // Opened straight on a page (reload, ?page= link): run its loader, as tapping there would.
  if (sectionOf(state.route) === "none") go(state.route, true);
  else render();
  if (!signedInOnce) {
    signedInOnce = true;
    voice.onChange(onVoiceChange);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && state.user) {
        if (state.agentUnread && seeingSelenaChat()) markAgentRead();
        void checkForUpdate();
        void voice.connect().catch(() => {});
        if (!events || events.readyState === EventSource.CLOSED) startLiveUpdates();
        void refresh();
      }
    });
  }
  // A failed connection shows as a slim bar over the inbox (see connBar), not a pop-up over the header.
  if (state.user?.canCall) void voice.connect().catch(() => render());
  rerenderAny = () => void (state.user && render());
  void checkForUpdate();
  startLiveUpdates();
  void syncPushSubscription();
  void loadTeam();
  void loadPresence();
  lastActiveSent = 0;
  imActive();
  await refresh();
}

/** Makes sure this device's notifications go to whoever is signed in now (and still exist on the server). */
async function syncPushSubscription() {
  try {
    if (!alertsOn() || !state.vapidKey || !("serviceWorker" in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToBytes(state.vapidKey) }));
    await api.subscribePush(sub.toJSON());
  } catch {
    // the Enable button in Settings can retry
  }
}

/**
 * Sign-out is remembered on this device until the server confirms it, so a sign-out that couldn't reach the
 * server (offline, mid-update) can't leave the session alive and reopen the app signed in.
 */
// Function declarations (not consts): boot() runs before this part of the file has loaded.
function markSignedOut(on: boolean) {
  try {
    if (on) localStorage.setItem("joy.signedOut", "1");
    else localStorage.removeItem("joy.signedOut");
  } catch {
    // private mode
  }
}
function pendingSignOut() {
  try {
    return localStorage.getItem("joy.signedOut") === "1";
  } catch {
    return false;
  }
}
/**
 * Signing in again cancels a sign-out still in progress: the remembered sign-out is cleared the moment the
 * person starts signing in, so a reload halfway through (iPhone does this when you switch apps, say to approve
 * from your computer) can't end the new session.
 */
let signInGen = 0;
/** A sign-out request on its way: its answer clears the session cookie, so a new sign-in waits for it first. */
let logoutInFlight: Promise<unknown> | null = null;
/** Forget everything the last person signed in here could see (their personal line above all), so the next
 *  person on this browser never sees a flash of it. */
function resetUserState() {
  state.line = "team";
  setApiLine("team");
  try {
    localStorage.removeItem("joy-line");
  } catch {
    // not kept
  }
  state.personalLine = null;
  state.teamUnread = 0;
  state.agentUnread = 0;
  state.conversations = [];
  state.live = [];
  state.thread = null;
  state.chat = [];
  state.chatReads = [];
  state.team = [];
  state.presence = [];
  state.invites = [];
  state.drafts = {};
  state.search = "";
  state.searchContacts = null;
  state.reply = null;
  state.editingMsg = null;
  state.typing = {};
  state.teamSites = null;
  state.route = { name: "inbox" };
  threadCache.clear();
  inboxLoaded = false;
  aiAgent.calls = []; aiAgent.loaded = false; aiAgent.chat = []; aiAgent.chatLoaded = false; aiAgent.settings = null;
  aiAgent.pa = null; aiAgent.paDraft = null; aiAgent.mode = "business"; aiAgent.side = null; aiAgent.error = "";
  aiAgent.form = { to: "", who: "", ask: "", details: "", book: false };
  // 1.10.28: nothing of the last person's is left for whoever signs in next on this browser (a contacts-app
  // password, a two-step setup key, unsaved settings, search results, tasks, notes moves were all still showing).
  state.appPw = null; appPwList = null;
  state.sec = null; state.secDraft = null; state.secSetup = null;
  state.prefs = null; state.phoneDraft = null; state.phoneSaving = false; state.emailCfg = null; state.mediaSite = null;
  state.tasks = []; state.tasksLoaded = false; state.activity = []; state.activityLoaded = false; state.activityUnread = 0;
  state.contacts = []; state.contactsLoaded = false; state.searchHits = null; state.searchQ = "";
  state.snippets = []; state.snippetsLoaded = false; state.blocked = []; state.analytics = null; state.focusRef = null;
  notesMove.job = null; notesMove.loaded = false; notesMove.asked = false; notesMove.busy = false; notesMove.chosen.clear(); clearTimeout(notesMove.timer);
  hdVoice = null;
  alertsKeptOn = false; minVolume = 0; teamAlertsLocked = false;
}
/** Only an admin ever has a personal line: anyone else is kept on the team's inbox. */
function keepMembersOnTeamLine() {
  if (isAdmin()) return;
  if (state.line === "personal") {
    state.line = "team";
    setApiLine("team");
  }
  state.personalLine = null;
  try {
    localStorage.removeItem("joy-line");
  } catch {
    // not kept
  }
}
async function startingSignIn() {
  signInGen++;
  resetUserState();
  markSignedOut(false);
  if (logoutInFlight) await atMost(logoutInFlight, 4000);
}
/** Ends the session on the server; tries a few times. True when the server confirmed it. */
async function endServerSession(): Promise<boolean> {
  const gen = signInGen;
  for (let i = 0; i < 3; i++) {
    if (gen !== signInGen) return false; // they're signing in again: leave the new session alone
    try {
      logoutInFlight = api.logout();
      await logoutInFlight;
      logoutInFlight = null;
      if (gen !== signInGen) return true; // signed in again meanwhile: don't touch the flag
      markSignedOut(false);
      return true;
    } catch (e) {
      logoutInFlight = null;
      // 401: the session was already gone, which is what we want.
      if ((e as { status?: number }).status === 401) {
        markSignedOut(false);
        return true;
      }
      await new Promise((r) => setTimeout(r, 800 * (i + 1)));
    }
  }
  return false;
}
/** Waits for a step, but never more than `ms` (a stuck phone line or push service mustn't block sign-out). */
function atMost(p: Promise<unknown>, ms: number) {
  return Promise.race([p.catch(() => {}), new Promise((r) => setTimeout(r, ms))]);
}

async function signOut() {
  markSignedOut(true);
  try {
    localStorage.removeItem("joy-agent:team");
    localStorage.removeItem("joy-agent:personal"); // the next person to sign in here starts with their own names
  } catch {
    // nothing saved
  }
  ringer.stop();
  events?.close();
  events = null;
  for (const a of players.values()) a.pause();
  state.user = null;
  // 1.10.28: unsent drafts are the person's own (the next one to sign in found them, ready to send as theirs).
  try {
    localStorage.removeItem("joy-drafts");
  } catch {
    // nothing saved
  }
  resetUserState();
  setSoundUser(null, null);
  try {
    void (navigator as Navigator & { clearAppBadge?: () => Promise<void> }).clearAppBadge?.().catch(() => {});
  } catch {
    // not supported
  }
  // Stop push alerts first (needs the session), then end the session; the phone line disconnects alongside.
  const line = atMost(voice.disconnect(), 3000);
  await atMost((async () => {
    const reg = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await api.unsubscribePush(sub.endpoint).catch(() => {});
      await sub.unsubscribe().catch(() => {});
    }
  })(), 3000);
  renderLogin();
  const ok = await endServerSession();
  await line;
  if (!ok) toast("Logged out on this phone. It will finish logging out with the server once it's back online.");
}

/** The session ended from somewhere else (password changed on another device): stop ringing and the phone line here too. */
async function signedOutElsewhere() {
  ringer.stop();
  events?.close();
  events = null;
  for (const a of players.values()) a.pause();
  renderLogin();
  await voice.disconnect().catch(() => {});
}

// ---------------------------------------------------------------- data

/** Recently opened conversations, so going back to one shows it instantly while it refreshes. */
type ThreadData = { thread: Thread; contact: ContactDetail | null; tasks: Task[] };
const threadCache = new Map<string, ThreadData>();
const threadInflight = new Map<string, Promise<ThreadData | null>>();
const threadSeq = new Map<string, number>();
let threadSeqNext = 0;
/** `fresh`: don't reuse a request that started earlier (it may miss what just changed, e.g. a text we sent). */
/** `only`: a reload after live news that wasn't about the contact or its tasks keeps the ones already loaded. */
function fetchThread(number: string, fresh = false, only?: { contact: boolean; tasks: boolean }): Promise<ThreadData | null> {
  if (isInternalKey(number)) return Promise.resolve(null); // calls inside the team: loadInternal()
  const running = threadInflight.get(number);
  if (running && !fresh) return running;
  const seq = ++threadSeqNext;
  threadSeq.set(number, seq);
  const had = threadCache.get(number);
  const p = Promise.all([
    api.thread(number),
    had && only && !only.contact ? Promise.resolve(had.contact)
      : canText(number) && !isGroup(number) ? api.contact(number).catch(() => null) : Promise.resolve(null),
    had && only && !only.tasks ? Promise.resolve(had.tasks) : api.tasks({ number }).then((x) => x.tasks).catch(() => [] as Task[]),
  ]).then(([thread, contact, tasks]) => {
    const data = { thread, contact, tasks };
    // An older request finishing after a newer one must not replace the newer data.
    if (threadSeq.get(number) !== seq) return threadCache.get(number) ?? data;
    threadCache.delete(number);
    threadCache.set(number, data);
    if (threadCache.size > 30) threadCache.delete(threadCache.keys().next().value!);
    return data;
  }).catch(() => null).finally(() => {
    if (threadInflight.get(number) === p) threadInflight.delete(number);
  });
  threadInflight.set(number, p);
  return p;
}
function applyThreadData(d: ThreadData) {
  rememberMembers(d.thread.members);
  if (isGroup(d.thread.number)) rememberGroupName(d.thread.number, d.thread.groupName);
  state.thread = withOlder(d.thread);
  state.contact = d.contact;
  state.threadTasks = d.tasks;
}
/** Warm the cache when a finger or the mouse goes down on a conversation (the click lands ~100 ms later). */
let prefetchTimer: number | undefined;
function prefetchThread(number: string, delay = 0) {
  clearTimeout(prefetchTimer);
  if (isInternalKey(number)) return;
  if (threadInflight.has(number)) return;
  const cached = threadCache.get(number);
  const conv = state.conversations.find((c) => c.external_number === number);
  // Fresh enough: nothing new since we cached it.
  if (cached && conv && lastActivity(cached.thread) >= (serverTime(conv.activity_at)?.getTime() ?? 0)) return;
  prefetchTimer = window.setTimeout(() => void fetchThread(number), delay);
}
document.addEventListener("scroll", () => clearTimeout(prefetchTimer), { capture: true, passive: true });
function lastActivity(t: Thread): number {
  let max = 0;
  for (const c of t.calls) max = Math.max(max, serverTime(c.started_at)?.getTime() ?? 0);
  for (const m of t.messages) max = Math.max(max, serverTime(m.created_at)?.getTime() ?? 0);
  for (const n of t.comments) max = Math.max(max, serverTime(n.created_at)?.getTime() ?? 0);
  return max;
}

/** Speed: one refresh at a time. Asks that come in while one runs fold into a single follow-up. */
let refreshing: Promise<void> | null = null;
let refreshAgain = false;
let lastRefreshDone = 0;
/** The live stream connected once already (a later open is a reconnect after a gap). */
let streamOpenedBefore = false;
/**
 * Whether the next refresh re-downloads the open conversation. Live events only need it when they're about that
 * number (or don't say which): a text from another customer used to re-download and rebuild a long open thread on
 * every event (~0.2–0.3 s on a computer, ~1 s on a phone, measured 9.10.18). Every other refresh always does.
 */
let threadWanted = true;
/** What the live news for the open conversation was about ("" = unknown: reload everything). */
let threadKinds = new Set<string>();
function refresh(from: "events" | "app" = "app"): Promise<void> {
  if (from === "app") threadWanted = true;
  if (refreshing) {
    refreshAgain = true;
    return refreshing;
  }
  refreshing = (async () => {
    do {
      refreshAgain = false;
      await refreshOnce();
    } while (refreshAgain);
  })().finally(() => ((refreshing = null), (lastRefreshDone = Date.now())));
  return refreshing;
}

const callLogBusy = new Set<string>();
const callLogAgain = new Set<string>();
async function onCallLog(callId: string, number: string) {
  if (state.route.name !== "thread" || state.route.number !== number) return;
  const call = state.thread?.calls?.find((c) => c.id === callId);
  if (!call) return void ((threadWanted = true), refresh("events")); // a call this screen hasn't shown yet
  if (callLogBusy.has(callId)) return void callLogAgain.add(callId); // one at a time; the newest wins
  callLogBusy.add(callId);
  try {
    const r = await api.callAiLog(callId);
    const cur = state.thread?.calls?.find((c) => c.id === callId);
    if (cur) {
      Object.assign(cur, { aiLog: r.aiLog, liveLog: r.liveLog ?? cur.liveLog, status: r.status, on_hold: r.on_hold });
      render();
    }
  } catch {
    // the next update brings it
  } finally {
    callLogBusy.delete(callId);
    if (callLogAgain.delete(callId)) void onCallLog(callId, number);
  }
}

let contactsLoadedAt = 0;
let analyticsLoadedAt = 0;
async function refreshOnce() {
  try {
    // The open conversation loads alongside the inbox (not after it), so it shows up in one round trip.
    const r0 = state.route;
    const wantThread = threadWanted;
    threadWanted = false;
    const kinds = threadKinds;
    threadKinds = new Set();
    // After live news, only what it was about: a new note or text doesn't refetch the contact card and the tasks
    // (19.10.18: every event made every open app fetch four things; a small server choked on it).
    const only = kinds.size && !kinds.has("") ? { contact: kinds.has("contact.updated"), tasks: kinds.has("tasks.updated") } : undefined;
    if (r0.name === "thread" && isInternalKey(r0.number)) void loadInternal(r0.number);
    const threadData = r0.name === "thread" && wantThread ? fetchThread(r0.number, true, only) : null;
    const contactData = r0.name === "contact" ? api.contact(r0.number).catch(() => null) : null;
    const chatData = r0.name === "chat" ? api.chat().catch(() => null) : null;
    if (r0.name === "ai-agent") void (aiAgent.tab === "calls" ? loadAiCalls() : loadAgentChat());
    const line0 = state.line;
    const inbox = await api.inbox();
    if (state.line !== line0) return; // switched inboxes meanwhile: the next refresh brings the right one
    state.personalLine = isAdmin() ? inbox.personal ?? null : null;
    state.teamUnread = inbox.team?.unread ?? 0;
    // Personal mode was turned off (or isn't allowed any more): back to the team's inbox.
    if (state.line === "personal" && !state.personalLine) {
      switchLine("team");
      return;
    }
    // Sounds for things that arrive while the app is open (calls ring separately).
    if (inboxLoaded && Date.now() > lineSwitchQuietUntil) {
      const unreadTexts = (list: Conversation[]) => list.filter((c) => c.unread && c.last_kind === "sms" && c.sms_direction === "inbound").length;
      if (unreadTexts(inbox.conversations) > unreadTexts(state.conversations)) playTextSound();
      else if ((inbox.activity?.unread ?? 0) > state.activityUnread || (state.line !== "personal" && inbox.chat.unread > state.chatSummary.unread) || (inbox.agent?.unread ?? 0) > state.agentUnread) playMentionSound();
    }
    for (const c of inbox.conversations) (rememberMembers(c.members), isGroup(c.external_number) && rememberGroupName(c.external_number, c.groupName));
    state.conversations = inbox.conversations;
    state.live = inbox.live;
    for (const c of [...inbox.conversations, ...inbox.live]) if (c.caller_name) callerNames.set(c.external_number, c.caller_name);
    state.chatSummary = inbox.chat;
    state.agentUnread = inbox.agent?.unread ?? 0;
    if (inbox.agents) state.agents = inbox.agents;
    if (inbox.agentName) {
      state.agentName = inbox.agentName;
      state.agentMan = !!inbox.agentMan;
      try {
        localStorage.setItem(`joy-agent:${state.line === "personal" ? "personal" : "team"}`, JSON.stringify({ name: inbox.agentName, man: !!inbox.agentMan })); // the right name from the first paint next time
      } catch {
        // not kept
      }
    }
    if (state.agentUnread && seeingSelenaChat()) markAgentRead();
    state.activityUnread = inbox.activity?.unread ?? 0;
    state.office = inbox.hours ?? null;
    state.storageWarn = inbox.storageWarn ?? null;
    state.pendingTakeover = inbox.takeover ?? null;
    state.liveBrowser = inbox.liveBrowser ?? null;
    syncOutgoingRing(); // the other side of an outgoing call may have just answered
    inboxLoaded = true;
    syncBadge();
    const r = state.route;
    // Only use what was fetched for the screen that was open when the fetch started (r0) if it's still open.
    if (r0.name === "thread") {
      const data = await threadData;
      if (data && state.route.name === "thread" && state.route.number === r0.number) {
        applyThreadData(data);
        if (document.visibilityState === "visible") markThreadRead(r0.number);
      }
    }
    if (r0.name === "contact") {
      const c = await contactData;
      if (c) contactCache.set(r0.number, c);
      if (c && state.route.name === "contact" && state.route.number === r0.number) state.contact = c;
    }
    // The big lists reload at most every 30 s (contacts) or 60 s (analytics) on live news: each one is a big read
    // for the server, and a busy day sends news every few seconds.
    if ((r.name === "contacts" || r.name === "contact" || sectionOf(r) === "contacts") && Date.now() - contactsLoadedAt > 30_000) ((contactsLoadedAt = Date.now()), void loadContacts());
    if (r.name === "activity") void loadActivity();
    if (r.name === "tasks") void loadTasks();
    if (r.name === "analytics" && Date.now() - analyticsLoadedAt > 60_000) ((analyticsLoadedAt = Date.now()), void loadAnalytics());
    if (r.name === "chat") {
      const chat = (await chatData) ?? (await api.chat());
      setChat(chat.messages);
      state.chatReads = chat.reads ?? [];
      chatLoaded = true;
      if (document.visibilityState === "visible" && state.chatSummary.unread) {
        state.chatSummary.unread = 0;
        void api.chatRead().catch(() => {});
        clearShownNotifications((url) => url.includes("chat=1"));
      }
    }
    refreshFails = 0;
    clearTimeout(refreshRetry);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return void signedOutElsewhere();
    // Didn't load (no signal yet, a dead connection after sleep): try again soon, a bit slower each time, instead
    // of leaving the inbox half empty until a manual reload.
    refreshFails++;
    clearTimeout(refreshRetry);
    refreshRetry = window.setTimeout(() => void refresh(), Math.min(30_000, 1000 * 2 ** Math.min(refreshFails - 1, 5)));
  }
  render();
}
let refreshFails = 0;
let refreshRetry = 0;
window.addEventListener("online", () => state.user && void refresh());

async function toggleDnd(on: boolean) {
  if (on && alertsKeptOn) return toast("Your admin keeps alerts on for the team, so Do Not Disturb stays off.");
  try {
    state.user = (await api.setDnd(on)).user;
    toast(on ? "Do Not Disturb on: you won't ring for new calls" : "Do Not Disturb off: you'll ring again");
    void loadTeam();
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

/** The red number on the app icon: unread conversations + team chat + activity (same as alerts send). */
function syncBadge() {
  // On your personal line: only that line's counts (Team chat is Joy Travel's).
  const n = needsYouCount() + otherLineUnread() + (state.line === "personal" ? 0 : state.chatSummary.unread) + state.activityUnread + state.agentUnread;
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  try {
    if (n > 0) void nav.setAppBadge?.(n).catch(() => {});
    else void nav.clearAppBadge?.().catch(() => {});
  } catch {
    // not supported
  }
}

/**
 * The count on the inbox (Joy Travel in the sidebar, the Inbox tab, the app icon): conversations with a customer
 * text or voicemail, or a teammate's internal comment, you haven't seen. A call on its own doesn't count.
 */
/** Switches between the Joy Travel inbox and your personal line (admins with Personal mode). */
function switchLine(line: "team" | "personal", to: Route = { name: "inbox" }) {
  if (line === "personal" && !(isAdmin() && state.personalLine)) line = "team";
  const changed = state.line !== line;
  state.line = line;
  setApiLine(line);
  if (changed) voice.recheckIgnored(); // a call still ringing for this line now rings here
  try {
    localStorage.setItem("joy-line", line);
  } catch {
    // not kept
  }
  if (changed) {
    // Each line has its own conversations: nothing from the other one may show for a moment.
    threadCache.clear();
    state.conversations = [];
    // Activity and follow-ups are per line too: reload them for this one (no "new" sound for what was already there).
    state.activity = [];
    state.activityLoaded = false;
    state.tasksLoaded = false;
    lineSwitchQuietUntil = Date.now() + 4000;
    aiAgent.chat = []; // Selena's chat is separate per line too
    aiAgent.chatLoaded = false;
    void syncPushSubscription(); // this device's alerts follow the line: no Joy Travel pop-ups on your personal line
    if (state.route.name === "activity" || to.name === "activity") void loadActivity();
    if (state.route.name === "tasks" || to.name === "tasks") void loadTasks();
    // Selena follows the line: business on Joy Travel, your personal assistant on your own phone.
    aiAgent.side = null;
    if (isAdmin() && state.personalLine) void setSelenaMode(line === "personal" ? "personal" : "business", true);
  }
  go(to);
  if (changed) void refresh();
}
/** Unread on the line you're not looking at (for the sidebar, the phone switch and the app badge). */
function otherLineUnread(): number {
  // Total separation: nothing from the other line shows on this one (no count, no dot), either way.
  return 0;
}

/** iOS 27+ Safari: WebKit bug 326286 (call audio crackles while the mic runs with echo cancellation). */
function iosCrackleBug(): boolean {
  const ua = navigator.userAgent;
  if (!/(iPhone|iPad)/.test(ua) && !(navigator.maxTouchPoints > 1 && /Macintosh/.test(ua))) return false;
  const v = /Version\/(\d+)/.exec(ua);
  if (v) return Number(v[1]) >= 27;
  // The home-screen app has no "Version/" in its browser ID: Safari 27 (iOS 27) is the first with jitterBufferTarget.
  return typeof RTCRtpReceiver !== "undefined" && "jitterBufferTarget" in RTCRtpReceiver.prototype;
}

function needsYouCount() {
  return state.conversations.filter((c) => (c.needs_you ?? (c.unread ? 1 : 0)) && c.conv_status !== "done").length;
}

/** Takes this app's notifications off the phone / computer once what they were about is on screen. */
function clearShownNotifications(match: (url: string) => boolean) {
  void navigator.serviceWorker?.getRegistration().then((reg) => reg?.getNotifications()).then((list) => {
    for (const n of list ?? []) if (match(String((n.data as { url?: string } | null)?.url ?? ""))) n.close();
  }).catch(() => {});
}

function markThreadRead(number: string) {
  if (isInternalKey(number)) return; // read when its calls load (GET /internal)
  const c = state.conversations.find((x) => x.external_number === number);
  const wasNew = !!c && (!!c.unread || !!c.comment_count || !!c.needs_you);
  if (c?.unread) c.unread = false;
  if (c) {
    c.comment_count = 0; // seen now: the yellow comment count goes away
    c.needs_you = 0;
  }
  syncBadge();
  // Only when there was something to mark: every live update used to send one, from every open app.
  if (!c || wasNew) void api.markRead(number).catch(() => {});
  clearShownNotifications((url) => url.includes(`thread=${encodeURIComponent(number)}`));
}

/** Who's online, idle, on Do Not Disturb or offline (sidebar). */
async function loadPresence() {
  try {
    const next = (await api.presence()).presence;
    // Redraw only what changed (17.10.14): every time any teammate opened a conversation, every phone rebuilt its
    // whole screen (~1 s each on a phone with a long chat). Only a teammate's colour/status needs the full screen;
    // who's looking at which conversation only shows on inbox rows; your own "viewing" echo shows nowhere.
    const me = state.user?.id;
    const key = (list: typeof next, withViewing: boolean) =>
      JSON.stringify(list.map((p) => [p.id, p.status, p.since ?? null, withViewing && p.id !== me ? p.viewing ?? null : null]));
    const prev = state.presence;
    state.presence = next;
    if (key(prev, false) !== key(next, false)) render();
    else if (key(prev, true) !== key(next, true)) renderListSoon();
  } catch {
    // keeps the last known statuses
  }
}
// Tell the server you're using the app (at most every 20 s) so teammates see you green; 15 minutes with the app
// off screen (no tap, key or scroll, and not showing) and you show as away (yellow).
let lastActiveSent = 0;
function imActive(e?: Event) {
  if (!state.user || document.visibilityState !== "visible" || Date.now() - lastActiveSent < 20_000) return;
  // Only real use: a mouse passing over (or scrolling) a window that's in the background on a computer isn't
  // "in the app" (8.10.18). Taps, keys and switching to the app count.
  if (e && (e.type === "pointermove" || e.type === "wheel") && !document.hasFocus()) return;
  lastActiveSent = Date.now();
  void api.imActive().catch(() => {});
}
for (const type of ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "focus"]) window.addEventListener(type, imActive, { passive: true, capture: true });
document.addEventListener("visibilitychange", imActive);
// Just looking at the app counts too (19.10.18): while it's on screen (a phone in hand, the window showing on a
// computer), a quiet "still here" every minute keeps you green. Minimized, in the background or locked: not.
setInterval(() => {
  if (document.visibilityState === "visible") imActive();
}, 60_000);
// "Active 3m ago" for admins stays current.
// "Active 3m ago" times in the side rail: computers only (the rail is hidden on phones, where this was a full redraw).
setInterval(() => isAdmin() && isWide() && state.presence.length && rerenderAny(), 30_000);

async function loadTeam(tries = 0) {
  try {
    const t = await api.team();
    state.team = t.users;
    state.invites = t.invites;
    teamAlertsLocked = !!t.alertsLocked;
    render();
  } catch (e) {
    // Names and faces come from here: try again rather than leave them blank.
    if (!(e instanceof ApiError) && tries < 6) setTimeout(() => void loadTeam(tries + 1), 1500 * (tries + 1));
  }
}

let events: EventSource | null = null;
let inboxLoaded = false;
/** Right after switching lines the other line's counts arrive: not a reason to play a sound. */
let lineSwitchQuietUntil = 0;
/** Last time the server said anything (the server pings every 25s). A silent stream after sleep or a network change is dead. */
let lastHeard = Date.now();
setInterval(() => {
  if (state.user && events && Date.now() - lastHeard > 60_000) {
    lastHeard = Date.now();
    startLiveUpdates();
  }
}, 15_000);
function startLiveUpdates() {
  events?.close();
  const es = new EventSource("/api/events"); // the session cookie rides along
  events = es;
  lastHeard = Date.now();
  let pending: number | undefined;
  es.addEventListener("ping", () => (lastHeard = Date.now()));
  // (Re)connected while the app is on screen (e.g. after a server restart): you're here, so stay green. In the
  // background this does nothing (imActive checks the app is visible).
  es.addEventListener("open", () => ((lastActiveSent = 0), imActive()));
  es.onmessage = (m) => {
    lastHeard = Date.now();
    try {
      const e = JSON.parse(m.data);
      if (e.type === "typing") return void onTypingEvent(e);
      // 2.10.29: answered on another phone: stop ringing here this instant (not a second or two later, when the phone
      // system's cancel arrives), and close its window. The usual inbox refresh follows below.
      // 11.10.30: or you pressed Answer on another of your devices (your phone): this one stops ringing now. Not on the
      // device that pressed it (it's answering; answeredElsewhere leaves a call it's answering alone).
      if ((e.type === "call.answered" || (e.type === "call.answering" && e.userId === state.user?.id && answering !== e.callId)) && typeof e.callId === "string") {
        voice.answeredElsewhere(e.callId);
        if (answering !== e.callId) state.glassDismissed.add(e.callId);
        if (!(voice.call && voice.call.direction === "inbound" && voice.call.state === "ringing" && !hiddenRing(voice.call))) ringer.stop();
        render();
      }
      // Her live transcript grew: only the app showing that call fetches it (no inbox or conversation reload).
      if (e.type === "call.log") return void onCallLog(e.callId, e.externalNumber);
      // Someone else's private chat with Selena: nothing to reload here.
      if (e.type === "agent.updated" && e.userId !== state.user?.id) return;
      // A call you placed didn't go through (busy, no answer): say why.
      if (e.type === "call.failed" && e.userId === state.user?.id && typeof e.reason === "string") toast(e.reason);
      // A teammate picked up your call to them: stop the ring tone, start the timer.
      if (e.type === "intercom.answered" && e.userId === state.user?.id) {
        if (voice.call) outAnswered.add(voice.call.id);
        syncOutgoingRing();
        render();
        return;
      }
      if (e.type === "team.updated") (void loadTeam(), void loadPresence());
      if (e.type === "presence.updated") return void loadPresence();
      // An admin switched the office number (Settings → Phone numbers): the sidebar shows the new one.
      if ((e as { type: string }).type === "settings.updated") void api.me().then((m) => {
        if (m.teamNumber !== state.teamNumber) ((state.teamNumber = m.teamNumber), render());
      }).catch(() => {});
      // An admin moved your calling login to another media site: reconnect so the next call uses it.
      if (e.type === "line.reconnect") {
        if (e.userId === state.user?.id) voice.reconnectLine();
        return;
      }
      if (e.type === "team.updated") state.teamSites = null;
      if (e.type === "signin.approval" && e.userId === state.user?.id) return void checkApprovals();
      if (e.type === "snippets.updated") state.snippetsLoaded = false;
      if (e.type === "import.progress" && state.route.name === "import-quo") void loadQuo(false);
      if (e.type === "consult.failed" && e.userId === state.user?.id) toast(e.answered ? `${e.toName} hung up. You're back with the caller.` : `${e.toName} didn't answer. You're back with the caller.`);
      if (e.type === "participant.failed" && e.userId === state.user?.id) toast(`${e.toName} didn't answer`);
      if (e.type === "tasks.updated" && state.route.name === "tasks") void loadTasks();
      // A teammate read Team chat: only the chat screen shows it (seen checks).
      // 7.10.30: you read it on another device: reload here too, so the chat badge clears (it stayed on).
      if (e.type === "chat.read" && (e.userId === state.user?.id ? state.route.name === "chat" : state.route.name !== "chat")) return;
      if ((e.type === "transfer.done" || e.type === "transfer.failed") && e.userId === state.user?.id) onTransferResult(e.type, e.toName, !!e.stillOnHold);
      // Someone else's Activity changed (a mention of them): nothing here to reload (9.10.18: every app reloaded).
      if (e.type === "activity.updated" && typeof e.userId === "number" && e.userId !== state.user?.id) return;
      // Nothing in the inbox changed: skip reloading it (an import sends many progress updates).
      if (e.type === "import.progress" || e.type === "snippets.updated" || e.type === "team.updated") return;
      if (e.type === "tasks.updated" && state.route.name !== "thread") return;
    } catch {
      // keep-alive comments
    }
    // The open conversation reloads only for news about it (or news that doesn't say which number it's about).
    try {
      const ev = JSON.parse(m.data) as { externalNumber?: unknown; number?: unknown };
      const num = typeof ev.externalNumber === "string" ? ev.externalNumber : typeof ev.number === "string" ? ev.number : null;
      if (!num || (state.route.name === "thread" && (state.route.number === num || !!state.thread?.numbers?.includes(num)))) {
        threadWanted = true; // "" = not about one number
        threadKinds.add(num ? String((ev as { type?: unknown }).type ?? "") : "");
      }
    } catch {
      threadWanted = true;
      threadKinds.add("");
    }
    clearTimeout(pending);
    // On a call here: gather updates for a moment longer, so reloading never competes with the call's audio.
    pending = window.setTimeout(() => void refresh("events"), voice.call ? 600 : 60);
  };
  // Catch up on anything missed while disconnected.
  es.onopen = () => {
    lastHeard = Date.now();
    // Not right after a full load (at startup it fetched the inbox twice in a row).
    // (Nor while the first one is still loading: lastRefreshDone is only set once it's done.)
    // A REconnect always catches up (queued after a refresh still running: it may have started before the gap).
    if (streamOpenedBefore) void refresh();
    else if (!refreshing && Date.now() - lastRefreshDone > 3000) void refresh();
    streamOpenedBefore = true;
    // A reconnect often means the server was just redeployed.
    void checkForUpdate();
    void checkApprovals();
  };
  // The browser retries on its own, but gives up after an error response (e.g. during a deploy).
  // A closed stream can also mean we were signed out (401): ask the server first, and show the sign-in if so.
  es.onerror = () => {
    if (es.readyState !== EventSource.CLOSED || events !== es || !state.user) return;
    setTimeout(async () => {
      if (events !== es) return;
      try {
        await api.me();
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return void signedOutElsewhere();
        // offline or mid-deploy: try again below
      }
      if (events === es && state.user) startLiveUpdates();
    }, 3000);
  };
}

// ---------------------------------------------------------------- calls

let lastCallId: string | null = null;
/** Per-call screen state (on hold for the caller, recording paused, timer, transfer) for a call swapped out of view. */
const callUi = new Map<string, { held: boolean; recPaused: boolean; callStartedAt: number; transferringTo: string | null }>();
// Testing aid: say so when the start of a call came through with a gap (the "first seconds missing" problem).
voice.onStartGap = (ms) => toast(`Start of call: ${(ms / 1000).toFixed(1)} s with no audio from the other side`);
/**
 * Outgoing calls: the app's side connects right away (so no audio is lost when they answer), but the other
 * side is still ringing until the server says it answered. While it rings: "Ringing…", the app's ring tone,
 * no call timer.
 */
const outAnswered = new Set<string>();
const outActiveAt = new Map<string, number>();
function farRinging(c = voice.call): boolean {
  if (!c || c.direction !== "outbound" || c.isMonitor) return false;
  if (["new", "requesting", "trying", "ringing", "early"].includes(c.state)) return true;
  if (c.state !== "active" || outAnswered.has(c.id)) return false;
  if (!outActiveAt.has(c.id)) outActiveAt.set(c.id, Date.now());
  const lc = state.live.find((l) => l.external_number === otherParty(c) && l.direction === "outbound");
  if (lc && lc.status !== "ringing") {
    outAnswered.add(c.id);
    return false;
  }
  // No word from the server for a long while: don't ring forever.
  if (!lc && Date.now() - outActiveAt.get(c.id)! > 75_000) return false;
  return true;
}
/** Ring tone and call timer follow the other side (call after any change to the call or the live calls). */
function syncOutgoingRing() {
  const c = voice.call;
  const ringing = farRinging(c);
  if (ringing) startRingback();
  else stopRingback();
  // Connected (or a call on hold): the app's own sounds stay off until it ends, so nothing plays next to the call.
  const live = (x: PhoneCall | null | undefined) => !!x && !isEnded(x) && ["active", "held", "hold"].includes(x.state);
  const on = (live(c) && !ringing) || live(voice.held);
  setCallAudio(on);
  // They hung up (24.10.18): the hang-up tone, once the call's sound is off (not while another call carries on).
  if (voice.takeTheyHungUp() && !on && !(c && !isEnded(c))) playHangupTone();
  // Calls that are over: send what really happened to their sound (network vs this phone) to the call audio log.
  for (const rep of voice.takeEndedAudioReports()) {
    if (rep.samples > 0) {
      const dp = getDevicePrefs();
      void api.audioReport({ ...rep, ua: navigator.userAgent.slice(0, 200), echoCancellation: dp.echoCancellation, noiseSuppression: dp.noiseSuppression,
        autoGainControl: dp.autoGainControl, codecPref: dp.codec, line: state.line, region: voice.region || "default", regionAuto: dp.regionAuto }).catch(() => {});
    }
  }
  if (c && c.state === "active" && !ringing && !state.callStartedAt) {
    state.callStartedAt = Date.now();
    if (c.direction === "outbound") {
      voice.traceMark("server says they answered");
      voice.watchStartNow(); // check the first seconds now that they answered
    }
  }
}

// The other side's first sound = they answered: stop the ring tone that instant.
voice.onFarAudio = (callId) => {
  outAnswered.add(callId);
  syncOutgoingRing();
  render();
};

/** The last phone-line state the screen was drawn for, when there was no call (see onVoiceChange). */
let lastIdleLine: boolean | null = null;
/** 7.10.30: the call the Speaker choice was last put back for, and the waiting call last shown (see onVoiceChange). */
let speakerSetFor: string | null = null;
let lastWaitingId: string | null = null;
function onVoiceChange() {
  const c = voice.call;
  // No call anywhere and the line is still just as connected / not connected as last time: nothing on screen
  // changes, so don't redraw. (While the line kept retrying, every failed try redrew the whole inbox — under a finger
  // that made scrolling catch and stick.)
  if (!c && !voice.held && !voice.waiting && !voice.monitoring && lastCallId === null) {
    if (lastIdleLine === voice.ready) return;
    lastIdleLine = voice.ready;
  } else lastIdleLine = null;
  syncOutgoingRing();
  if (c && !hiddenRing(c)) popupConnecting = null; // the call is here: its own window takes over
  // 10.10.30: not once you tapped Answer (it rang on through "Connecting…", over your first words).
  if (c && c.direction === "inbound" && c.state === "ringing" && !voice.autoAnswering && !hiddenRing(c) && !answeringRing.has(String(c.id))) ringer.start();
  else ringer.stop();
  if (c && c.id !== lastCallId) {
    // Call waiting / swap: remember the call that went on hold, and bring back what we knew about this one.
    if (lastCallId && voice.held?.id === lastCallId) callUi.set(lastCallId, { held: state.held, recPaused: state.recPaused, callStartedAt: state.callStartedAt, transferringTo: state.transferringTo });
    for (const id of callUi.keys()) if (id !== voice.held?.id && id !== c.id) callUi.delete(id);
    const saved = callUi.get(c.id);
    callUi.delete(c.id);
    lastCallId = c.id;
    // 7.10.30: Mute shows your own choice for this call (c.userMuted), not the library's mute, which the app also
    // sets while a call is on hold (resuming a held caller after a swap showed, and kept, a muted mic).
    // Speaker isn't reset: it's the one call speaker's output (#remote-audio), which stays as it was (see below).
    Object.assign(state, { callMinimized: false, muted: !!c.userMuted, held: false, showKeypad: false, showTransfer: false, callSheet: null, transferringTo: null, callStartedAt: 0, recPaused: false, quality: null }, saved);
    voice.setRemoteMuted(!!saved?.held);
  }
  // 7.10.30: Speaker was shown off for every new call while the call speaker stayed on the loudspeaker. The choice
  // stays, and is put back once each call connects (the calling library can switch the output when a call connects).
  if (c && c.state === "active" && state.speaker && speakerSetFor !== c.id && canPickSpeaker()) {
    speakerSetFor = c.id;
    setTimeout(() => voice.call === c && state.speaker && void setSpeaker(true), 50);
  }
  // 7.10.30: a call waiting while the call screen was minimized didn't show anywhere: bring the call screen back.
  if (voice.waiting && voice.waiting.id !== lastWaitingId) state.callMinimized = false;
  lastWaitingId = voice.waiting?.id ?? null;
  // (the call timer starts in syncOutgoingRing: for outgoing calls only once the other side answers)
  if (!c) {
    lastCallId = null;
    callUi.clear();
    voice.setRemoteMuted(false);
  }
  render();
}

/** Listen in on a teammate's live call. The server rings this app; it's answered automatically, muted. */
let listening: string | null = null;
async function listenIn(callId: string) {
  if (listening === callId) return; // one press = one listen
  listening = callId;
  setTimeout(() => listening === callId && (listening = null), 3000);
  ringer.unlock();
  try {
    await voice.listenIn(callId, "listen");
    toast("Connecting… you'll hear the call in a moment");
  } catch (e) {
    const err = e as Error & { status?: number };
    toast(err instanceof ApiError && err.status === 403 ? "Only admins can listen in" : err.message);
  }
}

/**
 * Answer (inbox card): handled on the press itself, by call id. While a call rings the inbox redraws often; a
 * redraw between finger-down and lift replaced the button and the phone dropped the tap, so Answer did nothing.
 */
{
  let down: { id: string; x: number; y: number; pid: number } | null = null;
  document.addEventListener("pointerdown", (e) => {
    // The listen-in ear on a live call's row is handled the same way (the row redraws every second during a call).
    const b = (e.target as HTMLElement | null)?.closest?.<HTMLElement>("[data-answer],[data-listen]");
    down = b && e.button === 0 ? { id: b.dataset.answer ?? `L${b.dataset.listen}`, x: e.clientX, y: e.clientY, pid: e.pointerId } : null;
    if (down && b!.dataset.listen) e.stopPropagation(); // not the start of a swipe on the row underneath
  }, true);
  document.addEventListener("pointerup", (e) => {
    const d = down;
    down = null;
    if (!d || d.pid !== e.pointerId || Math.hypot(e.clientX - d.x, e.clientY - d.y) > 16) return;
    if (d.id.startsWith("L")) void listenIn(d.id.slice(1));
    else void answerFromServer(d.id);
  }, true);
  document.addEventListener("pointercancel", () => (down = null), true);
}
let answering: string | null = null;
/**
 * 2.10.29: the call alert's window you pressed Answer on stays up saying "Connecting…" until the call is here (it
 * vanished, leaving the inbox with nothing happening for a moment, and then a window popped up).
 */
let popupConnecting: string | null = null;

/**
 * 3.10.23 "I need to press Answer 2 or 3 times until it answers": the incoming-call window redraws while it rings
 * (new events, presence, the inbox behind it), and a redraw between finger-down and finger-up replaced the button,
 * so the browser dropped the tap (measured: a redraw mid-press → nothing happened). Like the inbox's Answer: the
 * call buttons are handled on the press itself, by name, whatever was redrawn in between. The latest action for that
 * name runs once; a keyboard press (Enter / Space) still works through the button's own click.
 */
const pressActions = new Map<string, () => void>();
/**
 * 3.10.23 (second part): the button itself also stays the same element on screen while its window is up. A redraw
 * only updates it, so the finger never ends up on a button that was taken off the screen (iPhone's Safari sends the
 * lift to the element the finger went down on: if that one was replaced, nothing else hears it).
 */
const pressEls = new Map<string, HTMLElement>();
function pressButton(key: string, action: () => void, attrs: Record<string, unknown>, ...kids: (Node | string | null)[]) {
  pressActions.set(key, action);
  const fresh = h("button", { ...attrs, "data-press": key, onclick: (e: MouseEvent) => e.detail === 0 && pressActions.get(key)?.() }, ...kids) as HTMLElement;
  const kept = pressEls.get(key);
  if (kept?.isConnected && kept.tagName === fresh.tagName) {
    adoptInto(kept, fresh);
    kept.replaceChildren(...fresh.childNodes);
    return kept;
  }
  pressEls.set(key, fresh);
  return fresh;
}
{
  let down: { key: string; x: number; y: number; pid: number } | null = null;
  let lastRun = { key: "", at: 0 };
  document.addEventListener("pointerdown", (e) => {
    const b = (e.target as HTMLElement | null)?.closest?.<HTMLElement>("[data-press]");
    down = b && e.button === 0 && !(b as HTMLButtonElement).disabled ? { key: b.dataset.press!, x: e.clientX, y: e.clientY, pid: e.pointerId } : null;
  }, true);
  document.addEventListener("pointerup", (e) => {
    const d = down;
    down = null;
    if (!d || d.pid !== e.pointerId || Math.hypot(e.clientX - d.x, e.clientY - d.y) > 24) return;
    if (lastRun.key === d.key && Date.now() - lastRun.at < 800) return; // one press = one action
    lastRun = { key: d.key, at: Date.now() };
    pressActions.get(d.key)?.();
  }, true);
  document.addEventListener("pointercancel", () => (down = null), true);
}

/** Answer on the ringing window: tells you when it didn't work, instead of the call quietly going away. */
async function answerRinging(c: Parameters<typeof voice.answerCall>[0]) {
  const key = String((c as { id?: string }).id ?? "");
  if (answeringRing.has(key)) return;
  answeringRing.add(key);
  setTimeout(() => (answeringRing.delete(key), render()), 4000);
  render(); // the button says "Connecting…" right away (it can take a second or two to connect)
  ringer.stop(); // 10.10.30: the ringing stops at the tap, not when the call has connected
  // Answered right here: the "answer this call" left from tapping its alert is done too (it would pop up again later).
  state.pendingAnswer = null;
  await micForCall().catch(() => {});
  try {
    const { micBlocked } = await voice.answerCall(c);
    if (micBlocked) toast("Answered, but your microphone is blocked. Allow it in the browser, then tap Mic.");
  } catch (e) {
    toast(`Couldn't answer here: ${(e as Error).message || "the call ended"}. Try again or answer on your phone.`);
  }
  render();
}

async function answerFromServer(callId: string) {
  if (answering === callId) return; // one press = one answer
  answering = callId;
  if (state.pendingAnswer === callId || glassTeamRing()?.id === callId) {
    popupConnecting = callId;
    setTimeout(() => popupConnecting === callId && ((popupConnecting = null), render()), 20_000);
  }
  setTimeout(() => answering === callId && ((answering = null), render()), 3000);
  render(); // "Connecting…" right away
  // Same as Answer on the incoming-call window: with the Mic switch on, the mic is opened now (iPhone).
  if (isIOS()) await micForCall().catch(() => {});
  ringer.unlock();
  state.pendingAnswer = null;
  try {
    await voice.answerSessionCall(callId);
  } catch (e) {
    if (popupConnecting === callId) popupConnecting = null;
    toast(e instanceof ApiError && e.status === 410 ? "That call was already answered or ended" : (e as Error).message);
    void refresh();
  }
  render();
}

/** A value with a Copy button (setup steps). */
function copyCode(text: string) {
  return h("span", { class: "copy-code" }, h("code", {}, text),
    h("button", { class: "icon-btn sm", type: "button", "aria-label": `Copy ${text}`, onclick: () => void navigator.clipboard?.writeText(text).then(() => toast("Copied"), () => toast("Couldn't copy")) }, icon("copy", "sm")));
}

/** A phone (not a computer): where "connect calls via my cell" applies to calls you start. */
const onPhoneDevice = () => isIOS() || /Android.+Mobile/.test(navigator.userAgent);
const viaCell = () => state.user?.callVia === "cell" && !!state.user?.forwardNumber && onPhoneDevice();

/** Calls a teammate's Joy Phone app (from their name in the sidebar). */
/**
 * The Mic switch (green = on). Phones: off until you turn it on. On: the mic stays open, so calls never ask for it
 * (and it turns itself off after 3 minutes without talking). Off: calls start without the mic and nothing asks;
 * tap Turn on mic in the call. The only "Allow microphone?" prompt ever comes from tapping one of those.
 * On the keypad.
 */
function micSwitchButton(compact = false) {
  // iPhone that already allows the mic: "on" needs nothing open (calls open the mic without asking), so the switch
  // follows your choice. Before 15.10.14 it waited for an open mic that is deliberately never opened, so tapping it
  // on the keypad seemed to do nothing (it stayed "Mic off").
  const keepOn = micKeepReady() && (isIOS() ? micIsOn() || micOpensSilently() || micComingBack() : micReady());
  return h("button", { class: `mic-switch${keepOn ? " on" : ""}${compact ? " compact" : ""}`, type: "button", role: "switch", "aria-checked": String(keepOn),
    "aria-label": keepOn ? "Mic on. Tap to turn it off" : "Mic off. Tap to turn it on",
    title: keepOn ? "The mic stays on until you turn it off, or after 3 minutes without talking" : "Off: calls start without the mic. Tap to turn it on",
    onclick: async () => {
      if (keepOn) {
        setMicKeepReady(false);
        micOff();
        return render();
      }
      setMicKeepReady(true);
      if (!(await micOn())) {
        setMicKeepReady(false);
        toast("Microphone is blocked. Allow it for Joy Phone in your browser or phone settings.");
      }
      render();
    } },
    h("span", { class: "mic-icon" }, icon(keepOn ? "mic" : "micOff", "sm")),
    compact ? null : h("span", { class: "mic-label" }, keepOn ? "Mic on" : "Mic off"),
    h("span", { class: "mic-knob" }));
}

async function callTeammate(u: { id: number; name: string }) {
  return placing(() => callTeammateNow(u)); // 7.10.30: one call per tap
}
async function callTeammateNow(u: { id: number; name: string }) {
  if (!state.user?.canCall) return toast("Your account can't make calls");
  if (voice.call && !isEnded(voice.call)) return toast("Finish your call first");
  const p = state.presence.find((x) => x.id === u.id);
  if (p?.status === "offline") return toast(`${firstName(u.name)} isn't in Joy Phone right now`);
  if (p?.status === "dnd") return toast(`${firstName(u.name)} is on Do Not Disturb`);
  ringer.unlock();
  // Mic switch on: the mic is (re)opened now, so the call doesn't ask. Off: the call starts without it (no prompt).
  if (isIOS()) await micForCall();
  try {
    await voice.dialTeammate(u.id, u.name, state.user.name);
  } catch (e) {
    toast(`Couldn't call ${firstName(u.name)}: ${(e as Error).message}`);
  }
}

/** "800 555 1234 x22" typed in: once the call connects, the extension is keyed in for you (9.10.18). */
function keyInExtensionWhenConnected(ext: string) {
  const started = Date.now();
  const tick = setInterval(() => {
    const c = voice.call;
    if (Date.now() - started > 90_000 || (c && ["hangup", "destroy", "purge"].includes(String(c.state)))) return clearInterval(tick);
    // 7.10.30: an outgoing call is "active" while the far end still rings (see farRinging): the keys were sent
    // before anyone answered and got lost. Wait until they really answered.
    if (!c || c.state !== "active" || farRinging(c)) return;
    clearInterval(tick);
    toast(`Dialing extension ${ext}…`);
    // A moment for their system to pick up, then each key with a short gap (phone systems miss keys sent too fast).
    ext.split("").forEach((k, i) => setTimeout(() => voice.call === c && sendTone(c, k), 2500 + i * 400));
  }, 300);
}

async function dial(number: string) {
  return placing(() => dialNow(number)); // 7.10.30: one call per tap
}
async function dialNow(number: string) {
  const e164 = toE164(number);
  const ext = splitExtension(number).ext;
  if (!e164 || !state.user) return toast("Enter a full phone number");
  ringer.unlock();
  // From your personal inbox: the call goes out from your personal number (told to the server just before dialing).
  if (state.line === "personal") {
    try {
      await api.personalDial(e164);
    } catch (e) {
      return toast((e as Error).message);
    }
  }
  // "Connect my calls via my cell" (on a phone): your cell rings first, then the customer. No mic needed here.
  if (viaCell() && state.line !== "personal") {
    try {
      const r = await api.callViaCell(e164);
      state.dialed = "";
      toast(`Calling your cell${r.cell ? ` ${phone(r.cell)}` : ""}… pick up to reach ${nameFor(e164, state.conversations.find((c) => c.external_number === e164)?.contact_name)}`);
      go({ name: "thread", number: e164 });
    } catch (e) {
      toast((e as Error).message);
    }
    return;
  }
  // iPhone: opening the mic here (and keeping it open) means later calls in this session don't ask again.
  // Mic switch on: the mic is (re)opened now, so the call doesn't ask. Off: the call starts without it (no prompt).
  if (isIOS()) await micForCall();
  try {
    await voice.dial(e164, state.user.name);
    if (ext) keyInExtensionWhenConnected(ext);
    state.dialed = "";
    go({ name: "thread", number: e164 });
  } catch (e) {
    toast(`Couldn't start the call: ${(e as Error).message}`);
  }
}

/** Hold: the caller hears hold music (played by the phone system), and the two of you stop hearing each other. */
/**
 * 5.10.20 Pause the recording (e.g. while a customer reads out card details) and resume it. The server could do it
 * all along; the button was missing.
 */
async function setRecPaused(paused: boolean) {
  try {
    await api.pauseRecording(paused);
    state.recPaused = paused;
    toast(paused ? "Recording paused. Tap again to resume." : "Recording again");
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

async function setHold(c: PhoneCall, on: boolean) {
  try {
    await api.hold(on);
    state.held = on;
    // 7.10.30: the mic follows the hold and your own Mute, kept on the call (it survives swapping calls).
    c.serverHeld = on;
    voice.applyMute(c);
    voice.setRemoteMuted(on);
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

async function startTransfer(c: PhoneCall, to: User) {
  state.showTransfer = false;
  state.callSheet = null;
  try {
    await api.transfer(to.id);
    state.transferringTo = to.name;
    state.held = true;
    c.serverHeld = true; // 7.10.30
    voice.applyMute(c);
    voice.setRemoteMuted(true);
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

function onTransferResult(type: string, toName: string, stillOnHold: boolean) {
  const c = voice.call;
  state.transferringTo = null;
  if (type === "transfer.done") {
    toast(`Transferred to ${toName}`);
  } else if (stillOnHold) {
    toast(`${toName.split(" ")[0]} didn't answer. The caller is still on hold.`);
  } else {
    toast(`${toName.split(" ")[0]} didn't answer. You're back on the call.`);
    state.held = false;
    if (c) {
      c.serverHeld = false; // 7.10.30
      voice.applyMute(c);
    }
    voice.setRemoteMuted(false);
  }
  render();
}

// ---------------------------------------------------------------- alerts (web push)

async function enableAlerts() {
  ringer.unlock();
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    const iphone = /iPhone|iPad/.test(navigator.userAgent);
    return toast(iphone ? "Add Joy Phone to your Home Screen, then open it from there" : "This browser doesn't support notifications");
  }
  if (!state.vapidKey) return toast("Notifications aren't set up on the server yet");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return toast("Notifications are blocked. Allow them in Settings → Notifications → Joy Phone");
  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToBytes(state.vapidKey) }));
  await api.subscribePush(sub.toJSON());
  toast("Notifications enabled");
  render();
}

const alertsOn = () => typeof Notification !== "undefined" && Notification.permission === "granted";
const alertsBannerDismissed = () => {
  try {
    return localStorage.getItem("joy.alertsBanner") === "no";
  } catch {
    return false;
  }
};
const dismissAlertsBanner = () => {
  try {
    localStorage.setItem("joy.alertsBanner", "no");
  } catch {
    // not kept: it shows again next time
  }
};

function urlBase64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// ---------------------------------------------------------------- helpers

/** Tells teammates which conversation you have open (shown on that inbox row as your face with an eye). */
let sentViewing: string | null = null;
function sendViewing() {
  const n = state.user && document.visibilityState === "visible" && state.route.name === "thread" ? state.route.number : null;
  if (n === sentViewing) return;
  sentViewing = n;
  void api.setViewing(n).catch(() => {
    sentViewing = null;
  });
}
document.addEventListener("visibilitychange", () => setTimeout(sendViewing, 0));
window.addEventListener("pagehide", () => {
  if (sentViewing) navigator.sendBeacon?.("/api/presence/viewing", new Blob([JSON.stringify({ number: null })], { type: "application/json" }));
  sentViewing = null;
});

/**
 * 13.10.21 "default calling and texting Steven to personal": for an admin with a personal line, opening Steven's
 * chat or calling him takes them to their personal line (Steven as their own assistant). "Business" on his page
 * still switches to Joy Travel's Steven, for as long as they stay on his page.
 */
let stevenBusiness = false;
function stevenPersonalFirst(): boolean {
  return false; // 1.10.28: Steven follows the selected line (was: personal first for admins)
}
// 1.10.28: where you were before (a contact's Back returns there: it always went to the Contacts list).
let cameFrom: Route | null = null;
// 1.10.28: pages only admins have (Settings shows them only to admins; opened by address, a member got a form
// whose Save failed, and Notes into profiles retried forever).
const ADMIN_ONLY_PAGES = new Set<Route["name"]>(["phone-settings", "email-alerts", "settings-rcs", "call-settings", "phone-numbers", "import-quo", "storage", "billing", "notes-to-profiles"]);
function go(route: Route, fromHistory = false) {
  if (state.user && !isAdmin() && ADMIN_ONLY_PAGES.has(route.name)) route = { name: "settings" };
  if (route.name !== state.route.name || JSON.stringify(route) !== JSON.stringify(state.route)) cameFrom = state.route;
  if (route.name !== "ai-agent" && state.route.name === "ai-agent") stevenBusiness = false; // leaving his page
  // (1.10.28: opening his page no longer switches you to the personal line: he follows the line you're on.)
  setTimeout(sendViewing, 0);
  typedValues.clear();
  if (!fromHistory && state.user) recordNav(route);
  if (sample.audio && route.name !== state.route.name) stopSample(); // leaving the screen stops a sample
  const sameThread = route.name === "thread" && state.route.name === "thread" && state.route.number === route.number;
  if (!sameThread) {
    for (const a of players.values()) a.pause();
    state.reply = null;
    state.scrolled = "";
    state.commentOn = null;
    state.openThreads.clear();
    state.picker = null;
  }
  if (route.name === "thread" && !sameThread) {
    // Text by default; comments for numbers we can't text.
    state.composeMode = canText(route.number) ? "text" : "comment"; // one number or a group: texting first
  }
  state.route = route;
  if (!sameThread) sidePanel.peek = false;
  state.editingName = false;
  state.menuOpen = false;
  state.filterMenu = false;
  state.addingTask = false;
  state.taskFor = null;
  if (route.name !== "thread" || state.thread?.number !== route.number) state.thread = null;
  const cached = route.name === "thread" && !sameThread ? threadCache.get(route.number) : undefined;
  if ((route.name !== "contact" && route.name !== "thread") || state.contact?.number !== route.number) {
    state.contact = null;
    state.editingContact = false;
    state.noteReply = null;
  }
  if (route.name !== "thread" || !sameThread) state.threadTasks = []; // never show another conversation's tasks
  if (route.name === "contact" && !state.contact) state.contact = contactCache.get(route.number) ?? threadCache.get(route.number)?.contact ?? null;
  if (cached) applyThreadData(cached);
  if (route.name === "team") void loadTeam();
  if (route.name === "phone-settings") void loadPhoneSettings();
  if (sectionOf(route) === "contacts") void loadContacts();
  if (route.name === "activity") void loadActivity();
  if (route.name === "tasks") void loadTasks();
  if (route.name === "analytics") void loadAnalytics();
  if (route.name === "snippets") void loadSnippets(true);
  if (route.name === "blocked") void loadBlocked();
  if (route.name === "call-settings") void loadPhoneSettings();
  if (route.name === "speed") hdVoice = null; // 1.10.27: read the office line's HD formats from Telnyx again on each visit
  if (route.name === "notes-to-profiles" && isAdmin()) void loadNotesJob(); // 1.10.28: fresh on every visit (it stayed frozen)
  if (route.name === "settings-calling" || route.name === "settings-notifications") void loadPrefs();
  // Settings: load what its most-used pages need now, so they open filled in instead of with a spinner.
  if (route.name === "settings") {
    if (!state.prefs) void loadPrefs();
    if (!state.phoneDraft) void loadPhoneSettings();
  }
  if (route.name === "scheduled") void loadScheduled();
  if (route.name === "import-quo") void loadQuo(true);
  if (route.name === "storage") void loadStorage();
  if (route.name === "phone-numbers") void loadNumbers();
  if (route.name === "dialer") dialTextOpen = false;
  if (route.name === "billing") void loadBilling();
  if (route.name === "speed") void loadSpeed();
  if (route.name === "security") void loadSecurityPage();
  if (route.name === "email-alerts") void loadEmailPage();
  if (route.name === "settings-audio") void loadDevices(false);
  if (route.name !== "settings-audio") stopMicTest();
  if (route.name === "search" && route.q !== undefined) {
    state.searchQ = route.q;
    void runSearch(route.q);
  }
  // Opening Team chat: its unread count, your @mentions in Activity and its phone notifications all go.
  if (route.name === "chat" && (state.chatSummary.unread || state.activityUnread)) {
    state.chatSummary.unread = 0;
    void api.chatRead().then(() => refresh()).catch(() => {});
  }
  if (route.name === "chat") clearShownNotifications((url) => url.includes("chat=1"));
  if (route.name !== "chat") ((chatCut = null), (chatOlderLoaded = false), (chatNoOlder = false)); // left Team chat: next time it opens at the newest page again
  if (route.name === "thread") clearShownNotifications((url) => url.includes(`thread=${encodeURIComponent(route.number)}`));
  if (route.name === "ai-agent" && aiAgent.tab === "chat" && state.agentUnread) markAgentRead();
  loadOpenScreen(route);
  render();
}

/**
 * The screen you just opened loads its own data right away (reusing a fetch started when your finger went down),
 * and shows it the moment it arrives: it doesn't wait behind a refresh that's already running or for the whole inbox.
 */
function loadOpenScreen(r: Route) {
  const still = () => JSON.stringify(state.route) === JSON.stringify(r);
  if (r.name === "thread" && isInternalKey(r.number)) void loadInternal(r.number);
  else if (r.name === "thread") void fetchThread(r.number).then((d) => {
    if (!d || !still()) return;
    applyThreadData(d);
    if (document.visibilityState === "visible") {
      markThreadRead(r.number);
      // An inbox refresh already on its way was fetched before this read: fetch it again after (or it shows unread again).
      if (refreshing) void refresh();
    }
    render();
  });
  if (r.name === "contact") void api.contact(r.number).then((c) => {
    contactCache.set(r.number, c);
    if (still()) (state.contact = c), render();
  }).catch(() => {});
  if (r.name === "chat") void api.chat().then((chat) => {
    chatLoaded = true;
    if (!still()) return;
    setChat(chat.messages);
    state.chatReads = chat.reads ?? [];
    render();
  }).catch(() => {});
}
/** Contacts you opened recently: opening one again shows it at once while it refreshes. */
const contactCache = new Map<string, ContactDetail>();
let chatLoaded = false;

function sectionOf(r: Route): Section {
  if (r.name === "inbox" || r.name === "thread" || r.name === "dialer" || r.name === "chat" || r.name === "new-group") return "inbox";
  if (r.name === "contacts" || r.name === "contact" || r.name === "notes-to-profiles") return "contacts";
  return "none";
}

const brandMark = () => h("img", { class: "brand-mark", src: `/brand/mark.png?v=${VERSION}`, alt: "", width: 32, height: 32 });
const firstName = (name: string | null | undefined) => (name ?? "").split(/\s+/)[0] ?? "";
const isAdmin = () => state.user?.role === "admin";
const isGroup = (number: string) => number.startsWith("grp:");
const canText = (number: string) => /^\+\d{8,15}$/.test(number) || isGroup(number);
/** Names of group members we know (from the inbox and open threads). */
const memberNames = new Map<string, string>();
function rememberMembers(list: { number: string; name: string | null }[] | undefined) {
  for (const m of list ?? []) if (m.name) memberNames.set(m.number, m.name);
}
/** Names the team gave group texts (5.10.20). */
const groupNames = new Map<string, string>();
function rememberGroupName(key: string, name: string | null | undefined) {
  if (name) groupNames.set(key, name);
  else groupNames.delete(key);
}
/** Its own name if it has one, else "Maria, Greg & Dana" / "Maria, Greg & 3 others". */
function groupName(key: string): string {
  const own = groupNames.get(key);
  if (own) return own;
  const names = key.slice(4).split(",").map((n) => firstName(memberNames.get(n) ?? "") || phone(n));
  return names.length <= 3 ? `${names.slice(0, -1).join(", ")} & ${names.at(-1)}` : `${names.slice(0, 2).join(", ")} & ${names.length - 2} others`;
}

/** Contact name, else the caller ID name from a lookup, else the formatted number (a group: its members' names). */
const nameFor = (number: string, contactName?: string | null) =>
  isGroup(number) ? groupName(number)
  : contactName || callerNames.get(number) || (number === "anonymous" || !number ? "Unknown caller" : phone(number));
const callerNames = new Map<string, string>();
/** A caller's saved contact name from everything this app already knows (inbox, live calls, contacts), contact first. */
function contactNameOf(number: string): string | null {
  if (!number) return null;
  // Any way the number was written ("61412109137", "+61 412 109 137", "+61412109137") finds the saved name.
  const d = number.replace(/\D/g, "");
  const same = (x: string | null | undefined) => !!x && (x === number || (d.length >= 7 && x.replace(/\D/g, "") === d));
  return state.conversations.find((x) => same(x.external_number))?.contact_name
    || state.live.find((l) => same(l.external_number))?.contact_name
    || state.contacts.find((x) => same(x.number))?.name
    || null;
}

const AVATAR_COLOURS = 11;
/**
 * Teammates get their own clearly different, solid colour (in team order), so two of you never look alike.
 * Customers keep the soft colours from their number.
 */
const MATE_COLOURS = ["blue", "coral", "purple", "teal", "pink", "gold", "green", "red"] as const;
/** A teammate's email from their id (their avatar colour follows it). */
function mateEmail(id: number | null | undefined): string | null {
  if (id == null) return null;
  return (state.team.find((u) => u.id === id) ?? (state.user?.id === id ? state.user : null))?.email ?? null;
}
function mateColour(seed: string): string | null {
  const key = seed.toLowerCase();
  const team = [...state.team];
  if (state.user && !team.some((u) => u.id === state.user!.id)) team.push(state.user);
  team.sort((a, b) => a.id - b.id);
  const i = team.findIndex((u) => u.email.toLowerCase() === key);
  return i < 0 ? null : MATE_COLOURS[i % MATE_COLOURS.length];
}
function avatar(seed: string, name: string | null | undefined, size = "") {
  if (seed.startsWith("grp:")) return h("span", { class: `avatar group-av ${size}`, "aria-hidden": "true" }, icon("users", size === "xs" || size === "sm" ? "sm" : "md"));
  const mate = seed.includes("@") ? mateColour(seed) : null;
  if (mate) {
    const ini = name ? name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") : "";
    return h("span", { class: `avatar mate mate-${mate} ${size}`, "aria-hidden": "true" }, ini || icon("user", size ? "lg" : "md"));
  }
  // A well-mixed hash of the number / email, so similar numbers don't all land on the same colour. (The old one,
  // ×31 then "mod 6", came down to the digit sum: many numbers shared a colour.)
  let hash = 0x811c9dc5;
  for (const ch of seed) hash = Math.imul(hash ^ ch.charCodeAt(0), 0x01000193);
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x7feb352d);
  hash ^= hash >>> 15;
  const initials = name ? name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") : "";
  return h("span", { class: `avatar av-${(hash >>> 0) % AVATAR_COLOURS} ${size}`, "aria-hidden": "true" }, initials || icon("phone", size ? "lg" : "md"));
}

/**
 * 4.10.29: "Today", "Yesterday" or "Thursday, Oct 8". It built a new date formatter on every call (toLocaleDateString
 * with options), once per message drawn: measured as 40% of opening a long conversation at phone speed. Now each day
 * is worked out once.
 */
const dayLabels = new Map<string, string>();
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
function dayLabel(d: Date): string {
  const now = new Date();
  const key = dayKey(d);
  if (key === dayKey(now)) return "Today";
  if (key === dayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1))) return "Yesterday";
  let s = dayLabels.get(key);
  if (!s) dayLabels.set(key, (s = fmtDate(d, { weekday: "long", month: "short", day: "numeric" })));
  return s;
}

/**
 * 4.10.29: a ticking call timer changes only its digits. Setting textContent swaps the text for a new one, and that
 * made the phone restyle the whole app (every element: measured up to 0.1 s at phone speed) once a second during
 * any call, because of the app-wide style rules that look inside it.
 */
function setTextOnly(el: Element, text: string) {
  const t = el.firstChild;
  if (t && t.nodeType === Node.TEXT_NODE && !t.nextSibling) {
    if (t.nodeValue !== text) t.nodeValue = text;
  } else if (el.textContent !== text) el.textContent = text;
}

const clock = (d: Date | null) => (d ? fmtDate(d, { hour: "numeric", minute: "2-digit" }) : "");
/** Above each call: "Today, 7:11 AM", "Yesterday, 6:02 PM", "Oct 8, 3:33 PM" (the year when it isn't this year). */
function callStamp(d: Date): string {
  const day = dayLabel(d);
  const date = day === "Today" || day === "Yesterday" ? day
    : fmtDate(d, { month: "short", day: "numeric", ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) });
  return `${date}, ${clock(d)}`;
}

function lastLine(c: Conversation): { text: string; iconName: string; tone: "" | "bad" | "live" } {
  const me = state.user?.name;
  // A call inside the team: said from your side ("You called · 3:00", "Missed call from Ben").
  if (isInternalKey(c.external_number)) {
    const mine = !c.teammate_name || c.teammate_name === me;
    const by = firstName(c.teammate_name ?? "");
    const talk = c.talk_secs != null && c.talk_secs >= 1 ? ` · ${duration(c.talk_secs)}` : "";
    if (c.status === "ringing") return { text: mine ? "Calling…" : `${by} is calling`, iconName: mine ? "arrowOut" : "arrowIn", tone: "live" };
    if (c.status === "active") return { text: "On the call", iconName: "phone", tone: "live" };
    if (c.status === "missed") return mine ? { text: "You called · no answer", iconName: "arrowOut", tone: "" } : { text: `Missed call from ${by}`, iconName: "arrowIn", tone: "bad" };
    return { text: mine ? `You called${talk}` : `${by} called${talk}`, iconName: mine ? "arrowOut" : "arrowIn", tone: "" };
  }
  if (c.last_kind === "sms") {
    const out = c.sms_direction === "outbound";
    const who = out ? (c.sms_user_name && c.sms_user_name !== me ? `${firstName(c.sms_user_name)}: ` : "You: ") : "";
    const failed = c.sms_status === "failed" || c.sms_status === "sending_failed" || c.sms_status === "delivery_failed";
    return { text: failed ? "Text not delivered" : `${who}${c.sms_body || "📎 Attachment"}`, iconName: failed ? "close" : "", tone: failed ? "bad" : "" };
  }
  if (c.last_kind === "comment") {
    const who = c.comment_user_name === me ? "You" : firstName(c.comment_user_name);
    return { text: `${who}: ${c.comment_body ?? ""}`, iconName: "note", tone: "" };
  }
  const talk = c.talk_secs != null && c.talk_secs >= 1 ? ` · ${duration(c.talk_secs)}` : "";
  const mate = c.teammate_ai ? aiName() : c.teammate_name;
  if (c.status === "ringing") return { text: c.direction === "outbound" ? "Calling…" : "Incoming call", iconName: c.direction === "outbound" ? "arrowOut" : "arrowIn", tone: "live" };
  // Calls name the teammate by first name, you included ("Yossi called", not "You called").
  if (c.status === "active") return { text: mate ? `On a call with ${firstName(mate)}` : "On a call", iconName: "phone", tone: "live" };
  if (c.status === "voicemail") return { text: "Left a voicemail", iconName: "voicemail", tone: "bad" };
  if (c.status === "info") return { text: "Heard the phone menu", iconName: "arrowIn", tone: "" };
  if (c.direction === "outbound") {
    const who = mate ? `${firstName(mate)} called` : "Outgoing call";
    if (c.status === "canceled") return { text: mate ? `${firstName(mate)} hung up before they answered` : "Hung up before they answered", iconName: "arrowOut", tone: "" };
    return c.status === "missed" ? { text: `${who} · no answer`, iconName: "arrowOut", tone: "" } : { text: `${who}${talk}`, iconName: "arrowOut", tone: "" };
  }
  if (c.status === "missed") return { text: "Missed call", iconName: "arrowIn", tone: "bad" };
  return { text: `${mate ? `Called · ${firstName(mate)} answered` : "Called"}${talk}`, iconName: "arrowIn", tone: "" };
}

/** First line of the AI summary (or transcript) as a one-line preview. */
function previewText(text: string): string {
  const first = text.split("\n").map((l) => l.trim()).find((l) => l && !/^next:|^next steps|^action items/i.test(l)) ?? text;
  return first.replace(/^•\s*/, "");
}

/** Keeps focus, cursor and scroll positions across re-renders. */
/**
 * Text you've typed into a form field (settings, invites, greetings…) that isn't saved yet. Live updates rebuild the
 * screen; this puts your typing back into the fresh fields. Cleared when you press a button (save, add…) or leave.
 */
const typedValues = new Map<string, string>();
// Fields that keep their own text in state (restoring a stale copy would leak into the next edit / search).
const KEEPS_OWN_DRAFT = new Set(["draft", "search", "inline-edit", "reply-inline", "quick-add-front", "rail-search", "task-title", "task-due", "task-who", "shade", "line-task-title", "line-task-due", "line-task-who"]);
document.addEventListener("input", (e) => {
  const el = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
  if (!el?.id || KEEPS_OWN_DRAFT.has(el.id) || !("value" in el)) return;
  if (el instanceof HTMLInputElement && /^(file|checkbox|radio|range)$/.test(el.type)) return;
  typedValues.set(el.id, el.value);
}, true);
// The browser's autofill never writes into the search boxes (it once put the sign-in email into the sidebar
// search, 20.10): such an input is undone before the app sees it.
const NO_AUTOFILL: Record<string, () => string> = { "rail-search": () => state.railQ, "global-search": () => state.searchQ };
document.addEventListener("input", (e) => {
  const el = e.target as HTMLInputElement | null;
  const keep = el?.id ? NO_AUTOFILL[el.id] : undefined;
  if (!el || !keep) return;
  const is = (sel: string) => {
    try {
      return el.matches(sel);
    } catch {
      return false; // this browser doesn't know that selector
    }
  };
  if (!is(":autofill") && !is(":-webkit-autofill")) return;
  e.stopImmediatePropagation();
  el.value = keep();
}, true);
document.addEventListener("click", (e) => {
  if ((e.target as HTMLElement | null)?.closest?.("button, [role=button]") && !(e.target as HTMLElement).closest(".composer")) {
    // The click handler reads the fields first (it runs after this), then its own update shows the saved values.
    setTimeout(() => typedValues.clear(), 0);
  }
}, true);

setKeepTyped((id) => typedValues.has(id));
let lastScrollRoute = "";
function preserving(fn: () => void) {
  const active = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
  const focusId = active?.id;
  const sel = focusId && "selectionStart" in active! && typeof active.selectionStart === "number" ? [active.selectionStart, active.selectionEnd] : null;
  const scrolls = [...document.querySelectorAll<HTMLElement>("[data-scroll]")].map((el) => [el.dataset.scroll!, el.scrollTop, el] as const);
  // Pages (Settings and the rest) and the sidebar keep their scroll position when the screen refreshes; a page
  // only starts at the top when you go to a different one.
  const pageKey = `page:${JSON.stringify(state.route)}`;
  const pageTops = [...document.querySelectorAll<HTMLElement>(".page")].map((el, i) => [`${el.dataset.pageKey ?? ""}#${i}`, el.scrollTop, el] as const);
  const railEl = document.querySelector<HTMLElement>(".rail");
  const railTop = railEl?.scrollTop ?? 0;
  const winY = window.scrollY;
  fn();
  // Only write scroll positions that changed: each write after a refresh makes the browser lay the page out on
  // the spot, which was the biggest single cost of a refresh on phones.
  // Never write to a list that stayed on screen (see mount): you may be scrolling it right now, and on iPhone
  // putting it back where it was a moment ago fought your finger. Settings and the inbox "stuck" for the first
  // seconds, while the app was still refreshing often.
  document.querySelectorAll<HTMLElement>(".page").forEach((el, i) => {
    el.dataset.pageKey = pageKey;
    const prev = pageTops.find(([k]) => k === `${pageKey}#${i}`);
    if (prev) {
      if (prev[2] !== el && prev[1] && el.scrollTop !== prev[1]) el.scrollTop = prev[1];
    } else if (el.scrollTop && pageTops.some(([, , was]) => was === el)) el.scrollTop = 0; // the list you tapped in now shows another page
  });
  const rail = document.querySelector<HTMLElement>(".rail");
  if (rail && rail !== railEl && railTop && rail.scrollTop !== railTop) rail.scrollTop = railTop;
  if (winY && lastScrollRoute === pageKey && window.scrollY !== winY) window.scrollTo(0, winY);
  lastScrollRoute = pageKey;
  for (const [id, value] of typedValues) {
    const el = document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
    if (el && "value" in el && el.value !== value) el.value = value;
  }
  for (const [key, top, was] of scrolls) {
    const el = document.querySelector<HTMLElement>(`[data-scroll="${key}"]`);
    if (el && el !== was && top && el.scrollTop !== top) el.scrollTop = top;
  }
  if (focusId) {
    const el = document.getElementById(focusId) as HTMLInputElement | null;
    // The field you're typing in usually stayed on screen (see mount): then leave it completely alone.
    if (el && el !== active) {
      el.focus();
      try {
        if (sel) el.setSelectionRange(sel[0], sel[1]);
      } catch {
        // inputs like email don't support selection
      }
    }
  }
}

/** Bold @mentions in text. */
/**
 * Selena's words, easy to read: each paragraph its own block with space between, "• " lists with room per line,
 * and a "Draft:" for a client set apart. An older reply that came as one long block is split every two sentences.
 */
function selenaText(body: string): Node[] {
  let t = body.replace(/\r\n?/g, "\n").replace(/\*\*(.+?)\*\*/g, "$1").trim();
  if (!t.includes("\n") && t.length > 260) {
    const ABBR = /\b(Mr|Mrs|Ms|Dr|St|Jr|Sr|vs|etc|approx|No|e\.g|i\.e|a\.m|p\.m|U\.S|Ave|Inc|Ltd|Co)\.$/i;
    const split = (part: string) => {
      const out: string[] = [];
      let para: string[] = [], cur = "";
      for (const w of part.trim().split(/\s+/).filter(Boolean)) {
        cur = cur ? `${cur} ${w}` : w;
        if (/[.!?]["”')]?$/.test(w) && !ABBR.test(w)) {
          para.push(cur), (cur = "");
          if (para.length === 2 || para.join(" ").length > 220) out.push(para.join(" ")), (para = []);
        }
      }
      if (cur) para.push(cur);
      if (para.length) out.push(para.join(" "));
      return out;
    };
    // A quoted draft for the client stays whole, in its own paragraph.
    const m = /((?:Here's a )?[Dd]raft[^:"“]{0,40}:\s*)["“]([^"”]{40,})["”]/.exec(t);
    t = (m ? [...split(t.slice(0, m.index)), `${(m[1] ?? "Draft: ").trim()}\n"${m[2].trim()}"`, ...split(t.slice(m.index + m[0].length))] : split(t)).join("\n\n");
  }
  return t.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean).map((p) => {
    const list = p.split("\n").every((l) => /^\s*(•|-|\d+[.)])\s/.test(l)) && p.includes("\n");
    const kind = /^draft\b/i.test(p) ? "sel-p sel-draft" : list || /^\s*•/.test(p) ? "sel-p sel-list" : "sel-p";
    return h("div", { class: kind }, ...withMentions(p));
  });
}

function withMentions(body: string): Node[] {
  return body.split(/(@[\p{L}\p{N}_.-]+)/u).flatMap((part) => (part.startsWith("@") ? [h("span", { class: "mention" }, part)] : withLinks(part)));
}

/**
 * A web link in text: "https://…", and (1.10.29 "a url I send shows only as text") "www.…" or a bare site like
 * joytravel.com / example.co.uk/page. Not the domain of an email address, and not file names (no web ending).
 */
const LINK_RE = /(\/media\/web-[a-f0-9]{24}\.jpg|https?:\/\/[^\s<>"]+[^\s<>".,;:!?)\]'"]|(?<![@\w.\/-])(?:www\.)?(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|net|org|edu|gov|io|co|us|uk|ca|il|au|de|fr|it|es|eu|info|biz|me|app|ly|travel|tv|ai|link|site|online|store|shop|page|to)(?![\w@-])(?:\/[^\s<>"]*[^\s<>".,;:!?)\]'"])?)/i;
const linkHref = (part: string) => (/^https?:\/\//i.test(part) ? part : `https://${part}`);

/** Web links in texts, notes and chat open in a new tab. */
function withLinks(text: string): Node[] {
  // Selena's website-booking screenshots ("Screenshot: /media/web-….jpg") show as a picture you can tap open.
  return text.split(LINK_RE).map((part, i) =>
    i % 2 === 0 ? document.createTextNode(part)
    // 12.10.21 "Steven shares a screenshot, there is nowhere to X it": it opened as a bare page (on the phone app, with
    // no way back). Now it opens in the picture viewer, with its × (and Back / swipe down) to close.
    : part.startsWith("/media/") ? h("a", { href: part, class: "web-shot", onclick: (e: MouseEvent) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return; // computer: ⌘-click still opens a new tab
        e.preventDefault();
        openViewer([{ url: part, type: "image/jpeg", name: "Screenshot of the booking page" }], 0);
      } }, h("img", { src: part, alt: "Screenshot of the booking page", loading: "lazy" }))
    : h("a", { href: linkHref(part), target: "_blank", rel: "noopener noreferrer", class: "inline-link" }, part.replace(/^https?:\/\/(www\.)?/, "").slice(0, 60) + (part.length > 70 ? "…" : "")));
}

// ---------------------------------------------------------------- render

/**
 * Inbox search: only the inbox list changes while you type, so only it is rebuilt (once per frame). Rebuilding
 * the whole screen for every key made the phone redo the layout of everything on it.
 */
let listFrame = 0;
function renderListSoon() {
  if (listFrame || renderFrame) return;
  listFrame = requestAnimationFrame(() => {
    listFrame = 0;
    if (holdForFinger(true)) return;
    const old = document.querySelector(".pane-list");
    if (!old || sectionOf(state.route) !== "inbox" || !state.user) return render();
    preserving(() => {
      // 4.10.29: the same inbox: unchanged rows stay put (see setSameScreen).
      setSameScreen(true);
      try {
        patch(old, listPane());
      } finally {
        setSameScreen(false);
      }
    });
  });
}

/**
 * For typing: the screen catches up at most once per frame, however fast the keys come (each refresh rebuilds
 * the screen; doing it for every key made typing lag on phones). render() itself stays immediate.
 */
let renderFrame = 0;
/** The screen the last full redraw showed (page + line + layout): a redraw of the same one keeps its lists. */
let lastScreenKey = "";
function renderSoon() {
  if (renderFrame) return;
  renderFrame = requestAnimationFrame(() => {
    renderFrame = 0;
    render();
  });
}

let tick: number | undefined;
/**
 * Taps never get lost to a refresh: while a finger is on the screen, screen refreshes (a new text, a live update,
 * the call timer) wait until it lifts. Before, a refresh could swap out the button under your finger between touch
 * and release, and the tap did nothing. (Your own taps still update right away: the release comes before the click.)
 */
let fingerDown = 0;
let heldRender = false;
let fingerSince = 0;
document.addEventListener("pointerdown", (e) => {
  if (e.pointerType === "mouse") return;
  fingerDown++;
  fingerSince = Date.now();
}, { capture: true, passive: true });
/**
 * 3.10.29 "on mobile some things need pressing twice": the phone delivers the tap itself (the click) 6–18 ms after
 * the finger lifts (measured). The held refresh ran right at the lift, in between, and swapped the button out from
 * under the coming click, so the tap went nowhere. Now refreshes keep waiting from the lift until the click has
 * been delivered (or 350 ms, if the touch wasn't a tap), and then run.
 */
let clickDue = 0;
let clickDueTimer = 0;
const releaseHeld = () => {
  if (fingerDown || clickDue) return;
  if (heldRender) {
    heldRender = false;
    // after this tap's click handlers have run
    setTimeout(() => state.user && !fingerDown && !clickDue && render(), 0);
  }
};
const clickArrived = () => {
  if (!clickDue) return;
  clickDue = 0;
  clearTimeout(clickDueTimer);
  releaseHeld();
};
const fingerUp = (e?: Event) => {
  if (!fingerDown) return;
  fingerDown = 0;
  if (e?.type === "pointerup") {
    clickDue = Date.now();
    clearTimeout(clickDueTimer);
    clickDueTimer = window.setTimeout(clickArrived, 350);
  }
  releaseHeld();
};
for (const t of ["pointerup", "pointercancel"]) document.addEventListener(t, fingerUp, { capture: true, passive: true });
// Capture phase: runs before the button's own handler, so a redraw that handler asks for isn't held.
document.addEventListener("click", clickArrived, { capture: true, passive: true });
// 4.10.29: a button that handles the touch itself and cancels the click (Send keeps the keyboard open that way): no
// click is coming, so stop waiting for one. (Waiting the full 350 ms held the "sent" bubble back after every send.)
// Bubble phase: after the button's own handler has run.
document.addEventListener("touchend", (e) => e.defaultPrevented && clickArrived(), { passive: true });
window.addEventListener("blur", () => fingerUp());

/**
 * Scrolling: no redraws while you drag a list or while it's still gliding after you let go. (Phones cancel the
 * "finger down" pointer as soon as a touch turns into a scroll, so the hold above switched off exactly when you
 * scrolled, and a redraw mid-drag made the inbox catch and stick. Touch events keep going for the whole drag.)
 */
let touching = false;
let touchSince = 0;
let lastScrollAt = 0;
let scrollHoldTimer = 0;
document.addEventListener("touchstart", () => ((touching = true), (touchSince = Date.now())), { capture: true, passive: true });
for (const t of ["touchend", "touchcancel"]) document.addEventListener(t, (e) => {
  if ((e as TouchEvent).touches.length === 0) touching = false;
}, { capture: true, passive: true });
document.addEventListener("scroll", () => (lastScrollAt = Date.now()), { capture: true, passive: true });
// A tap, click or key of your own after the last scroll: that redraw is yours (open a page, focus a new field), so
// it's never held — only live updates wait for the list to stop.
let lastInputAt = 0;
for (const t of ["click", "keydown", "input", "change"]) document.addEventListener(t, () => (lastInputAt = Date.now()), { capture: true, passive: true });
const scrolling = () => lastInputAt < lastScrollAt
  && ((touching && Date.now() - touchSince < 15_000 && Date.now() - lastScrollAt < 2_000) || Date.now() - lastScrollAt < 180);
/** After a held redraw: try again once the list has stopped moving. */
/** Set when a whole-screen redraw waited for the scroll; otherwise only the inbox list waited (more rows loaded). */
let stillNeedsFull = false;
function renderWhenStill() {
  if (scrollHoldTimer) return;
  scrollHoldTimer = window.setTimeout(function again() {
    if (scrolling()) return void (scrollHoldTimer = window.setTimeout(again, 120));
    scrollHoldTimer = 0;
    if (!state.user) return;
    // Loading more inbox rows while scrolling redrew the whole screen (1–2.6 s freezes on a phone); now just the list.
    const full = stillNeedsFull;
    stillNeedsFull = false;
    if (full) render(); // render() holds again (and redraws on lift) if a finger is down
    else renderListSoon();
  }, 120);
}

/** A refresh that came while a finger was down or a list was moving: held (true) or go ahead (false). */
/** Dragging the Brightness lever: a redraw mid-drag would replace the slider and drop it, so it waits for the lift. */
let shadeDrag = false;
let shadeHeld = false;
document.addEventListener("pointerdown", (e) => (shadeDrag = (e.target as HTMLElement | null)?.id === "shade"), { capture: true, passive: true });
for (const t of ["pointerup", "pointercancel"]) document.addEventListener(t, () => {
  if (!shadeDrag) return;
  shadeDrag = false;
  if (shadeHeld) {
    shadeHeld = false;
    setTimeout(() => state.user && render(), 0);
  }
}, { capture: true, passive: true });

function holdForFinger(listOnly = false): boolean {
  // An incoming call shows right away even mid-scroll; but a finger on a button (Answer!) still holds the redraw
  // until it lifts (at most 1.5 s), or the tap would be dropped.
  const ringingHere = !!voice.call && voice.call.direction === "inbound" && voice.call.state === "ringing";
  if (shadeDrag) {
    shadeHeld = true;
    return true;
  }
  if (scrolling() && !ringingHere) {
    if (!listOnly) stillNeedsFull = true;
    renderWhenStill();
    return true;
  }
  // Lifted, and the tap (click) is on its way: hold until it lands (see clickDue).
  if (!fingerDown && clickDue && Date.now() - clickDue < 350) {
    heldRender = true;
    return true;
  }
  if (!fingerDown) return false;
  if (Date.now() - fingerSince > 1500) {
    fingerDown = 0;
    return false;
  }
  heldRender = true;
  return true;
}

/** Scroll positions of the list hidden (not drawn) while a phone shows a conversation, the keypad or a contact. */
/**
 * iPhone: closing the keyboard could leave the whole app pushed up a little (the top cut off under the clock) on
 * screens with a message box. The app itself never scrolls there, so put it back.
 */
function unshiftAfterKeyboard() {
  setTimeout(() => {
    const el = document.activeElement as HTMLElement | null;
    if (el && /^(INPUT|TEXTAREA)$/.test(el.tagName)) return; // still typing
    if (window.scrollY !== 0 && document.querySelector(".composer")) window.scrollTo(0, 0);
  }, 80);
}
document.addEventListener("focusout", unshiftAfterKeyboard);
// 2.11.1 safety net for the "screen wipeout": on a computer the window itself never scrolls (each screen scrolls inside
// its own area). If anything ever scrolls it, it's put straight back, so the app can't slide up and leave a blank band.
window.addEventListener("scroll", () => {
  if (window.scrollY !== 0 && matchMedia("(pointer: fine)").matches) window.scrollTo(0, 0);
}, { passive: true });
window.visualViewport?.addEventListener("resize", unshiftAfterKeyboard);

/** The AI agent's name (Selena unless renamed in Settings; Steven etc. with a man's voice). */
/** The assistant's name and he/she from the last visit, so the first screen never flashes "Selena" for Steven. */
function savedAgent(): { name: string; man: boolean } {
  try {
    // Per line (your personal assistant can be someone else); cleared when you sign out.
    const v = JSON.parse(localStorage.getItem(`joy-agent:${localStorage.getItem("joy-line") === "personal" ? "personal" : "team"}`) ?? "null");
    if (v && typeof v.name === "string" && v.name) return { name: v.name, man: !!v.man };
  } catch {
    // nothing saved
  }
  return { name: "Selena", man: false };
}
const aiName = () => state.agentName || "Selena";
/**
 * The app's words about the assistant, with the right pronouns: "she / her / herself" become "he / him / his / himself"
 * when Steven (or his custom name) answers. `man` defaults to who answers on this line now.
 */
function pron(text: string, man: boolean = state.agentMan): string {
  if (!man) return text;
  return text
    .replace(/\bShe('ll|'d|'s)?\b/g, (_m, t) => `He${t ?? ""}`)
    .replace(/\bshe('ll|'d|'s)?\b/g, (_m, t) => `he${t ?? ""}`)
    .replace(/\bherself\b/g, "himself")
    .replace(/\bHer\b/g, "His")
    .replace(/\bher\b(?=\s+(?:own|voice|voices|original|call|calls|browser|work|chat|details|name|personal|phone|line|results|message|rules)\b)/g, "his")
    .replace(/\bher\b/g, "him");
}

/** Where the finger first touched the send button (to tell a tap from a scroll). */
let sendTouch: { x: number; y: number } | null = null;

/** Which line a ringing call is for: the X-Joy-Line header, else what the server says about the live call. */
function ringLineOf(c: PhoneCall): "team" | "personal" | null {
  const hdr = c.options.customHeaders?.find((x) => x.name.toLowerCase() === "x-joy-line")?.value.trim().toLowerCase();
  if (hdr === "personal" || hdr === "team") return hdr;
  // 1.10.28: Steven calling you yourself ("any"): it rings on whichever line you're on (it was taken as Joy Travel's
  // teammate call and hidden on your personal line: "Calling…" in the inbox, but no call window).
  if (hdr === "any") return null;
  if (c.intercomName || c.isMonitor) return "team"; // a teammate calling, or listening in: Joy Travel
  const from = toE164(c.options.remoteCallerNumber ?? "") ?? c.options.remoteCallerNumber;
  const live = state.live.find((x) => x.direction === "inbound" && x.external_number === from && (x.status === "ringing" || x.status === "voicemail"));
  return live ? ((live as { private_to?: number | null }).private_to ? "personal" : "team") : null;
}
/**
 * Total separation: on your personal line Joy Travel's calls don't ring or show here, and on Joy Travel's line your
 * personal calls don't (each rings on the devices that are on its own line).
 */
function hiddenRing(c: PhoneCall | null | undefined): boolean {
  // (A brand-new incoming call has no direction yet: anything not outgoing counts.)
  if (!c || c.direction === "outbound" || !["new", "requesting", "trying", "ringing", "early"].includes(c.state)) return false;
  const line = ringLineOf(c);
  // Every ring carries its line (X-Joy-Line). In the rare case it doesn't and nothing else tells, it rings rather
  // than risk a missed call.
  if (!line) return false;
  return line !== (state.line === "personal" ? "personal" : "team");
}

// The phone line never even takes a call for the other line (see Phone.ignoreIncoming).
voice.ignoreIncoming = (c) => hiddenRing(c);

const listTops = new Map<string, number>();
/**
 * Things that animate in once (call windows, the contact side panel, pickers), by what they show: on screen after
 * the last render. A screen update rebuilds them, and without this their entrance animation replayed every time,
 * which looked like flashing (9.10.18).
 */
let shownWindows = new Set<string>();
let enteringNow = new Set<string>();
/** `el` animates in only the first time `key` appears; later updates show it as it is. */
function enterOnce<T extends Element>(el: T, key: string): T {
  enteringNow.add(key);
  if (shownWindows.has(key)) el.classList.add("steady");
  return el;
}
/**
 * Looping animations (pulses, ripples, typing dots) keep their rhythm across screen updates instead of restarting
 * from the beginning on each rebuilt element: all of them run on the same clock.
 */
function keepLoopsInStep() {
  try {
    for (const a of document.getAnimations()) {
      if (typeof CSSAnimation !== "undefined" && a instanceof CSSAnimation && a.effect?.getTiming().iterations === Infinity && a.startTime !== 0) a.startTime = 0;
    }
  } catch {
    // older browsers: they just restart, as before
  }
}
function render() {
  if (holdForFinger()) return;
  if (renderFrame) {
    cancelAnimationFrame(renderFrame);
    renderFrame = 0;
  }
  if (listFrame) {
    cancelAnimationFrame(listFrame);
    listFrame = 0;
  }
  if (!state.user) return;
  clearInterval(tick);
  // If you were reading the newest messages, stay at the bottom when new ones arrive.
  const tlBefore = document.querySelector<HTMLElement>('[data-scroll="timeline"]');
  const atBottom = !!tlBefore && tlBefore.scrollHeight - tlBefore.scrollTop - tlBefore.clientHeight < 60 && (state.route.name !== "contact" || tlBefore.scrollTop > 0);
  preserving(() => {
    const call = voice.call && !isEnded(voice.call) && !hiddenRing(voice.call) ? voice.call : null;
    // A call the app is answering by itself (you pressed Answer in the inbox) is "connecting", not ringing: no
    // incoming window with a second Answer button for it (1.10.20).
    const ringingIn = !!call && call.direction === "inbound" && call.state === "ringing" && !call.autoAnswered;
    // Desktop: calls live in a small window in the corner (like Quo), and the app stays usable around it.
    const desk = deskCalls();
    // A call window already on screen is updated in place: its pop-in plays once, when it first appears, not on
    // every update while the call sets up (calling → ringing → connected, plus inbox updates). That replay was the
    // window "flashing" 5–10 times at the start of every call (9.10.18).
    enteringNow = new Set<string>();
    const steady = (el: HTMLElement, key: string) => enterOnce(el, key);
    if (call && !ringingIn && !state.callMinimized && !desk) {
      const screen = steady(callScreen(call) as HTMLElement, `call:${call.id}`);
      screen.dataset.call = call.id;
      shownWindows = enteringNow;
      // The same call's screen is already up: update it in place (no flash), unless you're typing in it (then the
      // usual refresh keeps your typing).
      const onScreen = root.childElementCount === 1 ? (root.firstElementChild as HTMLElement) : null;
      const typing = isTextField(document.activeElement) && !!onScreen?.contains(document.activeElement);
      if (onScreen?.dataset.call === call.id && !typing && reuseFrame(onScreen, screen, ".glass-card", ".glass-avatar")) return void keepLoopsInStep();
      mount(root, screen);
      return void keepLoopsInStep();
    }
    const dock = call && !ringingIn && !state.callMinimized && desk ? steady(placeDock(h("div", { class: "call-dock live" }, callScreen(call)) as HTMLElement, "live"), `call:${call.id}`) : null;

    const bar = call && !ringingIn && !dock
      ? h("button", { class: "callbar", onclick: () => ((state.callMinimized = false), render()) },
          icon("phone", "sm"), `On a call with ${call.intercomName ?? phone(otherParty(call))} · Return to call`)
      : null;
    const r = state.route;
    const section = sectionOf(r);
    const showMain = r.name !== "inbox" && r.name !== "contacts";
    // Phones show one pane at a time: don't build the list that would be hidden under a conversation, the keypad,
    // Team chat or a contact (it cost a few hundred ms on every tap at phone speed).
    const hideList = showMain && !isWide();
    // Its scroll position is kept for when you come back to it.
    if (hideList) document.querySelectorAll<HTMLElement>(".pane-list [data-scroll]").forEach((el) => listTops.set(el.dataset.scroll!, el.scrollTop));
    const list = hideList ? null : section === "inbox" ? listPane() : section === "contacts" ? contactsPane() : null;
    const shell = h("div", { class: `shell sec-${section}${showMain ? " show-main" : ""}` }, rail(), list, mainPane());
    // Incoming calls pop up over whatever you're doing, on frosted glass.
    const teamRing = !call && !state.pendingAnswer && !popupConnecting ? glassTeamRing() : null;
    // It rings like any incoming call (your ringtone), and stops when it's answered, ends or you close it.
    if (teamRing) {
      ringer.start();
      glassRinging = true;
    } else if (glassRinging) {
      glassRinging = false;
      if (!call) ringer.stop();
    }
    const ringPopup = ringingIn ? incomingPopup(call!) : state.pendingAnswer && !call ? answerPopup(state.pendingAnswer)
      : popupConnecting && !call ? answerPopup(popupConnecting)
      : teamRing ? answerPopup(teamRing.id, () => (state.glassDismissed.add(teamRing.id), render())) : null;
    // A ringing call on desktop: a compact card in the corner, not a window over everything.
    const ringKey = ringingIn ? `ring:${call!.id}` : state.pendingAnswer && !call ? `answer:${state.pendingAnswer}` : popupConnecting && !call ? `answer:${popupConnecting}` : teamRing ? `team:${teamRing.id}` : "";
    const popup = ringPopup ? steady((desk ? placeDock(h("div", { class: "call-dock ring" }, ringPopup) as HTMLElement, "ring") : ringPopup) as HTMLElement, ringKey) : state.assignFor ? assignPicker(state.assignFor) : null;
    shownWindows = enteringNow;
    // 4.10.29: redrawing the screen that's showing (same page, same conversation, same line): its lists stay on screen
    // and unchanged rows aren't touched (see setSameScreen in ui.ts). Another screen is drawn fresh.
    const screenKey = `${JSON.stringify(state.route)}|${state.line}|${desk}`;
    setSameScreen(screenKey === lastScreenKey);
    lastScreenKey = screenKey;
    try {
      mount(root, ...([bar, takeoverBar() ?? liveBrowserBar(), shell, bottomNav(), popup, dock, takeoverView()].filter(Boolean) as Node[]));
    } finally {
      setSameScreen(false);
    }
    keepLoopsInStep();
  });
  if (listTops.size && document.querySelector(".pane-list")) {
    for (const [k, top] of listTops) {
      const el = document.querySelector<HTMLElement>(`.pane-list [data-scroll="${k}"]`);
      if (el && top && el.scrollTop !== top) el.scrollTop = top;
    }
    listTops.clear();
  }
  // Open a thread or the chat at its newest activity.
  const key = state.route.name === "thread" && state.thread ? state.thread.number : state.route.name === "chat" && state.chat.length ? "chat" : "";
  const tl = document.querySelector<HTMLElement>('[data-scroll="timeline"]');
  if (key && state.scrolled !== key) {
    if (tl) {
      tl.scrollTop = tl.scrollHeight;
      state.scrolled = key;
    }
  } else if (tl && (atBottom || Date.now() < jumpStickUntil) && !state.focusRef && !fingersOnScreen()) {
    tl.scrollTop = tl.scrollHeight;
  }
  if (state.focusRef) flashRef();
  placePopovers();
  // Phones: the floating tab bar is showing (pages leave room under their content for it).
  document.body.classList.toggle("bnav", !!document.querySelector(".bottom-nav"));
  fitDialCard();
  updateJumpDown();
}

/**
 * The round ↓ button over a conversation, Team chat or Selena's chat: shows whenever you're scrolled up from the
 * newest messages (with a dot when something new came in below), and takes you straight back down. One button for
 * the whole app, placed over whichever timeline is on screen, so a refresh never loses it.
 */
let jumpBtn: HTMLButtonElement | null = null;
/** Just after tapping ↓: refreshes keep the timeline at the bottom (until then, or until you scroll up). */
var jumpStickUntil = 0; // var: render() can run before this line on a fast start
let jumpNew = false;
let jumpLastHeight = 0;
let jumpLastTl: HTMLElement | null = null;
function jumpTimeline(): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>('[data-scroll="timeline"]')) if (el.offsetParent && el.clientHeight > 0) return el;
  return null;
}
function updateJumpDown() {
  const tl = jumpTimeline();
  if (!jumpBtn) {
    jumpBtn = h("button", { class: "jump-down", type: "button", "aria-label": "Jump to the newest messages", title: "Newest messages", onclick: () => {
      const t = jumpTimeline();
      if (!t) return;
      jumpNew = false;
      // Straight to the newest message, and kept there for a moment: a smooth scroll stopped halfway whenever the
      // screen refreshed or a picture finished loading meanwhile.
      jumpStickUntil = Date.now() + 1500;
      const down = () => {
        const x = jumpTimeline();
        if (x) x.scrollTop = x.scrollHeight;
      };
      down();
      requestAnimationFrame(down);
      for (const ms of [120, 400, 1000]) setTimeout(down, ms);
    } }, icon("chevronDown", "md"), h("span", { class: "jump-dot", "aria-hidden": "true" })) as HTMLButtonElement;
    document.body.append(jumpBtn);
  }
  const fromBottom = tl ? tl.scrollHeight - tl.scrollTop - tl.clientHeight : 0;
  if (tl !== jumpLastTl) {
    jumpLastTl = tl;
    jumpNew = false;
    jumpLastHeight = tl?.scrollHeight ?? 0;
  } else if (tl && tl.scrollHeight > jumpLastHeight + 4 && fromBottom > 60) {
    jumpNew = true; // something new arrived below while you were reading up
  }
  if (tl) jumpLastHeight = tl.scrollHeight;
  // Shown whenever you're not at the bottom (the same 60 px that decides whether new messages scroll you down), so
  // there's never a spot where it's hidden and new messages don't follow either.
  const show = !!tl && fromBottom > 60 && !document.querySelector(".modal-backdrop, .viewer, .glass-overlay:not(.call-dock .glass-overlay)");
  if (fromBottom < 60) jumpNew = false;
  jumpBtn.classList.toggle("show", show);
  jumpBtn.classList.toggle("new", show && jumpNew);
  if (!show || !tl) return;
  const r = tl.getBoundingClientRect();
  jumpBtn.style.left = `${Math.round(r.left + r.width / 2)}px`;
  jumpBtn.style.top = `${Math.round(r.bottom - 56)}px`;
}
// Any timeline scrolling (or the window resizing) moves or hides it.
document.addEventListener("scroll", (e) => {
  if ((e.target as HTMLElement)?.dataset?.scroll === "timeline") updateJumpDown();
}, { capture: true, passive: true });
window.addEventListener("resize", () => updateJumpDown(), { passive: true });
// Pictures finishing loading, a call screen covering the chat…: a cheap check every second keeps it right.
setInterval(() => { if (state.user) updateJumpDown(); else jumpBtn?.classList.remove("show"); }, 1000);

/** Keeps emoji pickers on screen: open downward when there's no room above, nudge in from the edges. */
function placePopovers() {
  for (const pop of document.querySelectorAll<HTMLElement>(".emoji-pop")) {
    if (getComputedStyle(pop).position === "fixed") { // phones: docked above the keyboard, just over the message box
      const box = document.querySelector<HTMLElement>(".composer");
      pop.style.bottom = box ? `${Math.max(0, window.innerHeight - box.getBoundingClientRect().top) + 6}px` : "";
      continue;
    }
    pop.classList.remove("below");
    pop.style.translate = "";
    const scroller = pop.closest<HTMLElement>("[data-scroll]") ?? document.body;
    const top = Math.max(scroller.getBoundingClientRect().top, 0);
    let r = pop.getBoundingClientRect();
    if (r.top < top + 6) {
      pop.classList.add("below");
      r = pop.getBoundingClientRect();
    }
    const overRight = r.right - (window.innerWidth - 8);
    if (overRight > 0) pop.style.translate = `${-overRight}px 0`;
    else if (r.left < 8) pop.style.translate = `${8 - r.left}px 0`;
  }
}

/** Scrolls to and highlights the item a search result or alert pointed at. */
let focusRefSince: { ref: string; at: number } | null = null;

function flashRef() {
  const ref = state.focusRef!;
  const [kind] = ref.split(":");
  const rest = ref.slice(kind.length + 1); // call ids can contain ":"
  const elId = kind === "comment" ? `c-${rest}` : kind === "sms" ? `s-${rest}` : kind === "call" ? `call-${rest}` : kind === "note" ? `n-${rest}` : kind === "chat" ? `m-${rest}` : kind === "agent" ? `a-${rest}` : kind === "task" ? `task-${rest}` : "";
  const el = elId ? document.getElementById(elId) : null;
  if (!el) {
    // Links that never become a message on screen (a contact, a task, a message too old to be loaded) are dropped
    // after a few seconds, so "stay at the bottom when new messages arrive" keeps working.
    if (focusRefSince?.ref !== ref) focusRefSince = { ref, at: Date.now() };
    if (!elId || Date.now() - focusRefSince.at > 5000) state.focusRef = null;
    return;
  }
  focusRefSince = null;
  state.focusRef = null;
  requestAnimationFrame(() => {
    el.scrollIntoView({ block: "center" });
    el.classList.add("flash");
    setTimeout(() => el.classList.remove("flash"), 1600);
  });
}

/** Your status. Click it to switch Do Not Disturb on or off. */
/** When the app started trying to reach the phone line: a short grace period before saying it's reconnecting. */
const lineSince = Date.now();
// The "Reconnecting the phone line…" bar waits 6 s before showing; draw once then in case nothing else redraws.
setTimeout(() => !voice.ready && rerenderAny(), 6100);
setTimeout(() => rerenderAny(), 6100);
window.addEventListener("online", () => rerenderAny());
window.addEventListener("offline", () => rerenderAny());
/** A slim amber bar while the phone line is offline (calls can't ring this device). Tap it to try again. */
// 3.10.21 "it still says reconnecting phone line … just the yellow or green light": the bar is gone everywhere; the
// light next to the name (yellow while connecting, green online) is the only sign, and the line reconnects by itself.
function connBar(): null {
  return null;
}

/**
 * 12.10.21 "put Do Not Disturb to the left top on the inbox screen, so it doesn't collide with the personal / business
 * toggle; make the icon something else so it looks unique instead of just a light": phones, top left of the header. A phone icon with a small light (green: you'll ring;
 * amber: connecting); Do Not Disturb on: a red moon. Tap to switch Do Not Disturb.
 */
function statusChip() {
  if (!state.user?.canCall) return null;
  const dnd = !!state.user.dnd;
  const ready = voice.ready;
  return h("button", { class: `status-chip only-narrow${dnd ? " dnd" : ready ? " ready" : " connecting"}`, type: "button",
    "aria-pressed": String(dnd),
    "aria-label": dnd ? "Do Not Disturb is on. Tap to turn it off." : ready ? "Calls ring here. Tap to turn on Do Not Disturb." : "Phone line connecting. Tap to turn on Do Not Disturb.",
    title: dnd ? "Do Not Disturb" : ready ? "Available" : "Connecting",
    onclick: () => void toggleDnd(!dnd) }, // (it says what changed)
    // 12.10.21: available = a green circle with a check; Do Not Disturb = a red circle with a moon. (The line's
    // connected light is back next to the name.)
    icon(dnd ? "moon" : "check", "sm"));
}

function lineState() {
  if (!state.user?.canCall) return h("span", { class: "line-state", title: "No calling" }, h("span", { class: "dot" }), h("span", { class: "ls-text" }, "No calling"));
  if (state.user.dnd) return h("button", { class: "line-state dnd", title: "Do Not Disturb is on: you won't ring. Click to turn it off.", onclick: (e: MouseEvent) => (e.stopPropagation(), void toggleDnd(false)) }, h("span", { class: "dot" }), h("span", { class: "ls-text" }, "Do not disturb"));
  return h("button", { class: voice.ready ? "line-state ready" : "line-state", title: `${voice.ready ? "Online" : "Connecting"}. Click to turn on Do Not Disturb (you won't ring)`, onclick: (e: MouseEvent) => (e.stopPropagation(), void toggleDnd(true)) },
    // 8.10.20: just the light (yellow while connecting, green when online), no "Connecting…" / "Online" words.
    h("span", { class: "dot", "aria-label": voice.ready ? "Online" : "Connecting" }));
}

// ---------------------------------------------------------------- list pane

const FILTER_LABEL: Record<Filter, string> = {
  all: "All", unread: "Unread", unresponded: "Unresponded", mine: "Assigned to me", missed: "Missed calls", voicemail: "Voicemail",
};

/** The inbox rows to show, in order (filter, search, on-a-call first, then pinned). */
function inboxItems() {
  const q = state.search.trim().toLowerCase();
  const digits = q.replace(/\D/g, "");
  const me = state.user!.id;
  const open = state.conversations.filter((c) => (state.showDone ? c.conv_status === "done" : c.conv_status !== "done"));
  const items = open.filter((c) => {
    if (state.filter === "unread" && !c.unread) return false;
    if (state.filter === "unresponded" && !c.unresponded) return false;
    if (state.filter === "mine" && c.assigned_to !== me) return false;
    if (state.filter === "missed" && !(c.status === "missed" && c.direction === "inbound")) return false;
    if (state.filter === "voicemail" && !c.voicemail_count) return false;
    if (!q) return true;
    // 7.10.30: also the name the row shows: caller ID name, a group's name or members (only the saved name matched).
    return (c.contact_name ?? "").toLowerCase().includes(q) || nameFor(c.external_number, c.contact_name).toLowerCase().includes(q)
      || ((c as { caller_name?: string | null }).caller_name ?? "").toLowerCase().includes(q) || (!!digits && c.external_number.includes(digits));
  });
  const onCall = new Set(state.live.filter((x) => x.status === "active").map((x) => x.external_number));
  items.sort((a, b) => Number(onCall.has(b.external_number)) - Number(onCall.has(a.external_number)) || Number(b.pinned) - Number(a.pinned));
  return items;
}

/**
 * 4.10.29: scrolling down the inbox froze for up to 0.6 s at phone speed each time more rows came in: the whole list
 * (up to hundreds of rows) was built again to add the next ones. Now only the new rows are built and added at the
 * bottom. (Anything else on screen that changed meanwhile is redrawn by the next refresh as usual.)
 */
function appendInboxRows(from: number) {
  const list = document.querySelector<HTMLElement>('.pane-list [data-scroll="list"]');
  const sentinel = list?.querySelector(".list-more");
  if (!list || !sentinel || !state.user || sectionOf(state.route) !== "inbox") return renderListSoon();
  const items = inboxItems();
  const current = state.route.name === "thread" ? state.route.number : null;
  const rows = inboxRowsWithGroups(items, from, listShown, current, state.user!.id);
  sentinel.replaceWith(...rows, ...(items.length > listShown ? [listMoreSentinel()] : []));
}

function listPane() {
  const q = state.search.trim().toLowerCase();
  const digits = q.replace(/\D/g, "");
  const me = state.user!.id;
  const items = inboxItems();
  const contactsEl = contactHits(q, items);
  const current = state.route.name === "thread" ? state.route.number : null;
  const unreadCount = state.conversations.filter((c) => c.unread && c.conv_status !== "done").length;

  // Every inbound call still on its way to a person: just arrived, in the greeting or menu, ringing, or leaving a voicemail.
  const incoming = state.live.filter((c) => (c.status === "ringing" || c.status === "voicemail") && c.direction === "inbound");
  // The caller shown in the glowing card at the top doesn't repeat the same status on their row below.
  inNotice = incoming.length === 1 ? incoming[0].external_number : null;

  const pill = (f: Filter, label: string, count = 0) =>
    h("button", { class: "tab", "aria-pressed": String(state.filter === f), onclick: () => ((state.filter = state.filter === f ? "all" : f), (state.filterMenu = false), render()) },
      label, count ? h("span", { class: "count" }, String(count)) : null);
  const moreActive = state.filter === "mine" || state.filter === "missed" || state.filter === "voicemail";
  const menuItem = (label: string, active: boolean, onclick: () => void) =>
    h("button", { role: "menuitemradio", "aria-checked": String(active), onclick: (e: MouseEvent) => (e.stopPropagation(), (state.filterMenu = false), onclick(), render()) },
      label, active ? icon("check", "sm") : null);

  const filters = h("div", { class: "tabs", role: "group", "aria-label": "Filter" },
    h("div", { class: "menu-wrap" },
      h("button", { class: "tab drop", "aria-pressed": "true", "aria-haspopup": "menu", onclick: (e: MouseEvent) => {
        e.stopPropagation();
        state.filterMenu = state.filterMenu === "status" ? false : "status";
        render();
      } }, state.showDone ? "Done" : "Open", icon("chevron", "sm")),
      state.filterMenu === "status"
        ? h("div", { class: "menu left", role: "menu" },
            menuItem("Open", !state.showDone, () => (state.showDone = false)),
            menuItem("Done", state.showDone, () => (state.showDone = true)))
        : null),
    pill("unread", "Unread", unreadCount),
    pill("unresponded", "Unresponded"),
    h("div", { class: "menu-wrap" },
      h("button", { class: "tab drop", "aria-pressed": String(moreActive), "aria-haspopup": "menu", "aria-label": moreActive ? undefined : "Filter", onclick: (e: MouseEvent) => {
        e.stopPropagation();
        state.filterMenu = state.filterMenu === "more" ? false : "more";
        render();
      } }, icon("filter", "sm"), h("span", { class: "ellipsis" }, moreActive ? FILTER_LABEL[state.filter] : "Filter")),
      state.filterMenu === "more"
        ? h("div", { class: "menu right", role: "menu" },
            menuItem("Everything", !moreActive, () => moreActive && (state.filter = "all")),
            menuItem("Assigned to me", state.filter === "mine", () => (state.filter = "mine")),
            menuItem("Missed calls", state.filter === "missed", () => (state.filter = "missed")),
            menuItem("Voicemail", state.filter === "voicemail", () => (state.filter = "voicemail")),
            h("div", { class: "menu-sep" }),
            h("button", { role: "menuitem", onclick: () => ((state.filterMenu = false), render(), void markAllRead()) }, icon("check", "sm"), "Mark all as read"))
        : null));

  const ringNotice = incoming.length
    ? (() => {
        const c = incoming[0];
        const st = incomingStatus(c, me);
        const title = incoming.length > 1 ? `${incoming.length} incoming calls` : nameFor(c.external_number, c.contact_name);
        return h("div", { class: "notice ringing", role: "status", "aria-live": "polite" },
          h("span", { class: "pulse" }, icon(c.status === "voicemail" ? "voicemail" : "incoming", "sm")),
          h("div", { class: "grow" }, h("strong", {}, title),
            h("span", { class: "sub" }, incoming.length > 1 ? `${nameFor(c.external_number, c.contact_name)}: ${st.text}` : st.text)),
          st.canAnswer && state.user!.canCall
            // 3.10.23: the same press handling as the incoming-call window (kept on screen through redraws).
            ? pressButton("inbox-answer", () => void answerFromServer(c.id), { class: answering === c.id ? "btn live sm busy" : "btn live sm", type: "button" }, answering === c.id ? "Connecting…" : "Answer")
            : null);
      })()
    : null;

  return h("aside", { class: state.line === "personal" ? "pane-list personal-line" : "pane-list" },
    // While searching on a computer, the box takes the whole line (status, keypad hidden until done).
    h("div", { class: listSearchOpen() ? "list-head searching" : "list-head" },
      // Phones: no Beta badge (the computer sidebar keeps it).
      // 12.10.21 "put Do Not Disturb to the right top on the inbox screen, and move alerts to the left top so it doesn't
      // collide with the personal / business switch": phones, alerts (the bell) top left…
      ((n) => h("button", { class: "icon-btn only-narrow head-bell", "aria-label": n ? `Activity, ${n} new` : "Activity", title: "Activity", onclick: () => go({ name: "activity" }) },
        icon("bell"), n ? h("span", { class: "nav-badge" }, n > 99 ? "99+" : String(n))
          : !alertsOn() && alertsBannerDismissed() ? h("span", { class: "bell-off-dot", "aria-hidden": "true" }) : null))(state.activityUnread + (updateAlert() ? 1 : 0)),
      h("div", { class: "brand only-narrow" }, brandMark(), phoneBrandName(),
        // The phone line's light right after the name, as before: green connected, amber connecting (just a light).
        state.user!.canCall ? h("span", { class: `brand-dot${voice.ready ? " ready" : ""}`, role: "img", "aria-label": voice.ready ? "Phone line connected" : "Phone line connecting", title: voice.ready ? "Connected" : "Connecting" }) : null),
      // Computer: a search icon next to "Inbox"; tapping it puts the search box where the title was.
      listSearchOpen() ? listSearchBox("list-search-wide") : h("div", { class: "list-title only-wide" }, state.line === "personal" ? personalLineName() : "Inbox"),
      listSearchOpen() ? null : h("button", { class: "icon-btn only-wide list-search-btn", "aria-label": "Search the inbox", title: "Search the inbox",
        onclick: () => {
          state.listSearchOpen = true;
          keyboardFor("search");
          render();
        } }, icon("search", "sm")),
      // While the inbox search is open (computers), it gets the whole line: the rest comes back when it closes.
      // Joy Travel's business hours are the office line's: never shown on your personal line (1.10.25).
      state.office?.enabled && !listSearchOpen() && state.line !== "personal" ? h("span", { class: state.office.open ? "office-pill open" : "office-pill", title: "Business hours (Settings → Phone line)" }, state.office.open ? "Open" : "Closed") : null,
      listSearchOpen() ? null : lineState(),
      // Computer: keypad up here. Phone: the keypad is the big button in the middle of the bottom bar, and
      // notifications (Activity) sit up here instead.
      state.user!.canCall && !listSearchOpen() ? h("button", { class: "icon-btn only-wide", "aria-label": "Keypad", title: state.line === "personal" ? `Call or text from ${personalLineName()}` : "Call or text a number", onclick: () => go({ name: "dialer" }) }, icon("dialpad")) : null,
      h("button", { class: "icon-btn only-narrow head-search", "aria-label": "Search the inbox", "aria-pressed": String(!!(state.phoneSearchOpen || state.search)), title: "Search",
        onclick: () => {
          if (state.phoneSearchOpen || state.search) {
            state.phoneSearchOpen = false;
            state.search = "";
            typedValues.delete("search");
            searchContactsSoon();
          } else state.phoneSearchOpen = true;
          // Grab the keyboard inside this tap (iPhone only opens it during the tap itself), then hand it to the box.
          if (state.phoneSearchOpen) keyboardFor("search");
          render();
        } }, icon("search")),
      // …and Do Not Disturb at the top right (12.10.21).
      statusChip()),
    connBar(),
    // Phones: the search bar opens from the search icon in the header (and stays while something is typed).
    isWide() || !(state.phoneSearchOpen || state.search) ? null : listSearchBox("search"),
    filters,
    ringNotice,
    state.storageWarn && isAdmin()
      ? h("button", { class: "notice slim storage-banner", onclick: () => go({ name: "storage" }) },
          icon("chart", "sm"), h("span", { class: "grow" }, `Storage ${Math.round(state.storageWarn.usedPct)}% full. When it's full, calls and texts stop.`), h("span", { class: "text-btn" }, "Fix"))
      : null,
    h("div", { class: "list", "data-scroll": "list" },
      // Banners scroll away with the conversations (pinned above, they left small phones a sliver of list).
      faceIdOffer(),
      speedSuggestBanner(),
      // 1.10.20 calmer: shown until you enable it or tap ×; after that it lives in Activity (and a quiet dot on the
      // phone's bell), instead of sitting on top of the inbox for good.
      !alertsOn() && !alertsBannerDismissed()
        ? h("div", { class: "notice slim alert-banner" },
            h("button", { class: "alert-banner-main", onclick: () => void enableAlerts() },
              icon("bell", "sm"), h("span", { class: "grow" }, "Turn on notifications for calls and texts"), h("span", { class: "text-btn" }, "Enable")),
            h("button", { class: "icon-btn alert-banner-x", "aria-label": "Not now", title: "Not now (you can turn them on in Activity)", onclick: () => (dismissAlertsBanner(), render()) }, icon("close", "sm")))
        : null,
      contactsEl,
      q.length > 1
        ? h("button", { class: "search-all", onclick: () => go({ name: "search", q: state.search.trim() }) },
            icon("search", "sm"), h("span", { class: "ellipsis" }, `Search texts, notes, calls and contacts for “${state.search.trim()}”`))
        : null,
      ...(items.length
        ? [...inboxRowsWithGroups(items, 0, listShown, current, me), items.length > listShown ? listMoreSentinel() : null].filter((x): x is HTMLElement => !!x)
        : contactsEl ? []
        : [inboxEmpty(q)])));
}

/** What the inbox says when the list is empty, per filter. */
function inboxEmpty(q: string) {
  const all = { label: "See all conversations", run: () => ((state.filter = "all"), (state.showDone = false), render()) };
  if (q) return emptyState({ icon: "search", tone: "slate", title: `No matches for “${q}”`, text: "Try another name or number, or search all messages above." });
  if (state.filter === "unread") return emptyState({ icon: "check", tone: "green", title: "You're all caught up", text: "No unread texts or calls. New ones will show up here.", action: all });
  if (state.filter === "unresponded") return emptyState({ icon: "check", tone: "green", title: "Nobody's waiting on a reply", text: "Every customer has an answer. Nice work.", action: all });
  if (state.filter === "mine") return emptyState({ icon: "user", tone: "teal", title: "Nothing assigned to you", text: "Conversations a teammate gives you will show up here.", action: all });
  if (state.filter === "missed") return emptyState({ icon: "phone", tone: "green", title: "No missed calls", text: "Every call was answered.", action: all });
  if (state.filter === "voicemail") return emptyState({ icon: "voicemail", tone: "gold", title: "No voicemails", text: "When a caller leaves a message, you'll find it here.", action: all });
  if (state.showDone) return emptyState({ icon: "check", tone: "slate", title: "Nothing marked done yet", text: "Conversations you mark done move here.", action: { label: "See open conversations", run: () => ((state.showDone = false), render()) } });
  return emptyState({ icon: "inbox", tone: "teal", title: "No conversations yet",
    text: state.line === "personal" ? `Calls and texts with ${personalLineName()} show up here, and only you see them.` : "Calls and texts with your number will show up here.",
    ...(state.user?.canCall ? { action: { label: "Open keypad", run: () => go({ name: "dialer" }) } } : {}) });
}

/**
 * The inbox draws the first rows only, and more as you scroll down. Drawing all 300 conversations took over a
 * second on a phone (measured: 1.0–1.5 s per refresh at phone speed), and every new text or call redrew them all.
 */
const LIST_PAGE = 40;
let listShown = LIST_PAGE;
let listObserver: IntersectionObserver | null = null;
function listMoreSentinel() {
  const el = h("div", { class: "list-more", "aria-hidden": "true" });
  listObserver?.disconnect();
  if ("IntersectionObserver" in window) {
    listObserver = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      listObserver?.disconnect();
      const from = listShown;
      listShown += LIST_PAGE * 2;
      appendInboxRows(from);
    }, { rootMargin: "600px 0px" });
    requestAnimationFrame(() => el.isConnected && listObserver?.observe(el));
  } else listShown = 1e9;
  return el;
}

/** Grey placeholder bubbles while a conversation loads (feels instant; no spinner). */
function skeletonBubbles() {
  return h("div", { class: "skeleton", "aria-label": "Loading", role: "status" },
    ...["in w62", "out w55", "in w44", "out w70 tall", "in w50"].map((c) => h("div", { class: `sk-bubble ${c}` })));
}

let contactSearchTimer: number | undefined;
/** Looks up saved contacts for the inbox search box (by name, number, company, email or tag). */
/**
 * 4.10.29: typing in the inbox search redrew the list after every letter (measured: 70–90 ms of phone time per key,
 * so keys lagged). The matches now update once you pause typing for a moment (90 ms); each letter still shows at once.
 */
let searchListTimer = 0;
function searchListSoon() {
  clearTimeout(searchListTimer);
  searchListTimer = window.setTimeout(renderListSoon, 90);
}

function searchContactsSoon() {
  clearTimeout(contactSearchTimer);
  const q = state.search.trim();
  if (!q) {
    state.searchContacts = null;
    return;
  }
  contactSearchTimer = window.setTimeout(() => {
    void api.contacts(q).then((list) => {
      if (state.search.trim() !== q) return; // typed on since
      state.searchContacts = { q, list };
      // 4.10.29: they show inside the inbox list only, so only the list is redrawn (typing in the search redrew the
      // whole app after every pause: measured as half of each keystroke's work at phone speed).
      renderListSoon();
    }, () => {});
  }, 150);
}

/** Call and Text buttons on a contact in search results (contacts only, not texts or voicemails). */
function contactActions(number: string, name: string | null) {
  const label = name || phone(number);
  const onCall = !!voice.call;
  return h("span", { class: "hit-acts" },
    state.user?.canCall && number !== "anonymous" && !isGroup(number)
      ? h("button", { class: "hit-act call", "aria-label": `Call ${label}`, title: onCall ? "You're on a call" : `Call ${label}`, disabled: onCall,
          onclick: (e: Event) => (e.stopPropagation(), void dial(number)) }, icon("phone", "sm"))
      : null,
    canText(number)
      ? h("button", { class: "hit-act", "aria-label": `Text ${label}`, title: `Text ${label}`, onclick: (e: Event) => {
          e.stopPropagation();
          state.composeMode = "text";
          state.reply = null;
          go({ name: "thread", number });
          requestAnimationFrame(() => document.getElementById("draft")?.focus());
        } }, icon("message", "sm"))
      : null);
}

/** "Contacts" results above the matching conversations: saved contacts that aren't already listed. */
function contactHits(q: string, shown: Conversation[]) {
  const found = state.searchContacts;
  if (!q || !found || found.q.toLowerCase() !== q) return null;
  const listed = new Set(shown.map((c) => c.external_number));
  const extra = found.list.filter((k) => !listed.has(k.number)).slice(0, 8);
  if (!extra.length) return null;
  return h("div", { class: "search-contacts" },
    h("div", { class: "list-section" }, "Contacts"),
    ...extra.map((k) => h("div", { class: "conv contact-hit" },
      h("button", { class: "hit-main", onclick: () => go({ name: "thread", number: k.number }) },
        h("span", { class: "av-wrap" }, avatar(k.number, k.name)),
        h("span", { class: "body" },
          h("span", { class: "top" }, h("span", { class: "name" }, k.name)),
          h("span", { class: "last" }, h("span", { class: "ellipsis" }, [phone(k.number), k.company, k.email].filter(Boolean).join(" · "))))),
      contactActions(k.number, k.name))),
    shown.length ? h("div", { class: "list-section" }, "Conversations") : null);
}

// ---- swipe a conversation: a short swipe shows Assign / Pin / Done, a full swipe marks it done ----

/** The conversation whose actions are showing (kept across screen refreshes). */
let swipeOpen: string | null = null;
const SWIPE_ACTIONS_W = 216;

function closeSwipe() {
  if (!swipeOpen) return;
  swipeOpen = null;
  for (const el of document.querySelectorAll<HTMLElement>(".swipe-row.open")) {
    el.classList.remove("open");
    (el.querySelector(".swipe-front") as HTMLElement | null)?.style.removeProperty("transform");
  }
}

function swipeRow(c: Conversation, front: HTMLElement): HTMLElement {
  const number = c.external_number;
  // Calls inside the team: no assign / pin / done (it's not a customer conversation).
  if (isInternalKey(number)) {
    // Admins can delete these too (28.10.18), the same X as a customer conversation; not during the call.
    const live = c.status === "active" || c.status === "ringing";
    const del = isAdmin() && !live
      ? h("button", { class: "conv-del", type: "button", "aria-label": `Delete calls with ${c.contact_name ?? "them"}`, title: "Delete conversation",
          onclick: (e: MouseEvent) => {
            e.stopPropagation();
            void deleteThread(number, c.contact_name ?? null);
          } }, icon("close", "sm"))
      : null;
    front.classList.add("swipe-front");
    return h("div", { class: "swipe-row", "data-number": number }, front, del);
  }
  const done = c.conv_status === "done";
  const act = (cls: string, iconName: string, label: string, fn: () => void) =>
    h("button", { class: `swipe-act ${cls}`, "aria-label": label, onclick: (e: MouseEvent) => {
      e.stopPropagation();
      closeSwipe();
      fn();
    } }, icon(iconName), h("span", {}, label));
  const actions = h("div", { class: "swipe-actions", "aria-hidden": String(swipeOpen !== number) },
    // 7.10.30: not on the personal line (nobody to assign it to there; the thread menu already hides it).
    state.line === "personal" ? null : act("assign", "assign", "Assign", () => ((state.assignFor = number), render())),
    act("pin", "pin", c.pinned ? "Unpin" : "Pin", () => void togglePin(number, !!c.pinned)),
    act("done", "checkCircle", done ? "Reopen" : "Done", () => void setConversation(number, { status: done ? "open" : "done" })));
  const fullLabel = h("div", { class: "swipe-full" }, icon("checkCircle"), h("span", {}, done ? "Reopen" : "Done"));
  front.classList.add("swipe-front");
  // Computers: a Call button appears on the right when you point at a conversation.
  const active = state.live.find((x) => x.external_number === number && x.status === "active");
  // On a call right now: an ear to listen in (admins, on someone else's call) instead of the Call button.
  const listenBtn = null; // the ear now sits next to the name on the row itself (convRow)
  const callBtn = !active && state.user?.canCall && /^\+\d{8,15}$/.test(number)
    ? h("button", { class: "conv-call hit-act call", type: "button", "aria-label": `Call ${nameFor(number, c.contact_name)}`, title: `Call ${nameFor(number, c.contact_name)}`,
        onclick: (e: MouseEvent) => {
          e.stopPropagation();
          void dial(number);
        } }, icon("phone", "sm"))
    : null;
  // Admins on computers: an X appears in the bottom-right corner when you point at a conversation (27.10.18), to
  // delete it (asks first). Not on a live call.
  const delBtn = isAdmin() && !active
    ? h("button", { class: "conv-del", type: "button", "aria-label": `Delete conversation with ${nameFor(number, c.contact_name)}`, title: "Delete conversation",
        onclick: (e: MouseEvent) => {
          e.stopPropagation();
          void deleteThread(number, c.contact_name ?? null);
        } }, icon("close", "sm"))
    : null;
  const row = h("div", { class: `swipe-row${swipeOpen === number ? " open" : ""}${active ? " on-call" : ""}`, "data-number": number, "data-done": done ? "1" : "" }, fullLabel, actions, front, callBtn, listenBtn, delBtn);
  // The screen can refresh mid-swipe (new texts, live updates): pick up where the finger is.
  const live = drag?.number === number && drag.moved ? drag.dx : swipeOpen === number ? -SWIPE_ACTIONS_W : 0;
  if (live) {
    front.style.transform = `translateX(${live}px)`;
    if (drag?.number === number) row.classList.add("swiping");
  }
  front.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    if (swipeOpen && swipeOpen !== number) closeSwipe();
    drag = { number, startX: e.clientX, startY: e.clientY, base: swipeOpen === number ? -SWIPE_ACTIONS_W : 0, decided: false, horizontal: false, moved: false, dx: 0 };
  });
  // A swipe isn't a tap: don't open the conversation; tapping a row with its buttons showing just tucks them away.
  front.addEventListener("click", (e) => {
    if (Date.now() < suppressClickUntil || swipeOpen === number) {
      e.stopImmediatePropagation();
      e.preventDefault();
      if (swipeOpen === number && Date.now() >= suppressClickUntil) finishSwipe(number, 0);
    }
  }, true);
  // Mac trackpad: two-finger swipe sideways does the same.
  row.addEventListener("wheel", (e) => {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    e.preventDefault();
    if (!wheel || wheel.number !== number) wheel = { number, x: swipeOpen === number ? -SWIPE_ACTIONS_W : 0, timer: undefined };
    const w = wheel;
    w.x = Math.min(0, Math.max(-rowWidth(number), w.x - e.deltaX));
    paintSwipe(number, w.x, false);
    clearTimeout(w.timer);
    w.timer = window.setTimeout(() => {
      wheel = null;
      finishSwipe(number, w.x);
    }, 140);
  }, { passive: false });
  return row;
}

let drag: { number: string; startX: number; startY: number; base: number; decided: boolean; horizontal: boolean; moved: boolean; dx: number } | null = null;
let wheel: { number: string; x: number; timer: number | undefined } | null = null;
let suppressClickUntil = 0;
const rowEl = (number: string) => document.querySelector<HTMLElement>(`.swipe-row[data-number="${CSS.escape(number)}"]`);
const rowWidth = (number: string) => rowEl(number)?.getBoundingClientRect().width ?? 360;
/** How far a swipe must go to count as "all the way" (well past the buttons). */
const fullAt = (number: string) => Math.max(rowWidth(number) * 0.6, SWIPE_ACTIONS_W + 60);

function paintSwipe(number: string, x: number, animate: boolean) {
  const row = rowEl(number);
  const front = row?.querySelector<HTMLElement>(".swipe-front");
  if (!row || !front) return;
  front.style.transition = animate ? "transform 220ms cubic-bezier(0.2, 0.9, 0.3, 1)" : "none";
  front.style.transform = x ? `translateX(${x}px)` : "";
  row.classList.toggle("full", -x > fullAt(number));
  row.classList.toggle("swiping", x < 0);
}

function finishSwipe(number: string, x: number) {
  const row = rowEl(number);
  const done = row?.dataset.done === "1";
  const w = rowWidth(number);
  if (-x > fullAt(number)) {
    // Full swipe: slide it away and mark it done (or reopen it when looking at Done).
    paintSwipe(number, -w, true);
    navigator.vibrate?.(12);
    if (swipeOpen === number) swipeOpen = null;
    setTimeout(() => void setConversation(number, { status: done ? "open" : "done" }), 180);
  } else if (-x > SWIPE_ACTIONS_W * 0.4) {
    if (swipeOpen && swipeOpen !== number) closeSwipe();
    swipeOpen = number;
    row?.classList.add("open");
    paintSwipe(number, -SWIPE_ACTIONS_W, true);
  } else {
    if (swipeOpen === number) swipeOpen = null;
    row?.classList.remove("open");
    paintSwipe(number, 0, true);
  }
}

document.addEventListener("pointermove", (e) => {
  const d = drag;
  if (!d) return;
  const mx = e.clientX - d.startX, my = e.clientY - d.startY;
  if (!d.decided) {
    if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
    d.decided = true;
    d.horizontal = Math.abs(mx) > Math.abs(my) * 1.2;
    if (!d.horizontal) return void (drag = null); // scrolling the list
  }
  d.moved = true;
  d.dx = Math.max(-rowWidth(d.number), Math.min(0, d.base + mx)); // only to the left
  paintSwipe(d.number, d.dx, false);
}, { passive: true });
const endDrag = () => {
  const d = drag;
  drag = null;
  if (!d?.moved) return;
  suppressClickUntil = Date.now() + 400;
  finishSwipe(d.number, d.dx);
};
document.addEventListener("pointerup", endDrag);
document.addEventListener("pointercancel", endDrag);

/** "Assign to…" picker on frosted glass (from a swipe). */
function assignPicker(number: string) {
  const conv = state.conversations.find((c) => c.external_number === number);
  const close = () => ((state.assignFor = null), render());
  return enterOnce(h("div", { class: "glass-overlay dialog", role: "dialog", "aria-modal": "true", "aria-label": "Assign conversation", onclick: (e: MouseEvent) => e.target === e.currentTarget && close() },
    h("div", { class: "glass-card glass-dialog assign-card" },
      h("div", { class: "glass-dialog-title" }, "Assign to"),
      h("div", { class: "glass-dialog-msg" }, nameFor(number, conv?.contact_name)),
      h("div", { class: "assign-list" },
        ...state.team.map((u) => h("button", { class: `assign-opt${conv?.assigned_to === u.id ? " on" : ""}`, onclick: () => {
          state.assignFor = null;
          void setConversation(number, { assignedTo: u.id });
        } }, avatar(u.email, u.name, "sm"), h("span", { class: "grow" }, u.id === state.user?.id ? `${u.name} (you)` : u.name), conv?.assigned_to === u.id ? icon("check", "sm") : null)),
        conv?.assigned_to ? h("button", { class: "assign-opt", onclick: () => {
          state.assignFor = null;
          void setConversation(number, { assignedTo: null });
        } }, h("span", { class: "assign-x" }, icon("close", "sm")), h("span", { class: "grow" }, "Unassign")) : null),
      h("div", { class: "glass-dialog-actions" }, h("button", { class: "btn quiet", onclick: close }, "Cancel")))), `assign:${number}`);
}

/** Where an incoming call is right now, step by step, for the inbox: arriving → greeting → menu → ringing → voicemail. */
function incomingStatus(c: LiveCall, me: number): { text: string; canAnswer: boolean } {
  if (c.status === "voicemail") return { text: "Leaving a voicemail", canAnswer: false };
  switch (c.stage) {
    case "answering": return { text: "Incoming call · connecting", canAnswer: false };
    case "greeting": return { text: "Listening to the greeting", canAnswer: false };
    case "menu": return { text: "Choosing from the phone menu", canAnswer: false };
  }
  if (c.ring_user && c.ring_user === me) return { text: "Ringing you", canAnswer: true };
  if (c.ring_user) return { text: `Ringing ${firstName(state.team.find((u) => u.id === c.ring_user)?.name) || "a teammate"}`, canAnswer: true };
  return { text: "Ringing everyone", canAnswer: true };
}

/** Teammates who have this conversation open: their letter, by the time. */
function viewersOf(number: string, me: number): HTMLElement | null {
  const who = state.presence.filter((p) => p.viewing === number && p.id !== me && p.status !== "offline")
    .map((p) => state.team.find((u) => u.id === p.id)).filter((u): u is User => !!u);
  if (!who.length) return null;
  const names = who.map((u) => firstName(u.name)).join(", ");
  return h("span", { class: "viewers", title: `${names} ${who.length === 1 ? "is" : "are"} looking at this` },
    ...who.slice(0, 2).map((u) => h("span", { class: "viewer" }, avatar(u.email, u.name, "xs"))));
}

/** The caller in the glowing incoming-call card at the top of the inbox (their row then shows its usual line). */
let inNotice: string | null = null;

/** Admins can listen in on someone else's live call (not Selena's, not while on a call themselves). */
function canListenTo(active: LiveCall | undefined): active is LiveCall {
  return !!active && !active.ai && isAdmin() && active.answered_by !== state.user?.id && !!state.user?.canCall && !(voice.call && !isEnded(voice.call));
}

function convRow(c: Conversation, current: string | null, me: number) {
  const line = lastLine(c);
  const live = inNotice === c.external_number ? undefined : state.live.find((x) => x.direction === "inbound" && x.external_number === c.external_number && (x.status === "ringing" || x.status === "voicemail"));
  // A teammate (or you) is on a call with them right now: said on the row itself (no separate strip above the inbox).
  const active = state.live.find((x) => x.external_number === c.external_number && x.status === "active");
  // Who's on it: you, a teammate, or Selena (her phone name: Steven when that's who answers).
  const mine = !!active && !active.ai && (active.answered_by === me || (active.direction === "outbound" && active.initiated_by === me && !active.answered_by));
  // Who: first names, you included (never "you're on this call").
  const byId = (id?: number | null) => (id ? firstName(state.team.find((u) => u.id === id)?.name) : "");
  const who = !active ? "" : active.ai ? aiName()
    : (active.direction === "outbound" ? byId(active.initiated_by) || firstName(active.answered_by_name ?? "") : firstName(active.answered_by_name ?? "") || byId(active.answered_by))
      || (mine ? byId(me) : "") || "A teammate";
  // "Mike answered" (they called in) / "Mike speaking with Greg" (we called out).
  const them = c.contact_name?.trim() || phone(c.external_number); // full name: "Grand Oasis Tulum", not "Grand"
  const callLine = !active ? "" : `${who} ${active.direction === "outbound" ? `speaking with ${them}` : "answered"}${active.on_hold ? " · on hold" : ""}${active.monitors?.length ? ` · ${active.monitors.length} listening` : ""}`;
  const since = active ? serverTime(active.answered_at ?? null) : null;
  const assignee = c.assigned_to ? state.team.find((u) => u.id === c.assigned_to) : null;
  const showPreview = c.last_kind === "call" && c.preview;
  // A text or a note: its words get two lines (the card stays the same size, and full).
  const twoLine = !showPreview && !active && !live && (c.last_kind === "sms" || c.last_kind === "comment") && !isInternalKey(c.external_number)
    && !(state.line !== "personal" && typingNow(`t:${c.external_number}`).length);
  return h("button", { class: `${c.unread ? "conv is-unread" : "conv"}${active ? ` on-call${active.ai ? " on-call-ai" : ""}` : ""}`, "aria-current": String(current === c.external_number), onclick: () => go({ name: "thread", number: c.external_number }),
    // A finger: a short wait, cancelled if it turns into a scroll (each scroll used to fetch a whole conversation).
    onpointerdown: (e: PointerEvent) => prefetchThread(c.external_number, e.pointerType === "touch" ? 70 : 0), onpointerenter: (e: PointerEvent) => e.pointerType === "mouse" && prefetchThread(c.external_number, 120),
    onpointerleave: () => clearTimeout(prefetchTimer) },
    h("span", { class: "av-wrap" }, c.external_number.startsWith("ai:") ? h("span", { class: "ai-av" }, icon("sparkle")) : c.external_number.startsWith("tm:") ? avatar(state.team.find((u) => u.name === c.contact_name)?.email ?? c.external_number, c.contact_name) : avatar(c.external_number, c.contact_name),
      assignee ? h("span", { class: "av-badge", title: `Assigned to ${assignee.id === me ? "you" : assignee.name}` }, avatar(assignee.email, assignee.name, "xs")) : null),
    h("span", { class: "body" },
      h("span", { class: "top" },
        c.pinned ? h("span", { class: "pin", title: "Pinned" }, icon("pin", "sm")) : null,
        h("span", { class: "name" }, nameFor(c.external_number, c.contact_name)),
        // On a call: the ear to listen in sits right after the name (19.10.18; it was a big circle at the far right).
        canListenTo(active) ? h("span", { class: "conv-listen-inline", role: "button", tabindex: "0", "data-listen": active.id,
          "aria-label": `Listen in on ${nameFor(c.external_number, c.contact_name)}'s call`, title: "Listen in (you can switch to whisper or join)",
          onclick: (e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            if (e.detail === 0) void listenIn(active.id); // keyboard; taps and clicks are handled on the press
          },
          onkeydown: (e: KeyboardEvent) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            e.stopPropagation();
            void listenIn(active.id);
          } }, icon("ear", "sm")) : null,
        state.line === "personal" ? null : viewersOf(c.external_number, me), // teammates only ever see Joy Travel's thread
        h("span", { class: "time", title: fullTime(c.activity_at) }, listTime(c.activity_at))),
      active
        ? h("span", { class: `last oncall-line${mine ? " mine" : ""}` }, h("span", { class: "oncall-dot", "aria-hidden": "true" }), h("span", { class: "oncall-ico" }, icon(active.direction === "outbound" ? "arrowOut" : "arrowIn", "sm")),
            h("span", { class: "ellipsis" }, callLine),
            since ? h("span", { class: "live-timer", "data-since": String(since.getTime()) }, liveElapsed(since.getTime())) : null)
        : live
        ? h("span", { class: "last live-line" }, h("span", { class: "live-blip", "aria-hidden": "true" }), h("span", { class: "ellipsis" }, incomingStatus(live, me).text))
        : state.line !== "personal" && typingNow(`t:${c.external_number}`).length
        ? h("span", { class: "last typing-line" }, typingDots(), h("span", { class: "ellipsis" }, typingText(`t:${c.external_number}`)))
        : (() => {
          // 4.11.2: the kind of last message as a small coloured badge, and a call's length as a pill.
          const dur = /^(.*) · (\d{1,2}:\d{2}(?::\d{2})?)$/.exec(line.text);
          return h("span", { class: `last ${line.tone}${twoLine ? " two-line" : ""}` },
            line.iconName ? h("span", { class: `kind-ico k-${line.iconName}${line.tone ? ` t-${line.tone}` : ""}`, "aria-hidden": "true" }, icon(line.iconName, "sm")) : null,
            h("span", { class: "ellipsis" }, tidyPreview(dur ? dur[1] : line.text)),
            dur ? h("span", { class: "dur-pill" }, dur[2]) : null,
        c.comment_count && c.last_kind !== "comment" ? h("span", { class: "cmt-count", title: `${c.comment_count} new internal comment${c.comment_count > 1 ? "s" : ""}` }, icon("note", "sm"), String(c.comment_count)) : null,
        c.unread ? h("span", { class: "unread-dot", "aria-label": "Unread" }) : null);
        })(),
      // During a call the last call's summary would be misleading: hidden until it's over.
      // Every card the same height and none half empty (19.10.18): a text or note uses two lines; a call without a
      // summary shows who it is underneath (their number, or that they're not in the contacts).
      twoLine ? null
        : showPreview && !active ? h("span", { class: "preview" }, tidyPreview(previewText(c.preview!)))
        : h("span", { class: "preview detail" }, detailLine(c))));
}
/**
 * 4.11.2 "breathe more life into this UI": the inbox is split under small headings (Pinned, Today, Yesterday, This
 * week, Earlier), so where you are in the day reads at a glance.
 */
function inboxGroup(c: Conversation, now = new Date()): string {
  // Calls going on right now are listed first: they're their own group.
  if (state.live.some((x) => x.external_number === c.external_number && (x.status === "active" || ((x.status === "ringing" || x.status === "voicemail") && x.direction === "inbound")))) return "On calls now";
  if (c.pinned) return "Pinned";
  const d = serverTime(c.activity_at);
  if (!d) return "Earlier";
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86_400_000);
  return days <= 0 ? "Today" : days === 1 ? "Yesterday" : days < 7 ? "This week" : "Earlier";
}
/** The rows from `from` to `to`, each new group starting with its heading. */
function inboxRowsWithGroups(items: Conversation[], from: number, to: number, current: string | null, me: number): HTMLElement[] {
  const out: HTMLElement[] = [];
  const now = new Date();
  let prev = from > 0 ? inboxGroup(items[from - 1], now) : null;
  for (const c of items.slice(from, to)) {
    const g = inboxGroup(c, now);
    // 8.11.2 "take out the Pinned and Today marks: one continuous inbox, pinned on top": no headings any more.
    void g;
    prev = g;
    out.push(swipeRow(c, convRow(c, current, me)));
  }
  return out;
}
/** Long links and raw numbers in a one-line preview, made readable: "🔗 pay.joytravelinc.com", "(646) 491-8880". */
export function tidyPreview(text: string): string {
  return text
    .replace(/https?:\/\/(?:www\.)?([^\s/?#]+)[^\s]*/gi, (_m, host: string) => `🔗 ${host}`)
    .replace(/\+1(\d{3})(\d{3})(\d{4})\b/g, "($1) $2-$3");
}

/** The third line of a card with no summary: the contact's number, or what we know about an unknown caller. */
function detailLine(c: Conversation): string {
  const n = c.external_number;
  if (n.startsWith("ai:")) return "AI assistant";
  if (n.startsWith("tm:")) return "Teammate";
  if (isGroup(n)) return "Group text";
  // A name is shown (saved, or from caller ID): the number underneath. Only a bare number says it's unknown.
  // 9.11.2: a merged contact: their numbers.
  if (c.numbers && c.numbers.length > 1) return `${phone(n)} · +${c.numbers.length - 1} more number${c.numbers.length > 2 ? "s" : ""}`;
  if (c.contact_name?.trim() || (c as { caller_name?: string | null }).caller_name) return phone(n);
  return "Not in your contacts";
}
/** "3:07" since a call was answered (rows on a live call tick along; see the timer below). */
function liveElapsed(since: number): string {
  const s = Math.max(0, Math.floor((Date.now() - since) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
setInterval(() => {
  for (const el of document.querySelectorAll<HTMLElement>(".live-timer[data-since]")) setTextOnly(el, liveElapsed(Number(el.dataset.since)));
}, 1000);

// ---------------------------------------------------------------- sidebar (desktop) and tab bar (phone)

const PRESENCE_LABEL: Record<Presence["status"], string> = { online: "Online", idle: "Away", dnd: "Do not disturb", offline: "Offline" };
/** The other teammates under you in the sidebar, each with a colored dot: green online, yellow away (no use for
 * over a minute), red Do Not Disturb, grey offline / signed out. Admins also see when each was last active. */
/** Phones and tablets have no hover: tapping a teammate shows their "since" / last seen (admins). */
let presenceOpen: number | null = null;
/** The call a teammate is on right now (answered, or one they placed), if any. */
function teammateCall(userId: number) {
  return state.live.find((c) => c.status === "active" && !c.ai && (c.answered_by === userId || (c.direction === "outbound" && c.initiated_by === userId))) ?? null;
}

/**
 * The Joy Phone logo's phone (a solid handset with two signal arcs), filled with the current color. The arcs
 * pulse while a call is live.
 */
function joyPhoneGlyph() {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("class", "jp-glyph");
  for (const [d, cls] of [
    ["M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.59l2.2-2.21c.28-.26.36-.65.25-1L8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z", ""],
    ["M15 12h2c0-2.76-2.24-5-5-5v2c1.66 0 3 1.34 3 3z", "a1"],
    ["M19 12h2c0-4.97-4.03-9-9-9v2c3.87 0 7 3.13 7 7z", "a2"],
  ]) {
    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", d);
    path.setAttribute("fill", "currentColor");
    if (cls) path.setAttribute("class", cls);
    svg.append(path);
  }
  return svg;
}

/** "2:14" since a server time; the on-call timers tick by themselves (see below), without redrawing the screen. */
function sinceClock(at: string | null | undefined) {
  const t = serverTime(at ?? null);
  if (!t) return "";
  const s = Math.max(0, Math.floor((Date.now() - t.getTime()) / 1000));
  return s >= 3600 ? `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
setInterval(() => {
  for (const el of document.querySelectorAll<HTMLElement>(".jp-time[data-since]")) setTextOnly(el, sinceClock(el.dataset.since));
}, 1000);

function teammatesOnline(variant: "rail" | "page" = "rail") {
  const others = state.team.filter((u) => u.id !== state.user?.id);
  if (!others.length) return null;
  const rank: Record<Presence["status"], number> = { online: 0, dnd: 1, idle: 2, offline: 3 };
  const rows = others.map((u) => ({ u, p: state.presence.find((x) => x.id === u.id) ?? { id: u.id, status: "offline" as const } }))
    .sort((a, b) => rank[a.p.status] - rank[b.p.status] || a.u.name.localeCompare(b.u.name));
  const admin = isAdmin();
  return h("div", { class: variant === "rail" ? "rail-team" : "group team-presence", role: "list", "aria-label": "Teammates" },
    ...rows.map(({ u, p }) => {
      const label = PRESENCE_LABEL[p.status];
      // Hover (admins): "Active since 10:15 PM", "Away since Fri 4:02 PM", "Offline · last seen Fri 4:02 PM".
      const sinceText = !admin ? ""
        : p.status === "offline" ? (p.lastActiveAt ? `Last seen ${listTime(p.lastActiveAt)}` : "Hasn't signed in yet")
        : p.since ? `${p.status === "online" ? "Active" : label} since ${listTime(p.since)}` : "";
      const open = presenceOpen === u.id && !!sinceText;
      return h("div", { class: `rail-mate ${variant === "page" ? "item " : ""}${teammateCall(u.id) ? "online" : p.status}${open ? " open" : ""}`, role: "listitem", tabindex: sinceText ? 0 : -1,
        "aria-label": `${u.name}: ${label}${sinceText ? `, ${sinceText.toLowerCase()}` : ""}`, "aria-expanded": sinceText ? String(open) : undefined,
        // Clicking the name never calls (only the phone icon does). Admins: it shows when they were last active.
        onclick: sinceText ? () => ((presenceOpen = open ? null : u.id), render()) : undefined,
        onkeydown: sinceText ? (e: KeyboardEvent) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), (presenceOpen = open ? null : u.id), render()) : undefined },
        // On a call: their circle becomes the Joy Phone logo (gradient, white phone, pulsing waves) with the timer.
        ((call) => h("span", { class: call ? "av-wrap on-call" : "av-wrap", title: call ? `${firstName(u.name)} is on a call` : undefined },
          avatar(u.email, u.name, "sm"),
          call ? h("span", { class: "jp-full", "aria-hidden": "true" }, joyPhoneGlyph()) : h("span", { class: `presence ${p.status}`, "aria-hidden": "true" })))(teammateCall(u.id)),
        h("span", { class: "rail-label two" }, h("span", {}, u.name),
          h("small", { class: sinceText ? "has-since" : "" },
            // On screen just the status; the times (since, last seen) only show on hover, for admins.
            ((call) => call
              ? h("span", { class: "now-text jp-sub" }, "On a call · ", h("span", { class: "jp-time", "data-since": call.answered_at ?? "" }, sinceClock(call.answered_at)))
              : h("span", { class: "now-text" }, p.status === "online" ? "Active" : label))(teammateCall(u.id)),
            sinceText ? h("span", { class: "since-text" }, sinceText) : null)),
        // Call them inside Joy Phone: the phone icon shows on hover (always on phones, which can't hover).
        // Not while they're on a call: their row is just the Joy Phone logo and "On a call · time".
        state.user?.canCall && u.canCall && !teammateCall(u.id)
          ? h("button", { class: "mate-call", type: "button", "aria-label": `Call ${u.name}`, title: `Call ${firstName(u.name)} in Joy Phone`,
              onclick: (e: MouseEvent) => (e.stopPropagation(), void callTeammate(u)) }, icon("phone", "sm"))
          : null);
    }));
}

/**
 * Opens the keyboard for a box that's about to appear (6.10.20): one tap on the search icon and you can type, no
 * second tap on the box. Phones only show the keyboard for a focus made during the tap, and the box isn't on
 * screen yet, so a hidden stand-in box takes the focus now and passes it (keyboard and all) to the real one.
 */
function keyboardFor(id: string) {
  const ready = document.getElementById(id) as HTMLInputElement | null;
  if (ready?.offsetParent) return void ready.focus();
  const proxy = h("input", { type: "text", class: "kb-proxy", "aria-hidden": "true", tabindex: -1, autocomplete: "off" });
  document.body.append(proxy);
  proxy.focus({ preventScroll: true });
  let tries = 0;
  const pass = () => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el?.offsetParent) {
      el.focus({ preventScroll: true });
      const end = el.value.length;
      try {
        el.setSelectionRange(end, end);
      } catch {
        /* search boxes in some browsers */
      }
      proxy.remove();
    } else if (++tries < 30) requestAnimationFrame(pass);
    else proxy.remove();
  };
  requestAnimationFrame(pass);
}

/** Re-draws the sidebar when your next task comes due, so its number shows up right at the due time. */
let taskDueTimer: number | undefined;
let taskDueAt = 0;
function taskDueWake(upcoming: number[]) {
  const next = upcoming.length ? Math.min(...upcoming) : 0;
  if (next === taskDueAt) return;
  clearTimeout(taskDueTimer);
  taskDueAt = next;
  if (!next) return;
  // Timers longer than ~24 days overflow; a day is plenty (the next render sets it again).
  taskDueTimer = window.setTimeout(() => ((taskDueAt = 0), render()), Math.min(next - Date.now() + 500, 86_400_000));
}

let railSearchTimer: number | undefined;
/** Sidebar soft search: matching contacts, as you type. */
function railSoftSearch() {
  clearTimeout(railSearchTimer);
  const q = state.railQ.trim();
  if (!q) return void (state.railHits = null);
  railSearchTimer = window.setTimeout(() => {
    void api.contacts(q).then((list) => {
      if (state.railQ.trim() !== q) return; // typed on since
      state.railHits = { q, list: list.slice(0, 6) };
      render();
    }, () => {});
  }, 120);
}
/**
 * 1.10.23 "type a number in search and no number comes up: Enter calls it". Only something that is just a phone
 * number ("310 555 0123", "(310) 555-0123", "+44 20…"), never words with digits in them; and only when no other
 * contact came up (one that is this very number is fine: it's the same call).
 */
function railDialable(q: string, hits: { number: string }[]): string | null {
  if (!/^[\d\s()+.\-]+$/.test(q)) return null;
  const num = toE164(q);
  if (!num || !state.user?.canCall) return null;
  return hits.every((k) => k.number === num) ? num : null;
}
function railSoftResults() {
  const q = state.railQ.trim();
  if (!q) return null;
  // Already showing the full search for this: no dropdown on top of it.
  if (state.route.name === "search" && state.searchQ.trim() === q) return null;
  const hits = state.railHits && state.railHits.q === q ? state.railHits.list : null;
  return h("div", { class: "rail-hits" },
    ...(hits ?? []).map((k, i) => h("div", { class: i === state.railSel ? "rail-hit sel" : "rail-hit", "aria-selected": String(i === state.railSel) },
      h("button", { class: "rail-hit-main", title: `Open ${k.name || phone(k.number)}`, onclick: () => ((state.railQ = ""), (state.railHits = null), go({ name: "thread", number: k.number })) },
        h("span", { class: "rail-hit-name" }, k.name || phone(k.number)),
        h("span", { class: "rail-hit-sub" }, phone(k.number))),
      contactActions(k.number, k.name || null))),
    hits && !hits.length && !railDialable(q, hits) ? h("div", { class: "rail-hit-empty" }, "No contacts") : null,
    // A phone number nobody has: Enter calls it (said, with a button too).
    state.railSel < 0 && !hits?.length && railDialable(q, hits ?? []) ? h("button", { class: "rail-hit-more rail-hit-call", onclick: () => {
      const num = railDialable(q, hits ?? [])!;
      state.railQ = "";
      state.railHits = null;
      typedValues.delete("rail-search");
      render();
      void dial(num);
    }, title: `Call ${phone(toE164(q)!)}`, "aria-label": `Call ${phone(toE164(q)!)}` }, icon("phone", "sm"), h("span", {}, "Call ", h("b", {}, phone(toE164(q)!))), h("kbd", {}, "Enter")) : null,
    h("button", { class: "rail-hit-more", onclick: () => ((state.railHits = null), go({ name: "search", q })) },
      icon("search", "sm"), h("span", {}, "Search everything"), state.railSel < 0 && !railDialable(q, hits ?? []) ? h("kbd", {}, "Enter") : null),
    // With a contact highlighted, Enter calls them: say so (also when the typed number is that contact's).
    state.railSel >= 0 && hits?.[state.railSel] ? h("div", { class: "rail-hit-empty" }, `Enter calls ${hits[state.railSel].name || phone(hits[state.railSel].number)}`)
    : state.railSel < 0 && hits?.length && railDialable(q, hits) ? h("div", { class: "rail-hit-empty" }, `Enter calls ${hits[0].name || phone(hits[0].number)}`) : null);
}
/** Computer layout (sidebar + list side by side)? Same breakpoint as the stylesheet. */
const isWide = () => matchMedia("(min-width: 768px)").matches;
/** The inbox search box on computers: shown after tapping the search icon, and while there's something typed. */
const listSearchOpen = () => isWide() && (state.listSearchOpen || !!state.search);
function listSearchBox(cls: string) {
  const close = () => {
    state.search = "";
    state.listSearchOpen = false;
    state.phoneSearchOpen = false;
    render();
    searchContactsSoon();
  };
  return h("label", { class: cls === "search" ? "search" : `search ${cls}` }, icon("search", "sm"),
    h("span", { class: "sr-only" }, "Search the inbox"),
    h("input", { id: "search", type: "search", placeholder: "Search the inbox", value: state.search, autocomplete: "off", autofocus: cls !== "search",
      oninput: (e: Event) => ((state.search = (e.target as HTMLInputElement).value), searchListSoon(), searchContactsSoon()),
      onkeydown: (e: KeyboardEvent) => {
        if (e.key === "Enter" && state.search.trim().length > 1) go({ name: "search", q: state.search.trim() });
        if (e.key === "Escape" && isWide()) close();
      } }),
    isWide() ? h("button", { class: "icon-btn sm", type: "button", "aria-label": "Close search", title: "Close", onmousedown: (e: MouseEvent) => e.preventDefault(), onclick: close }, icon("close", "sm"))
    // Phones: an × to clear what you typed and go back to the normal inbox (keyboard closes too).
    : state.search ? h("button", { class: "search-clear", type: "button", "aria-label": "Clear search", onmousedown: (e: MouseEvent) => e.preventDefault(),
        onclick: (e: MouseEvent) => {
          e.preventDefault();
          (document.getElementById("search") as HTMLInputElement | null)?.blur();
          typedValues.delete("search");
          close();
        } }, icon("close", "sm"))
    : null);
}

/**
 * Phones: the title at the top. For an admin with a personal line it's a switch: "Joy Phone" (the office inbox) ⇄
 * "Yossi's Phone" (their personal line), with a dot when the other one has something new.
 */
/** The admin's personal line by name: "Yossi's Phone" (the name set for Selena, or their first name). */
function personalLineName(): string {
  if (!aiAgent.pa && state.personalLine) void loadPersonal();
  return `${aiAgent.pa?.name || firstName(state.user?.name ?? "") || "My"}'s Phone`;
}

function phoneBrandName() {
  if (!state.personalLine) return h("span", { class: "brand-name" }, "Joy Phone");
  if (!aiAgent.pa) void loadPersonal();
  const mine = `${aiAgent.pa?.name || firstName(state.user?.name ?? "") || "My"}'s Phone`;
  const personal = state.line === "personal";
  const other = otherLineUnread();
  return h("button", { class: `brand-name brand-switch${personal ? " personal" : ""}`, type: "button",
    "aria-label": personal ? `${mine}. Switch to Joy Phone` : `Joy Phone. Switch to ${mine}`,
    onclick: () => switchLine(personal ? "team" : "personal") },
    personal ? mine : "Joy Phone",
    h("span", { class: "brand-swap", "aria-hidden": "true" }, icon("chevronDown", "sm")),
    other ? h("span", { class: "brand-other-dot", title: "Something new on your other line" }) : null);
}

function rail() {
  const r = state.route.name;
  const section = sectionOf(state.route);
  const settingsRoutes = SETTINGS_ROUTES;
  const item = (iconName: string, label: string, to: Route, active: boolean, badge = 0) =>
    h("button", { class: "rail-item", "aria-current": String(active), title: label, onclick: () => go(to) },
      icon(iconName), h("span", { class: "rail-label" }, label), badge ? h("span", { class: "rail-badge" }, badge > 99 ? "99+" : String(badge)) : null);
  const inboxUnread = needsYouCount();
  // The number on Tasks: only yours whose due time has come (6.10.20: "tasks due tomorrow should never show until
  // the due time"). Later ones (later today too) and ones with no date don't light it up; it appears on its own
  // the minute one comes due (taskDueWake).
  const now = Date.now();
  const mine = state.tasksLoaded ? state.tasks.filter((t) => !t.done && t.assigneeId === state.user?.id && !!t.dueAt) : [];
  const myTasks = mine.filter((t) => Date.parse(t.dueAt!) <= now).length;
  taskDueWake(mine.map((t) => Date.parse(t.dueAt!)).filter((at) => at > now));
  return h("nav", { class: "rail", "aria-label": "Main" },
    h("div", { class: "rail-brand" }, brandMark(), h("span", { class: "brand-name rail-label" }, "Joy Phone"), h("span", { class: "beta rail-label", title: `Version ${VERSION}` }, "Beta")),
    item("bell", "Activity", { name: "activity" }, r === "activity", state.activityUnread),
    h("div", { class: "rail-title" }, "Workspace"),
    // Search everything (texts, notes, transcripts, contacts). Narrow sidebar: just the icon.
    // Soft search: contacts appear right under the box as you type (with Call and Text). Enter = the full
    // ("hard") search: texts, notes, call transcripts.
    h("div", { class: "rail-search-wrap" },
      h("label", { class: "rail-search", title: "Type to find a contact · Enter searches texts, notes and call transcripts" }, icon("search", "sm"),
        h("span", { class: "sr-only" }, "Search"),
        h("input", { id: "rail-search", type: "search", name: "joy-sidebar-search", placeholder: "Search", autocomplete: "off", "data-1p-ignore": "true", "data-lpignore": "true", "data-form-type": "other", value: state.railQ,
          oninput: (e: Event) => {
            state.railQ = (e.target as HTMLInputElement).value;
            state.railSel = -1;
            railSoftSearch();
            renderSoon();
          },
          onkeydown: (e: KeyboardEvent) => {
            const q = state.railQ.trim();
            const hits = state.railHits && state.railHits.q === q ? state.railHits.list : [];
            // ↓ / ↑ move through the matching contacts; Enter on a highlighted contact calls them.
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              if (!hits.length) return;
              e.preventDefault();
              state.railSel = e.key === "ArrowDown" ? Math.min(hits.length - 1, state.railSel + 1) : Math.max(-1, state.railSel - 1);
              render();
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              const pick = state.railSel >= 0 ? hits[state.railSel] : null;
              if (pick) {
                state.railQ = "";
                state.railHits = null;
                state.railSel = -1;
                typedValues.delete("rail-search");
                void dial(pick.number);
                return;
              }
              // 1.10.23: a phone number typed and no contact has it: Enter calls it.
              const num = railDialable(q, hits);
              if (num) {
                state.railQ = "";
                state.railHits = null;
                state.railSel = -1;
                typedValues.delete("rail-search");
                render();
                void dial(num);
                return;
              }
              // Nothing highlighted: the full search (texts, notes, transcripts) in the main panel.
              if (q.length > 1) {
                state.railHits = null;
                go({ name: "search", q });
              }
            }
            if (e.key === "Escape") ((state.railQ = ""), (state.railHits = null), (state.railSel = -1), typedValues.delete("rail-search"), render());
          } })),
      railSoftResults()),
    h("button", { class: "rail-item rail-search-icon", "aria-current": String(r === "search"), title: "Search", onclick: () => go({ name: "search" }) }, icon("search")),
    item("tasks", "Tasks", { name: "tasks" }, r === "tasks", myTasks),
    item("contacts", "Contacts", { name: "contacts" }, section === "contacts"),
    item("settings", "Settings", { name: "settings" }, settingsRoutes.includes(r)),
    h("div", { class: "rail-title" }, "Inboxes"),
    h("button", { class: "rail-item rail-inbox", "aria-current": String(section === "inbox" && r !== "chat" && state.line === "team"), title: "Joy Travel inbox", onclick: () => switchLine("team") },
      h("span", { class: "inbox-av" }, icon("phone", "sm")),
      h("span", { class: "rail-label two" }, h("span", {}, "Joy Travel"), state.teamNumber ? h("small", {}, phone(state.teamNumber)) : null),
      state.line === "team" && inboxUnread
        ? h("span", { class: "rail-badge clickable", role: "button", tabindex: "0", title: "Mark all as read", "aria-label": `${inboxUnread} unread. Mark all as read`,
            onclick: (e: MouseEvent) => (e.stopPropagation(), void markAllRead()),
            onkeydown: (e: KeyboardEvent) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), e.stopPropagation(), void markAllRead()) }, String(inboxUnread))
        : null), // (no Joy Travel count while you're on your personal line)
    // Admins with Personal mode: their own line, private to them.
    state.personalLine
      ? h("button", { class: "rail-item rail-inbox rail-personal", "aria-current": String(section === "inbox" && r !== "chat" && state.line === "personal"), title: "Your personal line (only you see it)", onclick: () => switchLine("personal") },
          h("span", { class: "inbox-av personal" }, icon("phone", "sm")),
          h("span", { class: "rail-label two" }, h("span", {}, personalLineName()), h("small", {}, state.personalLine.number ? phone(state.personalLine.number) : "Only you")),
          ((n) => n ? h("span", { class: "rail-badge" }, n > 99 ? "99+" : String(n)) : null)(state.line === "personal" ? inboxUnread : 0)) // (no personal count on Joy Travel's line)
      : null,
    // Agents: Selena (the AI assistant), you, then everyone else.
    h("div", { class: "rail-title rail-agents" }, "Agents"),
    aiAgentRow("rail"),
    // Everyone (you first, with your status / Do Not Disturb switch) right under Selena.
    h("button", { class: "rail-item rail-me", title: "Your settings", onclick: () => go({ name: "settings" }) },
      h("span", { class: "av-wrap" }, avatar(state.user!.email, state.user!.name, "sm"),
        h("span", { class: state.user!.dnd ? "presence dnd" : voice.ready ? "presence on" : "presence", role: "button", tabindex: 0,
          title: state.user!.dnd ? "Do Not Disturb is on. Click to turn it off." : "Click to turn on Do Not Disturb",
          onclick: (e: MouseEvent) => (e.stopPropagation(), void toggleDnd(!state.user!.dnd)) })),
      h("span", { class: "rail-label two" }, h("span", {}, state.user!.name),
        state.user!.canCall
          ? h("small", { class: state.user!.dnd ? "status-toggle dnd-text" : voice.ready ? "status-toggle" : "status-toggle connecting", role: "button", tabindex: 0,
              title: state.user!.dnd ? "Click to turn off Do Not Disturb" : "Click to turn on Do Not Disturb",
              onclick: (e: MouseEvent) => (e.stopPropagation(), void toggleDnd(!state.user!.dnd)) },
              // The light next to your picture says connecting/online (8.10.20: no "Connecting" words).
              state.user!.dnd ? "Do not disturb" : "Available")
          : null)),
    state.line === "personal" ? null : teammatesOnline(),
    // 3.10.20: Team chat sits under the Agents, with its own "Chat" heading. Your personal line shows nothing of
    // Joy Travel's: no Team chat, no teammates' calls.
    state.line === "personal" ? null : h("div", { class: "rail-title" }, "Chat"),
    // Same gradient badge as the Joy Travel inbox above and the Team chat header.
    state.line === "personal" ? null : h("button", { class: "rail-item rail-inbox", "aria-current": String(r === "chat"), title: "Team chat", onclick: () => go({ name: "chat" }) },
      h("span", { class: "inbox-av" }, icon("chat", "sm")),
      h("span", { class: "rail-label two" }, h("span", {}, "Team chat"), h("small", {}, CHAT_TAGLINE)),
      state.chatSummary.unread ? h("span", { class: "rail-badge" }, state.chatSummary.unread > 99 ? "99+" : String(state.chatSummary.unread)) : null),
    h("div", { class: "rail-spacer" }),
    // Log out: plain text, right above the version number.
    h("button", { class: "rail-item rail-signout", title: "Log out", onclick: async () => {
      const ok = await glassDialog({ title: "Log out of Joy Phone?", message: "Calls won't ring on this computer until you log in again.", confirm: "Log out" });
      if (ok) void signOut();
    } }, icon("signOut"), h("span", { class: "rail-label" }, "Log out")),
    h("div", { class: "rail-foot rail-label" }, versionLabel()));
}

function bottomNav() {
  const r = state.route.name;
  // On the top-level screens and Team chat (it stays pinned there too); a customer conversation gets the whole
  // screen for its composer.
  if (!["inbox", "contacts", "activity", "tasks", "analytics", "search", "chat", "ai-agent", ...SETTINGS_ROUTES].includes(r)) return null;
  if (r === "chat" && isWide()) return null; // computers: Team chat has the sidebar, not the phone bar
  const inboxUnread = needsYouCount();
  const tab = (iconName: string, label: string, to: Route, active: boolean, badge = 0) =>
    h("button", { class: "nav-tab", "aria-current": String(active), onclick: () => go(to) },
      h("span", { class: "nav-icon" }, icon(iconName), badge ? h("span", { class: "nav-badge" }, badge > 99 ? "99+" : String(badge)) : null), label);
  return h("nav", { class: "bottom-nav", "aria-label": "Main" },
    tab("home", "Home", { name: "inbox" }, r === "inbox" || r === "search", inboxUnread + otherLineUnread()),
    tab("contacts", "Contacts", { name: "contacts" }, r === "contacts"),
    // Keypad: in the middle, where your thumb is.
    h("button", { class: "nav-dial", "aria-label": "Keypad: call or text a number", onclick: () => go({ name: "dialer" }) }, h("span", { class: "nav-dial-btn" }, icon("dialgrid"))),
    // Your personal line is just you: Selena in place of Team chat.
    state.line === "personal"
      ? tab("sparkle", aiName(), { name: "ai-agent" }, r === "ai-agent", seeingSelenaChat() ? 0 : state.agentUnread)
      : tab("chat", "Team", { name: "chat" }, r === "chat" || r === "ai-agent", (r === "chat" ? 0 : state.chatSummary.unread) + (seeingSelenaChat() ? 0 : state.agentUnread)),
    // (Activity opens from the bell at the top, not from Settings: no tab lights up for it. 5.10.20)
    tab("settings", "Settings", { name: "settings" }, ["tasks", "analytics", ...SETTINGS_ROUTES].includes(r)),
    null);
}

// ---------------------------------------------------------------- main pane

function mainPane() {
  // 1.10.28: an admin-only page opened by address shows Settings to a member (see ADMIN_ONLY_PAGES).
  const r: Route = state.user && !isAdmin() && ADMIN_ONLY_PAGES.has(state.route.name) ? { name: "settings" } : state.route;
  const inner =
    r.name === "thread" ? threadView(r.number)
    : r.name === "dialer" ? dialerView()
    : r.name === "chat" ? chatView()
    : r.name === "ai-agent" ? aiAgentView()
    : r.name === "settings" ? settingsView()
    : r.name === "team" ? teamView()
    : r.name === "phone-settings" ? phoneSettingsView()
    : r.name === "contact" ? contactView(r.number)
    : r.name === "notes-to-profiles" ? notesToProfilesView()
    : r.name === "activity" ? activityView()
    : r.name === "tasks" ? tasksView()
    : r.name === "analytics" ? analyticsView()
    : r.name === "search" ? searchView()
    : r.name === "snippets" ? snippetsView()
    : r.name === "blocked" ? blockedView()
    : r.name === "settings-calling" ? callingSettingsView()
    : r.name === "settings-notifications" ? notificationSettingsView()
    : r.name === "settings-sounds" ? soundSettingsView()
    : r.name === "settings-audio" ? audioSettingsView()
    : r.name === "call-settings" ? callHandlingView()
    : r.name === "scheduled" ? scheduledView()
    : r.name === "import-quo" ? quoImportView()
    : r.name === "storage" ? storageView()
    : r.name === "phone-numbers" ? phoneNumbersView()
    : r.name === "new-group" ? newGroupView()
    : r.name === "billing" ? billingView()
    : r.name === "speed" ? speedView()
    : r.name === "settings-rcs" ? rcsView()
    : r.name === "security" ? securityView()
    : r.name === "email-alerts" ? emailAlertsView()
    : r.name === "contacts"
      ? h("div", { class: "placeholder-main" },
          h("div", {}, h("span", { class: "placeholder-icon" }, icon("contacts", "lg")), h("div", { style: "font-weight:600;color:var(--ink)" }, "No contact selected"),
            "Pick a contact to see their details, notes and history."))
    : h("div", { class: "placeholder-main" },
        h("div", {}, brandMark(), h("div", { style: "font-weight:600;color:var(--ink)" }, "No conversation selected"),
          "Select a conversation to see its calls, texts and comments."));
  return h("main", { class: "pane-main" }, lineBar(), inner);
}

/**
 * 3.10.21 "on top by the toggle in all screens": phones, for an admin with a personal line: the same
 * "Joy Phone ⌄ ⇄ Yossi's Phone ⌄" switch the inbox has at the top, on top of every other screen too. Switching keeps
 * you on the screen you're on (a conversation or contact belongs to one line, so those go to that line's list).
 */
function lineBar() {
  if (!state.personalLine || !isAdmin() || isWide() || window.matchMedia("(min-width: 768px)").matches) return null;
  const r = state.route;
  if (r.name === "dialer") return null; // the keypad has its own line switch at the top
  if (!aiAgent.pa) void loadPersonal();
  const mine = personalLineName();
  const personal = state.line === "personal";
  const other = otherLineUnread();
  const to: Route = r.name === "thread" ? { name: "inbox" } : r.name === "contact" ? { name: "contacts" } : r;
  return h("div", { class: "line-bar" },
    h("button", { class: `brand-name brand-switch${personal ? " personal" : ""}`, type: "button",
      "aria-label": personal ? `${mine}. Switch to Joy Phone` : `Joy Phone. Switch to ${mine}`,
      onclick: () => switchLine(personal ? "team" : "personal", to) },
      brandMark(), personal ? mine : "Joy Phone",
      h("span", { class: "brand-swap", "aria-hidden": "true" }, icon("chevronDown", "sm")),
      other ? h("span", { class: "brand-other-dot", title: "Something new on your other line" }) : null));
}

function backButton(to: Route = { name: "inbox" }) {
  return h("button", { class: to.name === "inbox" || to.name === "contacts" ? "icon-btn back-btn only-mobile" : "icon-btn back-btn", "aria-label": "Back", onclick: () => go(to) }, icon("back"));
}

function screen(...children: (Node | null)[]) {
  return h("section", { class: "screen", style: "flex:1;display:flex;flex-direction:column;min-height:0" }, ...children);
}

// ---------------------------------------------------------------- thread

type Item = { key: string; at: number; seq: number; label: string; node: () => Node[] };

function callLabel(c: ThreadCall) {
  const kind = c.status === "voicemail" ? "voicemail" : c.status === "missed" ? "missed call" : c.direction === "outbound" ? "outgoing call" : "call";
  return `${kind} at ${clock(serverTime(c.started_at))}`;
}

/** Opens the composer in "internal comment" mode, attached to a call or text. */
function commentOn(key: string, label: string) {
  state.composeMode = "comment";
  state.commentOn = { key, label };
  state.reply = null;
  state.openThreads.add(key);
  render();
  document.getElementById("draft")?.focus();
}

/**
 * 3.10.22: replies sit indented under the comment they answer (a reply to a reply joins the same group), and the
 * Reply bubble opens right under the comment it answers.
 */
function nestedNotes(notes: Note[], key: string): Node[] {
  const byId = new Map(notes.map((n) => [n.id, n]));
  const rootOf = (n: Note) => {
    let r = n;
    const seen = new Set<number>();
    while (r.reply_to && byId.has(r.reply_to) && !seen.has(r.id)) {
      seen.add(r.id);
      r = byId.get(r.reply_to)!;
    }
    return r;
  };
  const withBox = (n: Note, hideQuote = false): Node[] => [commentNote(n, key, hideQuote), ...(state.inlineReply === n.id ? [inlineReplyBox(n)] : [])];
  const out: Node[] = [];
  for (const root of notes.filter((n) => rootOf(n) === n)) {
    out.push(...withBox(root));
    const replies = notes.filter((n) => n !== root && rootOf(n) === root);
    // The quote is only needed when it answers a reply further down, not the comment right above.
    if (replies.length) out.push(h("div", { class: "note-replies" }, ...replies.flatMap((r) => withBox(r, r.reply_to === root.id))));
  }
  return out;
}

/** Opens the Reply bubble under an internal comment. */
function openInlineReply(id: number) {
  state.inlineReply = id;
  state.reply = null;
  render();
  requestAnimationFrame(() => {
    const ta = document.getElementById("reply-inline") as HTMLTextAreaElement | null;
    ta?.focus();
    ta?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  });
}

/** The Reply bubble under an internal comment: type, paste / drop / attach a photo or PDF, Send. */
function inlineReplyBox(n: Note) {
  const key = `ir:${n.id}`;
  const files = state.attachments[key] ?? [];
  let sendingNow = false;
  const close = () => {
    state.inlineReply = null;
    render();
  };
  const send = async () => {
    const body = (state.drafts[key] ?? "").trim();
    const media = (state.attachments[key] ?? []).map(({ url, type, name }) => ({ url, type, name }));
    if ((!body && !media.length) || sendingNow || state.uploading > 0) return;
    sendingNow = true;
    try {
      await api.comment(n.external_number, body, n.id, null, media);
      delete state.drafts[key];
      delete state.attachments[key];
      saveDrafts();
      state.inlineReply = null;
      await refresh();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      sendingNow = false;
      render();
    }
  };
  const suggest = h("div", { class: "mention-suggest", role: "listbox" });
  const showMentions = (el: HTMLTextAreaElement) => {
    suggest.replaceChildren();
    const before = el.value.slice(0, el.selectionStart ?? el.value.length);
    const m = /(^|\s)@([\p{L}]*)$/u.exec(before);
    if (!m || state.line === "personal") return;
    const typed = m[2].toLowerCase();
    const matches = state.team.filter((u) => u.id !== state.user?.id && (firstName(u.name).toLowerCase().startsWith(typed) || u.name.toLowerCase().replace(/\s+/g, "").startsWith(typed)));
    for (const u of matches.slice(0, 5)) {
      suggest.append(h("button", { type: "button", onmousedown: (e: Event) => e.preventDefault(), onclick: () => {
        const tag = mentionTag(u);
        const start = before.length - typed.length;
        el.value = `${el.value.slice(0, start)}${tag} ${el.value.slice(el.selectionStart ?? el.value.length)}`;
        state.drafts[key] = el.value;
        suggest.replaceChildren();
        el.focus();
        const caret = start + tag.length + 1;
        el.setSelectionRange(caret, caret);
        fitTextarea(el);
      } }, avatar(u.email, u.name, "sm"), u.name));
    }
  };
  const pick = (list: File[]) => void addFiles(key, list);
  const ta = h("textarea", { id: "reply-inline", class: "field inline-edit", rows: "1",
    placeholder: state.line === "personal" ? "Reply (only you see it)" : `Reply to ${n.user_id === state.user?.id ? "your comment" : firstName(n.user_name)} · @ to mention`,
    "aria-label": `Reply to ${n.user_name}`,
    oninput: (e: Event) => {
      const el = e.target as HTMLTextAreaElement;
      state.drafts[key] = el.value;
      saveDrafts();
      fitTextarea(el);
      showMentions(el);
    },
    onpaste: (e: ClipboardEvent) => {
      let pasted = [...(e.clipboardData?.files ?? [])];
      if (!pasted.length) pasted = [...(e.clipboardData?.items ?? [])].filter((i) => i.kind === "file").map((i) => i.getAsFile()).filter((f): f is File => !!f);
      if (!pasted.length) return;
      e.preventDefault();
      pick(pasted);
    },
    onkeydown: (e: KeyboardEvent) => {
      if (mentionKey(suggest, e)) return;
      if (e.key === "Escape") (e.preventDefault(), close());
      else if (e.key === "Enter" && !e.shiftKey && !e.isComposing && matchMedia("(pointer: fine)").matches) (e.preventDefault(), void send());
    } }) as HTMLTextAreaElement;
  ta.value = state.drafts[key] ?? "";
  requestAnimationFrame(() => fitTextarea(ta));
  const tray = files.length || state.uploading
    ? h("div", { class: "attach-tray" },
        ...files.map((f, i) => h("div", { class: "attach-chip" },
          f.type.startsWith("image/") ? h("img", { src: f.preview ?? f.url, alt: "" }) : h("span", { class: "pdf-badge" }, fileBadge(f.type)),
          h("span", { class: "ellipsis" }, f.name ?? (f.type.startsWith("image/") ? "Photo" : "Document")),
          h("button", { class: "icon-btn sm", type: "button", "aria-label": "Remove", onclick: () => {
            state.attachments[key] = files.filter((_, j) => j !== i);
            render();
          } }, icon("close", "sm")))),
        state.uploading ? h("div", { class: "attach-chip" }, h("span", { class: "spinner" }), "Uploading…") : null)
    : null;
  return h("div", { class: "reply-bubble",
      // A photo dropped here goes into this reply, not the message box at the bottom.
      ondragover: (e: DragEvent) => (e.preventDefault(), e.stopPropagation()),
      ondrop: (e: DragEvent) => {
        e.stopPropagation();
        (e.currentTarget as HTMLElement).closest(".drop-over")?.classList.remove("drop-over");
        if (!e.dataTransfer?.files.length) return;
        e.preventDefault();
        pick([...e.dataTransfer.files]);
      } },
    avatar(state.user?.email ?? "", state.user?.name ?? "", "xs"),
    h("div", { class: "reply-bubble-col" },
      tray, ta, suggest,
      h("div", { class: "inline-edit-actions" },
        h("label", { class: "icon-btn sm attach-btn", title: "Attach a photo or PDF (or drop / paste one)", "aria-label": "Attach a photo or PDF" }, icon("clip", "sm"),
          h("input", { type: "file", multiple: true, accept: "image/*,application/pdf", class: "sr-only", onchange: (e: Event) => {
            const input = e.target as HTMLInputElement;
            pick([...(input.files ?? [])]);
            input.value = "";
          } })),
        h("span", { class: "grow" }),
        h("button", { class: "btn quiet sm", onclick: close }, "Cancel"),
        h("button", { class: "btn sm", disabled: state.uploading > 0, onclick: () => void send() }, "Reply"))));
}

/** Collapsed: one line ("3 internal comments · Ana, Ben"). Expanded: the comments, with reply/react/add. */
function commentThread(key: string, label: string, notes: Note[]) {
  const open = state.openThreads.has(key);
  const people = [...new Map(notes.map((n) => [n.user_id, n])).values()];
  const last = notes[notes.length - 1];
  const toggle = () => {
    if (open) state.openThreads.delete(key);
    else state.openThreads.add(key);
    render();
  };
  const chip = h("button", { class: open ? "thread-chip open" : "thread-chip", "aria-expanded": String(open), onclick: toggle },
    h("span", { class: "stack" }, ...people.slice(0, 3).map((p) => (p as Note & { bot?: number | boolean }).bot ? h("span", { class: "ai-av xs" }, icon("sparkle", "sm")) : avatar(mateEmail(p.user_id) ?? String(p.user_id) + p.user_name, p.user_name, "xs"))),
    // 1.10.20: like the card in the photo: "4 comments", hanging off its call or text on a little branch.
    h("span", { class: "thread-count" }, `${notes.length} comment${notes.length > 1 ? "s" : ""}`),
    icon("chevron", "sm"));
  chip.title = `${people.map((p) => (p.user_id === state.user?.id ? "You" : firstName(p.user_name))).join(", ")} · ${listTime(last.created_at)}`;
  const head = h("div", { class: "cmt-head" }, h("span", { class: "thread-branch", "aria-hidden": "true" }), chip);
  if (!open) return h("div", { class: "thread-wrap" }, head);
  return h("div", { class: "thread-wrap open" }, head,
    h("div", { class: "thread-body" },
      ...nestedNotes(notes, key),
      h("div", { class: "thread-actions" },
        h("button", { class: "text-btn", onclick: () => commentOn(key, label) }, icon("plus", "sm"), "Add comment"),
        h("button", { class: "text-btn muted-btn", onclick: toggle }, "Hide"))));
}

// ---- "Take over my browser": Selena hit a step only a person can do (a security check, a password, a code sent
// to your phone). You see her live browser, do just that step with taps and typing, then hand it back. ----
const TK_W = 1280, TK_H = 900; // her browser's screen size (the frame and the coordinates sent back)
const tko = {
  token: "",
  info: null as (PendingTakeover & { url: string; agentName: string }) | null,
  src: "",
  zoom: false,
  show: false,
  err: "",
  loop: 0,
  lastInput: 0,
  pressed: false,
  lastMove: 0,
  chain: Promise.resolve() as Promise<unknown>,
  keys: "",
  keyTimer: 0,
};
const tkSleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
function openTakeover(token: string) {
  if (tko.token === token) return;
  tko.token = token;
  tko.info = null;
  tko.err = "";
  tko.zoom = false;
  render();
  void api.takeover(token).then((i) => {
    if (tko.token !== token) return;
    tko.info = i;
    render();
  }).catch((e) => {
    if (tko.token !== token) return;
    closeTakeover();
    toast((e as Error).message);
  });
  void takeoverFrames();
}
function closeTakeover() {
  tko.token = "";
  tko.info = null;
  tko.loop++;
  tko.pressed = false;
  if (tko.src) URL.revokeObjectURL(tko.src);
  tko.src = "";
  render();
}
/** Her screen, a few times a second (faster right after you tap or type, slower when the app is in the background). */
async function takeoverFrames() {
  const my = ++tko.loop;
  const token = tko.token;
  let fails = 0;
  while (tko.loop === my && tko.token === token) {
    try {
      const r = await fetch(`/api/takeover/${encodeURIComponent(token)}/frame`, { credentials: "same-origin", cache: "no-store" });
      if (tko.loop !== my) return;
      if (r.status === 404) {
        const watching = tko.info?.kind === "watch";
        closeTakeover();
        toast(watching ? `${aiName()} is done on that website: the result is in your chat.` : "That handover is over.");
        void refresh();
        return;
      }
      if (r.ok) {
        const url = URL.createObjectURL(await r.blob());
        if (tko.loop !== my) return URL.revokeObjectURL(url);
        const old = tko.src;
        tko.src = url;
        const img = document.getElementById("tk-screen") as HTMLImageElement | null;
        if (img) img.src = url;
        else render();
        if (old) setTimeout(() => URL.revokeObjectURL(old), 2000);
        if (tko.err) (tko.err = ""), render();
        fails = 0;
      } else fails++;
    } catch {
      fails++;
    }
    if (fails >= 8 && !tko.err) {
      tko.err = "The live view isn't coming through. Check your connection.";
      render();
    }
    await tkSleep(document.visibilityState !== "visible" ? 2000 : Date.now() - tko.lastInput < 4000 ? 150 : 600);
  }
}
/** Your taps and keys, in order. */
function tkSend(input: Record<string, unknown>) {
  tko.lastInput = Date.now();
  const token = tko.token;
  tko.chain = tko.chain.then(async (): Promise<unknown> => (token ? api.takeoverInput(token, input) : null)).catch((e) => toast((e as Error).message));
}
/** 3.10.30: drag-to-scroll. Finger movement is added up and sent one scroll at a time (never a backlog that keeps
 * scrolling after you let go). */
const tkScroll = { pending: 0, busy: false };
function tkScrollBy(at: { x: number; y: number }, dy: number) {
  tkScroll.pending += dy;
  if (tkScroll.busy || Math.abs(tkScroll.pending) < 8) return;
  const amount = Math.round(Math.max(-1500, Math.min(1500, tkScroll.pending)));
  tkScroll.pending -= amount;
  tkScroll.busy = true;
  tkSend({ kind: "scroll", ...at, dy: amount });
  void tko.chain.finally(() => {
    tkScroll.busy = false;
    if (Math.abs(tkScroll.pending) >= 8) tkScrollBy(at, 0);
  });
}
function tkPoint(e: { clientX: number; clientY: number }) {
  const img = document.getElementById("tk-screen");
  const r = img?.getBoundingClientRect();
  if (!r || !r.width) return null;
  return { x: Math.round(((e.clientX - r.left) / r.width) * TK_W), y: Math.round(((e.clientY - r.top) / r.height) * TK_H) };
}
const TK_KEYS = new Set(["Enter", "Tab", "Backspace", "Delete", "Escape", "ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home", "End", "PageDown", "PageUp"]);
function tkFlushKeys() {
  clearTimeout(tko.keyTimer);
  if (tko.keys) tkSend({ kind: "type", text: tko.keys });
  tko.keys = "";
}
/** 3.11.3: take over her browser from View browser (she waits at her next step until you tap Done). */
async function takeControl() {
  const token = tko.token;
  if (!token) return;
  try {
    await api.takeoverControl(token);
    tko.info = await api.takeover(token).catch(() => tko.info);
    render();
  } catch (e) {
    toast((e as Error).message);
  }
}
async function finishTakeover(stop: boolean) {
  const token = tko.token;
  const name = tko.info?.agentName ?? aiName();
  try {
    await tko.chain;
    await api.takeoverDone(token, stop);
    closeTakeover();
    state.pendingTakeover = null;
    if (stop) state.liveBrowser = null;
    toast(stop ? "Stopped. Nothing more is done on that site." : `Thanks! ${name} is carrying on.`);
  } catch (e) {
    toast((e as Error).message);
  }
}
function takeoverBar() {
  const t = state.pendingTakeover;
  if (!t || tko.token) return null;
  const what = t.kind === "check" ? "a security check" : t.kind === "login" ? "a sign-in" : t.kind === "code" ? "a code" : "one step";
  return h("button", { class: "callbar takeover-bar", onclick: () => openTakeover(t.token) },
    icon("user", "sm"), h("span", { class: "ellipsis" }, `${aiName()} needs you on ${t.where} for ${what}`), h("b", {}, "Open my browser"));
}
/** 4.10.21 "show me your page with view browser": while she's on a website for you, watch her browser live. */
function liveBrowserBar() {
  const l = state.liveBrowser;
  if (!l || tko.token) return null;
  // 2.11.3 "there is no View browser": it showed only once the page was open. Now the bar is there from the start
  // ("opening the browser…", or waiting for a free one) and becomes View browser the moment the page opens.
  if (!l.token) return h("div", { class: "callbar takeover-bar live-browser-bar starting", role: "status" },
    h("span", { class: "spinner sm", "aria-hidden": "true" }),
    h("span", { class: "ellipsis" }, l.starting === "queued" ? `${aiName()} is waiting for a free browser for ${l.where}…` : `${aiName()} is opening the browser for ${l.where}…`));
  const token = l.token;
  return h("button", { class: "callbar takeover-bar live-browser-bar", onclick: () => openTakeover(token) },
    icon("eye", "sm"), h("span", { class: "ellipsis" }, `${aiName()} is on ${l.where}${l.by ? ` for ${firstName(l.by)}` : ""}`), h("b", {}, "View browser"));
}
function takeoverView() {
  if (!tko.token) return null;
  const i = tko.info;
  // Watching (view only): no taps or typing reach her browser; you can stop the booking or close the view.
  const watch = (i?.kind === "watch" || (!i && state.liveBrowser?.token === tko.token)) && !(i as { control?: boolean } | null)?.control;
  const ctl = i as { control?: boolean; controlledBy?: string | null } | null;
  if (watch) return h("div", { class: "takeover watching", role: "dialog", "aria-label": `${i?.agentName ?? aiName()}'s browser` },
    h("header", { class: "tk-head" },
      h("div", { class: "grow" },
        h("b", {}, `${i?.agentName ?? aiName()} on ${i?.where ?? state.liveBrowser?.where ?? "the website"}`),
        h("div", { class: "muted tk-what" }, ctl?.controlledBy ? `${firstName(ctl.controlledBy)} is using the browser now; ${i?.agentName ?? aiName()} is waiting.` : `Live. ${i?.agentName ?? aiName()} keeps working while you watch. Tap Take over to click and type yourself.`)),
      h("button", { class: "icon-btn", type: "button", "aria-label": "Close", title: "Close", onclick: () => closeTakeover() }, icon("close"))),
    h("div", { class: `tk-stage${tko.zoom ? " zoom" : ""}` },
      tko.src ? h("img", { id: "tk-screen", src: tko.src, alt: "Live browser", draggable: "false" })
        : h("div", { class: "tk-wait" }, h("span", { class: "spinner" }), tko.err || "Connecting to the browser…"),
      tko.err && tko.src ? h("div", { class: "tk-err" }, tko.err) : null),
    h("div", { class: "tk-controls" },
      h("div", { class: "tk-finish" },
        h("button", { class: "btn quiet sm", type: "button", "aria-pressed": String(tko.zoom), onclick: () => ((tko.zoom = !tko.zoom), render()) }, tko.zoom ? "Fit" : "Zoom"),
        // 3.11.3 "let me select things on the browser by clicking the screen": she pauses; you click and type.
        !ctl?.controlledBy ? h("button", { class: "btn sm tk-takeover", type: "button", onclick: () => void takeControl() }, icon("hand", "sm"), "Take over") : null,
        // 7.10.30: a price check is stopped as one (it never books).
        h("button", { class: "btn quiet danger-text", type: "button", onclick: () => void finishTakeover(true) }, (i as { check?: boolean } | null)?.check ? "Stop price check" : "Stop booking"),
        h("button", { class: "btn", type: "button", onclick: () => closeTakeover() }, "Close"))));
  const what = !i ? "" : ctl?.control ? `Click and type right on the picture; ${i.agentName} waits, then carries on from the page you leave.` : i.kind === "check" ? "Do the security check (tick the box, solve the puzzle or press and hold)." : i.kind === "login" ? "Sign in. Your password goes straight into the site; it's never saved or shown to anyone."
    : i.kind === "code" ? "Type the code they sent you." : i.reason;
  const typed = () => document.getElementById("tk-text") as HTMLInputElement | null;
  const sendTyped = () => {
    const f = typed();
    if (!f?.value) return;
    tkSend({ kind: "type", text: f.value });
    f.value = "";
  };
  const key = (label: string, k: string) => h("button", { class: "btn quiet sm", type: "button", onclick: () => tkSend({ kind: "key", key: k }) }, label);
  return h("div", { class: "takeover", role: "dialog", "aria-label": `${i?.agentName ?? aiName()}'s browser` },
    h("header", { class: "tk-head" },
      h("div", { class: "grow" },
        h("b", {}, !i ? "Opening the browser…" : ctl?.control ? `${i.where}: you're using ${i.agentName}'s browser` : `${i.where}: ${i.agentName} needs you`),
        h("div", { class: "muted tk-what" }, what ? `${what} Drag up or down on the picture to scroll. Then tap Done.` : "")),
      h("button", { class: "icon-btn", type: "button", "aria-label": "Close (it keeps waiting)", title: "Close (it keeps waiting)", onclick: () => closeTakeover() }, icon("close"))),
    h("div", { class: `tk-stage${tko.zoom ? " zoom" : ""}`, tabindex: "0", "aria-label": "Live browser. Tap to click, type with your keyboard.",
      onkeydown: (e: KeyboardEvent) => {
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        if (e.key.length === 1) {
          tko.keys += e.key;
          clearTimeout(tko.keyTimer);
          tko.keyTimer = window.setTimeout(tkFlushKeys, 120);
        } else if (TK_KEYS.has(e.key)) {
          tkFlushKeys();
          tkSend({ kind: "key", key: e.key });
        } else return;
        e.preventDefault();
        e.stopPropagation();
      },
      onwheel: (e: WheelEvent) => {
        if (tko.zoom) return; // zoomed in: the wheel moves around the picture
        e.preventDefault();
        const now = Date.now();
        if (now - tko.lastMove < 90) return;
        tko.lastMove = now;
        const p = tkPoint(e);
        tkSend({ kind: "scroll", ...(p ?? {}), dy: Math.max(-1200, Math.min(1200, e.deltaY * 2)) });
      } },
      tko.src
        ? h("img", { id: "tk-screen", src: tko.src, alt: "Live browser", draggable: "false",
            // Fit: press, drag and hold work (sliders, "press and hold"). Zoomed in: a tap is a click, dragging pans.
            // 3.10.30: drag up or down with one finger (or the mouse) to scroll the site, like a normal page. A sideways
            // drag (a slider) or holding still (press and hold) still goes to the site as a press.
            onpointerdown: (e: PointerEvent) => {
              if (tko.zoom || e.button > 0) return;
              e.preventDefault();
              (document.querySelector(".tk-stage") as HTMLElement | null)?.focus({ preventScroll: true });
              const p = tkPoint(e);
              if (!p) return;
              tko.pressed = true;
              let mode: "wait" | "press" | "scroll" = "wait";
              let lastY = e.clientY;
              const startX = e.clientX, startY = e.clientY;
              const press = () => {
                if (mode !== "wait") return;
                mode = "press";
                tkSend({ kind: "down", ...p });
              };
              // Held still for 0.7 s: press and hold. (3.10.30: was 0.18 s, so a mouse press with a short pause before
              // dragging became a press on the site instead of a scroll.) Moving first always decides.
              const hold = window.setTimeout(press, 700);
              const move = (ev: PointerEvent) => {
                if (!tko.pressed) return;
                if (mode === "wait") {
                  const dx = ev.clientX - startX, dy = ev.clientY - startY;
                  if (Math.abs(dy) > 10 && Math.abs(dy) > 1.4 * Math.abs(dx)) {
                    clearTimeout(hold);
                    mode = "scroll";
                    lastY = ev.clientY;
                    return;
                  }
                  if (Math.hypot(dx, dy) > 10) (clearTimeout(hold), press());
                  else return;
                }
                if (mode === "scroll") {
                  const img = document.getElementById("tk-screen")?.getBoundingClientRect();
                  if (img?.height) tkScrollBy(p, ((lastY - ev.clientY) / img.height) * TK_H);
                  lastY = ev.clientY;
                  return;
                }
                if (Date.now() - tko.lastMove < 70) return;
                tko.lastMove = Date.now();
                const q = tkPoint(ev);
                if (q) tkSend({ kind: "move", ...q });
              };
              // On the window: a refresh may swap the picture while your finger is down, and the release must still arrive.
              const up = (ev: PointerEvent) => {
                window.removeEventListener("pointermove", move);
                window.removeEventListener("pointerup", up);
                window.removeEventListener("pointercancel", up);
                clearTimeout(hold);
                if (!tko.pressed) return;
                tko.pressed = false;
                if (mode === "scroll") return;
                if (mode === "wait") tkSend({ kind: "down", ...p }); // a quick tap: a click where you tapped
                tkSend({ kind: "up", ...(mode === "wait" ? p : tkPoint(ev) ?? p) });
              };
              window.addEventListener("pointermove", move);
              window.addEventListener("pointerup", up);
              window.addEventListener("pointercancel", up);
            },
            onclick: (e: MouseEvent) => {
              if (!tko.zoom) return;
              const p = tkPoint(e);
              if (p) tkSend({ kind: "click", ...p });
            } })
        : h("div", { class: "tk-wait" }, h("span", { class: "spinner" }), tko.err || "Connecting to the browser…"),
      tko.err && tko.src ? h("div", { class: "tk-err" }, tko.err) : null),
    h("div", { class: "tk-controls" },
      h("div", { class: "tk-type" },
        // Always a plain text box (hidden with dots via CSS, not type=password): a password box made Chrome drop your
        // saved Joy Phone sign-in email into the sidebar search while Steven was on a website (20.10).
        h("input", { id: "tk-text", class: tko.show ? "field" : "field masked", type: "text", name: "joy-type-into-page", autocomplete: "off",
          "data-1p-ignore": "true", "data-lpignore": "true", "data-form-type": "other", autocapitalize: "off", spellcheck: "false", enterkeyhint: "send",
          placeholder: "Type here, then Send (it goes into the box you tapped)",
          onkeydown: (e: KeyboardEvent) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            sendTyped();
          } }),
        h("button", { class: "icon-btn sm", type: "button", "aria-label": tko.show ? "Hide what you type" : "Show what you type", title: tko.show ? "Hide" : "Show",
          onclick: () => ((tko.show = !tko.show), render()) }, icon("eye", "sm")),
        h("button", { class: "btn sm", type: "button", onclick: sendTyped }, "Send")),
      h("div", { class: "tk-keys" },
        key("Enter", "Enter"), key("Tab", "Tab"), key("⌫", "Backspace"),
        h("button", { class: "btn quiet sm", type: "button", onclick: () => tkSend({ kind: "scroll", dy: -500 }) }, "Scroll ↑"),
        h("button", { class: "btn quiet sm", type: "button", onclick: () => tkSend({ kind: "scroll", dy: 500 }) }, "Scroll ↓"),
        h("button", { class: "btn quiet sm", type: "button", "aria-pressed": String(tko.zoom), onclick: () => ((tko.zoom = !tko.zoom), render()) }, tko.zoom ? "Fit" : "Zoom")),
      h("div", { class: "tk-finish" },
        h("button", { class: "btn quiet danger-text", type: "button", onclick: () => void finishTakeover(true) }, (i as { check?: boolean } | null)?.check ? "Stop price check" : "Stop booking"),
        h("button", { class: "btn", type: "button", disabled: !i, onclick: () => void finishTakeover(false) }, icon("check", "sm"), `Done, ${i?.agentName ?? aiName()} carries on`))));
}

/** Calls and texts shown at first in a long conversation (more with "Show earlier"). */
const THREAD_PAGE = 120;
let threadCut: { key: string; at: number; revealBelow?: number } = { key: "", at: -Infinity };
/** 7.10.30: older pages of a conversation fetched with "Show earlier" past what the first load had (per line + number). */
const olderPages = new Map<string, { calls: Thread["calls"]; messages: Thread["messages"]; comments: Thread["comments"]; more: boolean; olderBefore: string | null }>();
const olderKey = (t: Thread) => `${t.line === "personal" ? "personal" : "team"}:${t.number}`;
/** Adds the older pages already fetched to a freshly loaded conversation (a live reload mustn't drop them). */
function withOlder(t: Thread): Thread {
  const o = olderPages.get(olderKey(t));
  if (!o) return t;
  const add = <X extends { id: number | string }>(older: X[], now: X[]) => {
    const ids = new Set(now.map((x) => x.id));
    return [...older.filter((x) => !ids.has(x.id)), ...now];
  };
  return { ...t, calls: add(o.calls, t.calls), messages: add(o.messages, t.messages), comments: add(o.comments, t.comments), more: o.more, olderBefore: o.olderBefore };
}
async function loadOlderThread(t: Thread) {
  if (!t.olderBefore) return;
  const page = await api.threadBefore(t.number, t.olderBefore);
  const key = olderKey(t);
  const had = olderPages.get(key);
  olderPages.set(key, {
    calls: [...page.calls, ...(had?.calls ?? [])],
    messages: [...page.messages, ...(had?.messages ?? [])],
    comments: [...page.comments, ...(had?.comments ?? [])],
    more: !!page.more,
    olderBefore: page.olderBefore ?? null,
  });
  if (state.thread?.number === t.number) state.thread = withOlder(state.thread);
}
/** Calls inside the team show in the inbox too: "tm:1-2" (two teammates) or "ai:1" (a teammate and the AI). */
const isInternalKey = (n: string) => /^(tm|ai):/.test(n);
let internalThread: InternalThread | null = null;
async function loadInternal(key: string) {
  try {
    const t = await api.internal(key);
    if (state.route.name === "thread" && state.route.number === key) {
      internalThread = t;
      const c = state.conversations.find((x) => x.external_number === key);
      if (c) Object.assign(c, { unread: false, needs_you: 0 });
      syncBadge();
      render();
    }
  } catch (e) {
    toast((e as Error).message);
  }
}
/** A teammate's (or the AI's) call history with you: who called, when, how long; call them from the top. */
function internalView(key: string) {
  const t = internalThread?.key === key ? internalThread : null;
  const conv = state.conversations.find((c) => c.external_number === key);
  const name = t?.name ?? conv?.contact_name ?? "";
  const ai = key.startsWith("ai:");
  const mate = t?.userId ? state.team.find((u) => u.id === t.userId) : null;
  const canCall = !!state.user?.canCall && !(voice.call && !isEnded(voice.call));
  const head = h("header", { class: "thread-head" },
    backButton(),
    ai ? h("span", { class: "ai-av lg" }, icon("sparkle")) : avatar(mate?.email ?? key, name),
    ai
      ? h("button", { class: "who who-link", type: "button", title: `Text ${name || aiName()}`, onclick: () => openAgentChat() },
          h("div", { class: "title contact-title" }, name), h("div", { class: "number" }, "Your calls with your AI assistant · tap to text"))
      : h("div", { class: "who" }, h("div", { class: "title contact-title" }, name), h("div", { class: "number" }, "Teammate calls")),
    ai ? h("button", { class: "btn sm selena-text-btn", type: "button", "aria-label": `Text ${name || aiName()}`, title: `Text ${name || aiName()}`, onclick: () => openAgentChat() }, icon("chat", "sm"), h("span", { class: "hide-narrow" }, "Text")) : null,
    canCall && (ai || mate)
      ? h("button", { class: "btn live sm selena-call-btn", "aria-label": `Call ${name}`, title: `Call ${name}`, onclick: () => void (ai ? callSelena() : callTeammate(mate!)) }, icon("phone", "sm"), h("span", { class: "hide-narrow" }, "Call"))
      : null,
    // 1.10.28 "hard to find delete conversation for Steven calls (on mobile)": right here in the header (admins).
    isAdmin() && t?.calls.length
      ? h("button", { class: "icon-btn internal-delete", type: "button", "aria-label": "Delete these calls", title: "Delete these calls", onclick: () => void deleteThread(key, name || null) }, icon("trash", "sm"))
      : null);
  const items = !t ? [h("div", { class: "empty" }, h("strong", {}, "Loading…"))]
    : !t.calls.length ? [h("div", { class: "empty" }, h("strong", {}, "No calls yet"))]
    : t.calls.map((c) => {
        // 1.10.28 "Steven's and teammates' calls have the old recording look": the same call card as a customer
        // call (round icon, what happened, who · how long, the player inside, and the summary card under it).
        const secs = c.answeredAt && c.endedAt ? Math.round(((serverTime(c.endedAt)?.getTime() ?? 0) - (serverTime(c.answeredAt)?.getTime() ?? 0)) / 1000) : 0;
        const by = firstName(c.callerName ?? name);
        const started = serverTime(c.startedAt) ?? new Date();
        const missedTheirs = c.status === "missed" && !c.mine;
        const headOf =
          missedTheirs ? { title: "Missed call", sub: `${by} called`, tone: "missed", iconName: "missed" }
          : c.status === "missed" ? { title: "No answer", sub: "You called", tone: "out", iconName: "outgoing" }
          : c.status === "ringing" ? { title: c.mine ? "Calling…" : "Incoming call", sub: c.mine ? "You called" : `${by} called`, tone: "live", iconName: c.mine ? "outgoing" : "incoming" }
          : c.status === "active" ? { title: "Ongoing call", sub: c.mine ? "You called" : `${by} called`, tone: "live", iconName: "phone" }
          : c.mine ? { title: "Call ended", sub: ["You called", secs ? duration(secs) : ""].filter(Boolean).join(" · "), tone: "out", iconName: "callDone" }
          : { title: "Call ended", sub: [`${by} called`, secs ? duration(secs) : ""].filter(Boolean).join(" · "), tone: "in", iconName: "callWaves" };
        const players = (c.recordings ?? []).map((r) => {
          const el = player({ id: 1_000_000_000 + r.id, kind: "call", durationSecs: r.durationSecs, hasAudio: true, src: `/api/internal-recordings/${r.id}/audio` } as unknown as Recording, false, true);
          el.classList.add("compact");
          return el;
        });
        // The summary: the same "Call summary" card (folded, a one-line peek), kept open across refreshes.
        let sumId = 0;
        for (const ch of c.id) sumId = (sumId * 31 + ch.charCodeAt(0)) >>> 0;
        const summary = c.summary
          ? recordingParts({ id: 2_000_000_000 + (sumId % 100_000_000), kind: "call", summary: c.summary, transcript: null, hasAudio: false, aiStatus: "done" } as unknown as Recording, false, true, false, true)
          : [];
        const bubble = h("article", { class: `rec-d call-bubble ${headOf.tone}${players.length ? "" : " no-audio"}`, id: `call-${c.id}`, "aria-label": `${headOf.title}, ${clock(started)}` },
          h("div", { class: "cb-head" },
            h("span", { class: `cb-icon ${headOf.tone}`, role: "img", "aria-label": headOf.title }, icon(headOf.iconName, "md")),
            h("span", { class: "cb-text" },
              h("span", { class: "cb-title", title: `${headOf.title} · ${fullTime(c.startedAt)}` }, headOf.title, h("span", { class: "cb-time" }, clock(started))),
              h("span", { class: "cb-sub" }, ai ? h("span", { class: "cb-ai", "aria-label": "AI agent" }, icon("aiSparkles", "sm")) : null, headOf.sub)),
            // 1.10.28 "make all recorded calls to Steven downloadable": the same ⋯ as a customer call's card.
            (c.recordings ?? []).length
              ? h("button", { class: "icon-btn sm rd-more", type: "button", "aria-label": "More for this call", onclick: (e: MouseEvent) => {
                  const b = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const recs = c.recordings ?? [];
                  openPointMenu(b.right, b.bottom + 4, recs.map((r, i) => ({
                    label: recs.length > 1 ? `Download part ${i + 1}` : "Download recording", iconName: "download",
                    run: () => downloadRecording({ id: 1_000_000_000 + r.id, src: `/api/internal-recordings/${r.id}/audio` } as unknown as Recording),
                  })));
                } }, icon("more", "sm"))
              : null),
          ...players);
        return h("div", { class: `call-tree ${c.mine ? "out" : "in"}` }, bubble,
          summary.length ? h("div", { class: "tree-branch" }, h("div", { class: "rec-d tree-sum" }, ...summary)) : null);
      });
  return screen(head, h("div", { class: "page" }, h("div", { class: "page-inner internal-calls" }, ...items)),
    // 5.11.2: text him right from his calls (it goes to his chat, which opens).
    ai ? composer({
      key: "agent",
      modes: ["chat"],
      placeholder: `Text ${name || aiName()}…`,
      send: async (body, media) => {
        if (!body && !media.length) return;
        await api.agentSend(body, media, null);
        typedValues.delete("draft");
        aiAgent.thinking = true;
        openAgentChat();
        void loadAgentChat();
      },
    }) : null);
}

function threadView(number: string) {
  if (isInternalKey(number)) return internalView(number);
  const t = state.thread?.number === number ? state.thread : null;
  const conv = state.conversations.find((c) => c.external_number === number);
  const name = t?.name ?? conv?.contact_name ?? null;
  const guess = t?.nameGuess ?? null;
  if (t?.callerName) callerNames.set(number, t.callerName);
  const done = (t?.status ?? conv?.conv_status) === "done";
  const assignedTo = t ? t.assignedTo : conv?.assigned_to ?? null;
  const assignee = state.team.find((u) => u.id === assignedTo);

  const nameLine = state.editingName
    ? h("form", { class: "name-edit", onsubmit: async (e: Event) => {
        e.preventDefault();
        const input = document.getElementById("contact-name") as HTMLInputElement;
        try {
          await api.setContact(number, input.value);
          state.editingName = false;
          await refresh();
        } catch (err) {
          toast((err as Error).message);
        }
      } },
        h("input", { id: "contact-name", value: name ?? "", placeholder: "Name", maxlength: 80, autocomplete: "off" }),
        h("button", { class: "btn sm", type: "submit" }, "Save"),
        h("button", { class: "btn quiet sm", type: "button", onclick: () => ((state.editingName = false), render()) }, "Cancel"))
    : h("div", { class: "number" },
        h("span", { class: "num-text" }, isGroup(number) ? `Group text · ${number.slice(4).split(",").length} people` : number === "anonymous" ? "Number withheld" : name || guess ? phone(number) : "Unknown contact"),
        guess && !name
          ? h("button", { class: "guess-chip", title: guess.source === "caller-id" ? "Name from caller ID. Tap to save the contact." : `The AI picked this name up from their ${guess.source}. Tap to save the contact.`,
              onclick: (e: MouseEvent) => {
                e.stopPropagation();
                state.editingContact = true;
                const panelShown = !!document.querySelector(".side-panel") && getComputedStyle(document.querySelector(".side-panel")!).display !== "none";
                if (panelShown) render();
                else {
                  go({ name: "contact", number });
                  state.editingContact = true;
                  render();
                }
                document.getElementById("ct-name")?.focus();
              } },
              icon(guess.source === "caller-id" ? "user" : "sparkle", "sm"), guess.source === "caller-id" ? "Caller ID" : "AI guess")
          : null,
        assignee ? h("span", { class: "assigned-chip" }, icon("assign", "sm"), assignee.id === state.user!.id ? "You" : firstName(assignee.name)) : null);

  // The customer conversation (calls and texts) stays front and center. Internal comments are folded
  // into a small thread under the call or text they're about; tap it to read, reply or @mention.
  const items: Item[] = [];
  if (t) {
    for (const c of t.calls) items.push({ key: `call:${c.id}`, at: serverTime(c.started_at)?.getTime() ?? 0, seq: 0, label: callLabel(c), node: () => [ticket(c, number)] });
    for (const m of t.messages) items.push({ key: `sms:${m.id}`, at: serverTime(m.created_at)?.getTime() ?? 0, seq: m.id, label: `text “${m.body.slice(0, 40)}${m.body.length > 40 ? "…" : ""}”`, node: () => [textBubble(m)] });
  }
  items.sort((a, b) => a.at - b.at || a.seq - b.seq);
  const groups = new Map<string, Note[]>();
  const commentGroup = new Map<string, string>();
  const keys = new Set(items.map((i) => i.key));
  // A comment still being saved (negative id, see the composer) goes after the saved ones, in the order you wrote
  // them: by id alone it sorted first, so it showed at the top of its thread until the server's copy came back.
  const order = (n: Note) => (n.id > 0 ? n.id : Number.MAX_SAFE_INTEGER); // unsaved ones keep their order (stable sort)
  for (const n of [...(t?.comments ?? [])].sort((a, b) => order(a) - order(b))) {
    let key = n.anchor && keys.has(n.anchor) ? n.anchor : "";
    if (!key) {
      // Older comments (or ones made from the composer): attach to the latest call or text before them.
      const at = serverTime(n.created_at)?.getTime() ?? 0;
      for (const it of items) if (it.at <= at) key = it.key;
      key ||= "top";
    }
    groups.set(key, [...(groups.get(key) ?? []), n]);
    commentGroup.set(`comment:${n.id}`, key);
    // Opened from a search result or an @mention: unfold the thread it lives in.
    if (state.focusRef === `comment:${n.id}`) state.openThreads.add(key);
  }
  // Follow-ups sit under the call or text they're about. Older ones (made for the whole conversation) go under
  // the newest call or text from when they were made.
  const taskGroups = new Map<string, Task[]>();
  for (const tk of state.threadTasks) {
    // A task on an internal comment sits under the call or text that comment thread belongs to.
    let key = tk.ref ? (keys.has(tk.ref) ? tk.ref : commentGroup.get(tk.ref) ?? "") : "";
    if (!key) {
      const at = serverTime(tk.createdAt)?.getTime() || Date.parse(tk.createdAt) || 0;
      for (const it of items) if (it.at <= at) key = it.key;
      key ||= items.length ? items[items.length - 1].key : "top";
    }
    taskGroups.set(key, [...(taskGroups.get(key) ?? []), tk]);
  }
  const lineTasks = (key: string) => {
    const list = taskGroups.get(key) ?? [];
    if (!list.length && state.taskFor !== key) return null;
    return h("div", { class: "line-tasks" }, ...list.map(lineTask), state.taskFor === key ? lineTaskForm(number, key) : null);
  };
  const timeline: Node[] = [];
  const topTasks = lineTasks("top");
  if (topTasks) timeline.push(topTasks);
  if (groups.get("top")) timeline.push(commentThread("top", "this conversation", groups.get("top")!));
  // Speed: a long conversation shows its newest THREAD_PAGE calls and texts ("Show earlier" brings more). The cut is a
  // point in time kept while the conversation is open, so new messages are added below and nothing slides off the top.
  const cutKey = `${state.line}:${number}`;
  if (threadCut.key !== cutKey || !t) threadCut = { key: t ? cutKey : "", at: t && items.length > THREAD_PAGE ? items[items.length - THREAD_PAGE].at : -Infinity };
  // Opened at an older call, text, note or task (search, @mention): shown from there, so it's on screen to jump to.
  const ref = state.focusRef ?? "";
  const focusKey = !ref ? "" : keys.has(ref) ? ref : commentGroup.get(ref)
    ?? [...taskGroups].find(([, list]) => list.some((tk) => `task:${tk.id}` === ref))?.[0] ?? "";
  const focusAt = items.find((i) => i.key === focusKey)?.at;
  if (focusAt != null && focusAt < threadCut.at) threadCut.at = focusAt;
  // Older calls or texts with something current on them stay in view: an open task, or a note written after the
  // oldest message shown (a teammate replying today under an old call).
  for (const it of items) {
    if (it.at >= threadCut.at) break;
    const live = (taskGroups.get(it.key) ?? []).some((tk) => !tk.done)
      || (groups.get(it.key) ?? []).some((n) => (serverTime(n.created_at)?.getTime() ?? 0) >= threadCut.at);
    if (live) {
      threadCut.at = it.at;
      break;
    }
  }
  // 7.10.30: just fetched an older page: show the next THREAD_PAGE items above what was the oldest.
  if (threadCut.revealBelow != null) {
    const idx = items.findIndex((i) => i.at >= threadCut.revealBelow!);
    threadCut.at = idx - THREAD_PAGE > 0 ? items[idx - THREAD_PAGE].at : -Infinity;
    threadCut.revealBelow = undefined;
  }
  const hiddenCount = items.filter((i) => i.at < threadCut.at).length;
  // 7.10.30: everything loaded is shown, and the conversation goes back further: fetch the next older page.
  if (!hiddenCount && t?.more && t.olderBefore) {
    timeline.push(h("button", { class: "show-earlier", type: "button", onclick: async (e: MouseEvent) => {
      const b = e.currentTarget as HTMLButtonElement;
      b.disabled = true;
      b.textContent = "Loading…";
      try {
        threadCut.revealBelow = items[0]?.at ?? Infinity;
        await loadOlderThread(t);
      } catch (err) {
        threadCut.revealBelow = undefined;
        toast((err as Error).message);
      }
      render();
    } }, "Show earlier history"));
  }
  if (hiddenCount) {
    timeline.push(h("button", { class: "show-earlier", type: "button", onclick: () => {
      // Stay on the same message (the earlier ones appear above it): the first one on screen keeps its place.
      const tl = document.querySelector<HTMLElement>('[data-scroll="timeline"]');
      const top = tl?.getBoundingClientRect().top ?? 0;
      const anchor = tl ? [...tl.querySelectorAll<HTMLElement>('.timeline-inner [id^="s-"], .timeline-inner [id^="call-"]')].find((el) => el.getBoundingClientRect().bottom > top) : undefined;
      const offset = anchor ? anchor.getBoundingClientRect().top - top : 0;
      const first = hiddenCount - THREAD_PAGE; // the earliest of the next THREAD_PAGE (items are oldest first)
      threadCut.at = first > 0 ? items[first].at : -Infinity;
      render();
      // Again over the next frames (messages far off screen get their real height only once drawn), until it holds
      // still; it stops the moment you scroll yourself, so it never fights your finger.
      const until = performance.now() + 1000;
      let stop = false, still = 0;
      const quit = () => (stop = true);
      for (const ev of ["wheel", "touchstart", "pointerdown", "keydown"]) window.addEventListener(ev, quit, { once: true, capture: true, passive: true });
      const keep = () => {
        if (stop) return;
        const tl2 = document.querySelector<HTMLElement>('[data-scroll="timeline"]');
        const again = anchor?.id ? document.getElementById(anchor.id) : null;
        const off = tl2 && again ? again.getBoundingClientRect().top - tl2.getBoundingClientRect().top - offset : 0;
        if (tl2 && Math.abs(off) >= 1) (tl2.scrollTop += off), (still = 0);
        else still++;
        if (still < 3 && performance.now() < until) requestAnimationFrame(keep);
      };
      keep();
    } }, `Show earlier (${hiddenCount})`));
  }
  let lastDay = "";
  for (const it of items) {
    if (it.at < threadCut.at) continue;
    const label = it.at ? dayLabel(new Date(it.at)) : "";
    // 1.10.20: every call has its date and time above it, like the photo ("Oct 8, 3:33 PM"); it stands in for the
    // day divider, so a call never sits under a "Today" pill as well.
    // 3.10.20: centred over its own card (not the page), a little smaller.
    let stamp: HTMLElement | null = null;
    if (it.key.startsWith("call:") && it.at) {
      stamp = h("div", { class: "call-when" }, callStamp(new Date(it.at))) as HTMLElement;
      lastDay = label;
    } else if (label && label !== lastDay) {
      timeline.push(h("div", { class: "day" }, label));
      lastDay = label;
    }
    const nodes = it.node();
    const tree = stamp && nodes[0] instanceof HTMLElement && nodes[0].classList.contains("call-tree") ? nodes[0] : null;
    if (tree) tree.prepend(stamp!);
    else if (stamp) timeline.push(stamp);
    timeline.push(...nodes);
    const tasksHere = lineTasks(it.key);
    if (tasksHere) timeline.push(tasksHere);
    const notes = groups.get(it.key);
    if (notes) timeline.push(commentThread(it.key, it.label, notes));
  }
  for (const sc of t?.scheduled ?? []) timeline.push(scheduledBubble(sc));
  if (t && !items.length && !t.scheduled?.length) {
    timeline.push(emptyState({ icon: "message", tone: "teal", title: "No messages yet",
      text: canText(number) ? "Send a text or call to start the conversation." : "Calls with this number will show up here." }));
  }
  if (!t) timeline.push(skeletonBubbles());

  const menu = state.menuOpen
    ? h("div", { class: "menu", role: "menu" },
        state.line === "personal" ? null : h("div", { class: "menu-label" }, "Assign to"),
        ...(state.line === "personal" ? [] : state.team).map((u) =>
          h("button", { role: "menuitemradio", "aria-checked": String(assignedTo === u.id), onclick: () => void setConversation(number, { assignedTo: u.id }) },
            avatar(u.email, u.name, "sm"), u.id === state.user!.id ? `${u.name} (you)` : u.name, assignedTo === u.id ? icon("check", "sm") : null)),
        assignedTo && state.line !== "personal" ? h("button", { role: "menuitem", onclick: () => void setConversation(number, { assignedTo: null }) }, icon("close", "sm"), "Unassign") : null,
        state.line === "personal" ? null : h("div", { class: "menu-sep" }),
        h("button", { role: "menuitem", onclick: () => void setConversation(number, { status: done ? "open" : "done" }) },
          icon("checkCircle", "sm"), done ? "Mark unresolved" : "Mark resolved"),
        h("button", { role: "menuitem", onclick: async () => {
          state.menuOpen = false;
          await api.markUnread(number).catch(() => {});
          go({ name: "inbox" });
        } }, icon("unread", "sm"), "Mark as unread"),
        h("button", { role: "menuitem", onclick: () => void togglePin(number, !!conv?.pinned) }, icon("pin", "sm"), conv?.pinned ? "Unpin" : "Pin to top"),
        h("button", { role: "menuitem", onclick: () => {
          state.menuOpen = false;
          taskOn(latestItemKey(t) ?? "top");
        } }, icon("checkCircle", "sm"), "Add task on the latest message"),
        h("div", { class: "menu-sep" }),
        number !== "anonymous" && !isGroup(number) ? h("button", { role: "menuitem", onclick: () => {
          state.menuOpen = false;
          state.editingName = true;
          render();
          document.getElementById("contact-name")?.focus();
        } }, icon("edit", "sm"), name ? "Rename" : "Add name") : null,
        // 5.10.20: a group text can have its own name (its members can't change: that would be a new group).
        isGroup(number) ? h("button", { role: "menuitem", onclick: async () => {
          state.menuOpen = false;
          render();
          const own = groupNames.get(number) ?? "";
          const v = await glassDialog({ title: own ? "Rename this group" : "Name this group", message: "Everyone on the team sees this name. Leave it empty to show the members' names.", confirm: "Save", input: { placeholder: "e.g. Lopez family trip", value: own } });
          if (v === null || v === false || (v === "" && !own)) return; // cancelled, or nothing to clear
          try {
            const r = await api.renameGroup(number, typeof v === "string" ? v : "");
            rememberGroupName(number, r.name);
            toast(r.name ? "Group named" : "Showing the members' names");
          } catch (e) {
            toast((e as Error).message);
          }
          render();
        } }, icon("edit", "sm"), groupNames.get(number) ? "Rename group" : "Name this group") : null,
        canText(number) && !isGroup(number) ? h("button", { role: "menuitem", onclick: () => go({ name: "contact", number }) }, icon("user", "sm"), "Contact details & notes") : null,
        canText(number) && !isGroup(number) ? h("button", { role: "menuitem", class: "danger-text", onclick: () => void toggleBlock(number, !!state.contact?.blocked) },
          icon("block", "sm"), state.contact?.blocked ? "Unblock number" : "Block number") : null,
        isAdmin() ? h("button", { role: "menuitem", class: "danger-text", onclick: () => ((state.menuOpen = false), render(), void deleteThread(number, name)) },
          icon("trash", "sm"), "Delete conversation") : null)
    : null;

  const onCall = !!voice.call && !isEnded(voice.call);
  const contact = state.contact?.number === number ? state.contact : null;
  const pinnedNotes = contact?.notes.filter((n) => n.pinned) ?? [];
  const main = screen(
    h("header", { class: "thread-head" },
      backButton(),
      h("button", { class: "who-btn", title: canText(number) && !isGroup(number) ? "Contact details" : "", onclick: () => canText(number) && !isGroup(number) && go({ name: "contact", number }) },
        avatar(number, name, "lg")),
      // 3.10.23: the name looks like a link on hover, and a click edits it right there.
      h("div", { class: "who" }, canText(number) && !isGroup(number) && number !== "anonymous" && !state.editingName
          ? h("button", { class: "title contact-title name-link", type: "button", title: "Edit name", onclick: () => {
              state.editingName = true;
              render();
              requestAnimationFrame(() => {
                const el = document.getElementById("contact-name") as HTMLInputElement | null;
                el?.focus();
                el?.select();
              });
            } }, nameFor(number, name))
          : h("div", { class: "title contact-title" }, nameFor(number, name)),
        state.line === "personal" ? h("div", { class: "number" }, h("span", { class: "line-pill", title: "Your personal line: only you see this conversation" }, icon("lock", "sm"), "Personal line")) : null, nameLine),
      !state.editingName
        ? h("div", { class: "head-actions" },
            number !== "anonymous" && !isGroup(number) && state.user!.canCall && !onCall ? h("button", { class: "btn live sm", onclick: () => void dial(replyNumberFor(number)), "aria-label": mergedNumbers() ? `Call ${phone(replyNumberFor(number))}` : "Call", title: mergedNumbers() ? `Call ${phone(replyNumberFor(number))}` : undefined }, icon("phone", "sm"), h("span", { class: "hide-narrow" }, "Call")) : null,
            // Resolve when the customer's request is handled (moves it to Done); tap again to unresolve.
            h("button", { class: done ? "resolve-btn on" : "resolve-btn", "aria-pressed": String(done), title: done ? "Resolved. Tap to unresolve" : "Mark resolved (moves it to Done)",
              onclick: () => void setConversation(number, { status: done ? "open" : "done" }) }, icon("checkCircle", "sm"), h("span", {}, done ? "Resolved" : "Resolve")),
            canText(number) && !isGroup(number) ? h("button", { class: `icon-btn info-btn${sidePanel.hidden ? "" : " on-wide"}`, "aria-label": "Contact details", title: "Contact details",
              onclick: () => toggleSidePanel(number) }, icon("user")) : null,
            h("div", { class: "menu-wrap" },
              h("button", { class: "icon-btn", "aria-label": "More", "aria-haspopup": "menu", "aria-expanded": String(state.menuOpen), onclick: (e: MouseEvent) => {
                e.stopPropagation();
                state.menuOpen = !state.menuOpen;
                render();
              } }, icon("more")),
              menu))
        : null),
    done ? h("div", { class: "done-bar" }, icon("checkCircle", "sm"), h("span", { class: "grow" }, "Resolved and moved to Done. A new call or text reopens it."),
      h("button", { class: "text-btn", onclick: () => void setConversation(number, { status: "open" }) }, "Unresolve")) : null,
    contact?.blocked ? h("div", { class: "warn-bar" }, icon("block", "sm"), "This number is blocked. Calls and texts from it are dropped.",
      h("button", { class: "text-btn", onclick: () => void toggleBlock(number, true) }, "Unblock")) : null,
    contact?.optedOut ? h("div", { class: "warn-bar" }, icon("block", "sm"), "They replied STOP, so texts can't be sent until they text START.") : null,
    pinnedNotes.length && state.line !== "personal"
      ? h("button", { class: "pinned-notes", onclick: () => go({ name: "contact", number }) },
          icon("pin", "sm"), h("span", { class: "ellipsis" }, h("strong", {}, `${firstName(pinnedNotes[0].userName)}: `), pinnedNotes[0].body),
          pinnedNotes.length > 1 ? h("span", { class: "muted" }, `+${pinnedNotes.length - 1}`) : null)
      : null,
    h("div", { class: "timeline", "data-scroll": "timeline" }, h("div", { class: "timeline-inner" }, ...timeline)),
    state.composeMode === "text" ? replyViaBar(number) : null,
    composer({
      // The personal line keeps its own drafts (and never tells teammates you're typing).
      key: `${state.line === "personal" ? "p" : "t"}:${number}`,
      modes: canText(number) ? ["text", "comment", "selena"] : ["comment", "selena"],
      placeholder: state.composeMode === "selena"
        ? `Ask ${aiName()} about this conversation: summarize, draft a reply, call them…`
        : state.composeMode === "text" && canText(number)
        ? isGroup(number) ? `Text ${groupName(number)}…`
        : state.line === "personal" ? `Text ${name ? firstName(name) : phone(number)} from your personal number…` : `Text ${name ? firstName(name) : phone(number)}…`
        : state.line === "personal" ? "Private note (only you see it)" : "Internal comment · @ to mention",
      schedule: canText(number) ? async (body, media, at) => void (await api.scheduleText(replyNumberFor(number), body, at, media)) : undefined,
      send: async (body, media) => {
        if (state.composeMode === "selena") {
          if (media.length) toast(`${aiName()} can't read files yet; ask in words`);
          await api.askSelena(number, body);
          // Her answer comes in under the question (the notes at the end of the conversation).
          state.openThreads.add(items[items.length - 1]?.key ?? "top");
        } else if (state.composeMode === "text" && canText(number) && !state.reply) {
          await api.text(replyNumberFor(number), body, media);
        } else {
          const anchor = state.reply ? null : state.commentOn?.key && state.commentOn.key !== "top" ? state.commentOn.key : null;
          // Show the thread it goes into.
          const into = anchor ?? t?.comments.find((c) => c.id === state.reply?.id)?.anchor ?? items[items.length - 1]?.key ?? "top";
          // 4.10.29: the comment shows right away, in its place (it waited for the server to save it and the conversation
          // to reload: measured 0.4 s at phone speed). The reload after saving brings the real one in its place.
          const me = state.user;
          if (me && state.thread?.number === number) {
            const reply = state.reply ? t?.comments.find((c) => c.id === state.reply?.id) : undefined;
            state.thread.comments = [...state.thread.comments, { id: -Date.now(), external_number: number, anchor: anchor ?? reply?.anchor ?? null, reactions: [], user_id: me.id, user_name: me.name, body,
              reply_to: state.reply?.id ?? null, reply_body: reply?.body ?? null, reply_user_name: reply?.user_name ?? null, created_at: new Date().toISOString().replace("T", " ").replace("Z", ""), media }];
            state.openThreads.add(into);
            state.scrolled = "";
            render();
          }
          await api.comment(number, body, state.reply?.id ?? null, anchor, media);
          state.openThreads.add(into);
          state.commentOn = null;
        }
      },
    }));
  // Wide screens: the customer's details sit to the right of the conversation (like Quo). The ⓘ button hides it,
  // or slides it over the conversation on medium screens.
  if (!canText(number) || isGroup(number)) return main;
  const cls = `thread-split${sidePanel.hidden ? " side-off" : ""}${sidePanel.peek ? " side-peek" : ""}`;
  return h("div", { class: cls }, main,
    enterOnce(h("aside", { class: "side-panel", "data-scroll": "side", "aria-label": "Contact details" },
      h("button", { class: "icon-btn sm side-close", "aria-label": "Close details", onclick: () => ((sidePanel.peek = false), render()) }, icon("close", "sm")),
      contactCard(number, contact, true)), `side:${number}:${sidePanel.peek ? "peek" : "pinned"}`));
}

/** Contact side panel: shown beside the conversation on wide screens (hide/show is remembered), slides over on medium ones. */
const sidePanel = { hidden: (() => { try { return localStorage.getItem("joy.sideHidden") === "1"; } catch { return false; } })(), peek: false };
const wideEnoughForSide = () => matchMedia("(min-width: 1340px)").matches;
function toggleSidePanel(number: string) {
  if (!matchMedia("(min-width: 768px)").matches) return go({ name: "contact", number });
  if (wideEnoughForSide()) {
    sidePanel.hidden = !sidePanel.hidden;
    try { localStorage.setItem("joy.sideHidden", sidePanel.hidden ? "1" : "0"); } catch { /* private mode */ }
  } else sidePanel.peek = !sidePanel.peek;
  render();
}

async function togglePin(number: string, pinned: boolean) {
  state.menuOpen = false;
  // Show it right away; the server catches up behind the scenes.
  const c = state.conversations.find((x) => x.external_number === number);
  if (c) c.pinned = !pinned;
  toast(pinned ? "Unpinned" : "Pinned to the top of your inbox");
  render();
  try {
    if (pinned) await api.unpinThread(number);
    else await api.pinThread(number);
  } catch (e) {
    toast((e as Error).message);
  }
  await refresh();
}

async function toggleBlock(number: string, blocked: boolean) {
  state.menuOpen = false;
  if (!blocked && !(await glassDialog({ title: `Block ${phone(number)}?`, message: "Their calls will be hung up and their texts dropped. You can unblock any time.", confirm: "Block", danger: true }))) return render();
  try {
    if (blocked) await api.unblock(number);
    else await api.block(number);
    toast(blocked ? "Unblocked" : "Blocked");
    await refresh();
  } catch (e) {
    toast((e as Error).message);
  }
}

/** Open follow-ups for this conversation, plus the quick "add follow-up" form. */
/** Start a follow-up on one call or text (its timeline key, e.g. "sms:12"). */
function taskOn(key: string, ref: string = key) {
  state.taskFor = key;
  state.taskRef = ref;
  render();
  const f = document.getElementById("line-task-title") as HTMLInputElement | null;
  f?.focus();
  f?.scrollIntoView({ block: "nearest" });
}

function latestItemKey(t: Thread | null): string | null {
  if (!t) return null;
  let best: { key: string; at: number } | null = null;
  for (const c of t.calls) {
    const at = serverTime(c.started_at)?.getTime() ?? 0;
    if (!best || at >= best.at) best = { key: `call:${c.id}`, at };
  }
  for (const m of t.messages) {
    const at = serverTime(m.created_at)?.getTime() ?? 0;
    if (m.id > 0 && (!best || at >= best.at)) best = { key: `sms:${m.id}`, at };
  }
  return best?.key ?? null;
}

/** One follow-up under its call or text: tick it to resolve (tick again to reopen). */
function lineTask(t: Task) {
  return h("div", { class: t.done ? "line-task done" : "line-task", id: `task-${t.id}` },
    h("button", { class: t.done ? "task-check done" : "task-check", role: "checkbox", "aria-checked": String(t.done), "aria-label": t.done ? `Reopen: ${t.title}` : `Resolve: ${t.title}`,
      onclick: () => void setTaskDone(t, !t.done) }, t.done ? icon("check", "sm") : null),
    h("button", { class: "grow line-task-title", type: "button", title: "Edit task", onclick: () => editTask(t) }, t.title),
    taskDue(t),
    t.done ? h("span", { class: "muted" }, "Done") : null,
    // Tap who it's for to hand it to someone else, or delete it.
    h("button", { class: "task-who", "aria-label": t.assigneeName ? `For ${t.assigneeName}. Change or delete` : "Assign or delete", "aria-haspopup": "menu",
      onclick: (e: MouseEvent) => {
        e.stopPropagation();
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        openPointMenu(r.left - 140, r.bottom + 4, [
          { label: "Edit task", iconName: "edit", run: () => editTask(t) },
          ...delegateItems(t),
          ...(t.assigneeId && state.line !== "personal" ? [{ label: "Unassign", iconName: "close", run: () => void setTaskAssignee(t, null) }] : []),
          { label: "Delete task", iconName: "trash", danger: true, run: () => void deleteLineTask(t) },
        ]);
      } },
      t.assigneeName ? avatar(String(t.assigneeId) + t.assigneeName, t.assigneeName, "xs") : icon("assign", "sm")));
}

async function setTaskAssignee(t: Task, assigneeId: number | null) {
  const u = state.team.find((x) => x.id === assigneeId);
  Object.assign(t, { assigneeId, assigneeName: u?.name ?? null });
  render();
  try {
    await api.updateTask(t.id, { assigneeId });
    toast(u ? `Task assigned to ${u.id === state.user?.id ? "you" : firstName(u.name)}` : "Task unassigned");
  } catch (e) {
    toast((e as Error).message);
  }
  void refresh();
}

/** Hand a follow-up on: to the AI assistant ("I'm busy, you do it") or, on the team line, to a teammate. */
function delegateItems(t: Task) {
  const items: { label: string; iconName: string; run: () => void }[] = [];
  if (t.aiCall) return items; // her own call for later: already delegated
  // Teammates first, then the AI assistant. The personal line is yours alone: its follow-ups never go to teammates.
  if (state.line !== "personal") items.push(...state.team.filter((u) => u.id !== t.assigneeId && (u as { active?: number | boolean }).active !== 0)
    .map((u) => ({ label: u.id === state.user?.id ? "Assign to me" : `Give to ${firstName(u.name)}`, iconName: "assign", run: () => void setTaskAssignee(t, u.id) })));
  if (!t.done) items.push({ label: `Let ${aiName()} handle it`, iconName: "sparkle", run: () => void delegateToAi(t) });
  return items;
}
async function delegateToAi(t: Task) {
  Object.assign(t, { done: true });
  render();
  try {
    const r = await api.delegateTask(t.id);
    toast(r.where === "thread" ? `${r.agent} is on it: the update comes in the conversation` : `${r.agent} is on it: the update comes in ${r.agent}'s chat`);
  } catch (e) {
    t.done = false;
    toast((e as Error).message);
  }
  if (state.route.name === "tasks") await loadTasks();
  else void refresh();
}

async function deleteLineTask(t: Task) {
  const ok = await glassDialog({ title: "Delete this task?", message: t.title, confirm: "Delete", danger: true });
  if (!ok) return;
  state.threadTasks = state.threadTasks.filter((x) => x.id !== t.id);
  render();
  try {
    await api.deleteTask(t.id);
  } catch (e) {
    toast((e as Error).message);
  }
  void refresh();
}

/**
 * Who a new follow-up goes to by default: the other agent when it's a two-person team (so what you add lands on their
 * list, with a notification), otherwise you. "@Name" in the title picks that teammate instead.
 */
function defaultTaskAssignee(): number {
  const me = state.user!.id;
  const others = state.team.filter((u) => u.id !== me && (u as { active?: number | boolean }).active !== 0 && (u as { active?: number | boolean }).active !== false);
  return others.length === 1 ? others[0].id : me;
}
function mentionedAssignee(title: string): number | null {
  for (const m of title.matchAll(/@([\p{L}'-]+)/gu)) {
    const hit = state.team.find((u) => firstName(u.name).toLowerCase() === m[1].toLowerCase() || u.name.toLowerCase() === m[1].toLowerCase());
    if (hit) return hit.id;
  }
  return null;
}

/**
 * A follow-up under a call goes to the teammate who was on that call (19.10.18: one under Mike's call defaulted to
 * Yossi); under a text, to whoever sent it. Otherwise the usual default.
 */
function assigneeForItem(key: string): number {
  const team = (id: number | null | undefined) => (id && state.team.some((u) => u.id === id) ? id : null);
  if (key.startsWith("call:")) {
    const c = state.thread?.calls?.find((x) => x.id === key.slice(5)) as { answered_by?: number | null; initiated_by?: number | null } | undefined;
    const who = team(c?.answered_by) ?? team(c?.initiated_by);
    if (who) return who;
  }
  if (key.startsWith("sms:")) {
    const m = state.thread?.messages?.find((x) => x.id === Number(key.slice(4)));
    const who = m?.direction === "outbound" ? team(m.user_id) : null;
    if (who) return who;
  }
  return defaultTaskAssignee();
}

/** The quick add form under a call or text: what, when, who. */
// 1.10.28: what's typed in "Add task" is kept here (it lived only on the page, so any other click or live update
// emptied it).
const lineTaskDraft = { at: "", title: "", due: "1", who: "" };
function lineTaskForm(number: string, key: string) {
  const presets = duePresets();
  const forWho = assigneeForItem(state.taskRef && state.taskRef !== "top" ? state.taskRef : key);
  const at = `${number}|${key}|${state.taskRef ?? ""}`;
  if (lineTaskDraft.at !== at) Object.assign(lineTaskDraft, { at, title: "", due: "1", who: String(forWho ?? "") });
  const who = h("select", { id: "line-task-who", class: "field sm", "aria-label": "For", onchange: (e: Event) => (lineTaskDraft.who = (e.target as HTMLSelectElement).value) },
    ...state.team.map((u) => h("option", { value: String(u.id), selected: lineTaskDraft.who ? String(u.id) === lineTaskDraft.who : u.id === forWho }, u.id === state.user?.id ? "Me" : firstName(u.name))));
  const when = h("select", { id: "line-task-due", class: "field sm", "aria-label": "Due", onchange: (e: Event) => (lineTaskDraft.due = (e.target as HTMLSelectElement).value) },
    ...presets.map((p, i) => h("option", { value: String(i), selected: lineTaskDraft.due === String(i) }, p.label)), h("option", { value: "none", selected: lineTaskDraft.due === "none" }, "No due date"));
  let busy = false;
  return h("form", { class: "line-task-form", onsubmit: async (e: Event) => {
    e.preventDefault();
    if (busy) return;
    const input = document.getElementById("line-task-title") as HTMLInputElement;
    const title = input.value.trim();
    if (!title) return toast("What's the follow-up?");
    busy = true;
    const dueAt = when.value === "none" ? null : presets[Number(when.value)]?.at ?? null;
    try {
      const ref = state.taskRef && state.taskRef !== "top" ? state.taskRef : key === "top" ? null : key;
      await api.createTask({ title, number, ref, dueAt, assigneeId: mentionedAssignee(title) ?? Number(who.value) });
      state.taskFor = null;
      state.taskRef = null;
      lineTaskDraft.at = "";
      toast("Task added");
      await refresh();
    } catch (err) {
      toast((err as Error).message);
    } finally {
      busy = false;
    }
  } },
    h("span", { class: "task-check", "aria-hidden": "true" }),
    h("input", { id: "line-task-title", class: "field sm grow", placeholder: "Task for this… (e.g. send the quote)", maxlength: 200, autocomplete: "off", value: lineTaskDraft.title,
      oninput: (e: Event) => (lineTaskDraft.title = (e.target as HTMLInputElement).value),
      onkeydown: (e: KeyboardEvent) => e.key === "Escape" && ((state.taskFor = null), render()) }),
    h("div", { class: "line-task-opts" },
      when, who,
      h("button", { class: "btn sm", type: "submit" }, "Add"),
      h("button", { class: "icon-btn sm", type: "button", "aria-label": "Cancel", onclick: () => ((state.taskFor = null), render()) }, icon("close", "sm"))));
}

async function setConversation(number: string, change: { status?: "open" | "done"; assignedTo?: number | null }) {
  state.menuOpen = false;
  // Optimistic: update the screen first, then tell the server.
  const c = state.conversations.find((x) => x.external_number === number);
  if (state.thread?.number === number) {
    if (change.status) state.thread.status = change.status;
    if (change.assignedTo !== undefined) state.thread.assignedTo = change.assignedTo;
  }
  if (c) {
    if (change.status) c.conv_status = change.status;
    if (change.assignedTo !== undefined) c.assigned_to = change.assignedTo;
  }
  if (change.status === "done") toast("Resolved · moved to Done");
  if (change.status === "open") toast("Unresolved · back in your inbox");
  if (change.assignedTo !== undefined) {
    const u = state.team.find((x) => x.id === change.assignedTo);
    toast(u ? `Assigned to ${u.id === state.user!.id ? "you" : firstName(u.name)}` : "Unassigned");
  }
  if (change.status === "done" && matchMedia("(max-width: 767px)").matches) go({ name: "inbox" });
  else render();
  try {
    const r = await api.setConversation(number, change);
    if (state.thread?.number === number) Object.assign(state.thread, { status: r.status, assignedTo: r.assignedTo });
  } catch (e) {
    toast((e as Error).message);
  }
  await refresh();
}

function ticket(c: ThreadCall, number: string): HTMLElement {
  const card = callCard(c, number);
  // Outgoing on the right, incoming on the left (cards that already come in a tree are set).
  return card.classList.contains("call-tree") ? card : h("div", { class: `call-tree ${c.direction === "outbound" ? "out" : "in"}` }, card) as HTMLElement;
}

function callCard(c: ThreadCall, number: string): HTMLElement {
  // Selena's calls look exactly like everyone's; only while she's still on the line does her live card show.
  if (c.aiTask && !c.recordings.some((r) => r.hasAudio) && (c.status === "ringing" || c.status === "active")) return aiCallCard(c);
  // Who may delete this call's recordings: admins, or the teammate who took or placed the call (same rule as the server).
  const canDeleteRec = isAdmin(); // admins only
  const started = serverTime(c.started_at);
  const answered = serverTime(c.answered_at);
  const ended = serverTime(c.ended_at);
  const talk = answered && ended ? Math.max(0, (ended.getTime() - answered.getTime()) / 1000) : null;
  const vm = c.recordings.find((r) => r.kind === "voicemail");

  let title = c.direction === "outbound" ? "Outgoing call" : "Incoming call";
  let iconName = c.direction === "outbound" ? "outgoing" : "incoming";
  let tone = "";
  let who = c.direction === "outbound" ? `Called by ${c.initiated_by_name ?? "a teammate"}` : c.answered_by_name ? `Answered by ${c.answered_by_name}` : "";
  if (c.status === "canceled") {
    // A teammate hung up before the customer picked up.
    title = "Canceled";
    who = c.aiTask && c.direction === "outbound" ? `${aiName()}'s call was stopped before they answered`
      : `${c.initiated_by_name ? (c.initiated_by === state.user?.id ? "You" : firstName(c.initiated_by_name)) : "The caller"} hung up before they answered`;
  } else if (c.status === "missed") {
    title = c.direction === "outbound" ? "No answer" : "Missed call";
    iconName = c.direction === "outbound" ? "outgoing" : "missed";
    tone = c.direction === "outbound" ? "" : "missed";
    if (c.direction === "inbound") who = "No answer";
  } else if (c.status === "voicemail") {
    title = "Voicemail";
    iconName = "voicemail";
    tone = "missed";
    who = "Left a voicemail";
  } else if (c.status === "info") {
    title = "Incoming call";
    who = "Heard a message in the phone menu";
  } else if (c.status === "ringing") {
    title = c.direction === "outbound" ? "Calling…" : "Incoming call";
    tone = "live";
  } else if (c.status === "active") {
    title = "Ongoing call";
    iconName = "phone";
    tone = "live";
  }

  // The call's length: its talk time, unless its recording is clearly longer (then the call record ended early; the
  // recording is what really happened). Parts of one call add up.
  const recSecs = c.recordings.filter((r) => r.kind === "call" && r.durationSecs != null).reduce((n, r) => n + (r.durationSecs ?? 0), 0) || null;
  const callSecs = talk != null && talk >= 1 ? (recSecs && recSecs > talk + 5 ? recSecs : talk) : recSecs;
  const stubBig = vm?.durationSecs != null ? duration(vm.durationSecs) : callSecs ? duration(callSecs) : "";
  const stubSmall = vm ? "voicemail" : c.status === "active" ? "ongoing" : stubBig ? "duration" : "";

  const withAudio = c.recordings.filter((r) => r.hasAudio);
  // Every call is a bubble (old calls without a recording, missed calls and calls in parts too): outgoing on the right,
  // incoming on the left, with its summary hanging off it on a little branch.
  const isVm = !!vm;
  const calls = c.recordings.filter((x) => x.kind === "call");
  const multi = calls.length > 1;
  const mate = c.answered_by_name ?? (c.direction === "outbound" ? c.initiated_by_name : null);
  const label = isVm ? "Voicemail" : title;
  const canCallBack = (c.status === "missed" || c.status === "voicemail") && c.direction === "inbound" && state.user!.canCall && canText(number) && !(voice.call && !isEnded(voice.call));
  const one = withAudio.length === 1 ? withAudio[0] : null;
  const menu = (e: MouseEvent) => {
    const b = (e.currentTarget as HTMLElement).getBoundingClientRect();
    openPointMenu(b.right, b.bottom + 4, [
      ...(canCallBack ? [{ label: "Call back", iconName: "phone", run: () => void dial(mergedNumbers() && c.external_number ? c.external_number : number) }] : []),
      { label: "Comment", iconName: "note", run: () => commentOn(`call:${c.id}`, callLabel(c)) },
      { label: "Follow-up task", iconName: "checkCircle", run: () => taskOn(`call:${c.id}`) },
      ...(one ? [{ label: "Download", iconName: "download", run: () => downloadRecording(one) }] : []),
      ...(one && canDeleteRec ? [{ label: isVm ? "Delete voicemail" : "Delete recording", iconName: "trash", danger: true, run: () => void confirmDeleteRecording(one) }] : []),
    ]);
  };
  const side = c.direction === "outbound" ? "out" : "in";
  const parts = c.recordings.flatMap((r) => multi && r.kind === "call"
    ? [h("div", { class: "rec-part" }, `Part ${calls.indexOf(r) + 1} of ${calls.length}`), ...recordingParts(r, canDeleteRec, !!one, false, true)]
    : recordingParts(r, canDeleteRec, !!one, false, true));
  // Waiting for the recording: only for a call that was recorded, and not forever (19.10.18: it spun for 20+ minutes
  // when the recording's "saved" message was lost; the server now fetches it from Telnyx within about 5 minutes).
  if (!c.recordings.length && c.status === "completed") {
    const rec = (c as { recording_started?: number }).recording_started;
    const endedMs = c.ended_at ? serverTime(c.ended_at)?.getTime() ?? 0 : 0;
    const waiting = !endedMs || Date.now() - endedMs < 30 * 60_000;
    if (rec !== 0 && waiting) parts.push(h("div", { class: "pending" }, h("span", { class: "spinner" }), "Processing recording…"));
    else if (rec !== 0) parts.push(h("div", { class: "pending muted" }, "No recording came in for this call."));
  }
  const branchy = (n: Node) => n instanceof HTMLElement && (n.matches("details.ai-fold") || n.matches(".pending"));
  for (const n of parts) if (n instanceof HTMLElement && n.matches(".player")) n.classList.add("compact");
  // A summary per part: say which part it is.
  if (multi) {
    let k = 0;
    for (const n of parts) if (n instanceof HTMLElement && n.matches("details.ai-fold")) {
      const t = n.querySelector(".fold-title");
      if (t && calls[k]) t.textContent = `${t.textContent} · part ${k + 1}`;
      k++;
    }
  }
  // The header, like a chat bubble: a round icon, what happened, and who · how long.
  const meId = state.user?.id;
  const whoName = (id: number | null | undefined, name: string | null) => (id != null && id === meId ? "You" : name ? firstName(name) : "");
  const caller = c.aiTask ? aiName() : whoName(c.initiated_by, c.initiated_by_name) || (c.direction === "outbound" && mate ? firstName(mate) : "") || "We";
  const head =
    isVm ? { title: "Voicemail", sub: canCallBack ? `Left at ${clock(started)}` : "Left a voicemail", tone: "vm", iconName: "voicemail" }
    : c.status === "canceled" ? { title: "Canceled", sub: c.direction === "outbound" ? `${caller} hung up first` : "They hung up first", tone: "missed", iconName: c.direction === "outbound" ? "outgoing" : "missed" }
    : c.status === "missed" && c.direction === "inbound" ? { title: "Missed call", sub: canCallBack ? `Missed at ${clock(started)}` : "No answer", tone: "missed", iconName: "missed" }
    : c.status === "missed" ? { title: "No answer", sub: `${caller} called`, tone: "out", iconName: "outgoing" }
    : c.status === "info" ? { title: "Incoming call", sub: who, tone: "in", iconName: "callWaves" }
    : c.status === "ringing" ? { title, sub: c.direction === "outbound" ? `${caller} calling…` : "Ringing…", tone: "live", iconName: c.direction === "outbound" ? "outgoing" : "incoming" }
    : c.status === "active" ? { title: "Ongoing call", sub: mate ? `With ${whoName(c.answered_by ?? c.initiated_by, mate) || firstName(mate)}` : "In progress", tone: "live", iconName: "phone" }
    : c.direction === "outbound" ? { title: "Call ended", sub: [`${caller} called`, stubBig].filter(Boolean).join(" · "), tone: "out", iconName: "callDone" }
    : { title: "Call ended", sub: [c.aiTask ? `${aiName()} answered` : mate ? `${whoName(c.answered_by, mate) || firstName(mate)} answered` : "Answered", stubBig].filter(Boolean).join(" · "), tone: "in", iconName: "callWaves" };
  const hideTime = (isVm || c.status === "missed") && canCallBack;
  const bubble = h("article", { class: `rec-d call-bubble ${head.tone}${isVm ? " vm" : ""}${withAudio.length ? "" : " no-audio"}`, id: `call-${c.id}`, "aria-label": `${label}, ${clock(started)}` },
    h("div", { class: "cb-head" },
      h("span", { class: `cb-icon ${head.tone}`, role: "img", "aria-label": label }, icon(head.iconName, "md")),
      h("span", { class: "cb-text" },
        h("span", { class: "cb-title", title: `${head.title} · ${fullTime(c.started_at)}` }, head.title, hideTime ? null : h("span", { class: "cb-time" }, clock(started))),
        // Selena's (or Steven's) calls: the AI mark right before the name, otherwise just like everyone's.
        head.sub || viaLabel(c.external_number) ? h("span", { class: "cb-sub" }, c.aiTask ? h("span", { class: "cb-ai", "aria-label": "AI agent" }, icon("aiSparkles", "sm")) : null,
          [head.sub, viaLabel(c.external_number)].filter(Boolean).join(" · ")) : null),
      canCallBack ? h("button", { class: "cb-callback", type: "button", "aria-label": "Call back", title: "Call back", onclick: () => void dial(mergedNumbers() && c.external_number ? c.external_number : number) }, icon("phone", "sm")) : null,
      h("button", { class: "icon-btn sm rd-more", type: "button", "aria-label": "More for this call", onclick: menu }, icon("more", "sm"))),
    ...parts.filter((n) => !branchy(n)));
  // What Selena was asked to do: inside her call's summary (open it to see), not on the call itself.
  const fold = c.aiTask ? parts.find((n): n is HTMLDetailsElement => n instanceof HTMLDetailsElement && n.matches("details.ai-fold")) : undefined;
  fold?.querySelector(":scope > summary")?.after(h("div", { class: "ai-ask in-sum" }, h("b", {}, `What ${aiName()} was asked: `), c.aiTask!.request));
  // 4.10.22: the live transcript of a customer call, like Steven's: shown as they talk; after the call, folded
  // (until the recording's own transcript is in, which then takes over).
  const live = liveTranscript(c, number);
  if (live) bubble.append(live);
  const branchParts = parts.filter(branchy);
  return h("div", { class: `call-tree ${side}` }, bubble,
    branchParts.length ? h("div", { class: "tree-branch" }, h("div", { class: "rec-d tree-sum" }, ...branchParts)) : null);
}

/** 4.10.22: who said each line: the customer by first name ("Them" if unknown), a teammate by first name or "You". */
function liveTranscript(c: ThreadCall, number: string): HTMLElement | null {
  const lines = c.liveLog ?? [];
  if (!lines.length || c.aiTask || !isAdmin()) return null; // 5.10.22: admins only
  const on = c.status === "active" || c.status === "ringing";
  if (!on && c.recordings.some((r) => r.kind === "call" && r.transcript)) return null;
  const them = firstName(contactNameOf(number) ?? "") || "Them";
  const label = (l: { who: string; name?: string }) => l.who === "them" ? them : l.name ? (l.name === state.user?.name ? "You" : firstName(l.name)) : "Team";
  const shown = on ? lines.slice(-8) : lines;
  const box = h("div", { class: "ai-lines live-lines", "aria-live": on ? "polite" : "off" },
    ...shown.map((l) => h("div", { class: `ai-line ${l.who}${l.partial ? " partial" : ""}` }, h("span", { class: "who" }, label(l)), h("span", {}, l.text))));
  if (on) {
    requestAnimationFrame(() => (box.scrollTop = box.scrollHeight));
    return h("div", { class: "live-ts" }, h("div", { class: "live-ts-head" }, h("span", { class: "rec-dot" }), "Live transcript"), box);
  }
  return h("details", { class: "ts-fold live-fold" }, h("summary", {}, h("span", { class: "fold-title" }, "Live transcript"), icon("chevron", "sm")), box);
}

/** Summaries and transcripts that are open (kept across screen refreshes). */
const openSummaries = new Set<number>();
/** Transcripts opened under a summary (closed by default, kept across screen refreshes). */
const openTranscripts = new Set<number>();
function transcriptFold(r: Recording): Node {
  return h("details", { class: "ts-fold", open: openTranscripts.has(r.id), ontoggle: (e: Event) => {
    if ((e.currentTarget as HTMLDetailsElement).open) openTranscripts.add(r.id);
    else openTranscripts.delete(r.id);
  } },
    h("summary", {}, h("span", { class: "fold-icon ticket", "aria-hidden": "true" }, ticketIcon()), h("span", { class: "fold-title" }, "Transcript"), icon("chevron", "sm")),
    transcriptView(r));
}
/** Transcript search text per recording. */
const transcriptQuery = new Map<number, string>();

function recordingParts(r: Recording, canDelete = false, hideMore = false, results = false, tree = false): Node[] {
  const parts: Node[] = [];
  if (r.hasAudio) parts.push(player(r, canDelete, hideMore));
  const hasText = !!r.summary || r.transcript != null;
  if (hasText) {
    // Collapsed by default: a one-line peek at the summary. Open it for the full summary and the transcript,
    // where every line has its time and plays that moment when tapped.
    const short = r.kind === "voicemail" && !r.summary && (r.transcript ?? "").length < 280;
    const open = openSummaries.has(r.id) || short;
    const peek = r.summary ? summaryPeek(r.summary) : previewText(r.transcript ?? "");
    parts.push(h("details", { class: "ai-fold", open, ontoggle: (e: Event) => {
      const el = e.currentTarget as HTMLDetailsElement;
      if (el.open) openSummaries.add(r.id);
      else openSummaries.delete(r.id);
    } },
      h("summary", {}, tree ? h("span", { class: "fold-icon spark", "aria-hidden": "true" }, icon("aiSparkles", "sm")) : h("span", { class: "fold-icon ticket", "aria-hidden": "true" }, ticketIcon()),
        h("span", { class: "fold-title" }, r.summary
          ? (results ? (tree ? "Call results" : "Results") : tree ? (r.kind === "voicemail" ? "Voicemail summary" : "Call summary") : "Summary")
          : r.kind === "voicemail" ? "Voicemail" : "Transcript"),
        h("span", { class: "fold-peek" }, peek), icon("chevron", "sm")),
      r.summary ? summaryBlock(r.summary) : null,
      // With a summary, the transcript stays folded too (tap "Transcript" to open it); on its own, it's the content.
      r.transcript != null ? (r.summary ? transcriptFold(r) : transcriptView(r)) : null));
  } else if (r.aiStatus === "pending") {
    parts.push(h("div", { class: "pending" }, h("span", { class: "spinner" }), "Generating summary…"));
  } else if (r.aiStatus === "failed") {
    parts.push(h("div", { class: "pending" }, "Transcript unavailable"));
  }
  if (hasText && r.aiStatus === "pending" && !r.summary) parts.push(h("div", { class: "pending" }, h("span", { class: "spinner" }), "Generating summary…"));
  return parts;
}

function summaryPeek(text: string): string {
  const { points, notes } = parseSummary(text);
  return points[0] ?? notes[0] ?? "";
}

type Seg = { s: number; e: number; t: string; sp?: string };
/**
 * Transcript lines with times. Real timestamps when the transcription service gave them; otherwise each line's
 * time is estimated from where it falls in the text (marked "~").
 */
function segmentsOf(r: Recording): { segs: Seg[]; estimated: boolean } {
  // Saved lines whose times are all 0:00 (the transcription gave none): estimate them instead, like untimed text.
  const timed = r.segments?.length && !(r.segments.length > 1 && r.segments.every((x) => !x.s && !x.e));
  if (timed) return { segs: r.segments!, estimated: false };
  const text = (r.transcript ?? "").trim() || (r.segments ?? []).map((x) => x.t).join("\n");
  if (!text) return { segs: [], estimated: true };
  // "Name: words" lines (imported / labeled transcripts) stay whole; plain text splits into sentences.
  const lines = text.includes("\n") ? text.split("\n").map((l) => l.trim()).filter(Boolean) : text.match(/[^.!?]+[.!?]+["')\]]*|[^.!?]+$/g)?.map((x) => x.trim()).filter(Boolean) ?? [text];
  const total = r.durationSecs ?? 0;
  const chars = lines.reduce((n, l) => n + l.length, 0) || 1;
  let at = 0;
  return {
    estimated: true,
    segs: lines.map((l) => {
      const m = /^([^:]{1,40}):\s+(.*)$/.exec(l);
      const len = (l.length / chars) * total;
      const seg = { s: at, e: at + len, t: m ? m[2] : l, sp: m ? m[1] : undefined };
      at += len;
      return seg;
    }),
  };
}

function seekAndPlay(r: Recording, secs: number) {
  const audio = audioFor(r);
  for (const [id, other] of players) if (id !== r.id) other.pause();
  const go = () => {
    audio.currentTime = Math.max(0, secs - 0.3);
    audio.play().catch(() => toast("Couldn't play this recording"));
  };
  if (audio.readyState >= 1) go();
  else {
    audio.preload = "metadata";
    audio.addEventListener("loadedmetadata", go, { once: true });
    audio.load();
  }
}

/** Recordings whose transcript search is open (the search icon was tapped). */
const tsSearchOpen = new Set<number>();
function transcriptView(r: Recording): Node {
  const { segs, estimated } = segmentsOf(r);
  const q = (transcriptQuery.get(r.id) ?? "").trim().toLowerCase();
  const canSeek = r.hasAudio && (!estimated || !!r.durationSecs);
  let hits = 0;
  const mark = (text: string): Node[] => {
    if (!q) return [document.createTextNode(text)];
    const out: Node[] = [];
    let i = 0;
    const low = text.toLowerCase();
    for (let j = low.indexOf(q); j >= 0; j = low.indexOf(q, j + q.length)) {
      out.push(document.createTextNode(text.slice(i, j)), h("mark", {}, text.slice(j, j + q.length)));
      i = j + q.length;
      hits++;
    }
    out.push(document.createTextNode(text.slice(i)));
    return out;
  };
  const rows = segs.map((sg, i) =>
    h(canSeek ? "button" : "div", {
      class: `ts-line${q && !sg.t.toLowerCase().includes(q) && !(sg.sp ?? "").toLowerCase().includes(q) ? " dim" : ""}`,
      "data-s": sg.s, "data-e": sg.e, "data-i": i,
      title: canSeek ? "Play from here" : undefined,
      onclick: canSeek ? () => seekAndPlay(r, sg.s) : undefined,
    },
      h("span", { class: "ts-time" }, `${estimated ? "~" : ""}${duration(sg.s)}`),
      h("span", { class: "ts-text" }, sg.sp ? h("strong", {}, `${sg.sp}: `) : null, ...mark(sg.t))));
  // Search: just an icon in the transcript's header; tapping it opens a small search field in that same line
  // (no wide search bar across the transcript any more).
  const searchOpen = tsSearchOpen.has(r.id) || !!q;
  const closeSearch = () => {
    tsSearchOpen.delete(r.id);
    transcriptQuery.delete(r.id);
    render();
  };
  const search = h("div", { class: "ts-search ts-find" },
    icon("search", "sm"),
    h("input", { id: `ts-q-${r.id}`, type: "search", placeholder: "Search this call", value: transcriptQuery.get(r.id) ?? "", autocomplete: "off",
      oninput: (e: Event) => {
        transcriptQuery.set(r.id, (e.target as HTMLInputElement).value);
        renderSoon();
      },
      onkeydown: (e: KeyboardEvent) => {
        if (e.key !== "Enter") return;
        // Enter jumps to the next match (and plays from there).
        const box = (e.target as HTMLElement).closest(".transcript-box");
        const matches = [...(box?.querySelectorAll<HTMLElement>(".ts-line:not(.dim)") ?? [])];
        if (!matches.length) return;
        const cur = Number(box?.getAttribute("data-cursor") ?? -1);
        const next = matches.find((m) => Number(m.dataset.i) > cur) ?? matches[0];
        box?.setAttribute("data-cursor", next.dataset.i ?? "0");
        next.scrollIntoView({ block: "nearest" });
        next.classList.add("flash");
        setTimeout(() => next.classList.remove("flash"), 1200);
      } }),
    q ? h("span", { class: "ts-count" }, hits ? `${hits} match${hits === 1 ? "" : "es"}` : "No matches") : null,
    h("button", { class: "ts-find-close", type: "button", "aria-label": "Close search", onclick: closeSearch }, icon("close", "sm")));
  const searchBtn = h("button", { class: "ts-find-btn", type: "button", "aria-label": "Search this call", title: "Search this call", onclick: () => {
    tsSearchOpen.add(r.id);
    render();
    requestAnimationFrame(() => (document.getElementById(`ts-q-${r.id}`) as HTMLInputElement | null)?.focus());
  } }, h("span", { class: "ts-find-label" }, "Search transcript"), icon("search", "sm"));
  return h("div", { class: "transcript-box", "data-rec": r.id },
    // The same ticket icon as the summary line, so the transcript reads as part of the same record.
    h("div", { class: "ts-head" }, h("span", { class: "ts-title" }, h("span", { class: "fold-icon ticket", "aria-hidden": "true" }, ticketIcon()), "Transcript"),
      searchOpen && segs.length > 3
        ? search
        : h("span", { class: "ts-head-end" }, segs.length > 3 ? searchBtn : null)),
    segs.length ? h("div", { class: "ts-lines" }, ...rows) : h("p", { class: "sub" }, "No speech detected."),
    estimated && segs.length > 1 && canSeek ? h("div", { class: "ts-note" }, "~ times are estimated for this recording.") : null);
}

/**
 * A summary's parts: the key points, other lines ("Not found: …"), and the next steps ("Name: action"). Reads the
 * current format ("Next steps:" then "• Name: action" lines) and the older one ("Next: a; b", also Quo's imports).
 */
function parseSummary(text: string): { points: string[]; notes: string[]; steps: string[] } {
  const points: string[] = [], notes: string[] = [], steps: string[] = [];
  let inSteps = false;
  for (const raw of text.split("\n")) {
    const l = raw.trim();
    if (!l) continue;
    const header = /^(?:next steps|action items)\s*:?\s*(.*)$/i.exec(l);
    const old = /^next:\s*(.*)$/i.exec(l);
    if (header || old) {
      inSteps = true;
      const inline = (header ?? old)![1].trim();
      if (inline && !/^none\.?$/i.test(inline)) steps.push(...inline.split(/;\s+/).map((x) => x.trim()).filter(Boolean));
      continue;
    }
    const item = l.replace(/^•\s*/, "");
    if (inSteps && l.startsWith("•")) steps.push(item);
    else if (l.startsWith("•")) points.push(item);
    else notes.push(l);
  }
  return { points, notes, steps: steps.map((x) => x.charAt(0).toUpperCase() + x.slice(1)) };
}
function summaryBlock(text: string) {
  const { points, notes, steps } = parseSummary(text);
  // "Nettie: send the mileage numbers": the owner in bold, like Quo.
  const step = (x: string) => {
    const m = /^([^:]{1,40}):\s+(.+)$/.exec(x);
    return m && m[1].split(/\s+/).length <= 4 ? h("li", {}, h("b", {}, `${m[1]}:`), ` ${m[2]}`) : h("li", {}, x);
  };
  return h("div", { class: "summary" },
    h("div", { class: "summary-head" }, "Summary"),
    points.length ? h("ul", { class: "summary-points" }, ...points.map((b) => h("li", {}, b))) : null,
    ...notes.map((l) => h("div", { class: "summary-note" }, l)),
    steps.length ? h("div", { class: "next" }, h("div", { class: "next-label" }, "Next steps"), h("ul", { class: "summary-steps" }, ...steps.map(step))) : null);
}

// One audio element per recording survives re-renders, so playback isn't interrupted by live updates.
const players = new Map<number, HTMLAudioElement>();
const SPEEDS = [1, 1.25, 1.5, 2, 2.5, 0.5, 0.75];
function playbackRate(): number {
  try {
    const v = Number(localStorage.getItem("joy.playbackRate"));
    return SPEEDS.includes(v) ? v : 1;
  } catch {
    return 1;
  }
}
function audioFor(r: Recording): HTMLAudioElement {
  let audio = players.get(r.id);
  if (!audio) {
    audio = new Audio((r as Recording & { src?: string }).src ?? `/api/recordings/${r.id}/audio`);
    audio.preload = "none";
    audio.playbackRate = playbackRate();
    audio.defaultPlaybackRate = audio.playbackRate;
    players.set(r.id, audio);
  }
  return audio;
}

/** Saves the recording (long-press / hard press on a phone, right-click on a computer, or the menu). */
function downloadRecording(r: Recording) {
  const a = document.createElement("a");
  a.href = `${(r as Recording & { src?: string }).src ?? `/api/recordings/${r.id}/audio`}?download=1`;
  a.download = "";
  document.body.append(a);
  a.click();
  a.remove();
  toast("Downloading the recording…");
}

/** Asks, then deletes a recording (the audio, transcript and summary; the call itself stays). */
async function confirmDeleteRecording(r: Recording) {
  const what = r.kind === "voicemail" ? "voicemail" : "call recording";
  const ok = await glassDialog({ title: `Delete this ${what}?`, message: "The audio, transcript and summary are deleted for everyone. The call stays in the conversation. This can't be undone.", confirm: "Delete", danger: true });
  if (!ok) return;
  try {
    players.get(r.id)?.pause();
    players.delete(r.id);
    await api.deleteRecording(r.id);
    toast(r.kind === "voicemail" ? "Voicemail deleted" : "Recording deleted");
    void refresh();
  } catch (e) {
    toast((e as Error).message);
  }
}

/**
 * A recording: play / pause, a bar you can drag to any moment (or tap), the time, speed, and a ⋯ menu
 * (Download, Delete). Compact: one slim row.
 */
/** Play/pause for each recording's button, by recording id (the newest player for that recording). */
const playToggles = new Map<number, () => void>();
{
  // One press = one toggle, even if the player is redrawn while the finger / mouse is down.
  let down: { rec: number; x: number; y: number; id: number } | null = null;
  document.addEventListener("pointerdown", (e) => {
    const b = (e.target as HTMLElement | null)?.closest?.<HTMLElement>(".player .play[data-rec]");
    down = b && e.button === 0 ? { rec: Number(b.dataset.rec), x: e.clientX, y: e.clientY, id: e.pointerId } : null;
  }, true);
  document.addEventListener("pointerup", (e) => {
    const d = down;
    down = null;
    if (!d || d.id !== e.pointerId || Math.hypot(e.clientX - d.x, e.clientY - d.y) > 16) return; // a scroll, not a tap
    playToggles.get(d.rec)?.();
  }, true);
  document.addEventListener("pointercancel", () => (down = null), true);
}

function player(r: Recording, canDelete = false, hideMore = false) {
  const fill = h("span", { class: "fill" });
  const thumb = h("span", { class: "thumb" });
  const now = h("span", { class: "clock now" }, "0:00");
  const total = h("span", { class: "clock total" }, r.durationSecs != null ? duration(r.durationSecs) : "");
  const btn = h("button", { class: "play", type: "button", "aria-label": "Play recording" }, icon("playFill", "sm"));
  const track = h("span", { class: "scrub", role: "slider", "aria-label": "Recording position", tabindex: 0, "aria-valuemin": 0, "aria-valuemax": r.durationSecs ?? 0, "aria-valuenow": 0 },
    h("span", { class: "bar-rail" }, fill), thumb);
  const rate = playbackRate();
  const speed = h("button", { class: "speed", type: "button", "aria-label": "Playback speed", title: "Playback speed" }, `${rate}×`);
  const more = h("button", { class: "icon-btn sm rec-more", type: "button", "aria-label": "Recording options", title: "More" }, icon("more", "sm"));

  const highlight = (audio: HTMLAudioElement) => {
    const box = document.querySelector<HTMLElement>(`.transcript-box[data-rec="${r.id}"]`);
    if (!box) return;
    const t = audio.currentTime;
    for (const el of box.querySelectorAll<HTMLElement>(".ts-line")) {
      const on = !audio.paused && t >= Number(el.dataset.s) && t < Number(el.dataset.e);
      el.classList.toggle("now", on);
    }
  };
  const totalOf = (audio?: HTMLAudioElement) => (audio && Number.isFinite(audio.duration) ? audio.duration : r.durationSecs ?? 0);
  let dragging = false;
  const paint = (secs: number, len: number) => {
    const pct = len ? Math.min(100, Math.max(0, (secs / len) * 100)) : 0;
    fill.style.width = `${pct}%`;
    thumb.style.left = `${pct}%`;
    now.textContent = duration(secs);
    // The compact player (call bubbles) shows one time on the right: the length, counting down while it plays.
    total.textContent = el.classList.contains("compact") && secs > 0 ? duration(Math.max(0, len - secs)) : duration(len);
    track.setAttribute("aria-valuenow", String(Math.round(secs)));
  };
  // The play/pause icon is swapped only when it changes. (It used to be redrawn on every time update, ~4×/second: a
  // press that landed on the icon while it was being replaced was dropped by the browser, so Pause sometimes failed.)
  let shownPaused = true;
  const sync = (audio: HTMLAudioElement) => {
    if (!dragging) paint(audio.currentTime, totalOf(audio));
    if (audio.paused !== shownPaused) {
      shownPaused = audio.paused;
      btn.replaceChildren(icon(audio.paused ? "playFill" : "pauseFill", "sm"));
      btn.setAttribute("aria-label", audio.paused ? "Play recording" : "Pause recording");
    }
    el.classList.toggle("playing", !audio.paused);
    highlight(audio);
  };
  const wire = (audio: HTMLAudioElement) => {
    audio.ontimeupdate = audio.onplay = audio.onpause = audio.onended = audio.onloadedmetadata = () => sync(audio);
  };
  const toggle = () => {
    const audio = audioFor(r);
    wire(audio);
    for (const [id, other] of players) if (id !== r.id) other.pause();
    if (audio.paused) audio.play().catch(() => toast("Couldn't play this recording"));
    else audio.pause();
  };
  // Play / pause on the press itself (see playToggles below): a live update can redraw the player between press and
  // release, and then the browser drops the "click", so Pause sometimes did nothing.
  btn.dataset.rec = String(r.id);
  playToggles.set(r.id, toggle);
  btn.addEventListener("click", (e) => {
    if (e.detail === 0) toggle(); // keyboard (Enter / Space); taps and clicks are handled on press
  });
  speed.addEventListener("click", () => {
    const next = SPEEDS[(SPEEDS.indexOf(playbackRate()) + 1) % SPEEDS.length];
    try {
      localStorage.setItem("joy.playbackRate", String(next));
    } catch {
      // remembered for this page only
    }
    for (const a of players.values()) {
      a.playbackRate = next;
      a.defaultPlaybackRate = next;
    }
    for (const x of document.querySelectorAll<HTMLElement>(".player .speed")) x.textContent = `${next}×`;
  });
  more.addEventListener("click", (e) => {
    const rect = more.getBoundingClientRect();
    openPointMenu(rect.right, rect.bottom + 4, [
      { label: "Download", iconName: "download", run: () => downloadRecording(r) },
      ...(canDelete ? [{ label: r.kind === "voicemail" ? "Delete voicemail" : "Delete recording", iconName: "trash", danger: true, run: () => void confirmDeleteRecording(r) }] : []),
    ]);
  });

  // Drag (or tap) anywhere on the bar to jump there. Audio moves when you let go; the time shows while dragging.
  const fracAt = (clientX: number) => {
    const rect = track.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  };
  const seekTo = (frac: number) => {
    const audio = audioFor(r);
    wire(audio);
    const go = () => {
      audio.currentTime = frac * audio.duration;
      sync(audio);
    };
    if (Number.isFinite(audio.duration)) go();
    else {
      audio.preload = "metadata";
      audio.addEventListener("loadedmetadata", go, { once: true });
      audio.load();
    }
  };
  track.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    track.setPointerCapture(e.pointerId);
    dragging = true;
    el.classList.add("scrubbing");
    paint(fracAt(e.clientX) * totalOf(players.get(r.id)), totalOf(players.get(r.id)));
  });
  track.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    paint(fracAt(e.clientX) * totalOf(players.get(r.id)), totalOf(players.get(r.id)));
  });
  const release = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    el.classList.remove("scrubbing");
    seekTo(fracAt(e.clientX));
  };
  track.addEventListener("pointerup", release);
  track.addEventListener("pointercancel", () => {
    dragging = false;
    el.classList.remove("scrubbing");
    const a = players.get(r.id);
    if (a) sync(a);
  });
  track.addEventListener("keydown", (e) => {
    const a = audioFor(r);
    wire(a);
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const len = totalOf(a);
      if (len) seekTo(Math.min(1, Math.max(0, (a.currentTime + (e.key === "ArrowRight" ? 10 : -10)) / len)));
    }
  });

  // Capsule: [▶] 0:00 ━━━●━━ 0:38 [1×] (⋯ sits on the label line above when there's only one recording)
  const el = h("div", { class: "player capsule" }, btn, now, h("div", { class: "player-mid" }, track), total, speed, hideMore ? null : more);
  const existing = players.get(r.id);
  if (existing) {
    sync(existing);
    wire(existing);
  } else paint(0, r.durationSecs ?? 0);
  el.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    if ((e as PointerEvent).pointerType && (e as PointerEvent).pointerType !== "mouse") return;
    downloadRecording(r);
  });
  return el;
}

const TEXT_STATUS: Record<string, string> = {
  sending: "Sending…", queued: "Sending…", sent: "Sent", delivered: "Delivered", received: "",
  failed: "Not sent", sending_failed: "Not sent", delivery_failed: "Not delivered", delivery_unconfirmed: "Sent", expired: "Not delivered",
};

/**
 * Pictures and documents in a text or chat message. Tapping one opens it full screen over the conversation (with ×,
 * swipe down or Back to close), never as a separate page: the iPhone app had no way back from that page.
 */
const isDoc = (type: string) => Object.values(DOC_TYPES).includes(type);
/** The little badge on a file chip: PDF, DOC, XLS, CSV, TXT. */
function fileBadge(type: string): string {
  if (type === "application/pdf") return "PDF";
  if (type === DOC_TYPES.docx) return "DOC";
  if (type === DOC_TYPES.xlsx) return "XLS";
  if (type === DOC_TYPES.csv) return "CSV";
  if (type === DOC_TYPES.txt) return "TXT";
  return "FILE";
}
function mediaList(media: Attachment[]) {
  const view = (i: number) => (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return; // computer: ⌘-click still opens a new tab
    e.preventDefault();
    openViewer(media, i);
  };
  return media.map((x, i) => /^image\//.test(x.type)
    ? h("a", { href: x.url, onclick: view(i) }, h("img", { class: "mms", src: (x as Attachment & { preview?: string }).preview ?? x.url, alt: x.name ?? "Picture", loading: "lazy", width: picSizes.get((x as Attachment & { preview?: string }).preview ?? x.url)?.[0], height: picSizes.get((x as Attachment & { preview?: string }).preview ?? x.url)?.[1], onload: keepAtBottom }))
    : isDoc(x.type)
    // Word, Excel, CSV, text: downloaded and opened in their own app (the viewer can't show them).
    ? h("a", { class: "attachment", href: x.url, download: x.name ?? "" }, h("span", { class: "pdf-badge" }, fileBadge(x.type)), x.name ?? "Document")
    : h("a", { class: "attachment", href: x.url, onclick: view(i) },
        x.type === "application/pdf" ? h("span", { class: "pdf-badge" }, "PDF") : icon("download", "sm"),
        x.name ?? (x.type === "application/pdf" ? "PDF document" : x.type.startsWith("video/") ? "Video" : x.type.startsWith("audio/") ? "Voice note" : "Attachment")));
}

/**
 * 9.11.2 "merge the cards after merging contacts: one fluid conversation (can have which number called)": a merged
 * contact's numbers are one conversation; each text and call says which number it was, and you choose which number
 * to answer on (it starts on the one they last called or texted from).
 */
const replyVia = new Map<string, string>();
function mergedNumbers(): string[] | null {
  const t = state.thread;
  return t?.numbers && t.numbers.length > 1 ? t.numbers : null;
}
function replyNumberFor(number: string): string {
  const nums = mergedNumbers();
  if (!nums || state.thread?.number !== number) return number;
  const picked = replyVia.get(number);
  return picked && nums.includes(picked) ? picked : state.thread?.replyNumber && nums.includes(state.thread.replyNumber) ? state.thread.replyNumber : number;
}
/** "· (646) 491-8880" after a text or call of a merged contact's conversation. */
function viaLabel(n: string | undefined | null): string {
  return n && mergedNumbers() ? phone(n) : "";
}
function replyViaBar(number: string): HTMLElement | null {
  const nums = mergedNumbers();
  if (!nums || !canText(number)) return null;
  const cur = replyNumberFor(number);
  return h("div", { class: "via-bar", role: "group", "aria-label": "Reply on which number" },
    h("span", { class: "muted" }, "Reply to"),
    ...nums.map((n) => h("button", { class: `via-chip${n === cur ? " on" : ""}`, type: "button", "aria-pressed": String(n === cur), onclick: () => (replyVia.set(number, n), render()) }, phone(n))));
}
/** Phones (1.10.20 calmer): the text whose React / Comment / Task buttons are showing (tap a text to show them). */
let actsOpenFor: number | null = null;
function textBubble(m: Text) {
  const out = m.direction === "outbound";
  const bad = /fail|expired/.test(m.status);
  const who = out ? (m.user_id === state.user?.id ? "You" : firstName(m.user_name) || "Team")
    : m.sender ? memberNames.get(m.sender) ?? phone(m.sender) : ""; // group texts: who wrote it
  // RCS read receipt: "Read 10:42 AM" once the customer opened it.
  const status = out ? (m.read_at ? `Read ${clock(serverTime(m.read_at))}` : TEXT_STATUS[m.status] ?? m.status) : "";
  const actsOpen = actsOpenFor === m.id || state.picker === `react:sms:${m.id}`;
  return h("div", { class: `${out ? "bubble-row mine text" : "bubble-row text"}${actsOpen ? " acts-open" : ""}`, id: `s-${m.id}` },
    !out && m.sender ? h("div", { class: "group-sender" }, memberNames.get(m.sender) ?? phone(m.sender)) : null,
    h("div", { class: "bubble", onclick: (e: MouseEvent) => {
      // A tap on the text itself (not a link, picture or file) shows or hides its buttons on phones.
      if (!window.matchMedia("(hover: none)").matches || (e.target as HTMLElement).closest("a, button, img") || window.getSelection()?.toString()) return;
      actsOpenFor = actsOpenFor === m.id ? null : m.id;
      render();
    } },
      ...withLinks(m.body ?? ""), // 1.10.29: links in texts are clickable (they showed as plain text)
      ...mediaList(m.media)),
    // Reactions sit right under the bubble, just touching it.
    reactionRow("sms", m.id, m.reactions),
    h("div", { class: bad ? "bubble-meta bad item-actions" : "bubble-meta item-actions" },
      h("span", {}, [who, viaLabel((m as { external_number?: string }).external_number), clock(serverTime(m.created_at)), bad ? m.error || status : status].filter(Boolean).join(" · ")),
      out && bad ? h("button", { class: "mini-act retry", onclick: () => void retryText(m.id) }, icon("retry", "sm"), h("span", {}, "Retry")) : null,
      m.id < 0 ? null : reactButton("sms", m.id),
      m.id < 0 ? null : h("button", { class: "mini-act", "aria-label": "Internal comment on this text", onclick: () => commentOn(`sms:${m.id}`, `text “${m.body.slice(0, 40)}${m.body.length > 40 ? "…" : ""}”`) },
        icon("note", "sm"), h("span", {}, "Comment")),
      m.id < 0 ? null : h("button", { class: "mini-act", "aria-label": "Add a follow-up task for this text", onclick: () => taskOn(`sms:${m.id}`) },
        icon("checkCircle", "sm"), h("span", {}, "Task"))));
}

/** A picture finished loading: if you're reading the newest messages, keep the newest one in view. */
/** Each picture's size once loaded: a refresh redraws pictures, and a fresh <img> is 0 px tall until it loads (the thread jumped). */
const picSizes = new Map<string, [number, number]>();
function keepAtBottom(e: Event) {
  const img = e.target as HTMLImageElement;
  const src = img.getAttribute("src") ?? "";
  const seen = picSizes.has(src);
  picSizes.set(src, [img.naturalWidth, img.naturalHeight]);
  if (seen || fingersOnScreen()) return; // redrawn at its size already, or you're scrolling
  const tl = img.closest<HTMLElement>('[data-scroll="timeline"]');
  if (tl && tl.scrollHeight - tl.scrollTop - tl.clientHeight < 420) tl.scrollTop = tl.scrollHeight;
}

/** Scheduled texts being sent now (a second tap mustn't send them twice). */
const scheduledBusy = new Set<number>();
/** A text waiting to go out later: dashed bubble with its time, Send now and Cancel. */
function scheduledBubble(sc: ScheduledText) {
  const at = new Date(sc.sendAt);
  return h("div", { class: "bubble-row mine text scheduled" },
    h("div", { class: "bubble" }, ...withLinks(sc.body ?? ""),
      ...sc.media.map((x) => h("span", { class: "attachment" }, x.type === "application/pdf" ? h("span", { class: "pdf-badge" }, "PDF") : icon("download", "sm"), x.name ?? "Attachment"))),
    h("div", { class: "bubble-meta item-actions" },
      icon("clock", "sm"),
      h("span", {}, `Scheduled · ${fmtDate(at, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}${sc.userName && sc.userId !== state.user?.id ? ` · by ${firstName(sc.userName)}` : ""}`),
      h("button", { class: "mini-act show", onclick: async () => {
        if (scheduledBusy.has(sc.id)) return;
        scheduledBusy.add(sc.id);
        // 7.10.30: send first, then take it off the schedule (it was canceled first: a refused send lost the text).
        try {
          await api.text(sc.number, sc.body, sc.media);
          try {
            await api.cancelScheduled(sc.id);
            toast("Sent");
          } catch (e) {
            toast(`Sent, but it's still scheduled too: ${(e as Error).message}`);
          }
        } catch (e) {
          toast((e as Error).message);
        } finally {
          scheduledBusy.delete(sc.id);
        }
        await refresh();
      } }, h("span", {}, "Send now")),
      h("button", { class: "mini-act show", onclick: () => editScheduled(sc) }, h("span", {}, "Edit")),
      h("button", { class: "mini-act show", onclick: async () => {
        // 7.10.30: "Canceled" only when it was (it showed after a failed cancel too).
        try {
          await api.cancelScheduled(sc.id);
          toast("Canceled");
        } catch (e) {
          toast((e as Error).message);
        }
        await refresh();
      } }, h("span", {}, "Cancel"))));
}

async function retryText(id: number) {
  try {
    await api.retryText(id);
    await refresh();
  } catch (e) {
    toast((e as Error).message);
  }
}

// ---------------------------------------------------------------- emoji

const QUICK_EMOJI = ["👍", "❤️", "😂", "🎉", "🙏", "👀", "✅", "🔥"];
const EMOJI_SETS: [string, string[]][] = [
  ["Smileys", ["😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "😉", "😊", "😇", "🥰", "😍", "🤩", "😘", "😋", "😛", "😜", "🤪", "🤗", "🤭", "🤫", "🤔", "😐", "😑", "😶", "🙄", "😏", "😬", "😌", "😴", "😷", "🤒", "🥳", "😎", "🤓", "😕", "😟", "🙁", "😮", "😯", "😲", "😳", "🥺", "😢", "😭", "😱", "😤", "😡", "🤯", "😵", "🫠", "🫡", "🥹"]],
  ["Gestures", ["👍", "👎", "👌", "✌️", "🤞", "🤟", "🤙", "👋", "👏", "🙌", "🙏", "🤝", "💪", "👀", "🫶", "👆", "👇", "👉", "👈", "✋", "🤚", "🖐️", "☝️", "🫵", "💅", "🧠"]],
  ["Hearts", ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💔", "❣️", "💕", "💖", "💗", "💯", "🔥", "✨", "⭐", "🌟", "💫", "🎉", "🎊", "🥂", "🍾", "🎂", "🎁", "🏆"]],
  ["Travel", ["✈️", "🛫", "🛬", "🧳", "🛂", "🛃", "🗺️", "🧭", "🌍", "🌎", "🌏", "🏝️", "🏖️", "🌴", "⛱️", "🌊", "☀️", "🌅", "🏔️", "⛰️", "🏕️", "🏨", "🏰", "🗽", "🗼", "🎡", "🚢", "🛳️", "⛴️", "🚆", "🚗", "🚕", "🚌", "🚁", "🍹", "🍸", "🍷", "🍽️", "🍕", "🌮", "🍣", "☕", "📸", "🎟️", "💍", "💒"]],
  ["Work", ["📞", "☎️", "📱", "💬", "📧", "📝", "📅", "📆", "⏰", "⏳", "📌", "📎", "📄", "📋", "✏️", "💰", "💳", "🧾", "💵", "🔑", "🔒", "📣", "🔔", "🚀", "💡", "🗂️"]],
  ["Symbols", ["✅", "☑️", "✔️", "❌", "❎", "⚠️", "🚫", "❓", "❗", "‼️", "⁉️", "➕", "➖", "🔴", "🟠", "🟡", "🟢", "🔵", "🟣", "⚫", "⚪", "🆗", "🆕", "🆙", "🔝", "⬆️", "⬇️", "➡️", "⬅️", "🔁"]],
];

/** How often you've picked each emoji on this device (blended with your server history for the top row). */
const EMOJI_USE_KEY = "joy.emojiUse";
function localEmojiUse(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(EMOJI_USE_KEY) ?? "{}") ?? {};
  } catch {
    return {};
  }
}
function noteEmojiUse(e: string) {
  try {
    const use = localEmojiUse();
    use[e] = (use[e] ?? 0) + 1;
    localStorage.setItem(EMOJI_USE_KEY, JSON.stringify(use));
  } catch {
    // storage unavailable
  }
  // Move it up right away in the in-memory list too.
  state.topEmoji = [e, ...state.topEmoji.filter((x) => x !== e)].slice(0, 16);
}
let emojiLoaded = false;
let emojiMoreFor: string | null = null;
async function loadTopEmoji() {
  if (emojiLoaded) return;
  emojiLoaded = true;
  try {
    const { emoji } = await api.myEmoji();
    const mine = new Set(state.topEmoji);
    state.topEmoji = [...state.topEmoji, ...emoji.filter((e) => !mine.has(e))].slice(0, 16);
  } catch {
    emojiLoaded = false;
  }
}
/** Your 8 most-used emoji (this device's picks first, then what you use across the app, then the defaults). */
function frequentEmoji(): string[] {
  const local = Object.entries(localEmojiUse()).sort((a, b) => b[1] - a[1]).map(([e]) => e);
  const out: string[] = [];
  for (const e of [...state.topEmoji, ...local, ...QUICK_EMOJI]) {
    if (!out.includes(e)) out.push(e);
    if (out.length === 8) break;
  }
  return out;
}

/** Emoji picker: your 8 most-used first; "More emoji" opens the full set. */
function emojiPicker(onPick: (e: string) => void, _wide = false) {
  void loadTopEmoji();
  function emojiBtn(e: string) {
    return h("button", { class: "emoji", type: "button", "aria-label": e, onmousedown: (ev: Event) => ev.preventDefault(), onclick: (ev: MouseEvent) => {
      ev.stopPropagation();
      noteEmojiUse(e);
      onPick(e);
    } }, e);
  }
  const all = h("div", { class: "emoji-all" });
  const pop = h("div", { class: "emoji-pop", role: "dialog", "aria-label": "Emoji", onclick: (ev: MouseEvent) => ev.stopPropagation() },
    h("div", { class: "emoji-head" }, "Frequently used"),
    h("div", { class: "emoji-grid" }, ...frequentEmoji().map(emojiBtn)),
    all);
  const expand = () => {
    pop.classList.add("expanded");
    all.append(...EMOJI_SETS.flatMap(([title, list]) => [h("div", { class: "emoji-head" }, title), h("div", { class: "emoji-grid" }, ...list.map(emojiBtn))]));
  };
  // Stays open on the full set if the screen refreshes while you're browsing.
  if (emojiMoreFor && emojiMoreFor === state.picker) {
    expand();
    return pop;
  }
  const more = h("button", { class: "emoji-more", type: "button", onmousedown: (ev: Event) => ev.preventDefault(), onclick: (ev: MouseEvent) => {
    ev.stopPropagation();
    emojiMoreFor = state.picker;
    more.remove();
    expand();
    placePopovers();
  } }, "More emoji");
  pop.append(more);
  return pop;
}

function reactButton(kind: "sms" | "comment" | "chat" | "note", id: number) {
  const key = `react:${kind}:${id}`;
  const open = state.picker === key;
  return h("span", { class: "emoji-wrap" },
    h("button", { class: "mini-act", "aria-label": "Add reaction", "aria-expanded": String(open), onclick: (e: MouseEvent) => {
      e.stopPropagation();
      state.picker = open ? null : key;
      render();
    } }, h("span", { class: "smile" }, icon("smilePlus", "sm")), h("span", { class: "act-label" }, "React")),
    open ? emojiPicker((emoji) => void react(kind, id, emoji)) : null);
}

async function react(kind: "sms" | "comment" | "chat" | "note", id: number, emoji: string) {
  state.picker = null;
  // Show the reaction instantly; the server's answer replaces it a moment later.
  const target = kind === "chat" ? state.chat.find((m) => m.id === id)
    : kind === "sms" ? state.thread?.messages.find((m) => m.id === id)
    : kind === "comment" ? state.thread?.comments.find((m) => m.id === id)
    : state.contact?.notes.find((n) => n.id === id);
  const me = state.user!;
  if (target) {
    const list: Reaction[] = (target.reactions ??= []);
    const r = list.find((x) => x.emoji === emoji);
    const mine = r?.users.some((u) => u.id === me.id);
    if (r && mine) {
      r.users = r.users.filter((u) => u.id !== me.id);
      if (!r.users.length) list.splice(list.indexOf(r), 1);
    } else if (r) r.users.push({ id: me.id, name: me.name });
    else list.push({ emoji, users: [{ id: me.id, name: me.name }] });
    render();
  }
  try {
    await api.react(kind, id, emoji);
    await refresh();
  } catch (e) {
    toast((e as Error).message);
  }
}

/** Reaction chips under a text, comment or chat message. Tap to add or remove yours. */
function reactionRow(kind: "sms" | "comment" | "chat" | "note", id: number, reactions: Reaction[] | undefined) {
  if (!reactions?.length) return null;
  return h("div", { class: "reactions" },
    ...reactions.map((r) => {
      const mine = r.users.some((u) => u.id === state.user?.id);
      return h("button", { class: mine ? "reaction mine" : "reaction", title: r.users.map((u) => u.name).join(", "), onclick: () => void react(kind, id, r.emoji) },
        r.emoji, h("span", {}, String(r.users.length)));
    }));
}

function quoteBlock(name: string | null, body: string | null, targetId: string) {
  if (!body) return h("div", { class: "quote deleted" }, h("span", { class: "quote-body" }, "Original message was deleted"));
  return h("button", { class: "quote", onclick: () => {
    if (!document.getElementById(targetId) && /^m-\d+$/.test(targetId)) showChatFrom(Number(targetId.slice(2)));
    const el = document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("flash");
    setTimeout(() => el.classList.remove("flash"), 1400);
  } }, h("span", { class: "quote-name" }, firstName(name) || "Message"), h("span", { class: "quote-body" }, body));
}

function replyButton(r: Reply, mode?: "comment") {
  return h("button", { class: "reply-btn", "aria-label": `Reply to ${r.name}`, onclick: () => {
    state.reply = r;
    if (mode) state.composeMode = mode;
    render();
    document.getElementById("draft")?.focus();
  } }, icon("reply", "sm"), h("span", { class: "act-label" }, "Reply"));
}

/** Internal comment: only the team sees it. */
function commentNote(n: Note, at = "top", hideQuote = false) {
  const mine = n.user_id === state.user?.id;
  const menu: MsgMenuOpts = {
    kind: "comment", id: n.id, body: n.body, mine, canEdit: mine, taskAt: at, reply: { id: n.id, name: n.user_name, body: n.body }, replyMode: "comment",
    remove: () => api.deleteComment(n.external_number, n.id), what: mine ? "your note" : `${firstName(n.user_name)}'s note`,
    canDelete: !!n.bot && !!n.reply_to && n.reply_user_name === state.user?.name,
  };
  const bot = !!(n as Note & { bot?: number | boolean }).bot; // Selena's answer (Ask Selena)
  return h("div", { class: bot ? "note-row bot-note" : "note-row", id: `c-${n.id}` },
    bot ? h("span", { class: "ai-av sm" }, icon("sparkle", "sm")) : avatar(mateEmail(n.user_id) ?? String(n.user_id) + n.user_name, n.user_name, "sm"),
    // Reactions go under the card (touching it), not inside.
    h("div", { class: "note-col" }, h("div", { class: "note" },
      // Just the name and ⋯ (React, Reply, Task, Edit, Delete live in that menu, on phones and computers).
      h("div", { class: "note-meta" }, h("strong", {}, mine ? "You" : n.user_name),
        h("span", { class: "note-acts" }, moreButton(menu))),
      n.reply_to && !hideQuote ? quoteBlock(n.reply_user_name, n.reply_body, `c-${n.reply_to}`) : null,
      n.media?.length ? h("div", { class: "note-media" }, ...mediaList(n.media)) : null,
      isEditing("comment", n.id)
        ? inlineEditor((body) => api.editComment(n.external_number, n.id, body))
        : n.body ? h("div", { class: menu.canEdit ? "note-body can-edit" : "note-body", ...msgGestures(menu) }, ...(n.bot ? selenaText(n.body) : withMentions(n.body))) : null,
      ), reactionRow("comment", n.id, n.reactions),
      // Under the card: when, and that it's internal.
      h("div", { class: "note-stamp" },
        h("span", { class: "stamp-time", title: fullTime(n.created_at) }, clock(serverTime(n.created_at))),
        n.edited_at ? h("span", { class: "stamp-edited", title: `Edited ${when(n.edited_at)}` }, "edited") : null,
        h("span", { class: "stamp-internal" }, "Internal"),
        // 1.10.30: react in one tap (it was only inside ⋯).
        n.id > 0 && !isEditing("comment", n.id) ? reactButton("comment", n.id) : null)));
}

// ---------------------------------------------------------------- editing your own notes and messages

const isEditing = (kind: "comment" | "chat" | "note", id: number) => state.editingMsg?.kind === kind && state.editingMsg.id === id;

function startEdit(kind: "comment" | "chat" | "note", id: number, body: string) {
  state.editingMsg = { kind, id, draft: body };
  render();
  requestAnimationFrame(() => {
    const ta = document.getElementById("inline-edit") as HTMLTextAreaElement | null;
    if (!ta) return;
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);
    fitTextarea(ta);
  });
}

function editButton(kind: "comment" | "chat" | "note", id: number, body: string) {
  return h("button", { class: "reply-btn edit-btn", "aria-label": "Edit", title: "Edit (or double-click the message)", onclick: () => startEdit(kind, id, body) },
    icon("edit", "sm"), h("span", { class: "act-label" }, "Edit"));
}

/** Delete (your own note or message; admins: anyone's), after a confirm. */
function deleteButton(run: () => Promise<unknown>, what: string) {
  return h("button", { class: "reply-btn delete-btn", "aria-label": "Delete", title: "Delete", onclick: () => void confirmDelete(run, what) },
    icon("trash", "sm"), h("span", { class: "act-label" }, "Delete"));
}

async function confirmDelete(run: () => Promise<unknown>, what: string) {
  const ok = await glassDialog({ title: `Delete ${what}?`, message: "It's removed for the whole team. This can't be undone.", confirm: "Delete", danger: true });
  if (!ok) return;
  try {
    await run();
    render();
    void refresh();
  } catch (e) {
    toast((e as Error).message);
  }
}

interface MsgMenuOpts {
  kind: "comment" | "chat";
  id: number;
  body: string;
  mine: boolean;
  /** Only the author can edit (admins can still delete). */
  canEdit: boolean;
  reply: Reply;
  replyMode?: "comment";
  /** Internal comments: where a task about it shows (its thread's call or text). */
  taskAt?: string;
  remove: () => Promise<unknown>;
  what: string;
  /** Also deletable by you: Selena's answer to your own question. */
  canDelete?: boolean;
}

/** Reply / Copy / Edit / Delete for an internal note or a team chat message. */
function msgMenuItems(o: MsgMenuOpts) {
  return [
    { label: "Reply", iconName: "reply", run: () => {
      // 3.10.22: an internal comment's reply opens a bubble right under it.
      if (o.kind === "comment") return openInlineReply(o.id);
      state.reply = o.reply;
      if (o.replyMode) state.composeMode = o.replyMode;
      render();
      document.getElementById("draft")?.focus();
    } },
    { label: "Copy text", iconName: "copy", run: () => void navigator.clipboard?.writeText(o.body).then(() => toast("Copied"), () => toast("Couldn't copy")) },
    ...(o.kind === "comment" ? [{ label: "Add task", iconName: "checkCircle", run: () => taskOn(o.taskAt ?? "top", `comment:${o.id}`) }] : []),
    ...(o.canEdit ? [{ label: "Edit", iconName: "edit", run: () => startEdit(o.kind, o.id, o.body) }] : []),
    ...(o.mine || isAdmin() || o.canDelete ? [{ label: "Delete", iconName: "trash", danger: true, run: () => void confirmDelete(o.remove, o.what) }] : []),
  ];
}

/** A small menu at a point (right-click, press-and-hold or the ⋯ button). Esc or a click elsewhere closes it. */
function openPointMenu(x: number, y: number, items: { label: string; iconName: string; danger?: boolean; run: () => void }[], quickReact?: (emoji: string) => void, byTouch = false) {
  document.querySelector(".ctx-menu")?.remove();
  // Opened by press-and-hold: the finger is still down. Its lift-off (and the tap the browser makes from it)
  // must not press whatever item ends up under it, nor close the menu.
  let armed = !byTouch;
  if (byTouch) {
    const arm = () => setTimeout(() => (armed = true), 0);
    document.addEventListener("pointerup", arm, { once: true, capture: true });
    document.addEventListener("click", (e) => { if (!armed || menu.contains(e.target as Node)) { e.stopPropagation(); e.preventDefault(); } }, { once: true, capture: true });
    setTimeout(() => (armed = true), 1500);
  }
  const close = () => {
    menu.remove();
    document.removeEventListener("pointerdown", outside, true);
    document.removeEventListener("keydown", onKey, true);
    window.removeEventListener("scroll", onScroll, true);
  };
  const outside = (e: Event) => armed && !menu.contains(e.target as Node) && close();
  const onKey = (e: KeyboardEvent) => e.key === "Escape" && (e.preventDefault(), close());
  // Scrolling the page closes it, but not the little settle-scroll that can follow the tap that opened it.
  const openedAt = performance.now();
  // Only a real scroll closes it (a list nudging a few pixels as the finger lifts doesn't).
  const startTops = new Map<EventTarget, number>([[document, window.scrollY]]);
  document.querySelectorAll<HTMLElement>("[data-scroll], .timeline, .page, .list").forEach((el) => startTops.set(el, el.scrollTop));
  const onScroll = (e: Event) => {
    if (performance.now() - openedAt < 400 || menu.contains(e.target as Node)) return;
    const t = e.target as EventTarget;
    const now = t === document ? window.scrollY : (t as HTMLElement).scrollTop;
    const start = startTops.get(t);
    if (start === undefined) return void startTops.set(t, now);
    if (Math.abs(now - start) < 24) return;
    close();
  };
  const menu = h("div", { class: "menu ctx-menu", role: "menu" },
    // Press-and-hold on a phone: react in one tap from the top of the menu.
    quickReact ? h("div", { class: "ctx-react", role: "group", "aria-label": "React" },
      ...["👍", "❤️", "😂", "🙏", "✅", "🎉"].map((em) => h("button", { type: "button", "aria-label": `React ${em}`, onclick: () => armed && (close(), quickReact(em)) }, em)),
      // Any other emoji: the full set opens right here in the menu.
      h("button", { type: "button", class: "ctx-react-more", "aria-label": "More emoji", title: "More emoji", onclick: (ev: MouseEvent) => {
        if (!armed) return;
        ev.stopPropagation();
        moreEmoji();
      } }, icon("plus", "sm"))) : null,
    ...items.map((it) => h("button", { role: "menuitem", class: it.danger ? "danger-text" : "", onclick: () => armed && (close(), it.run()) }, icon(it.iconName, "sm"), it.label)));
  // The full emoji set inside the menu (the six quick ones are only the most common).
  const moreEmoji = () => {
    const full = h("div", { class: "ctx-emoji-all" },
      ...EMOJI_SETS.flatMap(([title, list]) => [h("div", { class: "emoji-head" }, title),
        h("div", { class: "emoji-grid" }, ...list.map((em) => h("button", { class: "emoji", type: "button", "aria-label": em, onclick: (ev: MouseEvent) => {
          ev.stopPropagation();
          noteEmojiUse(em);
          close();
          quickReact?.(em);
        } }, em)))]));
    menu.replaceChildren(full);
    menu.classList.add("ctx-emoji");
    const rr = menu.getBoundingClientRect();
    menu.style.left = `${Math.max(8, Math.min(rr.left, innerWidth - rr.width - 8))}px`;
    menu.style.top = `${Math.max(8, Math.min(rr.top, innerHeight - rr.height - 8))}px`;
  };
  document.body.append(menu);
  const r = menu.getBoundingClientRect();
  menu.style.left = `${Math.max(8, Math.min(x, innerWidth - r.width - 8))}px`;
  // From a finger: open just above it (so the hand doesn't cover it), else below if there's no room.
  const top = byTouch ? (y - r.height - 14 >= 8 ? y - r.height - 14 : y + 24) : y;
  menu.style.top = `${Math.max(8, Math.min(top, innerHeight - r.height - 8))}px`;
  setTimeout(() => {
    document.addEventListener("pointerdown", outside, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onScroll, true);
  });
  (menu.querySelector("button") as HTMLElement | null)?.focus({ preventScroll: true });
}

/** The ⋯ button next to a note or chat message. */
function moreButton(o: MsgMenuOpts) {
  return h("button", { class: "reply-btn more-btn", "aria-label": "More actions", "aria-haspopup": "menu", title: "Edit, delete and more", onclick: (e: MouseEvent) => {
    e.stopPropagation();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    openPointMenu(r.left, r.bottom + 4, msgMenuItems(o), (em) => void react(o.kind, o.id, em));
  } }, icon("more", "sm"));
}

let touchHoldAt = 0;
/** Right-click (computer) or press-and-hold (phone) a note or message for its menu; double-click your own to edit. */
function msgGestures(o: MsgMenuOpts) {
  let timer: number | undefined;
  let x = 0, y = 0;
  const stop = () => clearTimeout(timer);
  return {
    title: o.canEdit ? "Double-click to edit · right-click for more" : "Right-click for more",
    oncontextmenu: (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest("a")) return;
      e.preventDefault();
      // A phone's own long-press "right click": the press-and-hold timer already opened the menu.
      if (Date.now() - touchHoldAt < 1500) return;
      openPointMenu(e.clientX, e.clientY, msgMenuItems(o), (em) => void react(o.kind, o.id, em));
    },
    ondblclick: (e: MouseEvent) => {
      if (!o.canEdit || (e.target as HTMLElement).closest("a, button")) return;
      window.getSelection()?.removeAllRanges();
      startEdit(o.kind, o.id, o.body);
    },
    onpointerdown: (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      x = e.clientX;
      y = e.clientY;
      stop();
      timer = window.setTimeout(() => {
        touchHoldAt = Date.now();
        navigator.vibrate?.(10);
        openPointMenu(x, y, msgMenuItems(o), (em) => void react(o.kind, o.id, em), true);
      }, 500);
    },
    onpointermove: (e: PointerEvent) => {
      if (Math.abs(e.clientX - x) > 8 || Math.abs(e.clientY - y) > 8) stop();
    },
    onpointerup: stop,
    onpointercancel: stop,
  };
}

function fitTextarea(ta: HTMLTextAreaElement) {
  ta.style.height = "auto";
  ta.style.height = `${Math.min(ta.scrollHeight, 320)}px`;
}

/**
 * Keyboard for the @mention list: ↑/↓ move the highlight, Enter/Tab pick it (the first one if none), Esc closes.
 * Returns true when the key was used by the list (so it doesn't send/save).
 */
function mentionKey(suggest: HTMLElement, e: KeyboardEvent): boolean {
  const items = [...suggest.querySelectorAll<HTMLButtonElement>("button")];
  if (!items.length || e.isComposing) return false;
  let at = items.findIndex((b) => b.classList.contains("active"));
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault();
    at = e.key === "ArrowDown" ? (at + 1) % items.length : (at <= 0 ? items.length : at) - 1;
    items.forEach((b, i) => (b.classList.toggle("active", i === at), b.setAttribute("aria-selected", String(i === at))));
    return true;
  }
  if ((e.key === "Enter" && !e.shiftKey) || e.key === "Tab") {
    e.preventDefault();
    items[Math.max(at, 0)]!.click();
    return true;
  }
  if (e.key === "Escape") {
    e.preventDefault();
    suggest.replaceChildren();
    return true;
  }
  return false;
}

/** Edits the text in place: Enter saves (Shift+Enter for a new line), Esc cancels. */
function inlineEditor(save: (body: string) => Promise<unknown>) {
  const ed = state.editingMsg!;
  let saving = false;
  const cancel = () => {
    state.editingMsg = null;
    render();
  };
  const commit = async () => {
    const body = (state.editingMsg?.draft ?? "").trim();
    if (saving) return;
    if (!body) return toast("It can't be empty");
    saving = true;
    try {
      await save(body);
      state.editingMsg = null;
      await refresh();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      saving = false;
      render();
    }
  };
  // @mentions work while editing too (same suggestions as the message box).
  const suggest = h("div", { class: "mention-suggest", role: "listbox" });
  const showMentions = (el: HTMLTextAreaElement) => {
    suggest.replaceChildren();
    const before = el.value.slice(0, el.selectionStart ?? el.value.length);
    const m = /(^|\s)@([\p{L}]*)$/u.exec(before);
    if (!m) return;
    const typed = m[2].toLowerCase();
    const matches = state.team.filter((u) => u.id !== state.user?.id && (firstName(u.name).toLowerCase().startsWith(typed) || u.name.toLowerCase().replace(/\s+/g, "").startsWith(typed)));    for (const u of matches.slice(0, 5)) {
      suggest.append(h("button", { type: "button", onmousedown: (e: Event) => e.preventDefault(), onclick: () => {
        const tag = mentionTag(u);
        const start = before.length - typed.length;
        el.value = `${el.value.slice(0, start)}${tag} ${el.value.slice(el.selectionStart ?? el.value.length)}`;
        if (state.editingMsg) state.editingMsg.draft = el.value;
        suggest.replaceChildren();
        el.focus();
        const caret = start + tag.length + 1;
        el.setSelectionRange(caret, caret);
        fitTextarea(el);
      } }, avatar(u.email, u.name, "sm"), u.name));
    }
  };
  const ta = h("textarea", { id: "inline-edit", class: "field inline-edit", rows: "2", "aria-label": "Edit message",
    oninput: (e: Event) => {
      const el = e.target as HTMLTextAreaElement;
      if (state.editingMsg) state.editingMsg.draft = el.value;
      fitTextarea(el);
      showMentions(el);
    },
    onkeydown: (e: KeyboardEvent) => {
      if (mentionKey(suggest, e)) return;
      if (e.key === "Escape") (e.preventDefault(), cancel());
      else if (e.key === "Enter" && !e.shiftKey && !e.isComposing) (e.preventDefault(), void commit());
    } }) as HTMLTextAreaElement;
  ta.value = ed.draft;
  requestAnimationFrame(() => fitTextarea(ta));
  return h("div", { class: "inline-editor" }, ta, suggest,
    h("div", { class: "inline-edit-actions" },
      h("span", { class: "sub" }, "Enter to save · Esc to cancel"),
      h("button", { class: "btn quiet sm", onclick: cancel }, "Cancel"),
      h("button", { class: "btn sm", onclick: () => void commit() }, "Save")));
}

// ---------------------------------------------------------------- composer (texts, comments, team chat)

// Drafts survive a reload (iOS often closes Home Screen apps in the background).
const DRAFTS_KEY = "joy-drafts";
try {
  Object.assign(state.drafts, JSON.parse(localStorage.getItem(DRAFTS_KEY) ?? "{}"));
} catch {
  // private mode: drafts stay in memory
}
let draftTimer: number | undefined;
let scheduling = false;
function saveDrafts() {
  clearTimeout(draftTimer);
  draftTimer = window.setTimeout(() => {
    try {
      const keep = Object.fromEntries(Object.entries(state.drafts).filter(([, v]) => v.trim()));
      localStorage.setItem(DRAFTS_KEY, JSON.stringify(keep));
    } catch {
      // storage full or blocked
    }
  }, 300);
}

/** Text length in SMS segments: 160 plain characters, or 70 when there's an emoji or accented letter. */
function segments(text: string): { count: number; left: number } {
  const gsm = /^[A-Za-z0-9 \r\n@£$¥èéùìòÇØøÅå_ÆæßÉ!"#%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[\]~|€]*$/.test(text);
  const len = [...text].length;
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;
  if (len <= single) return { count: 1, left: single - len };
  const count = Math.ceil(len / multi);
  return { count, left: count * multi - len };
}

/** The one send in progress per composer (stops double taps and Enter + click from sending twice). A send can't
 * hold the button for more than 35 s (requests give up at 30 s): a send the phone lost while asleep never locks it. */
const sendingSince = new Map<string, number>();
const sending = {
  has: (key: string) => {
    const t = sendingSince.get(key);
    if (t !== undefined && Date.now() - t > 35_000) sendingSince.delete(key);
    return sendingSince.has(key);
  },
  add: (key: string) => void sendingSince.set(key, Date.now()),
  delete: (key: string) => void sendingSince.delete(key),
};

/** What @ inserts: the first name if no one else shares it, otherwise the full name without spaces. */
function mentionTag(u: User) {
  const first = firstName(u.name);
  const shared = state.team.filter((x) => firstName(x.name).toLowerCase() === first.toLowerCase()).length > 1;
  return shared ? u.name.replace(/\s+/g, "") : first;
}

/** Puts what you just sent on screen immediately ("Sending…"); the server's copy replaces it a moment later. */
function showSentNow(key: string, mode: "text" | "comment" | "chat", body: string, media: Attachment[]): number | null {
  const me = state.user;
  if (!me) return null;
  const now = new Date().toISOString().replace("T", " ").replace("Z", "");
  const id = -Date.now();
  if (key === "chat" && mode === "chat") {
    state.chat = [...state.chat, { id, user_id: me.id, user_name: me.name, body, media, reply_to: state.reply?.id ?? null, reply_body: state.reply?.body ?? null, reply_user_name: state.reply?.name ?? null, created_at: now, reactions: [] }];
  // 4.10.29: your personal line too ("p:"): its texts waited for the server and the phone company before showing.
  } else if ((key.startsWith("t:") || key.startsWith("p:")) && mode === "text" && !state.reply && state.thread?.number === key.slice(2)) {
    state.thread.messages = [...state.thread.messages, { id, direction: "outbound", body, media, status: "sending", error: null, user_id: me.id, user_name: me.name, created_at: now, reactions: [] }];
  } else return null;
  state.scrolled = "";
  render();
  return id;
}

function composer(opts: { key: string; modes: ("text" | "comment" | "chat" | "selena")[]; placeholder: string; send: (body: string, media: Attachment[]) => Promise<void>; reply?: "note";
  schedule?: (body: string, media: Attachment[], sendAt: number) => Promise<void> }) {
  const mode = opts.modes.includes(state.composeMode) ? state.composeMode : opts.modes[0];
  /** For typing indicators and the instant "sent" bubble, asking Selena counts as a note. */
  const noteMode = (mode === "selena" ? "comment" : mode) as "text" | "comment" | "chat";
  const value = state.drafts[opts.key] ?? "";
  // Pictures / PDFs: texts, team chat and (3.10.22) internal comments in a conversation (not contact notes).
  const canAttach = mode === "text" || mode === "chat" || (mode === "comment" && !opts.key.startsWith("note:"));
  // A photo still uploading holds only a composer that sends photos (a note or Ask Steven never waits on it).
  const uploadBusy = () => canAttach && state.uploading > 0;
  const busy = sending.has(opts.key) || uploadBusy();
  const files = state.attachments[opts.key] ?? [];
  const hasContent = () => !!(state.drafts[opts.key] ?? "").trim() || (canAttach && (state.attachments[opts.key] ?? []).length > 0);
  const reply = opts.reply === "note" ? state.noteReply : state.reply;
  // One tap sends. On phones the first tap used to only close the keyboard (the screen jumped and the tap missed):
  // the button keeps the keyboard open and sends right on the touch.
  const sendBtn = h("button", { class: mode === "comment" ? "send note-send" : "send", "aria-label": "Send", disabled: busy || !hasContent(),
    onmousedown: (e: MouseEvent) => e.preventDefault(),
    ontouchstart: (e: TouchEvent) => {
      const t = e.touches[0];
      sendTouch = t ? { x: t.clientX, y: t.clientY } : null;
    },
    ontouchend: (e: TouchEvent) => {
      // A tap, not a scroll or a finger slid off to cancel: it has to end on the button, close to where it began.
      const t = e.changedTouches[0];
      const start = sendTouch;
      sendTouch = null;
      const btn = e.currentTarget as HTMLElement;
      const r = btn.getBoundingClientRect();
      const tap = !!t && !!start && Math.hypot(t.clientX - start.x, t.clientY - start.y) < 12
        && t.clientX >= r.left - 8 && t.clientX <= r.right + 8 && t.clientY >= r.top - 8 && t.clientY <= r.bottom + 8;
      e.preventDefault();
      if (tap && !sending.has(opts.key) && !uploadBusy() && hasContent()) void submit();
    },
    onclick: () => void submit() }, icon("send"));
  const suggest = h("div", { class: "mention-suggest", role: "listbox" });
  const counter = h("span", { class: "seg-count" });
  const paintCounter = (text: string) => {
    if (mode !== "text" || text.length < 100) return void (counter.textContent = "");
    const seg = segments(text);
    counter.textContent = seg.count > 1 ? `${seg.count} texts · ${seg.left} left` : `${seg.left} left`;
  };
  paintCounter(value);

  async function submit() {
    const body = (state.drafts[opts.key] ?? "").trim();
    const media = canAttach ? state.attachments[opts.key] ?? [] : [];
    if ((!body && !media.length) || sending.has(opts.key) || uploadBusy()) return;
    sending.add(opts.key);
    sendBtn.disabled = true;
    stopTyping(opts.key, noteMode);
    // Clear right away so a re-render can't show (and resend) the same text.
    state.drafts[opts.key] = "";
    if (canAttach) delete state.attachments[opts.key];
    saveDrafts();
    const tempId = showSentNow(opts.key, noteMode, mode === "selena" ? `@${aiName()} ${body}` : body, media);
    try {
      await opts.send(body, media.map(({ url, type, name }) => ({ url, type, name })));
      if (opts.reply === "note") state.noteReply = null;
      else state.reply = null;
      state.scrolled = "";
    } catch (e) {
      // 7.10.30: the "Sending…" bubble goes (a failed Team chat message stayed "Sending…" next to the restored draft).
      if (tempId !== null) {
        state.chat = state.chat.filter((m) => m.id !== tempId);
        if (state.thread) state.thread.messages = state.thread.messages.filter((m) => m.id !== tempId);
      }
      // 7.10.30: a text the server kept as "Not sent" (502 with the message) has its Retry: putting the words back in
      // the box too made it easy to send twice.
      const keptAsFailed = mode === "text" && e instanceof ApiError && !!(e.data as { message?: unknown } | undefined)?.message;
      if (!keptAsFailed) {
        state.drafts[opts.key] = body;
        if (media.length) state.attachments[opts.key] = media;
        saveDrafts();
      }
      render();
      toast((e as Error).message);
    } finally {
      sending.delete(opts.key);
    }
    await refresh();
  }

  const draft = h("textarea", {
    id: "draft", rows: 1, placeholder: opts.placeholder, value,
    "aria-label": mode === "text" ? "Text message" : mode === "chat" ? "Message" : "Comment",
    oninput: (e: Event) => {
      const el = e.target as HTMLTextAreaElement;
      state.drafts[opts.key] = el.value;
      saveDrafts();
      sendBtn.disabled = sending.has(opts.key) || uploadBusy() || !hasContent();
      grow(el);
      showMentions(el);
      paintCounter(el.value);
      if (el.value.trim()) typingPing(opts.key, noteMode);
      else stopTyping(opts.key, noteMode);
      if (mode === "text" && el.value === "/") {
        state.picker = "snippets";
        void loadSnippets();
        render();
      }
    },
    onpaste: (e: ClipboardEvent) => {
      // Some browsers (iPhone Safari, pictures copied from some apps) hand over a pasted picture only as an item.
      let pasted = [...(e.clipboardData?.files ?? [])];
      if (!pasted.length) pasted = [...(e.clipboardData?.items ?? [])].filter((i) => i.kind === "file").map((i) => i.getAsFile()).filter((f): f is File => !!f);
      if (!pasted.length || !canAttach) return;
      e.preventDefault();
      void addFiles(opts.key, pasted);
    },
    onkeydown: (e: KeyboardEvent) => {
      if (mentionKey(suggest, e)) return;
      if (e.key === "Enter" && !e.shiftKey && !e.isComposing && matchMedia("(pointer: fine)").matches) {
        e.preventDefault();
        void submit();
      }
    },
  });
  const grow = (el: HTMLTextAreaElement) => {
    const tl = document.querySelector<HTMLElement>('[data-scroll="timeline"]');
    const atBottom = !!tl && tl.scrollHeight - tl.scrollTop - tl.clientHeight < 60;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
    if (tl && atBottom) tl.scrollTop = tl.scrollHeight;
  };
  if (value) requestAnimationFrame(() => grow(draft));

  // @mention suggestions (comments, notes and chat).
  function showMentions(el: HTMLTextAreaElement) {
    suggest.replaceChildren();
    if (mode === "text") return;
    const before = el.value.slice(0, el.selectionStart ?? el.value.length);
    const m = /(^|\s)@([\p{L}]*)$/u.exec(before);
    if (!m) return;
    const typed = m[2].toLowerCase();
    const matches = state.team.filter((u) => u.id !== state.user?.id && (firstName(u.name).toLowerCase().startsWith(typed) || u.name.toLowerCase().replace(/\s+/g, "").startsWith(typed)));
    // Team chat: "@Selena" (or "@AI") asks the AI assistant.
    if (opts.key === "chat" && (aiName().toLowerCase().startsWith(typed) || "selena".startsWith(typed) || "ai".startsWith(typed))) {
      suggest.append(h("button", { type: "button", onmousedown: (e: Event) => e.preventDefault(), onclick: () => {
        const start = before.length - typed.length;
        const next = `${el.value.slice(0, start)}${aiName()} ${el.value.slice(el.selectionStart ?? el.value.length)}`;
        el.value = next;
        state.drafts[opts.key] = next;
        saveDrafts();
        sendBtn.disabled = false;
        suggest.replaceChildren();
        el.focus();
        el.setSelectionRange(start + aiName().length + 1, start + aiName().length + 1);
      } }, h("span", { class: "ai-av sm" }, icon("sparkle", "sm")), aiName(), h("small", { class: "muted" }, " · AI assistant")));
    }

    for (const u of matches.slice(0, 5)) {
      suggest.append(h("button", { type: "button", onmousedown: (e: Event) => e.preventDefault(), onclick: () => {
        const tag = mentionTag(u);
        const start = before.length - typed.length;
        const next = `${el.value.slice(0, start)}${tag} ${el.value.slice(el.selectionStart ?? el.value.length)}`;
        el.value = next;
        state.drafts[opts.key] = next;
        saveDrafts();
        sendBtn.disabled = false;
        suggest.replaceChildren();
        el.focus();
        const caret = start + tag.length + 1;
        el.setSelectionRange(caret, caret);
      } }, avatar(u.email, u.name, "sm"), u.name));
    }
  }

  const insert = (text: string, replaceAll = false) => {
    const el = document.getElementById("draft") as HTMLTextAreaElement | null;
    const cur = replaceAll ? "" : state.drafts[opts.key] ?? "";
    const at = replaceAll ? 0 : el?.selectionStart ?? cur.length;
    state.drafts[opts.key] = cur.slice(0, at) + text + cur.slice(replaceAll ? 0 : el?.selectionEnd ?? at);
    saveDrafts();
    state.picker = null;
    render();
    const d = document.getElementById("draft") as HTMLTextAreaElement | null;
    d?.focus();
    d?.setSelectionRange(at + text.length, at + text.length);
  };

  const tabs = opts.modes.length > 1
    // 8.10.20 "put back texting / internal comments connected in one bubble like yesterday": the tabs are joined to
    // the message box again (folder tabs), not the separate pill switch from 1.10.20.
    ? h("div", { class: "compose-tabs", role: "tablist" },
        ...opts.modes.map((m) =>
          h("button", { role: "tab", "aria-selected": String(mode === m), class: m === "comment" ? "is-note" : m === "selena" ? "is-selena" : "", onclick: () => {
            state.composeMode = m as "text" | "comment" | "selena";
            if (m === "text" || m === "selena") state.reply = null;
            render();
            document.getElementById("draft")?.focus();
          } }, m === "selena" ? h("span", { class: "ai-av xs" }, icon("sparkle", "sm")) : icon(m === "text" ? "message" : "note", "sm"),
            m === "text" ? "Text" : m === "selena" ? `Ask ${aiName()}` : opts.key.startsWith("p:") ? "Private note" : "Internal comment")),
        counter)
    : null;

  async function scheduleFor(at: number) {
    if (scheduling) return;
    const body = (state.drafts[opts.key] ?? "").trim();
    const media = state.attachments[opts.key] ?? [];
    // 7.10.30: like Send, it waits for a photo still uploading (scheduling then went out without it).
    if (uploadBusy()) return toast("Wait for the upload to finish");
    if (!body && !media.length) return toast("Write the text first, then schedule it");
    if (!opts.schedule) return;
    scheduling = true;
    // Clear first so a second tap can't schedule the same text twice; put it back if it fails.
    const prevDraft = state.drafts[opts.key] ?? "";
    state.drafts[opts.key] = "";
    delete state.attachments[opts.key];
    saveDrafts();
    state.picker = null;
    render();
    try {
      await opts.schedule(body, media.map(({ url, type, name }) => ({ url, type, name })), at);
      toast(`Scheduled for ${fmtDate(new Date(at), { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`);
      await refresh();
    } catch (e) {
      if (!state.drafts[opts.key]) state.drafts[opts.key] = prevDraft;
      if (!state.attachments[opts.key]?.length && media.length) state.attachments[opts.key] = media;
      saveDrafts();
      render();
      toast((e as Error).message);
    } finally {
      scheduling = false;
    }
  }
  const schedulePicker = state.picker === "schedule"
    ? (() => {
        const at9 = (days: number, weekday?: number) => {
          const d = new Date();
          if (weekday !== undefined) d.setDate(d.getDate() + ((weekday + 7 - d.getDay()) % 7 || 7));
          else d.setDate(d.getDate() + days);
          d.setHours(9, 0, 0, 0);
          return d.getTime();
        };
        // Pre-filled with tomorrow 9:00 AM, so the box never looks empty (iPhone shows a blank box otherwise).
        const local = (ms: number) => { const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000); return d.toISOString().slice(0, 16); };
        const custom = h("input", { id: "schedule-at", class: "field", type: "datetime-local", value: local(at9(1)), "aria-label": "Pick a date and time" });
        return h("div", { class: "emoji-pop snippet-pop", role: "dialog", "aria-label": "Schedule text" },
          h("div", { class: "pop-title" }, "Send later"),
          ...[["In 1 hour", Date.now() + 3600_000], ["Tomorrow 9:00 AM", at9(1)], ["Monday 9:00 AM", at9(0, 1)]].map(([label, at]) =>
            h("button", { class: "snippet-item", type: "button", onclick: (e: MouseEvent) => (e.stopPropagation(), void scheduleFor(at as number)) },
              h("strong", {}, String(label)), h("span", { class: "ellipsis" }, fmtDate(new Date(at as number), { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })))),
          // Own date and time: the box on its own line, Schedule under it (side by side, Schedule was cut off on phones).
          h("div", { class: "sched-custom" }, h("div", { class: "sched-label" }, "Pick a date and time"), custom,
            h("button", { class: "btn sm", type: "button", onclick: (e: MouseEvent) => {
              e.stopPropagation();
              if (!custom.value) return toast("Pick a date and time");
              void scheduleFor(new Date(custom.value).getTime());
            } }, "Schedule")));
      })()
    : null;

  const snippetPicker = state.picker === "snippets"
    ? h("div", { class: "emoji-pop snippet-pop", role: "dialog", "aria-label": "Saved replies" },
        h("div", { class: "pop-title" }, "Saved replies"),
        ...(state.snippets.length
          ? state.snippets.map((sn) => h("button", { class: "snippet-item", type: "button", onmousedown: (e: Event) => e.preventDefault(), onclick: (e: MouseEvent) => {
              e.stopPropagation();
              const body = fillSnippet(sn.body);
              insert(body, (state.drafts[opts.key] ?? "").trim() === "/");
            } }, h("strong", {}, sn.title), h("span", { class: "ellipsis" }, sn.body)))
          : [h("div", { class: "sub", style: "padding:6px 8px" }, state.snippetsLoaded ? "No saved replies yet." : "Loading…")]),
        h("button", { class: "emoji-more", type: "button", onclick: () => go({ name: "snippets" }) }, "Manage saved replies"))
    : null;

  const tray = canAttach && (files.length || state.uploading)
    ? h("div", { class: "attach-tray" },
        ...files.map((f, i) => h("div", { class: "attach-chip" },
          f.type.startsWith("image/") ? h("img", { src: f.preview ?? f.url, alt: "" }) : h("span", { class: "pdf-badge" }, fileBadge(f.type)),
          h("span", { class: "ellipsis" }, f.name ?? (f.type.startsWith("image/") ? "Photo" : "Document")),
          h("button", { class: "icon-btn sm", type: "button", "aria-label": "Remove", onclick: () => {
            state.attachments[opts.key] = files.filter((_, j) => j !== i);
            render();
          } }, icon("close", "sm")))),
        state.uploading ? h("div", { class: "attach-chip" }, h("span", { class: "spinner" }), "Uploading…") : null)
    : null;

  return h("div", { class: mode === "comment" ? "composer is-note" : "composer",
      ondragover: (e: DragEvent) => canAttach && e.preventDefault(),
      ondrop: (e: DragEvent) => {
        if (!canAttach || !e.dataTransfer?.files.length) return;
        e.preventDefault();
        void addFiles(opts.key, [...e.dataTransfer.files]);
      } },
    h("div", { class: "composer-inner" },
      tray,
      reply
        ? h("div", { class: "replying" }, icon("reply", "sm"),
            h("span", { class: "grow" }, h("strong", {}, `Replying to ${reply.name === state.user?.name ? "yourself" : firstName(reply.name)}: `), h("span", { class: "replying-body" }, reply.body)),
            h("button", { class: "icon-btn sm", "aria-label": "Cancel reply", onclick: () => ((opts.reply === "note" ? (state.noteReply = null) : (state.reply = null)), render()) }, icon("close", "sm")))
        : null,
      !reply && mode === "comment" && state.commentOn && opts.key.startsWith("t:")
        ? h("div", { class: "replying" }, icon("note", "sm"),
            h("span", { class: "grow" }, h("strong", {}, "Commenting on "), h("span", { class: "replying-body" }, state.commentOn.label)),
            h("button", { class: "icon-btn sm", "aria-label": "Don't attach", onclick: () => ((state.commentOn = null), render()) }, icon("close", "sm")))
        : null,
      suggest,
      typingIndicator(opts.key),
      // Text / Internal comment tabs sit on top of the box like folder tabs, so the mode is obvious.
      tabs,
      h("div", { class: tabs ? "composer-box has-tabs" : "composer-box" },
        // The message box first, full width; the tools go in a row underneath.
        draft,
        h("div", { class: "composer-tools" },
        h("span", { class: "emoji-wrap compose-emoji" },
          h("button", { class: "icon-btn emoji-toggle", type: "button", "aria-label": "Add emoji", "aria-expanded": String(state.picker === "compose"), onclick: (e: MouseEvent) => {
            e.stopPropagation();
            state.picker = state.picker === "compose" ? null : "compose";
            render();
          } }, h("span", { class: "smile big" }, "☺")),
          state.picker === "compose" ? emojiPicker((emoji) => insert(emoji), true) : null),
        canAttach
          ? h("label", { class: "icon-btn emoji-toggle attach-btn",
              title: takesDocs(opts.key) ? "Attach a photo, PDF, Word, Excel, CSV or text file (or drop / paste one)" : "Attach a photo or PDF (or drop / paste one)",
              "aria-label": takesDocs(opts.key) ? "Attach a file" : "Attach a photo or PDF" }, icon("clip"),
              h("input", { type: "file", multiple: true, accept: takesDocs(opts.key) ? `image/*,application/pdf,${DOC_ACCEPT}` : "image/*,application/pdf", class: "sr-only", onchange: (e: Event) => {
                const input = e.target as HTMLInputElement;
                void addFiles(opts.key, [...(input.files ?? [])]);
                input.value = "";
              } }))
          : null,
        mode === "text"
          ? h("span", { class: "emoji-wrap compose-emoji" },
              h("button", { class: "icon-btn emoji-toggle", type: "button", "aria-label": "Saved replies", title: "Saved replies (or type /)", onclick: (e: MouseEvent) => {
                e.stopPropagation();
                state.picker = state.picker === "snippets" ? null : "snippets";
                void loadSnippets();
                render();
              } }, icon("bolt")),
              snippetPicker)
          : null,
        mode === "text" && opts.schedule
          ? h("span", { class: "emoji-wrap compose-emoji" },
              h("button", { class: "icon-btn emoji-toggle", type: "button", "aria-label": "Send later", title: "Schedule this text", onclick: (e: MouseEvent) => {
                e.stopPropagation();
                state.picker = state.picker === "schedule" ? null : "schedule";
                render();
              } }, icon("clock")),
              schedulePicker)
          : null,
        h("span", { class: "tools-spacer" }),
        sendBtn))));
}

/** Shrinks big photos so they go through as a text (carriers reject large MMS). GIFs and PDFs are sent as they are. */
async function prepareFile(file: File): Promise<{ blob: Blob; name: string; preview?: string }> {
  const isImage = file.type.startsWith("image/") || /\.(heic|heif)$/i.test(file.name);
  if (!isImage || file.type === "image/gif" || (file.size < 900_000 && /^image\/(jpeg|png)$/.test(file.type))) {
    return { blob: file, name: file.name || "photo", preview: isImage ? URL.createObjectURL(file) : undefined };
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error("This browser can't read that photo. Save it as a JPG and try again.");
  }
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't prepare the photo"))), "image/jpeg", 0.82));
  return { blob, name: (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", preview: URL.createObjectURL(blob) };
}

// ---------------------------------------------------------------- "typing…" indicators

const TYPING_TTL = 6000;
const typingSent = new Map<string, number>();
let typingTimer: number | undefined;

/** While you type: tell teammates (at most every 3 s per conversation). */
function typingPing(where: string, mode: "chat" | "text" | "comment") {
  if (where.startsWith("p:")) return; // your personal line: nobody else is told
  if (typingRejected.has(typingKind(where))) return;
  const now = Date.now();
  if (now - (typingSent.get(where) ?? 0) < 3000) return;
  typingSent.set(where, now);
  void api.typing(where, mode).catch((e) => {
    // An older server that doesn't know this kind of box (e.g. "note:<number>"): stop asking, quietly.
    if (e instanceof ApiError && e.status === 400) typingRejected.add(typingKind(where));
  });
}
/** Kinds of box ("note", "t", "chat") the server said no to; no more typing pings for them this session. */
const typingRejected = new Set<string>();
const typingKind = (where: string) => where.split(":")[0]!;

/** You sent it or cleared the box: the indicator goes away for everyone right away. */
function stopTyping(where: string, mode: "chat" | "text" | "comment") {
  if (!typingSent.has(where)) return;
  typingSent.delete(where);
  if (typingRejected.has(typingKind(where))) return;
  void api.typing(where, mode, true).catch(() => {});
}

function onTypingEvent(e: { userId: number; name: string; where: string; mode: "chat" | "text" | "comment"; stop?: boolean }) {
  if (e.userId === state.user?.id || typeof e.where !== "string") return;
  const at = (state.typing[e.where] ??= {});
  if (e.stop) delete at[e.userId];
  else at[e.userId] = { name: e.name, mode: e.mode, until: Date.now() + TYPING_TTL };
  scheduleTypingExpiry();
  render();
}

/** Clears indicators that went quiet (closed app, lost connection) without a "stop". */
function scheduleTypingExpiry() {
  clearTimeout(typingTimer);
  const next = Math.min(...Object.values(state.typing).flatMap((u) => Object.values(u).map((x) => x.until)));
  if (!Number.isFinite(next)) return;
  typingTimer = window.setTimeout(() => {
    const now = Date.now();
    for (const [where, users] of Object.entries(state.typing)) {
      for (const [id, x] of Object.entries(users)) if (x.until <= now) delete users[Number(id)];
      if (!Object.keys(users).length) delete state.typing[where];
    }
    render();
    scheduleTypingExpiry();
  }, Math.max(50, next - Date.now() + 20));
}

function typingNow(where: string) {
  const now = Date.now();
  return Object.values(state.typing[where] ?? {}).filter((x) => x.until > now);
}

/** "Ben is typing…", "Ben and Dana are typing…". */
function typingText(where: string) {
  const who = typingNow(where);
  if (!who.length) return "";
  const names = who.map((x) => firstName(x.name));
  if (names.length > 1) return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]} are typing…`;
  return `${names[0]} is typing…`;
}

function typingDots() {
  return h("span", { class: "typing-dots", "aria-hidden": "true" }, h("i"), h("i"), h("i"));
}

function typingIndicator(where: string) {
  const text = typingText(where);
  return text ? h("div", { class: "typing-indicator", role: "status", "aria-live": "polite" }, typingDots(), text) : null;
}

/** Drop pictures anywhere on a conversation to attach them (highlights while you drag over it). */
function dropTarget(key: string) {
  const hasFiles = (e: DragEvent) => !!e.dataTransfer && [...e.dataTransfer.types].includes("Files");
  return {
    ondragenter: (e: DragEvent) => hasFiles(e) && (e.currentTarget as HTMLElement).classList.add("drop-over"),
    ondragover: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer!.dropEffect = "copy";
    },
    ondragleave: (e: DragEvent) => {
      const el = e.currentTarget as HTMLElement;
      if (!el.contains(e.relatedTarget as Node | null)) el.classList.remove("drop-over");
    },
    ondrop: (e: DragEvent) => {
      (e.currentTarget as HTMLElement).classList.remove("drop-over");
      if (!e.dataTransfer?.files.length) return;
      e.preventDefault();
      void addFiles(key, [...e.dataTransfer.files]);
      document.getElementById("draft")?.focus();
    },
  };
}

/** Documents Selena reads (her chat and Team chat only; texts take photos and PDFs). */
const DOC_TYPES: Record<string, string> = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  txt: "text/plain",
};
const DOC_ACCEPT = ".docx,.xlsx,.csv,.txt";
const docTypeOf = (f: { name: string }) => DOC_TYPES[f.name.split(".").pop()?.toLowerCase() ?? ""] ?? null;
/** Her chat ("agent") and Team chat take documents too. */
const takesDocs = (key: string) => key === "agent" || key === "chat";

async function addFiles(key: string, list: File[]) {
  const docs = takesDocs(key);
  const ok = list.filter((f) => f.type.startsWith("image/") || f.type === "application/pdf" || /\.(pdf|heic|heif)$/i.test(f.name) || (docs && !!docTypeOf(f)));
  if (ok.length < list.length) toast(docs ? `${aiName()} reads photos, PDFs, Word, Excel, CSV and text files` : "Only photos and PDFs can be added");
  if (!ok.length) return;
  // A photo dropped while writing an internal comment stays a comment (3.10.22); Ask Steven can't read photos, so
  // that one switches to Text. The Reply bubble under a comment keeps its own photos.
  if (!docs && !key.startsWith("ir:") && state.composeMode !== "text" && state.composeMode !== "comment") state.composeMode = "text";
  for (const file of ok.slice(0, 10)) {
    state.uploading++;
    render();
    try {
      const docType = docs ? docTypeOf(file) : null;
      const prepared = docType ? { blob: file as Blob, name: file.name } : await prepareFile(file);
      const max = docs ? 15 : 5;
      if (prepared.blob.size > max * 1024 * 1024) throw new Error(`${file.name} is over ${max} MB`);
      const up = await api.uploadAttachment(prepared.blob, prepared.name, docs, docType ?? undefined);
      state.attachments[key] = [...(state.attachments[key] ?? []), { ...up, name: prepared.name, preview: prepared.preview }];
    } catch (e) {
      toast((e as Error).message);
    } finally {
      state.uploading--;
    }
  }
  render();
  document.getElementById(key.startsWith("ir:") ? "reply-inline" : "draft")?.focus();
}

/** Fills {first_name} / {name} in a saved reply from the open contact. */
function fillSnippet(body: string) {
  const name = state.thread?.name ?? state.contact?.name ?? "";
  return body.replace(/\{first_name\}/gi, firstName(name) || "there").replace(/\{name\}/gi, name || "there");
}

// ---------------------------------------------------------------- team chat

/**
 * Your Team chat messages: a grey check once sent, two blue checks once a teammate has seen it (had Team chat
 * open after it arrived). Hover / long-press the checks for who.
 */
function seenCheck(m: ChatMessage) {
  if (m.id < 0) return h("span", { class: "seen-check sending", title: "Sending…" }, icon("clock", "sm"));
  const seenBy = state.chatReads.filter((r) => r.userId !== state.user?.id && r.readAt >= m.created_at).map((r) => firstName(r.name));
  return seenBy.length
    ? h("span", { class: "seen-check seen", title: `Seen by ${seenBy.join(", ")}`, "aria-label": `Seen by ${seenBy.join(", ")}` }, icon("check", "sm"), icon("check", "sm"))
    : h("span", { class: "seen-check", title: "Sent", "aria-label": "Sent" }, icon("check", "sm"));
}

/**
 * 4.10.29 "the app lags": Team chat drew all of its last 400 messages, again on every new one (measured: about half of
 * each refresh on the chat screen). Now it draws the newest CHAT_PAGE; "Show earlier messages" brings more. The cut
 * is kept while Team chat is open, so new messages are added below and nothing slides off the top.
 */
const CHAT_PAGE = 80;
let chatCut: number | null = null; // the id of the oldest message drawn (null: not decided yet)
function chatShownFrom(): number {
  const real = state.chat.filter((m) => m.id > 0);
  if (chatCut === null && chatLoaded && real.length) chatCut = real.length > CHAT_PAGE ? real[real.length - CHAT_PAGE].id : 0;
  // Opened at an older message (an @mention, a search result): drawn from there.
  const ref = state.focusRef?.startsWith("chat:") ? Number(state.focusRef.slice(5)) : NaN;
  if (chatCut !== null && Number.isFinite(ref) && ref > 0 && ref < chatCut) chatCut = ref;
  return chatCut ?? 0;
}
/** 7.10.30: Team chat loads its newest 400 messages; older ones come a page at a time (GET /chat?before=). */
const CHAT_LOADED = 400;
let chatOlderLoaded = false; // older pages were fetched and put above the newest ones
let chatNoOlder = false; // the last fetch of older messages came back empty: that's the start of the chat
let chatOlderBusy = false;
/** Puts the server's newest messages in place, keeping the older pages fetched while Team chat stays open. */
function setChat(messages: ChatMessage[]) {
  const first = messages.reduce((n, m) => Math.min(n, m.id), Infinity);
  state.chat = chatOlderLoaded && messages.length ? [...state.chat.filter((m) => m.id > 0 && m.id < first), ...messages] : messages;
}
function chatMayHaveOlder() {
  return !chatNoOlder && (chatOlderLoaded || state.chat.filter((m) => m.id > 0).length >= CHAT_LOADED);
}
async function loadOlderChat() {
  const oldest = state.chat.reduce((n, m) => (m.id > 0 ? Math.min(n, m.id) : n), Infinity);
  if (chatOlderBusy || !Number.isFinite(oldest)) return;
  chatOlderBusy = true;
  try {
    const { messages } = await api.chatBefore(oldest);
    const older = messages.filter((m) => m.id > 0 && m.id < oldest).sort((a, b) => a.id - b.id);
    if (!older.length) chatNoOlder = true;
    else {
      chatOlderLoaded = true;
      state.chat = [...older, ...state.chat];
      chatCut = older.length > CHAT_PAGE ? older[older.length - CHAT_PAGE].id : older[0].id;
    }
  } catch (e) {
    toast((e as Error).message);
  } finally {
    chatOlderBusy = false;
  }
}
/** A quoted reply to a message older than the ones drawn: draw back to it first. */
function showChatFrom(id: number) {
  if (chatCut !== null && id > 0 && id < chatCut) {
    chatCut = id;
    render();
  }
}

function chatView() {
  const timeline: Node[] = [];
  let lastDay = "";
  let lastUser = -1;
  const from = chatShownFrom();
  const hiddenChat = state.chat.filter((m) => m.id > 0 && m.id < from);
  // 7.10.30: also at the start of what's loaded when the chat goes back further (fetched from the server).
  if (hiddenChat.length || chatMayHaveOlder()) {
    timeline.push(h("button", { class: "show-earlier", type: "button", disabled: chatOlderBusy, onclick: async () => {
      // Stay on the same message: the earlier ones appear above it.
      const tl = document.querySelector<HTMLElement>('[data-scroll="timeline"]');
      const top = tl?.getBoundingClientRect().top ?? 0;
      const anchor = tl ? [...tl.querySelectorAll<HTMLElement>('.timeline-inner [id^="m-"]')].find((el) => el.getBoundingClientRect().bottom > top) : undefined;
      const offset = anchor ? anchor.getBoundingClientRect().top - top : 0;
      if (hiddenChat.length) chatCut = hiddenChat.length > CHAT_PAGE ? hiddenChat[hiddenChat.length - CHAT_PAGE].id : 0;
      else await loadOlderChat();
      render();
      const tl2 = document.querySelector<HTMLElement>('[data-scroll="timeline"]');
      const again = anchor?.id ? document.getElementById(anchor.id) : null;
      if (tl2 && again) tl2.scrollTop += again.getBoundingClientRect().top - tl2.getBoundingClientRect().top - offset;
    } }, hiddenChat.length ? `Show earlier messages (${hiddenChat.length})` : "Show earlier messages"));
  }
  for (const m of state.chat) {
    if (m.id > 0 && m.id < from) continue;
    const d = serverTime(m.created_at);
    const label = d ? dayLabel(d) : "";
    if (label && label !== lastDay) {
      timeline.push(h("div", { class: "day" }, label));
      lastDay = label;
      lastUser = -1;
    }
    const mine = m.user_id === state.user?.id;
    const menu: MsgMenuOpts = {
      kind: "chat", id: m.id, body: m.body, mine, canEdit: mine, reply: { id: m.id, name: m.user_name, body: m.body },
      remove: async () => {
        const r = await api.deleteChat(m.id);
        state.chat = state.chat.filter((x) => x.id !== m.id);
        return r;
      },
      what: mine ? "your message" : `${firstName(m.user_name)}'s message`,
      canDelete: !!m.bot && !!m.reply_to && state.chat.find((x) => x.id === m.reply_to)?.user_id === state.user?.id,
    };
    const showName = !mine && m.user_id !== lastUser;
    lastUser = m.user_id;
    timeline.push(h("div", { class: mine ? "bubble-row mine chat-row" : "bubble-row chat-row", id: `m-${m.id}` },
      showName ? h("div", { class: m.bot ? "bubble-meta bot-meta" : "bubble-meta" }, m.bot ? h("span", { class: "ai-av xs" }, icon("sparkle", "sm")) : null, m.user_name) : null,
      h("div", { class: "bubble-line" },
        // Under the message, one line: reactions and React on the left, the time (and, for yours, the seen check)
        // on the right.
        h("div", { class: "bubble-stack" },
          isEditing("chat", m.id)
            ? h("div", { class: "bubble editing" }, inlineEditor((body) => api.editChat(m.id, body)))
            : h("div", { class: `${menu.canEdit && m.id > 0 ? "bubble can-edit" : "bubble"}${m.id > 0 ? " has-dots" : ""}${m.bot ? " bot-bubble" : ""}`, ...(m.id > 0 ? msgGestures(menu) : {}) },
                m.reply_to ? quoteBlock(m.reply_user_name, m.reply_body, `m-${m.reply_to}`) : null,
                ...(m.bot ? selenaText(m.body) : withMentions(m.body)),
                ...mediaList(m.media ?? []),
                // Reply, Copy, Edit and Delete: one faint ⋮ in the bubble's bottom-right corner.
                m.id > 0 ? moreButton(menu) : null),
          // Reactions: the same pills as on texts and internal comments, tucked over the bubble's bottom edge
          // (left for theirs, right for yours), not loose on the line underneath.
          m.id < 0 || isEditing("chat", m.id) ? null : reactionRow("chat", m.id, m.reactions),
          h("div", { class: "under-bubble" },
            m.id < 0 || isEditing("chat", m.id) ? null : reactButton("chat", m.id),
            h("span", { class: "bubble-time" }, m.edited_at ? h("span", { class: "edited", title: `Edited ${when(m.edited_at)}` }, "edited · ") : null, clock(d),
              mine ? seenCheck(m) : null))))));
  }
  if (!state.chat.length && !chatLoaded) timeline.push(skeletonBubbles());
  else if (!state.chat.length) {
    timeline.push(emptyState({ icon: "chat", tone: "purple", title: "Team chat",
      text: "Talk with your team here. Customers never see these messages. Use @ to get someone's attention." }));
  }
  return screen(
    h("header", { class: "thread-head" },
      backButton(),
      h("span", { class: "avatar lg chat-av" }, icon("chat", "lg")),
      h("div", { class: "who" }, h("div", { class: "title" }, "Team chat"),
        h("div", { class: "number" }, CHAT_TAGLINE)),
      // Phones: the AI agent sits with the team (computers list it in the sidebar under Team chat).
      h("div", { class: "head-actions only-narrow" }, h("button", { class: "btn quiet sm ai-agent-chip", onclick: () => go({ name: "ai-agent" }) },
        h("span", { class: "ai-av xs" }, icon("sparkle", "sm")), aiName(),
        state.agentUnread ? h("span", { class: "chip-badge", "aria-label": `${state.agentUnread} new` }, state.agentUnread > 99 ? "99+" : String(state.agentUnread)) : null))),
    h("div", { class: "timeline", "data-scroll": "timeline", ...dropTarget("chat") }, h("div", { class: "timeline-inner" }, ...timeline)),
    composer({
      key: "chat",
      modes: ["chat"],
      placeholder: "Message the team",
      send: async (body, media) => void (await api.sendChat(body, state.reply?.id ?? null, media)),
    }));
}

// ---------------------------------------------------------------- dialer

const LETTERS: Record<string, string> = { "2": "ABC", "3": "DEF", "4": "GHI", "5": "JKL", "6": "MNO", "7": "PQRS", "8": "TUV", "9": "WXYZ", "0": "+" };

/**
 * The keypad card always fits the screen, no scrolling: on a short screen (iPhone with Display Zoom or larger text,
 * Safari's own bars, a small phone) the whole card shrinks evenly until it fits. Uses the full screen height, not
 * the part left above the keyboard.
 */
function fitDialCard() {
  // The new-call keypad, and the call screen (with its keypad open mid-call) the same way.
  const card = document.querySelector<HTMLElement>(".dialer-sheet .dial-card, .call-screen .call-card");
  if (!card) return;
  const sheet = card.parentElement!;
  card.style.removeProperty("zoom");
  const cs = getComputedStyle(sheet);
  const top = Math.max(0, sheet.getBoundingClientRect().top); // anything above the sheet (a banner) takes room too
  const room = window.innerHeight - top - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom) - 4;
  const need = card.getBoundingClientRect().height;
  if (need > room && need > 0) card.style.setProperty("zoom", String(Math.max(0.6, Math.floor((room / need) * 100) / 100)));
}
window.addEventListener("resize", () => fitDialCard());
window.addEventListener("orientationchange", () => setTimeout(fitDialCard, 300));

/**
 * The number on the dialer, grouped as you type like the iPhone keypad: (310) 555-1234, 1 (310) 555-1234.
 * Only for plain US/Canada digits; anything else (+44…, *, #, extensions) shows exactly as typed.
 */
function dialFormat(raw: string): string {
  if (!/^\d+$/.test(raw)) return raw;
  const one = raw.length > 1 && raw[0] === "1" ? "1 " : "";
  const d = one ? raw.slice(1) : raw;
  if (d.length > 10 || (!one && raw[0] === "0")) return raw;
  if (d.length <= 3) return one ? `${one}${d}` : d;
  if (d.length <= 6) return `${one}(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `${one}(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
const dialRaw = (v: string) => v.replace(/[\s()\-.]/g, "");

// ---------------------------------------------------------------- new group text (up to 6 people)

const GROUP_MAX = 6;
const groupDraft = { members: [] as { number: string; name: string | null }[], input: "", hits: [] as { number: string; name: string }[], error: "" };
let groupSearchTimer: number | undefined;
function addGroupMember(number: string, name: string | null) {
  const n = toE164(number);
  if (!n) return void ((groupDraft.error = "That isn't a full phone number"), render());
  if (groupDraft.members.some((m) => m.number === n)) return;
  if (groupDraft.members.length >= GROUP_MAX) return void ((groupDraft.error = `A group text can have up to ${GROUP_MAX} people`), render());
  if (n === state.teamNumber) return void ((groupDraft.error = "That's our own number"), render());
  groupDraft.members.push({ number: n, name });
  groupDraft.input = "";
  groupDraft.hits = [];
  groupDraft.error = "";
  typedValues.delete("group-input");
  render();
  requestAnimationFrame(() => document.getElementById("group-input")?.focus());
}
function newGroupView() {
  const d = groupDraft;
  const term = d.input.trim().toLowerCase();
  const fromInbox = term.length >= 2
    ? state.conversations.filter((c) => !isGroup(c.external_number) && c.contact_name && c.contact_name.toLowerCase().includes(term)).map((c) => ({ number: c.external_number, name: c.contact_name! }))
    : [];
  const seen = new Set(d.members.map((m) => m.number));
  const hits = [...d.hits, ...fromInbox].filter((x) => !seen.has(x.number) && (seen.add(x.number), true)).slice(0, 6);
  const start = () => {
    const nums = [...new Set(d.members.map((m) => m.number))].sort();
    if (nums.length < 2) return void ((d.error = "Add at least 2 people"), render());
    for (const m of d.members) if (m.name) memberNames.set(m.number, m.name);
    const key = `grp:${nums.join(",")}`;
    groupDraft.members = [];
    state.composeMode = "text";
    go({ name: "thread", number: key });
    requestAnimationFrame(() => document.getElementById("draft")?.focus());
  };
  return screen(
    h("header", { class: "thread-head" }, backButton({ name: "dialer" }),
      h("div", { class: "who" }, h("div", { class: "title" }, "New group text"), h("div", { class: "number" }, `Up to ${GROUP_MAX} people · everyone sees the replies`))),
    h("div", { class: "page" }, h("div", { class: "page-inner group-new" },
      h("div", { class: "group-chips" }, ...d.members.map((m) => h("span", { class: "group-chip" },
        avatar(m.number, m.name, "xs"), h("span", {}, m.name ?? phone(m.number)),
        h("button", { type: "button", class: "icon-btn sm", "aria-label": `Remove ${m.name ?? phone(m.number)}`, onclick: () => ((d.members = d.members.filter((x) => x !== m)), render()) }, icon("close", "sm"))))),
      d.members.length < GROUP_MAX ? h("input", { id: "group-input", class: "field", type: "text", inputmode: "text", autocomplete: "off", value: d.input,
        placeholder: d.members.length ? "Add another (name or number)" : "Name or number", "aria-label": "Add a person",
        oninput: (e: Event) => {
          d.input = (e.target as HTMLInputElement).value;
          d.error = "";
          render();
          clearTimeout(groupSearchTimer);
          const t = d.input.trim();
          if (t.length >= 2 && !toE164(t)) groupSearchTimer = window.setTimeout(async () => {
            try {
              const list = await api.contacts(t);
              if (d.input.trim() !== t) return;
              d.hits = list.slice(0, 6).map((c) => ({ number: c.number, name: c.name }));
              render();
            } catch {
              // conversations still suggest
            }
          }, 180);
        },
        onkeydown: (e: KeyboardEvent) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          if (toE164(d.input)) addGroupMember(d.input, null);
          else if (hits[0]) addGroupMember(hits[0].number, hits[0].name);
        } }) : null,
      hits.length && d.members.length < GROUP_MAX ? h("div", { class: "ai-matches", role: "listbox" }, ...hits.map((c) => h("button", { type: "button", class: "ai-match", role: "option",
        onpointerdown: (e: PointerEvent) => e.preventDefault(), onclick: () => addGroupMember(c.number, c.name) },
        avatar(c.number, c.name, "xs"), h("span", {}, c.name), h("small", {}, phone(c.number))))) : null,
      toE164(d.input) && !hits.length ? h("button", { class: "btn quiet sm", type: "button", onclick: () => addGroupMember(d.input, null) }, icon("userPlus", "sm"), `Add ${phone(toE164(d.input)!)}`) : null,
      d.error ? h("div", { class: "ai-err", role: "alert" }, d.error) : null,
      h("div", { class: "group-foot" },
        h("small", {}, state.line === "personal" ? "From your personal number." : "From the office number. Replies from anyone in the group land here."),
        h("button", { class: "btn", type: "button", disabled: d.members.length < 2, onclick: start }, icon("message", "sm"), "Start group text")))));
}

/** Keypad header: "Joy Phone ⌄" ⇄ "Yossi's Phone ⌄" (admins with a personal line). Calls then go from that number. */
function dialLineName() {
  if (!state.personalLine) return h("span", {}, "Joy Phone");
  if (!aiAgent.pa) void loadPersonal();
  const mine = `${aiAgent.pa?.name || firstName(state.user?.name ?? "") || "My"}'s Phone`;
  const personal = state.line === "personal";
  return h("button", { class: `dial-line${personal ? " personal" : ""}`, type: "button",
    "aria-label": personal ? `Calling from ${mine}. Switch to Joy Phone` : `Calling from Joy Phone. Switch to ${mine}`,
    title: personal ? `Calls go out from your personal number` : "Calls go out from the office number",
    onclick: () => switchLine(personal ? "team" : "personal", { name: "dialer" }) },
    personal ? mine : "Joy Phone", h("span", { class: "brand-swap", "aria-hidden": "true" }, icon("chevronDown", "sm")));
}

/** The keypad's Text button was tapped with nothing typed: the Group text option shows. */
let dialTextOpen = false;

function dialerView() {
  const display = h("input", {
    id: "dial-display", class: "display", type: "tel", inputmode: "tel", autocomplete: "tel", placeholder: "Enter a number",
    value: dialFormat(state.dialed), "aria-label": "Phone number",
    oninput: (e: Event) => {
      const el = e.target as HTMLInputElement;
      const atEnd = el.selectionStart === el.value.length;
      state.dialed = dialRaw(el.value);
      const shown = dialFormat(state.dialed);
      if (shown !== el.value) {
        el.value = shown;
        if (atEnd) el.setSelectionRange(shown.length, shown.length);
      }
    },
    onkeydown: (e: KeyboardEvent) => {
      if (e.key === "Enter") void dial(state.dialed);
      if (e.key === "Escape") go({ name: "inbox" });
    },
  });
  // On a computer, the number box is ready for typing (and Enter calls).
  if (matchMedia("(pointer: fine)").matches) requestAnimationFrame(() => {
    if (document.activeElement?.id !== "dial-display") {
      display.focus();
      display.setSelectionRange(display.value.length, display.value.length);
    }
  });
  const press = (k: string) => {
    playDtmf(k, true);
    state.dialed += k;
    display.value = dialFormat(state.dialed);
    if (dialTextOpen) ((dialTextOpen = false), render()); // typing a number: the group option goes away
  };
  // Mic switch: "keep the mic ready". Nothing waits for it: the keypad and Call always work, and a call opens the
  // mic itself when it starts. Phones: off until you turn it on (then the mic stays ready while the app is open).
  const cellMode = viaCell();
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((k) =>
    h("button", { class: "key", "aria-label": k, ...pressKey(() => press(k)) }, k, h("small", {}, LETTERS[k] ?? " ")));
  const micSwitch = micSwitchButton();
  const message = () => {
    const e164 = toE164(state.dialed);
    // Nothing typed: Text offers a group text (the button stays hidden until then).
    if (!state.dialed.trim()) return void ((dialTextOpen = !dialTextOpen), render());
    if (!e164) return toast("Enter a full phone number");
    state.dialed = "";
    go({ name: "thread", number: e164 });
    state.composeMode = "text";
    render();
    document.getElementById("draft")?.focus();
  };
  // The keypad sits on the same frosted-glass card as the incoming-call window.
  return h("section", { class: "sheet dialer-sheet glass-stage" }, h("div", { class: "glass-card dial-card" },
    h("div", { class: "glass-top" },
      h("button", { class: "icon-btn sm glass-min back-btn", "aria-label": "Back", onclick: () => go({ name: "inbox" }) }, icon("back", "sm")),
      brandMark(), dialLineName(), h("span", { class: "glass-dot" }), h("span", {}, "New call")),
    display,
    cellMode
      ? h("button", { class: "mic-switch on via-cell", type: "button", title: "Change in Settings › Calls & forwarding", onclick: () => go({ name: "settings-calling" }) },
          h("span", { class: "mic-icon" }, icon("phone", "sm")), h("span", { class: "mic-label" }, `Calls ring your cell ${phone(state.user!.forwardNumber!)} first`))
      : micSwitch,
    h("div", { class: "pad" }, ...keys),
    // Shown after tapping Text with no number typed: text one number (type it) or start a group text.
    dialTextOpen && !state.dialed.trim()
      ? h("div", { class: "dial-text-options" },
          h("span", {}, "Type a number to text, or"),
          h("button", { class: "group-text-btn", type: "button", title: `Text up to ${GROUP_MAX} people at once`, onclick: () => ((dialTextOpen = false), go({ name: "new-group" })) },
            icon("users", "sm"), "Group text"))
      : null,
    h("div", { class: "glass-actions dial-actions" },
      h("div", { class: "ctrl" }, h("button", { class: "icon-btn glass-round", style: "width:52px;height:52px", "aria-label": "Send a text", title: "Send a text", onclick: message }, icon("message")), "Text"),
      state.user!.canCall
        ? h("div", { class: "ctrl" }, h("button", { class: "call-fab", "aria-label": "Call", onclick: () => void dial(state.dialed) }, icon("phone", "lg")), "Call")
        : h("span", { style: "width:72px" }),
      h("div", { class: "ctrl" }, h("button", { class: "icon-btn glass-round", style: "width:52px;height:52px", "aria-label": "Delete digit", onclick: () => {
        state.dialed = state.dialed.slice(0, -1);
        display.value = dialFormat(state.dialed);
      } }, icon("delete")), "Delete"))));
}

// ---------------------------------------------------------------- settings

/**
 * Empty screens: a soft coloured icon tile, a short friendly title and one line, plus an optional button
 * (e.g. back to all conversations), instead of a blank list.
 */
function emptyState(o: { icon: string; tone?: string; title: string; text?: string; action?: { label: string; run: () => void } }) {
  return h("div", { class: "empty pretty" },
    h("span", { class: `empty-icon tone-${o.tone ?? "teal"}` }, icon(o.icon)),
    h("strong", {}, o.title),
    o.text ? h("p", {}, o.text) : null,
    o.action ? h("button", { class: "btn quiet sm empty-action", type: "button", onclick: o.action.run }, o.action.label) : null);
}

/** Settings: each kind of setting gets its own soft colour tile (styles.css .tone-*), so it's quicker to find. */
const SETTINGS_TONE: Record<string, string> = {
  "settings-calling": "teal", "settings-notifications": "gold", "settings-sounds": "red", "settings-audio": "blue", security: "slate",
  speed: "green", team: "green", "phone-settings": "teal", "email-alerts": "gold", "settings-rcs": "blue", "call-settings": "purple",
  scheduled: "blue", snippets: "gold", blocked: "red", "import-quo": "slate", storage: "slate", billing: "green", "phone-numbers": "teal",
  tasks: "green", analytics: "blue", search: "slate",
};

function settingsView() {
  const theme = storedTheme();
  const seg = (t: Theme, label: string) =>
    h("button", { "aria-pressed": String(theme === t), onclick: () => (applyTheme(t), render()) }, label);
  const link = (iconName: string, title: string, sub: string, to: Route) =>
    h("button", { class: "item link", onclick: () => go(to) },
      h("span", { class: `item-icon tone-${SETTINGS_TONE[to.name] ?? "teal"}` }, icon(iconName)),
      h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, title), h("div", { class: "sub" }, sub)),
      icon("chevron", "sm"));
  return screen(
    h("header", { class: "thread-head" }, h("div", { class: "who" }, h("div", { class: "title" }, "Settings"))),
    h("div", { class: "page" }, h("div", { class: "page-inner" },
      // Phones: who's online (the computer shows this in the sidebar). Admins tap a name for since / last seen.
      h("div", { class: "only-narrow" }, h("div", { class: "group-title" }, "Agents"),
        // One list: Selena first, then everyone else (same rows as the sidebar).
        ((list) => {
          if (!list) return h("div", { class: "group team-presence agents-page", role: "list" }, aiAgentRow("page"));
          list.classList.add("agents-page");
          list.prepend(aiAgentRow("page"));
          return list;
        })(state.team.length > 1 ? teammatesOnline("page") : null)),
      h("div", { class: "group-title" }, "Account"),
      h("div", { class: "group" },
        h("div", { class: "item" }, avatar(state.user!.email, state.user!.name),
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, state.user!.name),
            h("div", { class: "sub" }, `${state.user!.email} · ${state.user!.role === "admin" ? "Admin" : "Member"}`)),
          lineState()),
        state.editingMyName
          ? h("form", { class: "item stack", onsubmit: async (e: Event) => {
              e.preventDefault();
              try {
                state.user = (await api.setMyName((document.getElementById("my-name") as HTMLInputElement).value)).user;
                state.editingMyName = false;
                toast("Name updated");
                await loadTeam();
              } catch (err) {
                toast((err as Error).message);
              }
            } },
              h("input", { id: "my-name", class: "field", value: state.user!.name, placeholder: "Your name", autocomplete: "name", required: true }),
              h("div", { class: "row-actions" },
                h("button", { class: "btn sm", type: "submit" }, "Save name"),
                h("button", { class: "btn quiet sm", type: "button", onclick: () => ((state.editingMyName = false), render()) }, "Cancel")))
          : h("button", { class: "item link", onclick: () => ((state.editingMyName = true), render()) },
              h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Edit name"), h("div", { class: "sub" }, "How teammates see you in comments and chat")), icon("chevron", "sm")),
        state.changingPassword
          ? h("form", { class: "item stack", onsubmit: async (e: Event) => {
              e.preventDefault();
              const cur = (document.getElementById("pw-cur") as HTMLInputElement).value;
              const n1 = (document.getElementById("pw-new") as HTMLInputElement).value;
              const n2 = (document.getElementById("pw-new2") as HTMLInputElement).value;
              if (n1 !== n2) return toast("The new passwords don't match");
              try {
                await api.changePassword(cur, n1);
                void syncPushSubscription(); // 1.10.28: the server dropped every device's alerts; this one comes back
                state.changingPassword = false;
                toast("Password changed. Your other devices were signed out.");
                render();
              } catch (err) {
                toast((err as Error).message);
              }
            } },
              h("input", { type: "text", autocomplete: "username", value: state.user?.email ?? "", tabindex: -1, "aria-hidden": "true", class: "pm-user", readonly: true }),
              h("input", { id: "pw-cur", class: "field", type: "password", placeholder: "Current password", autocomplete: "current-password", required: true }),
              h("input", { id: "pw-new", class: "field", type: "password", placeholder: "New password (10+ characters)", autocomplete: "new-password", minlength: 10, required: true }),
              h("input", { id: "pw-new2", class: "field", type: "password", placeholder: "Confirm new password", autocomplete: "new-password", required: true }),
              h("div", { class: "row-actions" },
                h("button", { class: "btn sm", type: "submit" }, "Change password"),
                h("button", { class: "btn quiet sm", type: "button", onclick: () => ((state.changingPassword = false), render()) }, "Cancel")))
          : h("button", { class: "item link", onclick: () => ((state.changingPassword = true), render()) },
              h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Change password")), icon("chevron", "sm"))),
      state.user!.canCall
        ? h("div", { class: "group", style: "margin-top:12px" },
            h("div", { class: "item" },
              h("span", { class: "item-icon tone-purple" }, icon("moon")),
              h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Do Not Disturb"),
                h("div", { class: "sub" }, state.user!.dnd ? "On: you won't ring. Texts and alerts still come in." : "Turn on to stop ringing (lunch, meetings, off shift).")),
              h("label", { class: "switch", for: "dnd-on" },
                h("input", { id: "dnd-on", type: "checkbox", checked: !!state.user!.dnd, onchange: (e: Event) => void toggleDnd((e.target as HTMLInputElement).checked) }),
                h("span", { class: "track-sw" }), h("span", { class: "sr-only" }, "Do Not Disturb"))),
            "registerProtocolHandler" in navigator && matchMedia("(pointer: fine)").matches
              ? h("div", { class: "item" },
                  h("span", { class: "item-icon tone-teal" }, icon("phone")),
                  h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Open phone links with Joy Phone"),
                    h("div", { class: "sub" }, "Click a phone number on any website (or in email) to call it from here.")),
                  h("button", { class: "btn quiet sm", onclick: () => {
                    try {
                      navigator.registerProtocolHandler("tel", `${location.origin}/?dial=%s`);
                      toast("Your browser will ask to confirm");
                    } catch {
                      toast("This browser doesn't allow it");
                    }
                  } }, "Set up"))
              : null)
        : null,
      h("div", { class: "group-title" }, "Your preferences"),
      h("div", { class: "group" },
        // Light or dark: right at the top of your preferences (it used to sit at the very bottom of Settings).
        h("div", { class: "item theme-row" },
          h("span", { class: "item-icon tone-blue" }, icon("theme")),
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Light or dark"), h("div", { class: "sub" }, "Auto follows your phone or computer")),
          h("div", { class: "segmented", role: "group", "aria-label": "Light or dark" }, seg("system", "Auto"), seg("light", "Light"), seg("dark", "Dark"))),
        // Brightness lever: Darker ↔ Lighter, live as you drag (this device only).
        h("div", { class: "item shade-item" },
          h("span", { class: "item-icon tone-gold" }, icon("sun")),
          h("div", { class: "grow" },
            h("div", { class: "shade-head" }, h("span", { style: "font-weight:600" }, "Brightness"),
              Math.abs(storedShade()) >= 0.12 ? h("button", { class: "text-btn shade-reset", type: "button", onclick: () => (applyShade(0), render()) }, "Standard") : null),
            h("div", { class: "shade-lever" },
              h("span", { class: "shade-end" }, "Darker"),
              // Smooth: any point along the lever (not steps); it snaps to standard right at the middle notch.
              h("input", { id: "shade", type: "range", min: SHADE_MIN, max: SHADE_MAX, step: "any", value: storedShade(), "aria-label": "Brightness",
                style: `--pos:${((storedShade() - SHADE_MIN) / (SHADE_MAX - SHADE_MIN)) * 100}%`,
                oninput: (e: Event) => {
                  const el = e.target as HTMLInputElement;
                  let v = Number(el.value);
                  if (Math.abs(v) < 0.12) v = 0;
                  el.style.setProperty("--pos", `${((v - SHADE_MIN) / (SHADE_MAX - SHADE_MIN)) * 100}%`);
                  applyShade(Math.round(v * 100) / 100);
                },
                onchange: () => render() }),
              h("span", { class: "shade-end" }, "Lighter")))),
        link("phone", "Calls & forwarding", "Ring my cell, my voicemail greeting, text signature", { name: "settings-calling" }),
        link("bell", "Notifications", "What alerts you get, quiet hours", { name: "settings-notifications" }),
        link("music", "Sounds & ringtones", "Your own ringtone and call sounds, on all your devices", { name: "settings-sounds" }),
        link("headset", "Audio devices", "Microphone, speaker, mic test", { name: "settings-audio" }),
        link("lock", "Security & sign-in", isAdmin() ? `${bioName()}, two-step sign-in, calling protection` : `${bioName()}, two-step sign-in`, { name: "security" })),
      state.user!.canCall
        ? h("div", { class: "group-title" }, "Call speed")
        : null,
      state.user!.canCall
        ? h("div", { class: "group" }, link("chart", "Call speed", "Measure and use the fastest connection for calls", { name: "speed" }))
        : null,
      // Your own phone has its own settings, apart from the team's (admins with a personal line only).
      isAdmin() && state.personalLine ? h("div", { class: "group-title" }, `${personalLineName()} · just you`) : null,
      isAdmin() && state.personalLine ? h("div", { class: "group" },
        h("button", { class: "item link", onclick: () => ((aiAgent.tab = "voice"), (aiAgent.side = "personal"), go({ name: "ai-agent" })) },
          h("span", { class: "item-icon tone-purple" }, icon("lock")),
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Your phone and personal assistant"),
            h("div", { class: "sub" }, `Your number, voicemail greeting, caller ID, what ${state.agents?.personal?.name ?? aiName()} may use and say for you`)),
          icon("chevron", "sm"))) : null,
      h("div", { class: "group-title" }, isAdmin() && state.personalLine ? "Joy Travel · whole team" : "Workspace"),
      h("div", { class: "group" },
        link("users", "Team", isAdmin() ? "Invite teammates, manage roles" : "See who's on the team", { name: "team" }),
        isAdmin() ? link("menu", "Phone line", "Greeting, phone menu, business hours, auto-texts, hold music", { name: "phone-settings" }) : null,
        isAdmin() ? link("bell", "Email alerts", "Missed calls, voicemails and texts by email", { name: "email-alerts" }) : null,
        isAdmin() ? link("message", "RCS texts", "Rich texts that show “Read” (needs an approved RCS agent)", { name: "settings-rcs" }) : null,
        isAdmin() ? link("transfer", "Call handling", "Ring order, no-answer forwarding, recording, listen in, caller ID", { name: "call-settings" }) : null,
        // Selena's own settings live on her screen (Selena → Settings); this opens them there.
        h("button", { class: "item link", onclick: () => ((aiAgent.tab = "voice"), (aiAgent.side = "business"), go({ name: "ai-agent" })) },
          h("span", { class: "item-icon tone-teal" }, icon("sparkle")),
          // Joy Travel's assistant (this row opens the team side), whichever line you're on.
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, `${state.agents?.team.name ?? aiName()} (AI assistant)`),
            h("div", { class: "sub" }, pron("Voice, language, what she may do and what she can say", state.agents?.team.man ?? state.agentMan))),
          icon("chevron", "sm")),
        link("clock", "Scheduled texts", "Texts waiting to go out", { name: "scheduled" }),
        link("bolt", "Saved replies", "Texts you send often, one tap away", { name: "snippets" }),
        link("block", "Blocked numbers", "Spam callers and texters", { name: "blocked" }),
        isAdmin() ? link("phone", "Phone numbers", "Your Telnyx numbers: bring ported ones into the inbox, pick the office line", { name: "phone-numbers" }) : null,
        isAdmin() ? link("download", "Import from Quo", "Bring over contacts, notes, tasks, texts, calls and recordings", { name: "import-quo" }) : null,
        isAdmin() ? link("chart", "Storage", "Space for calls, recordings and messages on the server", { name: "storage" }) : null,
        isAdmin() ? link("chart", "Billing", "What Telnyx charged this month, and for what", { name: "billing" }) : null),
      h("div", { class: "group-title only-narrow-block" }, "More"),
      h("div", { class: "group only-narrow-block" },
        link("tasks", "Tasks", "Follow-ups with reminders", { name: "tasks" }),
        link("chart", "Analytics", "Calls answered, missed, reply times", { name: "analytics" }),
        link("search", "Search", "Texts, notes, call transcripts", { name: "search" })),
      h("div", { class: "group-title" }, "Notifications"),
      h("div", { class: "group" },
        h("div", { class: "item" },
          h("div", { class: "grow" },
            h("div", { style: "font-weight:600" }, alertsOn() ? "Enabled on this device" : "Disabled"),
            h("div", { class: "sub" }, alertsOn()
              ? "To get calls during a Focus mode, add Joy Phone to that Focus's allowed apps."
              : "Enable to get calls, texts and mentions when the app is closed.")),
          !alertsOn() ? h("button", { class: "btn sm", onclick: () => void enableAlerts() }, "Enable") : null)),
      h("div", { style: "height:24px" }),
      h("button", { class: "btn quiet block", onclick: () => void signOut() }, "Log out"),
      versionLabel())));
}

// ---------------------------------------------------------------- team & invites

function teamView() {
  const me = state.user!;
  const admin = isAdmin();
  const name = h("input", { id: "inv-name", class: "field", placeholder: "Name", autocomplete: "off" });
  const email = h("input", { id: "inv-email", class: "field", type: "email", placeholder: "Email", autocomplete: "off", autocapitalize: "none" });
  const role = h("select", { id: "inv-role", class: "field" }, h("option", { value: "member" }, "Member"), h("option", { value: "admin" }, "Admin"));

  async function createInvite(e: Event) {
    e.preventDefault();
    try {
      const r = await api.invite(name.value, email.value, role.value as "admin" | "member");
      state.newInviteUrl = r.url;
      state.linkLabel = "Invite link ready";
      await loadTeam();
    } catch (err) {
      toast((err as Error).message);
    }
  }

  const share = async (url: string) => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Join Joy Phone", text: "You're invited to Joy Travel's phone line:", url });
        return;
      } catch {
        // fall back to copy
      }
    }
    await navigator.clipboard?.writeText(url).then(() => toast("Link copied"), () => toast("Copy the link above"));
  };

  return screen(
    h("header", { class: "thread-head" }, backButton({ name: "settings" }), h("div", { class: "who" }, h("div", { class: "title" }, "Team"))),
    h("div", { class: "page" }, h("div", { class: "page-inner" },
      // 1.10.28 "regular users can't block notifications": one switch for the whole team (admins aren't affected).
      admin ? h("div", { class: "group-title" }, "Alerts") : null,
      admin ? h("div", { class: "group" }, switchRow("alerts-lock", "Keep teammates' alerts on",
        `Members can't turn off alerts, set quiet hours, use Do Not Disturb or pick a silent ("None") sound, and the app's sound stays at 60% or more. Their phone's own Focus, silent switch or blocked notifications can't be overridden: “No alerts” below shows who gets none.`,
        teamAlertsLocked, (v) => void (async () => {
          try {
            teamAlertsLocked = (await api.setAlertsLock(v)).locked;
            toast(teamAlertsLocked ? "Teammates' alerts are kept on" : "Teammates can choose their own alerts again");
            await loadTeam();
          } catch (e) {
            toast((e as Error).message);
          }
          render();
        })())) : null,
      h("div", { class: "group-title" }, `Members · ${state.team.length}`),
      h("div", { class: "group" },
        ...state.team.map((u) => memberRow(u, admin, me.id))),
      admin ? h("div", { class: "group-title" }, "Invite a teammate") : null,
      admin
        ? h("form", { class: "group form", onsubmit: createInvite },
            h("div", { class: "form-row" }, name, email, role),
            h("button", { class: "btn", type: "submit" }, icon("userPlus", "sm"), "Create invite link"),
            h("p", { class: "sub" }, "Send them the link by text or email. It works once and expires in 7 days. They choose their own password, and their phone gets its own line to ring."))
        : null,
      state.newInviteUrl
        ? h("div", { class: "group invite-ready" },
            h("div", { class: "item" },
              h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, state.linkLabel),
                h("input", { class: "field mono", readonly: true, value: state.newInviteUrl, onfocus: (e: Event) => (e.target as HTMLInputElement).select() })),
              h("div", { class: "row-actions" },
                h("button", { class: "btn sm", onclick: () => void share(state.newInviteUrl!) }, icon("share", "sm"), "Share"),
                h("button", { class: "btn quiet sm", onclick: () => void navigator.clipboard?.writeText(state.newInviteUrl!).then(() => toast("Link copied")) }, icon("copy", "sm"), "Copy"))))
        : null,
      admin && state.invites.length ? h("div", { class: "group-title" }, "Pending invites") : null,
      admin && state.invites.length
        ? h("div", { class: "group" },
            ...state.invites.map((i) =>
              h("div", { class: "item" }, avatar(i.email, i.name),
                h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, i.name),
                  h("div", { class: "sub" }, `${i.email} · invited by ${firstName(i.invitedBy)} · expires ${timeAgoFuture(i.expiresAt)}`)),
                h("div", { class: "row-actions" },
                  h("button", { class: "text-btn", onclick: async () => {
                    try {
                      state.newInviteUrl = (await api.invite(i.name, i.email, i.role)).url;
                      state.linkLabel = "Invite link ready";
                      toast("New link created");
                      await loadTeam();
                    } catch (e) {
                      toast((e as Error).message);
                    }
                  } }, "New link"),
                  h("button", { class: "text-btn danger", onclick: async () => {
                    await api.revokeInvite(i.id).catch(() => {});
                    await loadTeam();
                  } }, "Revoke")))))
        : null)));
}

function memberRow(u: User, admin: boolean, myId: number) {
  const open = admin && state.managing === u.id;
  const row = h("div", { class: "item" }, avatar(u.email, u.name),
    h("div", { class: "grow" },
      h("div", { style: "font-weight:600" }, u.name, u.id === myId ? h("span", { class: "muted" }, " (you)") : null),
      h("div", { class: "sub" }, u.email)),
    u.dnd ? h("span", { class: "role-badge dnd" }, "Do not disturb") : null,
    // 1.10.28: admins see who gets no alerts on any device (blocked, or never turned on).
    admin && u.alertDevices === 0 ? h("span", { class: "role-badge no-alerts", title: "No device gets Joy Phone alerts for them: notifications are blocked or were never turned on. They can turn them on in Settings → Notifications." }, "No alerts") : null,
    h("span", { class: u.role === "admin" ? "role-badge admin" : "role-badge" }, u.role === "admin" ? "Admin" : "Member"),
    admin
      ? h("button", { class: "text-btn", "aria-expanded": String(open), onclick: () => {
          state.managing = open ? null : u.id;
          state.confirmRemove = null;
          render();
        } }, open ? "Done" : "Manage")
      : null);
  if (!open) return row;

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok);
      await loadTeam();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const nameInput = h("input", { id: `m-name-${u.id}`, class: "field", value: u.name, autocomplete: "off" });
  const pwInput = h("input", { id: `m-pw-${u.id}`, class: "field", type: "password", placeholder: "New password (10+ characters)", autocomplete: "new-password" });
  // Whose password this is, for the browser's password manager (hidden): without it Chrome puts YOUR saved email
  // into the nearest text box (the teammate's Name, or the sidebar search).
  const pwUser = h("input", { type: "text", autocomplete: "username", value: u.email, tabindex: -1, "aria-hidden": "true", class: "pm-user", readonly: true });
  const panel = h("div", { class: "manage-panel" },
    h("label", { class: "mini-label", for: `m-name-${u.id}` }, "Name"),
    h("div", { class: "inline-form" }, nameInput,
      h("button", { class: "btn sm", onclick: () => void act(() => api.updateUser(u.id, { name: nameInput.value }), "Name saved") }, "Save")),
    // 7.10.30: your own password is changed in Settings → Change password (it asks for your current one); the
    // server refuses it here.
    u.id === myId ? h("p", { class: "muted mini-label" }, "To change your own password, use Change password in Settings (it asks for your current one).") : null,
    u.id === myId ? null : h("label", { class: "mini-label", for: `m-pw-${u.id}` }, `Set a new password for ${firstName(u.name)}`),
    u.id === myId ? null : h("div", { class: "inline-form" }, pwUser, pwInput,
      h("button", { class: "btn sm", onclick: () => void act(async () => {
        await api.setUserPassword(u.id, pwInput.value);
        pwInput.value = "";
      }, u.id === myId ? "Password changed" : `Password set. Tell ${firstName(u.name)} the new one; they've been signed out.`) }, "Set")),
    u.canCall ? memberSiteSetting(u) : null,
    u.id !== myId
      ? h("div", { class: "row-actions" },
          h("button", { class: "btn quiet sm", onclick: async () => {
            try {
              state.newInviteUrl = (await api.resetLink(u.id)).url;
              state.linkLabel = `Password reset link for ${firstName(u.name)} (works once, 24 hours)`;
              render();
            } catch (e) {
              toast((e as Error).message);
            }
          } }, icon("share", "sm"), "Send reset link instead"),
          h("button", { class: "btn quiet sm", onclick: () => void act(() => api.updateUser(u.id, { role: u.role === "admin" ? "member" : "admin" }), "Role updated") },
            u.role === "admin" ? "Make member" : "Make admin"),
          h("button", { class: "btn quiet sm", title: "For a lost phone: they set up two-step sign-in / Face ID again", onclick: async () => {
            if (!(await glassDialog({ title: `Reset ${firstName(u.name)}'s two-step sign-in?`, message: "Removes their authenticator app, backup codes and Face ID. They'll set it up again at their next sign-in.", confirm: "Reset", danger: true }))) return;
            await act(() => api.resetTwoFactor(u.id), "Two-step sign-in reset");
          } }, "Reset 2-step"),
          state.confirmRemove === u.id
            ? h("button", { class: "btn danger sm", onclick: () => void act(async () => {
                await api.removeUser(u.id);
                state.managing = null;
                state.confirmRemove = null;
              }, `${firstName(u.name)} was removed`) }, `Yes, remove ${firstName(u.name)}`)
            : h("button", { class: "btn quiet sm danger-text", onclick: () => ((state.confirmRemove = u.id), render()) }, icon("trash", "sm"), "Remove"))
      : null);
  return h("div", { class: "member-block" }, row, panel);
}

/**
 * Where this teammate's app call audio is handled. Normally the team's media site; a teammate far away (e.g. in
 * the Philippines) can have a site near them (e.g. Sydney). Telnyx doesn't charge more for it.
 */
function memberSiteSetting(u: User) {
  const ts = state.teamSites;
  if (ts === null) {
    state.teamSites = "loading";
    void api.teamMediaSites().then((r) => (state.teamSites = r), (e) => (state.teamSites = { sites: [], users: {}, error: (e as Error).message })).finally(render);
  }
  if (!ts || ts === "loading") return h("div", { class: "sub", style: "margin:10px 2px" }, "Loading media site…");
  if (ts.error) return h("div", { class: "sub bad", style: "margin:10px 2px" }, ts.error);
  const current = ts.users[u.id] ?? "";
  return h("div", {},
    h("label", { class: "mini-label", for: `m-site-${u.id}` }, `Where ${firstName(u.name)}'s call audio is handled`),
    h("select", { id: `m-site-${u.id}`, class: "field", onchange: async (e: Event) => {
      const el = e.target as HTMLSelectElement;
      const site = el.value || null;
      el.disabled = true;
      try {
        await api.setUserMediaSite(u.id, site);
        ts.users = { ...ts.users };
        if (site) ts.users[u.id] = site;
        else delete ts.users[u.id];
        toast(site ? `${firstName(u.name)}'s calls now go through ${site}. Their phone line reconnects by itself.` : `${firstName(u.name)} is back on the team's media site.`);
      } catch (err) {
        toast((err as Error).message);
      }
      render();
    } },
      h("option", { value: "", selected: !current }, "Same as the team"),
      ...ts.sites.map((site) => h("option", { value: site, selected: current === site }, site))),
    h("div", { class: "sub", style: "margin:6px 2px 4px" }, "For a teammate far from the rest of the team. Calls to US and Canadian customers: pick the US site nearest them (Philippines or Asia: San Jose), not the overseas site nearest them. A site like Sydney is closer to them but sends every US call on a detour of about 7,000 km. Only their calls change. Call prices stay the same."),
    current && onwardMs(current) > 0 ? h("div", { class: "sub warn", style: "margin:2px 2px 4px" }, `${current} adds about ${onwardMs(current)} ms of round trip before audio reaches a US customer. San Jose is usually faster for US calls.`) : null);
}

function timeAgoFuture(s: string) {
  const d = serverTime(s);
  if (!d) return "";
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000);
  return days <= 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;
}

// ---------------------------------------------------------------- play buttons (greeting sample, hold music)

function stopSample() {
  sample.audio?.pause();
  if (sample.audio?.src.startsWith("blob:")) URL.revokeObjectURL(sample.audio.src);
  sample.audio = null;
  sample.id = "";
}
/** A clear Play / Stop pill. `source` gives a URL (a file) or a Blob (made on demand). */
function playButton(id: string, label: string, source: () => string | Promise<Blob | string>) {
  const playing = sample.id === id;
  const loading = sample.loading === id;
  return h("button", { class: playing ? "play-pill playing" : "play-pill", type: "button", "aria-pressed": String(playing), disabled: loading,
    onclick: async () => {
      if (playing) return void (stopSample(), render());
      stopSample();
      sample.loading = id;
      render();
      try {
        const src = await source();
        const url = typeof src === "string" ? src : URL.createObjectURL(src);
        if (sample.loading !== id) return; // tapped something else meanwhile
        const a = new Audio(url);
        sample.audio = a;
        sample.id = id;
        a.onended = () => {
          if (sample.audio === a) stopSample();
          render();
        };
        await a.play();
      } catch (e) {
        stopSample();
        toast((e as Error).message || "Couldn't play it");
      } finally {
        if (sample.loading === id) sample.loading = "";
        render();
      }
    } },
    loading ? h("span", { class: "spinner sm" }) : icon(playing ? "pauseFill" : "playFill", "sm"),
    loading ? "Loading…" : playing ? "Stop" : label);
}

// ---------------------------------------------------------------- greeting & phone menu (admins)

async function loadPhoneSettings() {
  try {
    const r = await api.phoneSettings();
    state.phoneDraft = r.settings;
    state.phonePreview = r.preview;
    state.voiceProblem = r.voiceProblem ?? null;
    render();
  } catch (e) {
    toast((e as Error).message);
  }
}

/**
 * The typed greeting already reads out every menu choice ("… press 1 … press 2 …")? Then callers don't also hear
 * the menu built from the options. Same rule as greetingSaysMenu in src/settings.ts.
 */
function greetingSaysMenu(text: string, options: { digit: string }[]) {
  const words: Record<string, string> = { "0": "zero", "1": "one", "2": "two", "3": "three", "4": "four", "5": "five", "6": "six", "7": "seven", "8": "eight", "9": "nine" };
  const t = text.toLowerCase();
  return !!options.length && !!t.trim() && options.every((o) =>
    [o.digit, words[o.digit]].some((n) => n && new RegExp(`\\b(press|dial|push|select|choose|option)\\s*(the\\s+)?(number\\s+)?${n}\\b`).test(t)));
}
/** What the menu adds after the greeting (nothing when the greeting already says the choices). */
function menuAfterGreeting(s: PhoneSettings) {
  if (s.menu.prompt.trim()) return s.menu.prompt.trim();
  if (s.greeting.enabled && s.greeting.mode === "text" && greetingSaysMenu(s.greeting.text, s.menu.options)) return "";
  return autoPrompt(s.menu);
}

function autoPrompt(m: PhoneSettings["menu"]) {
  return m.options
    .filter((o) => o.label.trim())
    .map((o) => {
      const word = m.speech ? o.keywords.find((k) => k.trim()) : undefined;
      return `For ${o.label.trim()}, ${word ? `say ${word.trim()}, or ` : ""}press ${o.digit}.`;
    })
    .join(" ");
}

const DAY_ORDER: [Weekday, string][] = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "Europe/London", "Asia/Jerusalem"];

/** "Then…" picker for menu options, after hours and no-answer: ring, voicemail, message, or forward to a number. */
function routeActionField(id: string, a: RouteAction, set: (a: RouteAction) => void, opts: { allowMessage?: boolean; allowTeam?: boolean } = {}) {
  const value = a.type === "user" ? `user:${a.userId}` : a.type;
  const select = h("select", { id, class: "field", onchange: (e: Event) => {
    const v = (e.target as HTMLSelectElement).value;
    set(v.startsWith("user:") ? { type: "user", userId: Number(v.slice(5)) }
      : v === "message" ? { type: "message", text: a.type === "message" ? a.text : "" }
      : v === "forward" ? { type: "forward", number: a.type === "forward" ? a.number : "" }
      : v === "voicemail" ? { type: "voicemail" } : { type: "team" });
  } },
    opts.allowTeam !== false ? h("option", { value: "team", selected: value === "team" }, "Ring everyone") : null,
    ...state.team.filter((u) => u.canCall).map((u) => h("option", { value: `user:${u.id}`, selected: value === `user:${u.id}` }, `Ring ${u.name}`)),
    h("option", { value: "voicemail", selected: value === "voicemail" }, "Go to voicemail"),
    h("option", { value: "forward", selected: value === "forward" }, "Forward to a phone number"),
    opts.allowMessage !== false ? h("option", { value: "message", selected: value === "message" }, "Play a message, then hang up") : null);
  if (a.type !== "forward") return select;
  return h("div", { class: "stack-tight" }, select,
    h("input", { id: `${id}-number`, class: "field", type: "tel", inputmode: "tel", placeholder: "Phone number, e.g. (310) 555-0123", value: a.number ? phone(a.number) : "",
      onchange: (e: Event) => {
        const v = (e.target as HTMLInputElement).value;
        const n = toE164(v);
        if (!n) toast("Enter a full phone number");
        a.number = n ?? v;
      } }),
    h("div", { class: "sub" }, "The caller is connected to that phone (it sees your Joy Travel number). No answer in 25 seconds → voicemail."));
}

const VOICE_CHOICES: { key: VoiceKey; label: string; sub: string }[] = [
  { key: "ava", label: "Ava", sub: "Warm, natural woman's voice" },
  { key: "andrew", label: "Andrew", sub: "Friendly, natural man's voice" },
  { key: "danielle", label: "Danielle", sub: "Clear, upbeat woman's voice" },
  { key: "emma", label: "Emma", sub: "Bright, cheerful woman's voice" },
  { key: "brian", label: "Brian", sub: "Relaxed, easygoing man's voice" },
  { key: "jenny", label: "Jenny", sub: "Professional, friendly woman's voice" },
  { key: "gregory", label: "Gregory", sub: "Calm, confident man's voice" },
];

/** Settings → Phone line: which tab is open, "During business hours" or "After hours" (3.10.23). */
let phoneTab: "day" | "night" = "day";
function phoneSettingsView() {
  const s = state.phoneDraft;
  if (!s) return screen(
    h("header", { class: "thread-head" }, backButton({ name: "settings" }), h("div", { class: "who" }, h("div", { class: "title" }, "Phone line"))),
    h("div", { class: "pending", style: "justify-content:center;padding:40px" }, h("span", { class: "spinner" }), "Loading"));

  const rerender = () => render();
  const toggle = (id: string, on: boolean, label: string, set: (v: boolean) => void) =>
    h("label", { class: "switch", for: id },
      h("input", { id, type: "checkbox", checked: on, onchange: (e: Event) => (set((e.target as HTMLInputElement).checked), rerender()) }),
      h("span", { class: "track-sw" }), h("span", { class: "sr-only" }, label));

  const actionSelect = (id: string, a: RouteAction, set: (a: RouteAction) => void, allowMessage = true) =>
    routeActionField(id, a, (x) => (set(x), rerender()), { allowMessage });

  const upload = async (kind: "greeting" | "hold", file: File | undefined) => {
    if (!file) return;
    try {
      const { url } = await api.uploadAudio(file);
      if (kind === "greeting") s.greeting.audioUrl = url;
      else s.holdMusicUrl = url;
      toast("Uploaded. Save to use it.");
      rerender();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const fileButton = (label: string, kind: "greeting" | "hold") =>
    h("label", { class: "btn quiet sm file-btn" }, icon("upload", "sm"), label,
      h("input", { type: "file", accept: "audio/mpeg,audio/mp3,audio/wav,.mp3,.wav", class: "sr-only", onchange: (e: Event) => void upload(kind, (e.target as HTMLInputElement).files?.[0]) }));

  const options = s.menu.options.map((o: MenuOption, i: number) =>
    h("div", { class: "menu-option" },
      h("div", { class: "opt-key" },
        h("label", { class: "mini-label", for: `opt-key-${i}` }, "Key"),
        h("select", { id: `opt-key-${i}`, class: "field key-select", onchange: (e: Event) => ((o.digit = (e.target as HTMLSelectElement).value), rerender()) },
          ..."1234567890".split("").map((d) => h("option", { value: d, selected: o.digit === d }, d)))),
      h("div", { class: "opt-fields" },
        h("label", { class: "mini-label", for: `opt-label-${i}` }, "Option name (read to callers)"),
        h("input", { id: `opt-label-${i}`, class: "field", value: o.label, placeholder: "reservations", oninput: (e: Event) => {
          o.label = (e.target as HTMLInputElement).value;
          updatePreview();
        } }),
        s.menu.speech ? h("label", { class: "mini-label", for: `opt-words-${i}` }, "Words callers can say (comma separated)") : null,
        s.menu.speech ? h("input", { id: `opt-words-${i}`, class: "field", value: o.keywords.join(", "), placeholder: "reservations, booking", oninput: (e: Event) => {
          o.keywords = (e.target as HTMLInputElement).value.split(",").map((k) => k.trim()).filter(Boolean);
          updatePreview();
        } }) : null,
        h("label", { class: "mini-label", for: `opt-action-${i}` }, "Then"),
        actionSelect(`opt-action-${i}`, o.action, (a) => (o.action = a)),
        o.action.type === "message"
          ? h("textarea", { id: `opt-msg-${i}`, class: "field area", rows: 2, placeholder: "We're open Monday to Friday, 9 to 5…", value: o.action.text,
              oninput: (e: Event) => o.action.type === "message" && (o.action.text = (e.target as HTMLTextAreaElement).value) })
          : null),
      h("button", { class: "icon-btn", "aria-label": `Remove option ${o.digit}`, onclick: () => (s.menu.options.splice(i, 1), typedValues.clear(), rerender()) }, icon("trash", "sm")))); // 1.10.28: see detailsEditor

  const previewEl = h("div", { class: "hear-text" });
  function updatePreview() {
    const parts: string[] = [];
    if (s!.greeting.enabled) parts.push(s!.greeting.mode === "audio" ? "[Your recorded greeting]" : s!.greeting.text);
    if (s!.menu.enabled) parts.push(menuAfterGreeting(s!));
    if (!s!.greeting.enabled && !s!.menu.enabled) parts.push("Callers hear ringing right away, and everyone's phone rings.");
    else if (!s!.menu.enabled) parts.push("Then hold music while everyone's phone rings.");
    previewEl.textContent = parts.filter(Boolean).join(" ");
  }
  updatePreview();

  async function save() {
    state.phoneSaving = true;
    render();
    try {
      const r = await api.savePhoneSettings(s!);
      state.phoneDraft = r.settings;
      state.phonePreview = r.preview;
      toast("Saved. New calls use it right away.");
    } catch (e) {
      toast((e as Error).message);
    }
    state.phoneSaving = false;
    render();
  }

  const nextDigit = () => "1234567890".split("").find((d) => !s.menu.options.some((o) => o.digit === d));
  /** One automatic text: on/off and its words (3.10.23: daytime ones and the after-hours one in their own sections). */
  const autoRule = (key: "missedCall" | "voicemail" | "afterHours", title: string, sub: string): Node[] => {
    const rule = s.autoReply[key];
    return [
      h("div", { class: "item" },
        h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, title), h("div", { class: "sub" }, sub)),
        toggle(`auto-${key}`, rule.enabled, `${title} text`, (v) => (rule.enabled = v))),
      rule.enabled
        ? h("div", { class: "item stack auto-text" },
            h("textarea", { id: `auto-${key}-text`, class: "field area", rows: 3, maxlength: 480, value: rule.text,
              oninput: (e: Event) => (rule.text = (e.target as HTMLTextAreaElement).value) }))
        : null,
    ].filter((x) => !!x) as Node[];
  };

  return screen(
    h("header", { class: "thread-head" }, backButton({ name: "settings" }), h("div", { class: "who" }, h("div", { class: "title" }, "Phone line"))),
    h("div", { class: "page" }, h("div", { class: "page-inner" },
      h("div", { class: "hear" }, h("div", { class: "hear-label" }, icon("phone", "sm"), "What callers hear"), previewEl,
        s.greeting.enabled && s.greeting.mode === "audio" && s.greeting.audioUrl && !s.menu.enabled
          ? playButton("hear-all", "Hear it", () => s.greeting.audioUrl!)
          : s.greeting.enabled || s.menu.enabled
            ? playButton("hear-all", "Hear it", () => {
                const text = [s.greeting.enabled && s.greeting.mode === "text" ? s.greeting.text : "", s.menu.enabled ? menuAfterGreeting(s) : ""].filter(Boolean).join(" ");
                return api.phoneSample(text || "Thanks for calling Joy Travel.", s.voice);
              })
            : null),

      h("div", { class: "group-title" }, "Voice"),
      h("div", { class: "group" },
        h("div", { class: "item stack" },
          h("div", { class: "sub" }, "Who reads your greeting, menu and voicemail prompt to callers. Natural voices at a normal speaking pace."),
          h("div", { class: "voice-grid", role: "radiogroup", "aria-label": "Voice" },
            ...VOICE_CHOICES.map((v) => h("button", { class: "voice-opt", type: "button", role: "radio", "aria-checked": String((s.voice ?? "ava") === v.key),
              onclick: () => ((s.voice = v.key), rerender()) },
              h("span", { class: "voice-name" }, v.label), h("span", { class: "voice-sub" }, v.sub)))),
          h("div", { class: "row-actions" },
            playButton(`voice-${s.voice ?? "ava"}`, `Hear ${VOICE_CHOICES.find((v) => v.key === (s.voice ?? "ava"))?.label ?? "this voice"}`,
              () => api.phoneSample((s.greeting.mode === "text" && s.greeting.text.trim()) || "Thanks for calling Joy Travel. How can we help you today?", s.voice)),
            h("span", { class: "sub" }, "Reads your greeting in this voice, exactly as callers hear it.")),
          state.voiceProblem
            ? h("div", { class: "sub warn-text" }, `On a call ${when(state.voiceProblem.at.replace("T", " ").slice(0, 19))}, Telnyx refused this voice (${state.voiceProblem.reason}), so callers heard the basic voice instead.`)
            : null)),

      h("div", { class: "group-title" }, "Business hours",
        state.office?.enabled && s.hours.enabled ? h("span", { class: state.office.open ? "office-pill open" : "office-pill" }, state.office.open ? "Open now" : "Closed now") : null),
      h("div", { class: "group" },
        h("div", { class: "item" },
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Use business hours"),
            h("div", { class: "sub" }, s.hours.enabled ? "Outside these hours, calls skip ringing." : "Off: phones ring any time.")),
          toggle("hours-on", s.hours.enabled, "Use business hours", (v) => (s.hours.enabled = v))),
        ...(s.hours.enabled ? [
          h("div", { class: "item stack" },
            h("label", { class: "mini-label", for: "hours-tz" }, "Time zone"),
            h("select", { id: "hours-tz", class: "field", onchange: (e: Event) => ((s.hours.timezone = (e.target as HTMLSelectElement).value), rerender()) },
              ...TIMEZONES.concat(TIMEZONES.includes(s.hours.timezone) ? [] : [s.hours.timezone]).map((tz) => h("option", { value: tz, selected: tz === s.hours.timezone }, tz.replace(/_/g, " "))))),
          ...DAY_ORDER.map(([d, label]) => {
            const day = s.hours.days[d];
            return h("div", { class: "item hours-row" },
              h("span", { class: "day-name" }, label),
              toggle(`day-${d}`, day.open, `${label} open`, (v) => (day.open = v)),
              day.open
                ? h("span", { class: "hours-times" },
                    h("input", { class: "field time", type: "time", value: day.from, "aria-label": `${label} opens`, onchange: (e: Event) => (day.from = (e.target as HTMLInputElement).value) }),
                    h("span", { class: "muted" }, "to"),
                    // 7.10.30: open until midnight ("24:00"; a time box can't show it, so it's a tick box beside it).
                    day.to === "24:00"
                      ? h("span", { class: "muted" }, "midnight")
                      : h("input", { class: "field time", type: "time", value: day.to, "aria-label": `${label} closes`, onchange: (e: Event) => (day.to = (e.target as HTMLInputElement).value) }),
                    h("label", { class: "muted hours-midnight" },
                      h("input", { type: "checkbox", checked: day.to === "24:00", "aria-label": `${label} open until midnight`, onchange: (e: Event) => {
                        day.to = (e.target as HTMLInputElement).checked ? "24:00" : "23:00";
                        render();
                      } }), " Midnight"))
                : h("span", { class: "muted" }, "Closed"));
          }),
          h("div", { class: "item stack" },
            h("div", { style: "font-weight:600" }, "Holidays & closed days"),
            h("div", { class: "sub" }, "Closed all day, like a weekend. Callers get the after-hours treatment."),
            s.hours.closedDates.length
              ? h("div", { class: "chip-row" }, ...s.hours.closedDates.map((d) =>
                  h("span", { class: "date-chip" },
                    fmtDate(new Date(`${d}T12:00:00`), { weekday: "short", month: "short", day: "numeric", year: "numeric" }),
                    h("button", { type: "button", class: "icon-btn sm", "aria-label": `Remove ${d}`, onclick: () => ((s.hours.closedDates = s.hours.closedDates.filter((x) => x !== d)), typedValues.clear(), rerender()) }, icon("close", "sm")))))
              : null,
            h("div", { class: "inline-form" },
              h("input", { id: "holiday-date", class: "field", type: "date", "aria-label": "Closed date" }),
              h("button", { type: "button", class: "btn quiet sm", onclick: () => {
                const v = (document.getElementById("holiday-date") as HTMLInputElement).value;
                if (!v) return toast("Pick a date");
                if (!s.hours.closedDates.includes(v)) s.hours.closedDates = [...s.hours.closedDates, v].sort();
                rerender();
              } }, icon("plus", "sm"), "Add")))] : [])),

      // 3.10.23 "a clear distinction between greetings and texts during business hours and after business hours".
      // 3.10.23: the two as tabs side by side, so each is quick to view.
      h("div", { class: "hours-tabs", role: "tablist", "aria-label": "Business hours or after hours" },
        h("button", { type: "button", role: "tab", class: "hours-tab day", "aria-selected": String(phoneTab === "day"), onclick: () => ((phoneTab = "day"), rerender()) },
          icon("sun", "sm"), h("span", {}, "During business hours")),
        h("button", { type: "button", role: "tab", class: "hours-tab night", "aria-selected": String(phoneTab === "night"), onclick: () => ((phoneTab = "night"), rerender()) },
          icon("moon", "sm"), h("span", {}, "After hours"))),
      ...(phoneTab === "day" ? [
      h("div", { class: "band-title day" }, icon("sun", "sm"), h("span", {}, "During business hours"),
        h("small", {}, "What callers hear, and the texts they get, while you're open.")),
      h("div", { class: "group-title" }, "Welcome greeting"),
      h("div", { class: "group" },
        h("div", { class: "item" },
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Greet callers"), h("div", { class: "sub" }, "Played as soon as someone calls.")),
          toggle("greet-on", s.greeting.enabled, "Greet callers", (v) => (s.greeting.enabled = v))),
        s.greeting.enabled
          ? h("div", { class: "item stack" },
              h("div", { class: "segmented", role: "group", "aria-label": "Greeting type" },
                h("button", { "aria-pressed": String(s.greeting.mode === "text"), onclick: () => ((s.greeting.mode = "text"), rerender()) }, "Type it"),
                h("button", { "aria-pressed": String(s.greeting.mode === "audio"), onclick: () => ((s.greeting.mode = "audio"), rerender()) }, "Upload a recording")),
              s.greeting.mode === "text"
                ? h("div", { class: "stack-sm" },
                    h("textarea", { id: "greet-text", class: "field area", rows: 3, value: s.greeting.text, placeholder: "Thanks for calling Joy Travel.",
                      oninput: (e: Event) => ((s.greeting.text = (e.target as HTMLTextAreaElement).value), updatePreview()) }),
                    h("div", { class: "row-actions" },
                      playButton("greet", "Hear a sample", () => api.phoneSample(s.greeting.text.trim() || "Thanks for calling Joy Travel.", s.voice)),
                      h("span", { class: "sub" }, `In the ${VOICE_CHOICES.find((v) => v.key === (s.voice ?? "ava"))?.label ?? "chosen"} voice callers hear`)))
                : h("div", { class: "audio-row" },
                    s.greeting.audioUrl ? playButton("greet-audio", "Play greeting", () => s.greeting.audioUrl!) : h("span", { class: "sub" }, "MP3 or WAV, up to 8 MB."),
                    fileButton(s.greeting.audioUrl ? "Replace" : "Upload", "greeting")),
              s.greeting.mode === "audio" && s.menu.enabled
                ? h("p", { class: "sub" }, "The menu is read right after your recording.")
                : null)
          : null),

      h("div", { class: "group-title" }, "Phone menu"),
      h("div", { class: "group" },
        h("div", { class: "item" },
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Menu options"), h("div", { class: "sub" }, "“For reservations, say reservations or press 1.”")),
          toggle("menu-on", s.menu.enabled, "Phone menu", (v) => {
            s.menu.enabled = v;
            if (v && !s.menu.options.length) s.menu.options.push({ digit: "1", label: "reservations", keywords: ["reservations"], action: { type: "team" } });
          })),
        s.menu.enabled
          ? h("div", { class: "item" },
              h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Callers can say the option"), h("div", { class: "sub" }, "Besides pressing a key, callers can just say “reservations”.")),
              toggle("speech-on", s.menu.speech, "Callers can say the option", (v) => (s.menu.speech = v)))
          : null,
        ...(s.menu.enabled ? options : []),
        s.menu.enabled && nextDigit()
          ? h("div", { class: "item" }, h("button", { class: "text-btn", onclick: () => {
              s.menu.options.push({ digit: nextDigit()!, label: "", keywords: [], action: { type: "team" } });
              rerender();
              document.getElementById(`opt-label-${s.menu.options.length - 1}`)?.focus();
            } }, icon("plus", "sm"), "Add option"))
          : null,
        s.menu.enabled
          ? h("div", { class: "item stack" },
              h("label", { class: "mini-label", for: "menu-tries" }, "Play the menu"),
              h("select", { id: "menu-tries", class: "field", onchange: (e: Event) => ((s.menu.tries = Number((e.target as HTMLSelectElement).value)), rerender()) },
                ...[1, 2, 3].map((n) => h("option", { value: n, selected: (s.menu.tries ?? 3) === n }, n === 1 ? "Once" : n === 2 ? "2 times" : "3 times"))),
              h("label", { class: "mini-label", for: "fallback" }, "Then, if the caller still hasn't chosen"),
              actionSelect("fallback", s.menu.fallback, (a) => (s.menu.fallback = a), false),
              h("label", { class: "mini-label", for: "custom-prompt" }, "Menu wording (optional)"),
              h("textarea", { id: "custom-prompt", class: "field area", rows: 3, value: s.menu.prompt, placeholder: autoPrompt(s.menu) || "Built from your options",
                oninput: (e: Event) => ((s.menu.prompt = (e.target as HTMLTextAreaElement).value), updatePreview()) }),
              h("p", { class: "sub" }, "Leave empty to build it from your options."))
          : null),

      h("div", { class: "group-title" }, "Voicemail greeting"),
      h("div", { class: "group" },
        h("div", { class: "item stack" },
          h("textarea", { id: "vm-text", class: "field area", rows: 3, value: s.voicemailGreeting,
            oninput: (e: Event) => (s.voicemailGreeting = (e.target as HTMLTextAreaElement).value) }),
          h("div", { class: "row-actions" },
            playButton("vm", "Hear a sample", () => api.phoneSample(s.voicemailGreeting.trim() || "Leave a message.", s.voice)),
            h("span", { class: "sub" }, "Read before the beep when nobody answers during business hours.")))),


      h("div", { class: "group-title" }, "Automatic texts"),
      h("div", { class: "group" }, ...autoRule("missedCall", "Missed call", "Sent right after a call nobody answered (and the caller didn't leave a voicemail)."),
        ...autoRule("voicemail", "Voicemail", "Sent after someone leaves a voicemail.")),
      ] : [
      h("div", { class: "band-title night" }, icon("moon", "sm"), h("span", {}, "After business hours & holidays"),
        h("small", {}, s.hours.enabled ? "What callers hear, and the texts they get, while you're closed." : "Turn on business hours above for these to be used.")),
      h("div", { class: "group-title" }, "After-hours greeting"),
      h("div", { class: "group" },
        h("div", { class: "item" },
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Greet after-hours callers"),
            h("div", { class: "sub" }, s.hours.greeting.enabled ? "Played first, instead of the daytime greeting." : "Off: they hear the daytime welcome greeting (if it's on).")),
          toggle("ah-greet-on", s.hours.greeting.enabled, "Greet after-hours callers", (v) => (s.hours.greeting.enabled = v))),
        s.hours.greeting.enabled
          ? h("div", { class: "item stack" },
              h("textarea", { id: "ah-greet-text", class: "field area", rows: 3, value: s.hours.greeting.text, placeholder: "Thanks for calling Joy Travel. Our office is closed right now.",
                oninput: (e: Event) => (s.hours.greeting.text = (e.target as HTMLTextAreaElement).value) }),
              h("div", { class: "row-actions" },
                playButton("ah-greet", "Hear a sample", () => api.phoneSample(s.hours.greeting.text.trim() || "Thanks for calling Joy Travel. Our office is closed right now.", s.voice))))
          : null),
      h("div", { class: "group-title" }, "Then"),
      h("div", { class: "group" },
          h("div", { class: "item stack" },
            h("label", { class: "mini-label", for: "after-hours" }, "After hours, callers go to"),
            actionSelect("after-hours", s.hours.afterHours, (a) => (s.hours.afterHours = a)),
            s.hours.afterHours.type === "message"
              ? h("textarea", { id: "after-msg", class: "field area", rows: 2, value: s.hours.afterHours.text,
                  placeholder: s.hours.greeting.enabled ? "Empty: the call ends right after the after-hours greeting" : "Our office is closed. We're open Monday to Friday, 9 to 6…",
                  oninput: (e: Event) => s.hours.afterHours.type === "message" && (s.hours.afterHours.text = (e.target as HTMLTextAreaElement).value) })
              : null)),
      h("div", { class: "group-title" }, "After-hours voicemail greeting"),
      h("div", { class: "group" },
        h("div", { class: "item stack" },
          h("textarea", { id: "ah-vm-text", class: "field area", rows: 3, value: s.hours.voicemailGreeting,
            placeholder: `Empty: the daytime one ("${s.voicemailGreeting.slice(0, 60)}${s.voicemailGreeting.length > 60 ? "…" : ""}")`,
            oninput: (e: Event) => (s.hours.voicemailGreeting = (e.target as HTMLTextAreaElement).value) }),
          h("div", { class: "row-actions" },
            playButton("ah-vm", "Hear a sample", () => api.phoneSample(s.hours.voicemailGreeting.trim() || s.voicemailGreeting.trim() || "Leave a message.", s.voice)),
            h("span", { class: "sub" }, "Read before the beep while you're closed.")))),
      h("div", { class: "group-title" }, "Automatic text"),
      h("div", { class: "group" }, ...autoRule("afterHours", "Office closed", "Replaces the missed-call and voicemail texts while you're closed, and answers texts that come in then.")),
      ]),
      h("div", { class: "band-title" }, h("span", {}, "Any time")),
      h("div", { class: "group-title" }, "Automatic texts"),
      h("div", { class: "group" },
        h("div", { class: "item" },
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "At most one automatic text per number every"),
            h("div", { class: "sub" }, "So someone who calls three times doesn't get three texts. Blocked numbers and people who replied STOP never get one.")),
          h("select", { id: "auto-cooldown", class: "field", style: "width:130px", onchange: (e: Event) => (s.autoReply.cooldownHours = Number((e.target as HTMLSelectElement).value)) },
            ...[[1, "1 hour"], [4, "4 hours"], [12, "12 hours"], [24, "24 hours"], [72, "3 days"]].map(([v, l]) => h("option", { value: String(v), selected: s.autoReply.cooldownHours === v }, String(l))))),
        h("div", { class: "item" }, h("div", { class: "sub" }, "Automatic texts show up in the conversation like any other text. Texting needs your number's carrier approval (10DLC) to be done."))),
      h("div", { class: "group-title" }, "Hold music"),
      h("div", { class: "group" },
        h("div", { class: "item stack" },
          h("div", { class: "sub" }, "Callers hear this on hold, during transfers, and while phones ring after the menu."),
          h("div", { class: "audio-row" },
            playButton("hold", s.holdMusicUrl ? "Play your music" : "Play built-in music", () => s.holdMusicUrl ?? "/media/hold-default.mp3"),
            h("span", { class: "sub" }, s.holdMusicUrl ? "Your upload" : "Built-in (what callers hear now)")),
          h("div", { class: "row-actions" },
            fileButton(s.holdMusicUrl ? "Replace" : "Upload your own", "hold"),
            s.holdMusicUrl ? h("button", { class: "text-btn", onclick: () => ((s.holdMusicUrl = null), rerender()) }, "Use built-in") : null))),

      h("div", { style: "height:80px" }))),
    h("div", { class: "save-bar" },
      h("button", { class: "btn block", disabled: state.phoneSaving, onclick: () => void save() }, state.phoneSaving ? "Saving…" : "Save")));
}

// ---------------------------------------------------------------- contacts

let contactsShown = 60;
let contactsObserver: IntersectionObserver | null = null;
function contactsMoreSentinel() {
  const el = h("div", { class: "list-more", "aria-hidden": "true" });
  contactsObserver?.disconnect();
  if (!("IntersectionObserver" in window)) {
    contactsShown = 1e9;
    return el;
  }
  contactsObserver = new IntersectionObserver((entries) => {
    if (!entries.some((e) => e.isIntersecting)) return;
    contactsObserver?.disconnect();
    contactsShown += 120;
    renderSoon();
  }, { rootMargin: "600px 0px" });
  requestAnimationFrame(() => el.isConnected && contactsObserver?.observe(el));
  return el;
}

async function loadContacts() {
  try {
    const fresh = await api.contacts();
    // Nothing changed: don't redraw every contact again (it's reloaded each time you open Contacts or a contact).
    const same = state.contactsLoaded && JSON.stringify(fresh) === JSON.stringify(state.contacts);
    state.contacts = fresh;
    state.contactsLoaded = true;
    if (!same) {
      contactCache.clear(); // a contact changed: don't show its old copy when it's opened
      render();
    }
  } catch {
    // shown as empty
  }
}

function contactsPane() {
  const q = state.contactSearch.trim().toLowerCase();
  const digits = q.replace(/\D/g, "");
  const list = state.contacts.filter((c) => !q
    || c.name.toLowerCase().includes(q) || (c.company ?? "").toLowerCase().includes(q) || (c.email ?? "").toLowerCase().includes(q)
    || c.tags.some((t) => t.toLowerCase().includes(q)) || (!!digits && c.number.includes(digits)));
  const current = state.route.name === "contact" ? state.route.number : null;
  // A–Z sections, like a phone's contact list.
  const rows: Node[] = [];
  let letter = "";
  // Like the inbox: the first contacts now, more as you scroll (drawing hundreds made opening Contacts lag).
  for (const c of list.slice(0, contactsShown)) {
    const l = (c.name.trim()[0] ?? "#").toUpperCase();
    const head = /[A-Z]/.test(l) ? l : "#";
    if (head !== letter && !q) {
      rows.push(h("div", { class: "letter" }, head));
      letter = head;
    }
    rows.push(h("button", { class: "conv contact-row", "aria-current": String(current === c.number), onclick: () => go({ name: "contact", number: c.number }) },
      avatar(c.number, c.name),
      h("span", { class: "body" },
        h("span", { class: "top" }, h("span", { class: "name" }, c.name), c.notesCount ? h("span", { class: "cmt-count", title: `${c.notesCount} notes` }, icon("note", "sm"), String(c.notesCount)) : null),
        h("span", { class: "last" }, h("span", { class: "ellipsis" }, [c.company, phone(c.number)].filter(Boolean).join(" · ")),
          ...c.tags.slice(0, 2).map((t) => h("span", { class: "tag" }, t))))));
  }
  const importInput = h("input", { type: "file", accept: ".csv,text/csv", class: "sr-only", onchange: async (e: Event) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    try {
      const r = await api.importContacts(file);
      toast(`Imported: ${r.created} new, ${r.updated} updated${r.skipped ? `, ${r.skipped} skipped` : ""}`);
      await loadContacts();
    } catch (err) {
      toast((err as Error).message);
    }
  } });
  return h("aside", { class: "pane-list" },
    lineBar(),
    h("div", { class: "list-head" },
      h("div", { class: "list-title" }, "Contacts"),
      h("span", { class: "muted small" }, state.contactsLoaded ? String(state.contacts.length) : ""),
      h("div", { class: "menu-wrap" },
        h("label", { class: "icon-btn", title: "Import contacts from a CSV file", "aria-label": "Import CSV" }, icon("upload"), importInput)),
      h("a", { class: "icon-btn", href: api.contactsCsvUrl(), title: "Export contacts (CSV)", "aria-label": "Export CSV", download: "joy-phone-contacts.csv" }, icon("download")),
      // 1.10.26: birthdays and TSA numbers out of the notes and into the travel profiles (admins).
      isAdmin() ? h("button", { class: "icon-btn", title: "Move birthdays and TSA numbers from notes into profiles", "aria-label": "Organize notes into profiles", onclick: () => go({ name: "notes-to-profiles" }) }, icon("sparkle")) : null,
      h("button", { class: "icon-btn", title: "New contact", "aria-label": "New contact", onclick: () => void newContact() }, icon("userPlus"))),
    h("label", { class: "search" }, icon("search", "sm"),
      h("input", { id: "contact-search", type: "search", placeholder: "Search name, company, tag, number", value: state.contactSearch, autocomplete: "off",
        oninput: (e: Event) => ((state.contactSearch = (e.target as HTMLInputElement).value), renderSoon()) })),
    h("div", { class: "list", "data-scroll": "contacts" },
      ...(rows.length ? [...rows, list.length > contactsShown ? contactsMoreSentinel() : null].filter((x): x is Node => !!x) : [!q && !state.contactsLoaded ? h("div", { class: "empty" }, h("strong", {}, "Loading…"))
        : q ? emptyState({ icon: "search", tone: "slate", title: `No matches for “${q}”`, text: "Try another name, company, tag or number." })
        : emptyState({ icon: "contacts", tone: "teal", title: "No contacts yet", text: "Add one with the + button, name someone from a conversation, or import a CSV." })])));
}

// ---- 1.10.26 "bring the TSA numbers and birthdays out of the notes, organized nicely" ----
const notesMove = { job: null as NotesJob | null, loaded: false, asked: false, busy: false, chosen: new Set<string>(), timer: 0 };
async function loadNotesJob() {
  try {
    const prev = notesMove.job?.status;
    notesMove.job = (await api.notesJob()).job;
    notesMove.loaded = true;
    // Everything to apply is ticked when the list first comes in.
    if (notesMove.job?.status === "ready" && prev !== "ready") notesMove.chosen = new Set(notesMove.job.proposals.filter((p) => p.changes.length && !p.error).map((p) => p.number));
  } catch (err) {
    // 1.10.28: loaded either way (a failed load used to start another one on every redraw: an endless loop).
    notesMove.loaded = true;
    toast((err as Error).message);
  }
  clearTimeout(notesMove.timer);
  if (notesMove.job?.status === "running" && state.route.name === "notes-to-profiles") notesMove.timer = window.setTimeout(() => void loadNotesJob(), 1500);
  if (state.route.name === "notes-to-profiles") render();
}
function notesToProfilesView() {
  if (!notesMove.loaded && !notesMove.asked && isAdmin()) {
    notesMove.asked = true; // once here (go() asks on each visit)
    void loadNotesJob();
  }
  const j = notesMove.job;
  const act = async (f: () => Promise<unknown>) => {
    notesMove.busy = true;
    render();
    try {
      await f();
    } catch (err) {
      toast((err as Error).message);
    }
    notesMove.busy = false;
    await loadNotesJob();
    void loadContacts();
  };
  const start = () => act(async () => {
    notesMove.job = (await api.notesJobStart()).job;
    notesMove.chosen.clear();
  });
  const todo = j?.proposals.filter((p) => p.changes.length && !p.error) ?? [];
  const failed = j?.proposals.filter((p) => p.error) ?? [];
  // 1.10.26: a date in the notes that couldn't be read for sure: shown here to fill in by hand, never dropped quietly.
  const toCheck = j?.proposals.filter((p) => !p.error && !p.changes.length && p.unsure.length) ?? [];
  const checkCards = () => toCheck.length ? [
    h("div", { class: "sub notes-check-head" }, `Check ${toCheck.length === 1 ? "this one" : `these ${toCheck.length}`}: something in the notes couldn't be read for sure`),
    ...toCheck.map((p) => h("button", { type: "button", class: "group notes-card check", onclick: () => go({ name: "contact", number: p.number }) },
      h("div", { class: "grow" }, h("div", { class: "notes-card-head" }, h("strong", {}, p.name), h("span", { class: "sub" }, phone(p.number))),
        ...p.unsure.map((u) => h("div", { class: "sub notes-unsure" }, u))))),
  ] : [];
  const head = h("header", { class: "thread-head" }, backButton({ name: "contacts" }),
    h("div", { class: "who" }, h("div", { class: "title" }, "Notes into profiles"), h("div", { class: "number" }, "Birthdays, TSA / Known Traveler and Redress numbers")));
  const intro = h("div", { class: "group" }, h("div", { class: "item stack" },
    h("div", {}, "Reads every contact's notes and pulls out birthdays, Known Traveler (TSA) and Redress numbers, passport expiry dates and who travels with them, into each contact's travel profile."),
    h("div", { class: "sub" }, "This happens on its own: after each update, every hour, and right away when a note with a birthday or TSA number is saved. Only empty fields are filled, anything already in a profile is kept, and the notes stay exactly as they are. You can undo it here."),
    h("div", { class: "sub" }, "To see the list first instead, press Read the notes.")));
  let body: Node[];
  if (!notesMove.loaded) body = [h("div", { class: "pending" }, h("span", { class: "spinner" }), "Loading")];
  else if (!j || j.status === "undone") {
    body = [intro, h("div", { class: "row-actions notes-actions" }, h("button", { class: "btn", disabled: notesMove.busy, onclick: () => void start() }, icon("sparkle", "sm"), "Read the notes")),
      j?.status === "undone" ? h("p", { class: "sub" }, "The last run was undone.") : null].filter(Boolean) as Node[];
  } else if (j.status === "running") {
    const pct = j.total ? Math.round((j.done / j.total) * 100) : 0;
    body = [intro, h("div", { class: "group" }, h("div", { class: "item stack" },
      h("div", { style: "font-weight:600" }, j.total ? `Reading notes… ${j.done} of ${j.total} contacts` : "Finding notes with travel details…"),
      h("div", { class: "notes-progress", role: "progressbar", "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": pct }, h("span", { style: `width:${pct}%` }))))];
  } else if (j.status === "ready") {
    const n = [...notesMove.chosen].filter((x) => todo.some((p) => p.number === x)).length;
    body = [
      h("div", { class: "group" }, h("div", { class: "item stack" },
        h("div", { style: "font-weight:600" }, todo.length ? `${todo.length} contact${todo.length === 1 ? "" : "s"} can be filled in` : "Nothing new to add"),
        h("div", { class: "sub" }, [`${j.total} contacts had notes with travel details`, j.unchanged ? `${j.unchanged} had nothing new` : "", failed.length ? `${failed.length} couldn't be read` : ""].filter(Boolean).join(" · ")))),
      ...(todo.length ? [h("div", { class: "row-actions notes-actions" },
        h("button", { class: "btn", disabled: notesMove.busy || !n, onclick: () => void act(async () => {
          const r = await api.notesJobApply([...notesMove.chosen]);
          toast(`Updated ${r.applied} contact${r.applied === 1 ? "" : "s"}${r.skipped.length ? ` (left alone, edited since: ${r.skipped.join(", ")})` : ""}`);
        }) }, icon("check", "sm"), `Apply to ${n} contact${n === 1 ? "" : "s"}`),
        h("button", { class: "btn quiet", type: "button", onclick: () => ((notesMove.chosen = n === todo.length ? new Set() : new Set(todo.map((p) => p.number))), render()) }, n === todo.length ? "Untick all" : "Tick all"),
        h("button", { class: "btn quiet", disabled: notesMove.busy, onclick: () => void start() }, "Read again"))] : [h("div", { class: "row-actions notes-actions" }, h("button", { class: "btn quiet", onclick: () => void start() }, "Read again"))]),
      ...todo.map((p) => {
        const on = notesMove.chosen.has(p.number);
        return h("label", { class: `group notes-card${on ? " on" : ""}` },
          h("input", { type: "checkbox", checked: on, onchange: (e: Event) => {
            if ((e.target as HTMLInputElement).checked) notesMove.chosen.add(p.number);
            else notesMove.chosen.delete(p.number);
            render();
          } }),
          h("div", { class: "grow" },
            h("div", { class: "notes-card-head" }, h("strong", {}, p.name), h("button", { type: "button", class: "link-btn", onclick: (e: MouseEvent) => (e.preventDefault(), go({ name: "contact", number: p.number })) }, phone(p.number))),
            h("ul", { class: "notes-changes" }, ...p.changes.map((c) => h("li", {}, c))),
            ...p.unsure.map((u) => h("div", { class: "sub notes-unsure" }, `Check: ${u}`))));
      }),
      ...checkCards(),
      ...failed.map((p) => h("div", { class: "group notes-card failed" }, h("div", { class: "grow" }, h("strong", {}, p.name), h("div", { class: "sub" }, `Couldn't read these notes: ${p.error}`)))),
    ];
  } else {
    body = [
      h("div", { class: "group" }, h("div", { class: "item stack" },
        h("div", { style: "font-weight:600" }, `${j.auto ? "Filled automatically: " : "Updated "}${j.appliedNumbers.length} contact${j.appliedNumbers.length === 1 ? "" : "s"}`),
        j.auto ? h("div", { class: "sub" }, `From their notes, ${new Date(j.startedAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}. New notes keep filling in on their own.`) : null,
        j.skipped.length ? h("div", { class: "sub" }, `Left alone because someone edited them after the list was made: ${j.skipped.join(", ")}`) : null,
        h("div", { class: "sub" }, "The notes are unchanged. Open a contact to see their travelers and copy their GDS lines."))),
      h("div", { class: "row-actions notes-actions" },
        h("button", { class: "btn quiet", disabled: notesMove.busy, onclick: () => void act(async () => {
          const r = await api.notesJobUndo();
          toast(`Undone: ${r.undone} contact${r.undone === 1 ? "" : "s"} put back${r.kept.length ? ` (kept, edited since: ${r.kept.join(", ")})` : ""}`);
        }) }, "Undo"),
        h("button", { class: "btn quiet", disabled: notesMove.busy, onclick: () => void start() }, "Read again")),
      ...(j.proposals.filter((p) => j.appliedNumbers.includes(p.number)).map((p) => h("button", { type: "button", class: "group notes-card done", onclick: () => go({ name: "contact", number: p.number }) },
        h("div", { class: "grow" }, h("strong", {}, p.name), h("ul", { class: "notes-changes" }, ...p.changes.map((c) => h("li", {}, c))),
          ...p.unsure.map((u) => h("div", { class: "sub notes-unsure" }, `Check: ${u}`)))))),
      ...checkCards(),
      ...failed.map((p) => h("div", { class: "group notes-card failed" }, h("div", { class: "grow" }, h("strong", {}, p.name), h("div", { class: "sub" }, `Couldn't read these notes: ${p.error}. They'll be tried again.`)))),
    ];
  }
  return screen(head, h("div", { class: "page" }, h("div", { class: "page-inner notes-move" }, ...body)));
}

async function newContact() {
  const raw = (await glassDialog({ title: "New contact", message: "Their phone number:", confirm: "Next", input: { placeholder: "(310) 555-0123", type: "tel", inputmode: "tel" } })) as string | null;
  if (!raw) return;
  const e164 = toE164(raw);
  if (!e164) return toast("Enter a full phone number");
  go({ name: "contact", number: e164 });
  state.editingContact = true;
  render();
}

function contactView(number: string) {
  const c = state.contact?.number === number ? state.contact : null;
  // While it loads, the header already shows the name from the contacts list.
  const head = c ?? state.contacts.find((x) => x.number === number);
  const back: Route = cameFrom && cameFrom.name !== "contact" && cameFrom.name !== "inbox" ? cameFrom : { name: "contacts" };
  return screen(
    h("header", { class: "thread-head" },
      backButton(back),
      avatar(number, head?.name, "lg"),
      h("div", { class: "who" }, h("div", { class: "title contact-title" }, head?.name || phone(number)), h("div", { class: "number" }, head?.company || (head?.name ? phone(number) : c ? "Not saved yet" : ""))),
      h("div", { class: "head-actions" },
        state.user!.canCall && !(voice.call && !isEnded(voice.call)) ? h("button", { class: "btn live sm", onclick: () => void dial(number) }, icon("phone", "sm"), h("span", { class: "hide-narrow" }, "Call")) : null,
        h("button", { class: "btn quiet sm", onclick: () => go({ name: "thread", number }) }, icon("message", "sm"), h("span", { class: "hide-narrow" }, "Conversation")))),
    h("div", { class: "page", "data-scroll": "timeline" }, h("div", { class: "page-inner wide" }, c ? contactCard(number, c, false) : h("div", { class: "pending", style: "justify-content:center;padding:40px" }, h("span", { class: "spinner" }), "Loading"))),
    // Contact notes are Joy Travel's (the team sees them): none on your personal line.
    c && state.line !== "personal" ? composer({
      key: `note:${number}`,
      modes: ["comment"],
      reply: "note",
      placeholder: `Note about ${c.name ? firstName(c.name) : "this contact"} · @ to tag`,
      send: async (body) => void (await api.addContactNote(number, body, state.noteReply?.id ?? null)),
    }) : null);
}

// ---- customer profile (birthday, family, passports…) ----

const guessOf = (number: string) => (state.thread?.number === number ? state.thread.nameGuess ?? null : null);

type ContactDraft = { number: string; name: string; company: string; email: string; tags: string; details: ContactDetails };
let contactDraft: ContactDraft | null = null;
function draftFor(number: string, c: ContactDetail): ContactDraft {
  if (contactDraft?.number !== number) {
    contactDraft = { number, name: c.name ?? guessOf(number)?.name ?? callerNames.get(number) ?? "", company: c.company ?? "", email: c.email ?? "",
      tags: c.tags.join(", "), details: structuredClone(c.details ?? EMPTY_DETAILS) };
  }
  return contactDraft;
}
const EMPTY_DETAILS: ContactDetails = { legalName: "", birthday: "", anniversary: "", passportExp: "", ktn: "", redress: "", address: "", preferences: "", phones: [], emails: [], family: [] };

function parseDay(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
const fmtDay = (s: string) => ((x) => (x ? fmtDate(x, { month: "short", day: "numeric", year: "numeric" }) : s))(parseDay(s));
function ageOf(s: string): number | null {
  const d = parseDay(s);
  if (!d) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) a--;
  return a >= 0 && a < 130 ? a : null;
}
/** Days until the next birthday / anniversary (0 = today). */
function daysUntil(s: string): number | null {
  const d = parseDay(s);
  if (!d) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let next = new Date(today.getFullYear(), d.getMonth(), d.getDate());
  if (next < today) next = new Date(today.getFullYear() + 1, d.getMonth(), d.getDate());
  return Math.round((next.getTime() - today.getTime()) / 86400000);
}
function dayLine(s: string, withAge: boolean): Node {
  const left = daysUntil(s);
  const age = withAge ? ageOf(s) : null;
  const soon = left === 0 ? "today 🎉" : left !== null && left <= 30 ? `in ${left} day${left === 1 ? "" : "s"}` : "";
  return h("span", { class: "v" }, fmtDay(s), age !== null ? h("span", { class: "muted" }, ` · ${age}`) : null, soon ? h("span", { class: "soon" }, soon) : null);
}
/** Most countries want 6 months left on a passport. */
function passportLine(s: string): Node {
  const d = parseDay(s);
  const now = Date.now();
  const months = d ? (d.getTime() - now) / (30.44 * 86400000) : 99;
  const tone = months < 0 ? "bad" : months < 6 ? "warn" : "";
  return h("span", { class: "v" }, fmtDay(s), tone ? h("span", { class: `soon ${tone}` }, months < 0 ? "expired" : "under 6 months") : null);
}

/** The quick-add box's text and whether it's being organized (kept across redraws while you edit). */
let quickText = "";
let quickBusy = false;
/** 6.10.22: the contact whose Quick add box is open on the front of the card (next to Edit). */
let quickOpenFor: string | null = null;
let quickFocus = false;
/**
 * Quick add, on the front of the contact (6.10.22 "as soon as you click Quick add, the window opens up right there"):
 * paste or type travelers, birthdays, TSA numbers… and it's organized into the right places and saved at once.
 */
function quickAddPanel(number: string, c: ContactDetail): Node {
  const close = () => {
    quickOpenFor = null;
    quickText = "";
    render();
  };
  const run = async () => {
    if (!quickText.trim()) return toast("Type or paste the details first");
    quickBusy = true;
    render();
    try {
      const current = c.details ?? EMPTY_DETAILS;
      const r = await api.organizeDetails(number, { text: quickText, details: current, name: c.name ?? "" });
      const parts = [r.added.length ? `added ${r.added.join(", ")}` : "", r.updated.length ? `updated ${r.updated.join(", ")}` : ""].filter(Boolean);
      if (!parts.length) {
        toast("Nothing new found in that text");
        for (const u of r.unsure) toast(u);
        return;
      }
      const name = (c.name ?? "").trim() || r.details.legalName?.trim() || "";
      if (!name) {
        // Not saved as a contact yet and no name in the text: open the form with everything filled in, for a name.
        const dr = draftFor(number, c);
        dr.details = r.details;
        state.editingContact = true;
        quickOpenFor = null;
        quickText = "";
        toast(`Organized: ${parts.join("; ")}. Add a name, then Save.`);
        return;
      }
      await api.saveContact(number, { name, company: c.company ?? null, email: c.email ?? null, tags: c.tags, details: r.details });
      quickOpenFor = null;
      quickText = "";
      threadCache.delete(number);
      toast(`Saved: ${parts.join("; ")}`);
      for (const u of r.unsure) toast(u);
      await refresh();
    } catch (err) {
      toast((err as Error).message);
    } finally {
      quickBusy = false;
      render();
    }
  };
  const ta = h("textarea", { id: "quick-add-front", class: "field sm area", rows: 4, value: quickText,
    placeholder: "Paste from the GDS (1.SSR DOCS LY HK1/DB/30SEP87/M/NOUROLLAH/YOSEF  1.1 NOUROLLAH/YOSEF) or type: Esther DOB 1/28/1974 TSA TT11B4FKS, husband Leib 3/12/1971",
    oninput: (e: Event) => (quickText = (e.target as HTMLTextAreaElement).value),
    onkeydown: (e: KeyboardEvent) => {
      if (e.key === "Escape") (e.preventDefault(), close());
      else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) (e.preventDefault(), void run());
    } }, quickText) as HTMLTextAreaElement;
  if (quickFocus) {
    quickFocus = false;
    requestAnimationFrame(() => ta.focus());
  }
  return h("div", { class: "quick-front" },
    h("div", { class: "quick-front-head" }, icon("sparkle", "sm"), h("span", {}, "Express add"), h("small", {}, "GDS lines (SSR DOCS / DOCO), travelers, birthdays, KTN")),
    ta,
    h("div", { class: "inline-edit-actions" },
      h("button", { class: "btn quiet sm", type: "button", onclick: close }, "Cancel"),
      h("button", { class: "btn sm", type: "button", disabled: quickBusy, onclick: () => void run() }, quickBusy ? h("span", { class: "spinner" }) : icon("sparkle", "sm"), quickBusy ? "Organizing…" : "Organize & save")));
}
function detailsEditor(number: string, c: ContactDetail, compact: boolean): Node {
  const dr = draftFor(number, c);
  const d = dr.details;
  const id = (k: string) => `ct-${k}`;
  const text = (k: string, value: string, set: (v: string) => void, attrs: Record<string, unknown> = {}) =>
    h("input", { id: id(k), class: "field sm", value, autocomplete: "off", oninput: (e: Event) => set((e.target as HTMLInputElement).value), ...attrs });
  const date = (k: string, value: string, set: (v: string) => void) => text(k, value, set, { type: "date" });
  const label = (k: string, t: string) => h("label", { class: "mini-label", for: id(k) }, t);
  // 3.10.23: M / F for the GDS DOCS line.
  const gender = (k: string, value: "M" | "F" | undefined, set: (v: "M" | "F" | undefined) => void) =>
    h("select", { id: id(k), class: "field sm", onchange: (e: Event) => { const v = (e.target as HTMLSelectElement).value; set(v === "M" || v === "F" ? v : undefined); } },
      h("option", { value: "", selected: !value }, "—"), h("option", { value: "M", selected: value === "M" }, "M (male)"), h("option", { value: "F", selected: value === "F" }, "F (female)"));
  // 1.10.28: rows are numbered by position, so text typed in a removed row must not come back in the row that moves
  // up into its place (one keystroke there then saved it over that traveler's real name).
  const remove = (onclick: () => void, what: string) => h("button", { class: "icon-btn sm", type: "button", "aria-label": `Remove ${what}`, onclick: () => (onclick(), typedValues.clear(), render()) }, icon("close", "sm"));
  const add = (t: string, onclick: () => void) => h("button", { class: "text-btn add-row", type: "button", onclick: () => (onclick(), render()) }, icon("plus", "sm"), t);

  const save = async (e: Event) => {
    e.preventDefault();
    if (!dr.name.trim()) return toast("Add a name");
    try {
      await api.saveContact(number, { name: dr.name.trim(), company: dr.company.trim() || null, email: dr.email.trim() || null,
        tags: dr.tags.split(",").map((t) => t.trim()).filter(Boolean), details: dr.details });
      state.editingContact = false;
      contactDraft = null;
      quickText = "";
      threadCache.delete(number);
      toast("Contact saved");
      await refresh();
    } catch (err) {
      toast((err as Error).message);
    }
  };
  const cancel = () => {
    state.editingContact = false;
    contactDraft = null;
    quickText = "";
    render();
  };
  // 7.10.30: the personal line now keeps every field for its contacts (it used to keep only the name and drop the rest).
  const nameOnly = false;
  const personal = state.line === "personal";
  return h("form", { class: compact ? "group form details-form compact" : "group form details-form", onsubmit: save },
    h("div", { class: "form-sec" }, "Contact"),
    label("name", "Name"), text("name", dr.name, (v) => (dr.name = v), { required: true, placeholder: personal ? "Only you see this contact" : "What the team calls them" }),
    ...(nameOnly ? [] : [
    label("company", "Company"), text("company", dr.company, (v) => (dr.company = v)),
    label("email", "Email"), text("email", dr.email, (v) => (dr.email = v), { type: "email" }),
    ...d.emails.flatMap((x, i) => [h("div", { class: "pair" },
      text(`em${i}l`, x.label, (v) => (x.label = v), { placeholder: "Label", class: "field sm lbl" }),
      text(`em${i}`, x.value, (v) => (x.value = v), { type: "email", placeholder: "another@email.com" }),
      remove(() => d.emails.splice(i, 1), "email"))]),
    add("Add email", () => d.emails.push({ label: d.emails.length ? "" : "Work", value: "" })),
    h("div", { class: "mini-label" }, "Other phones"),
    ...d.phones.map((x, i) => h("div", { class: "pair" },
      text(`ph${i}l`, x.label, (v) => (x.label = v), { placeholder: "Label", class: "field sm lbl" }),
      text(`ph${i}`, x.value, (v) => (x.value = v), { type: "tel", inputmode: "tel", placeholder: "(310) 555-0123" }),
      remove(() => d.phones.splice(i, 1), "phone"))),
    add("Add phone", () => d.phones.push({ label: d.phones.length ? "" : "Mobile", value: "" })),
    label("address", "Address"), text("address", d.address, (v) => (d.address = v), { placeholder: "Street, city, state, zip" }),
    label("tags", "Tags (comma separated)"), text("tags", dr.tags, (v) => (dr.tags = v), { placeholder: "VIP, honeymoon, Cancun" }),

    h("div", { class: "form-sec" }, "Travel profile"),
    h("div", { class: "two-col" },
      h("div", {}, label("legal", "Legal name (as on passport)"), text("legal", d.legalName, (v) => (d.legalName = v), { placeholder: "First Middle Last" })),
      h("div", {}, label("gender", "Gender (for the GDS)"), gender("gender", d.gender, (v) => (d.gender = v)))),
    h("div", { class: "two-col" },
      h("div", {}, label("dob", "Birthday"), date("dob", d.birthday, (v) => (d.birthday = v))),
      h("div", {}, label("pp", "Passport expires"), date("pp", d.passportExp, (v) => (d.passportExp = v)))),
    h("div", { class: "two-col" },
      h("div", {}, label("ktn", "Known Traveler # (TSA)"), text("ktn", d.ktn ?? "", (v) => (d.ktn = v), { placeholder: "PreCheck / Global Entry" })),
      h("div", {}, label("redress", "Redress #"), text("redress", d.redress ?? "", (v) => (d.redress = v)))),
    label("anniv", "Anniversary"), date("anniv", d.anniversary, (v) => (d.anniversary = v)),
    label("prefs", "Preferences"),
    h("textarea", { id: id("prefs"), class: "field sm area", rows: 2, placeholder: "Aisle seat, vegetarian, Marriott Bonvoy…", oninput: (e: Event) => (d.preferences = (e.target as HTMLTextAreaElement).value) }, d.preferences),

    h("div", { class: "form-sec" }, "Family & travelers"),
    ...d.family.map((f, i) => h("div", { class: "family-edit" },
      h("div", { class: "pair" },
        text(`fm${i}n`, f.name, (v) => (f.name = v), { placeholder: "Legal name" }),
        remove(() => d.family.splice(i, 1), "traveler")),
      h("div", { class: "two-col" },
        h("div", {}, h("label", { class: "mini-label", for: id(`fm${i}r`) }, "Relation"), text(`fm${i}r`, f.relation, (v) => (f.relation = v), { placeholder: "Spouse, son…" }))),
      h("div", { class: "two-col" },
        h("div", {}, h("label", { class: "mini-label", for: id(`fm${i}g`) }, "Gender (for the GDS)"), gender(`fm${i}g`, f.gender, (v) => (f.gender = v))),
        h("div", {}, h("label", { class: "mini-label", for: id(`fm${i}b`) }, "Birthday"), date(`fm${i}b`, f.birthday, (v) => (f.birthday = v)))),
      h("div", { class: "two-col" },
        h("div", {}, h("label", { class: "mini-label", for: id(`fm${i}p`) }, "Passport expires"), date(`fm${i}p`, f.passportExp, (v) => (f.passportExp = v))),
        h("div", {}, h("label", { class: "mini-label", for: id(`fm${i}k`) }, "Known Traveler # (TSA)"), text(`fm${i}k`, f.ktn ?? "", (v) => (f.ktn = v)))),
      h("div", { class: "two-col" },
        h("div", {}, h("label", { class: "mini-label", for: id(`fm${i}d`) }, "Redress #"), text(`fm${i}d`, f.redress ?? "", (v) => (f.redress = v))),
        h("div", {}, h("label", { class: "mini-label", for: id(`fm${i}x`) }, "Notes"), text(`fm${i}x`, f.notes, (v) => (f.notes = v), { placeholder: "Allergies, seat, frequent flyer…" }))))),
    add("Add family member / traveler", () => d.family.push({ name: "", relation: "", birthday: "", passportExp: "", ktn: "", redress: "", notes: "" }))]),

    h("div", { class: "row-actions sticky-actions" },
      h("button", { class: "btn sm", type: "submit" }, "Save"),
      h("button", { class: "btn quiet sm", type: "button", onclick: cancel }, "Cancel"),
      c.exists && !compact && (isAdmin() || c.createdBy === state.user?.id)
        ? h("button", { class: "btn quiet sm danger-text", type: "button", onclick: async () => {
            if (!(await glassDialog({ title: "Delete this contact?", message: "Their calls, texts and notes stay in the conversation.", confirm: "Delete", danger: true }))) return;
            await api.deleteContact(number).catch((err) => toast((err as Error).message));
            state.editingContact = false;
            contactDraft = null;
            go({ name: "contacts" });
          } }, icon("trash", "sm"), "Delete")
        : null));
}

/** Details, tags and notes for a number. `compact` is the side panel next to a conversation. */
/** One traveler on a contact (the client, or someone in Family & travelers), as the cards and the GDS lines see them. */
interface Traveler { name: string; relation: string; birthday: string; passportExp: string; ktn: string; redress: string; notes: string; gender?: "M" | "F" }
const GDS_MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
/**
 * 7.10.30: a birthday written in a traveler's notes ("sep 12 22", "Sep 12, 2022", "12 Sep 22", "9/12/22", "12SEP22",
 * "2022-09-12") → "2022-09-12"; "" when the notes are anything other than one full date. A birthday is never in the
 * future (a 2-digit year that lands after today is last century).
 */
function looseBirthday(text: string): string {
  const s = text.trim().replace(/(\d)(st|nd|rd|th)\b/gi, "$1").replace(/[.,]/g, " ").replace(/\s+/g, " ").trim();
  if (!s) return "";
  const mon = (w: string) => GDS_MONTHS.indexOf(w.slice(0, 3).toUpperCase()) + 1;
  let y = 0, m = 0, d = 0, r: RegExpExecArray | null;
  if ((r = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(s))) [y, m, d] = [+r[1], +r[2], +r[3]];
  else if ((r = /^([A-Za-z]{3,9}) (\d{1,2}) (\d{2}|\d{4})$/.exec(s))) [m, d, y] = [mon(r[1]), +r[2], +r[3]];
  else if ((r = /^(\d{1,2}) ?([A-Za-z]{3,9}) ?(\d{2}|\d{4})$/.exec(s))) [d, m, y] = [+r[1], mon(r[2]), +r[3]];
  else if ((r = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/.exec(s))) [m, d, y] = [+r[1], +r[2], +r[3]];
  else return "";
  if (y < 100) y += 2000;
  const today = new Date();
  if (new Date(y, m - 1, d) > today) y -= 100;
  if (m < 1 || m > 12 || d < 1 || d > 31) return "";
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCMonth() !== m - 1) return ""; // "Feb 31"
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
/** A traveler with no birthday whose notes are just a date: that date is their birthday (it was typed in Notes). */
function withNotesBirthday<T extends { birthday: string; notes: string }>(t: T): T {
  if (t.birthday) return t;
  const b = looseBirthday(t.notes ?? "");
  return b ? { ...t, birthday: b, notes: "" } : t;
}
/** "1990-03-09" → "09MAR90" (the GDS's date). */
function gdsDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}${GDS_MONTHS[+m[2] - 1]}${m[1].slice(2)}` : "";
}
/**
 * 3.10.23 "copy the GDS format with one click": the lines the reservation system takes, written the way its own
 * screen shows them back (SSR DOCS …/DB/30SEP87/M/NOUROLLAH/YOSEF, SSR DOCO …//K/TT118X5XQ///USA):
 *   3DOCS/DB/09MAR90/M/SMITH/JOHN          date of birth, gender, LAST/FIRST (a middle name: LAST/FIRST MIDDLE)
 *   3DOCO//K/984543908///USA               Known Traveler (TSA); //R/ for a Redress number
 * Names in capitals, letters only (no spaces, hyphens or apostrophes inside a name).
 */
function gdsLines(t: Traveler): { lines: string[]; missing: string[] } {
  const lines: string[] = [];
  const missing: string[] = [];
  const parts = t.name.toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Z\s]/g, "").split(/\s+/).filter(Boolean);
  const who = firstName(t.name) || "This traveler";
  if (t.birthday) {
    if (parts.length < 2) missing.push(`${who}: needs a first and last name`);
    else if (!t.gender) missing.push(`${who}: pick M or F`);
    // A middle name goes after the first name with a space, not a slash: …/LOPEZ/MARIA ISABEL.
    else lines.push(`3DOCS/DB/${gdsDate(t.birthday)}/${t.gender}/${parts.at(-1)}/${parts.slice(0, -1).join(" ")}`);
  } else missing.push(`${who}: add a birthday for the GDS line`); // 7.10.30: said for everyone, not only with a KTN
  if (t.ktn) lines.push(`3DOCO//K/${t.ktn}///USA`);
  if (t.redress) lines.push(`3DOCO//R/${t.redress}///USA`);
  return { lines, missing };
}
const gdsEntry = (lines: string[]) => lines.join("§");
function copyGds(lines: string[], missing: string[] = []) {
  if (!lines.length) return toast(missing[0] ?? "Nothing to copy yet: add a birthday or KTN");
  // 7.10.30: one entry, the lines joined with § (the GDS's end-item mark): 3DOCS/DB/09MAR90/M/SMITH/JOHN§3DOCS/…
  void navigator.clipboard?.writeText(gdsEntry(lines)).then(
    () => toast(`Copied for the GDS: ${lines.length} item${lines.length === 1 ? "" : "s"} in one line${missing.length ? ` (left out: ${missing.join("; ")})` : ""}`),
    () => toast("Couldn't copy"));
}
/**
 * 2.11.3 "when copying all for the GDS with several passengers, ask first what passenger number each one is: more than
 * one passenger has a different format": each traveler's lines end in their PNR passenger number ("-1.1"):
 *   3DOCS/DB/28JAN74/F/LERNER/ESTHER-1.1§3DOCO//K/TT11B4FKS///USA-1.1§3DOCS/DB/12MAR71/M/LERNER/LEIB-2.1§…
 * A small box beside each name takes it (starting at 1.1, 2.1, 3.1…; remembered for that contact). One traveler: as before.
 */
const paxNumbers = new Map<string, string[]>();
function withPax(lines: string[], pax: string): string[] {
  return lines.map((l) => `${l}-${pax}`);
}
function copyAllGds(number: string, travelers: Traveler[]) {
  const each = travelers.map((t) => ({ t, ...gdsLines(t) }));
  const withLines = each.filter((x) => x.lines.length);
  const missing = each.flatMap((x) => x.missing);
  if (withLines.length < 2) return copyGds(withLines.flatMap((x) => x.lines), missing);
  const prev = document.activeElement as HTMLElement | null;
  const saved = paxNumbers.get(number) ?? [];
  const boxes = withLines.map((x, i) => h("input", { class: "field glass-input pax-box", value: saved[i] ?? `${i + 1}.1`, inputmode: "decimal", autocomplete: "off",
    "aria-label": `${x.t.name}'s passenger number`, maxlength: 6, oninput: (e: Event) => (e.target as HTMLInputElement).classList.remove("bad") }) as HTMLInputElement);
  const close = () => {
    overlay.classList.add("closing");
    document.removeEventListener("keydown", onKey, true);
    setTimeout(() => overlay.remove(), 140);
    prev?.focus?.();
  };
  const ok = () => {
    const pax = boxes.map((b) => b.value.trim());
    let bad = false;
    pax.forEach((v, i) => { if (!/^\d{1,2}\.\d{1,2}$/.test(v)) { boxes[i].classList.add("bad"); bad = true; } });
    if (bad) return toast("Passenger numbers look like 1.1, 2.1 or 1.2");
    const dup = pax.find((v, i) => pax.indexOf(v) !== i);
    if (dup) return toast(`${dup} is used twice: each passenger needs their own number`);
    paxNumbers.set(number, pax);
    close();
    copyGds(withLines.flatMap((x, i) => withPax(x.lines, pax[i])), missing);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") (e.preventDefault(), e.stopPropagation(), close());
    else if (e.key === "Enter" && !e.isComposing) (e.preventDefault(), e.stopPropagation(), ok());
  };
  const overlay = h("div", { class: "glass-overlay dialog", role: "dialog", "aria-modal": "true", "aria-label": "Passenger numbers", onclick: (e: MouseEvent) => e.target === overlay && close() },
    h("div", { class: "glass-card glass-dialog pax-dialog" },
      h("div", { class: "glass-dialog-title" }, "Passenger numbers"),
      h("div", { class: "glass-dialog-msg" }, "As each one is in the PNR (1.1, 2.1, or 1.2 for a second name in the same field)."),
      h("div", { class: "pax-list" }, ...withLines.map((x, i) => h("label", { class: "pax-row" },
        h("span", { class: "pax-name" }, h("b", {}, x.t.name), x.t.relation ? h("small", {}, x.t.relation) : null), boxes[i]))),
      h("div", { class: "glass-dialog-actions" },
        h("button", { class: "btn quiet", type: "button", onclick: close }, "Cancel"),
        h("button", { class: "btn", type: "button", onclick: ok }, icon("copy", "sm"), "Copy for GDS"))));
  document.addEventListener("keydown", onKey, true);
  document.body.append(overlay);
  requestAnimationFrame(() => (boxes[0].focus(), boxes[0].select()));
}
/** M / F picked on a traveler card: saved on the contact (so the next copy has it), then their lines are copied. */
async function setTravelerGender(number: string, c: ContactDetail, index: number, g: "M" | "F") {
  const d: ContactDetails = structuredClone(c.details ?? EMPTY_DETAILS);
  if (index === 0) d.gender = g;
  else if (d.family[index - 1]) {
    d.family[index - 1].gender = g;
    // 7.10.30: a birthday that was typed in their Notes is saved as their birthday now.
    d.family[index - 1] = withNotesBirthday(d.family[index - 1]);
  }
  const t = index === 0 ? { name: d.legalName || c.name || "", relation: "", birthday: d.birthday, passportExp: d.passportExp, ktn: d.ktn ?? "", redress: d.redress ?? "", notes: "", gender: g }
    : { ...d.family[index - 1], ktn: d.family[index - 1].ktn ?? "", redress: d.family[index - 1].redress ?? "" };
  const { lines, missing } = gdsLines(t);
  copyGds(lines, missing);
  if (!c.exists || !c.name) return; // not saved as a contact yet: copied, nothing to save it on
  try {
    await api.saveContact(number, { name: c.name, company: c.company ?? null, email: c.email ?? null, tags: c.tags, details: d });
    threadCache.delete(number);
    await refresh();
  } catch (err) {
    toast((err as Error).message);
  }
}
/** One traveler's card: the same for the client and each family member. */
function travelerCard(t: Traveler, isClient: boolean, setGender: (g: "M" | "F") => void): Node {
  const gds = gdsLines(t);
  const needsGender = !!t.birthday && !t.gender;
  const copy = needsGender
    ? h("span", { class: "gds-pick", title: "M or F for the GDS line (saved on the contact)" }, h("span", { class: "gds-pick-label" }, "GDS"),
        h("button", { type: "button", class: "gds-g", "aria-label": `${firstName(t.name)} is male: copy GDS lines`, onclick: () => setGender("M") }, "M"),
        h("button", { type: "button", class: "gds-g", "aria-label": `${firstName(t.name)} is female: copy GDS lines`, onclick: () => setGender("F") }, "F"))
    : gds.lines.length
      ? h("button", { type: "button", class: "gds-copy", title: gdsEntry(gds.lines), "aria-label": `Copy ${firstName(t.name)}'s GDS lines`, onclick: () => copyGds(gds.lines, gds.missing) }, icon("copy", "sm"), "GDS")
      // 7.10.30: no button at all left you guessing why: it's there, and says what's missing (a birthday, a last name).
      : h("button", { type: "button", class: "gds-copy gds-missing", title: gds.missing[0] ?? "", "aria-label": `${firstName(t.name)}'s GDS line: ${gds.missing[0] ?? "nothing to copy"}`, onclick: () => toast(gds.missing[0] ?? "Nothing to copy yet") }, icon("copy", "sm"), "GDS");
  const empty = !t.birthday && !t.passportExp && !t.ktn && !t.redress && !t.notes;
  const words = t.name.split(/\s+/).filter(Boolean);
  const short = words.length > 2 ? `${words[0]} ${words.at(-1)}` : t.name; // initials from first and last name
  return h("div", { class: "group family traveler-card" }, h("div", { class: "fam" },
    avatar(t.name, short, "sm"),
    h("div", { class: "grow" },
      h("div", { class: "fam-name" }, t.name, isClient ? h("span", { class: "muted" }, " · client") : t.relation ? h("span", { class: "muted" }, ` · ${t.relation}`) : null,
        t.gender ? h("span", { class: "muted" }, ` · ${t.gender}`) : null),
      h("div", { class: "fam-sub" },
        t.birthday ? h("span", {}, `🎂 ${fmtDay(t.birthday)}${ageOf(t.birthday) !== null ? ` (${ageOf(t.birthday)})` : ""}`) : null,
        t.passportExp ? h("span", {}, "🛂 ", passportLine(t.passportExp)) : null,
        t.ktn ? h("span", {}, `KTN ${t.ktn}`) : null,
        t.redress ? h("span", {}, `Redress ${t.redress}`) : null,
        t.notes ? h("span", {}, t.notes) : null,
        empty ? h("span", {}, "No birthday or KTN yet") : null)),
    copy));
}

/** 6.11.2: takes a merged number back out (it's its own contact again, with the name). */
async function separateNumber(n: string, owner: string) {
  if (!(await glassDialog({ title: `Separate ${phone(n)}?`, message: "It becomes its own contact again, with the same name. Notes stay with this contact.", confirm: "Separate" }))) return;
  try {
    await api.unmergeContact(n);
    toast(`${phone(n)} is its own contact again`);
    contactCache.delete(owner);
    contactCache.delete(n);
    state.contact = await api.contact(state.route.name === "contact" ? state.route.number : owner);
    void loadContacts();
    render();
  } catch (e) {
    toast((e as Error).message);
  }
}
/**
 * 6.11.2 "allow to merge contacts" (7.11.2: "we have several of them": pick as many as you need, in one go): tick the
 * other contacts (or type a number), choose whose name stays, merge. One person or business then has every number:
 * everything about them on one card, each number keeping its own conversation.
 */
function mergeContactDialog(number: string, c: ContactDetail) {
  const prev = document.activeElement as HTMLElement | null;
  let hits: { number: string; name: string; company: string | null }[] = [];
  const picked = new Map<string, { number: string; name: string | null }>();
  let step: "pick" | "confirm" = "pick";
  /** Whose name stays: this contact's number, or one of the picked. */
  let keepFrom = number;
  let busy = false;
  let timer = 0;
  const close = () => {
    overlay.classList.add("closing");
    document.removeEventListener("keydown", onKey, true);
    setTimeout(() => overlay.remove(), 140);
    prev?.focus?.();
  };
  const onKey = (e: KeyboardEvent) => e.key === "Escape" && (e.preventDefault(), e.stopPropagation(), close());
  const search = h("input", { class: "field glass-input", placeholder: "Search a contact, or type a phone number", autocomplete: "off", "aria-label": "Contacts to merge" }) as HTMLInputElement;
  const body = h("div", { class: "merge-body" });
  const nameOf = (x: { number: string; name: string | null }) => x.name || phone(x.number);
  const draw = () => {
    body.replaceChildren();
    search.hidden = step !== "pick";
    if (step === "pick") {
      const typed = toE164(search.value.trim());
      const own = new Set([number, ...(c.otherNumbers ?? [])]);
      const list = hits.filter((x) => !own.has(x.number)).slice(0, 10);
      if (typed && !own.has(typed) && !list.some((x) => x.number === typed)) list.push({ number: typed, name: "", company: null });
      const toggle = (x: { number: string; name: string }) => {
        if (picked.has(x.number)) picked.delete(x.number);
        else picked.set(x.number, { number: x.number, name: x.name || null });
        draw();
      };
      body.append(
        picked.size ? h("div", { class: "merge-picked" }, ...[...picked.values()].map((x) =>
          h("button", { class: "merge-pill", type: "button", title: `Remove ${nameOf(x)}`, onclick: () => (picked.delete(x.number), draw()) }, nameOf(x), icon("close", "sm")))) : h("span", {}),
        list.length
          ? h("div", { class: "merge-list", role: "list" }, ...list.map((x) => h("button", { class: `merge-hit${picked.has(x.number) ? " on" : ""}`, type: "button", role: "listitem", "aria-pressed": String(picked.has(x.number)), onclick: () => toggle(x) },
              h("span", { class: "merge-tick", "aria-hidden": "true" }, picked.has(x.number) ? icon("check", "sm") : null),
              avatar(x.number, x.name || null, "sm"), h("span", { class: "grow" }, h("b", {}, x.name || phone(x.number)), h("small", {}, x.name ? [phone(x.number), x.company].filter(Boolean).join(" · ") : "Not saved as a contact")))))
          : h("div", { class: "muted merge-empty" }, search.value.trim() ? "No contact found" : "Start typing a name or number"),
        h("div", { class: "glass-dialog-actions" },
          h("button", { class: "btn quiet", type: "button", onclick: close }, "Cancel"),
          h("button", { class: "btn", type: "button", disabled: !picked.size, onclick: () => ((step = "confirm"), draw()) }, picked.size ? `Next (${picked.size})` : "Next")));
      return;
    }
    const all = [{ number, name: c.name }, ...picked.values()];
    const names = all.filter((x, i) => x.name && all.findIndex((y) => y.name === x.name) === i);
    body.append(...[
      h("div", { class: "glass-dialog-msg" },
        picked.size === 1 ? "" : `These ${picked.size} become part of this contact: `,
        ...[...picked.values()].flatMap((x, i) => [i ? ", " : "", h("b", {}, nameOf(x)), x.name ? ` · ${phone(x.number)}` : ""]),
        picked.size === 1 ? " becomes part of this contact." : ".",
        " Their company, email, tags, details and notes are added here; every number stays, each with its own conversation."),
      names.length > 1
        ? h("div", { class: "merge-keep" }, h("div", { class: "muted" }, "Keep which name?"),
            h("div", { class: "chips" }, ...names.map((x) =>
              h("button", { class: `chip${keepFrom === x.number ? " on" : ""}`, type: "button", "aria-pressed": String(keepFrom === x.number), onclick: () => ((keepFrom = x.number), draw()) }, x.name!))))
        : null,
      h("div", { class: "glass-dialog-actions" },
        h("button", { class: "btn quiet", type: "button", onclick: () => ((step = "pick"), draw(), search.focus()) }, "Back"),
        h("button", { class: "btn", type: "button", disabled: busy, onclick: async () => {
          busy = true;
          draw();
          // The one whose name stays goes first (taking its name); then the rest, keeping that name.
          const order = [...picked.keys()].sort((a, b) => Number(b === keepFrom) - Number(a === keepFrom));
          let last: { number: string; name: string; otherNumbers: string[] } | null = null;
          let done = 0;
          try {
            for (const n of order) {
              last = await api.mergeContact(number, n, n === keepFrom ? "theirs" : "mine");
              done++;
              contactCache.delete(n);
            }
            toast(`Merged: ${last!.name} now has ${1 + last!.otherNumbers.length} numbers`);
            close();
          } catch (e) {
            toast(done ? `Merged ${done} of ${order.length}; then: ${(e as Error).message}` : (e as Error).message);
            if (!done) {
              busy = false;
              draw();
              return;
            }
            close();
          }
          contactCache.delete(number);
          state.contact = await api.contact(last?.number ?? number).catch(() => state.contact);
          void loadContacts();
          render();
        } }, busy ? "Merging…" : picked.size === 1 ? "Merge" : `Merge ${picked.size + 1} into one`))].filter((x): x is HTMLDivElement => !!x));
  };
  search.addEventListener("input", () => {
    clearTimeout(timer);
    timer = window.setTimeout(async () => {
      const q = search.value.trim();
      hits = q ? await api.contacts(q).catch(() => []) : [];
      draw();
    }, 180);
    draw();
  });
  const overlay = h("div", { class: "glass-overlay dialog", role: "dialog", "aria-modal": "true", "aria-label": "Merge contacts", onclick: (e: MouseEvent) => e.target === overlay && close() },
    h("div", { class: "glass-card glass-dialog merge-dialog" },
      h("div", { class: "glass-dialog-title" }, `Merge with ${c.name || phone(number)}`),
      h("div", { class: "glass-dialog-msg" }, "The same person or business saved more than once (other numbers of theirs)? Tick each one."),
      search, body));
  document.addEventListener("keydown", onKey, true);
  document.body.append(overlay);
  draw();
  requestAnimationFrame(() => search.focus());
}
function contactCard(number: string, c: ContactDetail | null, compact: boolean): Node {
  if (!c) return h("div", { class: "pending", style: "padding:24px" }, h("span", { class: "spinner" }), "Loading");
  const editing = state.editingContact;
  if (!editing && contactDraft?.number === number) contactDraft = null;
  const field = (label: string, value: string | null, href?: string) => value
    ? h("div", { class: "kv" }, h("span", { class: "k" }, label), href ? h("a", { class: "v", href }, value) : h("span", { class: "v" }, value))
    : null;
  const kv = (label: string, v: Node) => h("div", { class: "kv" }, h("span", { class: "k" }, label), v);
  const d = c.details ?? EMPTY_DETAILS;
  // The client is the first traveler (legal name, or the contact's name); then everyone traveling with them.
  const travelers: Traveler[] = [
    { name: d.legalName || c.name || nameFor(number), relation: "", birthday: d.birthday, passportExp: d.passportExp, ktn: d.ktn ?? "", redress: d.redress ?? "", notes: "", gender: d.gender },
    ...d.family.map((f) => withNotesBirthday({ name: f.name, relation: f.relation, birthday: f.birthday, passportExp: f.passportExp, ktn: f.ktn ?? "", redress: f.redress ?? "", notes: f.notes, gender: f.gender })),
  ];
  const hasProfile = !!(d.legalName || d.birthday || d.passportExp || d.ktn || d.redress);
  const extras = [
    d.anniversary ? kv("Anniversary", dayLine(d.anniversary, false)) : null,
    d.preferences ? kv("Prefers", h("span", { class: "v pre" }, d.preferences)) : null,
  ].filter(Boolean) as Node[];
  const startEdit = () => {
    state.editingContact = true;
    draftFor(number, c);
    render();
    document.getElementById("ct-name")?.focus();
  };
  const details = editing
    ? detailsEditor(number, c, compact)
    : h("div", { class: "contact-blocks" },
      h("div", { class: "group contact-info" },
        compact ? h("div", { class: "contact-head" }, avatar(number, c.name, "lg"),
          h("div", { class: "grow" }, h("div", { class: "contact-name" }, c.name || nameFor(number)),
            c.company ? h("div", { class: "sub" }, c.company) : null,
            !c.name && guessOf(number) ? h("div", { class: "sub guess-sub" }, icon(guessOf(number)!.source === "caller-id" ? "user" : "sparkle", "sm"),
              guessOf(number)!.source === "caller-id" ? "Name from caller ID" : `AI guessed the name from their ${guessOf(number)!.source}`) : null)) : null,
        // 6.11.2: a number merged into another contact says whose it is.
        c.mergedInto ? h("div", { class: "merged-note" }, icon("link", "sm"),
          h("span", { class: "grow" }, "This number is part of ", h("b", {}, c.name || phone(c.mergedInto)), "'s contact."),
          h("button", { class: "btn quiet sm", onclick: () => go({ name: "contact", number: c.mergedInto! }) }, "Open"),
          h("button", { class: "btn quiet sm", onclick: () => void separateNumber(number, number) }, "Separate")) : null,
        field("Phone", phone(number), `tel:${number}`),
        // The numbers merged into this contact: each opens its own conversation, and can be separated again.
        ...(c.otherNumbers ?? []).map((n) => h("div", { class: "kv merged-number" }, h("span", { class: "k" }, "Also"),
          h("span", { class: "v" }, h("a", { href: `tel:${n}` }, phone(n)), " ",
            h("button", { class: "link-btn", type: "button", onclick: () => go({ name: "thread", number: n }) }, "Conversation"), " · ",
            h("button", { class: "link-btn", type: "button", onclick: () => void separateNumber(n, number) }, "Separate")))),
        ...d.phones.filter((p) => !(c.otherNumbers ?? []).some((n) => (toE164(p.value) ?? p.value) === n)).map((p) => field(p.label || "Phone", phone(toE164(p.value) ?? p.value), `tel:${toE164(p.value) ?? p.value}`)),
        field("Email", c.email, c.email ? `mailto:${c.email}` : undefined),
        ...d.emails.map((e) => field(e.label || "Email", e.value, `mailto:${e.value}`)),
        d.address ? field("Address", d.address, `https://maps.google.com/?q=${encodeURIComponent(d.address)}`) : null,
        !compact ? field("Company", c.company) : null,
        c.tags.length ? h("div", { class: "kv" }, h("span", { class: "k" }, "Tags"), h("span", { class: "v tags" }, ...c.tags.map((t) => h("span", { class: "tag" }, t)))) : null,
        h("div", { class: "kv" }, h("span", { class: "k" }, "History"), h("span", { class: "v" }, `${c.stats.calls} call${c.stats.calls === 1 ? "" : "s"} · ${c.stats.texts} text${c.stats.texts === 1 ? "" : "s"}${c.stats.lastActivityAt ? ` · last ${listTime(c.stats.lastActivityAt)}` : ""}`)),
        c.optedOut ? h("div", { class: "kv" }, h("span", { class: "k" }, "Texting"), h("span", { class: "v bad" }, "Opted out (replied STOP)")) : null,
        c.blocked ? h("div", { class: "kv" }, h("span", { class: "k" }, "Blocked"), h("span", { class: "v bad" }, "Calls and texts are dropped")) : null,
        h("div", { class: "row-actions", style: "padding:10px 14px 12px" },
          h("button", { class: "btn quiet sm", onclick: startEdit }, icon("edit", "sm"), c.exists ? (compact ? "Edit" : "Edit details") : "Save contact"),
          // 6.10.22: Quick add (1.10.23: "Express add") right next to Edit; its box opens right here.
          h("button", { class: `btn quiet sm quick-pill${quickOpenFor === number ? " on" : ""}`, "aria-expanded": String(quickOpenFor === number), onclick: () => {
            quickOpenFor = quickOpenFor === number ? null : number;
            quickFocus = !!quickOpenFor;
            if (!quickOpenFor) quickText = "";
            render();
          } }, icon("sparkle", "sm"), "Express add"),
          !compact ? h("button", { class: "btn quiet sm", onclick: () => void toggleBlock(number, c.blocked) }, icon("block", "sm"), c.blocked ? "Unblock" : "Block") : null,
          // 6.11.2 "allow to merge contacts".
          // 7.11.2: on the conversation's side panel too (that's where contacts are opened on a computer).
          !c.mergedInto ? h("button", { class: "btn quiet sm", onclick: () => void mergeContactDialog(number, c) }, icon("users", "sm"), "Merge") : null),
        quickOpenFor === number ? quickAddPanel(number, c) : null),
      // 3.10.23 "each card should look the same": the client and everyone traveling with them, one identical card
      // each (name, birthday, passport, KTN, Redress), with a one-click copy of their GDS lines.
      h("div", { class: "group-title travelers-title" }, h("span", { class: "grow" }, `Travelers · ${1 + d.family.length}`),
        travelers.some((t) => t.birthday || t.ktn || t.redress) ? h("button", { class: "link-btn gds-all", type: "button", title: "Copy every traveler's DOCS / DOCO lines for the GDS",
          onclick: () => copyAllGds(number, travelers) }, icon("copy", "sm"), "Copy all for GDS") : null),
      ...travelers.map((t, i) => travelerCard(t, i === 0, (g) => void setTravelerGender(number, c, i, g))),
      !hasProfile && !d.family.length ? h("button", { class: "group empty-profile", onclick: startEdit }, icon("plus", "sm"), "Add birthday, KTN, passport, family…") : null,
      extras.length ? h("div", { class: "group-title" }, "Good to know") : null,
      extras.length ? h("div", { class: "group contact-info" }, ...extras) : null);

  const notes = [...c.notes].sort((a, b) => Number(b.pinned) - Number(a.pinned) || a.id - b.id);
  const shown = compact ? notes.slice(-4) : notes;
  return h("div", { class: compact ? "contact-card compact" : "contact-card" },
    details,
    h("div", { class: "group-title" }, `Notes${notes.length ? ` · ${notes.length}` : ""}`),
    shown.length
      ? h("div", { class: "notes-list" }, ...shown.map((n) => contactNote(number, n, c.notes, compact)))
      : h("div", { class: "sub empty-notes" }, compact ? "No notes yet." : "No notes yet. Add what the team should know (preferences, passport dates, family) and @tag a teammate to loop them in."),
    compact ? h("button", { class: "text-btn", onclick: () => go({ name: "contact", number }) }, icon("plus", "sm"), notes.length > shown.length ? `See all ${notes.length} notes` : "Add a note") : null);
}

function contactNote(number: string, n: ContactNote, all: ContactNote[], compact: boolean) {
  const mine = n.userId === state.user?.id;
  const parent = n.replyTo ? all.find((x) => x.id === n.replyTo) : null;
  return h("div", { class: "note-row", id: `n-${n.id}` },
    avatar(String(n.userId) + n.userName, n.userName, "sm"),
    h("div", { class: "note-col" }, h("div", { class: n.pinned ? "note pinned" : "note" },
      h("div", { class: "note-meta" },
        n.pinned ? icon("pin", "sm") : null,
        h("strong", {}, mine ? "You" : n.userName), h("span", {}, when(n.createdAt)),
        n.updatedAt ? h("span", { class: "edited", title: `Edited ${when(n.updatedAt)}` }, "edited") : null,
        !compact ? h("span", { class: "note-acts" },
          reactButton("note", n.id),
          h("button", { class: "reply-btn", "aria-label": "Reply", title: "Reply", onclick: () => {
            state.noteReply = { id: n.id, name: n.userName, body: n.body };
            render();
            document.getElementById("draft")?.focus();
          } }, icon("reply", "sm"), h("span", { class: "act-label" }, "Reply")),
          h("button", { class: "reply-btn", "aria-label": n.pinned ? "Unpin" : "Pin", title: n.pinned ? "Unpin" : "Pin to the top of the conversation", onclick: async () => {
            await api.updateContactNote(number, n.id, { pinned: !n.pinned }).catch((e) => toast((e as Error).message));
            await refresh();
          } }, icon("pin", "sm")),
          mine ? editButton("note", n.id, n.body) : null,
          mine || isAdmin() ? h("button", { class: "reply-btn", "aria-label": "Delete note", onclick: async () => {
            if (!(await glassDialog({ title: "Delete this note?", confirm: "Delete", danger: true }))) return;
            await api.deleteContactNote(number, n.id).catch((e) => toast((e as Error).message));
            await refresh();
          } }, icon("trash", "sm")) : null) : null),
      parent ? quoteBlock(parent.userName, parent.body, `n-${parent.id}`) : null,
      isEditing("note", n.id)
        ? inlineEditor((body) => api.updateContactNote(number, n.id, { body }))
        : h("div", { class: "note-body" }, ...withMentions(n.body)),
      ), reactionRow("note", n.id, n.reactions)));
}

// ---------------------------------------------------------------- activity

async function loadActivity() {
  try {
    const r = await api.activity();
    state.activity = r.items;
    state.activityUnread = r.unread;
    state.activityLoaded = true;
    render();
  } catch {
    // shown as empty
  }
}

const ACTIVITY_TEXT: Record<string, string> = {
  mention: "mentioned you in a comment", note_mention: "tagged you in a contact note", chat_mention: "mentioned you in team chat",
  assigned: "assigned you a conversation", task_due: "Follow-up due", missed_call: "tried to call you",
  task_assigned: "gave you a follow-up",
};
/** 7.10.30: a follow-up given to you ("task_assigned"; older rows are "assigned" with ref task:<id>). */
const isTaskAssigned = (a: ActivityItem) => a.kind === "task_assigned" || (a.kind === "assigned" && !!a.ref?.startsWith("task:"));

/** Clears the unread badge on every conversation (for you only). */
async function markAllRead() {
  const n = state.conversations.filter((c) => c.unread).length;
  const ok = await glassDialog({
    title: n ? `Mark all ${n} as read?` : "Mark everything as read?",
    message: "Clears the unread count on every conversation, for you only. Nothing is deleted.",
    confirm: "Mark all read",
  });
  if (!ok) return;
  try {
    await api.readAll();
    state.conversations.forEach((c) => ((c.unread = false), (c.comment_count = 0), (c.needs_you = 0)));
    toast("All caught up");
    render();
    void refresh();
  } catch (e) {
    toast((e as Error).message);
  }
}

/** Removes all your notifications from Activity. */
async function clearNotifications() {
  const ok = await glassDialog({ title: "Clear all notifications?", message: "Removes everything from your Activity list. Your teammates' lists aren't affected.", confirm: "Clear all", danger: true });
  if (!ok) return;
  try {
    await api.clearActivity();
    state.activity = [];
    state.activityUnread = 0;
    render();
  } catch (e) {
    toast((e as Error).message);
  }
}

/** Admins: deletes a whole conversation (calls, recordings, texts, notes) for everyone. */
async function deleteThread(number: string, name: string | null) {
  const internal = isInternalKey(number);
  const who = name ? `${name}, ${phone(number)},` : phone(number);
  const ok = await glassDialog({
    title: "Delete this conversation?",
    message: internal
      ? `All calls with ${name ?? "them"} on this line are removed from the inbox. This can't be undone.${number.startsWith("ai:") ? ` Your chat with ${name ?? "them"} stays.` : ""}`
      : `All calls, recordings, voicemails, texts and internal notes with ${who} are removed for the whole team. This can't be undone. The saved contact stays.`,
    confirm: "Delete",
    danger: true,
  });
  if (!ok) return;
  try {
    const r = internal ? await api.deleteInternal(number) : await api.deleteThread(number);
    state.conversations = state.conversations.filter((c) => c.external_number !== number);
    const parts = [r.calls ? `${r.calls} call${r.calls === 1 ? "" : "s"}` : "", r.messages ? `${r.messages} text${r.messages === 1 ? "" : "s"}` : ""].filter(Boolean);
    toast(parts.length ? `Deleted ${parts.join(" and ")}` : "Conversation deleted");
    // Deleted from the inbox list while another conversation is open: that one stays open.
    if ((state.route as { number?: string }).number === number) go({ name: "inbox" });
    else render();
    void refresh();
  } catch (e) {
    toast((e as Error).message);
  }
}

function activityView() {
  const open = (a: ActivityItem) => {
    if (!a.readAt) void api.readActivity([a.id]).then((r) => ((state.activityUnread = r.unread), render())).catch(() => {});
    a.readAt = a.readAt ?? new Date().toISOString();
    // 7.10.30: a chat @mention keeps its ref too (Team chat opens at that message; it used to be dropped).
    if (a.ref) state.focusRef = a.ref;
    if (a.kind === "chat_mention") return go({ name: "chat" });
    if (a.kind === "missed_call") {
      // A teammate call you missed: offer to call them back.
      const mate = state.team.find((u) => u.id === a.actorId);
      if (!mate || !state.user?.canCall) return render();
      return void glassDialog({ title: `Call ${mate.name} back?`, confirm: "Call" }).then((ok) => (ok ? void callTeammate(mate) : render()));
    }
    if (a.kind === "note_mention" && a.number) return go({ name: "contact", number: a.number });
    // 7.10.30: a follow-up given to you without a conversation did nothing when tapped.
    if ((a.kind === "task_due" || isTaskAssigned(a)) && !a.number) return go({ name: "tasks" });
    if (a.number) {
      return go({ name: "thread", number: a.number });
    }
  };
  return screen(
    h("header", { class: "thread-head" },
      h("div", { class: "who" }, h("div", { class: "title" }, "Activity"), h("div", { class: "number wrap-sub" }, "Mentions, assignments, follow-ups and missed teammate calls")),
      state.activityUnread ? h("button", { class: "btn quiet sm", onclick: async () => {
        const r = await api.readActivity().catch(() => null);
        if (r) state.activityUnread = r.unread;
        state.activity.forEach((a) => (a.readAt ??= new Date().toISOString()));
        render();
      } }, icon("check", "sm"), "Mark all read") : null,
      state.activity.length ? h("button", { class: "btn quiet sm", onclick: () => void clearNotifications() }, icon("trash", "sm"), "Clear all") : null),
    h("div", { class: "page" }, h("div", { class: "page-inner" },
      // Notifications off: turned on from here (the inbox banner shows only until you tap × on it).
      !alertsOn()
        ? h("div", { class: "group" },
            h("button", { class: "item link activity alerts-off", onclick: () => void enableAlerts().then(() => render()) },
              h("span", { class: "item-icon" }, icon("bell", "sm")),
              h("div", { class: "grow" }, h("div", {}, h("strong", {}, "Notifications are off")), h("div", { class: "sub" }, "Turn them on to hear about calls and texts when Joy Phone isn't open.")),
              h("span", { class: "text-btn" }, "Turn on")))
        : null,
      // Phones: a new version of Joy Phone, always on top until you update.
      updateAlert()
        ? h("div", { class: "group" },
            h("button", { class: "item link activity unread update-alert", onclick: () => void promptUpdate() },
              h("span", { class: "item-icon" }, icon("download", "sm")),
              h("div", { class: "grow" },
                h("div", {}, h("strong", {}, `Joy Phone ${updateState.version} is ready`)),
                h("div", { class: "sub" }, `You're on ${VERSION}. Tap to update (takes a second).`)),
              h("span", { class: "unread-dot" })))
        : null,
      state.activity.length
        ? h("div", { class: "group" }, ...state.activity.map((a) => {
            const who = a.kind === "task_due" ? "" : a.actorName ? (a.actorId === state.user?.id ? "You" : a.actorName) : "Someone";
            const about = a.contactName || (a.number ? phone(a.number) : "");
            return h("button", { class: a.readAt ? "item link activity" : "item link activity unread", onclick: () => open(a) },
              a.kind === "missed_call" ? h("span", { class: "item-icon missed-mate" }, icon("missed", "sm"))
                : a.actorName && a.kind !== "task_due" ? avatar(String(a.actorId) + a.actorName, a.actorName, "sm") : h("span", { class: "item-icon" }, icon(a.kind === "task_due" ? "clock" : "bell", "sm")),
              h("div", { class: "grow" },
                h("div", {}, who ? h("strong", {}, `${who} `) : null, a.kind === "task_due" ? h("strong", {}, "Follow-up due") : isTaskAssigned(a) ? ACTIVITY_TEXT.task_assigned : ACTIVITY_TEXT[a.kind] ?? a.kind, about ? h("span", { class: "muted" }, ` · ${about}`) : null),
                a.body ? h("div", { class: "sub clamp2" }, a.body) : null),
              h("span", { class: "time muted small", title: fullTime(a.createdAt) }, listTime(a.createdAt)),
              a.readAt ? null : h("span", { class: "unread-dot" }));
          }))
        : state.activityLoaded
          ? emptyState({ icon: "bell", tone: "green", title: "You're all caught up", text: "When someone @mentions you, assigns you a conversation or a follow-up is due, it shows up here." })
          : h("div", { class: "empty" }, h("strong", {}, "Loading…")))));
}

// ---------------------------------------------------------------- tasks (follow-ups)

async function loadTasks() {
  try {
    state.tasks = (await api.tasks({})).tasks;
    state.tasksLoaded = true;
    render();
  } catch {
    // shown as empty
  }
}

function dueLabel(dueAt: string) {
  const d = new Date(dueAt.includes("T") || dueAt.includes("Z") ? dueAt : dueAt.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const tomorrow = new Date(Date.now() + 86_400_000);
  const time = fmtDate(d, { hour: "numeric", minute: "2-digit" });
  if (d.toDateString() === today.toDateString()) return `Today ${time}`;
  if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow ${time}`;
  return fmtDate(d, { weekday: "short", month: "short", day: "numeric" }) + ` ${time}`;
}

/** When it's due, or for her own call for later: "Steven calls Today 9:00 AM" (hers to do, never shown overdue). */
function taskDue(t: Task) {
  if (t.done || !t.dueAt) return null;
  if (t.aiCall) return h("span", { class: "due ai-due" }, h("span", { class: "ai-av xs" }, icon("sparkle", "sm")), ` ${t.aiCall.agent} calls ${dueLabel(t.dueAt).replace(/^(Today|Tomorrow)/, (w) => w.toLowerCase())}`);
  return h("span", { class: dueClass(t) }, dueLabel(t.dueAt));
}

function dueClass(t: Task) {
  if (!t.dueAt || t.done) return "due";
  const d = new Date(t.dueAt.includes("T") || t.dueAt.includes("Z") ? t.dueAt : t.dueAt.replace(" ", "T") + "Z");
  return d.getTime() < Date.now() ? "due overdue" : "due";
}

async function setTaskDone(t: Task, done: boolean) {
  // Show it checked off right away; the server catches up.
  t.done = done;
  render();
  try {
    await api.updateTask(t.id, { done });
    toast(done && t.aiCall ? `Cancelled: ${t.aiCall.agent} won't make this call` : done ? "Task done" : "Task reopened");
    if (state.route.name === "tasks") await loadTasks();
    else await refresh();
  } catch (e) {
    t.done = !done;
    render();
    toast((e as Error).message);
  }
}

/** Presets plus a date picker. Returns ms since epoch. */
function duePresets(): { label: string; at: number }[] {
  const at9 = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(9, 0, 0, 0);
    return d.getTime();
  };
  return [
    { label: "In 1 hour", at: Date.now() + 3600_000 },
    { label: "Tomorrow 9am", at: at9(1) },
    { label: "In 3 days", at: at9(3) },
    { label: "Next week", at: at9(7) },
  ];
}

/** The New follow-up form's choices; kept outside the form so live updates (re-renders) don't reset them. */
let taskDraft: { title: string; preset: number; custom: string; assignee: string } | null = null;
const freshTaskDraft = () => ({ title: "", preset: 1, custom: "", assignee: String(defaultTaskAssignee() ?? "") });

function taskForm(number: string | null) {
  const d = (taskDraft ??= freshTaskDraft());
  const dueAtNow = (): number | null => {
    if (d.preset >= 0) return duePresets()[d.preset]?.at ?? null;
    return d.custom ? new Date(d.custom).getTime() : null;
  };
  const custom = h("input", { id: "task-due", class: "field", type: "datetime-local", value: d.custom, onchange: (e: Event) => {
    d.custom = (e.target as HTMLInputElement).value;
    d.preset = d.custom ? -1 : d.preset;
    presetRow.querySelectorAll("button").forEach((b, i) => b.setAttribute("aria-pressed", String(i === d.preset)));
  } });
  const presetRow = h("div", { class: "segmented wrap", role: "group", "aria-label": "Due" },
    ...duePresets().map((p, i) => h("button", { type: "button", "aria-pressed": String(i === d.preset), onclick: () => {
      d.preset = i;
      d.custom = "";
      custom.value = "";
      presetRow.querySelectorAll("button").forEach((b, j) => b.setAttribute("aria-pressed", String(j === i)));
    } }, p.label)));
  const assignee = h("select", { id: "task-who", class: "field", onchange: (e: Event) => (d.assignee = (e.target as HTMLSelectElement).value) },
    ...state.team.map((u) => h("option", { value: String(u.id), selected: String(u.id) === d.assignee }, u.id === state.user?.id ? `${u.name} (you)` : u.name)));
  return h("form", { class: "task-form", onsubmit: async (e: Event) => {
    e.preventDefault();
    const title = (document.getElementById("task-title") as HTMLInputElement).value.trim();
    if (!title) return toast("What's the follow-up?");
    try {
      await api.createTask({ title, number, dueAt: dueAtNow(), assigneeId: mentionedAssignee(title) ?? Number(assignee.value) });
      state.addingTask = false;
      taskDraft = null;
      toast("Follow-up added");
      if (state.route.name === "tasks") await loadTasks();
      else await refresh();
    } catch (err) {
      toast((err as Error).message);
    }
  } },
    h("input", { id: "task-title", class: "field", placeholder: number ? "Follow up about… (e.g. send the Cancun quote)" : "Follow-up", maxlength: 200, autocomplete: "off",
      value: d.title, oninput: (e: Event) => (d.title = (e.target as HTMLInputElement).value) }),
    presetRow,
    h("div", { class: "form-row two" }, custom, assignee),
    h("div", { class: "row-actions" },
      h("button", { class: "btn sm", type: "submit" }, "Add follow-up"),
      h("button", { class: "btn quiet sm", type: "button", onclick: () => ((state.addingTask = false), render()) }, "Cancel")));
}

function tasksView() {
  const me = state.user!.id;
  const list = state.tasks
    .filter((t) => (state.showDoneTasks ? t.done : !t.done))
    .filter((t) => !state.tasksMine || t.assigneeId === me || (!t.assigneeId && t.createdBy === me))
    .sort((a, b) => (a.dueAt ?? "9").localeCompare(b.dueAt ?? "9"));
  return screen(
    h("header", { class: "thread-head" },
      h("div", { class: "who" }, h("div", { class: "title" }, "Tasks"), h("div", { class: "number" }, "Follow-ups with reminders")),
      h("button", { class: "btn sm", onclick: () => ((state.addingTask = true), (taskDraft = freshTaskDraft()), render(), document.getElementById("task-title")?.focus()) }, icon("plus", "sm"), "New")),
    h("div", { class: "page" }, h("div", { class: "page-inner" },
      h("div", { class: "toolbar" },
        h("div", { class: "segmented", role: "group" },
          h("button", { "aria-pressed": String(state.tasksMine), onclick: () => ((state.tasksMine = true), render()) }, "Mine"),
          h("button", { "aria-pressed": String(!state.tasksMine), onclick: () => ((state.tasksMine = false), render()) }, "Everyone")),
        h("div", { class: "segmented", role: "group" },
          h("button", { "aria-pressed": String(!state.showDoneTasks), onclick: () => ((state.showDoneTasks = false), render()) }, "Open"),
          h("button", { "aria-pressed": String(state.showDoneTasks), onclick: () => ((state.showDoneTasks = true), render()) }, "Done"))),
      state.addingTask ? h("div", { class: "group form" }, taskForm(null)) : null,
      list.length
        ? h("div", { class: "group" }, ...list.map((t) =>
            h("div", { class: "item task-row" },
              h("button", { class: t.done ? "task-check done" : "task-check", "aria-label": t.done ? "Reopen" : t.aiCall ? `Cancel ${t.aiCall.agent}'s call` : "Mark done", title: !t.done && t.aiCall ? `Tick to cancel ${t.aiCall.agent}'s call` : undefined, onclick: () => void setTaskDone(t, !t.done) }, t.done ? icon("check", "sm") : null),
              h("div", { class: "grow" },
                h("button", { class: t.done ? "task-title done" : "task-title", type: "button", title: "Edit task", onclick: () => editTask(t) }, t.title),
                h("div", { class: "sub" },
                  t.done ? null : taskDue(t),
                  t.number ? h("button", { class: "link-btn", onclick: () => {
                    go({ name: "thread", number: t.number! });
                    state.focusRef = `task:${t.id}`;
                  } }, ` · ${t.contactName || phone(t.number)}`) : null,
                  !state.tasksMine && t.assigneeName ? ` · ${t.assigneeId === me ? "You" : firstName(t.assigneeName)}` : null)),
              !t.done && delegateItems(t).length ? h("button", { class: "icon-btn sm", "aria-label": "Delegate", title: "Delegate", "aria-haspopup": "menu", onclick: (e: MouseEvent) => {
                e.stopPropagation();
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                openPointMenu(r.right - 220, r.bottom + 4, delegateItems(t));
              } }, icon("assign", "sm")) : null,
              h("button", { class: "icon-btn sm", "aria-label": "Edit task", title: "Edit", onclick: () => editTask(t) }, icon("edit", "sm")),
              h("button", { class: "icon-btn sm", "aria-label": "Delete", onclick: async () => {
                const ok = await glassDialog({ title: "Delete this task?", message: t.title, confirm: "Delete", danger: true });
                if (!ok) return;
                await api.deleteTask(t.id).catch((e) => toast((e as Error).message));
                await loadTasks();
              } }, icon("trash", "sm")))))
        : !state.tasksLoaded ? h("div", { class: "empty" }, h("strong", {}, "Loading…"))
        : state.showDoneTasks ? emptyState({ icon: "tasks", tone: "slate", title: "Nothing done yet", text: "Follow-ups you tick off show up here." })
        : emptyState({ icon: "tasks", tone: "green", title: "No open follow-ups", text: "Add one here, or from a conversation's ⋯ menu. You'll get a reminder when it's due." }))));
}

// ---------------------------------------------------------------- call speed (Settings › Call speed)
//
// Measures instead of guessing: short silent test calls through every calling region (and, for admins, every
// media site) measure the real round trip of call audio. The fastest measured is used for calls automatically
// and kept as the yardstick; when real calls get clearly slower than it, the app measures again by itself.

const speed = {
  loading: false,
  route: null as null | { serverRegion: string | null; loginsSite: string | null; appSite: string | null; errors: string[]; ownSite?: boolean },
  you: null as GeoIp | null,
  /** The last call's Telnyx media server, when it isn't a Telnyx address (those can't be placed by IP lookup). */
  media: null as GeoIp | null,
  error: null as string | null,
  /** A speed test in progress: what's been measured so far, and what it's on now. */
  run: null as null | { results: SpeedResult[]; now: string; total: number; quiet: boolean },
  device: null as null | DeviceDelay,
  testSites: false,
};
/** The map lives in one element kept across screen refreshes (rebuilding it would flicker and lose zoom). */
let speedMapEl: HTMLElement | null = null;
let speedMapKey = "";

async function loadSpeed() {
  speed.loading = true;
  speed.error = null;
  render();
  const path = lastMediaPath();
  const [route, you, media] = await Promise.all([
    api.diagRoute().catch((e) => ((speed.error = (e as Error).message), null)),
    geoip(""),
    path && !isTelnyxMediaIp(path.ip) ? geoip(path.ip) : Promise.resolve(null),
  ]);
  speed.route = route;
  speed.you = you;
  speed.media = media;
  speed.loading = false;
  render();
  if (!speed.device) void measureDevice(false);
}

async function measureDevice(askMic: boolean) {
  const dp = getDevicePrefs();
  speed.device = await measureDeviceDelay(dp.speakerId, dp.micId, askMic && matchMedia("(pointer: fine)").matches).catch(() => null);
  render();
}

const SITE_TEST_ORDER = ["San Jose, CA", "Vancouver, Canada", "Chicago, IL", "Ashburn, VA", "Toronto, Canada", "Montreal, Canada", "Latency"];
const regionLabel = (r: string) => REGION_NAMES[r] ?? (r || "Telnyx default");
const siteLabel = (s: string | null | undefined) => (!s ? "Not set (Telnyx picks)" : s === "Latency" ? "Automatic (Telnyx picks)" : s);
const comboLabel = (r: { region: string; site: string | null }) => `${regionLabel(r.region)}${r.site ? ` · ${MEDIA_SITE_PLACES[r.site]?.name ?? siteLabel(r.site)}` : ""}`;

/**
 * The speed test. Regions first (with the media site as it is), then, for admins who ticked it, every media site
 * through the fastest region; the winner is applied (region on this device, site for the team) and remembered.
 */
async function runSpeedTest(opts: { sites: boolean; quiet: boolean }) {
  if (speed.run) return;
  if (voice.isBusy()) {
    if (!opts.quiet) toast("Finish your call first");
    return;
  }
  const route = await api.diagRoute().catch(() => null);
  if (route) speed.route = route;
  const startSite = route?.loginsSite ?? null;
  const far = (speed.you?.lon ?? -100) > -30; // in Europe: Europe is worth testing too
  const regions = ["", ...AUTO_REGIONS.filter((r) => r !== "eu" || far)];
  const sites = opts.sites && isAdmin() && !route?.ownSite ? SITE_TEST_ORDER.filter((x) => x !== startSite) : [];
  const run = { results: [] as SpeedResult[], now: "", total: regions.length + sites.length, quiet: opts.quiet };
  speed.run = run;
  render();
  let siteNow = startSite;
  let aborted: string | null = null;
  try {
    for (const r of regions) {
      run.now = `Calling region ${regionLabel(r)}`;
      render();
      const res = await voice.testCall(r, startSite);
      if (res.error && /call/i.test(res.error) && voice.isBusy()) {
        aborted = "Stopped: a call started";
        break;
      }
      run.results.push(res);
      render();
    }
    const fastest = !aborted ? pickBest(run.results) : null;
    if (fastest && sites.length) {
      for (const site of sites) {
        run.now = `Media site ${siteLabel(site)}`;
        render();
        try {
          await api.setMediaSite(site);
          siteNow = site;
        } catch (e) {
          run.results.push({ region: fastest.region, site, ms: null, spread: null, samples: 0, ip: null, relay: null, error: (e as Error).message });
          continue;
        }
        await new Promise((r) => setTimeout(r, 1500)); // let Telnyx apply it
        const res = await voice.testCall(fastest.region, site);
        if (res.error && voice.isBusy()) {
          aborted = "Stopped: a call started";
          break;
        }
        run.results.push(res);
        render();
      }
    }
  } finally {
    const cur = { region: effectiveRegion(getDevicePrefs()), site: startSite };
    const best = !aborted ? pickBest(run.results, cur) : null;
    // The team's media site: the fastest measured, or back to how it was.
    const wantSite = best?.site ?? startSite ?? "Latency";
    if (siteNow !== wantSite && (siteNow !== startSite || wantSite !== "Latency")) await api.setMediaSite(wantSite).catch((e) => toast((e as Error).message));
    const before = lastSpeedRun()?.best ?? null;
    if (best && best.ms != null) {
      saveSpeedRun({ at: Date.now(), results: run.results, best: { region: best.region, site: best.site, ms: best.ms } });
      if (!opts.quiet) setDevicePrefs({ regionAuto: true }); // ran on purpose: use what it found
    }
    speed.run = null;
    voice.endSpeedTest();
    void loadSpeed();
    if (aborted) toast(aborted);
    else if (!best) {
      // Say what actually happened (the phone system's own reason), not a guess.
      const why = run.results.find((r) => r.error)?.error;
      if (!opts.quiet) toast(`Couldn't measure: no test call connected${why ? ` (${why})` : ""}.`);
    }
    else if (!opts.quiet || !before || before.region !== best.region || before.site !== best.site) {
      toast(`Calls now connect through ${comboLabel(best)} (${best.ms} ms, the fastest measured)`);
    }
    render();
  }
}

/** Measures again by itself (quietly) when the measurement is old or calls got clearly slower than the best. */
let speedWatchStarted = false;
/** Why a new speed test would help (calls got slower than the best measured), shown as a suggestion on Home. */
let speedSuggest: string | null = null;
function speedSuggestBanner() {
  if (!speedSuggest || speed.run) return null;
  return h("div", { class: "notice slim speed-banner" },
    icon("chart", "sm"),
    h("span", { class: "grow" }, "Calls are slower than your best connection. Test again to pick the fastest?"),
    h("button", { class: "text-btn", type: "button", onclick: () => {
      speedSuggest = null;
      go({ name: "speed" });
      void runSpeedTest({ sites: false, quiet: false });
    } }, "Test"),
    h("button", { class: "icon-btn sm", type: "button", "aria-label": "Not now", onclick: () => ((speedSuggest = null), rerenderAny()) }, icon("close", "sm")));
}

function watchCallSpeed() {
  if (speedWatchStarted) return;
  speedWatchStarted = true;
  let lastBusy = Date.now();
  const LAST_AUTO = "joy.speed.autoAt";
  const tick = () => {
    if (voice.isBusy()) lastBusy = Date.now();
    if (!state.user?.canCall || speed.run || !voice.ready || document.visibilityState !== "visible") return;
    if (!getDevicePrefs().regionAuto || Date.now() - lastBusy < 120_000) return;
    let last = 0;
    try {
      last = Number(localStorage.getItem(LAST_AUTO) ?? 0);
    } catch {
      // ok
    }
    if (Date.now() - last < 6 * 3600_000) return;
    const why = retestReason();
    if (!why) return;
    try {
      localStorage.setItem(LAST_AUTO, String(Date.now()));
    } catch {
      // ok
    }
    // Never test by itself (test calls you didn't ask for): suggest it, and you decide.
    speedSuggest = why;
    rerenderAny();
  };
  setInterval(tick, 30_000);
}

function speedView() {
  const rt = speed.route;
  const dp = getDevicePrefs();
  const regionKey = voice.region || "";
  const regionPlace = REGION_PLACES[regionKey] ?? null;
  const loginsPlace = rt?.loginsSite ? MEDIA_SITE_PLACES[rt.loginsSite] ?? null : null;
  const appPlace = rt?.appSite ? MEDIA_SITE_PLACES[rt.appSite] ?? null : null;
  const serverPlace = rt?.serverRegion ? FLY_PLACES[rt.serverRegion] ?? null : null;
  const path = lastMediaPath();
  const telnyxPath = !!path && isTelnyxMediaIp(path.ip);
  const you = speed.you;
  // Where the last call's audio went: a Telnyx address is at the media site set (IP lookups put them all in Chicago).
  const media: Place | null = telnyxPath ? loginsPlace : speed.media;
  const lastRun = lastSpeedRun();
  const best = lastRun?.best ?? null;
  const run = speed.run;
  const kv = (k: string, v: string, cls = "v") => h("div", { class: "kv" }, h("span", { class: "k wide" }, k), h("span", { class: cls }, v));
  const tone = (ms: number | null) => (ms == null ? "v bad" : ms <= 60 ? "v good-text" : ms <= 150 ? "v warn-text" : "v bad");

  // ---- the fastest connection
  const results = (run?.results ?? lastRun?.results ?? []).slice().sort((a, b) => speedScore(a) - speedScore(b));
  const bestCard = h("div", { class: "group", style: "margin-top:12px" },
    h("div", { class: "item" },
      h("div", { class: "grow" },
        h("div", { style: "font-weight:600" }, run ? "Measuring…" : best ? `Fastest measured: ${best.ms} ms` : "Find the fastest connection"),
        h("div", { class: "sub" }, run
          ? `${run.now} (${run.results.length + 1} of ${run.total}). Short silent test calls; nobody rings. A real call stops the test.`
          : best
            ? `${comboLabel(best)} · measured ${listTime(new Date(lastRun!.at).toISOString().replace("T", " ").slice(0, 19))}. ${dp.regionAuto ? "Calls connect through it automatically. If calls get clearly slower than this, the app measures again by itself." : `You picked ${regionLabel(dp.region)} by hand, so calls don't use it.`}`
            : "Places a short silent test call through each calling region and measures the real delay of call audio. The fastest is used for your calls, even if it's not the closest.")),
      run ? h("span", { class: "spinner" }) : h("button", { class: "btn sm", onclick: () => {
        void measureDevice(true);
        void runSpeedTest({ sites: speed.testSites, quiet: false });
      } }, best ? "Measure again" : "Measure")),
    isAdmin() && !run && !speed.route?.ownSite
      ? switchRow("speed-sites", "Also try every media site", "Tests each Telnyx site and keeps the fastest. Changes the site for the whole team for about a minute while it runs.", speed.testSites, (v) => ((speed.testSites = v), render()))
      : null,
    !dp.regionAuto && best && !run
      ? h("div", { class: "item" }, h("div", { class: "grow sub" }, "Use the fastest measured region on this device"),
          h("button", { class: "btn quiet sm", onclick: () => (setDevicePrefs({ regionAuto: true }), render()) }, "Use it"))
      : null);
  const resultsCard = results.length
    ? h("div", { class: "group" }, ...results.map((r, i) =>
        h("div", { class: "kv" },
          h("span", { class: "k wide" }, comboLabel(r), i === 0 && r.ms != null ? h("span", { class: "speed-best" }, "fastest") : null,
            r.relay ? h("span", { class: "sub" }, ` · relayed (${r.relay.toUpperCase()})`) : null),
          h("span", { class: tone(r.ms == null ? null : r.ms + onwardMs(r.site)) }, r.ms == null ? (r.error ?? "no answer") : `${r.ms} ms${r.spread ? ` ±${Math.round(r.spread / 2)}` : ""}${onwardMs(r.site) ? ` + ~${onwardMs(r.site)} ms to US customers` : ""}`))))
    : null;

  // ---- what adds delay on this device (all measured)
  const dev = speed.device;
  const netMs = path?.rttMs ?? best?.ms ?? null;
  const bufMs = path?.bufferMs ?? null;
  const parts: [string, number | null, string][] = [
    ["Network round trip to Telnyx", netMs, path?.rttMs != null ? "last call" : best ? "speed test" : "make a call or measure"],
    ["Smoothing buffer for incoming audio", bufMs, bufMs != null ? "last call" : "make a call to see"],
    [`Speaker / headset${dev?.outLabel ? ` (${dev.outLabel.replace(/^Default - /, "")})` : ""}`, dev?.outMs ?? null, dev?.outMs != null ? "measured" : "this browser can't tell"],
    ["Microphone", dev?.inMs ?? null, dev?.inMs != null ? "measured" : "measured when you tap Measure (computer)"],
  ];
  // What the other person waits for your voice: mic + half the round trip + their side's buffer (~same) + 20 ms packets.
  const oneWay = netMs != null ? Math.round((dev?.inMs ?? 0) + netMs / 2 + (bufMs ?? 40) + 20 + (dev?.outMs ?? 0)) : null;
  const deviceCard = h("div", { class: "group" },
    ...parts.map(([k, ms, how]) => h("div", { class: "kv" }, h("span", { class: "k wide" }, k, h("span", { class: "sub" }, ` · ${how}`)), h("span", { class: "v", style: "margin-left:auto;text-align:right;white-space:nowrap" }, ms == null ? "—" : `${ms} ms`))),
    oneWay != null ? h("div", { class: "kv" }, h("span", { class: "k wide", style: "font-weight:600" }, "Your voice reaches them in about"), h("span", { class: tone(oneWay > 200 ? 999 : oneWay > 150 ? 100 : 10) }, `${oneWay} ms`)) : null,
    dev?.bluetooth ? h("div", { class: "item" }, h("span", { class: "speed-dot bad" }, "!"), h("div", { class: "grow sub" }, "Bluetooth headset: these add 100–250 ms each way, more than anything else on this list, and switch to low-quality sound when the mic is on. A wired or USB headset (or the computer's own mic and speaker) is much faster.")) : null);

  // ---- findings
  type Finding = { ok: boolean | null; title: string; detail: string };
  const findings: Finding[] = [];
  if (rt?.ownSite) {
    findings.push({ ok: null, title: `Your calls use your own media site: ${siteLabel(rt.loginsSite)}`, detail: `An admin set it for you (Team page). Your audio reaches Telnyx there; the customer's side stays at the team's site (${siteLabel(rt.appSite)}), and Telnyx carries the audio between them on its own network.` });
  } else if (rt) {
    const same = (rt.loginsSite ?? "") === (rt.appSite ?? "");
    findings.push(same
      ? { ok: true, title: "Both media site settings match", detail: `Your app's side and the customer's side meet Telnyx at the same place (${siteLabel(rt.loginsSite)}), so there's no hop between two sites.` }
      : { ok: false, title: "The two media site settings don't match", detail: `Your app's audio meets Telnyx in ${siteLabel(rt.loginsSite)}, but the customer's side is handled in ${siteLabel(rt.appSite)}. Audio travels between the two on every call. ${isAdmin() ? "Pick the media site again below (it sets both), or measure with “also try every media site”." : "An admin can fix it here."}` });
    if (rt.loginsSite === "Latency") findings.push({ ok: null, title: "Media site: Telnyx picks", detail: "Telnyx picks by pinging your network, which most home and office routers don't answer, so its pick can be far off. Measuring every media site finds the real fastest." });
  }
  if (path) {
    if (path.relay === "tcp" || path.relay === "tls") findings.push({ ok: false, title: "Audio is forced through a relay (TCP)", detail: "The network on this device blocks direct call traffic (often a VPN, firewall or office Wi-Fi). That alone adds a lot of delay. Try without VPN or on another network." });
    else if (path.relay === "udp") findings.push({ ok: null, title: "Audio goes through a relay (UDP)", detail: "Slightly slower than direct. Usually caused by the router or a VPN." });
    else findings.push({ ok: true, title: "Audio goes direct (no relay)", detail: "Nothing on this network is detouring the call audio." });
    if (path.rttMs != null && best) {
      findings.push(slowerThanBest(best.ms, path.rttMs)
        ? { ok: false, title: `Last call: ${path.rttMs} ms, slower than the ${best.ms} ms measured`, detail: "The same path measured faster, so the extra is this network at that moment (busy Wi-Fi, VPN, uploads). If it keeps happening, the app measures again by itself and switches if something else is faster." }
        : { ok: true, title: `Last call: ${path.rttMs} ms`, detail: `In line with the fastest measured (${best.ms} ms).` });
    } else if (path.rttMs != null && media && you) {
      const expected = minRttMs(km(you, media)) + 25;
      findings.push(path.rttMs <= Math.max(80, expected * 2)
        ? { ok: true, title: `Last call delay: ${path.rttMs} ms`, detail: "In line with the distance." }
        : { ok: false, title: `Last call delay: ${path.rttMs} ms`, detail: `More than the distance explains (about ${expected} ms). The rest is the local network: Wi-Fi, VPN, or the computer's network card. Compare on the phone's cellular data.` });
    }
  } else {
    findings.push({ ok: null, title: "No call measured on this device yet", detail: "Tap Measure, or make one call (10 seconds is enough), then come back." });
  }
  if (serverPlace) findings.push({ ok: null, title: `App server: ${serverPlace.name}`, detail: "It only sets calls up (ringing, answering). Call audio never passes through it, so it doesn't add delay during a call." });

  // ---- map
  const pins: MapPin[] = [];
  const lines: MapLine[] = [];
  for (const [k, p] of Object.entries(MEDIA_SITE_PLACES)) {
    const used = k === rt?.loginsSite || k === rt?.appSite;
    if (!used && p.lon > -30) continue; // keep the map on North America unless used
    pins.push({ ...p, kind: used ? "site" : "other", label: used ? `Media site: ${p.name}` : p.name });
  }
  if (regionPlace) pins.push({ ...regionPlace, lat: regionPlace.lat + 0.6, kind: "region", label: `Calling region: ${regionPlace.name}` });
  if (serverPlace) pins.push({ ...serverPlace, lat: serverPlace.lat - 0.6, kind: "server", label: `App server: ${serverPlace.name}` });
  if (media && !telnyxPath) pins.push({ ...media, kind: "media", label: `Last call's audio: ${media.name}${path?.rttMs != null ? ` · ${path.rttMs} ms` : ""}` });
  if (you) pins.push({ ...you, kind: "you", label: `You: ${you.name}` });
  if (you && media) lines.push({ from: you, to: media, kind: "app", label: `Your call audio${path?.rttMs != null ? ` (last call ${path.rttMs} ms)` : ""}` });
  if (loginsPlace && appPlace && rt?.loginsSite !== rt?.appSite) lines.push({ from: loginsPlace, to: appPlace, kind: "customer", label: "Extra trip between the two media sites" });
  if (you && serverPlace) lines.push({ from: you, to: serverPlace, kind: "setup", label: "Call setup only (no audio)" });
  const key = JSON.stringify([pins, lines]);
  speedMapEl ??= h("div", { class: "speed-map", id: "speed-map" });
  if (key !== speedMapKey && pins.length) {
    speedMapKey = key;
    requestAnimationFrame(() => void drawMap(speedMapEl!, pins, lines).then((ok) => {
      if (!ok) speedMapEl!.replaceChildren(h("div", { class: "sub", style: "padding:16px" }, "The map couldn't load. The checks below still apply."));
    }));
  }

  return settingsPage("Call speed", "Measured on this device",
    bestCard,
    resultsCard ? h("div", { class: "group-title" }, run ? "So far" : "Last measurement (round trip of call audio)") : null,
    resultsCard,
    h("div", { class: "group-title" }, "What adds delay on this device"),
    deviceCard,
    h("div", { class: "group-title" }, "Where your call audio goes"),
    h("div", { class: "group", style: "overflow:hidden" }, speedMapEl,
      h("div", { class: "speed-legend" },
        h("span", {}, h("i", { style: "background:#4fc0dc" }), "You"),
        h("span", {}, h("i", { style: "background:#3fc48c" }), "Call audio"),
        h("span", {}, h("i", { style: "background:#f2c46d" }), "Media site"),
        h("span", {}, h("i", { style: "background:#a78bfa" }), "Calling region"),
        h("span", {}, h("i", { style: "background:#9aa0ad" }), "App server (setup only)"))),
    speed.loading ? h("div", { class: "pending", style: "padding:16px;justify-content:center" }, h("span", { class: "spinner" }), "Checking…") : null,
    speed.error ? h("div", { class: "group" }, h("div", { class: "item" }, h("div", { class: "sub bad" }, speed.error))) : null,
    h("div", { class: "group-title" }, "What we found"),
    h("div", { class: "group" }, ...findings.map((f) =>
      h("div", { class: "item" },
        h("span", { class: `speed-dot ${f.ok === true ? "ok" : f.ok === false ? "bad" : "info"}` }, f.ok === true ? "✓" : f.ok === false ? "!" : "i"),
        h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, f.title), h("div", { class: "sub" }, f.detail))))),
    h("div", { class: "group-title" }, "Settings"),
    h("div", { class: "group" },
      regionSettings(),
      isAdmin() ? mediaSiteSettings() : null,
      h("div", { class: "item stack" },
        h("div", { style: "font-weight:600" }, "Audio format for calls"),
        h("div", { class: "segmented wrap", role: "group" },
          ...([["auto", "HD voice"], ["pcmu", "Phone-line (G.711)"]] as const).map(([v, l]) =>
            h("button", { "aria-pressed": String(v === "pcmu" ? dp.codec === "pcmu" : dp.codec !== "pcmu"), onclick: () => (setDevicePrefs({ codec: v }), render()) }, l))),
        h("div", { class: "sub" }, "HD voice sends your voice in full quality (Opus or G.722, whichever the call agrees on). With HD Voice on for the office number at Telnyx, calls with cell phones on AT&T, T-Mobile and Verizon (4G/5G) can be HD end to end; Telnyx doesn't promise it on every call, and others step down to phone-line quality by themselves. Phone-line (G.711) is the old narrowband format.")),
      isAdmin() ? hdVoiceStatus() : null,
      h("div", { class: "item stack" },
        h("div", { style: "font-weight:600" }, "How fast calls ring and connect"),
        h("div", { class: "row-actions" },
          h("button", { class: "btn quiet sm", disabled: state.connTest === "running", onclick: () => void runConnectionTest() }, state.connTest === "running" ? "Testing…" : "Run connection test")),
        state.connTest && state.connTest !== "running" ? connectionReport(state.connTest) : null)),
    h("div", { class: "group-title" }, "Details"),
    h("div", { class: "group" },
      kv("You are near", you ? you.name || you.ip : "Unknown"),
      kv("Calling region (this device)", regionPlace ? regionPlace.name : regionLabel(regionKey)),
      kv("Media site: your app's side", siteLabel(rt?.loginsSite)),
      kv("Media site: customer's side", siteLabel(rt?.appSite)),
      kv("App server", serverPlace ? serverPlace.name : rt?.serverRegion ?? "Unknown"),
      path ? kv("Last call on this device", `${path.rttMs ?? "?"} ms · ${path.relay ? `relayed (${path.relay.toUpperCase()})` : "direct"} · Telnyx ${path.ip} · ${listTime(new Date(path.at).toISOString().replace("T", " ").slice(0, 19))}`) : null,
      ...(rt?.errors ?? []).map((e) => kv("Couldn't read", e, "v bad"))),
    h("button", { class: "btn quiet block", style: "margin-top:14px", onclick: () => ((speedMapKey = ""), void loadSpeed()) }, "Refresh"));
}

// ---------------------------------------------------------------- RCS texts (admins)

let rcsState: null | "loading" | { rcs: { enabled: boolean; agentId: string; messagingProfileId: string }; envProfile: boolean } | { error: string } = null;

function rcsView() {
  if (rcsState === null) {
    rcsState = "loading";
    void api.rcsSettings().then((r) => (rcsState = r), (e) => (rcsState = { error: (e as Error).message })).finally(render);
  }
  if (rcsState === "loading") return settingsPage("RCS texts", "", h("div", { class: "pending", style: "padding:40px;justify-content:center" }, h("span", { class: "spinner" }), "Loading"));
  if ("error" in rcsState) return settingsPage("RCS texts", "", h("div", { class: "group" }, h("div", { class: "item" }, h("div", { class: "sub bad" }, rcsState.error))));
  const cur = rcsState;
  const agent = h("input", { id: "rcs-agent", class: "field", placeholder: "e.g. joytravel_agent", value: cur.rcs.agentId, autocomplete: "off", autocapitalize: "none" }) as HTMLInputElement;
  const profile = h("input", { id: "rcs-profile", class: "field", placeholder: cur.envProfile ? "Optional: uses the one already set in Fly" : "Messaging profile id", value: cur.rcs.messagingProfileId, autocomplete: "off", autocapitalize: "none" }) as HTMLInputElement;
  const save = async (enabled: boolean) => {
    try {
      rcsState = await api.saveRcsSettings({ enabled, agentId: agent.value, messagingProfileId: profile.value });
      toast(enabled ? "RCS is on: texts go by RCS where the customer's phone supports it" : "Saved. RCS is off: all texts go as normal texts");
    } catch (e) {
      toast((e as Error).message);
    }
    render();
  };
  return settingsPage("RCS texts", "Rich texts with read receipts",
    h("div", { class: "group", style: "margin-top:12px" },
      h("div", { class: "item" },
        h("span", { class: `speed-dot ${cur.rcs.enabled ? "ok" : "info"}` }, cur.rcs.enabled ? "✓" : "i"),
        h("div", { class: "grow" },
          h("div", { style: "font-weight:600" }, cur.rcs.enabled ? "RCS is on" : "RCS is off"),
          h("div", { class: "sub" }, cur.rcs.enabled
            ? "Texts go by RCS where the customer's phone supports it, and show “Read” when they open them. Other phones get a normal text from the team number. Texts with pictures still go as picture texts."
            : "All texts go as normal texts (no read receipts). Turn this on once Telnyx has approved your RCS agent.")),
        h("button", { class: cur.rcs.enabled ? "btn quiet sm" : "btn sm", onclick: () => void save(!cur.rcs.enabled) }, cur.rcs.enabled ? "Turn off" : "Turn on"))),
    h("div", { class: "group-title" }, "RCS agent (from the Telnyx portal → Messaging → RCS)"),
    h("div", { class: "group" },
      h("div", { class: "item stack" }, h("label", { class: "mini-label", for: "rcs-agent" }, "RCS agent id"), agent),
      h("div", { class: "item stack" }, h("label", { class: "mini-label", for: "rcs-profile" }, "Messaging profile id"), profile,
        h("div", { class: "sub" }, cur.envProfile ? "Leave empty to use the messaging profile already set for Joy Phone." : "The messaging profile your RCS agent is attached to.")),
      h("div", { class: "item" }, h("div", { class: "grow" }), h("button", { class: "btn quiet sm", onclick: () => void save(cur.rcs.enabled) }, "Save"))),
    h("div", { class: "group-title" }, "Good to know"),
    h("div", { class: "group" }, h("div", { class: "item" }, h("div", { class: "sub" },
      "RCS texts come from your verified business name and logo, not the team number, and customers' replies come back here as usual. “Read” only shows when the customer's phone sends read receipts (some people turn them off). If Telnyx refuses an RCS text (for example before the agent is approved), it goes as a normal text automatically."))));
}

// ---------------------------------------------------------------- billing (admins)

function billingMonthKey(which: "this" | "last"): string {
  const d = new Date();
  const ym = which === "this" ? [d.getUTCFullYear(), d.getUTCMonth()] : d.getUTCMonth() === 0 ? [d.getUTCFullYear() - 1, 11] : [d.getUTCFullYear(), d.getUTCMonth() - 1];
  return `${ym[0]}-${String(ym[1] + 1).padStart(2, "0")}`;
}
let billingLoading = false;
async function loadBilling(refresh = false) {
  const month = billingMonthKey(state.billingMonth);
  if (state.billing?.month !== month) state.billing = null; // another month: don't show the wrong one meanwhile
  state.billingError = null;
  billingLoading = true;
  render();
  try {
    state.billing = await api.billing(month, refresh);
  } catch (e) {
    state.billingError = (e as Error).message;
  }
  billingLoading = false;
  render();
}
/** Always cents ($0.37, $12.04); a charge under a cent shows as "< $0.01" rather than $0.00. */
const money = (n: number | string, cur = "USD") => {
  const v = typeof n === "string" ? Number(n) : n;
  if (!Number.isFinite(v)) return String(n);
  const fmt = (x: number) => x.toLocaleString(undefined, { style: "currency", currency: cur, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v > 0 && v < 0.005 ? `< ${fmt(0.01)}` : fmt(v);
};
function billingView() {
  const b = state.billing;
  const cur = b?.balance?.currency ?? "USD";
  const max = b ? Math.max(0.0001, ...b.products.map((p) => p.cost)) : 1;
  const nice = (label: string) => (label === "inbound" ? "Incoming" : label === "outbound" ? "Outgoing" : label.charAt(0).toUpperCase() + label.slice(1));
  const monthName = (key: string) => fmtDate(new Date(`${key}-15T12:00:00Z`), { month: "long", year: "numeric" });
  const tile = (label: string, value: string) => h("div", { class: "tile" }, h("div", { class: "tile-value" }, value), h("div", { class: "tile-label" }, label));
  return screen(
    h("header", { class: "thread-head" }, backButton({ name: "settings" }),
      h("div", { class: "who" }, h("div", { class: "title" }, "Billing"), h("div", { class: "number" }, "From Telnyx")),
      h("div", { class: "segmented", role: "group", "aria-label": "Month" },
        h("button", { "aria-pressed": String(state.billingMonth === "this"), onclick: () => ((state.billingMonth = "this"), void loadBilling()) }, "This month"),
        h("button", { "aria-pressed": String(state.billingMonth === "last"), onclick: () => ((state.billingMonth = "last"), void loadBilling()) }, "Last month"))),
    h("div", { class: "page" }, h("div", { class: "page-inner" },
      state.billingError
        ? h("div", { class: "group", style: "margin-top:12px" }, h("div", { class: "item" }, h("div", { class: "sub bad" }, state.billingError)))
        : !b
          ? h("div", { class: "pending", style: "padding:40px;justify-content:center" }, h("span", { class: "spinner" }), "Asking Telnyx")
          : h("div", {},
              h("div", { class: "tiles", style: "margin-top:12px" },
                tile(`Spent in ${monthName(b.month)}`, money(b.total, cur)),
                b.balance ? tile("Balance", money(b.balance.balance, cur)) : null,
                // Same as the balance unless there's a credit line: only shown when it adds something.
                b.balance && b.balance.available_credit !== b.balance.balance ? tile("Available to spend", money(b.balance.available_credit, cur)) : null),
              b.ai ? aiBillingSection(b.ai, cur) : null,
              h("div", { class: "group-title" }, b.ai?.products.length ? "Everything else" : "By product"),
              h("div", { class: "group" },
                ...(b.products.length
                  ? b.products.map((p) =>
                      h("div", { class: "item stack bill-row" },
                        h("div", { class: "bill-top" }, h("span", { class: "grow", style: "font-weight:600" }, p.label), h("span", { class: "bill-cost" }, money(p.cost, cur))),
                        h("div", { class: "bill-bar" }, h("span", { style: `width:${Math.max(2, Math.round((p.cost / max) * 100))}%` })),
                        p.parts.length > 1 ? h("div", { class: "sub" }, p.parts.map((x) => `${nice(x.label)} ${money(x.cost, cur)}`).join(" · ")) : null))
                  : [h("div", { class: "item" }, h("div", { class: "sub" }, "Nothing charged yet this month."))])),
              b.failed.length ? h("p", { class: "sub warn-text", style: "margin:8px 6px" }, `Telnyx didn't answer for: ${b.failed.join(", ")}. Showing the last numbers it gave for those; tap Refresh in a minute.`) : null,
              h("p", { class: "sub", style: "margin:10px 6px" },
                `Usage charges from Telnyx for ${monthName(b.month)} (days in UTC), as of ${b.updatedAt ? new Date(b.updatedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "now"}. Telnyx can take a little while to post each call or text, so today's numbers may still go up. Monthly phone number fees and taxes are on the invoices.`),
              b.invoices.length
                ? h("div", {},
                    h("div", { class: "group-title" }, "Invoices"),
                    h("div", { class: "group" }, ...b.invoices.map((inv) =>
                      h("div", { class: "item" },
                        h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, `${inv.period_start} to ${inv.period_end}`), h("div", { class: inv.paid ? "sub good-text" : "sub warn-text" }, inv.paid ? "Paid" : "Not paid yet")),
                        inv.url ? h("a", { class: "btn quiet sm", href: inv.url, target: "_blank", rel: "noopener" }, "Open") : null))))
                : null,
              h("button", { class: "btn quiet block", style: "margin-top:14px", disabled: billingLoading, onclick: () => void loadBilling(true) },
                billingLoading ? h("span", { class: "spinner" }) : null, billingLoading ? "Checking with Telnyx" : "Refresh")))));
}

/** Billing → AI usage: each AI charge broken down (model, voice, assistant), plus what Joy did with AI. */
function aiBillingSection(ai: NonNullable<Billing["ai"]>, cur: string) {
  // "deepseek-ai/DeepSeek-V4-Flash" → "DeepSeek-V4-Flash"; "Telnyx.Ultra.<id>" → "Ultra voices"; "azure.en-US-AvaMultilingualNeural" → "Azure · AvaMultilingualNeural".
  const clean = (label: string) => {
    if (/AI caller/i.test(label)) return `${aiName()}'s calls to businesses`;
    if (/team line/i.test(label)) return `Teammates calling ${aiName()}`;
    if (/^telnyx\.ultra\./i.test(label)) return "Ultra voices";
    if (/^telnyx\.kokorotts\./i.test(label)) return "Kokoro voices";
    if (/^telnyx\.natural/i.test(label)) return "Natural voices";
    const az = /^azure\.[a-z]{2}-[A-Z]{2}-(.+)$/i.exec(label);
    if (az) return `Azure · ${az[1].replace(/(Multilingual)?Neural$/, "")}`;
    return label.includes("/") ? label.split("/").pop()! : label.charAt(0).toUpperCase() + label.slice(1);
  };
  const merged = (parts: { label: string; cost: number }[]) => {
    const m = new Map<string, number>();
    for (const p of parts) m.set(clean(p.label), (m.get(clean(p.label)) ?? 0) + p.cost);
    return [...m].sort((a, b) => b[1] - a[1]);
  };
  const a = ai.activity;
  const did = [
    a.aiCalls ? `${aiName()} made ${a.aiCalls} call${a.aiCalls === 1 ? "" : "s"} (${a.aiMinutes} min talking)` : "",
    a.chatReplies ? `answered ${a.chatReplies} chat message${a.chatReplies === 1 ? "" : "s"}` : "",
    a.summaries ? `${a.summaries} call summar${a.summaries === 1 ? "y" : "ies"}` : "",
    a.transcribedMinutes ? `${a.transcribedMinutes} min of calls transcribed` : "",
  ].filter(Boolean);
  return h("div", { class: "ai-bill" },
    h("div", { class: "group-title ai-bill-title" }, h("span", { class: "ai-av xs" }, icon("sparkle", "sm")), "AI usage", h("span", { class: "bill-cost" }, money(ai.total, cur))),
    h("div", { class: "group" },
      ...(ai.products.length
        ? ai.products.map((p) => h("div", { class: "item stack bill-row" },
            h("div", { class: "bill-top" }, h("span", { class: "grow", style: "font-weight:600" }, p.label), h("span", { class: "bill-cost" }, money(p.cost, cur))),
            p.parts.length
              ? h("div", { class: "ai-bill-parts" }, ...merged(p.parts).map(([label, cost]) =>
                  h("div", { class: "ai-bill-part" }, h("span", { class: "grow" }, label), h("span", {}, money(cost, cur)))))
              : null))
        : [h("div", { class: "item" }, h("div", { class: "sub" }, "No AI charges yet this month."))]),
      did.length ? h("div", { class: "item" }, h("div", { class: "sub" }, `This month in Joy: ${did.join(" · ")}.`)) : null));
}

// ---------------------------------------------------------------- analytics

async function loadAnalytics() {
  try {
    state.analytics = await api.analytics(state.analyticsDays);
    render();
  } catch (e) {
    toast((e as Error).message);
  }
}

function analyticsView() {
  const a = state.analytics;
  const tile = (label: string, value: string, sub = "") =>
    h("div", { class: "tile" }, h("div", { class: "tile-value" }, value), h("div", { class: "tile-label" }, label), sub ? h("div", { class: "tile-sub" }, sub) : null);
  const max = a ? Math.max(1, ...a.byHour) : 1;
  return screen(
    h("header", { class: "thread-head" }, backButton({ name: "settings" }),
      h("div", { class: "who" }, h("div", { class: "title" }, "Analytics"), h("div", { class: "number" }, a ? `Since ${new Date(a.since.replace(" ", "T") + (a.since.includes("Z") ? "" : "Z")).toLocaleDateString()}` : "")),
      h("div", { class: "segmented", role: "group" },
        h("button", { "aria-pressed": String(state.analyticsDays === 7), onclick: () => ((state.analyticsDays = 7), void loadAnalytics()) }, "7 days"),
        h("button", { "aria-pressed": String(state.analyticsDays === 30), onclick: () => ((state.analyticsDays = 30), void loadAnalytics()) }, "30 days"))),
    h("div", { class: "page" }, h("div", { class: "page-inner wide" },
      !a ? h("div", { class: "pending", style: "justify-content:center;padding:40px" }, h("span", { class: "spinner" }), "Loading") : h("div", {},
        h("div", { class: "tiles" },
          tile("Incoming calls", String(a.inbound), `${a.answered} answered`),
          tile("Answer rate", a.answerRate == null ? "—" : `${Math.round(a.answerRate * 100)}%`, `${a.missed} missed · ${a.voicemail} voicemail`),
          tile("Outgoing calls", String(a.outbound)),
          tile("Texts", String(a.textsIn + a.textsOut), `${a.textsIn} in · ${a.textsOut} out`),
          tile("Median reply time", a.medianResponseMins == null ? "—" : a.medianResponseMins < 60 ? `${Math.round(a.medianResponseMins)} min` : `${(a.medianResponseMins / 60).toFixed(1)} h`, "to texts & missed calls")),
        h("div", { class: "group-title" }, "Incoming calls by hour"),
        h("div", { class: "group chart" },
          h("div", { class: "bars" }, ...a.byHour.map((n, hr) => h("div", { class: "bar-col", title: `${hr}:00 · ${n} call${n === 1 ? "" : "s"}` },
            h("div", { class: "bar", style: `height:${Math.round((n / max) * 100)}%` }),
            h("span", { class: "bar-x" }, hr % 3 === 0 ? (hr === 0 ? "12a" : hr < 12 ? `${hr}a` : hr === 12 ? "12p" : `${hr - 12}p`) : ""))))),
        h("div", { class: "group-title" }, "By teammate"),
        h("div", { class: "group table-wrap" },
          h("table", { class: "table" },
            h("thead", {}, h("tr", {}, h("th", {}, "Teammate"), h("th", {}, "Answered"), h("th", {}, "Calls made"), h("th", {}, "Texts sent"), h("th", {}, "Talk time"))),
            h("tbody", {}, ...a.byAgent.map((r) => h("tr", {},
              h("td", {}, h("span", { class: "cell-user" }, avatar(String(r.userId) + r.name, r.name, "xs"), r.name)),
              h("td", {}, String(r.answered)), h("td", {}, String(r.outbound)), h("td", {}, String(r.textsSent)),
              h("td", {}, r.talkSecs >= 3600 ? `${(r.talkSecs / 3600).toFixed(1)} h` : `${Math.round(r.talkSecs / 60)} min`))))))))));
}

// ---------------------------------------------------------------- search

let searchSeq = 0;
async function runSearch(q: string) {
  const seq = ++searchSeq;
  if (q.trim().length < 2) {
    state.searchHits = null;
    state.searching = false; // 1.10.28: a search still loading for the longer text is dropped: its spinner too
    return render();
  }
  state.searching = true;
  render();
  try {
    const hits = await api.search(q.trim());
    if (seq !== searchSeq) return;
    state.searchHits = hits;
  } catch (e) {
    toast((e as Error).message);
  }
  state.searching = false;
  render();
}

const HIT_LABEL: Record<SearchHit["kind"], string> = {
  contact: "Contacts", text: "Texts", comment: "Internal comments", transcript: "Call transcripts", summary: "Call summaries", note: "Contact notes",
  chat: "Team chat", agent: "Your chat with the AI assistant", internal: "Calls inside the team",
};

function searchView() {
  let timer: number | undefined;
  const input = h("input", { id: "global-search", class: "field", type: "search", name: "joy-search", placeholder: "Search texts, notes, chats, transcripts, contacts", value: state.searchQ, autocomplete: "off", "data-1p-ignore": "true", "data-lpignore": "true",
    oninput: (e: Event) => {
      state.searchQ = (e.target as HTMLInputElement).value;
      clearTimeout(timer);
      timer = window.setTimeout(() => void runSearch(state.searchQ), 250);
    } });
  // Focus the search box that's showing: the sidebar one on wide screens, this page's own otherwise.
  if (!state.searchQ) requestAnimationFrame(() => {
    const side = document.getElementById("rail-search");
    (side && side.offsetParent ? side : document.getElementById("global-search"))?.focus();
  });
  const groups = new Map<string, SearchHit[]>();
  for (const hit of state.searchHits ?? []) groups.set(hit.kind, [...(groups.get(hit.kind) ?? []), hit]);
  const highlight = (text: string) => {
    const q = state.searchQ.trim();
    const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
    return i < 0 ? [text] : [text.slice(0, i), h("mark", {}, text.slice(i, i + q.length)), text.slice(i + q.length)];
  };
  return screen(
    h("header", { class: "thread-head" }, h("div", { class: "who" }, h("div", { class: "title" }, "Search"))),
    h("div", { class: "page" }, h("div", { class: "page-inner" },
      h("label", { class: "search big page-search" }, icon("search", "sm"), input, state.searching ? h("span", { class: "spinner" }) : null),
      state.searching ? h("div", { class: "pending rail-searching", style: "justify-content:center;padding:14px" }, h("span", { class: "spinner" }), "Searching") : null,
      state.searchHits && !state.searchHits.length ? emptyState({ icon: "search", tone: "slate", title: "No results", text: "Try a name, a word from a text or call, or part of a number." }) : null,
      ...[...groups].flatMap(([kind, hits]) => [
        h("div", { class: "group-title" }, kind === "agent" ? `Your chat with ${aiName()}` : HIT_LABEL[kind as SearchHit["kind"]] ?? kind),
        h("div", { class: "group" }, ...hits.map((hit) => {
          const row = h("button", { class: "item link", onclick: () => {
            state.focusRef = hit.ref;
            const to: Route = hit.kind === "contact" || hit.kind === "note" ? { name: "contact", number: hit.number }
              : hit.kind === "chat" ? { name: "chat" } : hit.kind === "agent" ? { name: "ai-agent" } : { name: "thread", number: hit.number };
            if ((to.name === "thread" || to.name === "ai-agent") && (hit.line === "personal") !== (state.line === "personal")) switchLine(hit.line === "personal" ? "personal" : "team", to);
            else go(to);
          } },
            hit.kind === "chat" ? h("span", { class: "hit-icon" }, icon("chat", "sm")) : hit.kind === "agent" ? h("span", { class: "hit-icon" }, icon("sparkle", "sm")) : hit.kind === "internal" ? h("span", { class: "hit-icon" }, icon("phone", "sm")) : avatar(hit.number, hit.contactName, "sm"),
            h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, hit.contactName || phone(hit.number)), h("div", { class: "sub clamp2" }, ...highlight(hit.snippet))),
            hit.kind === "contact" ? null : h("span", { class: "muted small", title: fullTime(hit.at) }, listTime(hit.at)));
          // Contacts get Call and Text right there; texts, voicemails and the rest just open.
          return hit.kind === "contact" ? h("div", { class: "hit-row" }, row, contactActions(hit.number, hit.contactName)) : row;
        }))]))));
}

// ---------------------------------------------------------------- saved replies

async function loadSnippets(force = false) {
  if (state.snippetsLoaded && !force) return;
  try {
    state.snippets = (await api.snippets()).snippets;
    state.snippetsLoaded = true;
    render();
  } catch {
    // shown as empty
  }
}

function snippetsView() {
  const form = (sn: Snippet | null) => h("form", { class: "item stack", onsubmit: async (e: Event) => {
    e.preventDefault();
    const title = (document.getElementById("sn-title") as HTMLInputElement).value.trim();
    const body = (document.getElementById("sn-body") as HTMLTextAreaElement).value.trim();
    if (!title || !body) return toast("Add a name and the text");
    try {
      if (sn) await api.updateSnippet(sn.id, { title, body });
      else await api.createSnippet(title, body);
      state.editingSnippet = null;
      await loadSnippets(true);
    } catch (err) {
      toast((err as Error).message);
    }
  } },
    h("input", { id: "sn-title", class: "field", placeholder: "Name (e.g. Deposit link)", value: sn?.title ?? "", maxlength: 60 }),
    h("textarea", { id: "sn-body", class: "field area", rows: 4, placeholder: "Hi {first_name}! Here's the link to pay your deposit: …", value: sn?.body ?? "" }),
    h("div", { class: "sub" }, "{first_name} and {name} are filled in from the contact."),
    h("div", { class: "row-actions" },
      h("button", { class: "btn sm", type: "submit" }, "Save"),
      h("button", { class: "btn quiet sm", type: "button", onclick: () => ((state.editingSnippet = null), render()) }, "Cancel")));
  return screen(
    h("header", { class: "thread-head" }, backButton({ name: "settings" }),
      h("div", { class: "who" }, h("div", { class: "title" }, "Saved replies"), h("div", { class: "number" }, "Type / in a text, or tap ⚡")),
      h("button", { class: "btn sm", onclick: () => ((state.editingSnippet = "new"), render(), document.getElementById("sn-title")?.focus()) }, icon("plus", "sm"), "New")),
    h("div", { class: "page" }, h("div", { class: "page-inner" },
      state.editingSnippet === "new" ? h("div", { class: "group", style: "margin-top:12px" }, form(null)) : null,
      state.snippets.length
        ? h("div", { class: "group", style: "margin-top:12px" }, ...state.snippets.map((sn) => state.editingSnippet === sn.id ? form(sn) :
            h("div", { class: "item" },
              h("span", { class: "item-icon" }, icon("bolt", "sm")),
              h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, sn.title), h("div", { class: "sub clamp2" }, sn.body)),
              h("button", { class: "text-btn", onclick: () => ((state.editingSnippet = sn.id), render()) }, "Edit"),
              h("button", { class: "icon-btn sm", "aria-label": "Delete", onclick: async () => {
                await api.deleteSnippet(sn.id).catch((e) => toast((e as Error).message));
                await loadSnippets(true);
              } }, icon("trash", "sm")))))
        : state.editingSnippet !== "new" ? emptyState({ icon: "bolt", tone: "gold", title: "No saved replies yet", text: "Save texts you send often (deposit links, office address, check-in reminders)." }) : null)));
}

// ---------------------------------------------------------------- blocked numbers

async function loadBlocked() {
  try {
    state.blocked = (await api.blocked()).blocked;
    render();
  } catch (e) {
    toast((e as Error).message);
  }
}

function blockedView() {
  return screen(
    h("header", { class: "thread-head" }, backButton({ name: "settings" }), h("div", { class: "who" }, h("div", { class: "title" }, "Blocked numbers")),
      // 5.10.20: block a number before it ever calls or texts (before, only from an existing conversation).
      h("button", { class: "btn sm", onclick: async () => {
        const raw = (await glassDialog({ title: "Block a number", message: "Their calls are hung up and their texts dropped.", confirm: "Block", danger: true, input: { placeholder: "(310) 555-0123", type: "tel", inputmode: "tel" } })) as string | null;
        if (!raw) return;
        const e164 = toE164(raw);
        if (!e164) return toast("That doesn't look like a phone number");
        try {
          await api.block(e164);
          toast(`${phone(e164)} blocked`);
        } catch (e) {
          toast((e as Error).message);
        }
        await loadBlocked();
      } }, icon("plus", "sm"), "Block a number")),
    h("div", { class: "page" }, h("div", { class: "page-inner" },
      state.blocked.length
        ? h("div", { class: "group", style: "margin-top:12px" }, ...state.blocked.map((b) =>
            h("div", { class: "item" }, avatar(b.number, b.contactName, "sm"),
              h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, b.contactName || phone(b.number)),
                h("div", { class: "sub" }, `Blocked ${listTime(b.createdAt)}${b.createdByName ? ` by ${firstName(b.createdByName)}` : ""}`)),
              h("button", { class: "text-btn", onclick: async () => {
                await api.unblock(b.number).catch((e) => toast((e as Error).message));
                await loadBlocked();
              } }, "Unblock"))))
        : emptyState({ icon: "block", tone: "red", title: "No blocked numbers", text: "Block spam here, or from a conversation's ⋯ menu. Their calls are hung up and texts dropped." }))));
}

// ---------------------------------------------------------------- your preferences

// 1.10.28: an admin keeps teammates' alerts on (this teammate can't turn alerts, quiet hours or Do Not Disturb off).
let alertsKeptOn = false;
let minVolume = 0;
let teamAlertsLocked = false;
async function loadPrefs() {
  try {
    const r = await api.prefs();
    state.prefs = r.prefs;
    alertsKeptOn = !!r.alertsLocked;
    minVolume = r.minVolume ?? 0;
    render();
  } catch (e) {
    toast((e as Error).message);
  }
}

async function savePrefs(change: Partial<UserPrefs>, ok = "Saved") {
  try {
    const r = await api.savePrefs(change);
    state.prefs = r.prefs;
    alertsKeptOn = !!r.alertsLocked;
    minVolume = r.minVolume ?? 0;
    toast(ok);
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

// ---------------------------------------------------------------- storage (admins)

let storageLoading = false;
let storageError = "";
async function loadStorage() {
  if (storageLoading) return;
  storageLoading = true;
  try {
    state.storageFull = await api.storage();
    storageError = "";
  } catch (e) {
    storageError = (e as Error).message;
    toast(storageError);
  } finally {
    storageLoading = false;
  }
  render();
}

// ---------------------------------------------------------------- Settings → Phone numbers (admins)

const numbersPage = { data: null as NumbersBody | null, error: "", busy: "" };
async function loadNumbers() {
  try {
    numbersPage.data = await api.numbers();
    numbersPage.error = "";
  } catch (e) {
    numbersPage.error = (e as Error).message;
  }
  if (state.route.name === "phone-numbers") render();
}
async function numbersDo(key: string, run: () => Promise<unknown>, said: string) {
  numbersPage.busy = key;
  render();
  try {
    await run();
    toast(said);
  } catch (e) {
    toast((e as Error).message);
  }
  numbersPage.busy = "";
  await loadNumbers();
}
const portStatus = (s: string) =>
  /foc|confirm/i.test(s) ? "Date confirmed" : /exception|reject/i.test(s) ? "Needs attention (see Telnyx)" : /submit|process|pending/i.test(s) ? "In progress"
  : /draft/i.test(s) ? "Draft (not submitted yet)" : /ported|complete/i.test(s) ? "Done" : /cancel/i.test(s) ? "Cancelled" : s || "In progress";

function phoneNumbersView() {
  const d = numbersPage.data;
  if (!d && !numbersPage.error) void loadNumbers();
  const busy = numbersPage.busy;
  const row = (n: NumbersBody["numbers"][number]) => {
    const live = n.status === "active";
    const connected = n.calls && n.texts;
    const badge = n.role === "office" ? h("span", { class: "num-role office" }, icon("inbox", "sm"), "Office line")
      : n.role === "personal" ? h("span", { class: "num-role personal" }, icon("lock", "sm"), "Your personal line")
      : n.role === "previous" ? h("span", { class: "num-role" }, "Previous office number · still answered")
      : n.role === "taken" ? h("span", { class: "num-role" }, "In use") : null;
    return h("div", { class: "item num-row" },
      h("div", { class: "grow" },
        h("div", { class: "num-top" }, h("strong", {}, phone(n.number)), badge),
        h("div", { class: "sub" },
          !live ? `Not active yet (${n.status.replace(/-/g, " ")})`
          : connected ? "Calls and texts come into Joy Phone"
          : [n.calls ? "Calls come in" : "Calls don't reach Joy Phone yet", n.texts ? "texts come in" : d!.textProfileKnown ? "texts don't yet" : "texts: set the messaging profile in Telnyx"].join(" · ")),
        h("div", { class: "num-actions" },
          live && !connected ? h("button", { class: "btn sm", type: "button", disabled: !!busy, onclick: () => void numbersDo(`c:${n.id}`, () => api.connectNumber(n.id), `${phone(n.number)} now comes into Joy Phone`) },
            busy === `c:${n.id}` ? h("span", { class: "spinner" }) : icon("link", "sm"), "Connect to Joy Phone") : null,
          live && !n.role ? h("button", { class: "btn quiet sm", type: "button", disabled: !!busy, onclick: async () => {
            const ok = await glassDialog({ title: `Make ${phone(n.number)} the office line?`,
              message: `It becomes Joy Travel's number: the inbox, caller ID for everyone's calls and texts, and ${aiName()}'s calls. ${phone(d!.office)} keeps working, so customers who still use it reach you.`,
              confirm: "Make it the office line" });
            if (ok) void numbersDo(`o:${n.id}`, async () => {
              state.teamNumber = (await api.setOfficeNumber(n.number)).office;
            }, `${phone(n.number)} is now the office line`);
          } }, "Make office line") : null,
          live && !n.role ? h("button", { class: "btn quiet sm", type: "button", disabled: !!busy, onclick: async () => {
            const ok = await glassDialog({ title: `Use ${phone(n.number)} as your personal line?`,
              message: "Calls and texts to it go to your Personal inbox (only you see it, only you ring), and you call and text from it there.",
              confirm: "Use as my personal line" });
            if (ok) void numbersDo(`p:${n.id}`, async () => {
              await api.setPersonalNumber(n.number);
              aiAgent.pa = null; // reload Selena's personal settings
              void refresh();
            }, `${phone(n.number)} is now your personal line`);
          } }, "Use as my personal line") : null)));
  };
  return settingsPage("Phone numbers", "Your Telnyx numbers, and which one is the office line",
    numbersPage.error ? h("div", { class: "group", style: "margin-top:12px" }, h("div", { class: "item" }, h("div", { class: "sub bad" }, numbersPage.error))) : null,
    !d && !numbersPage.error ? h("div", { class: "group", style: "margin-top:12px" }, h("div", { class: "item" }, h("span", { class: "spinner" }), "Asking Telnyx")) : null,
    d ? h("div", { class: "group-title" }, "On your account") : null,
    d ? h("div", { class: "group" }, ...(d.numbers.length ? d.numbers.map(row) : [h("div", { class: "item" }, h("div", { class: "sub" }, "No numbers on this Telnyx account yet."))])) : null,
    d?.porting.length ? h("div", { class: "group-title" }, "On the way (porting)") : null,
    d?.porting.length ? h("div", { class: "group" }, ...d.porting.map((p) => h("div", { class: "item num-row" },
      h("div", { class: "grow" },
        h("div", { class: "num-top" }, h("strong", {}, phone(p.number)), h("span", { class: "num-role" }, portStatus(p.status))),
        h("div", { class: "sub" }, p.focDate ? `Switches over ${fmtDate(new Date(p.focDate), { weekday: "short", month: "short", day: "numeric" })}. When it shows above as active, tap Connect, then pick what it's for.` : "When it shows above as active, tap Connect, then pick what it's for."))))) : null,
    d ? h("p", { class: "sub", style: "margin:12px 6px" },
      "Connect points a number's calls and texts at Joy Phone. Texting from a US number also needs it added to your texting registration (10DLC campaign) in the Telnyx portal.",
      d.office !== d.officeSecret ? ` The office line was picked here; TEAM_NUMBER in Fly is still ${phone(d.officeSecret)} (changing that secret later makes it the office line again).` : "") : null);
}

function storageView() {
  const d = state.storageFull;
  if (!d && !storageError) void loadStorage(); // opened straight from an alert (once: a failure would loop)
  const s = d?.storage;
  const gb = (b: number) => `${(b / 1024 ** 3).toFixed(b < 1024 ** 3 ? 2 : 1)} GB`;
  const warn = !!s && !!d && s.usedPct >= d.warnPct;
  return settingsPage("Storage", "Space for calls, recordings and messages on the server",
    h("div", { class: "group", style: "margin-top:12px" },
      !d
        ? (storageError ? h("div", { class: "item" }, h("div", { class: "sub bad" }, storageError)) : h("div", { class: "item" }, h("span", { class: "spinner" }), "Checking"))
        : !s
          ? h("div", { class: "item" }, h("div", { class: "sub" }, "The server couldn't read its storage size."))
          : h("div", { class: "item stack" },
              h("div", { class: "storage-head" },
                h("strong", {}, `${gb(s.usedBytes)} of ${gb(s.totalBytes)} used`),
                h("span", { class: warn ? "sub bad" : "sub" }, `${gb(s.freeBytes)} free`)),
              h("div", { class: `storage-bar${warn ? " warn" : ""}`, role: "meter", "aria-valuemin": "0", "aria-valuemax": "100", "aria-valuenow": String(s.usedPct), "aria-label": "Storage used" },
                h("span", { style: `width:${Math.min(100, s.usedPct)}%` })),
              h("div", { class: "sub" }, `${s.usedPct}% used. Admins get an alert at ${d.warnPct}%.`))),
    h("p", { class: "sub", style: "margin:12px 6px" },
      "Call recordings and voicemails (including ones copied from Quo) use most of the space. When storage is full, calls, texts and sign-ins stop working, so grow it before then. In the terminal:"),
    // One line that finds the volume itself (no ID to copy). -s is the new size in GB.
    h("pre", { class: "code-block" }, "fly volumes extend $(fly volumes list -a joy-phone --json | grep -o '\"id\": *\"vol_[^\"]*\"' | head -1 | grep -o 'vol_[^\"]*') -s 10 -a joy-phone"),
    h("p", { class: "sub", style: "margin:8px 6px" }, "Growing it keeps everything and doesn't restart the app. Fly charges about $0.15 per GB a month."));
}

// ---------------------------------------------------------------- Face ID offer (once per device)

/** This device has Face ID / Touch ID for Joy Phone, or the offer was dismissed: never offer again here. */
function faceIdDone(): boolean {
  try {
    return localStorage.getItem("joy.faceid") !== null;
  } catch {
    return true; // can't remember a choice: don't nag
  }
}
/** Face ID / Touch ID for Joy Phone was set up (or used) on this very device. Only then is "Use Face ID" offered:
 * on a device without it, the phone can only offer a QR code for another device, which isn't what anyone wants. */
function faceIdHere(): boolean {
  try {
    return localStorage.getItem("joy.faceid") === "added";
  } catch {
    return false;
  }
}
function forgetFaceIdHere() {
  try {
    if (localStorage.getItem("joy.faceid") === "added") localStorage.removeItem("joy.faceid");
  } catch {
    // private mode
  }
}
function setFaceIdDone(how: "added" | "dismissed") {
  try {
    localStorage.setItem("joy.faceid", how);
  } catch {
    // private mode
  }
}
/** "Add Face ID" in the inbox, once per device: gone for good when added (or dismissed with ×). */
function faceIdOffer() {
  if (!state.user || faceIdDone() || !canPasskey()) return null;
  return h("div", { class: "notice slim faceid-banner" },
    icon("user", "sm"),
    h("span", { class: "grow" }, `Sign in faster with ${bioName()}`),
    h("button", { class: "btn sm", type: "button", onclick: async () => {
      try {
        await passkeyRegister();
        setFaceIdDone("added");
        toast(`${bioName()} added for this device`);
        render();
      } catch (e) {
        const name = (e as Error).name;
        // Already set up on this device (the browser refuses a second one): that's done too.
        if (name === "InvalidStateError") {
          setFaceIdDone("added");
          render();
        } else if (name !== "NotAllowedError") toast((e as Error).message);
      }
    } }, `Add ${bioName()}`),
    h("button", { class: "icon-btn sm", type: "button", "aria-label": "Not now", title: "Not now", onclick: () => (setFaceIdDone("dismissed"), render()) }, icon("close", "sm")));
}

function settingsPage(title: string, sub: string, ...body: (Node | null)[]) {
  return screen(
    h("header", { class: "thread-head" }, backButton({ name: "settings" }),
      h("div", { class: "who" }, h("div", { class: "title" }, title), sub ? h("div", { class: "number" }, sub) : null)),
    h("div", { class: "page" }, h("div", { class: "page-inner" }, ...body, h("div", { style: "height:40px" }))));
}

function switchRow(id: string, title: string, sub: string, on: boolean, set: (v: boolean) => void) {
  return h("div", { class: "item" },
    h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, title), sub ? h("div", { class: "sub" }, sub) : null),
    h("label", { class: "switch", for: id },
      h("input", { id, type: "checkbox", checked: on, onchange: (e: Event) => set((e.target as HTMLInputElement).checked) }),
      h("span", { class: "track-sw" }), h("span", { class: "sr-only" }, title)));
}

function callingSettingsView() {
  const u = state.user!;
  const mode = u.forwardMode ?? (u.forwardNumber ? "always" : "never");
  const via = u.callVia ?? "app";
  const numberInput = h("input", { id: "fwd-number", class: "field", type: "tel", inputmode: "tel", placeholder: "Your cell, e.g. (310) 555-0123", value: u.forwardNumber ? phone(u.forwardNumber) : "" });
  const update = async (change: Parameters<typeof api.updateMyCallSettings>[0], ok: string) => {
    try {
      state.user = (await api.updateMyCallSettings(change)).user;
      toast(ok);
    } catch (e) {
      toast((e as Error).message);
    }
    render();
  };
  const cellNumber = (): string | null | false => {
    const raw = numberInput.value.trim();
    const n = raw ? toE164(raw) : null;
    if (raw && !n) {
      toast("Enter a full phone number");
      return false;
    }
    return n;
  };
  const saveForward = async (nextMode: "never" | "always" | "dnd") => {
    const n = cellNumber();
    if (n === false) return;
    if (nextMode !== "never" && !n) return toast("Add your cell number first");
    await update({ forwardNumber: n, forwardMode: nextMode }, nextMode === "never" ? "Your cell won't ring" : nextMode === "always" ? "Your cell rings together with the app" : "Your cell rings while you're on Do Not Disturb");
  };
  const setVia = async (v: "app" | "cell") => {
    const n = cellNumber();
    if (n === false) return;
    if (v === "cell" && !n) {
      toast("Add your cell number first");
      document.getElementById("fwd-number")?.focus();
      return;
    }
    await update({ forwardNumber: n, callVia: v }, v === "cell" ? "Calls now connect through your cell" : "Calls now connect over the internet");
  };
  const greeting = h("textarea", { id: "my-vm", class: "field area", rows: 3, maxlength: 1000, placeholder: "Hi, you've reached Ana at Joy Travel. Leave a message and I'll call you back.", value: u.vmGreeting ?? u.voicemailGreeting ?? "" });
  const sig = state.prefs?.textSignature;
  const sigInput = h("input", { id: "sig-text", class: "field", maxlength: 100, placeholder: "– Ana, Joy Travel", value: sig?.text ?? "" });
  const pick = (options: [string, string][], current: string, set: (v: string) => void, label: string) =>
    h("div", { class: "segmented wrap", role: "group", "aria-label": label },
      ...options.map(([v, text]) => h("button", { type: "button", "aria-pressed": String(current === v), onclick: () => set(v) }, text)));
  const host = location.host;
  return settingsPage("Calls & forwarding", "Just for you",
    h("div", { class: "group-title" }, "Availability"),
    h("div", { class: "group" },
      switchRow("dnd2", "Do Not Disturb", "You won't ring for new calls. Texts and alerts still come in.", !!u.dnd, (v) => void toggleDnd(v))),

    h("div", { class: "group-title" }, "Connect my calls"),
    h("div", { class: "group" },
      h("div", { class: "item stack" },
        pick([["app", "Over the internet (Wi-Fi or data)"], ["cell", "Through my cell phone"]], via, (v) => void setVia(v as "app" | "cell"), "Connect my calls"),
        h("div", { class: "sub" }, via === "cell"
          ? "Business calls ring your cell (and the app). Calls you start from the phone app ring your cell first; pick up and you're connected. Customers always see the Joy Travel number, and calls are still recorded and summarized here."
          : "Calls ring and connect in the Joy Phone app, using Wi-Fi or mobile data."),
        h("label", { class: "mini-label", for: "fwd-number" }, "My cell number"),
        numberInput,
        h("div", { class: "row-actions" }, h("button", { class: "btn quiet sm", onclick: async () => {
          const n = cellNumber();
          if (n === false) return;
          if (!n && via === "cell") return toast("Your cell number is needed while calls go through your cell");
          await update({ forwardNumber: n }, n ? "Cell number saved" : "Cell number removed");
        } }, "Save number"))),
      via === "cell"
        ? h("div", { class: "item stack" },
            h("div", { style: "font-weight:600" }, "On my cell, business calls show"),
            pick([["customer", "The customer's number"], ["team", "The Joy Phone number"]], u.cellCallerId ?? "customer",
              (v) => void update({ cellCallerId: v as "customer" | "team" }, "Saved"), "Caller ID on my cell"),
            h("div", { class: "sub" }, (u.cellCallerId ?? "customer") === "customer"
              ? "With Joy Phone contacts on your iPhone (below), you'll see “Scott Smith – Joy Phone”. New callers show their number, and the text below tells you who it is."
              : "Every business call shows the Joy Travel number. Save it as “Joy Phone” (the contacts below do this for you)."))
        : null,
      via === "cell"
        ? switchRow("cell-text", "Text me who's calling", "A text arrives as your cell rings: “📞 Scott Smith · (310) 555-1234 – Joy Phone”.", u.cellText !== false,
            (v) => void update({ cellText: v }, v ? "You'll get a heads-up text" : "No heads-up texts"))
        : null,
      via === "cell"
        ? h("div", { class: "item stack" }, h("div", { class: "sub" }, "When you pick up a forwarded call you'll hear “Joy Phone call from Scott Smith, press 1 to accept”, so your cell's voicemail can never grab a customer."))
        : h("div", { class: "item stack" },
            h("div", { style: "font-weight:600" }, "Also ring my cell"),
            pick([["never", "Never"], ["always", "Together with the app"], ["dnd", "Only on Do Not Disturb"]], mode, (m) => void saveForward(m as "never" | "always" | "dnd"), "When"))),

    h("div", { class: "group-title" }, "Joy Phone contacts on my iPhone"),
    h("div", { class: "group" },
      h("div", { class: "item stack" },
        h("div", { class: "sub" }, "Adds every customer to your iPhone's contacts as “Name – Joy Phone” (and keeps it up to date), so a business call to your cell shows who it is and that it's work. Read-only; nothing on your phone is sent to Joy Phone."),
        state.appPw
          ? h("div", { class: "dav-steps" },
              h("ol", {},
                h("li", {}, "On your iPhone open ", h("strong", {}, "Settings → Apps → Contacts → Contacts Accounts → Add Account → Other → Add CardDAV Account"), "."),
                h("li", {}, "Server: ", copyCode(host)),
                h("li", {}, "User name: ", copyCode(u.email)),
                h("li", {}, "Password: ", copyCode(state.appPw), h("span", { class: "sub" }, " (shown once; it only works for contacts)")),
                h("li", {}, "Tap ", h("strong", {}, "Next"), ". The “Joy Phone” contacts appear within a minute.")),
              h("button", { class: "btn quiet sm", onclick: () => ((state.appPw = null), render()) }, "Done"))
          : h("div", { class: "row-actions" },
              h("button", { class: "btn sm", onclick: async () => {
                try {
                  state.appPw = (await api.newAppPassword("iPhone contacts")).password;
                  appPwList = null; // the list below shows the new one too
                } catch (e) {
                  toast((e as Error).message);
                }
                render();
              } }, icon("contacts", "sm"), "Set up on my iPhone"))),
      // 5.10.20: the contacts passwords you've made (each "Set up" makes one): see them and remove old ones.
      ...appPasswordRows()),
    h("div", { class: "group-title" }, "My voicemail greeting"),
    h("div", { class: "group" },
      h("div", { class: "item stack" },
        greeting,
        h("div", { class: "sub" }, "Played when a caller asked for you (phone menu “Ring you”) and you didn't answer. Leave empty to use the team greeting."),
        h("div", { class: "row-actions" }, h("button", { class: "btn sm", onclick: async () => {
          try {
            state.user = (await api.updateMyCallSettings({ vmGreeting: greeting.value.trim() || null })).user;
            toast("Greeting saved");
          } catch (e) {
            toast((e as Error).message);
          }
        } }, "Save greeting")))),
    h("div", { class: "group-title" }, "Text signature"),
    h("div", { class: "group" },
      !state.prefs ? h("div", { class: "item" }, h("span", { class: "spinner" })) :
      switchRow("sig-on", "Sign my texts", "Added at the end of texts you send to customers.", !!sig?.enabled, (v) => {
        if (v && !sigInput.value.trim()) {
          toast("Type your signature first");
          return render();
        }
        void savePrefs({ textSignature: { enabled: v, text: sigInput.value.trim() } });
      }),
      h("div", { class: "item stack" }, sigInput,
        h("div", { class: "row-actions" }, h("button", { class: "btn quiet sm", onclick: () => void savePrefs({ textSignature: { enabled: !!sig?.enabled && !!sigInput.value.trim(), text: sigInput.value.trim() } }, "Signature saved") }, "Save signature")))));
}

function notificationSettingsView() {
  const p = state.prefs;
  if (!p) return settingsPage("Notifications", "", h("div", { class: "pending", style: "padding:40px;justify-content:center" }, h("span", { class: "spinner" }), "Loading"));
  const n = p.notify;
  const set = (key: keyof UserPrefs["notify"], v: boolean | string) => void savePrefs({ notify: { ...n, [key]: v } as UserPrefs["notify"] });
  const q = p.quietHours;
  // Kept on by an admin: the switches show as on and can't be changed; quiet hours are off.
  if (alertsKeptOn) return settingsPage("Notifications", "Alerts on this account (all your devices)",
    h("div", { class: "group-title" }, "This device"),
    h("div", { class: "group" },
      h("div", { class: "item" },
        h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, alertsOn() ? "Notifications are on here" : "Notifications are off here"),
          h("div", { class: "sub" }, alertsOn() ? "To get calls during a Focus mode, allow Joy Phone in that Focus." : "Turn them on: your admin needs everyone to get calls and texts.")),
        !alertsOn() ? h("button", { class: "btn sm", onclick: () => void enableAlerts() }, "Enable") : null)),
    h("div", { class: "group-title" }, "Alerts"),
    h("div", { class: "group" }, h("div", { class: "item stack" },
      h("div", { style: "font-weight:600" }, "Kept on by your admin"),
      h("div", { class: "sub" }, `You get every alert: calls, missed calls, voicemails, customer texts, @mentions, team chat and reminders. Quiet hours and Do Not Disturb are off, and the app's sound stays at ${minVolume}% or more.`))));
  return settingsPage("Notifications", "Alerts on this account (all your devices)",
    h("div", { class: "group-title" }, "This device"),
    h("div", { class: "group" },
      h("div", { class: "item" },
        h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, alertsOn() ? "Notifications are on here" : "Notifications are off here"),
          h("div", { class: "sub" }, alertsOn() ? "To get calls during a Focus mode, allow Joy Phone in that Focus." : "Turn on to get calls and texts when the app is closed.")),
        !alertsOn() ? h("button", { class: "btn sm", onclick: () => void enableAlerts() }, "Enable") : null)),
    h("div", { class: "group-title" }, "Alert me about"),
    h("div", { class: "group" },
      switchRow("n-calls", "Incoming calls", "", n.calls, (v) => set("calls", v)),
      switchRow("n-missed", "Missed calls", "", n.missedCalls, (v) => set("missedCalls", v)),
      switchRow("n-vm", "Voicemails", "", n.voicemail, (v) => set("voicemail", v)),
      h("div", { class: "item" },
        h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Customer texts"), h("div", { class: "sub" }, "“Mine” = assigned to me or to nobody.")),
        h("div", { class: "segmented", role: "group" },
          ...([["all", "All"], ["assigned", "Mine"], ["none", "None"]] as const).map(([v, l]) =>
            h("button", { "aria-pressed": String(n.texts === v), onclick: () => set("texts", v) }, l)))),
      switchRow("n-mentions", "@mentions & assignments", "", n.mentions, (v) => set("mentions", v)),
      switchRow("n-chat", "Team chat", "", n.teamChat, (v) => set("teamChat", v)),
      switchRow("n-tasks", "Follow-up reminders", "", n.tasks, (v) => set("tasks", v))),
    h("div", { class: "group-title" }, "Quiet hours"),
    h("div", { class: "group" },
      switchRow("q-on", "Quiet hours", "No text, chat or mention alerts in this window. Calls and voicemails still alert.", q.enabled, (v) => void savePrefs({ quietHours: { ...q, enabled: v } })),
      q.enabled
        ? h("div", { class: "item hours-row" },
            h("span", { class: "hours-times", style: "justify-content:flex-start" },
              h("input", { class: "field time", type: "time", value: q.from, "aria-label": "From", onchange: (e: Event) => void savePrefs({ quietHours: { ...q, from: (e.target as HTMLInputElement).value } }) }),
              h("span", { class: "muted" }, "to"),
              h("input", { class: "field time", type: "time", value: q.to, "aria-label": "To", onchange: (e: Event) => void savePrefs({ quietHours: { ...q, to: (e.target as HTMLInputElement).value } }) })),
            h("select", { class: "field", style: "max-width:200px", onchange: (e: Event) => void savePrefs({ quietHours: { ...q, timezone: (e.target as HTMLSelectElement).value } }) },
              ...TIMEZONES.concat(TIMEZONES.includes(q.timezone) ? [] : [q.timezone]).map((tz) => h("option", { value: tz, selected: tz === q.timezone }, tz.replace(/_/g, " ")))))
        : null));
}

function soundSettingsView() {
  const sp = getSoundPrefs();
  const opt = (cat: "ringtone" | "text" | "mention", o: { id: string; label: string }) =>
    h("button", { class: "sound-opt", "aria-pressed": String(sp[cat] === o.id), onclick: () => {
      setSoundPrefs({ [cat]: o.id });
      if (o.id !== "none") preview(cat, o.id);
      render();
    } }, o.id === "none" ? icon("close", "sm") : icon("playFill", "sm"), o.label);
  const picker = (cat: "ringtone" | "text" | "mention", title: string) => {
    // 1.10.28: alerts kept on by the admin: no "None" (it made calls and texts silent while showing online).
    const list = SOUND_OPTIONS[cat].filter((o) => !(alertsKeptOn && o.id === "none"));
    const groups = [...new Set(list.map((o) => o.group ?? ""))];
    return h("div", { class: "item stack" },
      h("div", { style: "font-weight:600" }, title),
      ...groups.flatMap((g) => [
        g ? h("div", { class: "sound-group" }, g) : null,
        h("div", { class: "sound-grid" }, ...list.filter((o) => (o.group ?? "") === g).map((o) => opt(cat, o))),
      ].filter(Boolean) as Node[]));
  };
  return settingsPage("Sounds & ringtones", "Yours only, on all your devices",
    h("div", { class: "group" },
      picker("ringtone", "Incoming call ringtone"),
      picker("text", "New customer text"),
      picker("mention", "@mentions & team chat"),
      h("div", { class: "item" },
        h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Volume"), h("div", { class: "sub" }, `${sp.volume}%`)),
        h("input", { type: "range", min: minVolume, max: 100, step: 5, value: Math.max(minVolume, sp.volume), class: "range", "aria-label": "Volume",
          oninput: (e: Event) => setSoundPrefs({ volume: Number((e.target as HTMLInputElement).value) }),
          onchange: () => (preview("ringtone", getSoundPrefs().ringtone), render()) }))),
    h("div", { class: "group-title" }, "Call sounds"),
    h("div", { class: "group" },
      switchRow("snd-ringback", "Ringing while you call out", "The ring you hear until they answer.", sp.ringback, (v) => (setSoundPrefs({ ringback: v }), render())),
      switchRow("snd-keypad", "Keypad tones", "A beep for each key you press during a call.", sp.keypadTones, (v) => (setSoundPrefs({ keypadTones: v }), render())),
      switchRow("snd-waiting", "Call waiting beep", "A soft beep when another call comes in while you're on one.", sp.waitingBeep, (v) => (setSoundPrefs({ waitingBeep: v }), render())),
      switchRow("snd-hangup", "Call ended tone", "A quick scissors snip when the other side hangs up.", sp.hangupTone, (v) => (setSoundPrefs({ hangupTone: v }), render()))),
    h("p", { class: "sub", style: "margin:12px 6px" }, "Only your sounds change here: teammates keep their own, even on a shared computer. Tap a sound to hear it. These play while Joy Phone is open. When it's closed, your phone or computer plays its own notification sound. On iPhone, the ring/silent switch mutes them."));
}

let micTestStop: (() => void) | null = null;
function stopMicTest() {
  micTestStop?.();
  micTestStop = null;
  state.micLevel = -1;
}

async function loadDevices(ask: boolean) {
  try {
    state.devices = await listDevices(ask);
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

function audioSettingsView() {
  const d = state.devices;
  const dp = getDevicePrefs();
  const select = (id: string, list: MediaDeviceInfo[], value: string | undefined, onchange: (v: string) => void) =>
    h("select", { id, class: "field", onchange: (e: Event) => onchange((e.target as HTMLSelectElement).value) },
      h("option", { value: "", selected: !value }, "System default"),
      ...list.filter((x) => x.deviceId && x.deviceId !== "default").map((x, i) => h("option", { value: x.deviceId, selected: value === x.deviceId }, x.label || `Device ${i + 1}`)));
  const meter = h("div", { class: "meter" }, h("span", { style: `width:${Math.max(0, state.micLevel) * 100}%` }));
  return settingsPage("Audio devices", "On this device",
    !d || !d.mics.some((m) => m.label)
      ? h("div", { class: "group", style: "margin-top:12px" }, h("div", { class: "item" },
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "See your microphones and speakers"), h("div", { class: "sub" }, "Your browser asks for microphone access once.")),
          h("button", { class: "btn sm", onclick: () => void loadDevices(true) }, "Show devices")))
      : null,
    h("div", { class: "group-title" }, "Devices"),
    h("div", { class: "group" },
      h("div", { class: "item stack" }, h("label", { class: "mini-label", for: "dev-mic" }, "Microphone"),
        select("dev-mic", d?.mics ?? [], dp.micId, (v) => (setDevicePrefs({ micId: v || undefined }), void voice.setMic(v).catch(() => {}), render()))),
      d?.canPickSpeaker
        ? h("div", { class: "item stack" }, h("label", { class: "mini-label", for: "dev-spk" }, "Speaker for calls"),
            select("dev-spk", d.speakers, dp.speakerId, (v) => (setDevicePrefs({ speakerId: v || undefined }), void voice.setSpeaker(v).catch(() => {}), render())))
        : null,
      d?.canPickSpeaker
        ? h("div", { class: "item stack" }, h("label", { class: "mini-label", for: "dev-ring" }, "Ring on (plays the ringtone here, e.g. laptop speakers while you wear a headset)"),
            select("dev-ring", d.speakers, dp.ringerId, (v) => (setDevicePrefs({ ringerId: v }), preview("ringtone", getSoundPrefs().ringtone), render())))
        : h("div", { class: "item" }, h("div", { class: "sub" }, "This browser doesn't let apps choose the speaker (Safari and iPhone). Calls use your system output."))),
    h("div", { class: "group-title" }, "Microphone test"),
    h("div", { class: "group" },
      h("div", { class: "item" }, meter,
        h("button", { class: "btn quiet sm", onclick: () => {
          if (micTestStop) {
            stopMicTest();
            return render();
          }
          micTestStop = startMicLevel((lvl) => {
            state.micLevel = lvl;
            const bar = document.querySelector<HTMLElement>(".meter span");
            if (bar) bar.style.width = `${Math.min(1, lvl) * 100}%`;
          }, dp.micId);
          state.micLevel = 0;
          render();
        } }, micTestStop ? "Stop" : "Test mic"))),
    h("div", { class: "group-title" }, "Sound processing"),
    h("div", { class: "group" },
      switchRow("ec", "Echo cancellation", iosCrackleBug()
        ? "iPhone on iOS 27: Apple has a known bug that makes call audio crackle while this is on (WebKit bug 326286). If you hear crackle with AirPods, headphones or the phone at your ear, turn it off. Keep it on for speakerphone."
        : "Keep on unless you use a headset and hear yourself cut out.", dp.echoCancellation, (v) => (setDevicePrefs({ echoCancellation: v }), render())),
      switchRow("ns", "Noise suppression", "Filters background noise (office chatter, fans).", dp.noiseSuppression, (v) => (setDevicePrefs({ noiseSuppression: v }), render())),
      switchRow("agc", "Automatic volume", "Evens out your voice level.", dp.autoGainControl, (v) => (setDevicePrefs({ autoGainControl: v }), render()))),
    h("p", { class: "sub", style: "margin:12px 6px" }, "Changes apply to your next call (the speaker switches right away where supported)."));
}

/** Calling region for this device: automatic (the fastest the speed test measured) or one picked by hand. */
function regionSettings() {
  const dp = getDevicePrefs();
  const calls = callDelayByRegion();
  const inUse = voice.region;
  const name = (r: string) => REGION_NAMES[r] ?? r;
  const tone = (ms: number, good: number, ok: number) => (ms <= good ? "v good-text" : ms <= ok ? "v warn-text" : "v bad");
  const value = dp.regionAuto ? "auto" : dp.region;
  return h("div", { class: "item stack" },
    h("label", { class: "mini-label", for: "dev-region" }, "Calling region (this device)"),
    h("select", { id: "dev-region", class: "field", onchange: (e: Event) => {
      const v = (e.target as HTMLSelectElement).value;
      setDevicePrefs(v === "auto" ? { regionAuto: true } : { regionAuto: false, region: v });
      toast("Reconnecting the phone line…");
      render();
    } },
      h("option", { value: "auto", selected: value === "auto" }, "Automatic (fastest measured)"),
      h("option", { value: "", selected: value === "" }, "Telnyx default"),
      ...Object.entries(REGION_NAMES).filter(([v]) => v).map(([v, l]) => h("option", { value: v, selected: value === v }, l))),
    h("div", { class: "sub" }, `Now connected to: ${name(inUse)}.${dp.regionAuto ? " Automatic uses the region the speed test measured fastest on this device." : ""}`),
    calls.length ? h("div", { class: "conn-report" },
      h("div", { class: "sub", style: "font-weight:600" }, "Real call delay on this device (typical)"),
      ...calls.map((c) => h("div", { class: "kv" },
        h("span", { class: "k wide" }, name(c.region), c.region === inUse ? " (in use)" : ""),
        h("span", { class: tone(c.ms, 50, 120) }, `${c.ms} ms`)))) : null);
}

const MEDIA_SITE_NAMES: Record<string, string> = { Latency: "Automatic (Telnyx picks)" };

/** Admin: the Telnyx site that carries call audio for the whole team. */
// 1.10.27 HD voice: what Telnyx's office connection allows (read from Telnyx), and the one switch only the portal has.
let hdVoice: { codecs: string[] | null; hd: boolean | null } | { error: string } | "loading" | null = null;
function hdVoiceStatus() {
  if (hdVoice === null) {
    hdVoice = "loading";
    void api.hdVoice().then((r) => (hdVoice = r), (e) => (hdVoice = { error: (e as Error).message })).finally(render);
  }
  const portal = "For HD with cell phones, also turn on HD Voice for the office number in the Telnyx portal: Numbers → My Numbers → the number → Voice settings → HD Voice (free). Telnyx has no way for the app to switch that one on.";
  if (hdVoice === "loading") return h("div", { class: "item" }, h("div", { class: "sub" }, "Checking the office line's HD formats at Telnyx…"));
  if ("error" in hdVoice) return h("div", { class: "item stack" }, h("div", { style: "font-weight:600" }, "HD voice on the office line"), h("div", { class: "sub bad" }, hdVoice.error));
  if (hdVoice.codecs === null) return null;
  return h("div", { class: "item stack" },
    h("div", { style: "font-weight:600" }, "HD voice on the office line"),
    h("div", { class: `sub${hdVoice.hd ? "" : " bad"}` }, hdVoice.hd
      ? `The office connection at Telnyx allows HD (${hdVoice.codecs.join(", ")}).`
      : `The office connection at Telnyx only allows phone-line formats (${hdVoice.codecs.join(", ") || "none listed"}). In the Telnyx portal: Voice → SIP Connections → the app's connection → Inbound → Codecs: tick G.722 and OPUS.`),
    h("div", { class: "sub" }, portal));
}

function mediaSiteSettings() {
  const m = state.mediaSite;
  if (m === null) {
    state.mediaSite = "loading";
    void api.mediaSite().then((r) => (state.mediaSite = r), (e) => (state.mediaSite = { error: (e as Error).message })).finally(render);
  }
  if (m === null || m === "loading") return h("div", { class: "item" }, h("div", { class: "sub" }, "Loading where call audio is handled…"));
  if ("error" in m) return h("div", { class: "item stack" }, h("div", { style: "font-weight:600" }, "Where call audio is handled"), h("div", { class: "sub bad" }, m.error));
  const current = m.site ?? "Latency";
  return h("div", { class: "item stack" },
    h("label", { class: "mini-label", for: "media-site" }, "Where call audio is handled (whole team)"),
    h("select", { id: "media-site", class: "field", onchange: (e: Event) => void saveMediaSite((e.target as HTMLSelectElement).value) },
      // Whatever is set in Telnyx shows here even if it isn't in our list (e.g. picked in the Telnyx portal).
      ...[...m.sites, ...(m.sites.includes(current) ? [] : [current])].map((s) => h("option", { value: s, selected: s === current }, MEDIA_SITE_NAMES[s] ?? s))),
    h("div", { class: "sub" }, "The Telnyx site that carries call audio for everyone. Applies from the next call. To find the fastest, use Measure with “Also try every media site” above."));
}

async function saveMediaSite(site: string) {
  try {
    const r = await api.setMediaSite(site);
    state.mediaSite = { site: r.site, sites: state.mediaSite && typeof state.mediaSite === "object" && "sites" in state.mediaSite ? state.mediaSite.sites : [r.site] };
    toast(r.appUpdated ? "Saved. Calls use it from the next call." : "Saved for app calls. Calls forwarded from the main number may still use the old site.");
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

async function runConnectionTest() {
  state.connTest = "running";
  render();
  // Your device ↔ the Joy Phone server (median of 4).
  const times: number[] = [];
  for (let i = 0; i < 5; i++) {
    const t0 = performance.now();
    try {
      await fetch(`/health?ping=${Date.now()}`, { cache: "no-store" });
      if (i > 0) times.push(performance.now() - t0);
    } catch {
      break;
    }
  }
  times.sort((a, b) => a - b);
  const browserMs = times.length ? Math.round(times[Math.floor(times.length / 2)]) : null;
  try {
    const d = await api.diagnostics();
    state.connTest = { browserMs, ...d };
  } catch (e) {
    state.connTest = { browserMs, serverRegion: null, telnyxApiMs: null, error: (e as Error).message };
  }
  render();
}

const FLY_REGIONS: Record<string, string> = { sjc: "San Jose", lax: "Los Angeles", sea: "Seattle", ord: "Chicago", iad: "Virginia", ewr: "New Jersey", dfw: "Dallas", yyz: "Toronto" };

function connectionReport(r: { browserMs: number | null; serverRegion: string | null; telnyxApiMs: number | null; error: string | null }) {
  const row = (label: string, ms: number | null, good: number, ok: number) =>
    h("div", { class: "kv" }, h("span", { class: "k wide" }, label),
      h("span", { class: ms == null ? "v" : ms <= good ? "v good-text" : ms <= ok ? "v warn-text" : "v bad" }, ms == null ? "couldn't measure" : `${ms} ms`));
  const tips: string[] = [];
  if ((r.browserMs ?? 0) > 150) tips.push("Your internet to the server is slow. Try wired or 5 GHz Wi-Fi, and pause big uploads or VPNs.");
  if ((r.telnyxApiMs ?? 0) > 60) tips.push("The server is far from Telnyx. Moving it closer would make ringing and connecting faster (ask me to move it).");
  return h("div", { class: "conn-report" },
    row("Your device ↔ Joy Phone server", r.browserMs, 80, 150),
    row(`Server${r.serverRegion ? ` (${FLY_REGIONS[r.serverRegion] ?? r.serverRegion})` : ""} ↔ Telnyx`, r.telnyxApiMs, 40, 80),
    h("div", { class: "sub" }, "These affect how fast calls ring and connect. Call audio goes directly between your device and Telnyx. Its delay shows on the call screen during a call."),
    ...tips.map((t) => h("div", { class: "sub warn-text" }, t)),
    r.error ? h("div", { class: "sub bad" }, r.error) : null);
}

// ---------------------------------------------------------------- call handling (admins)

function callHandlingView() {
  const s = state.phoneDraft;
  if (!s) return settingsPage("Call handling", "", h("div", { class: "pending", style: "padding:40px;justify-content:center" }, h("span", { class: "spinner" }), "Loading"));
  const c: CallSettings = s.calls;
  const rerender = () => render();
  const callers = state.team.filter((u) => u.canCall);
  const order = [...c.ringOrder.filter((id) => callers.some((u) => u.id === id)), ...callers.map((u) => u.id).filter((id) => !c.ringOrder.includes(id))];
  const move = (i: number, d: number) => {
    const next = [...order];
    const [x] = next.splice(i, 1);
    next.splice(i + d, 0, x);
    c.ringOrder = next;
    rerender();
  };
  async function save() {
    state.phoneSaving = true;
    render();
    try {
      const r = await api.savePhoneSettings(s!);
      state.phoneDraft = r.settings;
      toast("Saved. New calls use it right away.");
    } catch (e) {
      toast((e as Error).message);
    }
    state.phoneSaving = false;
    render();
  }
  return screen(
    h("header", { class: "thread-head" }, backButton({ name: "settings" }), h("div", { class: "who" }, h("div", { class: "title" }, "Call handling"), h("div", { class: "number" }, "How incoming calls ring and what happens next"))),
    h("div", { class: "page" }, h("div", { class: "page-inner" },
      h("div", { class: "group-title" }, "Ringing"),
      h("div", { class: "group" },
        h("div", { class: "item stack" },
          h("div", { class: "segmented", role: "group" },
            h("button", { "aria-pressed": String(c.ringStrategy === "simultaneous"), onclick: () => ((c.ringStrategy = "simultaneous"), rerender()) }, "Everyone at once"),
            h("button", { "aria-pressed": String(c.ringStrategy === "sequential"), onclick: () => ((c.ringStrategy = "sequential"), rerender()) }, "One at a time")),
          h("div", { class: "sub" }, c.ringStrategy === "simultaneous" ? "All available teammates ring together; the first to answer gets the call." : "Teammates ring in this order. Anyone on Do Not Disturb is skipped.")),
        ...(c.ringStrategy === "sequential"
          ? [...order.map((id, i) => {
              const u = state.team.find((x) => x.id === id)!;
              return h("div", { class: "item order-row" }, h("span", { class: "order-n" }, String(i + 1)), avatar(u.email, u.name, "sm"), h("span", { class: "grow" }, u.name),
                h("button", { class: "icon-btn sm", "aria-label": "Move up", disabled: i === 0, onclick: () => move(i, -1) }, h("span", { style: "transform:rotate(-90deg);display:inline-flex" }, icon("chevron", "sm"))),
                h("button", { class: "icon-btn sm", "aria-label": "Move down", disabled: i === order.length - 1, onclick: () => move(i, 1) }, h("span", { style: "transform:rotate(90deg);display:inline-flex" }, icon("chevron", "sm"))));
            }),
            h("div", { class: "item" }, h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Ring each person for")),
              secondsSelect("ring-each", c.ringSecondsEach, [10, 15, 20, 25, 30], (v) => (c.ringSecondsEach = v)))]
          : []),
        h("div", { class: "item" }, h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Ring for up to"), h("div", { class: "sub" }, "Then the no-answer rule below kicks in.")),
          secondsSelect("ring-total", c.ringTimeoutSecs, [15, 20, 25, 30, 45, 60, 90, 120], (v) => (c.ringTimeoutSecs = v))),
        switchRow("cw", "Call waiting", "Ring teammates who are already on a call (they see “Call waiting”).", c.callWaiting, (v) => ((c.callWaiting = v), rerender()))),
      h("div", { class: "group-title" }, "If nobody answers"),
      h("div", { class: "group" }, h("div", { class: "item stack" },
        routeActionField("overflow", c.overflow, (a) => ((c.overflow = a), rerender()), { allowTeam: false }),
        h("div", { class: "sub" }, "Tip: forward to an on-call cell after hours in Phone line → Business hours."))),
      h("div", { class: "group-title" }, "Recording & AI"),
      h("div", { class: "group" },
        switchRow("rec", "Record calls", "Answered calls are recorded (voicemails always are). Turning this off also skips the recording announcement.", c.recordCalls, (v) => ((c.recordCalls = v), rerender())),
        switchRow("ai", "Transcripts & AI summaries", "Calls and voicemails get a transcript, a summary and action items.", c.transcribe, (v) => ((c.transcribe = v), rerender()))),
      h("div", { class: "group-title" }, "Caller ID"),
      h("div", { class: "group" },
        switchRow("cnam", "Look up caller names", "Shows the name registered to unknown numbers (CNAM). Telnyx charges a small fee per lookup; each number is looked up at most once a month.", c.callerNameLookup, (v) => ((c.callerNameLookup = v), rerender()))),
      h("div", { class: "group-title" }, "Listen in"),
      h("div", { class: "group" },
        h("div", { class: "item" }, h("div", { class: "sub" }, "Admins can listen in, whisper or join any teammate's live call from the inbox. Teammates who aren't admins can't, and the teammate on the call sees who is listening."))),
      h("div", { style: "height:80px" }))),
    h("div", { class: "save-bar" }, h("button", { class: "btn block", disabled: state.phoneSaving, onclick: () => void save() }, state.phoneSaving ? "Saving…" : "Save")));
}

function secondsSelect(id: string, value: number, options: number[], set: (v: number) => void) {
  const opts = options.includes(value) ? options : [...options, value].sort((a, b) => a - b);
  return h("select", { id, class: "field", style: "width:130px", onchange: (e: Event) => set(Number((e.target as HTMLSelectElement).value)) },
    ...opts.map((v) => h("option", { value: String(v), selected: v === value }, `${v} seconds`)));
}

// ---------------------------------------------------------------- import from Quo (admins)

let quoPoll: number | undefined;
async function loadQuo(withNumbers: boolean) {
  try {
    state.quo = (await api.quoStatus()).status;
    if (withNumbers && state.quo.configured && !state.quoNumbers) {
      try {
        state.quoNumbers = (await api.quoNumbers()).numbers;
        state.quoPick = new Set(state.quoNumbers.map((n) => n.id));
        state.quoError = null;
      } catch (e) {
        state.quoError = (e as Error).message;
      }
    }
  } catch (e) {
    state.quoError = (e as Error).message;
  }
  clearTimeout(quoPoll);
  if (state.quo?.running && state.route.name === "import-quo") quoPoll = window.setTimeout(() => void loadQuo(false), 2500);
  render();
}

function quoImportView() {
  const st = state.quo;
  if (!st) return settingsPage("Import from Quo", "", h("div", { class: "pending", style: "padding:40px;justify-content:center" }, h("span", { class: "spinner" }), "Loading"));
  const steps = h("div", { class: "group", style: "margin-top:12px" },
    h("div", { class: "item stack" },
      h("div", { style: "font-weight:600" }, "Connect your Quo account (one time)"),
      h("ol", { class: "steps" },
        h("li", {}, "In Quo, go to Settings → API (owners and admins) and click Generate API key. Copy it."),
        h("li", {}, "In the Fly portal, open the joy-phone app → Secrets → New secret. Name: QUO_API_KEY, value: the key. Click Deploy secrets."),
        h("li", {}, "Come back here and refresh.")),
      h("div", { class: "row-actions" }, h("button", { class: "btn quiet sm", onclick: () => void loadQuo(true) }, "I've added it, check again"))));
  const c = st.counts;
  const stat = (label: string, n: number) => h("div", { class: "tile small" }, h("div", { class: "tile-value" }, n.toLocaleString()), h("div", { class: "tile-label" }, label));
  const results = st.startedAt
    ? h("div", { class: "group", style: "margin-top:12px" },
        h("div", { class: "item" },
          st.running ? h("span", { class: "spinner" }) : icon(st.lastError && !st.canceled ? "close" : "checkCircle", "sm"),
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, st.running ? st.phase : st.canceled ? "Import canceled" : st.lastError && st.phase !== "Done" ? "Import stopped" : "Import finished"),
            h("div", { class: "sub" }, st.running ? "You can leave this page. It keeps going on the server." : st.finishedAt ? `Finished ${when(st.finishedAt.replace("T", " ").replace("Z", ""))}` : "")),
          st.running ? h("button", { class: "btn quiet sm", onclick: async () => ((state.quo = (await api.quoCancel()).status), render()) }, "Cancel") : null),
        h("div", { class: "item" }, h("div", { class: "tiles", style: "width:100%;margin:0" },
          stat("Contacts", c.contacts), stat("Contact notes", c.notes ?? 0), stat("Tasks", c.tasks ?? 0), stat("Conversations", c.conversations), stat("Texts", c.messages), stat("Pictures & files", c.media),
          stat("Calls", c.calls), stat("Voicemails", c.voicemails), stat("Recordings", c.recordings), stat("Transcripts", c.transcripts), stat("AI summaries", c.summaries))),
        c.skippedGroups ? h("div", { class: "item" }, h("div", { class: "sub" }, `${c.skippedGroups} group text${c.skippedGroups === 1 ? "" : "s"} skipped (Joy Phone conversations are one-to-one).`)) : null,
        st.notOnPlan.length ? h("div", { class: "item" }, h("div", { class: "sub warn-text" }, `Your Quo plan doesn't include ${st.notOnPlan.join(" and ")} through the API (Business or Scale plan needed), so those were skipped.`)) : null,
        st.lastError ? h("div", { class: "item" }, h("div", { class: "sub bad" }, `${c.errors} problem${c.errors === 1 ? "" : "s"}. Last: ${st.lastError}`)) : null)
    : null;
  const form = st.configured && !st.running
    ? h("div", {},
        h("div", { class: "group-title" }, "Which Quo numbers"),
        state.quoError ? h("div", { class: "group" }, h("div", { class: "item" }, h("div", { class: "sub bad" }, state.quoError))) : null,
        h("div", { class: "group" },
          ...(state.quoNumbers ?? []).map((n) => switchRow(`quo-${n.id}`, n.name ?? phone(n.number), phone(n.number), state.quoPick.has(n.id), (v) => {
            if (v) state.quoPick.add(n.id);
            else state.quoPick.delete(n.id);
            render();
          })),
          !state.quoNumbers && !state.quoError ? h("div", { class: "item" }, h("span", { class: "spinner" }), "Loading your Quo numbers…") : null),
        h("div", { class: "group-title" }, "Options"),
        h("div", { class: "group" },
          h("div", { class: "item" },
            h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "How far back")),
            h("select", { class: "field", style: "width:170px", onchange: (e: Event) => (state.quoSince = (e.target as HTMLSelectElement).value) },
              ...[["", "Everything"], ["24", "Last 2 years"], ["12", "Last 12 months"], ["6", "Last 6 months"], ["3", "Last 3 months"]].map(([v, l]) => h("option", { value: v, selected: state.quoSince === v }, l)))),
          switchRow("quo-rec", "Copy call recordings & voicemail audio", "Stored privately on your Joy Phone server so you can play them here. Turn off to import only transcripts and summaries.", state.quoRecordings, (v) => ((state.quoRecordings = v), render())),
          switchRow("quo-old", "Mark old conversations as done", "Conversations with nothing new in 30 days go to Done instead of filling your inbox. New calls or texts reopen them.", state.quoMarkOld, (v) => ((state.quoMarkOld = v), render()))),
        h("p", { class: "sub", style: "margin:12px 6px" }, "Imported history shows in each conversation like any call or text, marked read. Teammates are matched by email; anyone not on Joy Phone shows as “Name (Quo)”. Safe to run again: it only adds what's new, so you can run it once more right before switching your number over."),
        h("button", { class: "btn block", disabled: !state.quoPick.size, onclick: async () => {
          const months = Number(state.quoSince);
          const since = months ? new Date(Date.now() - months * 30.44 * 86_400_000).toISOString() : null;
          try {
            state.quo = (await api.quoStart({ phoneNumberIds: [...state.quoPick], since, recordings: state.quoRecordings, markOldDone: state.quoMarkOld })).status;
            toast("Import started");
            void loadQuo(false);
          } catch (e) {
            toast((e as Error).message);
          }
          render();
        } }, icon("download", "sm"), st.startedAt ? "Import again (adds only what's new)" : "Start import"))
    : null;
  return settingsPage("Import from Quo", "Bring your old phone's history into Joy Phone",
    portingCard(),
    !st.configured ? steps : null,
    results,
    form);
}

/** "Can my number move to Joy Phone?": asks Telnyx (portability check) before starting a port. Nothing is moved. */
function portingCard() {
  const fromQuo = (state.quoNumbers ?? []).map((n) => phone(n.number)).join(", ");
  const value = state.portInput ?? fromQuo;
  const check = async () => {
    const numbers = (state.portInput ?? fromQuo).split(/[,;\n]+/).map((x) => x.trim()).filter(Boolean);
    if (!numbers.length) return toast("Type your Quo number first");
    state.portBusy = true;
    state.portError = null;
    render();
    try {
      state.portResults = (await api.portingCheck(numbers)).results;
    } catch (e) {
      state.portResults = null;
      state.portError = (e as Error).message;
    }
    state.portBusy = false;
    render();
  };
  return h("div", {},
    h("div", { class: "group-title" }, "Can my number move to Joy Phone?"),
    h("div", { class: "group" },
      h("div", { class: "item stack" },
        h("div", { class: "sub" }, "Checks with Telnyx whether your Quo number can be moved over (ported). This is only a check: nothing changes in Quo or Telnyx."),
        h("div", { class: "row-actions", style: "gap:8px;flex-wrap:nowrap;width:100%" },
          h("input", { class: "field", style: "flex:1;min-width:0", type: "tel", placeholder: "(415) 555-0123", value, "aria-label": "Numbers to check (separate with commas)",
            oninput: (e: Event) => (state.portInput = (e.target as HTMLInputElement).value),
            onkeydown: (e: KeyboardEvent) => e.key === "Enter" && void check() }),
          h("button", { class: "btn sm", disabled: state.portBusy, onclick: () => void check() }, state.portBusy ? h("span", { class: "spinner" }) : null, state.portBusy ? "Checking" : "Check"))),
      state.portError ? h("div", { class: "item" }, h("div", { class: "sub bad" }, state.portError)) : null,
      ...(state.portResults ?? []).map((r) =>
        h("div", { class: "item" },
          icon(r.portable ? "checkCircle" : "close", "sm"),
          h("div", { class: "grow" },
            h("div", { style: "font-weight:600" }, phone(r.phone_number)),
            h("div", { class: r.portable ? "sub good-text" : "sub bad" },
              r.portable
                ? r.fast_portable
                  ? "Can move. FastPort eligible: usually done in about a day."
                  : "Can move. Usually takes 1–2 weeks once the port request is filed."
                : `Can't move right now${r.not_portable_reason ? `: ${r.not_portable_reason}` : "."}`)))),
      state.portResults?.some((r) => r.portable)
        ? h("div", { class: "item" }, h("div", { class: "sub" },
            "Next: in the Telnyx portal, open the Porting section under Numbers and start a port-in order. You'll need your Quo account details and a recent bill or Letter of Authorization. Keep Quo active until the port finishes, and run the import above once more right before."))
        : null));
}

// ---------------------------------------------------------------- scheduled texts

async function loadScheduled() {
  try {
    state.scheduledAll = (await api.scheduled()).scheduled;
    render();
  } catch (e) {
    toast((e as Error).message);
  }
}

function scheduledView() {
  const list = state.scheduledAll;
  return settingsPage("Scheduled texts", "Waiting to go out",
    list.length
      ? h("div", { class: "group", style: "margin-top:12px" }, ...list.map((sc) =>
          h("div", { class: "item" }, avatar(sc.number, sc.contactName, "sm"),
            h("button", { class: "grow link-like", onclick: () => go({ name: "thread", number: sc.number }) },
              h("div", { style: "font-weight:600" }, nameFor(sc.number, sc.contactName)),
              h("div", { class: "sub clamp2" }, sc.body || "📎 Attachment"),
              h("div", { class: "sub" }, `${fmtDate(new Date(sc.sendAt), { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}${sc.userName ? ` · ${sc.userId === state.user?.id ? "you" : firstName(sc.userName)}` : ""}`)),
            h("button", { class: "text-btn", onclick: () => editScheduled(sc) }, "Edit"),
            h("button", { class: "text-btn danger", onclick: async () => {
              await api.cancelScheduled(sc.id).catch((e) => toast((e as Error).message));
              await loadScheduled();
            } }, "Cancel"))))
      : emptyState({ icon: "clock", tone: "blue", title: "Nothing scheduled", text: "In a conversation, write a text and tap the clock to send it later." }));
}

// ---------------------------------------------------------------- login & invite

function renderLogin() {
  state.user = null;
  const email = h("input", { id: "email", class: "field", type: "email", placeholder: "Email", "aria-label": "Email", autocomplete: "username", autocapitalize: "none", required: true });
  const password = h("input", { id: "password", class: "field", type: "password", placeholder: "Password", "aria-label": "Password", autocomplete: "current-password", required: true });
  // The second step on the same screen: type the authenticator code now, or leave it empty for the other ways
  // (approve on another device, backup code) on the next screen.
  const code = h("input", { id: "code", class: "field", type: "text", placeholder: "Authenticator code", "aria-label": "Authenticator code",
    autocomplete: "one-time-code", inputmode: "numeric", autocapitalize: "none", maxlength: 20 }) as HTMLInputElement;
  const error = h("div", { class: "error", role: "alert" });
  const button = h("button", { class: "btn block", type: "submit" }, "Sign in");
  const form = h("form", { class: "login-card", onsubmit: async (e: Event) => {
    e.preventDefault();
    button.disabled = true;
    error.textContent = "";
    await startingSignIn();
    try {
      const res = await api.login(email.value, password.value);
      const typed = code.value.replace(/\s+/g, "");
      if (res.mfa === "verify" && typed) {
        try {
          await api.verifyTwoFactor(typed, false);
        } catch (err) {
          error.textContent = (err as Error).message;
          code.value = "";
          code.focus();
          button.disabled = false;
          return;
        }
        return void (await finishSignIn());
      }
      if (res.mfa) return void renderMfa(res.mfa);
      state.user = res.user;
      const me = await api.me();
      state.vapidKey = me.vapidPublicKey;
      state.teamNumber = me.teamNumber;
      await afterSignIn();
    } catch (err) {
      error.textContent = (err as Error).message;
      button.disabled = false;
    }
  } },
    h("img", { class: "login-logo", src: `/brand/logo.png?v=${VERSION}`, alt: "Joy Phone", width: 88, height: 88 }),
    h("div", { class: "login-sub" }, "Sign in"),
    email, password, code,
    error, button,
    ...(canPasskey()
      ? [
          h("div", { class: "login-or" }, h("span", {}, "or")),
          h("button", { class: "btn quiet block passkey-btn", type: "button", onclick: async () => {
            error.textContent = "";
            await startingSignIn();
            try {
              await passkeySignIn();
              setFaceIdDone("added");
              await finishSignIn();
            } catch (err) {
              if ((err as { status?: number }).status === 401) forgetFaceIdHere(); // removed on the server: stop offering it here
              if ((err as Error).name === "NotAllowedError") {
                // Cancelled, or no Face ID saved for Joy Phone on this device yet.
                if (!faceIdHere()) error.textContent = `No ${bioName()} set up here yet. Sign in with your password and code once, then add ${bioName()}.`;
              } else error.textContent = (err as Error).message;
            }
          } }, icon("lock", "sm"), `Sign in with ${bioName()}`),
        ]
      : []),
    // Signed out: no "update" button (19.10.18); a newer version is simply loaded (nothing can be lost here).
    h("div", { class: "version" }, `v${VERSION}`));
  mount(root, h("div", { class: "login" }, form));
}

// ---------------------------------------------------------------- Security & Email alerts settings

async function loadSecurityPage() {
  try {
    const [me, admin] = await Promise.all([api.twoFactor(), isAdmin() ? api.security() : Promise.resolve(null)]);
    state.sec = { me, admin };
    if (admin && !state.secDraft) state.secDraft = structuredClone(admin.settings);
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

const SECURITY_LOG_LABEL: Record<string, string> = {
  call_blocked: "Blocked a call", "2fa_enabled": "Turned on two-step sign-in", "2fa_disabled": "Turned off two-step sign-in",
  "2fa_failed": "Wrong sign-in code", "2fa_passed": "Passed two-step sign-in", passkey_added: "Added Face ID", passkey_login: "Signed in with Face ID",
  passkey_failed: "Face ID sign-in failed", backup_code_used: "Used a backup code", "2fa_reset_by_admin": "Two-step sign-in reset by an admin",
  "2fa_required_on": "Required two-step sign-in for everyone", "2fa_required_off": "Stopped requiring two-step sign-in",
};

function securityView() {
  const sec = state.sec;
  if (!sec) return settingsPage("Security & sign-in", "", h("div", { class: "pending", style: "padding:24px" }, h("span", { class: "spinner" }), "Loading"));
  const me = sec.me;
  const reload = () => void loadSecurityPage();
  const passkeys = h("div", { class: "group" },
    ...me.passkeys.map((p) => h("div", { class: "item" },
      h("span", { class: "item-icon" }, icon("user", "sm")),
      h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, p.name), h("div", { class: "sub" }, p.lastUsedAt ? `Last used ${listTime(p.lastUsedAt)}` : `Added ${listTime(p.createdAt)}`)),
      h("button", { class: "btn quiet sm", onclick: async () => {
        if (!(await glassDialog({ title: `Remove ${p.name}?`, message: `You won't be able to sign in with ${bioName()} on it anymore.`, confirm: "Remove", danger: true }))) return;
        try {
          await api.deletePasskey(p.id);
          toast("Removed");
        } catch (e) {
          toast((e as Error).message);
        }
        reload();
      } }, "Remove"))),
    canPasskey()
      ? h("div", { class: "item" },
          h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, `Sign in with ${bioName()}`),
            h("div", { class: "sub" }, `No password to type: just look at your phone or touch the sensor. Works on this device; set it up on each device you use.`)),
          h("button", { class: "btn sm", onclick: async () => {
            try {
              await passkeyRegister();
              setFaceIdDone("added");
            toast(`${bioName()} is on for this device`);
            } catch (e) {
              if ((e as Error).name !== "NotAllowedError") toast((e as Error).message);
            }
            reload();
          } }, "Set up"))
      : h("div", { class: "item" }, h("div", { class: "sub" }, `${bioName()} sign-in needs Joy Phone opened at its https address on a device with Face ID, Touch ID, Windows Hello or a fingerprint reader.`)));

  const askCode = async (title: string) =>
    (await glassDialog({ title, message: "Enter the 6-digit code from your authenticator app (or a backup code).", confirm: "Continue", input: { placeholder: "123456", inputmode: "numeric" } })) as string | null;
  const app = h("div", { class: "group" },
    h("div", { class: "item" },
      h("span", { class: "item-icon" }, icon("keypad", "sm")),
      h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Authenticator app"),
        h("div", { class: "sub" }, me.authenticator ? `On · ${me.backupCodesLeft} backup code${me.backupCodesLeft === 1 ? "" : "s"} left` : "Off. Asks for a 6-digit code from your phone when you sign in on a new device.")),
      me.authenticator
        ? h("button", { class: "btn quiet sm", onclick: async () => {
            const code = await askCode("Turn off the authenticator app?");
            if (!code) return;
            try {
              await api.disableTwoFactor(code);
              toast("Turned off");
            } catch (e) {
              toast((e as Error).message);
            }
            reload();
          } }, "Turn off")
        : h("button", { class: "btn sm", onclick: async () => {
            try {
              state.secSetup = await api.setupTwoFactor();
              render();
            } catch (e) {
              toast((e as Error).message);
            }
          } }, "Turn on")),
    state.secSetup && !me.authenticator
      ? h("form", { class: "item stack mfa-inline", onsubmit: async (e: Event) => {
          e.preventDefault();
          const code = (document.getElementById("sec-code") as HTMLInputElement).value;
          try {
            const r = await api.enableTwoFactor(code);
            state.secSetup = null;
            await glassDialog({ title: "Save your backup codes", message: `Each signs you in once if you lose your phone:\n\n${r.backupCodes.join("   ")}`, confirm: "I saved them" });
            reload();
          } catch (err) {
            toast((err as Error).message);
          }
        } },
          h("div", { class: "sub" }, "Scan with Google Authenticator, Microsoft Authenticator, 1Password or your iPhone's Passwords app, then enter the code it shows."),
          h("a", { class: "mfa-qr", href: state.secSetup.otpauthUrl }, h("img", { src: state.secSetup.qr, alt: "Setup code", width: 180, height: 180 })),
          h("div", { class: "mfa-secret" }, "Key: ", h("code", {}, state.secSetup.secret.replace(/(.{4})/g, "$1 ").trim())),
          h("div", { class: "row-actions" },
            h("input", { id: "sec-code", class: "field mfa-code", inputmode: "numeric", autocomplete: "one-time-code", placeholder: "123456", maxlength: 6, required: true }),
            h("button", { class: "btn sm", type: "submit" }, "Turn on"),
            h("button", { class: "btn quiet sm", type: "button", onclick: () => ((state.secSetup = null), render()) }, "Cancel")))
      : null,
    me.authenticator
      ? h("button", { class: "item link", onclick: async () => {
          const code = await askCode("New backup codes");
          if (!code) return;
          try {
            const r = await api.newBackupCodes(code);
            await glassDialog({ title: "Your new backup codes", message: `The old ones no longer work:\n\n${r.backupCodes.join("   ")}`, confirm: "I saved them" });
            reload();
          } catch (e) {
            toast((e as Error).message);
          }
        } }, h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Get new backup codes")), icon("chevron", "sm"))
      : null);

  const admin = sec.admin && state.secDraft ? adminSecurity(sec.admin, state.secDraft) : null;
  return settingsPage("Security & sign-in", me.required ? "Two-step sign-in is required on this team" : "Protect your account and the phone line",
    h("div", { class: "group-title" }, bioName()), passkeys,
    h("div", { class: "group-title" }, "Two-step sign-in"), app,
    admin);
}

function adminSecurity(a: { settings: SecuritySettings; countries: Record<string, string>; log: SecurityLogItem[] }, d: SecuritySettings): Node {
  const c = d.calling;
  const dirty = JSON.stringify(d) !== JSON.stringify(a.settings);
  const save = async () => {
    const mine = state.sec?.me;
    if (d.require2fa && !a.settings.require2fa && mine && !mine.authenticator && !mine.passkeys.length) {
      const ok = await glassDialog({ title: "Require two-step sign-in?", message: `You'll set yours up first (${bioName()} or an authenticator app), then everyone else at their next sign-in.`, confirm: "Turn on" });
      if (!ok) return;
    }
    try {
      const r = await api.saveSecurity(d);
      a.settings = r.settings;
      state.secDraft = structuredClone(r.settings);
      toast("Security settings saved");
    } catch (e) {
      toast((e as Error).message);
    }
    render();
  };
  const num = (id: string, label: string, value: number, set: (n: number) => void, max: number) =>
    h("div", { class: "item" },
      h("label", { class: "grow", for: id }, h("div", { style: "font-weight:600" }, label)),
      h("input", { id, class: "field num", type: "number", min: 0, max, value, oninput: (e: Event) => (set(Number((e.target as HTMLInputElement).value)), render()) }));
  const q = state.countryQ.trim().toLowerCase();
  const all = Object.entries(a.countries).sort((x, y) => x[1].localeCompare(y[1]));
  const shown = all.filter(([code, name]) => c.countries.includes(code) || (q && (name.toLowerCase().includes(q) || code.startsWith(q.replace(/^\+/, "")))));
  return h("div", {},
    h("div", { class: "group-title" }, "Team (admins)"),
    h("div", { class: "group" },
      h("div", { class: "item" },
        h("span", { class: "item-icon" }, icon("check")),
        h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, "Two-step sign-in: required for everyone"),
          h("div", { class: "sub" }, `Every sign-in is confirmed with ${bioName()} or a code from an authenticator app (set up by scanning a QR code). With ${bioName()} set up, no code is needed. Teammates without either are asked to set one up at their next sign-in.`)))),
    h("div", { class: "group-title" }, "Calling protection (toll fraud)"),
    h("div", { class: "group" },
      h("div", { class: "item" }, h("div", { class: "sub" }, "Stops expensive calls if a password or teammate's phone is ever misused: only these destinations can be called (or forwarded / transferred to), with limits per hour. US and Canada always work; 900 / 976 premium numbers and satellite numbers are always blocked.")),
      switchRow("intl", "International calls", c.international ? "On, to the countries below only." : "Off: US and Canada only.", c.international, (v) => ((c.international = v), render())),
      c.international
        ? h("div", { class: "item stack" },
            h("div", { class: "chip-row" }, ...c.countries.map((code) => h("button", { class: "tag-chip", type: "button", title: "Remove", onclick: () => ((c.countries = c.countries.filter((x) => x !== code)), render()) },
              `${a.countries[code] ?? code} +${code}`, icon("close", "sm"))),
              !c.countries.length ? h("span", { class: "sub" }, "No countries yet: add one below.") : null),
            h("input", { id: "country-q", class: "field", type: "search", placeholder: "Add a country (Mexico, +44…)", value: state.countryQ, autocomplete: "off",
              oninput: (e: Event) => ((state.countryQ = (e.target as HTMLInputElement).value), renderSoon()) }),
            q ? h("div", { class: "country-list" }, ...shown.filter(([code]) => !c.countries.includes(code)).slice(0, 8).map(([code, name]) =>
              h("button", { class: "country-opt", type: "button", onclick: () => ((c.countries = [...c.countries, code]), (state.countryQ = ""), render()) }, icon("plus", "sm"), `${name}`, h("span", { class: "sub" }, ` +${code}`)))) : null)
        : null,
      switchRow("carib", "Caribbean +1 numbers", "Jamaica, Bahamas, Dominican Republic and other islands that use +1. A common toll-fraud target: turn on only if you call hotels or clients there.", c.caribbean, (v) => ((c.caribbean = v), render())),
      num("lim-user", "Most calls one teammate can make per hour", c.perUserHour, (n) => (c.perUserHour = n), 500),
      num("lim-line", "Most outside calls the line makes per hour", c.lineHour, (n) => (c.lineHour = n), 2000),
      num("lim-intl", "Most international calls per day", c.intlDay, (n) => (c.intlDay = n), 1000)),
    dirty ? h("div", { class: "sticky-save" }, h("button", { class: "btn block", onclick: () => void save() }, "Save security settings")) : null,
    h("div", { class: "group-title" }, "Recent security activity"),
    h("div", { class: "group" },
      ...(a.log.length ? a.log.slice(0, 20).map((l) => h("div", { class: "item" },
        h("div", { class: "grow" }, h("div", { style: "font-weight:600" }, SECURITY_LOG_LABEL[l.kind] ?? l.kind, l.user_name ? h("span", { class: "sub" }, ` · ${l.user_name}`) : null),
          l.detail && l.kind === "call_blocked" ? h("div", { class: "sub" }, l.detail) : null),
        h("span", { class: "sub", title: fullTime(l.created_at) }, listTime(l.created_at))))
        : [h("div", { class: "item" }, h("div", { class: "sub" }, "Nothing yet."))])));
}

async function loadEmailPage() {
  try {
    state.emailCfg = await api.emailSettings();
    state.emailDraft = state.emailCfg.settings.to.join(", ");
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

function emailAlertsView() {
  const e = state.emailCfg;
  if (!e) return settingsPage("Email alerts", "", h("div", { class: "pending", style: "padding:24px" }, h("span", { class: "spinner" }), "Loading"));
  const s = e.settings;
  const save = async (change: Partial<EmailSettings>, ok = "Saved") => {
    try {
      state.emailCfg = await api.saveEmailSettings(change);
      state.emailDraft = state.emailCfg.settings.to.join(", ");
      toast(ok);
    } catch (err) {
      toast((err as Error).message);
    }
    render();
  };
  return settingsPage("Email alerts", e.configured ? `Sending from ${e.via === "zoho" ? "your Zoho mailbox" : "Joy Phone's mail service"}` : "Get missed calls, voicemails and texts in your inbox",
    !e.configured
      ? h("div", { class: "warn-box" }, h("strong", {}, "Email isn't connected yet. "),
          "To send from your Zoho mailbox: in the Fly portal → joy-phone → Secrets, add ", h("code", {}, "SMTP_USER"), " (your address, like yossi@joytravelinc.com) and ",
          h("code", {}, "SMTP_PASS"), " (a Zoho app password: Zoho Mail → My Account → Security → App Passwords). Your settings below are saved either way.")
      : e.lastError ? h("div", { class: "warn-box" }, h("strong", {}, "Last email failed: "), e.lastError) : null,
    h("div", { class: "group-title" }, "Send to"),
    h("form", { class: "group form", onsubmit: (ev: Event) => {
      ev.preventDefault();
      void save({ to: state.emailDraft.split(/[,;\s]+/).filter(Boolean) }, "Recipients saved");
    } },
      h("input", { id: "email-to", class: "field", type: "text", inputmode: "email", value: state.emailDraft, placeholder: "yossi@joytravelinc.com, ana@joytravelinc.com", autocomplete: "off",
        oninput: (ev: Event) => (state.emailDraft = (ev.target as HTMLInputElement).value) }),
      h("div", { class: "row-actions" },
        h("button", { class: "btn sm", type: "submit" }, "Save"),
        e.configured ? h("button", { class: "btn quiet sm", type: "button", onclick: async () => {
          try {
            await api.testEmail();
            toast("Test email sent");
          } catch (err) {
            toast((err as Error).message);
          }
        } }, "Send a test email") : null)),
    h("div", { class: "group-title" }, "What to send"),
    h("div", { class: "group" },
      switchRow("em-missed", "Missed calls", "Who called and when.", s.missedCalls, (v) => void save({ missedCalls: v })),
      switchRow("em-vm", "Voicemails", "With the AI summary and the full transcript.", s.voicemails, (v) => void save({ voicemails: v })),
      switchRow("em-texts", "Customer texts", "Sent about 15 seconds after the text arrives (a quick burst from the same customer comes as one email).", s.texts, (v) => void save({ texts: v })),
      switchRow("em-sec", "Blocked calls", "When calling protection stops a call (possible toll fraud).", s.security, (v) => void save({ security: v }))),
    // 2.10.21: reply to a text email to text the customer back.
    e.replies ? h("div", { class: "group-title" }, "Reply by email") : null,
    e.replies ? h("div", { class: "group" },
      switchRow("em-reply", "Text back by replying to the email",
        `Reply to a "New text" email and Joy Phone texts your reply to the customer from Joy Travel's line, as you (with your text signature). Only what you write is sent, not the quoted email. Reply from the address you sign in to Joy Phone with.${
          s.replyByEmail === false ? "" : e.replies.state === "watching" ? ` Working: watching your mailbox${e.replies.handled ? ` (${e.replies.handled} sent since the app started)` : ""}.`
          : e.replies.state === "error" ? ` ⚠ Can't open your mailbox: ${e.replies.error ?? "unknown error"}. In Zoho Mail → Settings → Mail Accounts → IMAP Access, turn IMAP on.`
          : " Connecting to your mailbox…"}`,
        s.replyByEmail !== false, (v) => void save({ replyByEmail: v }, v ? "Email replies turned on" : "Email replies turned off"))) : null);
}

// ---------------------------------------------------------------- two-step sign-in & Face ID

const canPasskey = () => {
  try {
    return browserSupportsWebAuthn() && (location.protocol === "https:" || location.hostname === "localhost");
  } catch {
    return false;
  }
};
/** What the person will recognize: Face ID / Touch ID on Apple devices, Windows Hello, fingerprint on Android. */
function bioName(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return "Face ID";
  if (/Macintosh/.test(ua)) return "Touch ID";
  if (/Windows/.test(ua)) return "Windows Hello";
  if (/Android/.test(ua)) return "fingerprint";
  return "Face ID";
}
async function passkeySignIn() {
  const { flow, options } = await api.passkeyLoginOptions();
  const response = await startAuthentication({ optionsJSON: options });
  await api.passkeyLogin(flow, response);
}
async function passkeyRegister() {
  const { flow, options } = await api.passkeyRegisterOptions();
  const response = await startRegistration({ optionsJSON: options });
  const r = await api.passkeyRegister(flow, response);
  setFaceIdDone("added"); // this device has it now: no more "Add Face ID" offer here
  return r;
}
/** After a successful second step: open the app. */
async function finishSignIn() {
  const me = await api.me();
  if (me.mfa) return void renderMfa(me.mfa);
  state.user = me.user;
  state.vapidKey = me.vapidPublicKey;
  state.teamNumber = me.teamNumber;
  state.activityUnread = me.activityUnread ?? 0;
  await afterSignIn();
}

/** Signed-in device: "Approve sign-in on iPhone?" when this teammate is signing in on another device. */
let approvalsOpen = false;
async function checkApprovals() {
  if (approvalsOpen || !state.user) return;
  approvalsOpen = true;
  try {
    const { approvals } = await api.approvals();
    for (const a of approvals) {
      const where = a.device === "This device" ? "another device" : a.device;
      const picked = await numberChoiceDialog(`Sign-in on ${where}?`, "Tap the number shown on that device. Not you? Deny.", a.choices ?? []);
      try {
        await api.answerApproval(a.id, picked ? { number: picked } : "deny");
        toast(picked ? "Sent. If the number matched, that device is signed in." : "Denied");
      } catch (e) {
        toast((e as Error).message);
      }
    }
  } catch {
    // not signed in fully, or offline
  } finally {
    approvalsOpen = false;
  }
}

/** The device waiting on approval: asks, shows the number, and waits for the OK from a signed-in device. */
async function approveFromOtherDevice(show: (...body: (Node | null)[]) => void, back: () => void, done: () => Promise<void>, error: HTMLElement) {
  error.textContent = "";
  let req: { id: string; number: string; expiresInSecs: number };
  try {
    req = await api.approvalRequest();
  } catch (e) {
    error.textContent = (e as Error).message;
    return;
  }
  let stopped = false;
  show(
    h("h1", { class: "center" }, "Approve on your computer"),
    h("p", { class: "mfa-sub" }, "Open Joy Phone on a computer or phone where you're already signed in. Tap Approve when it asks, and check it shows this number:"),
    h("div", { class: "approval-number", "aria-label": `Number ${req.number}` }, req.number),
    h("p", { class: "mfa-sub pending" }, h("span", { class: "spinner" }), "Waiting for approval…"),
    h("button", { class: "btn quiet block", type: "button", onclick: () => ((stopped = true), back()) }, "Back"));
  const until = Date.now() + req.expiresInSecs * 1000;
  while (!stopped && Date.now() < until) {
    await new Promise((r) => setTimeout(r, 2000));
    if (stopped) return;
    let st: string;
    try {
      st = (await api.approvalStatus(req.id)).state;
    } catch {
      continue; // a blip: keep waiting
    }
    if (st === "approved") {
      await done();
      // Next time, Face ID on this device: offer it now (a tap is needed to start Face ID).
      if (canPasskey()) {
        const yes = await glassDialog({ title: `Use ${bioName()} next time?`, message: `Sign in on this device with ${bioName()} instead of approving from another one.`, confirm: `Turn on ${bioName()}`, cancel: "Not now" });
        if (yes) {
          try {
            await passkeyRegister();
            setFaceIdDone("added");
            toast(`${bioName()} is on for this device`);
          } catch (e) {
            if ((e as Error).name !== "NotAllowedError") toast((e as Error).message);
          }
        }
      }
      return;
    }
    if (st === "denied" || st === "expired") {
      back();
      error.textContent = st === "denied" ? "The sign-in was denied." : "That took too long. Try again.";
      return;
    }
  }
  if (!stopped) {
    back();
    error.textContent = "That took too long. Try again.";
  }
}

let mfaShowing: MfaGate | null = null;
/** Full-screen: enter your code / use Face ID, or set up two-step sign-in when the team requires it. */
async function renderMfa(gate: MfaGate) {
  if (mfaShowing === gate && document.querySelector(".mfa-card")) return;
  mfaShowing = gate;
  state.user = null;
  events?.close();
  events = null;
  void voice.disconnect().catch(() => {});
  const error = h("div", { class: "error", role: "alert" });
  const shell = (...body: (Node | null)[]) => mount(root, h("div", { class: "login" }, h("div", { class: "login-card mfa-card" },
    h("img", { class: "login-logo", src: `/brand/logo.png?v=${VERSION}`, alt: "Joy Phone", width: 72, height: 72 }), ...body, error,
    h("button", { class: "text-btn mfa-out", type: "button", onclick: async () => {
      mfaShowing = null;
      markSignedOut(true);
      renderLogin();
      void endServerSession();
    } }, "Log out"))));
  const done = async () => {
    mfaShowing = null;
    await finishSignIn();
  };

  if (gate === "verify") {
    let status: TwoFactorStatus | null = null;
    try {
      status = await api.twoFactor();
    } catch {
      status = null;
    }
    const code = h("input", { id: "mfa-code", class: "field mfa-code", inputmode: "numeric", autocomplete: "one-time-code", placeholder: "123456", maxlength: 12, required: true });
    const remember = h("input", { id: "mfa-remember", type: "checkbox", checked: true });
    const btn = h("button", { class: "btn block", type: "submit" }, "Verify");
    const form = h("form", { onsubmit: async (e: Event) => {
      e.preventDefault();
      btn.disabled = true;
      error.textContent = "";
      try {
        await api.verifyTwoFactor(code.value, remember.checked);
        await done();
      } catch (err) {
        error.textContent = (err as Error).message;
        btn.disabled = false;
        code.select();
      }
    } },
      status?.authenticator ? h("label", { class: "field-label", for: "mfa-code" }, "Code from your authenticator app (or a backup code)") : null,
      status?.authenticator ? code : null,
      status?.authenticator ? h("label", { class: "check-row", for: "mfa-remember" }, remember, "Don't ask again on this device for 30 days") : null,
      status?.authenticator ? btn : null);
    // Face ID only where it was set up on this device; elsewhere the phone could only show a QR code.
    const faceHere = !!status?.passkeys.length && canPasskey() && faceIdHere();
    const approve = (primary: boolean) =>
      h("button", { class: `btn ${primary ? "" : "quiet "}block approve-btn`, type: "button", onclick: () =>
        void approveFromOtherDevice((...body) => shell(...body), () => ((mfaShowing = null), void renderMfa("verify")), done, error) },
        "Approve from another device");
    shell(
      h("h1", { class: "center" }, "Two-step sign-in"),
      h("p", { class: "mfa-sub" },
        faceHere ? `Confirm it's you with ${bioName()}.`
        : status?.authenticator ? "Enter the 6-digit code from your authenticator app."
        : `${bioName()} isn't set up on this device yet. Approve this sign-in from a computer or phone where you're signed in.`),
      faceHere
        ? h("button", { class: `btn block ${status?.authenticator ? "quiet " : ""}passkey-btn`, type: "button", onclick: async () => {
            error.textContent = "";
            try {
              await passkeySignIn();
              setFaceIdDone("added");
              await done();
            } catch (err) {
              if ((err as { status?: number }).status === 401) forgetFaceIdHere();
              if ((err as Error).name !== "NotAllowedError") error.textContent = (err as Error).message;
            }
          } }, icon("user", "sm"), `Use ${bioName()}`)
        : null,
      form,
      approve(!faceHere && !status?.authenticator),
      // Face ID set up on this device in another app or browser: let them try it anyway.
      !faceHere && status?.passkeys.length && canPasskey()
        ? h("button", { class: "text-btn", type: "button", style: "margin-top:8px", onclick: async () => {
            error.textContent = "";
            try {
              await passkeySignIn();
              setFaceIdDone("added");
              await done();
            } catch (err) {
              if ((err as Error).name !== "NotAllowedError") error.textContent = (err as Error).message;
            }
          } }, `Already set up ${bioName()} on this device? Use it`)
        : null);
    requestAnimationFrame(() => code.focus());
    return;
  }

  // gate === "setup": the team requires two-step sign-in.
  const viaApp = async () => {
    error.textContent = "";
    let setup: { secret: string; otpauthUrl: string; qr: string };
    try {
      setup = await api.setupTwoFactor();
    } catch (err) {
      error.textContent = (err as Error).message;
      return;
    }
    const code = h("input", { id: "mfa-code", class: "field mfa-code", inputmode: "numeric", autocomplete: "one-time-code", placeholder: "123456", maxlength: 6, required: true });
    const btn = h("button", { class: "btn block", type: "submit" }, "Turn on");
    shell(
      h("h1", { class: "center" }, "Set up your authenticator"),
      h("p", { class: "mfa-sub" }, "Scan this with Google Authenticator, Microsoft Authenticator, 1Password or your iPhone's Passwords app. Then type the 6-digit code it shows."),
      h("a", { class: "mfa-qr", href: setup.otpauthUrl, title: "On this phone? Tap to add it to your authenticator app" }, h("img", { src: setup.qr, alt: "Setup code", width: 200, height: 200 })),
      h("div", { class: "mfa-secret" }, "Or enter this key: ", h("code", {}, setup.secret.replace(/(.{4})/g, "$1 ").trim())),
      h("form", { onsubmit: async (e: Event) => {
        e.preventDefault();
        btn.disabled = true;
        error.textContent = "";
        try {
          const r = await api.enableTwoFactor(code.value);
          showBackupCodes(r.backupCodes, done);
        } catch (err) {
          error.textContent = (err as Error).message;
          btn.disabled = false;
        }
      } }, code, btn));
    requestAnimationFrame(() => code.focus());
  };
  shell(
    h("h1", { class: "center" }, "Protect your account"),
    h("p", { class: "mfa-sub" }, `Two-step sign-in is required. Pick how you'll confirm it's you: ${bioName()} (no codes to type), or an authenticator app you set up by scanning a QR code.`),
    canPasskey()
      ? h("button", { class: "btn block passkey-btn", type: "button", onclick: async () => {
          error.textContent = "";
          try {
            await passkeyRegister();
            await done();
          } catch (err) {
            if ((err as Error).name !== "NotAllowedError") error.textContent = (err as Error).message;
          }
        } }, icon("user", "sm"), `Use ${bioName()} on this device`)
      : null,
    h("button", { class: "btn quiet block", type: "button", onclick: () => void viaApp() }, icon("keypad", "sm"), "Use an authenticator app"));
}

function showBackupCodes(codes: string[], next: () => void) {
  const list = codes.join("\n");
  mount(root, h("div", { class: "login" }, h("div", { class: "login-card mfa-card" },
    h("h1", { class: "center" }, "Save your backup codes"),
    h("p", { class: "mfa-sub" }, "If you lose your phone, each of these codes signs you in once. Keep them somewhere safe, like your password manager."),
    h("div", { class: "backup-codes" }, ...codes.map((c) => h("code", {}, c))),
    h("div", { class: "row-actions", style: "justify-content:center" },
      h("button", { class: "btn quiet sm", type: "button", onclick: () => void navigator.clipboard?.writeText(list).then(() => toast("Copied"), () => toast("Couldn't copy")) }, icon("copy", "sm"), "Copy")),
    h("button", { class: "btn block", type: "button", onclick: () => next() }, "I saved them"))));
}

async function renderReset(token: string) {
  const logo = () => h("img", { class: "login-logo", src: `/brand/logo.png?v=${VERSION}`, alt: "Joy Phone", width: 88, height: 88 });
  const card = h("div", { class: "login-card" }, logo(), h("div", { class: "pending" }, h("span", { class: "spinner" }), "Checking your link…"));
  mount(root, h("div", { class: "login" }, card));
  let who: { name: string; email: string };
  try {
    who = await api.getReset(token);
  } catch (e) {
    card.replaceChildren(logo(), h("h1", {}, "Link not valid"), h("p", {}, (e as Error).message), h("a", { class: "btn block", href: "/" }, "Go to sign in"));
    return;
  }
  const password = h("input", { id: "password", class: "field", type: "password", autocomplete: "new-password", minlength: 10, required: true });
  const confirm = h("input", { id: "password2", class: "field", type: "password", autocomplete: "new-password", required: true });
  const error = h("div", { class: "error", role: "alert" });
  const button = h("button", { class: "btn block", type: "submit" }, "Set new password");
  const form = h("form", { class: "login-card", onsubmit: async (e: Event) => {
    e.preventDefault();
    error.textContent = "";
    if (password.value !== confirm.value) return void (error.textContent = "The passwords don't match");
    button.disabled = true;
    try {
      state.user = (await api.useReset(token, password.value)).user;
      history.replaceState(null, "", "/");
      state.vapidKey = (await api.me()).vapidPublicKey;
      await afterSignIn();
      toast("Password updated");
    } catch (err) {
      error.textContent = (err as Error).message;
      button.disabled = false;
    }
  } },
    logo(),
    h("h1", {}, "Choose a new password"),
    h("p", {}, `For ${who.email}.`),
    h("input", { type: "email", autocomplete: "username", value: who.email, hidden: true, readonly: true }),
    h("label", { class: "field-label", for: "password" }, "New password (10+ characters)"), password,
    h("label", { class: "field-label", for: "password2" }, "Confirm password"), confirm,
    error, button);
  mount(root, h("div", { class: "login" }, form));
}

async function renderInvite(token: string) {
  const card = h("div", { class: "login-card" },
    h("img", { class: "login-logo", src: `/brand/logo.png?v=${VERSION}`, alt: "Joy Phone", width: 88, height: 88 }),
    h("div", { class: "pending" }, h("span", { class: "spinner" }), "Checking your invite…"));
  mount(root, h("div", { class: "login" }, card));
  let invite: { name: string; email: string };
  try {
    invite = await api.getInvite(token);
  } catch (e) {
    card.replaceChildren(
      h("img", { class: "login-logo", src: `/brand/logo.png?v=${VERSION}`, alt: "Joy Phone", width: 88, height: 88 }),
      h("h1", {}, "Invite not found"),
      h("p", {}, (e as Error).message),
      h("a", { class: "btn block", href: "/" }, "Go to sign in"));
    return;
  }
  const name = h("input", { id: "name", class: "field", autocomplete: "name", value: invite.name, required: true });
  const password = h("input", { id: "password", class: "field", type: "password", autocomplete: "new-password", minlength: 10, required: true });
  const confirm = h("input", { id: "password2", class: "field", type: "password", autocomplete: "new-password", required: true });
  const error = h("div", { class: "error", role: "alert" });
  const button = h("button", { class: "btn block", type: "submit" }, "Join the team");
  const form = h("form", { class: "login-card", onsubmit: async (e: Event) => {
    e.preventDefault();
    error.textContent = "";
    if (password.value !== confirm.value) return void (error.textContent = "The passwords don't match");
    button.disabled = true;
    try {
      state.user = (await api.acceptInvite(token, name.value, password.value)).user;
      history.replaceState(null, "", "/");
      state.vapidKey = (await api.me()).vapidPublicKey;
      navigator.serviceWorker?.addEventListener("message", (ev) => {
        if (ev.data?.type === "open") {
          readUrl(ev.data.url);
          void refresh();
        }
      });
      await afterSignIn();
      toast("Welcome to Joy Phone!");
    } catch (err) {
      error.textContent = (err as Error).message;
      button.disabled = false;
    }
  } },
    h("img", { class: "login-logo", src: `/brand/logo.png?v=${VERSION}`, alt: "Joy Phone", width: 88, height: 88 }),
    h("h1", {}, "Join Joy Phone"),
    h("p", {}, `You're invited as ${invite.email}. Choose a password to finish.`),
    h("label", { class: "field-label", for: "name" }, "Your name"), name,
    h("label", { class: "field-label", for: "password" }, "Password (10+ characters)"), password,
    h("label", { class: "field-label", for: "password2" }, "Confirm password"), confirm,
    h("input", { type: "email", autocomplete: "username", value: invite.email, hidden: true, readonly: true }),
    error, button);
  mount(root, h("div", { class: "login" }, form));
}

// ---------------------------------------------------------------- call screens

/** The frosted-glass card shown over the app while a call rings. */
function glassCard(opts: { number: string; name: string | null; gone?: boolean; onDecline: () => void; declineLabel: string; onAnswer?: () => void; connecting?: boolean }) {
  const conv = state.conversations.find((c) => c.external_number === opts.number);
  const known = !!opts.name;
  const context = conv
    ? [
        conv.assigned_name ? `Assigned to ${conv.assigned_to === state.user?.id ? "you" : firstName(conv.assigned_name)}` : null,
        conv.preview ? previewText(conv.preview) : conv.sms_body ? `Last text: ${conv.sms_body}` : null,
      ].filter(Boolean) as string[]
    : [];
  return h("div", { class: "glass-overlay", role: "dialog", "aria-modal": "true", "aria-label": `Incoming call from ${nameFor(opts.number, opts.name)}` },
    h("div", { class: "glass-card" },
      h("div", { class: "glass-top" }, brandMark(), h("span", {}, "Joy Phone"), h("span", { class: "glass-dot" }), h("span", {}, opts.gone ? "Call ended" : "Incoming call")),
      h("div", { class: opts.gone ? "glass-avatar" : "glass-avatar ringing" }, avatar(opts.number || "?", opts.name, "xl")),
      h("div", { class: "glass-name" }, opts.number ? nameFor(opts.number, opts.name) : opts.name ?? "Incoming call"),
      h("div", { class: "glass-sub" }, !opts.number && opts.name ? "Teammate · Joy Phone" : known ? phone(opts.number) : opts.number && opts.number !== "anonymous" ? "New caller" : ""),
      opts.gone
        ? h("div", { class: "glass-context" }, "This call was already answered or ended.")
        : context.length
          ? h("div", { class: "glass-context" }, ...context.map((c) => h("div", {}, c)))
          : null,
      h("div", { class: "glass-actions" },
        h("div", { class: "ctrl" }, pressButton("ring-decline", opts.onDecline, { class: "call-fab end", "aria-label": opts.declineLabel }, icon(opts.gone ? "close" : "hangup", "lg")), opts.declineLabel),
        opts.onAnswer && !opts.gone
          ? h("div", { class: opts.connecting ? "ctrl connecting" : "ctrl" }, pressButton("ring-answer", opts.onAnswer, { class: opts.connecting ? "call-fab answer busy" : "call-fab answer", "aria-label": opts.connecting ? "Connecting" : "Answer", "aria-busy": String(!!opts.connecting) }, opts.connecting ? h("span", { class: "spinner" }) : icon("phone", "lg")), opts.connecting ? "Connecting…" : "Answer")
          : null)));
}

function incomingPopup(c: PhoneCall) {
  const number = c.intercomName ? "" : otherParty(c);
  const name = c.intercomName ?? contactNameOf(number);
  return glassCard({
    number, name,
    declineLabel: "Decline",
    onDecline: () => (dismissTeamRing(number), void c.hangup()),
    onAnswer: () => {
      ringer.stop();
      void answerRinging(c);
    },
    connecting: answeringRing.has(String(c.id)),
  });
}

let glassRinging = false;

/** Desktop (a wide window and a mouse): calls show as a small window in the corner instead of the whole screen. */
// ---- desktop call windows you can drag out of the way ----
type DockKind = "live" | "ring";
/** Where you dragged each call window (kept on this computer); none: its usual place. */
const dockPos: Partial<Record<DockKind, { x: number; y: number }>> = (() => {
  try {
    return JSON.parse(localStorage.getItem("joy-dock-pos") ?? "{}") ?? {};
  } catch {
    return {};
  }
})();
function saveDockPos() {
  try {
    localStorage.setItem("joy-dock-pos", JSON.stringify(dockPos));
  } catch {
    // not kept
  }
}
/** Keeps a window fully on screen (a smaller browser window, another monitor). */
function clampDock(el: HTMLElement, x: number, y: number) {
  const w = el.offsetWidth || 360, hgt = el.offsetHeight || 300;
  return { x: Math.min(Math.max(8, x), Math.max(8, innerWidth - w - 8)), y: Math.min(Math.max(8, y), Math.max(8, innerHeight - Math.min(hgt, 120))) };
}
function applyDockPos(el: HTMLElement, kind: DockKind) {
  const pos = dockPos[kind];
  if (pos) {
    Object.assign(el.style, { left: `${pos.x}px`, top: `${pos.y}px`, right: "auto", bottom: "auto", transform: "none" });
    // Once it's on the page (and has a size), make sure it's still fully visible.
    requestAnimationFrame(() => {
      if (!el.isConnected) return;
      const c = clampDock(el, pos.x, pos.y);
      if (c.x !== pos.x || c.y !== pos.y) Object.assign(el.style, { left: `${c.x}px`, top: `${c.y}px` });
    });
    return;
  }
  if (kind === "live") {
    // Its usual place: over the bottom of the inbox list, so the conversation, its notes and the message box
    // stay clear. Without a list on screen: the bottom-right corner, as before.
    const list = document.querySelector<HTMLElement>(".pane-list")?.getBoundingClientRect();
    if (list && list.width >= 300) Object.assign(el.style, { left: `${Math.max(8, Math.round(list.left + (list.width - 360) / 2))}px`, right: "auto" });
  }
}
/** Drag a call window by any spot that isn't a button or a box you type in; double-click puts it back. */
function placeDock(el: HTMLElement, kind: DockKind): HTMLElement {
  el.dataset.dock = kind;
  el.title = "";
  applyDockPos(el, kind);
  el.addEventListener("pointerdown", (e: PointerEvent) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest("button, a, input, textarea, select, label, [contenteditable], .transfer-sheet, .pad")) return;
    const start = el.getBoundingClientRect();
    const dx = e.clientX - start.left, dy = e.clientY - start.top;
    let moved = false;
    const move = (ev: PointerEvent) => {
      if (!moved && Math.hypot(ev.clientX - e.clientX, ev.clientY - e.clientY) < 4) return;
      moved = true;
      document.body.classList.add("dragging-dock");
      const cur = document.querySelector<HTMLElement>(`.call-dock[data-dock="${kind}"]`) ?? el; // a re-render may have replaced it
      dockPos[kind] = clampDock(cur, ev.clientX - dx, ev.clientY - dy);
      applyDockPos(cur, kind);
      ev.preventDefault();
    };
    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      document.removeEventListener("pointercancel", up);
      document.body.classList.remove("dragging-dock");
      if (moved) saveDockPos();
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    document.addEventListener("pointercancel", up);
  });
  el.addEventListener("dblclick", (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest("button, a, input, textarea, select")) return;
    delete dockPos[kind];
    saveDockPos();
    el.removeAttribute("style");
    applyDockPos(el, kind);
  });
  // A grip at the top says it can be moved.
  const card = el.querySelector(".glass-card");
  if (card && !card.querySelector(".dock-grip")) card.prepend(h("div", { class: "dock-grip", title: "Drag to move · double-click to put it back", "aria-hidden": "true" }));
  return el;
}

function deskCalls(): boolean {
  return matchMedia("(min-width: 900px) and (pointer: fine)").matches;
}

/** This window is in front and showing an incoming call's window right now (see the service worker's ring-check). */
function appShowsRing(): boolean {
  if (document.visibilityState !== "visible" || !document.hasFocus() || !state.user) return false;
  const c = voice.call;
  if (c && c.direction === "inbound" && c.state === "ringing" && !c.autoAnswered && !isEnded(c)) return true;
  return !!glassTeamRing();
}

/** You declined a call on this computer: don't bring it back as a ringing team call (it may still ring for others). */
function dismissTeamRing(number: string) {
  for (const l of state.live) if (l.direction === "inbound" && l.status === "ringing" && l.external_number === number) state.glassDismissed.add(l.id);
}

/**
 * Desktop: a call ringing for you or the whole team shows the glass window too, even before (or without) it
 * ringing in this browser (e.g. calls set to connect via your cell, or still reaching this computer).
 */
function glassTeamRing(): LiveCall | null {
  if (!state.user?.canCall || state.user.dnd || !matchMedia("(min-width: 768px) and (pointer: fine)").matches) return null; // not on Do Not Disturb
  const me = state.user.id;
  return state.live.find((c) =>
    c.direction === "inbound" && c.status === "ringing" && (c.stage === null || c.stage === "ringing")
    && (!c.ring_user || c.ring_user === me) && !state.glassDismissed.has(c.id)) ?? null;
}

/** Opened from a call notification (or a team call ringing, on desktop): it may still be ringing, or already over. */
function answerPopup(callId: string, onDismiss?: () => void) {
  const live = state.live.find((c) => c.id === callId);
  // Once the live list has loaded, a call that isn't in it (or isn't ringing) is over.
  const gone = inboxLoaded && (!live || live.status !== "ringing");
  return glassCard({
    number: live?.external_number ?? "",
    name: live?.contact_name ?? null,
    gone,
    declineLabel: gone ? "Close" : "Not now",
    onDecline: () => {
      if (popupConnecting === callId) popupConnecting = null;
      if (onDismiss) onDismiss();
      else ((state.pendingAnswer = null), render());
    },
    onAnswer: () => void answerFromServer(callId),
    connecting: popupConnecting === callId,
  });
}

/** Switching earpiece ↔ loudspeaker needs audio-output selection (Android Chrome, desktop browsers; not iPhone Safari). */
function canPickSpeaker() {
  return typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype && matchMedia("(pointer: coarse)").matches;
}

/**
 * Speaker on / off, like a normal phone: on = loudspeaker; off = back to the quiet earpiece at your ear.
 * iPhone names its outputs "Speaker" and "iPhone" (the earpiece, also called "Receiver"). Turning speaker off used
 * to pick the "default" output, which on iPhone can stay the loudspeaker, so the call stayed loud.
 */
async function setSpeaker(on: boolean) {
  const el = document.getElementById("remote-audio") as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null;
  try {
    if (!el?.setSinkId) throw new Error("This phone doesn't let the app switch to speaker");
    const outs = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "audiooutput");
    if (on) {
      const speaker = outs.find((d) => /speaker/i.test(d.label)) ?? outs.find((d) => d.deviceId !== "default" && d.deviceId !== "" && !/earpiece|receiver|handset|iphone/i.test(d.label));
      if (!speaker) throw new Error("This phone doesn't let the app switch to speaker");
      await el.setSinkId(speaker.deviceId);
    } else {
      // Off = back to the earpiece (held to your ear), like the Phone app. The earpiece if the phone names one,
      // otherwise the phone's own choice for a call (""), which is the earpiece in a call. Not "default": on
      // iPhone that can be the loudspeaker, which is why turning Speaker off left the call loud.
      const earpiece = outs.find((d) => /earpiece|receiver|handset/i.test(d.label)) ?? outs.find((d) => /^iphone$/i.test(d.label.trim()));
      await el.setSinkId(earpiece?.deviceId ?? "");
      steadyAudioSession(); // calling mode, whose normal output is the earpiece
    }
    state.speaker = on;
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

/** The server's view of my current call (consult, added people, who's listening). */
function myLiveCall(number: string) {
  const me = state.user?.id;
  return state.live.find((l) => l.external_number === number && (l.answered_by === me || l.consult?.byUserId === me)) ?? state.live.find((l) => l.external_number === number) ?? null;
}

async function callAction(fn: () => Promise<unknown>, ok?: string) {
  try {
    await fn();
    if (ok) toast(ok);
    state.callSheet = null;
    state.sheetNumber = "";
    await refresh();
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

/** What you'd like Selena to do with a caller you hand her (kept while the sheet is open). */
let selenaNote = "";
/** Transfer sheet: Selena at the top. She greets the caller and takes over; her results come to your chat with her. */
function selenaTransferRow() {
  const send = () => {
    const note = selenaNote.trim();
    void callAction(async () => {
      await api.transferToSelena(note);
      selenaNote = "";
    }, pron(`${aiName()} has the caller. She'll post the results in your chat with her.`, state.agentMan));
  };
  return h("div", { class: "transfer-row selena-transfer" },
    h("span", { class: "grow row-name" }, h("span", { class: "ai-av sm" }, icon("sparkle", "sm")), h("span", { class: "two-line" }, h("span", {}, `${aiName()}`), h("small", { class: "muted" }, "AI assistant takes over"))),
    h("span", { class: "row-actions" }, h("button", { class: "btn sm", title: `${aiName()} greets the caller and takes it from here`, onclick: send }, "Transfer")),
    h("input", { class: "field selena-note", type: "text", maxlength: 1500, placeholder: pron("What should she do? (optional, e.g. take down their new flight times)", state.agentMan), value: selenaNote,
      oninput: (e: Event) => (selenaNote = (e.target as HTMLInputElement).value),
      onkeydown: (e: KeyboardEvent) => { if (e.key === "Enter") { e.preventDefault(); send(); } } }));
}

/** Picker for Transfer / Add: teammates, or any phone number. */
function targetSheet(c: PhoneCall, kind: "add" | "transfer") {
  const others = state.team.filter((u) => u.id !== state.user?.id && u.canCall);
  const numberInput = h("input", { id: "sheet-number", class: "field", type: "tel", inputmode: "tel", placeholder: "Or a phone number", value: state.sheetNumber,
    oninput: (e: Event) => (state.sheetNumber = (e.target as HTMLInputElement).value) });
  const numTarget = () => {
    const n = toE164(state.sheetNumber);
    if (!n) toast("Enter a full phone number");
    return n;
  };
  const row = (label: Node, target: () => ({ userId: number } | { number: string } | null), name: string) =>
    h("div", { class: "transfer-row" }, label,
      kind === "add"
        ? h("button", { class: "btn sm", onclick: () => { const t = target(); if (t) void callAction(() => api.addParticipant(t), `Calling ${name}…`); } }, icon("plus", "sm"), "Add")
        : h("span", { class: "row-actions" },
            h("button", { class: "btn quiet sm", title: "Caller waits on hold while you talk to them first", onclick: () => { const t = target(); if (t) void callAction(() => api.consult(t), `Calling ${name}. The caller is on hold.`); } }, "Talk first"),
            h("button", { class: "btn sm", title: "Send the caller straight over", onclick: () => {
              const t = target();
              if (!t) return;
              if ("userId" in t) void startTransfer(c, state.team.find((u) => u.id === t.userId)!);
              else void callAction(() => api.transferTo(t), `Transferring to ${name}…`);
            } }, "Transfer")));
  return h("div", { class: "transfer-sheet" },
    h("div", { class: "sheet-title" }, kind === "add" ? "Add someone to this call" : "Transfer to"),
    kind === "transfer" && otherParty(c) ? selenaTransferRow() : null,
    ...others.map((u) => row(h("span", { class: "grow row-name" }, avatar(u.email, u.name), u.name, u.dnd ? h("span", { class: "role-badge dnd" }, "DND") : null), () => ({ userId: u.id }), firstName(u.name))),
    h("div", { class: "transfer-row" }, numberInput),
    row(h("span", { class: "grow muted" }, "the number above"), () => { const n = numTarget(); return n ? { number: n } : null; }, "that number"),
    h("button", { class: "btn quiet block", onclick: () => ((state.callSheet = null), render()) }, "Cancel"));
}

/** Listen in / whisper / barge leg: you hear the call; mode decides who hears you. */
function monitorScreen(c: PhoneCall) {
  const lc = state.live.find((l) => l.monitors?.some((m) => m.userId === state.user?.id)) ?? null;
  const mode = voice.monitoring?.mode ?? c.monitorMode ?? "listen";
  const setMode = async (m: "listen" | "whisper" | "barge") => {
    // 7.10.30: a listen-in that started without the mic (Mic switch off) sent silence in Whisper / Join: the mic is
    // turned on first, and the mode stays as it was if it can't be.
    if (m !== "listen" && c.micOff && !(await voice.enableMic(c).catch(() => false))) {
      toast("Turn on the mic to talk: allow it for Joy Phone in your browser or phone settings.");
      return render();
    }
    try {
      await api.setMonitorMode(m);
      if (voice.monitoring) voice.monitoring.mode = m;
      c.monitorMode = m;
      if (m === "listen") c.muteAudio();
      else c.unmuteAudio();
    } catch (e) {
      if ((voice.monitoring?.mode ?? c.monitorMode ?? "listen") === "listen") c.muteAudio(); // 7.10.30: a mic just turned on stays silent
      toast((e as Error).message);
    }
    render();
  };
  return h("div", { class: "call-screen glass-stage monitor" }, h("div", { class: "glass-card call-card" },
    h("div", { class: "glass-top" }, brandMark(), h("span", {}, "Joy Phone"), h("span", { class: "glass-dot" }), h("span", {}, mode === "listen" ? "Listening in" : mode === "whisper" ? "Whispering" : "On the call")),
    h("div", { class: "glass-avatar" }, avatar(lc?.external_number ?? "?", lc?.contact_name ?? null, "xl")),
    h("div", { class: "glass-name who" }, lc ? nameFor(lc.external_number, lc.contact_name) : "Live call"),
    h("div", { class: "glass-sub sub" }, lc?.answered_by_name ? `with ${lc.answered_by_name}` : ""),
    h("div", { class: "segmented big", role: "group", "aria-label": "Mode", style: "margin-top:22px" },
      h("button", { "aria-pressed": String(mode === "listen"), onclick: () => void setMode("listen") }, "Listen"),
      h("button", { "aria-pressed": String(mode === "whisper"), onclick: () => void setMode("whisper") }, "Whisper"),
      h("button", { "aria-pressed": String(mode === "barge"), onclick: () => void setMode("barge") }, "Join")),
    h("div", { class: "sub", style: "margin:10px 0 24px;max-width:300px" },
      mode === "listen" ? "Nobody hears you." : mode === "whisper" ? `Only ${firstName(lc?.answered_by_name) || "your teammate"} hears you. The caller doesn't.` : "Everyone hears you."),
    h("div", { class: "call-actions glass-actions" },
      h("div", { class: "ctrl" }, h("button", { class: "call-fab end", "aria-label": "Stop listening", onclick: () => void c.hangup() }, icon("hangup", "lg")), "Leave"))));
}

function callScreen(c: PhoneCall) {
  if (c.isMonitor) return monitorScreen(c);
  const number = c.intercomName ? "" : otherParty(c);
  const name = c.intercomName ?? contactNameOf(number);
  const incomingRinging = c.direction === "inbound" && c.state === "ringing" && !c.autoAnswered;
  const live = (c.state === "active" && !farRinging(c)) || c.state === "held";
  const lc = live ? myLiveCall(number) : null;
  const consult = lc?.consult && lc.consult.byUserId === state.user?.id ? lc.consult : null;
  const label: Record<string, string> = {
    new: "Calling…", requesting: "Calling…", trying: "Calling…", early: "Ringing…", ringing: incomingRinging ? "Incoming call" : c.direction === "inbound" ? "Connecting…" : "Ringing…",
    answering: "Connecting…", active: "Recording", held: "On hold",
  };
  const stateEl = h("div", { class: live ? "state live" : "state" });
  const paint = () => {
    const what = state.transferringTo ? `Transferring to ${firstName(state.transferringTo)}…` : consult ? `Caller on hold · ${consult.state === "answered" ? "talking to" : "calling"} ${consult.name ?? phone(consult.number ?? "")}`
      : state.held ? "On hold · caller hears music"
      : c.state === "active" && farRinging(c) ? "Ringing…"
      : c.state === "active" && state.recPaused ? "Recording paused"
      : c.state === "active" && c.intercomName ? "Teammate call" : label[c.state] ?? c.state;
    stateEl.replaceChildren(
      ...(c.state === "active" && !c.intercomName && !farRinging(c) && !state.held && !state.transferringTo && !state.recPaused && !consult ? [h("span", { class: "rec-dot" })] : []),
      live && state.callStartedAt ? `${what} · ${duration((Date.now() - state.callStartedAt) / 1000)}` : what);
  };
  const qualityEl = h("button", { class: "quality-pill", title: "Connection quality", onclick: () => {
    const qq = state.quality;
    if (qq) {
      // What's making it slow, in plain words (Wi-Fi is usually the cause of jitter and loss).
      const why = qq.relay === "tcp" || qq.relay === "tls" ? " Your network blocks direct call traffic, so audio takes a longer route. Try another Wi-Fi or cellular."
        : (qq.jitterMs ?? 0) > 30 || (qq.loss ?? 0) > 0.02 ? " The Wi-Fi is uneven: move closer to the router, use its 5 GHz network, or pause big downloads and video on it."
        : (qq.rttMs ?? 0) > 150 ? " The trip to the calling server is long: try another region in Settings › Audio devices."
        : "";
      // The start of the call: did any of the first seconds go missing? (Longest stretch with no audio arriving.)
      const start = qq.startGapMs == null ? "" : ` · start of call: ${qq.startGapMs > 300 ? `${(qq.startGapMs / 1000).toFixed(1)} s with no audio` : "no gap"}`;
      toast(`Delay ${qq.rttMs ?? "?"} ms · audio buffer ${qq.bufferMs ?? "?"} ms · jitter ${qq.jitterMs ?? "?"} ms · loss ${qq.loss == null ? "?" : (qq.loss * 100).toFixed(1)}%${qq.relay ? ` · relayed over ${qq.relay.toUpperCase()}` : ""} · region: ${REGION_NAMES[voice.region] ?? voice.region}${start}.${why}`);
    }
  } });
  const paintQuality = () => {
    const qq = state.quality;
    qualityEl.className = `quality-pill ${qq?.grade ?? "unknown"}`;
    qualityEl.replaceChildren(h("span", { class: "q-dot" }),
      !qq || qq.grade === "unknown" ? "Checking connection…" : `${qq.grade === "good" ? "Good" : qq.grade === "fair" ? "Fair" : "Poor"} connection${qq.rttMs != null ? ` · ${qq.rttMs} ms` : ""}`);
  };
  paint();
  paintQuality();
  if (live) {
    let n = 0;
    tick = window.setInterval(() => {
      paint();
      if (n++ % 2 === 0) void voice.quality().then((qq) => ((state.quality = qq), paintQuality()));
    }, 1000);
  }

  const ctrl = (iconName: string, text: string, pressed: boolean, onclick: () => void, disabled = false) =>
    h("button", { class: "ctrl", "aria-pressed": String(pressed), disabled, onclick }, h("span", { class: "disc" }, icon(iconName)), text);
  /** The call started without the mic (Mic switch off): a green "Turn on mic" button. */
  const ctrlMic = (iconName: string, text: string, onclick: () => void) =>
    h("button", { class: "ctrl mic-needed", "aria-pressed": "false", onclick }, h("span", { class: "disc" }, icon(iconName)), text);

  const waiting = voice.waiting;
  const held = voice.held;
  const banners: Node[] = [];
  if (waiting) {
    const wn = otherParty(waiting);
    banners.push(h("div", { class: "call-banner" },
      h("span", { class: "pulse small" }, icon("incoming", "sm")),
      h("span", { class: "grow" }, h("strong", {}, "Call waiting"), ` · ${nameFor(wn, state.conversations.find((x) => x.external_number === wn)?.contact_name)}`),
      // 7.10.30: only a connected call can go on hold (it hung up a call still connecting): until then, End & answer or wait.
      // 11.10.30: Decline is a real button the size of the others (it was a small ×, hard to hit, easy to miss on a phone).
      h("div", { class: "waiting-actions" },
        h("button", { class: "btn live sm", disabled: c.state !== "active" && c.state !== "held", title: c.state !== "active" && c.state !== "held" ? "Your call is still connecting" : undefined,
          onclick: () => void voice.answerWaiting("hold").catch((e) => toast((e as Error).message)) }, "Hold & answer"),
        h("button", { class: "btn quiet sm", onclick: () => void voice.answerWaiting("end").catch((e) => toast((e as Error).message)) }, "End & answer"),
        h("button", { class: "btn sm decline-btn", "aria-label": "Decline the waiting call", onclick: () => void voice.declineWaiting() }, icon("close", "sm"), "Decline"))));
  }
  if (held) {
    const hn = otherParty(held);
    banners.push(h("div", { class: "call-banner" }, icon("pause", "sm"),
      h("span", { class: "grow" }, h("strong", {}, "On hold"), ` · ${nameFor(hn, state.conversations.find((x) => x.external_number === hn)?.contact_name)}`),
      h("button", { class: "btn sm", onclick: () => void voice.swap().catch((e) => toast((e as Error).message)) }, "Swap"),
      // 7.10.30: the caller on hold can be hung up too (only Swap before), keeping the call you're on.
      h("button", { class: "btn quiet sm", "aria-label": "End the call on hold",onclick: () => void voice.hangupHeld().then(render) }, "End")));
  }
  if (consult) {
    banners.push(h("div", { class: "call-banner accent" }, icon("transfer", "sm"),
      h("span", { class: "grow" }, consult.state === "answered" ? `Talking to ${consult.name ?? phone(consult.number ?? "")}. The caller is on hold.` : `Calling ${consult.name ?? phone(consult.number ?? "")}…`),
      consult.state === "answered" ? h("button", { class: "btn sm", onclick: () => void callAction(() => api.completeConsult(), "Transferred") }, "Complete transfer") : null,
      h("button", { class: "btn quiet sm", onclick: () => void callAction(() => api.cancelConsult(), "Back with the caller") }, "Cancel")));
  }
  for (const p of lc?.participants ?? []) {
    banners.push(h("div", { class: "call-banner" }, icon("users", "sm"),
      h("span", { class: "grow" }, `${p.name ?? phone(p.number ?? "")} ${p.state === "answered" ? "is on the call" : "· ringing…"}`),
      h("button", { class: "icon-btn sm", "aria-label": "Remove from call", title: "Remove from call", onclick: () => void callAction(() => api.removeParticipant(p.id), "Removed") }, icon("close", "sm"))));
  }
  const listeners = (lc?.monitors ?? []).filter((m) => m.state === "answered" && m.userId !== state.user?.id);
  if (listeners.length) {
    banners.push(h("div", { class: "call-banner subtle" }, icon("headset", "sm"),
      h("span", { class: "grow" }, `${listeners.map((m) => firstName(m.name)).join(", ")} ${listeners.length > 1 ? "are" : "is"} ${listeners.some((m) => m.mode === "barge") ? "on the call" : "listening"}`)));
  }

  const controls = state.showKeypad && live
    ? h("div", { class: "pad", style: "margin-top:auto;margin-bottom:24px" },
        ...["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((k) =>
          h("button", { class: "key", ...pressKey(() => sendTone(c, k)) }, k, h("small", {}, LETTERS[k] ?? " "))))
    : state.callSheet && live
      ? targetSheet(c, state.callSheet)
      : h("div", { class: "controls" },
          c.micOff
            // Started without the mic (Mic switch off): nothing was asked; tapping here is what opens it.
            ? ctrlMic("micOff", "Turn on mic", async () => {
                // 7.10.30: a failure to send the new mic is a "couldn't" too (it escaped as an unhandled error).
                if (!(await voice.enableMic(c).catch(() => false))) toast("The microphone is blocked. Allow it for Joy Phone in Settings › Safari › Microphone.");
                else (state.muted = false), (c.userMuted = false), voice.applyMute(c);
                render();
              })
            : ctrl(state.muted ? "micOff" : "mic", state.muted ? "Unmute" : "Mute", state.muted, () => {
                // 7.10.30: your Mute is kept on the call itself, so hold and swap leave it as you set it.
                c.userMuted = !state.muted;
                state.muted = c.userMuted;
                voice.applyMute(c);
                render();
              }, !live),
          ctrl("pause", state.held ? "Resume" : "Hold", state.held, () => void setHold(c, !state.held), !live || !!state.transferringTo || !!consult),
          ctrl("keypad", "Keypad", false, () => ((state.showKeypad = true), render()), !live),
          ctrl("transfer", "Transfer", !!state.transferringTo || !!consult, () => ((state.callSheet = "transfer"), render()), !live || !!state.transferringTo || !!consult),
          ctrl("userPlus", "Add", false, () => ((state.callSheet = "add"), render()), !live || !!consult),
          // Text the caller during the call (the call keeps going in the bar at the top).
          ctrl("message", "Text", false, () => {
            if (!deskCalls()) state.callMinimized = true; // desktop: the call window stays in its corner
            state.composeMode = "text";
            state.reply = null;
            go({ name: "thread", number });
            requestAnimationFrame(() => document.getElementById("draft")?.focus());
          }, !number || !canText(number)),
          canPickSpeaker() ? ctrl("speaker", "Speaker", state.speaker, () => void setSpeaker(!state.speaker)) : null,
          // Calls with a customer are recorded: pause it for something private, then resume.
          live && !c.intercomName && !c.isMonitor ? ctrl("recPause", state.recPaused ? "Resume rec." : "Pause rec.", state.recPaused, () => void setRecPaused(!state.recPaused)) : null,
          ctrl("note", "Comments", false, () => {
            if (!deskCalls()) state.callMinimized = true; // desktop: the call window stays in its corner
            state.composeMode = "comment";
            go({ name: "thread", number });
          }),
          ctrl("chat", "Team chat", false, () => {
            if (!deskCalls()) state.callMinimized = true; // desktop: the call window stays in its corner
            go({ name: "chat" });
          }));

  // Same frosted-glass card as the incoming-call window, so every call screen looks alike.
  const topLabel = incomingRinging ? "Incoming call" : live ? (c.state === "held" ? "On hold" : "On a call") : "Calling";
  return h("div", { class: "call-screen glass-stage" }, h("div", { class: "glass-card call-card" },
    h("div", { class: "glass-top" }, brandMark(), h("span", {}, "Joy Phone"), h("span", { class: "glass-dot" }), h("span", {}, topLabel),
      h("button", { class: "icon-btn sm glass-min back-btn", "aria-label": "Minimize", title: "Back to the app (call stays on)", onclick: () => ((state.callMinimized = true), render()) }, icon("back", "sm"))),
    h("div", { class: !live ? "glass-avatar ringing" : "glass-avatar" }, avatar(number || "?", name, "xl")),
    h("div", { class: "glass-name who" }, c.intercomName ?? (nameFor(number, name) || "Unknown caller")),
    c.intercomName ? h("div", { class: "glass-sub sub" }, "Teammate · Joy Phone") : name || callerNames.get(number) ? h("div", { class: "glass-sub sub" }, phone(number)) : null,
    stateEl,
    live ? qualityEl : null,
    live && (state.quality?.relay === "tcp" || state.quality?.relay === "tls")
      ? h("div", { class: "call-banners" }, h("div", { class: "call-banner subtle" }, icon("block", "sm"),
          h("span", { class: "grow" }, "Your network is blocking the fast audio path (UDP), so audio takes a slower route. Try another Wi-Fi or turn off VPN.")))
      : null,
    banners.length ? h("div", { class: "call-banners" }, ...banners) : null,
    controls,
    h("div", { class: "call-actions glass-actions" },
      state.showKeypad ? h("div", { class: "ctrl" }, h("button", { class: "icon-btn glass-round", style: "width:72px;height:72px", onclick: () => ((state.showKeypad = false), render()) }, icon("back", "lg")), "Back") : null,
      h("div", { class: "ctrl" }, pressButton("call-end", () => (incomingRinging && dismissTeamRing(otherParty(c)), void c.hangup()), { class: "call-fab end", "aria-label": incomingRinging ? "Decline" : "End call" }, icon("hangup", "lg")), incomingRinging ? "Decline" : "End"),
      incomingRinging
        ? (() => {
            const busy = answeringRing.has(String(c.id));
            return h("div", { class: busy ? "ctrl connecting" : "ctrl" }, pressButton("call-answer", () => { ringer.stop(); void answerRinging(c); }, { class: busy ? "call-fab answer busy" : "call-fab answer", "aria-label": busy ? "Connecting" : "Answer", "aria-busy": String(busy) }, busy ? h("span", { class: "spinner" }) : icon("phone", "lg")), busy ? "Connecting…" : "Answer");
          })()
        : null)));
}

// Local demo only (127.0.0.1, never the real site): ?previewCall=live or =ring shows the call window with a pretend call.
if (location.hostname === "127.0.0.1" && /[?&]previewCall=(live|ring)/.test(location.search)) {
  const mode = /previewCall=ring/.test(location.search) ? "ring" : "live";
  const wait = setInterval(() => {
    if (!state.user) return;
    clearInterval(wait);
    const fake = {
      id: "preview", state: mode === "ring" ? "ringing" : "active", direction: mode === "ring" ? "inbound" : "outbound",
      options: { destinationNumber: "+13105551234", remoteCallerNumber: "+13105551234" },
      hangup: async () => {}, muteAudio: () => {}, unmuteAudio: () => {}, answer: async () => {},
    } as unknown as PhoneCall;
    (voice as unknown as { call: PhoneCall }).call = fake;
    if (mode === "live") outAnswered.add("preview"); // the pretend call is answered (its buttons and keypad work)
    state.callStartedAt = Date.now() - 83_000;
    render();
  }, 200);
}
